import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useDeferredValue,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  ShoppingBag,
  Users,
  Search,
  Plus,
  Minus,
  Trash2,
  Send,
  DollarSign,
  Receipt,
  Printer,
  Package,
  GitMerge,
  Settings as SettingsIcon,
  Calculator,
  Truck,
  Calendar,
  User,
  UserPlus,
  LogOut,
  Clock,
  X,
  Check,
  ArrowLeftRight,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChefHat,
  FileText,
  Lock,
  AlertCircle,
  RefreshCw,
  RotateCcw,
  Monitor,
  Maximize2,
  Minimize2,
  Home,
  Sun,
  Moon,
  Edit3,
  Scissors,
  Utensils,
} from "lucide-react";
import io from "socket.io-client";
import Settings from "./Settings";
import PaymentInterface from "./PaymentInterface";
import CashMovementModal from "./CashMovementModal";
import CashJournalModal from "./CashJournalModal";
import CashDrawerModal from "./CashDrawerModal";
import CustomerCaisseModal from "./CustomerCaisseModal";
import CreditRecoveryModal from "./CreditRecoveryModal";
import XReportModal from "./XReportModal";
import StewardTableNavigator from "./StewardTableNavigator";
import { DiscountModal, ItemDiscountModal } from "./DiscountModal";
import RestaurantWaiterIcon from "./icons/RestaurantWaiterIcon";
import {
  clearLockedSession,
  getLockedSession,
  migrateLegacyLockedSession,
  setLockedSession,
} from "../utils/lockedSessions";
import { applyCategoryDefaults } from "../utils/categoryDefaults";
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";
import {
  markPerf,
  recordPerfMetric,
  startPerfTimer,
  incrementPerfCounter,
} from "../utils/perfMonitor";
import { getUiPerfProfile } from "../utils/perfProfile";
import { getStoredAuthToken } from "../utils/apiClient";
import { canUseCashSessionForRole } from "../utils/cashSessionRoles";
import {
  computeCashLogoutDrawerAmount,
  isPhysicalCashMethod,
} from "../utils/cashLogout";
import { isCreditPaymentMethod } from "../shared/paymentMethodKinds";
import {
  countSelectedAccompaniments,
  getLineAccompanimentCapacity,
  getSelectableExtraMaximum,
  resolveAccompanimentQuota,
} from "../utils/accompanimentQuota";
import { getSplitExtraRows } from "../utils/splitExtraPresentation";
import {
  createTableCartSyncTracker,
  isSameOrderItemId,
} from "../utils/tableCartSync";
import {
  addServerCustomerCountIfMissing,
  buildTableCartReconciliationState,
  canReleaseTableFromSnapshot,
  getExplicitOrderItemId,
  resolveLocalCartOccupancyCount,
  resolveCartCourseManagement,
  shouldClearCanonicalEmptyLocalCart,
  snapshotHasCanonicalItems,
} from "../utils/tableCartReconciliation";
import {
  normalizePaidOrderIds,
  shouldInvalidateRecentPaymentGuard,
} from "../utils/recentPaymentGuard";
import {
  TABLE_CART_EVENT_KIND,
  createDestructiveCartEvidenceRegistry,
  resolveTableCartEventPolicy,
} from "../utils/tableCartEventPolicy";
import {
  chooseMetadataRefreshMode,
  createFixedWindowCoordinator,
  mergeMetadataTableIds,
  mergeRealtimeTableIntent,
  resolveRealtimeTableContext,
} from "../utils/realtimeTableUpdates";
import {
  getSafeNetworkErrorMessage,
  normalizeNetworkRequestError,
} from "../utils/networkRequestError";
import { buildOrderSendRequestId } from "../utils/orderSendIdempotency";
import { occupyTableWithLatestCycle } from "../utils/tableOccupancyCycle";
import {
  buildTableTransferPayload,
  getStewardTransferTargets,
} from "../utils/stewardTransferUi";
import {
  buildSplitEditorSelectionRows,
  getSplitItemKey,
  moveSplitEditorNatureItem,
} from "../utils/splitEditorState";
import {
  AGGREGATOR_ORDER_META,
  isStandaloneAggregatorPayment,
  normalizeAggregatorCode,
} from "../utils/aggregatorPresentation";
import { readAggregatorPrice } from "../utils/aggregatorPricing";
import {
  buildPersistentCartCachePayload,
  clearPersistentSentItems,
  readPersistentCartCachePayload,
  upsertPersistentCartCacheEntry,
} from "../utils/persistentCartCache";
import {
  areInvoiceAdjustmentContextsCompatible,
  buildInvoiceAdjustmentContext,
  readInvoiceAdjustmentDraft,
  serializeInvoiceAdjustmentDraft,
} from "../utils/invoiceAdjustmentDraft";
import {
  createDraftClientLineId,
  ensureDraftLineIds,
  findDraftLineIndex,
  mergeDraftLines,
} from "../utils/draftCartLines";
import { reconcileDraftsWithSettledLines } from "../utils/settledDraftReconciliation";
import { hasCompatibleCartOrigin, preserveDetachedCart } from "../utils/draftCacheOrigin";
import {
  calculateThermalPageSize,
  resolveThermalPrintableWidthMm,
} from "../utils/thermalPrintLayout";
import { escapeHtml, renderUsbTicketHtml } from "../utils/usbTicketTemplate";
import {
  filterConfirmedCounterOrders,
  shouldShowCounterTreatmentBanner,
} from "../utils/counterTreatmentBanner";
import {
  hasLaterActiveCourse,
  isCourseEditable,
  resolveDisplayedCourseNumber,
  resolveFollowingCourseTarget,
  resolveNextCourseNumber,
  resolveSelectedCourseNumber,
  selectCourseDraftItems,
} from "../utils/courseSelection";
import {
  buildCourseStateSignature,
  mergeAuthoritativeCourseStates,
  retainLocalDraftCourseStates,
  upsertAuthoritativeCourseState,
} from "../utils/courseStateSync";
import { getOrCreateDeviceId } from "../utils/device";
import "./CaisseInterface.css";

const PRODUCT_PLACEHOLDER = "/placeholder-menu.jpg";
// Une identité d'appareil unique doit être utilisée au login, au heartbeat et
// par toutes les mutations de caisse. Deux clés divergentes faisaient rejeter
// un Bearer valide dès que la caisse ajoutait X-Moo-Device-Id.
const getPersistentDeviceId = () =>
  typeof window === "undefined" ? "server-render" : getOrCreateDeviceId();
const CASH_DEBUG_ENABLED =
  typeof window !== "undefined" &&
  window.localStorage?.getItem("moo_debug_cash") === "1";
const cashDebugLog = (...args) => {
  if (!CASH_DEBUG_ENABLED) return;
  window.console.log(...args);
};
const cashDebugLogLazy = (factory) => {
  if (!CASH_DEBUG_ENABLED) return;
  try {
    const payload = typeof factory === "function" ? factory() : factory;
    if (Array.isArray(payload)) {
      window.console.log(...payload);
      return;
    }
    window.console.log(payload);
  } catch (_error) {
    // ignore debug serialization errors
  }
};
const formatTableAccessConflictMessage = (conflict, options = {}) => {
  if (conflict?.sameUserOtherDevice) {
    return "Cette table est ouverte avec votre compte sur un autre appareil. Reprenez-la ici avant de continuer.";
  }
  const suffix = options.withRecoveryHint
    ? " Quittez la table ou demandez une reprise manager."
    : "";
  return `Cette table est désormais gérée par ${
    conflict?.managedBy || "un autre utilisateur"
  }.${suffix}`;
};
const UI_PERF_PROFILE = getUiPerfProfile();
const ONLINE_ORDERS_POLL_INTERVAL_MS =
  UI_PERF_PROFILE.onlineOrdersPollIntervalMs;
const HEADER_CLOCK_TICK_MS = UI_PERF_PROFILE.headerClockTickMs;
const UNPAID_SNAPSHOT_TTL_MS = UI_PERF_PROFILE.unpaidSnapshotTtlMs;
const DUAL_SCREEN_ALLOWED_STORAGE_KEY = "moo_dual_screen_allowed";
const POS_CART_CACHE_STORAGE_KEY = "moo_pos_known_carts_v1";
const POS_CART_CACHE_SESSION_BACKUP_KEY = "moo_pos_known_carts_session_v2";
const POS_COUNTER_RECOVERY_STORAGE_PREFIX = "moo_pos_counter_recovery_v1";
const getCounterRecoveryStorageKey = () =>
  `${POS_COUNTER_RECOVERY_STORAGE_PREFIX}:${getPersistentDeviceId()}`;
const getCounterRecoverySessionBackupKey = () =>
  `${getCounterRecoveryStorageKey()}:session-backup`;
const POS_CART_CACHE_MAX_AGE_MS = 18 * 60 * 60 * 1000;

const buildPersistentCartKey = (tableId, isDelivery = false) =>
  `${isDelivery ? "delivery" : "table"}:${String(tableId || "")}`;

const resolveTableOccupancyCycle = (table) => {
  const value = Number(table?.occupancy_cycle ?? table?.occupancyCycle);
  return Number.isInteger(value) && value > 0 ? value : null;
};

const readPersistentCartCache = () => {
  if (typeof window === "undefined") return new Map();
  const restored = new Map();
  let lastError = null;
  const candidates = [
    [() => window.localStorage, POS_CART_CACHE_STORAGE_KEY],
    [() => window.sessionStorage, POS_CART_CACHE_SESSION_BACKUP_KEY],
  ];
  candidates.forEach(([getStorage, key]) => {
    try {
      const storage = getStorage();
      const parsed = readPersistentCartCachePayload(storage?.getItem(key), {
        maxAgeMs: POS_CART_CACHE_MAX_AGE_MS,
      });
      parsed.forEach((entry, cacheKey) => {
        const previous = restored.get(cacheKey);
        if (
          !previous ||
          Number(entry?.timestamp || 0) >= Number(previous?.timestamp || 0)
        ) {
          restored.set(cacheKey, entry);
        }
      });
    } catch (error) {
      lastError = error;
    }
  });
  if (lastError) {
    console.warn(
      "[cart-cache] Une copie du cache panier n'a pas pu être lue:",
      lastError?.message || lastError,
    );
  }
  return restored;
};

const writePersistentCartCache = (map) => {
  if (typeof window === "undefined" || !(map instanceof Map)) return;
  const payload = buildPersistentCartCachePayload(map, {
    maxAgeMs: POS_CART_CACHE_MAX_AGE_MS,
  });
  const serialized = JSON.stringify(payload);
  let savedCopies = 0;
  let lastError = null;
  [
    [() => window.localStorage, POS_CART_CACHE_STORAGE_KEY],
    [() => window.sessionStorage, POS_CART_CACHE_SESSION_BACKUP_KEY],
  ].forEach(([getStorage, key]) => {
    try {
      const storage = getStorage();
      if (!storage) throw new Error("Web Storage indisponible");
      storage?.setItem(key, serialized);
      savedCopies += 1;
    } catch (error) {
      lastError = error;
    }
  });
  if (savedCopies === 0) {
    console.error(
      "[cart-cache] Échec de sauvegarde du panier local:",
      lastError?.message || lastError,
    );
  } else if (lastError) {
    console.warn(
      "[cart-cache] Une seule copie du panier a pu être sauvegardée:",
      lastError?.message || lastError,
    );
  }
};

const readFastFoodCartSnapshotFromStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    const storageKey = getCounterRecoveryStorageKey();
    const legacyKey = "moo_pos_fastfood_cart_v1";
    const raw =
      window.localStorage?.getItem(storageKey) ||
      window.sessionStorage?.getItem(getCounterRecoverySessionBackupKey()) ||
      window.localStorage?.getItem(legacyKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const timestamp = Number(parsed?.timestamp || 0);
    const items = Array.isArray(parsed?.items) ? parsed.items : [];
    if (items.length === 0) return null;
    const normalized = {
      ...parsed,
      items,
      timestamp: timestamp || Date.now(),
    };
    if (!window.localStorage?.getItem(storageKey)) {
      window.localStorage?.setItem(storageKey, JSON.stringify(normalized));
      window.localStorage?.removeItem(legacyKey);
    }
    return normalized;
  } catch (_error) {
    return null;
  }
};

const writeFastFoodCartSnapshotToStorage = (snapshot) => {
  if (typeof window === "undefined") return false;
  if (!snapshot || !Array.isArray(snapshot.items) || snapshot.items.length === 0) {
    clearFastFoodCartSnapshotFromStorage();
    return true;
  }
  const serialized = JSON.stringify({
    version: 1,
    ...snapshot,
    timestamp: Number(snapshot.timestamp || 0) || Date.now(),
  });
  let savedCopies = 0;
  [
    [window.localStorage, getCounterRecoveryStorageKey()],
    [window.sessionStorage, getCounterRecoverySessionBackupKey()],
  ].forEach(([storage, key]) => {
    try {
      storage?.setItem(key, serialized);
      if (storage?.getItem(key) === serialized) savedCopies += 1;
    } catch (_error) {
      // L'autre copie peut encore réussir.
    }
  });
  if (savedCopies === 0) {
    window.dispatchEvent(new Event("moo-counter-recovery-storage-error"));
    return false;
  }
  return true;
};

const clearFastFoodCartSnapshotFromStorage = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.removeItem(getCounterRecoveryStorageKey());
    window.sessionStorage?.removeItem(getCounterRecoverySessionBackupKey());
  } catch (_error) {
    // ignore persistence errors
  }
};

const normalizePackageServerParticipation = (pkg) => {
  if (typeof pkg?.server_participation_enabled === "boolean") {
    return pkg.server_participation_enabled;
  }
  if (typeof pkg?.serverParticipationEnabled === "boolean") {
    return pkg.serverParticipationEnabled;
  }
  if (typeof pkg?.server_enabled === "boolean") {
    return pkg.server_enabled;
  }
  return false;
};

const resolveServerParticipationFromPackages = (packages = [], activeId = "") => {
  if (!Array.isArray(packages) || packages.length === 0) return false;
  const selected =
    packages.find((pkg) => String(pkg?.id) === String(activeId || "")) ||
    packages[0] ||
    null;
  return normalizePackageServerParticipation(selected);
};

const normalizePackageMenuEnabled = (pkg) => {
  if (typeof pkg?.menu_enabled === "boolean") return pkg.menu_enabled;
  if (typeof pkg?.menuEnabled === "boolean") return pkg.menuEnabled;
  return false;
};

const normalizePackageMenuMode = (pkg) => {
  const raw = String(pkg?.menu_mode ?? pkg?.menuMode ?? "catalog")
    .trim()
    .toLowerCase();
  return raw === "ordering" ? "ordering" : "catalog";
};

const resolveOnlineMenuOrderingFromPackages = (packages = [], activeId = "") => {
  if (!Array.isArray(packages) || packages.length === 0) return false;
  const selected =
    packages.find((pkg) => String(pkg?.id) === String(activeId || "")) ||
    packages[0] ||
    null;
  if (!selected) return false;
  return (
    normalizePackageMenuEnabled(selected) &&
    normalizePackageMenuMode(selected) === "ordering"
  );
};

const normalizePackageFastFoodEnabled = (pkg) => {
  if (typeof pkg?.fast_food_enabled === "boolean") {
    return pkg.fast_food_enabled;
  }
  if (typeof pkg?.fastFoodEnabled === "boolean") {
    return pkg.fastFoodEnabled;
  }
  return false;
};

const normalizePackageServiceMode = (pkg) => {
  const raw = String(pkg?.service_mode ?? pkg?.serviceMode ?? "")
    .trim()
    .toLowerCase();
  if (["restaurant", "fastfood", "hybrid"].includes(raw)) return raw;
  return normalizePackageFastFoodEnabled(pkg) ? "fastfood" : "restaurant";
};

const resolveServiceModeFromPackages = (packages = [], activeId = "") => {
  if (!Array.isArray(packages) || packages.length === 0) return "restaurant";
  const selected =
    packages.find((pkg) => String(pkg?.id) === String(activeId || "")) ||
    packages[0] ||
    null;
  return normalizePackageServiceMode(selected);
};

const normalizePackageCourseManagement = (pkg) =>
  typeof pkg?.course_management_enabled === "boolean"
    ? pkg.course_management_enabled
    : Boolean(pkg?.courseManagementEnabled);

const resolveCourseManagementFromPackages = (packages = [], activeId = "") => {
  if (!Array.isArray(packages) || packages.length === 0) return false;
  const selected =
    packages.find((pkg) => String(pkg?.id) === String(activeId || "")) ||
    packages[0] ||
    null;
  return normalizePackageCourseManagement(selected);
};

const HeaderLiveClock = React.memo(() => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), HEADER_CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="text-right">
      <div className="text-2xl font-bold">{now.toLocaleTimeString("fr-FR")}</div>
      <div className="text-white/70 text-sm">{now.toLocaleDateString("fr-FR")}</div>
    </div>
  );
});

const TableCard = React.memo(
  ({
    table,
    displayName,
    isSelected,
    isMerged,
    customerCount,
    reservationLabel,
    draftPendingCount = 0,
    isChecking,
    onSelect,
  }) => {
    const handleClick = useCallback(() => {
      onSelect(table);
    }, [onSelect, table]);

    return (
      <button
        type="button"
        onClick={handleClick}
        aria-busy={isChecking ? "true" : undefined}
        className={`table-card gpu-stable-card relative p-4 rounded-2xl border ${
          isSelected
            ? "border-amber-200 bg-gradient-to-br from-amber-100 to-orange-50 shadow-lg shadow-amber-200/60"
            : isMerged
              ? "border-orange-200 bg-orange-50/80"
              : table.status === "occupied"
                ? "border-red-200 bg-red-50/80"
                : "border-emerald-200 bg-emerald-50/80"
        }`}
      >
        {isMerged && (
          <span className="absolute -top-2 left-3 z-10 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-orange-100 text-orange-700 border border-orange-200 shadow">
            Fus.
          </span>
        )}
        <div className="flex items-start justify-between mt-1">
          <div>
            <div className="font-bold text-lg text-slate-900">{displayName}</div>
            <div className="text-sm mt-1 text-slate-700">
              {table.status === "occupied" ? `${customerCount || "?"} pers.` : "Libre"}
            </div>
            {reservationLabel && (
              <div className="mt-1 text-[10px] font-semibold text-sky-700">
                En cours : {reservationLabel}
              </div>
            )}
            {draftPendingCount > 0 && (
              <div className="mt-1 text-[10px] font-semibold text-amber-700">
                {draftPendingCount} article{draftPendingCount > 1 ? "s" : ""}{" "}
                non envoyé{draftPendingCount > 1 ? "s" : ""}
              </div>
            )}
            {isChecking && (
              <div className="mt-1 text-[10px] font-semibold text-amber-700">
                Vérification…
              </div>
            )}
          </div>
        </div>
      </button>
    );
  },
  (prevProps, nextProps) =>
    prevProps.table?.id === nextProps.table?.id &&
    prevProps.table?.status === nextProps.table?.status &&
    prevProps.table?.number === nextProps.table?.number &&
    prevProps.displayName === nextProps.displayName &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isMerged === nextProps.isMerged &&
    prevProps.customerCount === nextProps.customerCount &&
    prevProps.reservationLabel === nextProps.reservationLabel &&
    prevProps.draftPendingCount === nextProps.draftPendingCount &&
    prevProps.isChecking === nextProps.isChecking,
);

const PRODUCT_CARD_NAME_MAX_CHARS = 48;
const clampProductCardName = (value) => {
  const normalized = String(value || "").trim().replace(/\s+/g, " ");
  if (!normalized) return "Article";
  if (normalized.length <= PRODUCT_CARD_NAME_MAX_CHARS) return normalized;
  return `${normalized.slice(0, PRODUCT_CARD_NAME_MAX_CHARS - 1).trimEnd()}…`;
};

const ProductCard = React.memo(
  ({
    product,
    isDarkTheme,
    isBoutique,
    isDisabled,
    priceLabel,
    primaryColor,
    secondaryColor,
    tertiaryColor,
    onSelect,
  }) => {
    const displayName = clampProductCardName(product?.name);
    const handleClick = useCallback(() => {
      onSelect(product);
    }, [onSelect, product]);

    return (
      <button
        type="button"
        onClick={handleClick}
        className={`product-card gpu-stable-card relative rounded-3xl overflow-hidden group flex flex-col h-72 ${
          isDisabled ? "opacity-60 cursor-not-allowed" : "hover:border-amber-300/60"
        } ${isDarkTheme ? "border bg-[#0f172a]" : "border-2 bg-white"}`}
        style={
          isDarkTheme
            ? {
                background: "linear-gradient(160deg, #0f172a 0%, #0b1320 100%)",
                borderColor: "rgba(148,163,184,0.5)",
              }
            : {
                borderColor:
                  product.destination === "cuisine"
                    ? secondaryColor
                    : product.destination === "bar"
                      ? primaryColor
                      : product.destination === "none"
                        ? "rgba(148,163,184,0.45)"
                        : product.destination_color || "#8b5cf6",
                background: "linear-gradient(145deg, #ffffff 0%, #fafafa 100%)",
              }
        }
        disabled={isDisabled}
      >
        <div
          className={`product-card-media relative h-40 w-full overflow-hidden rounded-t-3xl flex items-center justify-center flex-shrink-0 ${
            isDarkTheme
              ? "bg-gradient-to-br from-slate-800 to-slate-900"
              : "bg-gradient-to-br from-gray-100 to-gray-200"
          }`}
        >
          <img
            src={product.image_path || product.image_url || PRODUCT_PLACEHOLDER}
            alt={displayName}
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            className="product-image w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = PRODUCT_PLACEHOLDER;
            }}
          />

          {!isBoutique && (
            <div className="product-card-destination-badges absolute top-3 right-3 flex items-center gap-2">
              {product.destination !== "none" && (
                <div
                  className="px-3 py-1 rounded-full text-xs font-bold text-white shadow-md"
                  style={{
                    backgroundColor:
                      product.destination_color ||
                      (product.destination === "cuisine" ? "#ef4444" : "#0ea5e9"),
                  }}
                >
                  {product.destination_name ||
                    (product.destination === "cuisine"
                      ? "Cuisine"
                      : product.destination === "bar"
                        ? "Bar"
                        : product.destination)}
                </div>
              )}
              {product.is_daily_menu_active && (
                <div
                  className="p-2 rounded-full text-white shadow-md flex items-center justify-center"
                  style={{ backgroundColor: tertiaryColor || "#fbbf24" }}
                  title="Prix menu du jour appliqué"
                >
                  <Sun size={14} />
                </div>
              )}
            </div>
          )}

          {(product.product_type === "variable" ||
            (!isBoutique &&
              (!!product.has_accompaniments || !!product.has_supplements))) && (
            <div className="product-card-option-badges absolute top-3 left-3 flex space-x-1">
              {product.product_type === "variable" && (
                <div
                  className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  title="Produit variable"
                >
                  V
                </div>
              )}
              {!isBoutique && !!product.has_accompaniments && (
                <div
                  className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  title="Accompagnements disponibles"
                >
                  A
                </div>
              )}
              {!isBoutique && !!product.has_supplements && (
                <div
                  className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  title="Suppléments disponibles"
                >
                  S
                </div>
              )}
            </div>
          )}
        </div>

        <div className="product-card-body p-4 flex-1 flex flex-col justify-between">
          <h3
            className={`font-bold text-lg mb-3 line-clamp-2 text-center flex-1 flex items-center justify-center break-words leading-tight ${
              isDarkTheme ? "text-white" : "text-gray-800"
            }`}
            title={product?.name || ""}
          >
            {displayName}
          </h3>

          <div className="text-center mt-auto">
            <div
              className={`product-card-price text-xl font-bold ${isDarkTheme ? "text-amber-300" : ""}`}
              style={!isDarkTheme ? { color: secondaryColor } : {}}
            >
              {priceLabel}
            </div>
          </div>
        </div>

        {isDisabled && (
          <div className="absolute inset-0 bg-gray-900 bg-opacity-50 rounded-3xl flex items-center justify-center">
            <div className="text-white text-center">
              <Users size={32} className="mx-auto mb-2" />
              <p className="text-sm font-semibold">Sélectionnez une table</p>
            </div>
          </div>
        )}
      </button>
    );
  },
  (prevProps, nextProps) =>
    prevProps.product?.id === nextProps.product?.id &&
    prevProps.product?.name === nextProps.product?.name &&
    prevProps.product?.image_path === nextProps.product?.image_path &&
    prevProps.product?.image_url === nextProps.product?.image_url &&
    prevProps.product?.destination === nextProps.product?.destination &&
    prevProps.product?.product_type === nextProps.product?.product_type &&
    prevProps.product?.price === nextProps.product?.price &&
    prevProps.product?.product_price === nextProps.product?.product_price &&
    (prevProps.product?.variations?.length || 0) ===
      (nextProps.product?.variations?.length || 0) &&
    prevProps.product?.has_accompaniments === nextProps.product?.has_accompaniments &&
    prevProps.product?.has_supplements === nextProps.product?.has_supplements &&
    prevProps.product?.is_daily_menu_active === nextProps.product?.is_daily_menu_active &&
    prevProps.isDarkTheme === nextProps.isDarkTheme &&
    prevProps.isBoutique === nextProps.isBoutique &&
    prevProps.isDisabled === nextProps.isDisabled &&
    prevProps.priceLabel === nextProps.priceLabel &&
    prevProps.primaryColor === nextProps.primaryColor &&
    prevProps.secondaryColor === nextProps.secondaryColor &&
    prevProps.tertiaryColor === nextProps.tertiaryColor,
);

const MOOCaisseInterface = ({ currentUser: authenticatedUser = null }) => {
  const normalizeRoleKey = (value) =>
    String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "");

  const location = useLocation();
  const navigate = useNavigate();
  const isSuperPath = location.pathname.startsWith("/superadmin");
  const [currentUser, setCurrentUser] = useState(() => {
    if (authenticatedUser?.id) return authenticatedUser;
    try {
      const user = localStorage.getItem("moo_user");
      return user ? JSON.parse(user) : null;
    } catch (e) {
      return null;
    }
  });
  useEffect(() => {
    if (authenticatedUser?.id) {
      setCurrentUser(authenticatedUser);
    }
  }, [authenticatedUser]);
  const currentRole = String(currentUser?.role || "").toLowerCase();
  const isAdminUser = currentRole === "admin";
  const isServerRole = currentRole === "server" || currentRole === "serveur";
  const canUseCashSession = canUseCashSessionForRole(currentRole);
  const canAcceptOnlineOrders = [
    "server",
    "serveur",
    "cashier",
    "caissier",
    "caisse",
    "manager",
    "admin",
  ].includes(currentRole);
  const canRejectOnlineOrders = ["manager", "admin"].includes(currentRole);
  // Les options de licence et de parcours démarrent fermées. Le cache local
  // reste une optimisation de transport, jamais une source d'autorité.
  const [serverParticipationEnabled, setServerParticipationEnabled] =
    useState(false);
  const [onlineMenuOrderingEnabled, setOnlineMenuOrderingEnabled] =
    useState(false);
  const [serviceMode, setServiceMode] = useState(null);
  const [courseManagementAvailable, setCourseManagementAvailable] =
    useState(false);
  const [packageConfigHydrated, setPackageConfigHydrated] = useState(false);
  const isServerParticipationMode =
    isServerRole && Boolean(serverParticipationEnabled);
  const [onlineOrders, setOnlineOrders] = useState([]);
  const [onlineOrdersLoading, setOnlineOrdersLoading] = useState(false);
  const [onlineOrdersError, setOnlineOrdersError] = useState("");
  const [onlineUnreadCount, setOnlineUnreadCount] = useState(0);
  const [onlineActionLoadingId, setOnlineActionLoadingId] = useState(null);
  const [onlineAssignOrder, setOnlineAssignOrder] = useState(null);
  const [onlineAssignTableId, setOnlineAssignTableId] = useState("");
  const [onlineAssignCovers, setOnlineAssignCovers] = useState("");
  const [onlineRejectDraft, setOnlineRejectDraft] = useState({ id: null, reason: "" });
  const [adminCashDrawerRequested, setAdminCashDrawerRequested] = useState(() => {
    const flag = localStorage.getItem("moo_admin_cash_drawer");
    if (flag === "1") {
      localStorage.removeItem("moo_admin_cash_drawer");
      return true;
    }
    return false;
  });
  const [showAdminCashDrawer, setShowAdminCashDrawer] = useState(false);
  const [adminLastAmount, setAdminLastAmount] = useState(0);
  const [restaurantInfo, setRestaurantInfo] = useState({
    name: "Restaurant",
  });
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const isBoutique = businessType === "boutique";
  const isCounterOperatorRole = [
    "cashier",
    "caissier",
    "caisse",
    "manager",
    "admin",
  ].includes(currentRole);
  const serviceModeHydrated = ["restaurant", "fastfood", "hybrid"].includes(
    serviceMode,
  );
  const hasTableService =
    !isBoutique && serviceModeHydrated && serviceMode !== "fastfood";
  const hasCounterService =
    !isBoutique &&
    serviceModeHydrated &&
    isCounterOperatorRole &&
    (serviceMode === "fastfood" || serviceMode === "hybrid");
  const isFastFoodOnly = hasCounterService && !hasTableService;
  const isHybridMode = hasCounterService && hasTableService;
  const [activeOrderContext, setActiveOrderContext] = useState("table");
  const isCounterContext = hasCounterService && activeOrderContext === "counter";
  // Compatibility alias for the existing, proven Fast-food transaction path.
  const isFastFoodMode = isCounterContext;
  const [foreignCounterRecovery, setForeignCounterRecovery] = useState(() => {
    const recovery = readFastFoodCartSnapshotFromStorage();
    if (!recovery?.items?.length) return null;
    const ownerId = recovery.origin_user_id;
    return ownerId && currentUser?.id && String(ownerId) !== String(currentUser.id)
      ? recovery
      : null;
  });
  const [openCounterOrders, setOpenCounterOrders] = useState([]);
  const confirmedCounterOrderIdsRef = useRef(new Set());
  const [counterDispatchPending, setCounterDispatchPending] = useState(() => {
    const recovery = readFastFoodCartSnapshotFromStorage();
    return recovery?.payment_confirmed && recovery?.created_order_id
      ? recovery
      : null;
  });
  const [counterRecoveryStorageError, setCounterRecoveryStorageError] =
    useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [showAllCategories, setShowAllCategories] = useState(true);
  const [currentOrder, setCurrentOrder] = useState({ items: [], total: 0 });
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableAccessCheckingId, setTableAccessCheckingId] = useState(null);
  const [selectedTableAccessConflict, setSelectedTableAccessConflict] =
    useState(null);
  const [isReclaimingOwnTable, setIsReclaimingOwnTable] = useState(false);
  const [activeAggregator, setActiveAggregator] = useState(null);
  const [aggregatorSettings, setAggregatorSettings] = useState({
    aggregators: false,
    yango: false,
    glovo: false,
  });
  const isAggregatorOrderMode = Boolean(activeAggregator);
  const canOrderWithoutTable = isFastFoodMode || isAggregatorOrderMode;
  const scannerBufferTimeoutRef = useRef(null);
  const counterDispatchRetryRef = useRef(false);

  useEffect(() => {
    if (isBoutique) return;
    if (isFastFoodOnly) {
      setActiveOrderContext("counter");
    } else if (serviceMode === "hybrid") {
      const recovery = readFastFoodCartSnapshotFromStorage();
      const sameOwner =
        !recovery?.origin_user_id ||
        !currentUser?.id ||
        String(recovery.origin_user_id) === String(currentUser.id);
      setActiveOrderContext(
        sameOwner && (recovery?.items || []).length > 0 ? "counter" : "table",
      );
    } else if (serviceMode === "restaurant") {
      setActiveOrderContext("table");
    }
  }, [currentUser?.id, isBoutique, isFastFoodOnly, serviceMode]);

  useEffect(() => {
    if (!isBoutique || typeof window === "undefined") return;

    const handleScannerKey = (event) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable =
        target?.isContentEditable ||
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select";

      if (isEditable) return;

      if (event.key === "Escape") {
        setSearchTerm("");
        return;
      }

      if (event.key === "Backspace") {
        setSearchTerm((prev) => prev.slice(0, -1));
        return;
      }

      if (event.key.length === 1 && /[0-9a-zA-Z._-]/.test(event.key)) {
        setSearchTerm((prev) => {
          const next = `${prev}${event.key}`.slice(0, 64);
          return next;
        });
        if (scannerBufferTimeoutRef.current) {
          clearTimeout(scannerBufferTimeoutRef.current);
        }
        scannerBufferTimeoutRef.current = setTimeout(() => {
          scannerBufferTimeoutRef.current = null;
        }, 1200);
      }
    };

    window.addEventListener("keydown", handleScannerKey);
    return () => {
      window.removeEventListener("keydown", handleScannerKey);
    };
  }, [isBoutique]);

  // États pour le système de livraison
  const [deliveryMode, setDeliveryMode] = useState(""); // 'delivery' ou 'takeaway'
  const [deliveryMethod, setDeliveryMethod] = useState("Direct"); // 'Direct', 'Yango', 'Glovo', etc.
  const [deliveryCustomer, setDeliveryCustomer] = useState(null);
  const [deliveryCustomerSearch, setDeliveryCustomerSearch] = useState("");
  const [deliveryCustomers, setDeliveryCustomers] = useState([]);
  const [deliveryMethods, setDeliveryMethods] = useState([
    "Direct",
    "Yango",
    "Glovo",
  ]);
  // Nouveaux états pour la gestion des références
  const [deliveryRef, setDeliveryRef] = useState("");
  const [deliveryMethodCodes] = useState({
    Yango: "YG",
    Glovo: "GL",
  });
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [hiddenDeliveryOrderIds, setHiddenDeliveryOrderIds] = useState(() => {
    try {
      const stored = localStorage.getItem("moo_hidden_delivery_orders");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed.filter(
            (value) => typeof value === "number" || typeof value === "string",
          );
        }
      }
    } catch (_) {}
    return [];
  });
  const [currentDeliveryNumber, setCurrentDeliveryNumber] = useState(1);
  const [deliveryOrderCounter, setDeliveryOrderCounter] = useState({});

  // États séparés pour les commandes de livraison
  const [currentDeliveryOrder, setCurrentDeliveryOrder] = useState({
    items: [],
    total: 0,
  });
  const [existingDeliveryItems, setExistingDeliveryItems] = useState([]);
  const [selectedDeliveryOrder, setSelectedDeliveryOrder] = useState(null);

  // États pour gérer les onglets clients comme des tables virtuelles
  const [clientTabs, setClientTabs] = useState([]); // Liste des onglets clients ouverts
  const [activeClientTab, setActiveClientTab] = useState(null); // Onglet client actuellement sélectionné
  const [showDeliveryList, setShowDeliveryList] = useState(true);
  const [autoSelectDeliveryTab, setAutoSelectDeliveryTab] = useState(true);

  // Nouveaux états pour les améliorations
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountType, setDiscountType] = useState("percentage"); // 'percentage' ou 'amount'
  const [additionalFees, setAdditionalFees] = useState(0);
  const [feesType, setFeesType] = useState("amount"); // 'percentage' ou 'amount'

  // En mode superadmin, on laisse la caisse accessible en LIVE.

  // États pour les vraies données API
  const [tables, setTables] = useState([]);
  const [floorPlanEnabled, setFloorPlanEnabled] = useState(false);
  const [printAreaOnDocuments, setPrintAreaOnDocuments] = useState(false);
  const [visibleZones, setVisibleZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [floorPlanHydrated, setFloorPlanHydrated] = useState(false);
  const [floorPlanLoadState, setFloorPlanLoadState] = useState("loading");
  const [stewardModeEnabled, setStewardModeEnabled] = useState(false);
  const [stewardReplacesServer, setStewardReplacesServer] = useState(true);
  const [stewardOverview, setStewardOverview] = useState({
    stewards: [],
    unassigned: null,
  });
  const [selectedStewardKey, setSelectedStewardKey] = useState(null);
  const floorPlanRequestSequenceRef = useRef(0);
  const floorPlanFailureCountRef = useRef(0);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userSessionHydrated, setUserSessionHydrated] = useState(false);
  const [initialDataHydrated, setInitialDataHydrated] = useState(false);
  const [bootHydrated, setBootHydrated] = useState(false);
  const [shellMotionReady, setShellMotionReady] = useState(false);
  const [apiStatus, setApiStatus] = useState("disconnected");
  const [reconcilingVisibleTableId, setReconcilingVisibleTableId] =
    useState(null);
  const [draftPreservedNotice, setDraftPreservedNotice] = useState("");
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  const loadOpenCounterOrdersRef = useRef(null);
  const [isDualScreenAllowed, setIsDualScreenAllowed] = useState(false);
  const [isDualScreenActive, setIsDualScreenActive] = useState(() => {
    if (typeof localStorage !== "undefined") {
      return localStorage.getItem("moo_dual_screen_active") === "true";
    }
    return false;
  });
  const [isAppFullscreen, setIsAppFullscreen] = useState(() =>
    typeof document !== "undefined" ? Boolean(document.fullscreenElement) : false,
  );
  const dualScreenWindowRef = useRef(null);
  const dualScreenChannelRef = useRef(null);
  const openDualScreenWindowRef = useRef(null);
  const dualScreenAutoOpenAttemptedRef = useRef(false);
  const lastSendAtRef = useRef(new Map());
  const lastSelectAtRef = useRef(new Map());
  const lastExistingItemsRef = useRef(new Map()); // tableId -> {items, isDelivery}
  const persistentCartCacheRef = useRef(readPersistentCartCache());
  const draftRecoveryTableIdsRef = useRef(
    new Set(
      Array.from(persistentCartCacheRef.current.entries())
        .filter(([, entry]) => entry?.recoveryRequired)
        .map(([key]) => String(key).replace(/^table:/, "")),
    ),
  );
  const draftPreservedNoticeTimerRef = useRef(null);
  const tableCartSyncRef = useRef(createTableCartSyncTracker());
  useEffect(
    () => () => {
      if (draftPreservedNoticeTimerRef.current) {
        clearTimeout(draftPreservedNoticeTimerRef.current);
      }
    },
    [],
  );
  const tableCloseInFlightRef = useRef(new Map());
  const tableSelectionRequestRef = useRef(0);
  const tableAccessCheckingIdRef = useRef(null);
  const socketRuntimeRef = useRef({});
  const tableCartFlushHandlerRef = useRef(async () => {});
  const metadataFlushHandlerRef = useRef(async () => {});
  const tableCartEventCoordinatorRef = useRef(null);
  if (!tableCartEventCoordinatorRef.current) {
    tableCartEventCoordinatorRef.current = createFixedWindowCoordinator({
      windowMs: 150,
      merge: mergeRealtimeTableIntent,
      onFlush: (tableId, intent) =>
        tableCartFlushHandlerRef.current(tableId, intent),
    });
  }
  const metadataRefreshCoordinatorRef = useRef(null);
  if (!metadataRefreshCoordinatorRef.current) {
    metadataRefreshCoordinatorRef.current = createFixedWindowCoordinator({
      windowMs: 300,
      merge: mergeMetadataTableIds,
      onFlush: (_key, tableIds) =>
        metadataFlushHandlerRef.current(Array.from(tableIds || [])),
    });
  }
  const destructiveCartEvidenceRef = useRef(null);
  if (!destructiveCartEvidenceRef.current) {
    destructiveCartEvidenceRef.current =
      createDestructiveCartEvidenceRegistry({ ttlMs: 15000 });
  }
  const cartCacheReconciliationRef = useRef({
    inFlight: false,
    pending: false,
    pendingReason: null,
    promise: null,
  });
  const cartCacheReconciliationRetryRef = useRef(new Map());
  const cartCacheInitialPassDoneRef = useRef(false);
  const runCartCacheReconciliationPassRef = useRef(async () => false);
  const reconcilingVisibleTableIdRef = useRef(null);
  const reconcilingVisibleRunSequenceRef = useRef(0);
  const loadItemsRetryRef = useRef(new Map()); // tableId -> last retry timestamp
  const loadTableItemsForPaymentRef = useRef(async () => []);
  const visibleTableCartSyncRef = useRef({
    tableId: null,
    timer: null,
  });
  const optimisticTableStatusRef = useRef(new Map()); // tableId -> {status:"occupied", ts}
  const recentlyPaidTablesRef = useRef(new Map()); // table -> commandes payées + expiration
  const forcedFreeTablesRef = useRef(new Set()); // tables à forcer en mode libre (post-paiement)
  const lastPaymentAtRef = useRef(0);
  const sessionSalesByMethodRef = useRef(new Map());
  const localSalesTotalRef = useRef(0);
  const hasLocalSalesRef = useRef(false);
  const lastSessionIdRef = useRef(null);
  const cashLogoutInputsDirtyRef = useRef(false);
  const refreshProductsRef = useRef(() => {});
  const emptyOrdersStreakRef = useRef(new Map());
  const loadBasicDataRef = useRef(() => {});
  const basicDataLoadingRef = useRef(false);
  const nonCriticalHydrationInFlightRef = useRef(false);
  const apiRetryTimerRef = useRef(null);
  const apiRetryAttemptsRef = useRef(0);
  const isResolvingSessionRef = useRef(false);
  const firstApiConnectedMarkedRef = useRef(false);
  const firstPaymentMetricMarkedRef = useRef(false);
  const firstUiReadyMarkedRef = useRef(false);
  const onlineOrdersPollTimerRef = useRef(null);
  const onlineOrderSoundedRef = useRef(new Set());
  const onlineOrderAudioRef = useRef(null);
  const loadTablesRef = useRef(async () => false);
  const loadTablesRequestSequenceRef = useRef(0);
  const loadTablesScheduleTimerRef = useRef(null);
  const loadTablesInFlightRef = useRef(false);
  const loadTablesPendingRef = useRef(false);
  const loadTablesForceRefreshPendingRef = useRef(false);
  const manualCloudSyncInFlightRef = useRef(false);
  const lastManualCloudSyncAtRef = useRef(0);
  const lastLoadTablesRunRef = useRef(0);
  const unpaidTableSnapshotRef = useRef({ ts: 0, ids: new Set() });
  const LOAD_TABLES_COOLDOWN_MS = UI_PERF_PROFILE.loadTablesCooldownMs;
  const appBootPerfRef = useRef(
    typeof performance !== "undefined" ? performance.now() : Date.now(),
  );

  const getRecentlyPaidGuard = (tableId) =>
    recentlyPaidTablesRef.current.get(tableId) ||
    recentlyPaidTablesRef.current.get(String(tableId)) ||
    null;

  const clearRecentlyPaidGuard = (tableId) => {
    const guard = getRecentlyPaidGuard(tableId);
    if (guard?.timer) clearTimeout(guard.timer);
    recentlyPaidTablesRef.current.delete(tableId);
    recentlyPaidTablesRef.current.delete(String(tableId));
    const numericId = Number(tableId);
    if (Number.isFinite(numericId)) {
      recentlyPaidTablesRef.current.delete(numericId);
    }
  };

  const markRecentlyPaidGuard = (tableId, orderIds = []) => {
    if (tableId === null || tableId === undefined) return null;
    clearRecentlyPaidGuard(tableId);
    const paidOrderIds = normalizePaidOrderIds(orderIds);
    const guard = {
      paidOrderIds,
      expiresAt: Date.now() + 20000,
      timer: null,
    };
    guard.timer = setTimeout(() => {
      clearRecentlyPaidGuard(tableId);
      scheduleTableCartRefresh({
        tableId,
        reason: "recent-payment-expired",
        delayMs: 0,
      });
    }, 20000);
    recentlyPaidTablesRef.current.set(tableId, guard);
    recentlyPaidTablesRef.current.set(String(tableId), guard);
    const numericId = Number(tableId);
    if (Number.isFinite(numericId)) {
      recentlyPaidTablesRef.current.set(numericId, guard);
    }
    return guard;
  };

  const clearAllRecentlyPaidGuards = () => {
    const guards = new Set(recentlyPaidTablesRef.current.values());
    guards.forEach((guard) => {
      if (guard?.timer) clearTimeout(guard.timer);
    });
    recentlyPaidTablesRef.current.clear();
  };

  // Nouveaux états pour l'historique des tables
  const [tableOrders, setTableOrders] = useState([]);
  const [showTableHistory, setShowTableHistory] = useState(false);
  const [isAddingToExistingOrder, setIsAddingToExistingOrder] = useState(false);
  const [isOrderActionInProgress, setIsOrderActionInProgress] = useState(false);
  const [orderActionKind, setOrderActionKind] = useState(null); // "send" | "add"
  const orderActionInFlightRef = useRef(false);
  const [isPrintActionInProgress, setIsPrintActionInProgress] = useState(false);
  const printActionInFlightRef = useRef(false);
  const printPromotionContextRef = useRef(null);
  const lastPrintPromotionSignatureRef = useRef("");
  const lastPrintPromotionAtRef = useRef(0);
  const [hasExistingOrders, setHasExistingOrders] = useState(false); // Nouvel état pour suivre si la table a déjà des commandes
  const [existingItemsForPayment, setExistingItemsForPayment] = useState([]); // Articles existants chargés pour le paiement
  const [updatingStatus, setUpdatingStatus] = useState(false); // État pour les animations de mise à jour
  const [expandedOrderId, setExpandedOrderId] = useState(null); // Pour le déploiement des détails

  // États pour la gestion des annulations
  const [cancellationOrder, setCancellationOrder] = useState(null);
  const [managerPassword, setManagerPassword] = useState("");
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [cancellationStep, setCancellationStep] = useState("reason");
  const [cancellationReason, setCancellationReason] = useState("");

  const [cashRegisterLog, setCashRegisterLog] = useState([]);
  const [cashRegisterSummary, setCashRegisterSummary] = useState([]);
  const [showCashJournal, setShowCashJournal] = useState(false);
  const [isCurrentSessionUnlocked, setIsCurrentSessionUnlocked] =
    useState(false);
  const [isJournalUnlocking, setIsJournalUnlocking] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [cashierName] = useState("Caissier");

  // États pour les animations spéciales
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [showErrorAnimation, setShowErrorAnimation] = useState(false);
  const [successAnimationMessage, setSuccessAnimationMessage] = useState("");
  const [errorAnimationMessage, setErrorAnimationMessage] = useState("");
  const [notificationSwipeOffset, setNotificationSwipeOffset] = useState(0);
  const [notificationSwipeDragging, setNotificationSwipeDragging] =
    useState(false);
  const successAnimationTimerRef = useRef(null);
  const errorAnimationTimerRef = useRef(null);
  const notificationSwipeRef = useRef(null);

  // États pour le système intelligent de rafraîchissement

  // États pour la calculatrice
  const [calculatorDisplay, setCalculatorDisplay] = useState("0");
  const [calculatorPrevValue, setCalculatorPrevValue] = useState(null);
  const [calculatorOperation, setCalculatorOperation] = useState(null);
  const [calculatorWaitingForOperand, setCalculatorWaitingForOperand] =
    useState(false);
  const [
    showIntelligentRefreshNotification,
    setShowIntelligentRefreshNotification,
  ] = useState(false);
  const [intelligentRefreshMessage, setIntelligentRefreshMessage] =
    useState("");
  const [kdsActionNotice, setKdsActionNotice] = useState(null);
  const selectTableRef = useRef(null);

  // États pour les logs de débogage

  useEffect(() => {
    markPerf("caisse.mount", { path: location.pathname });
    incrementPerfCounter("caisse.mount.count", 1);
  }, [location.pathname]);

  useEffect(() => {
    if (firstUiReadyMarkedRef.current) return;
    if (
      (Array.isArray(products) && products.length > 0) ||
      (Array.isArray(categories) && categories.length > 0) ||
      (Array.isArray(tables) && tables.length > 0)
    ) {
      firstUiReadyMarkedRef.current = true;
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      recordPerfMetric(
        "caisse.time_to_ui_ready_ms",
        now - appBootPerfRef.current,
        {
          products: Array.isArray(products) ? products.length : 0,
          categories: Array.isArray(categories) ? categories.length : 0,
          tables: Array.isArray(tables) ? tables.length : 0,
        },
      );
    }
  }, [products, categories, tables]);

  // Charger les infos du restaurant sauvegardées dans l'admin
  useEffect(() => {
    const applyRestaurantInfo = (data) => {
      if (!data || typeof data !== "object") return;
      setBusinessType(extractBusinessType(data));
      const name =
        data?.name ||
        data?.restaurant_name ||
        data?.restaurantName ||
        data?.etablissement ||
        data?.etablissement_name ||
        "";
      const address = data?.address || data?.locality || data?.city || data?.location || "";
      const phone = data?.phone || data?.telephone || "";

      const next = {};
      if (name) next.name = String(name).trim();
      if (address) next.address = String(address).trim();
      if (phone) next.phone = String(phone).trim();

      if (Object.keys(next).length > 0) {
        setRestaurantInfo((prev) => ({ ...prev, ...next }));
      }
    };

    const loadRestaurantInfo = async () => {
      try {
        const res = await fetchWithTimeout("/api/restaurant-info");
        if (res.ok) {
          const data = await res.json();
          applyRestaurantInfo(data);
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("restaurant-info", JSON.stringify(data || {}));
          }
          return;
        }
      } catch (error) {
        console.warn("⚠�? Chargement restaurant-info API:", error);
      }
      try {
        const stored = localStorage.getItem("restaurant-info");
        if (stored) {
          const parsed = JSON.parse(stored);
          applyRestaurantInfo(parsed);
        }
      } catch (error) {
        console.error("Erreur chargement restaurant-info", error);
      }
    };
    loadRestaurantInfo();
  }, []);

  useEffect(() => {
    ensureDualScreenChannel();
    return () => {
      if (dualScreenChannelRef.current) dualScreenChannelRef.current.close();
      closeDualScreenWindow();
    };
  }, []);

  useEffect(() => {
    const syncBrowserFullscreen = () =>
      setIsAppFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", syncBrowserFullscreen);

    let removeElectronListener = null;
    let removeCustomerDisplayListener = null;
    if (window?.electronAPI?.onFullscreenChanged) {
      removeElectronListener = window.electronAPI.onFullscreenChanged(
        setIsAppFullscreen,
      );
      window.electronAPI
        .getFullscreenState?.()
        .then(setIsAppFullscreen)
        .catch(() => {});
    }
    if (window?.electronAPI?.onCustomerDisplayClosed) {
      removeCustomerDisplayListener =
        window.electronAPI.onCustomerDisplayClosed(() => {
          dualScreenWindowRef.current = null;
          setIsDualScreenActive(false);
          localStorage.setItem("moo_dual_screen_active", "false");
        });
    }

    return () => {
      document.removeEventListener("fullscreenchange", syncBrowserFullscreen);
      if (typeof removeElectronListener === "function") {
        removeElectronListener();
      }
      if (typeof removeCustomerDisplayListener === "function") {
        removeCustomerDisplayListener();
      }
    };
  }, []);

  useEffect(() => {
    const loadDualScreenSetting = async () => {
      try {
        // Cache-bust pour éviter un 304 (Not Modified) qui empêcherait .ok
        // et donc l'activation du bouton au premier chargement.
        const res = await fetchWithTimeout(
          `${API_BASE}/kds-settings?_=${Date.now()}`,
        );
        if (res.ok) {
          const data = await res.json();
          const allowed = Boolean(data?.dual_screen_enabled);
          setIsDualScreenAllowed(allowed);
          localStorage.setItem(
            DUAL_SCREEN_ALLOWED_STORAGE_KEY,
            allowed ? "true" : "false",
          );
          if (!allowed && isDualScreenActive) {
            closeDualScreenWindow();
            setIsDualScreenActive(false);
            localStorage.setItem("moo_dual_screen_active", "false");
          }
        }
      } catch (error) {
        console.warn("⚠�? Chargement réglages KDS (double écran):", error);
      }
    };
    loadDualScreenSetting();
  }, [isDualScreenActive]);

  useEffect(() => {
    if (isDualScreenActive) {
      sendDualScreenState();
    }
  }, [isDualScreenActive, selectedTable, currentOrder.items, existingItemsForPayment, restaurantInfo]);

  useEffect(() => {
    if (
      !isDualScreenAllowed ||
      isServerParticipationMode ||
      !window?.electronAPI?.openCustomerDisplay ||
      dualScreenAutoOpenAttemptedRef.current
    ) {
      return;
    }
    dualScreenAutoOpenAttemptedRef.current = true;
    void openDualScreenWindowRef.current?.({ silent: true });
  }, [isDualScreenAllowed, isServerParticipationMode]);

  // Fonction utilitaire pour filtrer les articles annulés de manière consistante
  const isItemCancelled = (item) => {
    // CORRECTION CRITIQUE: Ne pas filtrer les nouveaux articles (sans order_item_id)
    // Les nouveaux articles n'ont pas de status/cancelled_at définis
    if (!item.order_item_id && !item.orderId) {
      return false; // Nouveaux articles jamais considérés comme annulés
    }

    return (
      item.status === "cancelled" ||
      item.status === "canceled" ||
      (item.cancelled_at !== null && item.cancelled_at !== undefined) ||
      (item.cancellation_reason !== null &&
        item.cancellation_reason !== undefined)
    );
  };

  // Fonction utilitaire pour filtrer les articles actifs (non annulés)
  const isItemActive = (item) => {
    return !isItemCancelled(item);
  };

  // 🚨 SYSTÈME DE MÉMOIRE DES ANNULATIONS RÉCENTES
  const [recentCancellations, setRecentCancellations] = useState(new Map());
  const [tableStatesCache, setTableStatesCache] = useState(new Map());
  const tableStatesCacheRef = useRef(new Map());

  // React peut appliquer les setState après un changement de table. La ref garantit
  // qu'un paiement ou un événement socket lit toujours le dernier cache par table.
  const commitTableStatesCache = (nextOrUpdater) => {
    const current = tableStatesCacheRef.current;
    const resolved =
      typeof nextOrUpdater === "function"
        ? nextOrUpdater(current)
        : nextOrUpdater;
    const next = resolved instanceof Map ? resolved : new Map(resolved || []);
    tableStatesCacheRef.current = next;
    setTableStatesCache(new Map(next));
    return next;
  };

  // Fonction pour mémoriser une annulation récente
  const memorizeRecentCancellation = (itemId, tableId, orderInfo) => {
    const cancellationInfo = {
      itemId,
      tableId,
      orderInfo,
      timestamp: Date.now(),
      processed: false,
    };

    setRecentCancellations((prev) => {
      const newMap = new Map(prev);
      newMap.set(itemId, cancellationInfo);
      return newMap;
    });

    // Nettoyer les annulations anciennes (> 5 minutes)
    setTimeout(
      () => {
        setRecentCancellations((prev) => {
          const newMap = new Map(prev);
          newMap.delete(itemId);
          return newMap;
        });
      },
      5 * 60 * 1000,
    );

    cashDebugLog(`🧠 Annulation mémorisée: Item ${itemId} sur table ${tableId}`);

    // Afficher notification système intelligent
    setIntelligentRefreshMessage(
      `🧠 Annulation mémorisée: ${orderInfo.productName}`,
    );
    setShowIntelligentRefreshNotification(true);
    setTimeout(() => setShowIntelligentRefreshNotification(false), 3000);
  };

  const countActiveCartStateItems = (state = {}) => {
    const currentItems = (state?.currentOrder?.items || []).filter(isItemActive);
    const existingItems = normalizeItemsForExisting(state?.existingItems || []).filter(
      isItemActive,
    );
    return currentItems.length + existingItems.length;
  };

  const getTableCartGeneration = (tableId, { isDelivery = false } = {}) => {
    return tableCartSyncRef.current.getGeneration(tableId, { isDelivery });
  };

  const bumpTableCartGeneration = (tableId, { isDelivery = false } = {}) => {
    return tableCartSyncRef.current.bumpGeneration(tableId, { isDelivery });
  };

  const getActiveLocalCartSignalForTable = (
    tableId,
    { isDelivery = false } = {},
  ) => {
    if (!tableId) {
      return {
        activeCount: 0,
        pendingCount: 0,
        existingCount: 0,
        sources: [],
      };
    }
    const tableKey = String(tableId);
    const sources = [];
    let activeCount = 0;
    let pendingCount = 0;
    let existingCount = 0;

    const addCount = (label, count, kind) => {
      const value = Number(count) || 0;
      if (value <= 0) return;
      activeCount += value;
      if (kind === "pending") pendingCount += value;
      if (kind === "existing") existingCount += value;
      sources.push(`${label}:${value}`);
    };

    const selected = selectedTableRef.current;
    const selectedMatches =
      selected &&
      String(selected.id) === tableKey &&
      Boolean(selected.isDelivery) === Boolean(isDelivery);
    if (selectedMatches) {
      addCount(
        "selected-pending",
        (isDelivery
          ? currentDeliveryOrderRef.current?.items || []
          : currentOrderRef.current?.items || []
        ).filter(isItemActive).length,
        "pending",
      );
      addCount(
        "selected-existing",
        normalizeItemsForExisting(
          isDelivery
            ? existingDeliveryItemsRef.current || []
            : existingItemsForPaymentRef.current || [],
        ).filter(isItemActive).length,
        "existing",
      );
    }

    const cachedState =
      tableStatesCacheRef.current.get(tableId) ||
      tableStatesCacheRef.current.get(tableKey);
    addCount(
      "cache-pending",
      (cachedState?.currentOrder?.items || []).filter(isItemActive).length,
      "pending",
    );
    addCount(
      "cache-existing",
      normalizeItemsForExisting(cachedState?.existingItems || []).filter(
        isItemActive,
      ).length,
      "existing",
    );

    const known =
      lastExistingItemsRef.current.get(tableId) ||
      lastExistingItemsRef.current.get(tableKey);
    if (known && Boolean(known.isDelivery) === Boolean(isDelivery)) {
      addCount(
        "known-existing",
        normalizeItemsForExisting(known.items || []).filter(isItemActive).length,
        "existing",
      );
    }

    const persistedState = persistentCartCacheRef.current.get(
      buildPersistentCartKey(tableId, isDelivery),
    );
    addCount(
      "persisted-pending",
      (persistedState?.pendingItems || []).filter(isItemActive).length,
      "pending",
    );
    addCount(
      "persisted-existing",
      normalizeItemsForExisting(persistedState?.items || []).filter(isItemActive)
        .length,
      "existing",
    );

    if (draftRecoveryTableIdsRef.current.has(tableKey)) {
      return {
        activeCount: existingCount,
        pendingCount: 0,
        existingCount,
        recoveryPendingCount: pendingCount,
        sources,
      };
    }
    return { activeCount, pendingCount, existingCount, sources };
  };

  // Cache uniquement les états utiles. Un cache vide sur table occupée est la source
  // principale des paniers fantômes après timeout/réveil réseau.
  const updateTableStateCache = (tableId, state, options = {}) => {
    if (!tableId) return;
    const isDelivery = Boolean(options?.isDelivery);
    const activeCount = countActiveCartStateItems(state);
    const selectedTarget = Boolean(
      selectedTableRef.current &&
        String(selectedTableRef.current.id) === String(tableId) &&
        Boolean(selectedTableRef.current.isDelivery) === isDelivery,
    );
    const originTable = selectedTarget ? selectedTableRef.current
      : tables.find((table) => String(table.id) === String(tableId));
    const originCycle = state?.occupancyCycle ?? resolveTableOccupancyCycle(originTable);
    const originContext = state?.cacheContextId ?? originTable?.cache_context_id ?? "";
    const pendingItems = (state?.currentOrder?.items || []).filter(isItemActive);
    const existingItems = normalizeItemsForExisting(
      state?.existingItems || [],
    ).filter(isItemActive);
    const cachedCourses = Array.isArray(state?.courses)
      ? state.courses
      : selectedTarget
        ? orderCourses
        : [];
    const cachedActiveCourseNumber = resolveSelectedCourseNumber(
      cachedCourses,
      state?.activeCourseNumber ??
        (selectedTarget ? activeCourseNumber : 1),
    );
    const resolvedCourseMode = resolveCartCourseManagement({
      explicitEnabled:
        state?.courseManagementEnabled ??
        (selectedTarget ? courseModeEnabled : false),
      courses: cachedCourses,
      pendingItems,
      existingItems,
    });
    commitTableStatesCache((prev) => {
      const newMap = new Map(prev);
      if (activeCount > 0) {
        const cacheEntry = {
          ...state,
          occupancyCycle: originCycle,
          cacheContextId: originContext,
          existingItems,
          lastUpdate: Date.now(),
          timestamp: Date.now(),
          courseManagementEnabled: resolvedCourseMode,
          activeCourseNumber: cachedActiveCourseNumber,
          courses: cachedCourses,
        };
        newMap.set(tableId, cacheEntry);
        newMap.set(String(tableId), cacheEntry);
      } else {
        newMap.delete(tableId);
        newMap.delete(String(tableId));
      }
      return newMap;
    });
    try {
      const cacheKey = buildPersistentCartKey(tableId, isDelivery);
      const previousOrigin = persistentCartCacheRef.current.get(cacheKey);
      if (!isDelivery && previousOrigin && !hasCompatibleCartOrigin(previousOrigin,
        { ...originTable, occupancy_cycle: originCycle, cache_context_id: originContext })) {
        preserveDetachedCart(persistentCartCacheRef.current, cacheKey, previousOrigin);
        persistentCartCacheRef.current.delete(cacheKey);
      }
      if (activeCount > 0) {
        rememberKnownExistingItems(tableId, state?.existingItems || [], isDelivery);
        upsertPersistentCartCacheEntry(
          persistentCartCacheRef.current,
          cacheKey,
          {
            items: existingItems,
            occupancyCycle: originCycle,
            cacheContextId: originContext,
            pendingItems,
            isDelivery,
            timestamp: Date.now(),
            courseManagementEnabled: resolvedCourseMode,
            activeCourseNumber: cachedActiveCourseNumber,
            courses: cachedCourses,
          },
        );
      } else {
        persistentCartCacheRef.current.delete(cacheKey);
        if (!isDelivery) {
          draftRecoveryTableIdsRef.current.delete(String(tableId));
        }
      }
      writePersistentCartCache(persistentCartCacheRef.current);
    } catch (_) {}
  };

  const persistCartStateForTable = (
    table,
    orderState,
    existingItems = [],
  ) => {
    if (!table?.id || !orderState) return;
    const isDelivery = Boolean(table.isDelivery);
    const identifiedItems = ensureDraftLineIds(orderState.items || []);
    const normalizedOrder = {
      items: identifiedItems.filter(isItemActive),
      total: Number(orderState.total) || computeItemsTotal(identifiedItems),
    };
    bumpTableCartGeneration(table.id, { isDelivery });
    if (isDelivery) {
      currentDeliveryOrderRef.current = normalizedOrder;
      existingDeliveryItemsRef.current = existingItems || [];
    } else {
      currentOrderRef.current = normalizedOrder;
      existingItemsForPaymentRef.current = existingItems || [];
    }
    updateTableStateCache(
      table.id,
      {
        currentOrder: normalizedOrder,
        occupancyCycle: resolveTableOccupancyCycle(table),
        cacheContextId: table.cache_context_id || "",
        existingItems,
        hasExistingOrders: (existingItems || []).filter(isItemActive).length > 0,
      },
      { isDelivery },
    );
    const isRecoveredFreeTable =
      !isDelivery && draftRecoveryTableIdsRef.current.has(String(table.id));
    if (normalizedOrder.items.length > 0 && !isRecoveredFreeTable) {
      optimisticTableStatusRef.current.set(table.id, {
        status: "occupied",
        ts: Date.now(),
      });
    } else if (isRecoveredFreeTable) {
      optimisticTableStatusRef.current.delete(table.id);
      optimisticTableStatusRef.current.delete(String(table.id));
    }
  };

  const getTableClearProtection = (tableId, { isDelivery = false } = {}) => {
    const tableKey = String(tableId);
    const selected = selectedTableRef.current;
    const selectedMatches =
      selected &&
      String(selected.id) === tableKey &&
      Boolean(selected.isDelivery) === Boolean(isDelivery);
    const localSignal = getActiveLocalCartSignalForTable(tableId, {
      isDelivery,
    });
    const lastSendTs =
      lastSendAtRef.current.get(tableId) || lastSendAtRef.current.get(tableKey) || 0;
    const withinSendGrace = lastSendTs && Date.now() - lastSendTs < 15000;
    const activeLocalCount = localSignal.activeCount;
    return {
      shouldProtect:
        Boolean(withinSendGrace) ||
        Boolean(orderActionInFlightRef.current) ||
        activeLocalCount > 0,
      withinSendGrace: Boolean(withinSendGrace),
      activeLocalCount,
      pendingCount: localSignal.pendingCount,
      existingCount: localSignal.existingCount,
      selectedMatches,
      sources: localSignal.sources,
    };
  };

  const isSelectedTableReconciliationInFlight = () => {
    const selectedNow = selectedTableRef.current;
    return Boolean(
      selectedNow?.id &&
        reconcilingVisibleTableIdRef.current !== null &&
        String(selectedNow.id) ===
          String(reconcilingVisibleTableIdRef.current?.tableId),
    );
  };

  const confirmTableEmptyForUiClear = async (tableId) => {
    try {
      const res = await fetchWithTimeout(
        `${API_BASE}/tables/${tableId}/occupancy`,
        {},
        2500,
      );
      if (!res.ok) return false;
      const occupancy = await res.json().catch(() => null);
      const activeItemsCount = Number(
        occupancy?.active_items_count ?? occupancy?.activeItemsCount ?? 0,
      );
      return Number.isFinite(activeItemsCount) && activeItemsCount === 0;
    } catch (_) {
      return false;
    }
  };

  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebugLogs, setShowDebugLogs] = useState(false);

  // État pour le modal d'aide
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Clé pour forcer le re-render du panier
  const [cartRenderKey, setCartRenderKey] = useState(0);
  const [cartBottomCollapsed, setCartBottomCollapsed] = useState(true);
  const refreshCartSnapshotRef = useRef(null);
  const refreshEmptyConfirmRef = useRef(new Map());
  const selectedTableRef = useRef(null);
  const currentOrderRef = useRef({ items: [], total: 0 });
  const existingItemsForPaymentRef = useRef([]);
  const currentDeliveryOrderRef = useRef({ items: [], total: 0 });
  const existingDeliveryItemsRef = useRef([]);
  const fastFoodCartSnapshotRef = useRef(readFastFoodCartSnapshotFromStorage());
  const previousTableBeforeCounterRef = useRef(null);
  const occupiedCartHydrationTimerRef = useRef(null);
  const occupiedCartHydrationLastAtRef = useRef(new Map());

  const markTableOccupiedInUi = (tableId) => {
    if (tableId === null || tableId === undefined) return;
    const selectedNow = selectedTableRef.current;
    if (
      selectedNow &&
      !selectedNow.isDelivery &&
      String(selectedNow.id) === String(tableId) &&
      selectedNow.status !== "occupied"
    ) {
      selectedTableRef.current = { ...selectedNow, status: "occupied" };
    }
    optimisticTableStatusRef.current.set(tableId, {
      status: "occupied",
      ts: Date.now(),
    });
    setTables((prev) =>
      Array.isArray(prev)
        ? prev.map((table) =>
            String(table.id) === String(tableId)
              ? { ...table, status: "occupied" }
              : table,
          )
        : prev,
    );
    setSelectedTable((prev) => {
      if (
        !prev ||
        prev.isDelivery ||
        String(prev.id) !== String(tableId) ||
        prev.status === "occupied"
      ) {
        return prev;
      }
      const next = { ...prev, status: "occupied" };
      selectedTableRef.current = next;
      return next;
    });
  };

  // États pour la gestion du nombre de clients
  const [showCustomerCountModal, setShowCustomerCountModal] = useState(false);
  // Onglet actif sur mobile serveur ("tables" | "menu" | "cart")
  const [mobileServerTab, setMobileServerTab] = useState("tables");
  const [customerCount, setCustomerCount] = useState(""); // Valeur vide par défaut
  const [courseModeEnabled, setCourseModeEnabled] = useState(false);
  const [activeCourseNumber, setActiveCourseNumber] = useState(1);
  const [orderCourses, setOrderCourses] = useState([]);
  const orderCoursesRef = useRef([]);
  const [courseDispatchStates, setCourseDispatchStates] = useState({});
  const [productionRetryingItemIds, setProductionRetryingItemIds] = useState(
    () => new Set(),
  );
  const courseFireRequestIdsRef = useRef(new Map());
  const [pendingTableSelection, setPendingTableSelection] = useState(null);
  const [tableCustomerCounts, setTableCustomerCounts] = useState({}); // Pour la persistance du nombre de clients

  useEffect(() => {
    orderCoursesRef.current = orderCourses;
  }, [orderCourses]);

  useEffect(() => {
    setCourseDispatchStates({});
    setProductionRetryingItemIds(new Set());
    courseFireRequestIdsRef.current.clear();
  }, [selectedTable?.id]);

  const courseModeContextForTable = useCallback(
    (table) =>
      Boolean(
        table &&
          !isBoutique &&
          !isFastFoodMode &&
          !activeAggregator &&
          !table.isDelivery &&
          Number(getApiTableId(table)) > 0,
      ),
    [activeAggregator, isBoutique, isFastFoodMode],
  );
  const courseModeEligibleForTable = useCallback(
    (table) =>
      courseManagementAvailable && courseModeContextForTable(table),
    [courseManagementAvailable, courseModeContextForTable],
  );

  // État pour éviter les vérifications multiples de libération de table
  const [isCheckingTableFree, setIsCheckingTableFree] = useState(false);

  // États pour le menu vertical
  const [activeTab, setActiveTab] = useState(() =>
    isBoutique ? (isServerParticipationMode ? "profile" : "customers") : "tables",
  ); // 'tables', 'calculator', 'delivery', 'reservation', 'customers', 'profile'

  // États pour la gestion des clients
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerModalContext, setCustomerModalContext] = useState("general");
  const [customerModalInitialView, setCustomerModalInitialView] =
    useState("list");
  const [selectedCustomerForOrder, setSelectedCustomerForOrder] =
    useState(null);
  const [showCreditRecoveryModal, setShowCreditRecoveryModal] = useState(false);
  const [creditSaleEnabled, setCreditSaleEnabled] = useState(false);

  const resetCustomerModalState = () => {
    setCustomerModalContext("general");
    setCustomerModalInitialView("list");
  };

  const openCustomerModal = (context = "general", initialView = "list") => {
    setCustomerModalContext(context);
    setCustomerModalInitialView(initialView);
    setShowCustomerModal(true);
  };

  const loadAggregatorSettings = useCallback(async () => {
    try {
      const [globalRes, yangoRes, glovoRes] = await Promise.all([
        fetch("/api/settings/aggregators").catch(() => null),
        fetch("/api/settings/aggregator-yango").catch(() => null),
        fetch("/api/settings/aggregator-glovo").catch(() => null),
      ]);
      const readEnabled = async (response) => {
        if (!response?.ok) return false;
        const payload = await response.json().catch(() => ({}));
        return Boolean(payload?.enabled ?? payload?.value ?? false);
      };
      const nextSettings = {
        aggregators: await readEnabled(globalRes),
        yango: await readEnabled(yangoRes),
        glovo: await readEnabled(glovoRes),
      };
      setAggregatorSettings(nextSettings);
      setActiveAggregator((current) => {
        if (!current) return current;
        if (!nextSettings.aggregators || !nextSettings[current]) return null;
        return current;
      });
    } catch (error) {
      console.warn("Impossible de charger les réglages agrégateurs:", error);
    }
  }, []);

  useEffect(() => {
    if (isBoutique || isServerParticipationMode) return;
    loadAggregatorSettings();
    const handleSettingsUpdate = () => loadAggregatorSettings();
    window.addEventListener("moo-payment-settings-updated", handleSettingsUpdate);
    window.addEventListener("storage", handleSettingsUpdate);
    return () => {
      window.removeEventListener("moo-payment-settings-updated", handleSettingsUpdate);
      window.removeEventListener("storage", handleSettingsUpdate);
    };
  }, [isBoutique, isServerParticipationMode, loadAggregatorSettings]);

  const [showOccupiedTablesModal, setShowOccupiedTablesModal] = useState(false);
  const [occupiedTablesDetails, setOccupiedTablesDetails] = useState([]);

  // États pour l'interface de paiement
  const [showPaymentInterface, setShowPaymentInterface] = useState(false);
  const [currentOrderForPayment, setCurrentOrderForPayment] = useState(null);
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const paymentPreparationInFlightRef = useRef(false);

  // États pour la génération de factures avec ajustements
  const [showInvoiceGenerationModal, setShowInvoiceGenerationModal] =
    useState(false);
  const [tempAdjustments, setTempAdjustments] = useState([]); // Ajustements temporaires non validés
  const invoiceModalCloseLockRef = useRef(false);
  const invoiceModalForceCloseRef = useRef(false);
  const invoiceAdjustmentHydratedKeyRef = useRef(null);
  const skipInvoiceAdjustmentPersistRef = useRef(false);
  const [discountDraft, setDiscountDraft] = useState({
    scope: "cart",
    unit: "amount",
    value: "",
    reason: "",
    targetItemId: null,
    targetQuantity: 1,
  });
  const INVOICE_SESSION_STORAGE_KEY = "moo_invoice_session";
  const MAX_INVOICE_SESSION_AGE_MS = 10 * 60 * 1000;
  const invoiceSessionRestoreRef = useRef(false);

  const persistInvoiceSession = useCallback((payload) => {
    if (typeof window === "undefined") return;
    try {
      if (!payload) {
        sessionStorage.removeItem(INVOICE_SESSION_STORAGE_KEY);
        return;
      }
      sessionStorage.setItem(
        INVOICE_SESSION_STORAGE_KEY,
        JSON.stringify(payload),
      );
    } catch (err) {
      console.warn("Impossible de sauvegarder l'état facture:", err);
    }
  }, []);

  const clearInvoiceSession = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      sessionStorage.removeItem(INVOICE_SESSION_STORAGE_KEY);
    } catch (err) {
      console.warn("Impossible de nettoyer l'état facture:", err);
    }
  }, []);

  // États pour les mouvements de caisse
  const [showCashMovementModal, setShowCashMovementModal] = useState(false);
  // (currentUser déjà défini en haut pour gérer le mode superadmin)
  const [currentSession, setCurrentSession] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState("");
  const [permissionPolicies, setPermissionPolicies] = useState(null);

  // État pour le rapport X
  const [showXReport, setShowXReport] = useState(false);

  // (Diagnostic UI retiré)

  // État pour le modal de déconnexion
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isXReportFromLogout, setIsXReportFromLogout] = useState(false);
  const [showCashLogoutValidation, setShowCashLogoutValidation] = useState(false);
  const [cashLogoutLoading, setCashLogoutLoading] = useState(false);
  const [cashLogoutError, setCashLogoutError] = useState("");
  const [cashLogoutReport, setCashLogoutReport] = useState(null);
  const [cashLogoutMethods, setCashLogoutMethods] = useState([]);
  const [cashLogoutInputs, setCashLogoutInputs] = useState({});
  const [cashLogoutSession, setCashLogoutSession] = useState(null);
  const [showForceLogoutModal, setShowForceLogoutModal] = useState(false);
  const [forceLogoutReason, setForceLogoutReason] = useState("");
  const [forceLogoutManagerCode, setForceLogoutManagerCode] = useState("");
  const [forceLogoutError, setForceLogoutError] = useState("");
  const [forceLogoutSubmitting, setForceLogoutSubmitting] = useState(false);

  // États pour les extras (accompagnements et suppléments)
  const [showExtrasModal, setShowExtrasModal] = useState(false);
  const [variationSelections, setVariationSelections] = useState({});
  const [selectedProductForExtras, setSelectedProductForExtras] =
    useState(null);
  const [productExtras, setProductExtras] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [loadingExtras, setLoadingExtras] = useState(false);
  const extrasLoadSequenceRef = useRef(0);
  const productExtrasCacheRef = useRef(new Map());

  // État pour le modal d'impression de reçu
  const [showPrintReceiptModal, setShowPrintReceiptModal] = useState(false);
  const [pendingReceiptData, setPendingReceiptData] = useState(null);
  const [isReceiptPrinting, setIsReceiptPrinting] = useState(false);
  const receiptPrintingRef = useRef(false);
  const receiptServiceTotal =
    pendingReceiptData?.order?.total_amount ??
    pendingReceiptData?.amount ??
    0;
  const receiptPerceivedTotal =
    pendingReceiptData?.amountReceived ??
    pendingReceiptData?.totalPaid ??
    pendingReceiptData?.amount ??
    0;
  const receiptChangeAmount =
    pendingReceiptData?.changeGiven ??
    (receiptPerceivedTotal > receiptServiceTotal
      ? receiptPerceivedTotal - receiptServiceTotal
      : 0);
  const receiptPaymentNumber =
    pendingReceiptData?.paymentNumber ||
    pendingReceiptData?.order?.payment_number ||
    null;
  const formatReceiptOptionalAmount = (value) =>
    typeof value === "number" && value > 0
      ? `${value.toLocaleString()} F`
      : "—";

  // États pour le transfert de table
  const [showTableTransferModal, setShowTableTransferModal] = useState(false);
  const [availableTablesForTransfer, setAvailableTablesForTransfer] = useState(
    [],
  );
  const [availableServersForTransfer, setAvailableServersForTransfer] = useState([]);
  const [tableTransferMode, setTableTransferMode] = useState("table");

  // États pour la fusion de tables
  const [showTableMergeModal, setShowTableMergeModal] = useState(false);
  const [availableTablesForMerge, setAvailableTablesForMerge] = useState([]);
  const [isTableOperationInProgress, setIsTableOperationInProgress] =
    useState(false);
  const [tableMerges, setTableMerges] = useState({}); // Tracker les fusions: {tableId: [fusedTableNumbers]}
  const [fusedAwayTables, setFusedAwayTables] = useState(new Set()); // Tables qui ont été fusionnées dans d'autres (ne doivent plus apparaître)

  // États pour la scission de facture
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitInvoices, setSplitInvoices] = useState([]);
  const [unassignedItems, setUnassignedItems] = useState([]);
  const [isSplitMode, setIsSplitMode] = useState(false);
  const [selectedSplitInvoiceId, setSelectedSplitInvoiceId] = useState(null);
  const [splitEditorPartId, setSplitEditorPartId] = useState(null);
  const [persistedSplitBill, setPersistedSplitBill] = useState(null);
  const [splitDiscountEditor, setSplitDiscountEditor] = useState(null);
  const [splitItemDiscountDraft, setSplitItemDiscountDraft] = useState(null);
  const [splitAssignmentDrafts, setSplitAssignmentDrafts] = useState({});
  const [isSplitActionPending, setIsSplitActionPending] = useState(false);
  const splitActionInFlightRef = useRef(false);
  const splitDiscountInFlightRef = useRef(false);
  const splitEditorStateRef = useRef({ invoices: [], unassignedItems: [] });
  splitEditorStateRef.current = {
    invoices: splitInvoices,
    unassignedItems,
  };

  const commitSplitEditorState = useCallback((nextState) => {
    splitEditorStateRef.current = {
      invoices: nextState.invoices,
      unassignedItems: nextState.unassignedItems,
    };
    setSplitInvoices(nextState.invoices);
    setUnassignedItems(nextState.unassignedItems);
  }, []);

  const resetSplitUiState = useCallback(() => {
    setPersistedSplitBill(null);
    setSplitInvoices([]);
    setUnassignedItems([]);
    setIsSplitMode(false);
    setSelectedSplitInvoiceId(null);
    setSplitEditorPartId(null);
    setSplitDiscountEditor(null);
    setSplitAssignmentDrafts({});
    setShowSplitModal(false);
    setCurrentOrderForPayment((previous) =>
      previous?.isSplit ? null : previous,
    );
  }, []);

  useEffect(() => {
    if (splitInvoices.length > 0 && !selectedSplitInvoiceId) {
      setSelectedSplitInvoiceId(splitInvoices[0].id);
    }
  }, [splitInvoices, selectedSplitInvoiceId]);

  const existingSplitItemsSignature = existingItemsForPayment
    .map(
      (item) =>
        `${item?.order_item_id ?? item?.id}:${Number(item?.quantity || 0)}`,
    )
    .sort()
    .join("|");

  useEffect(() => {
    const tableId = Number(getApiTableId(selectedTable));
    if (!Number.isInteger(tableId) || tableId <= 0 || selectedTable?.isDelivery) {
      resetSplitUiState();
      return undefined;
    }
    let cancelled = false;
    void fetch(`${API_BASE}/split-bills/active?tableId=${tableId}`, { cache: "no-store" })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "Scission indisponible");
        if (cancelled) return;
        if (!payload.split) {
          resetSplitUiState();
          return;
        }
        const split = payload.split;
        const hydratedInvoices = (split.parts || []).map((part) => {
          const items = (part.lines || []).map((line) => {
            const extrasTotal = (line.extras || []).reduce(
              (sum, extra) => sum + Number(extra.total_price || 0),
              0,
            );
            const baseTotal = Number(line.total_price || 0) *
              (Number(line.quantity || 0) / Number(line.source_quantity || line.quantity || 1));
            return {
              ...line,
              id: line.order_item_id,
              order_item_id: line.order_item_id,
              order_id: line.order_id,
              name: line.product_name,
              price: Number(line.unit_price || 0),
              totalPrice: baseTotal + extrasTotal,
              total_price: baseTotal + extrasTotal,
            };
          });
          return {
            id: part.id,
            name: part.name,
            status: part.status,
            settlement_id: part.settlement_id,
            receipt_number: part.receipt_number,
            adjustments: part.adjustments || [],
            items,
            subtotal: items.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0),
          };
        });
        setPersistedSplitBill(split);
        setSplitInvoices(hydratedInvoices);
        setCurrentOrderForPayment({
          table: selectedTable,
          table_id: tableId,
          table_number: selectedTable.number,
          table_label: `Table ${selectedTable.number}`,
          isSplit: true,
          splitBill: split,
          splitInvoices: hydratedInvoices,
          items: hydratedInvoices.flatMap((invoice) => invoice.items),
          total_amount: hydratedInvoices
            .filter((invoice) => invoice.status !== "paid")
            .reduce((sum, invoice) => sum + invoice.subtotal, 0),
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [
    selectedTable?.id,
    selectedTable?.isDelivery,
    selectedTable?.number,
    existingSplitItemsSignature,
    resetSplitUiState,
  ]);

  const isCurrentTableSplit =
    currentOrderForPayment?.isSplit &&
    currentOrderForPayment?.table_number === selectedTable?.number;

  // État pour contrôler la visibilité de tous les panneaux
  const [visiblePanels, setVisiblePanels] = useState(() => ({
    tables: !isBoutique,
    online: false,
    calculator: false,
    delivery: false,
    reservations: false,
    customers: isBoutique && !isServerParticipationMode,
    profile: false,
  }));

  useEffect(() => {
    if (!isFastFoodMode) return;
    setVisiblePanels((prev) => ({
      ...prev,
      tables: false,
      delivery: false,
      reservations: false,
    }));
    if (["tables", "delivery", "reservations"].includes(activeTab)) {
      setActiveTab("");
    }
    setShowCustomerCountModal(false);
    setPendingTableSelection(null);
  }, [activeTab, isFastFoodMode]);

  useEffect(() => {
    if (!isBoutique) return;
    setVisiblePanels((prev) => ({
      ...prev,
      tables: false,
      online: prev.online && Boolean(onlineMenuOrderingEnabled),
      delivery: false,
      reservations: false,
      customers: isServerParticipationMode ? false : prev.customers || true,
    }));
    if (
      ["tables", "delivery", "reservations"].includes(activeTab) ||
      (isServerParticipationMode && activeTab === "customers")
    ) {
      setActiveTab(isServerParticipationMode ? "profile" : "customers");
    }
    setShowCustomerCountModal(false);
    setPendingTableSelection(null);
  }, [isBoutique, activeTab, isServerParticipationMode, onlineMenuOrderingEnabled]);

  useEffect(() => {
    if (!isServerParticipationMode) return;
    setVisiblePanels((prev) => ({
      ...prev,
      calculator: false,
      delivery: false,
      reservations: false,
      customers: false,
    }));
    if (["calculator", "delivery", "reservations", "customers"].includes(activeTab)) {
      setActiveTab(isBoutique ? "profile" : isFastFoodMode ? "" : "tables");
    }
    setSelectedCustomerForOrder(null);
    setShowAdminCashDrawer(false);
  }, [activeTab, isBoutique, isFastFoodMode, isServerParticipationMode]);

  useEffect(() => {
    if (!onlineMenuOrderingEnabled) {
      setVisiblePanels((prev) => ({ ...prev, online: false }));
      if (activeTab === "online") {
        setActiveTab(
          isFastFoodMode ? "" : isServerParticipationMode ? "tables" : isBoutique ? "customers" : "tables",
        );
      }
    }
  }, [activeTab, isBoutique, isFastFoodMode, isServerParticipationMode, onlineMenuOrderingEnabled]);

  // États pour la pagination des produits
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(15);

  // Fonction pour toggle un panneau
  const togglePanel = (panelName) => {
    const counterBlockMessage = getCounterMutationBlockMessage();
    if (isCounterContext && (hasActiveCounterCart() || counterBlockMessage)) {
      triggerErrorAnimation(
        counterBlockMessage || "Terminez la vente Comptoir avant de changer de contexte.",
      );
      return;
    }
    if (isFastFoodMode && ["tables", "delivery", "reservations"].includes(panelName)) {
      return;
    }
    if (
      isServerParticipationMode &&
      ["calculator", "delivery", "reservations", "customers"].includes(panelName)
    ) {
      return;
    }
    setVisiblePanels((prev) => ({
      ...prev,
      [panelName]: !prev[panelName],
    }));
  };

  // Fonction pour montrer un panneau et masquer les autres
  const showPanelExclusive = (panelName) => {
    const counterBlockMessage = getCounterMutationBlockMessage();
    if (isCounterContext && (hasActiveCounterCart() || counterBlockMessage)) {
      triggerErrorAnimation(
        counterBlockMessage || "Terminez la vente Comptoir avant de changer de contexte.",
      );
      return;
    }
    if (isFastFoodMode && ["tables", "delivery", "reservations"].includes(panelName)) {
      return;
    }
    if (isBoutique && ["tables", "delivery", "reservations"].includes(panelName)) {
      return;
    }
    if (panelName === "online" && !onlineMenuOrderingEnabled) {
      return;
    }
    if (
      isServerParticipationMode &&
      ["calculator", "delivery", "reservations", "customers"].includes(panelName)
    ) {
      return;
    }
    setVisiblePanels((prev) => {
      const normalized = {
        tables: Boolean(prev?.tables),
        online: Boolean(prev?.online),
        calculator: Boolean(prev?.calculator),
        delivery: Boolean(prev?.delivery),
        reservations: Boolean(prev?.reservations),
        customers: Boolean(prev?.customers),
        profile: Boolean(prev?.profile),
        settings: Boolean(prev?.settings),
      };
      return Object.keys(normalized).reduce((acc, key) => {
        acc[key] = key === panelName;
        return acc;
      }, {});
    });
    setActiveTab(panelName);
  };


  const switchMobileServerTab = (tabName) => {
    setMobileServerTab(tabName);

    if (tabName === "tables") {
      showPanelExclusive("tables");
      return;
    }

    if (tabName === "menu" || tabName === "cart") {
      setVisiblePanels((prev) =>
        Object.keys(prev || {}).reduce((acc, key) => {
          acc[key] = false;
          return acc;
        }, {}),
      );
      if (activeTab === "tables") {
        setActiveTab("");
      }
    }
  };

  useEffect(() => {
    if (!isServerParticipationMode || isBoutique || isFastFoodMode) return;
    switchMobileServerTab("tables");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isServerParticipationMode, isBoutique, isFastFoodMode]);

  // Protection contre les exécutions multiples
  const [processingAction, setProcessingAction] = useState(false);

  // Références pour le panneau collapsable et la zone menu
  const panelRef = useRef(null);
  const menuAreaRef = useRef(null);

  const [themeMode, setThemeMode] = useState("dark");

  const isDarkTheme = themeMode === "dark";

  // Couleurs MOO selon le thème
  const colors = useMemo(() => {
    if (isDarkTheme) {
      return {
        primary: "#FFB800",
        secondary: "#FF4757",
        accent: "#f59e0b",
        dark: "#0a0f1a",
        light: "#111827",
        success: "#16a34a",
        warning: "#f59e0b",
        background: "#0a0f1a",
        cardBg: "#0f172a",
      };
    }

    return {
      primary: "#FFB800",
      secondary: "#FF4757",
      accent: "#FF9F43",
      dark: "#2C3E50",
      light: "#F8F9FA",
      success: "#2ED573",
      warning: "#FFA726",
      background: "#F5F6FA",
      cardBg: "#FFFFFF",
    };
  }, [themeMode]);

  // Fonction pour obtenir la description du statut
  const getStatusDescription = (status) => {
    switch (status) {
      case "pending":
        return { text: "En attente", color: "yellow", icon: "⏳" };
      case "preparing":
        return { text: "En préparation", color: "blue", icon: "👨‍🍳" };
      case "ready":
        return { text: "Prêt à servir", color: "green", icon: "✅" };
      case "served":
        return { text: "Servi", color: "purple", icon: "🍽️" };
      case "paid":
        return { text: "Payé", color: "gray", icon: "💰" };
      case "cancelled":
        return { text: "Annulé", color: "red", icon: "❌" };
      default:
        return { text: status, color: "gray", icon: "•" };
    }
  };

  // Fonction pour obtenir la classe CSS du statut
  const getStatusClass = (status) => {
    const statusInfo = getStatusDescription(status);
    const colorMap = {
      yellow: "bg-yellow-100 text-yellow-700 border-yellow-200",
      blue: "bg-blue-100 text-blue-700 border-blue-200",
      green: "bg-green-100 text-green-700 border-green-200",
      purple: "bg-purple-100 text-purple-700 border-purple-200",
      gray: "bg-gray-100 text-gray-700 border-gray-200",
      red: "bg-red-100 text-red-700 border-red-200",
    };
    return colorMap[statusInfo.color] || colorMap.gray;
  };

  const getOnlineOrderStatusMeta = (statusRaw) => {
    const status = String(statusRaw || "").toLowerCase();
    if (status === "pending") {
      return {
        label: "En attente",
        className: "bg-amber-500/15 border border-amber-400/40 text-amber-100",
      };
    }
    if (status === "accepted" || status === "in_kitchen") {
      return {
        label: "En cuisine",
        className: "bg-sky-500/15 border border-sky-400/35 text-sky-100",
      };
    }
    if (status === "ready") {
      return {
        label: "Prêt",
        className: "bg-indigo-500/15 border border-indigo-400/35 text-indigo-100",
      };
    }
    if (status === "served") {
      return {
        label: "Servi",
        className: "bg-emerald-500/15 border border-emerald-400/35 text-emerald-100",
      };
    }
    if (status === "rejected") {
      return {
        label: "Refusée",
        className: "bg-rose-500/15 border border-rose-400/35 text-rose-100",
      };
    }
    if (status === "cancelled") {
      return {
        label: "Annulée",
        className: "bg-slate-500/15 border border-slate-400/35 text-slate-100",
      };
    }
    return {
      label: statusRaw || "Inconnu",
      className: "bg-white/5 border border-white/20 text-white/80",
    };
  };

  const API_BASE = "/api";
  const API_TIMEOUT = 3500;
  // Les écritures de commande peuvent attendre brièvement le verrou SQLite
  // lorsqu'une caisse, une tablette et un KDS travaillent en même temps. Le
  // délai court reste adapté aux lectures, mais interrompre une écriture après
  // 3,5 s produit une fausse erreur alors que le serveur peut encore la valider.
  // Le serveur ne considère une impression `in_progress` relançable qu'après
  // 30 secondes. Ce délai client plus court doit donc uniquement basculer vers
  // la vérification d'état, jamais autoriser directement une réimpression.
  const ORDER_MUTATION_TIMEOUT = 12000;

  const loadOpenCounterOrders = useCallback(async () => {
    if (!isCounterOperatorRole || isServerParticipationMode) {
      setOpenCounterOrders([]);
      return [];
    }
    try {
      const response = await fetch(`${API_BASE}/counter-orders/open`, {
        cache: "no-store",
        headers: { "Cache-Control": "no-store" },
      });
      if (!response.ok) return null;
      const payload = await response.json().catch(() => ({}));
      const orders = filterConfirmedCounterOrders(
        Array.isArray(payload?.orders) ? payload.orders : [],
        Array.from(confirmedCounterOrderIdsRef.current),
      );
      setOpenCounterOrders(orders);
      return orders;
    } catch (_error) {
      return null;
    }
  }, [isCounterOperatorRole, isServerParticipationMode]);

  const canManageCounterRecovery = ["manager", "admin", "superadmin"].includes(
    currentRole,
  );

  const retryCounterDispatch = useCallback(
    async (snapshot = counterDispatchPending) => {
      if (!snapshot?.created_order_id || counterDispatchRetryRef.current) return false;
      counterDispatchRetryRef.current = true;
      try {
        const response = await fetch(
          `${API_BASE}/orders/${snapshot.created_order_id}/dispatch-production`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          },
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload?.success === false) {
          throw new Error(payload?.error || "Envoi production non confirmé");
        }
        clearFastFoodCartSnapshotFromStorage();
        fastFoodCartSnapshotRef.current = null;
        setCounterDispatchPending(null);
        setCurrentOrder({ items: [], total: 0 });
        triggerSuccessAnimation("Vente payée : envoi Cuisine/Bar confirmé");
        return true;
      } catch (error) {
        setCounterDispatchPending(snapshot);
        console.warn("Relance dispatch Comptoir en attente:", error?.message || error);
        return false;
      } finally {
        window.setTimeout(() => {
          counterDispatchRetryRef.current = false;
        }, 2000);
      }
    },
    [counterDispatchPending],
  );

  const cancelCounterRecovery = async (orderId = null) => {
    if (!canManageCounterRecovery) {
      triggerErrorAnimation("Une autorisation manager est requise.");
      return;
    }
    const reason = window.prompt("Motif obligatoire de l’annulation Comptoir :", "");
    if (!String(reason || "").trim()) return;
    if (orderId) {
      try {
        const response = await fetch(`${API_BASE}/counter-orders/${orderId}/cancel`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: String(reason).trim() }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || "Annulation Comptoir refusée");
        }
        await loadOpenCounterOrders();
        const localRecovery =
          fastFoodCartSnapshotRef.current || readFastFoodCartSnapshotFromStorage();
        if (
          localRecovery?.created_order_id &&
          String(localRecovery.created_order_id) === String(orderId)
        ) {
          clearFastFoodCartSnapshotFromStorage();
          fastFoodCartSnapshotRef.current = null;
          setCounterDispatchPending(null);
          setCurrentOrder({ items: [], total: 0 });
        }
        if (
          foreignCounterRecovery?.created_order_id &&
          String(foreignCounterRecovery.created_order_id) === String(orderId)
        ) {
          clearFastFoodCartSnapshotFromStorage();
          fastFoodCartSnapshotRef.current = null;
          setForeignCounterRecovery(null);
        }
        triggerSuccessAnimation("Commande Comptoir annulée");
      } catch (error) {
        triggerErrorAnimation(error.message || "Annulation Comptoir impossible");
      }
      return;
    }
    clearFastFoodCartSnapshotFromStorage();
    fastFoodCartSnapshotRef.current = null;
    setForeignCounterRecovery(null);
    triggerSuccessAnimation("Transaction Comptoir locale annulée");
  };

  useEffect(() => {
    if (!isCounterOperatorRole || isServerParticipationMode) return undefined;
    void loadOpenCounterOrders();
    const handleOnline = () => void loadOpenCounterOrders();
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [isCounterOperatorRole, isServerParticipationMode, loadOpenCounterOrders]);

  useEffect(() => {
    if (!hasCounterService || counterDispatchRetryRef.current) return;
    const recovery = readFastFoodCartSnapshotFromStorage();
    if (
      !recovery?.payment_confirmed ||
      !recovery?.created_order_id ||
      (recovery?.origin_user_id &&
        currentUser?.id &&
        String(recovery.origin_user_id) !== String(currentUser.id))
    ) {
      return;
    }
    void retryCounterDispatch(recovery);
  }, [currentUser?.id, hasCounterService, retryCounterDispatch]);

  useEffect(() => {
    let mounted = true;
    let retryTimer = null;
    let retryAttempt = 0;
    let syncInFlight = false;

    const syncFromServer = async ({ persistCache = true } = {}) => {
      if (syncInFlight || !mounted) return;
      syncInFlight = true;
      try {
        const response = await fetch(`${API_BASE}/packages?_ts=${Date.now()}`, {
          headers: { "Cache-Control": "no-store" },
          cache: "no-store",
        });
        if (!response.ok || response.status === 304) {
          throw new Error(`package settings unavailable (${response.status})`);
        }
        const data = await response.json();
        const serverPackages = Array.isArray(data?.packages) ? data.packages : [];
        const activeId = data?.activeId || serverPackages[0]?.id || "";
        const serverParticipation = resolveServerParticipationFromPackages(
          serverPackages,
          activeId,
        );
        const onlineEnabled = resolveOnlineMenuOrderingFromPackages(
          serverPackages,
          activeId,
        );
        const nextServiceMode = resolveServiceModeFromPackages(
          serverPackages,
          activeId,
        );
        const nextCourseManagement = resolveCourseManagementFromPackages(
          serverPackages,
          activeId,
        );
        if (persistCache) {
          localStorage.setItem("moo_packages", JSON.stringify(serverPackages));
          localStorage.setItem("moo_active_package", activeId);
          localStorage.setItem(
            "moo_package_broadcast",
            `${activeId}_${Date.now()}`,
          );
        }
        if (mounted) {
          retryAttempt = 0;
          setServerParticipationEnabled(Boolean(serverParticipation));
          setOnlineMenuOrderingEnabled(Boolean(onlineEnabled));
          setServiceMode(nextServiceMode);
          setCourseManagementAvailable(Boolean(nextCourseManagement));
          setPackageConfigHydrated(true);
        }
      } catch (_error) {
        if (mounted && retryTimer === null) {
          const delay = Math.min(5000, 750 * (2 ** retryAttempt));
          retryAttempt += 1;
          retryTimer = window.setTimeout(() => {
            retryTimer = null;
            void syncFromServer({ persistCache });
          }, delay);
        }
      } finally {
        syncInFlight = false;
      }
    };

    const closeUnconfirmedPackageOptions = () => {
      if (!mounted) return;
      setServerParticipationEnabled(false);
      setOnlineMenuOrderingEnabled(false);
      setServiceMode(null);
      setCourseManagementAvailable(false);
    };

    const handleStorage = (event) => {
      if (
        !event ||
        event.key === "moo_package_broadcast" ||
        event.key === "moo_packages" ||
        event.key === "moo_active_package"
      ) {
        closeUnconfirmedPackageOptions();
        // Ne pas réécrire le broadcast reçu : deux fenêtres se répondraient
        // indéfiniment. Seule la lecture serveur remet les options en service.
        void syncFromServer({ persistCache: false });
      }
    };

    const handlePackagesUpdated = () => {
      closeUnconfirmedPackageOptions();
      void syncFromServer({ persistCache: false });
    };

    const handleAuthRefresh = () => {
      void syncFromServer({ persistCache: false });
    };

    void syncFromServer();

    window.addEventListener("storage", handleStorage);
    window.addEventListener("moo-auth-refresh", handleAuthRefresh);
    window.addEventListener("moo-packages-updated", handlePackagesUpdated);
    return () => {
      mounted = false;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("moo-auth-refresh", handleAuthRefresh);
      window.removeEventListener("moo-packages-updated", handlePackagesUpdated);
    };
  }, [API_BASE]);

  useEffect(() => {
    let mounted = true;

    const loadCreditSaleSetting = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/settings/credit-sale?_=${Date.now()}`,
          {
            cache: "no-store",
          },
        );
        if (!response.ok) return;
        const data = await response.json();
        if (mounted) {
          setCreditSaleEnabled(Boolean(data?.enabled));
        }
      } catch (_error) {
        // garder false par défaut
      }
    };

    const handlePaymentSettingsUpdate = (event) => {
      const enabled = event?.detail?.creditSaleEnabled;
      if (typeof enabled === "boolean") {
        setCreditSaleEnabled(enabled);
        return;
      }
      void loadCreditSaleSetting();
    };

    void loadCreditSaleSetting();
    window.addEventListener("moo-payment-settings-updated", handlePaymentSettingsUpdate);
    window.addEventListener("app:payment-settings", handlePaymentSettingsUpdate);
    return () => {
      mounted = false;
      window.removeEventListener(
        "moo-payment-settings-updated",
        handlePaymentSettingsUpdate,
      );
      window.removeEventListener("app:payment-settings", handlePaymentSettingsUpdate);
    };
  }, [isSuperPath]);

  const fetchWithTimeout = useCallback(async (url, options = {}, timeout = API_TIMEOUT) => {
    const effectiveUrl = `${url}`;
    const method = (options.method || "GET").toUpperCase();
    const incomingHeaders = { ...(options.headers || {}) };
    const hasAuthorization = Object.keys(incomingHeaders).some(
      (key) => String(key).toLowerCase() === "authorization",
    );
    const authToken = hasAuthorization
      ? ""
      : getStoredAuthToken({
          tokenMode: isSuperPath ? "superadmin" : "admin",
          url: effectiveUrl,
        });

    const headers = {
      // Éviter les réponses 304 (Not Modified) sur des endpoints API JSON,
      // qui font "échouer" les contrôles .ok et peuvent reset l'UI.
      ...(method === "GET"
        ? { "Cache-Control": "no-store", Pragma: "no-cache" }
        : {}),
      ...incomingHeaders,
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      "X-Moo-Device-Id": getPersistentDeviceId(),
    };
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    const stopApiTimer = startPerfTimer("caisse.api_request_ms", {
      method,
      timeout,
      route: String(url || "").replace(/\?.*$/, ""),
    });
    try {
      const response = await fetch(effectiveUrl, {
        ...options,
        headers,
        signal: controller.signal,
        credentials: options.credentials ?? "include",
        cache: options.cache ?? "no-store",
      });
      clearTimeout(id);
      stopApiTimer({
        ok: response.ok,
        status: response.status,
      });
      return response;
    } catch (error) {
      clearTimeout(id);
      stopApiTimer({
        ok: false,
        aborted: controller.signal.aborted,
      });
      throw normalizeNetworkRequestError(error, controller.signal);
    }
  }, [API_TIMEOUT, isSuperPath]);

  const waitForOrderRequestOutcome = useCallback(
    async ({ clientRequestId, operation, orderId = null, waitMs = 6 * 60 * 1000 }) => {
      const deadline = Date.now() + waitMs;
      let lastKnownState = "unknown";
      while (Date.now() < deadline) {
        const query = new URLSearchParams({ operation });
        if (orderId != null) query.set("orderId", String(orderId));
        try {
          const response = await fetchWithTimeout(
            `${API_BASE}/order-requests/${encodeURIComponent(clientRequestId)}/status?${query.toString()}`,
            { method: "GET" },
            3000,
          );
          if (response.ok) {
            const outcome = await response.json().catch(() => ({}));
            lastKnownState = String(outcome?.state || "unknown");
            if (lastKnownState === "completed" || lastKnownState === "failed") {
              return outcome;
            }
          }
        } catch (_error) {
          // La sonde suivante permettra de distinguer un simple délai d'une
          // perte durable. Ne jamais réémettre automatiquement la commande.
        }
        await new Promise((resolve) => setTimeout(resolve, 900));
      }
      return { state: lastKnownState || "unknown" };
    },
    [fetchWithTimeout],
  );

  const sendOrderMutationWithRecovery = useCallback(
    async ({ url, options, clientRequestId, operation, orderId = null }) => {
      let shouldResolveOutcome = false;
      try {
        const response = await fetchWithTimeout(
          url,
          options,
          ORDER_MUTATION_TIMEOUT,
        );
        if (response.status !== 409) return response;
        const conflict = await response.clone().json().catch(() => ({}));
        if (String(conflict?.code || "") !== "REQUEST_PENDING") {
          return response;
        }
        shouldResolveOutcome = true;
      } catch (error) {
        if (
          !["REQUEST_TIMEOUT", "REQUEST_NETWORK_UNKNOWN"].includes(
            String(error?.code || ""),
          )
        ) {
          throw error;
        }
        shouldResolveOutcome = true;
      }
      if (!shouldResolveOutcome) {
        throw new Error("État de commande inattendu.");
      }
      const outcome = await waitForOrderRequestOutcome({
        clientRequestId,
        operation,
        orderId,
      });
      if (outcome?.state === "completed" && outcome?.payload) {
        return new Response(JSON.stringify(outcome.payload), {
          status: Number(outcome.status) || 200,
          headers: { "Content-Type": "application/json" },
        });
      }
      if (outcome?.state === "failed") {
        const confirmedError = new Error(
          outcome?.payload?.error || "La commande n'a pas pu être enregistrée.",
        );
        confirmedError.code =
          outcome?.payload?.code || "ORDER_REQUEST_FAILED";
        confirmedError.currentCycle =
          Number(outcome?.payload?.currentCycle) || null;
        throw confirmedError;
      }
      const uncertainError = new Error(
        "Le serveur traite peut-être encore cette commande. Elle n'a pas été renvoyée pour éviter un doublon. Actualisez la table avant toute nouvelle tentative.",
      );
      uncertainError.code = "REQUEST_OUTCOME_UNKNOWN";
      throw uncertainError;
    },
    [fetchWithTimeout, waitForOrderRequestOutcome],
  );

  const loadFloorPlan = useCallback(async () => {
    const requestSequence = floorPlanRequestSequenceRef.current + 1;
    floorPlanRequestSequenceRef.current = requestSequence;
    const isCurrentRequest = () =>
      floorPlanRequestSequenceRef.current === requestSequence;
    setFloorPlanLoadState((previous) =>
      previous === "ready" ? "refreshing" : "loading",
    );
    try {
      const settingsResponse = await fetchWithTimeout(
        `${API_BASE}/settings/floor-plan?_ts=${Date.now()}`,
      );
      if (!settingsResponse.ok) {
        throw new Error("floor-plan settings unavailable");
      }
      const settingsPayload = await settingsResponse.json();
      const nextEnabled = Boolean(settingsPayload?.floor_plan_enabled);
      if (!isCurrentRequest()) return;
      setPrintAreaOnDocuments(nextEnabled && Boolean(settingsPayload?.print_area_on_documents));
      if (!nextEnabled) {
        setFloorPlanEnabled(false);
        setVisibleZones([]);
        setSelectedZoneId(null);
        setFloorPlanHydrated(true);
        setFloorPlanLoadState("ready");
        floorPlanFailureCountRef.current = 0;
        return;
      }

      const zonesResponse = await fetchWithTimeout(
        `${API_BASE}/zones?_ts=${Date.now()}`,
      );
      if (!zonesResponse.ok) {
        throw new Error("floor-plan zones unavailable");
      }
      const zonesPayload = await zonesResponse.json();
      if (!isCurrentRequest()) return;
      const nextZones = Array.isArray(zonesPayload) ? zonesPayload : [];
      setFloorPlanEnabled(true);
      setVisibleZones(nextZones);
      setSelectedZoneId((previous) => {
        if (nextZones.length === 0) return null;
        if (nextZones.some((zone) => String(zone.id) === String(previous))) {
          return previous;
        }
        return nextZones[0].id;
      });
      setFloorPlanHydrated(true);
      setFloorPlanLoadState("ready");
      floorPlanFailureCountRef.current = 0;
    } catch (_error) {
      if (!isCurrentRequest()) return;
      // Une panne réseau ne change jamais la configuration métier. Conserver
      // le dernier plan valide empêche une tablette serveur de voir toutes les
      // tables pendant une microcoupure. Au premier chargement, l'interface
      // reste fermée jusqu'à l'obtention d'une réponse faisant autorité.
      floorPlanFailureCountRef.current += 1;
      setFloorPlanLoadState("error");
    }
  }, [fetchWithTimeout]);

  const loadStewardOverview = useCallback(async () => {
    try {
      const settingsResponse = await fetchWithTimeout(
        `${API_BASE}/stewards/settings?_ts=${Date.now()}`,
      );
      if (!settingsResponse.ok) return;
      const settings = await settingsResponse.json();
      if (!settings?.enabled) {
        setStewardModeEnabled(false);
        try {
          localStorage.setItem("moo_steward_role_enabled", "false");
        } catch (_error) {}
        setStewardReplacesServer(true);
        setSelectedStewardKey(null);
        setStewardOverview({ stewards: [], unassigned: null });
        return;
      }
      setStewardReplacesServer(settings.replaces_server !== false);
      // Dès que le serveur confirme l'activation, ne jamais retomber sur la
      // liste brute des tables parce que le calcul d'un aperçu échoue ensuite.
      setStewardModeEnabled(true);
      try {
        localStorage.setItem("moo_steward_role_enabled", "true");
      } catch (_error) {}
      const overviewResponse = await fetchWithTimeout(
        `${API_BASE}/stewards/overview?_ts=${Date.now()}`,
      );
      if (!overviewResponse.ok) return;
      const overview = await overviewResponse.json();
      setStewardOverview({
        stewards: Array.isArray(overview?.stewards) ? overview.stewards : [],
        unassigned: overview?.unassigned || null,
      });
    } catch (_error) {
      // Conserver le dernier état valide pendant une microcoupure réseau.
    }
  }, [fetchWithTimeout]);

  useEffect(() => {
    void loadFloorPlan();
    const handleFloorPlanUpdated = () => void loadFloorPlan();
    const handleStorage = (event) => {
      if (
        event.key === "moo_floor_plan_enabled" ||
        event.key === "moo_floor_plan_broadcast"
      ) {
        void loadFloorPlan();
      }
    };
    const reloadAfterExternalSignal = () => {
      floorPlanFailureCountRef.current = 0;
      void loadFloorPlan();
    };
    const handleFocus = reloadAfterExternalSignal;
    const handleOnline = reloadAfterExternalSignal;
    window.addEventListener("moo-floor-plan-updated", handleFloorPlanUpdated);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener(
        "moo-floor-plan-updated",
        handleFloorPlanUpdated,
      );
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
    };
  }, [loadFloorPlan]);

  useEffect(() => {
    void loadStewardOverview();
    const refresh = () => void loadStewardOverview();
    window.addEventListener("moo-steward-mode-updated", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    return () => {
      window.removeEventListener("moo-steward-mode-updated", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [loadStewardOverview]);

  useEffect(() => {
    if (apiStatus === "connected") void loadStewardOverview();
  }, [apiStatus, loadStewardOverview]);

  useEffect(() => {
    if (!stewardModeEnabled) return undefined;
    const timer = setTimeout(() => void loadStewardOverview(), 180);
    return () => clearTimeout(timer);
  }, [tables, stewardModeEnabled, loadStewardOverview]);

  useEffect(() => {
    if (
      floorPlanLoadState !== "error" ||
      floorPlanFailureCountRef.current > 3
    ) {
      return undefined;
    }
    const retryDelay = 750 * 2 ** (floorPlanFailureCountRef.current - 1);
    const retryTimer = setTimeout(() => void loadFloorPlan(), retryDelay);
    return () => clearTimeout(retryTimer);
  }, [floorPlanLoadState, loadFloorPlan]);

  const getCurrentActorName = () =>
    String(
      currentUser?.first_name ||
        currentUser?.firstName ||
        currentUser?.username ||
        currentUser?.name ||
        "Utilisateur",
    ).trim();

  const getOnlineActorRole = () => {
    const normalized = normalizeRoleKey(currentRole || "");
    if (normalized === "caissier" || normalized === "caisse") return "cashier";
    if (normalized === "caissiere") return "cashier";
    if (normalized === "serveur") return "server";
    return normalized;
  };

  const playOnlineOrderSound = useCallback(async () => {
    try {
      const src = "/assets/sounds/moo_sync_sound.mp3";
      if (!onlineOrderAudioRef.current) {
        onlineOrderAudioRef.current = new Audio(src);
        onlineOrderAudioRef.current.preload = "auto";
      }
      onlineOrderAudioRef.current.currentTime = 0;
      await onlineOrderAudioRef.current.play();
    } catch (_e) {
      // ignore autoplay restrictions
    }
  }, []);

  const fetchOnlineOrders = useCallback(
    async ({ silent = false, syncNow = false } = {}) => {
      if (!onlineMenuOrderingEnabled) return;
      if (!silent) setOnlineOrdersLoading(true);
      setOnlineOrdersError("");
      try {
        const query = new URLSearchParams();
        query.set("status", "pending,accepted,in_kitchen,ready");
        query.set("limit", "120");
        if (currentRole) {
          query.set("actor_role", String(currentRole));
        }
        if (currentUser?.id) {
          query.set("actor_user_id", String(currentUser.id));
        }
        if (syncNow) query.set("sync", "1");
        const response = await fetchWithTimeout(`${API_BASE}/online-orders?${query.toString()}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.error || "Impossible de charger les commandes en ligne");
        }
        const list = Array.isArray(data?.orders) ? data.orders : [];
        setOnlineOrders(list);
        setOnlineUnreadCount(Number(data?.unread_count || 0));

        const newlyUnread = list.filter((order) => {
          const id = Number(order?.id);
          const pending = String(order?.status_internal || "").toLowerCase() === "pending";
          const unread = Number(order?.unread_local ?? order?.unreadLocal ?? 0) === 1;
          return Number.isFinite(id) && id > 0 && pending && unread;
        });

        for (const order of newlyUnread) {
          const id = Number(order.id);
          if (onlineOrderSoundedRef.current.has(id)) continue;
          onlineOrderSoundedRef.current.add(id);
          // eslint-disable-next-line no-await-in-loop
          await playOnlineOrderSound();
          fetchWithTimeout(`${API_BASE}/online-orders/${id}/ack-sound`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          }).catch(() => null);
        }
      } catch (error) {
        setOnlineOrdersError(error?.message || "Erreur chargement commandes en ligne");
      } finally {
        if (!silent) setOnlineOrdersLoading(false);
      }
    },
    [
      API_BASE,
      currentRole,
      currentUser?.id,
      fetchWithTimeout,
      onlineMenuOrderingEnabled,
      playOnlineOrderSound,
    ],
  );

  const refreshOnlineOrdersQuick = useCallback(() => {
    void fetchOnlineOrders({ silent: true });
  }, [fetchOnlineOrders]);

  const openOnlineAcceptModal = (order) => {
    if (!order) return;
    setOnlineAssignOrder(order);
    setOnlineAssignCovers("");
    const clientTableLabel = String(order?.table_label_client || "").trim();
    const clientTableNumber = Number(clientTableLabel);
    const matchedClientTable =
      Number.isFinite(clientTableNumber) && clientTableNumber > 0
        ? (tables || []).find((table) => Number(table?.number) === clientTableNumber)
        : null;
    const preferredTable =
      matchedClientTable?.id ||
      (selectedTable && !selectedTable?.isDelivery ? Number(selectedTable.id) : null);
    setOnlineAssignTableId(preferredTable ? String(preferredTable) : "");
  };

  const closeOnlineAcceptModal = () => {
    setOnlineAssignOrder(null);
    setOnlineAssignTableId("");
    setOnlineAssignCovers("");
  };

  const submitOnlineOrderAccept = async () => {
    const orderId = Number(onlineAssignOrder?.id);
    const tableId = Number(onlineAssignTableId);
    if (!Number.isFinite(orderId) || orderId <= 0) return;
    if (!Number.isFinite(tableId) || tableId <= 0) {
      setOnlineOrdersError("Choisis une table valide pour accepter la commande.");
      return;
    }

    setOnlineActionLoadingId(orderId);
    try {
      const payload = {
        target_table_id: tableId,
        covers_count: Number(onlineAssignCovers || 0) || null,
        validation_context: {
          user_id: currentUser?.id || null,
          role: getOnlineActorRole(),
          name: getCurrentActorName(),
        },
      };
      const response = await fetchWithTimeout(`${API_BASE}/online-orders/${orderId}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || "Impossible d'accepter la commande");
      }
      closeOnlineAcceptModal();
      await fetchOnlineOrders({ silent: true, syncNow: true });
      scheduleLoadTables("online-order-accept", { delayMs: 150 });
      triggerSuccessAnimation("Commande en ligne acceptée");
    } catch (error) {
      setOnlineOrdersError(error?.message || "Erreur d'acceptation");
      triggerErrorAnimation(error?.message || "Erreur d'acceptation");
    } finally {
      setOnlineActionLoadingId(null);
    }
  };

  const submitOnlineOrderReject = async (orderId, reason = "") => {
    const parsedId = Number(orderId);
    if (!Number.isFinite(parsedId) || parsedId <= 0) return;
    if (!canRejectOnlineOrders) return;

    setOnlineActionLoadingId(parsedId);
    try {
      const response = await fetchWithTimeout(`${API_BASE}/online-orders/${parsedId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason: String(reason || "").trim() || "Commande non retenue",
          validation_context: {
            user_id: currentUser?.id || null,
            role: getOnlineActorRole(),
            name: getCurrentActorName(),
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || "Impossible de refuser la commande");
      }
      setOnlineRejectDraft({ id: null, reason: "" });
      await fetchOnlineOrders({ silent: true, syncNow: true });
      triggerSuccessAnimation("Commande en ligne refusée");
    } catch (error) {
      setOnlineOrdersError(error?.message || "Erreur de refus");
      triggerErrorAnimation(error?.message || "Erreur de refus");
    } finally {
      setOnlineActionLoadingId(null);
    }
  };

  const submitOnlineOrderStatusChange = async (orderId, action, successMessage) => {
    const parsedId = Number(orderId);
    if (!Number.isFinite(parsedId) || parsedId <= 0) return;
    if (!canAcceptOnlineOrders) return;

    setOnlineActionLoadingId(parsedId);
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/online-orders/${parsedId}/${action}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            validation_context: {
              user_id: currentUser?.id || null,
              role: getOnlineActorRole(),
              name: getCurrentActorName(),
            },
          }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || "Mise à jour impossible");
      }
      await fetchOnlineOrders({ silent: true, syncNow: true });
      triggerSuccessAnimation(successMessage || "Statut mis à jour");
    } catch (error) {
      setOnlineOrdersError(error?.message || "Erreur de mise à jour");
      triggerErrorAnimation(error?.message || "Erreur de mise à jour");
    } finally {
      setOnlineActionLoadingId(null);
    }
  };

  const getOnlineOrderAmount = (order) => {
    const raw =
      order?.total_amount ??
      order?.totalAmount ??
      order?.payload?.total ??
      order?.payload?.total_amount ??
      0;
    const amount = Number(raw);
    return Number.isFinite(amount) ? amount : 0;
  };

  const getOnlineOrderDisplayCode = (order) =>
    String(order?.tracking_display_code || "").trim() ||
    String(order?.tracking_code || "").trim() ||
    (Number(order?.id) > 0 ? `#${order.id}` : "—");

  const getOnlineOrderItemName = (item) =>
    String(item?.product_name || item?.name || "Article").trim();

  const getOnlineOrderItemVariationLabel = (item) => {
    if (item?.variation_name) return String(item.variation_name);
    const attrs = item?.variation_attributes;
    if (attrs && typeof attrs === "object" && !Array.isArray(attrs)) {
      return Object.entries(attrs)
        .map(([key, value]) => `${key}: ${value}`)
        .join(" · ");
    }
    return "";
  };

  const getOnlineOrderLineTotal = (item) => {
    const explicit = Number(item?.total_price);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    const unit = Number(item?.unit_price || item?.price || 0);
    const qty = Number(item?.quantity || 0);
    const computed = unit * qty;
    return Number.isFinite(computed) ? computed : 0;
  };

  const getOnlineOrderItemsCount = (order) => {
    const list = Array.isArray(order?.items) ? order.items : [];
    return list.reduce((sum, item) => {
      const qty = Number(item?.quantity || 0);
      return sum + (Number.isFinite(qty) ? Math.max(0, qty) : 0);
    }, 0);
  };

  const availableOnlineAcceptTables = useMemo(() => {
    if (!Array.isArray(tables)) return [];
    const actorId = Number(currentUser?.id || 0) || 0;
    const isPrivileged = ["admin", "manager"].includes(currentRole);

    return tables
      .filter((table) => {
        if (!table || table.number === 999 || table.isDelivery) return false;
        const status = String(table.status || "").toLowerCase();
        if (status === "maintenance") return false;
        if (isPrivileged) return true;
        if (status === "free") return true;
        const ownerId = Number(table.active_owner_user_id || 0) || 0;
        return actorId > 0 && ownerId > 0 && actorId === ownerId;
      })
      .sort((a, b) => Number(a.number || 0) - Number(b.number || 0));
  }, [tables, currentRole, currentUser?.id]);

  useEffect(() => {
    if (!onlineAssignOrder) return;
    const isValid = availableOnlineAcceptTables.some(
      (table) => String(table.id) === String(onlineAssignTableId || ""),
    );
    if (isValid) return;
    const fallback = availableOnlineAcceptTables[0];
    setOnlineAssignTableId(fallback ? String(fallback.id) : "");
  }, [onlineAssignOrder, onlineAssignTableId, availableOnlineAcceptTables]);

  useEffect(() => {
    if (onlineOrdersPollTimerRef.current) {
      clearInterval(onlineOrdersPollTimerRef.current);
      onlineOrdersPollTimerRef.current = null;
    }
    if (!onlineMenuOrderingEnabled) {
      setOnlineOrders([]);
      setOnlineUnreadCount(0);
      return undefined;
    }

    void fetchOnlineOrders({ syncNow: true });
    const stopOnlinePolling = () => {
      if (onlineOrdersPollTimerRef.current) {
        clearInterval(onlineOrdersPollTimerRef.current);
        onlineOrdersPollTimerRef.current = null;
      }
    };
    const startOnlinePolling = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (onlineOrdersPollTimerRef.current) return;
      onlineOrdersPollTimerRef.current = setInterval(() => {
        if (typeof document !== "undefined" && document.hidden) return;
        void fetchOnlineOrders({ silent: true });
      }, ONLINE_ORDERS_POLL_INTERVAL_MS);
    };
    const handleVisibilityChange = () => {
      if (typeof document !== "undefined" && document.hidden) {
        stopOnlinePolling();
        return;
      }
      void fetchOnlineOrders({ silent: true, syncNow: true });
      startOnlinePolling();
    };
    const handleFocus = () => {
      void fetchOnlineOrders({ silent: true, syncNow: true });
      startOnlinePolling();
    };

    startOnlinePolling();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", handleFocus);
    }

    return () => {
      stopOnlinePolling();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", handleFocus);
      }
    };
  }, [fetchOnlineOrders, onlineMenuOrderingEnabled]);

  const fetchAdminLastAmount = async (cashierId) => {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/cash-sessions/last-amount/${cashierId}`,
      );
      if (response.ok) {
        const data = await response.json();
        setAdminLastAmount(data.lastAmount || 0);
        return;
      }
    } catch (error) {
      console.error("Erreur récupération dernier montant admin:", error);
    }
    setAdminLastAmount(0);
  };

  const fetchActiveCashSession = async (cashierId) => {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/cash-sessions/active`,
      );
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      // Pas de session active (404 attendu)
    }
    return null;
  };

  const recoverAuthenticatedCashSession = async () => {
    let verifiedUser = currentUser;
    try {
      // Utiliser le même transport authentifié que toutes les mutations de
      // caisse. Un appel fetch brut pouvait tomber sur un cookie ancien alors
      // que l'interface affichait encore l'utilisateur du Bearer courant.
      const meResponse = await fetchWithTimeout(`${API_BASE}/auth/me`);
      if (!meResponse.ok) throw new Error("Session d’authentification invalide");
      const mePayload = await meResponse.json();
      verifiedUser = mePayload?.user || null;
      if (!verifiedUser?.id || normalizeRoleKey(verifiedUser.role) === "superadmin") {
        throw new Error("Identité de caisse non autorisée");
      }
      setCurrentUser(verifiedUser);
    } catch (error) {
      triggerErrorAnimation(error?.message || "Identité de caisse non vérifiée");
      return null;
    }
    const actorId = Number(verifiedUser?.id);
    if (!Number.isInteger(actorId) || actorId <= 0) return null;

    setCurrentSession(null);
    try {
      localStorage.removeItem("moo_cash_session");
    } catch (_) {}

    let session = await fetchActiveCashSession(actorId);
    if (!session?.id) {
      const role = normalizeRoleKey(verifiedUser?.role || "");
      const shouldPromptCashDrawer =
        role === "cashier" ||
        role === "caissier" ||
        role === "caissiere" ||
        role === "caisse" ||
        role === "admin";

      if (shouldPromptCashDrawer) {
        await fetchAdminLastAmount(actorId);
        setShowAdminCashDrawer(true);
        triggerErrorAnimation(
          "Session de caisse à confirmer avant de reprendre le paiement.",
        );
        return null;
      }

      const createResponse = await fetchWithTimeout(
        `${API_BASE}/cash-sessions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cashierId: actorId,
            terminalId: getPersistentDeviceId(),
            initialAmount: 0,
            notes: "Session restaurée après changement d’utilisateur",
          }),
        },
      ).catch(() => null);

      if (createResponse?.ok) {
        session = await createResponse.json();
      } else if (createResponse?.status === 409) {
        session = await fetchActiveCashSession(actorId);
      }
    }

    if (session?.id) {
      const ownerId = Number(session.cashier_id ?? session.user_id);
      if (Number.isInteger(ownerId) && ownerId > 0 && ownerId !== actorId) {
        triggerErrorAnimation("La session de caisse reçue appartient à un autre utilisateur.");
        return null;
      }
      setCurrentSession(session);
      localStorage.setItem("moo_cash_session", JSON.stringify(session));
      const recovery =
        fastFoodCartSnapshotRef.current || readFastFoodCartSnapshotFromStorage();
      if (recovery?.items?.length) {
        const nextRecovery = {
          ...recovery,
          payment_payload: recovery.payment_payload
            ? { ...recovery.payment_payload, sessionId: session.id }
            : recovery.payment_payload,
          timestamp: Date.now(),
        };
        fastFoodCartSnapshotRef.current = nextRecovery;
        writeFastFoodCartSnapshotToStorage(nextRecovery);
        setCurrentOrderForPayment((previous) =>
          previous
            ? {
                ...previous,
                counter_recovery: previous.counter_recovery
                  ? {
                      ...previous.counter_recovery,
                      payment_payload: previous.counter_recovery.payment_payload
                        ? {
                            ...previous.counter_recovery.payment_payload,
                            sessionId: session.id,
                          }
                        : previous.counter_recovery.payment_payload,
                    }
                  : previous.counter_recovery,
              }
            : previous,
        );
      }
      triggerSuccessAnimation("Session de caisse actualisée. Confirmez à nouveau.");
      return session;
    }

    triggerErrorAnimation("Aucune session de caisse active pour cet utilisateur.");
    return null;
  };

  useEffect(() => {
    const actorId = Number(currentUser?.id);
    const sessionOwnerId = Number(
      currentSession?.cashier_id ?? currentSession?.user_id,
    );
    if (
      !Number.isInteger(actorId) ||
      actorId <= 0 ||
      !Number.isInteger(sessionOwnerId) ||
      sessionOwnerId <= 0 ||
      actorId === sessionOwnerId ||
      isResolvingSessionRef.current
    ) {
      return;
    }
    void recoverAuthenticatedCashSession();
  }, [
    currentUser?.id,
    currentSession?.id,
    currentSession?.cashier_id,
    currentSession?.user_id,
  ]);
  // Normaliser la destination pour l'API backend
  const normalizeDestination = (dest) => {
    const value = String(dest || "").trim().toLowerCase();
    if (value === "bar") return "bar";
    if (value === "kitchen" || value === "cuisine") return "cuisine";
    if (!value || value === "none" || value === "aucun" || value === "aucune") {
      return "none";
    }
    // Les destinations configurées (pâtisserie, grill, froid…) doivent rester
    // intactes dans le cache de caisse. Le serveur relit ensuite la fiche
    // produit en base comme autorité avant tout envoi.
    return value;
  };

  // Vérifie si deux clients de livraison représentent la même personne
  const isSameDeliveryCustomer = (customerA, customerB) => {
    if (!customerA || !customerB) return false;
    if (customerA.id && customerB.id && customerA.id === customerB.id) {
      return true;
    }
    const methodA = getDeliveryMethodValue(customerA, "Direct");
    const methodB = getDeliveryMethodValue(customerB, "Direct");
    if (
      methodA !== "Direct" &&
      methodB !== "Direct" &&
      methodA === methodB
    ) {
      const refA = normalizeDeliveryReferenceValue(
        getDeliveryReferenceValue(customerA),
      );
      const refB = normalizeDeliveryReferenceValue(
        getDeliveryReferenceValue(customerB),
      );
      if (refA && refB && refA === refB) {
        return true;
      }
    }
    const phoneA =
      typeof customerA.phone === "string"
        ? customerA.phone.trim()
        : customerA.phone || "";
    const phoneB =
      typeof customerB.phone === "string"
        ? customerB.phone.trim()
        : customerB.phone || "";
    if (phoneA && phoneB && phoneA === phoneB) {
      return true;
    }
    return false;
  };

  const formatDeliveryCustomerName = (customer) => {
    if (!customer) return null;
    if (typeof customer.name === "string" && customer.name.trim()) {
      return customer.name.trim();
    }
    const parts = [
      customer.civility,
      customer.first_name || customer.firstName,
      customer.last_name || customer.lastName,
    ].filter((part) => typeof part === "string" && part.trim());
    if (parts.length > 0) {
      return parts.join(" ").trim();
    }
    if (typeof customer.company === "string" && customer.company.trim()) {
      return customer.company.trim();
    }
    if (typeof customer.phone === "string" && customer.phone.trim()) {
      return customer.phone.trim();
    }
    return null;
  };

const getDeliveryTabLabel = (tab) => {
  if (!tab) return "Livraison";
  const methodValue = (tab.deliveryMethod || "Direct").toString().trim();
  if (methodValue !== "Direct") {
    const reference =
      tab.deliveryRef ||
      tab.deliveryOrder?.deliveryRef ||
      getDeliveryReferenceValue(tab.deliveryCustomer) ||
      "";
    if (reference) {
      const code = deliveryMethodCodes[tab.deliveryMethod] || methodValue;
      return `${code}${reference}`;
    }
    return methodValue;
  }

  const customerName =
    formatDeliveryCustomerName(tab.deliveryCustomer || tab.customer) ||
    formatDeliveryCustomerName(tab.customer);
  if (customerName) {
    return customerName;
  }
  if (typeof tab.customerName === "string" && tab.customerName.trim()) {
    return tab.customerName.trim();
  }
  if (typeof tab.customerPhone === "string" && tab.customerPhone.trim()) {
    return tab.customerPhone.trim();
  }
  return tab.name || tab.number || tab.orderNumber || "Livraison";
};

  const handleServiceModeSelect = (mode) => {
    setDeliveryMode((prevMode) => {
      const nextMode = prevMode === mode ? "" : mode;
      if (nextMode !== "delivery") {
        setDeliveryCustomer(null);
        setDeliveryCustomerSearch("");
        setDeliveryRef("");
      }
      if (nextMode !== "delivery") {
        setDeliveryMethod("Direct");
      }
      return nextMode;
    });
  };

  const formatFCFA = (value) => {
    const amount = Number(value || 0);
    return `${amount.toLocaleString("fr-FR")} F`;
  };

const computeItemsTotal = (items = []) =>
  items.reduce((sum, item) => {
    if (!item) return sum;
    const basePrice =
      item.price ??
      item.unitPrice ??
      item.product_price ??
      item.totalPrice ??
      0;
    const extrasPrice = Array.isArray(item.extras)
      ? item.extras.reduce(
          (extraSum, extra) =>
            extraSum +
            (extra && (extra.price ?? extra.unitPrice ?? 0)) *
              (extra?.quantity || 1),
          0,
        )
      : 0;
    return sum + (basePrice + extrasPrice) * (item.quantity || 1);
  }, 0);

const formatPrice = (value) =>
  `${Number(value || 0).toLocaleString("fr-FR")} F`;

const normalizeMethodLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const parseAmountInput = (value) => {
  if (value === null || value === undefined) return 0;
  const cleaned = String(value).replace(/[^\d.-]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseVariationAttributesValue = (raw) => {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return parsed;
      }
    } catch (_) {
      return null;
    }
  } else if (typeof raw === "object") {
    return raw;
  }
  return null;
};

const buildVariationLabelFromParts = (name, attributes) => {
  const parts = [];
  if (name && name.toString().trim()) {
    parts.push(name.toString().trim());
  }
  if (attributes) {
    if (Array.isArray(attributes)) {
      const attrValues = attributes
        .map((value) => value && value.toString().trim())
        .filter(Boolean);
      if (attrValues.length) {
        parts.push(attrValues.join(" / "));
      }
    } else if (typeof attributes === "object") {
      const attrValues = Object.values(attributes)
        .map((value) => value && value.toString().trim())
        .filter(Boolean);
      if (attrValues.length) {
        parts.push(attrValues.join(" / "));
      }
    }
  }
  return parts.join(" · ");
};

const buildVariationAttributePairs = (variation) => {
  if (!variation) return [];
  const attrs = parseVariationAttributesValue(variation.attributes);
  if (!attrs) return [];
  if (Array.isArray(attrs)) {
    return attrs
      .map((value, index) => ({
        name: `Option ${index + 1}`,
        value,
      }))
      .filter((pair) => pair.value);
  }
  return Object.entries(attrs)
    .map(([name, value]) => ({
      name,
      value,
    }))
    .filter((pair) => pair.value);
};

const getItemVariationLabel = (item = {}) => {
  const directLabelRaw =
    item.variation_label ??
    item.variationLabel ??
    item.variation_name ??
    item.variationName ??
    "";

  const rawAttributes =
    item.variation_attributes ?? item.variationAttributes ?? null;
  const parsedAttributes = parseVariationAttributesValue(rawAttributes);

  const attributesText = (() => {
    if (!parsedAttributes) return "";
    if (Array.isArray(parsedAttributes)) {
      return parsedAttributes.filter(Boolean).join(" / ");
    }
    if (typeof parsedAttributes === "object") {
      return Object.values(parsedAttributes)
        .filter((value) => value !== undefined && value !== null && value !== "")
        .join(" / ");
    }
    return "";
  })();

  if (directLabelRaw) {
    const normalizedLabel = directLabelRaw.toString().trim();
    const looksGeneric = /^variation\s*\d*/i.test(normalizedLabel);
    if (looksGeneric && attributesText) {
      return attributesText;
    }
    if (/^variation\b/i.test(normalizedLabel) && attributesText) {
      return attributesText;
    }
    return normalizedLabel;
  }

  if (attributesText) {
    return attributesText;
  }

  return "";
};

const normalizeVariationLabel = (label) => {
  if (!label) return "";
  const raw = String(label).trim();
  if (!raw) return "";

  const separators = [
    { pattern: /\s+·\s+/g, joiner: " · " },
    { pattern: /\s+\/\s+/g, joiner: " / " },
    { pattern: /\s+\|\s+/g, joiner: " | " },
    { pattern: /\s+-\s+/g, joiner: " - " },
  ];

  for (const { pattern, joiner } of separators) {
    const parts = raw
      .split(pattern)
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length < 2) continue;
    const seen = new Set();
    const deduped = [];
    for (const part of parts) {
      const key = part.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(part);
    }
    if (deduped.length < parts.length) {
      return deduped.join(joiner);
    }
  }

  return raw;
};

const getItemVariationAttributesDisplay = (item = {}) => {
  const rawAttributes =
    item.variation_attributes ?? item.variationAttributes ?? null;
  const parsed = parseVariationAttributesValue(rawAttributes);
  if (!parsed) return "";
  if (Array.isArray(parsed)) {
    return parsed.filter(Boolean).join(" / ");
  }
  if (typeof parsed === "object") {
    return Object.values(parsed)
      .filter((value) => value !== undefined && value !== null && value !== "")
      .join(" / ");
  }
  return "";
};

 const getItemVariationDisplay = (item = {}) => {
  const attributesOnly = getItemVariationAttributesDisplay(item);
  if (attributesOnly) return attributesOnly;
  return getItemVariationLabel(item);
};

const extractVariationKeyFromItemKey = (itemKey) => {
  if (!itemKey || typeof itemKey !== "string") return null;
  const match = itemKey.match(/(?:^|_)var:([^_]+)(?:_|$)/i);
  if (!match?.[1]) return null;
  const raw = String(match[1]).trim();
  if (!raw || raw.toLowerCase() === "base") return null;
  return raw;
};

const isNumericVariationKey = (value) => /^\d+$/.test(String(value || "").trim());

const findVariationInProduct = (product, variationKey) => {
  if (!product || !variationKey) return null;
  const variations = Array.isArray(product.variations) ? product.variations : [];
  if (!variations.length) return null;

  const key = String(variationKey).trim();
  if (isNumericVariationKey(key)) {
    const id = Number(key);
    return (
      variations.find((v) => {
        const vId = v?.variation_id ?? v?.variationId ?? v?.id ?? null;
        return vId !== null && vId !== undefined && Number(vId) === id;
      }) || null
    );
  }

  const normalizedKey = key.toLowerCase();
  return (
    variations.find((v) => {
      const name = (
        v?.variation_name ??
        v?.variationName ??
        v?.name ??
        ""
      )
        .toString()
        .trim()
        .toLowerCase();
      if (name && name === normalizedKey) return true;

      const attrs = parseVariationAttributesValue(v?.attributes);
      const attrsText = (() => {
        if (!attrs) return "";
        if (Array.isArray(attrs)) return attrs.filter(Boolean).join(" / ");
        if (typeof attrs === "object") {
          return Object.values(attrs)
            .filter((value) => value !== undefined && value !== null && value !== "")
            .join(" / ");
        }
        return "";
      })()
        .toString()
        .trim()
        .toLowerCase();
      if (attrsText && attrsText === normalizedKey) return true;

      const label = buildVariationLabelFromParts(
        v?.name ?? v?.variation_name ?? v?.variationName ?? "",
        attrs,
      )
        .toString()
        .trim()
        .toLowerCase();
      return label && label === normalizedKey;
    }) || null
  );
};

const resolveVariationForPayload = (item, product) => {
  const explicitIdRaw =
    item?.variation_id ??
    item?.variationId ??
    item?.variationID ??
    item?.selectedVariationId ??
    item?.default_variation_id ??
    item?.variation?.id ??
    item?.variation?.variation_id ??
    item?.variation?.variationId ??
    null;
  const explicitId =
    explicitIdRaw !== null && explicitIdRaw !== undefined && explicitIdRaw !== ""
      ? Number(explicitIdRaw)
      : null;

  const keyFromItemKey = extractVariationKeyFromItemKey(item?.itemKey);
  const labelCandidate =
    item?.variation_label ??
    item?.variationLabel ??
    item?.variation_name ??
    item?.variationName ??
    null;

  const candidates = [
    explicitIdRaw,
    keyFromItemKey,
    labelCandidate,
    // En dernier recours: ce que l'UI sait afficher.
    getItemVariationDisplay(item),
  ]
    .filter((v) => v !== undefined && v !== null && String(v).trim() !== "");

  let matchedVariation = null;
  for (const candidate of candidates) {
    matchedVariation = findVariationInProduct(product, candidate);
    if (matchedVariation) break;
  }

  const resolvedVariationId = (() => {
    if (matchedVariation) {
      const vId =
        matchedVariation?.variation_id ??
        matchedVariation?.variationId ??
        matchedVariation?.id ??
        null;
      const parsed = vId !== null && vId !== undefined ? Number(vId) : null;
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (explicitId !== null && Number.isFinite(explicitId)) return explicitId;
    if (keyFromItemKey && isNumericVariationKey(keyFromItemKey)) {
      const parsed = Number(keyFromItemKey);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  })();

  const resolvedAttributes = (() => {
    if (matchedVariation?.attributes) {
      return parseVariationAttributesValue(matchedVariation.attributes);
    }
    const raw = item?.variation_attributes ?? item?.variationAttributes ?? null;
    return parseVariationAttributesValue(raw);
  })();

  const resolvedName = (() => {
    if (matchedVariation) {
      const name =
        matchedVariation?.variation_name ??
        matchedVariation?.variationName ??
        matchedVariation?.name ??
        null;
      return name ? String(name).trim() : null;
    }
    const name = item?.variation_name ?? item?.variationName ?? null;
    return name ? String(name).trim() : null;
  })();

  const resolvedLabel = (() => {
    if (matchedVariation) {
      return buildVariationLabelFromParts(
        matchedVariation?.name ??
          matchedVariation?.variation_name ??
          matchedVariation?.variationName ??
          "",
        resolvedAttributes,
      );
    }
    const label = item?.variation_label ?? item?.variationLabel ?? null;
    if (label) return String(label).trim();
    if (keyFromItemKey && !isNumericVariationKey(keyFromItemKey)) return keyFromItemKey;
    return buildVariationLabelFromParts(resolvedName, resolvedAttributes);
  })();

  return {
    variation: matchedVariation,
    variationId: resolvedVariationId,
    variationName: resolvedName,
    variationAttributes: resolvedAttributes,
    variationLabel: resolvedLabel,
  };
};

// True only when the item is truly un produit variable (ou porte des données de variation) ET possède un libellé de variation distinct.
const hasVariationDisplay = (item = {}) => {
  const variationText = getItemVariationDisplay(item);
  if (!variationText) return false;
  const hasVariationData =
    item.product_type === "variable" ||
    item.variation_attributes ||
    item.variationName ||
    item.variationLabel ||
    item.variation_name;
  // Évite d'afficher un doublon sur les produits simples dont le libellé serait identique au nom
  if (variationText === item.name) return false;
  return !!hasVariationData;
};

// Parse robuste des montants (gère espaces, NBSP, symbole "F", séparateurs FR).
const parseMoney = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const raw = String(value)
    .replace(/\u202f/g, " ") // narrow no-break space
    .replace(/\u00a0/g, " ") // nbsp
    .trim();
  if (!raw) return 0;

  // Conserver uniquement chiffres + séparateurs usuels.
  let cleaned = raw.replace(/[^\d,.\-]/g, "");
  if (!cleaned) return 0;

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");

  if (lastComma !== -1 && lastDot !== -1) {
    // Le dernier séparateur rencontré est le séparateur décimal.
    if (lastComma > lastDot) {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      cleaned = cleaned.replace(/,/g, "");
    }
  } else if (lastComma !== -1) {
    cleaned = cleaned.replace(",", ".");
  }

  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Prix unitaire effectif (gère promo menu du jour / variations).
const getUnitPriceForSelection = (product, variation) => {
  if (variation) {
    if (variation.effective_price_for_day !== undefined && variation.effective_price_for_day !== null) {
      return parseMoney(variation.effective_price_for_day);
    }
    if (variation.daily_menu_price !== undefined && variation.daily_menu_price !== null) {
      return parseMoney(variation.daily_menu_price);
    }
    if (variation.display_price !== undefined && variation.display_price !== null) {
      return parseMoney(variation.display_price);
    }
    return (
      parseMoney(variation.price) ||
      parseMoney(variation.price_base) ||
      parseMoney(product?.price) ||
      0
    );
  }

  if (product?.effective_price_for_day !== undefined && product?.effective_price_for_day !== null) {
    return parseMoney(product.effective_price_for_day);
  }
  if (product?.daily_menu_price !== undefined && product?.daily_menu_price !== null) {
    return parseMoney(product.daily_menu_price);
  }
  if (product?.display_price !== undefined && product?.display_price !== null) {
    return parseMoney(product.display_price);
  }
  if (product?.price_min_today !== undefined && product?.price_min_today !== null) {
    return parseMoney(product.price_min_today);
  }
  return parseMoney(product?.price);
};

const getProductDisplayPrice = (product) => {
  if (!product) return 0;
  if (Array.isArray(product.variations) && product.variations.length > 0) {
    const prices = product.variations
      .filter((v) => v && v.is_active !== false)
      .map((v) => {
        if (v.effective_price_for_day !== undefined && v.effective_price_for_day !== null) {
          return parseMoney(v.effective_price_for_day);
        }
        if (v.daily_menu_price !== undefined && v.daily_menu_price !== null) {
          return parseMoney(v.daily_menu_price);
        }
        if (v.display_price !== undefined && v.display_price !== null) {
          return parseMoney(v.display_price);
        }
        return parseMoney(v.price);
      });
    if (prices.length) {
      return Math.min(...prices);
    }
  }
  if (product.price_min_today !== undefined && product.price_min_today !== null) {
    return parseMoney(product.price_min_today);
  }
  if (product.display_price !== undefined && product.display_price !== null) {
    return parseMoney(product.display_price);
  }
  if (product.effective_price_for_day !== undefined && product.effective_price_for_day !== null) {
    return parseMoney(product.effective_price_for_day);
  }
  return parseMoney(product.price);
};

const getProductPriceRange = (product) => {
  if (!product || !Array.isArray(product.variations) || !product.variations.length) {
    const price = getProductDisplayPrice(product);
    return { min: price, max: price };
  }
  const prices = product.variations
    .filter((v) => v && v.is_active !== false)
    .map((v) => {
      if (v.effective_price_for_day !== undefined && v.effective_price_for_day !== null) {
        return parseMoney(v.effective_price_for_day);
      }
      if (v.daily_menu_price !== undefined && v.daily_menu_price !== null) {
        return parseMoney(v.daily_menu_price);
      }
      if (v.display_price !== undefined && v.display_price !== null) {
        return parseMoney(v.display_price);
      }
      return parseMoney(v.price);
    });
  if (
    product.daily_menu_active_today &&
    product.daily_menu_price !== null &&
    product.daily_menu_price !== undefined
  ) {
    prices.push(parseMoney(product.daily_menu_price));
  }
  if (!prices.length) {
    const price = getProductDisplayPrice(product);
    return { min: price, max: price };
  }
  return { min: Math.min(...prices), max: Math.max(...prices) };
};

const getAggregatorProductPrice = (product, aggregatorCode) => {
  return readAggregatorPrice(product, aggregatorCode);
};

const getAggregatorVariationPrice = (variation, aggregatorCode) => {
  return readAggregatorPrice(variation, aggregatorCode);
};

const getProductDisplayPriceForMode = (product, aggregatorCode) => {
  const aggregatorPrice = getAggregatorProductPrice(product, aggregatorCode);
  return aggregatorPrice ?? getProductDisplayPrice(product);
};

const getProductPriceRangeForMode = (product, aggregatorCode) => {
  const aggregatorPrice = getAggregatorProductPrice(product, aggregatorCode);
  if (aggregatorPrice !== null) {
    return { min: aggregatorPrice, max: aggregatorPrice };
  }
  const code = normalizeAggregatorCode(aggregatorCode);
  const variationPrices =
    code && Array.isArray(product?.variations)
      ? product.variations
          .map((variation) => getAggregatorVariationPrice(variation, code))
          .filter((price) => price !== null)
      : [];
  if (variationPrices.length > 0) {
    return {
      min: Math.min(...variationPrices),
      max: Math.max(...variationPrices),
    };
  }
  return getProductPriceRange(product);
};

const getUnitPriceForSelectionMode = (product, variation, aggregatorCode) => {
  const variationAggregatorPrice = getAggregatorVariationPrice(variation, aggregatorCode);
  if (variationAggregatorPrice !== null) return variationAggregatorPrice;
  const aggregatorPrice = getAggregatorProductPrice(product, aggregatorCode);
  if (aggregatorPrice !== null) return aggregatorPrice;
  return getUnitPriceForSelection(product, variation);
};

const buildVariationMetadata = (source) => {
  if (!source) {
    return {
      variationId: null,
      variation_id: null,
      variationName: null,
      variation_name: null,
      variationAttributes: null,
      variation_attributes: null,
      variationLabel: "",
      variation_label: "",
    };
  }
  const isLikelyVariationObject =
    (source?.order_item_id === undefined && source?.orderItemId === undefined) &&
    (source?.quantity === undefined && source?.qty === undefined) &&
    (source?.attributes !== undefined && source?.attributes !== null);
  const fallbackKey = extractVariationKeyFromItemKey(source.itemKey);
  const variationId =
    source.variation_id ??
    source.variationId ??
    source.default_variation_id ??
    source.selectedVariationId ??
    (isLikelyVariationObject ? source.id : null) ??
    (fallbackKey && isNumericVariationKey(fallbackKey) ? Number(fallbackKey) : null);
  const variationName =
    source.variation_name ??
    source.variationName ??
    (isLikelyVariationObject ? source.name : null) ??
    null;
  const rawAttributes =
    source.variation_attributes ??
    source.variationAttributes ??
    source.attributes ??
    null;
  const parsedAttributes = parseVariationAttributesValue(rawAttributes);
  let variationLabel = source.variation_label ?? source.variationLabel ?? null;
  if (!variationLabel) {
    if (fallbackKey && !isNumericVariationKey(fallbackKey)) {
      variationLabel = fallbackKey;
    } else {
      variationLabel = buildVariationLabelFromParts(variationName, parsedAttributes);
    }
  }
  return {
    variationId,
    variation_id: variationId,
    variationName,
    variation_name: variationName,
    variationAttributes: parsedAttributes,
    variation_attributes: parsedAttributes,
    variationLabel,
    variation_label: variationLabel,
  };
};

const computeSupplementTotal = (extras = []) =>
  (extras || []).reduce((sum, extra) => {
    if (!extra || extra.type !== "supplement") return sum;
    return (
      sum +
      parseMoney(extra.price) *
        (extra.selectedQuantity || extra.quantity || 1)
    );
  }, 0);

const resolveVariationMetadata = (source) => {
  if (!source) {
    return buildVariationMetadata(null);
  }
  return buildVariationMetadata(source);
};

const getVariationLabelFromItem = (item = {}) => getItemVariationLabel(item);

const getItemDisplayName = (item = {}) => {
  const baseName =
    item.name ||
    item.product_name ||
    item.productName ||
    item.title ||
    "Article";
  const variationLabel = getItemVariationLabel(item);
  return variationLabel ? `${baseName} ${variationLabel}` : baseName;
};

  const normalizeTableLabel = (table) => {
    if (!table) return "Table";
    if (isBoutique) return "Vente boutique";
    if (table.isDelivery) {
      return `Mode : ${table.deliveryMethod || "Direct"}`;
    }
    if (table.number) return `Table ${table.number}`;
    if (table.name) {
      const match = String(table.name).match(/(\d+)/);
      if (match && match[1]) return `Table ${match[1]}`;
      return `Table ${table.name}`;
    }
    return "Table";
  };

  const ensureDualScreenChannel = () => {
    if (typeof BroadcastChannel === "undefined") return;
    try {
      if (!dualScreenChannelRef.current || dualScreenChannelRef.current._closed) {
        dualScreenChannelRef.current = new BroadcastChannel("moo-dual-screen");
      }
    } catch (_) {
      // Si le channel est fermé, on recréera au prochain appel
      dualScreenChannelRef.current = null;
    }
  };

  const closeDualScreenWindow = () => {
    if (window?.electronAPI?.closeCustomerDisplay) {
      void window.electronAPI.closeCustomerDisplay().catch(() => {});
    }
    try {
      if (dualScreenWindowRef.current && !dualScreenWindowRef.current.closed) {
        dualScreenWindowRef.current.close();
      }
    } catch (_) {}
    dualScreenWindowRef.current = null;
  };

  const postDualScreenMessage = (payload) => {
    ensureDualScreenChannel();
    if (!dualScreenChannelRef.current) return;
    try {
      dualScreenChannelRef.current.postMessage(payload);
    } catch (error) {
      console.warn("⚠�? BroadcastChannel fermé, recréation…", error?.message || error);
      dualScreenChannelRef.current = null;
      ensureDualScreenChannel();
      try {
        dualScreenChannelRef.current?.postMessage(payload);
      } catch (_) {
        // On garde silencieux si ça échoue encore
      }
    }
  };

  const sendDualScreenState = (force = false) => {
    if (!force && !isDualScreenActive) return;
    if (dualScreenWindowRef.current && dualScreenWindowRef.current.closed) {
      setIsDualScreenActive(false);
      localStorage.setItem("moo_dual_screen_active", "false");
      return;
    }

    const mergedItems = [
      ...(existingItemsForPayment || []),
      ...(currentOrder?.items || []),
    ].filter(Boolean);

    if (!selectedTable || mergedItems.length === 0) {
      postDualScreenMessage({
        type: "welcome",
        restaurant: { name: restaurantInfo?.name || "MOO Sync" },
        timestamp: Date.now(),
      });
      return;
    }

    const displayItems = mergedItems.map((item) => {
      const qty = item.quantity || 1;
      const basePrice =
        item.price ??
        item.unitPrice ??
        item.product_price ??
        item.totalPrice ??
        0;
      const variationLabel =
        item.variation_label ??
        item.variationLabel ??
        "";
      const extrasArray = Array.isArray(item.extras) ? item.extras : [];
      const extras = extrasArray
        .filter(Boolean)
        .map((extra) => ({
          name: extra.name || extra.title || "Extra",
          quantity: extra.quantity || 1,
          price: Number(
            extra.price ?? extra.unitPrice ?? extra.totalPrice ?? 0,
          ),
        }));
      const extrasTotal = extras.reduce(
        (sum, ex) => sum + (ex.price || 0) * (ex.quantity || 1),
        0,
      );
      const lineTotal = (Number(basePrice) + extrasTotal) * qty;
      return {
        name: item.name || item.product_name || "Article",
        quantity: qty,
        extras,
        total: lineTotal,
        variationLabel,
      };
    });

    const subtotal = computeItemsTotal(mergedItems);

    postDualScreenMessage({
      type: "cart-update",
      tableLabel: normalizeTableLabel(selectedTable),
      items: displayItems,
      subtotal,
      total: subtotal,
      restaurant: { name: restaurantInfo?.name || "MOO Sync" },
      timestamp: Date.now(),
    });
  };

  const openDualScreenWindow = async ({ silent = false } = {}) => {
    ensureDualScreenChannel();

    if (window?.electronAPI?.openCustomerDisplay) {
      try {
        const result = await window.electronAPI.openCustomerDisplay();
        if (!result?.ok) {
          setIsDualScreenActive(false);
          localStorage.setItem("moo_dual_screen_active", "false");
          if (!silent) {
            triggerErrorAnimation(
              result?.message || "Aucun deuxième écran n’est détecté.",
            );
          }
          return false;
        }
        setIsDualScreenActive(true);
        localStorage.setItem("moo_dual_screen_active", "true");
        setTimeout(() => sendDualScreenState(true), 250);
        return true;
      } catch (error) {
        if (!silent) {
          triggerErrorAnimation(
            error?.message || "Impossible d’ouvrir l’affichage client.",
          );
        }
        return false;
      }
    }

    const availTop = window.screen?.availTop ?? 0;
    const availWidth = window.screen?.availWidth ?? window.innerWidth;
    const availHeight = window.screen?.availHeight ?? window.innerHeight;
    const currentScreenLeft = window.screenLeft ?? window.screenX ?? 0;
    const currentScreenWidth = window.screen?.width ?? availWidth;
    const guessSecondLeft = currentScreenLeft + currentScreenWidth;
    const featureStr = `left=${guessSecondLeft},top=${availTop},width=${availWidth},height=${availHeight},popup=yes,toolbar=no,menubar=no,location=no,resizable=yes,scrollbars=no,status=no`;
    const win = window.open("/display", "MOO_SYNC_DISPLAY", featureStr);
    if (!win) {
      if (!silent) {
        triggerErrorAnimation("Autorisez les fenêtres surgissantes pour le deuxième écran.");
      }
      return false;
    }
    dualScreenWindowRef.current = win;
    setIsDualScreenActive(true);
    localStorage.setItem("moo_dual_screen_active", "true");
    setTimeout(() => {
      const geometry = {
        left: guessSecondLeft,
        top: availTop,
        width: availWidth,
        height: availHeight,
      };
      postDualScreenMessage({ type: "geometry", geometry });
      postDualScreenMessage({ type: "enter-fullscreen" });
      postDualScreenMessage({ type: "fit-screen" });
      sendDualScreenState(true);
    }, 400);
    return true;
  };
  openDualScreenWindowRef.current = openDualScreenWindow;

  const toggleDualScreen = async () => {
    if (!isDualScreenAllowed) {
      triggerErrorAnimation("Double écran désactivé dans l'admin");
      return;
    }
    if (!isDualScreenActive) {
      await openDualScreenWindow();
    } else {
      closeDualScreenWindow();
      setIsDualScreenActive(false);
      localStorage.setItem("moo_dual_screen_active", "false");
      postDualScreenMessage({ type: "welcome", restaurant: { name: restaurantInfo?.name || "MOO Sync" } });
    }
  };

  const toggleAppFullscreen = async () => {
    if (window?.electronAPI?.toggleFullscreen) {
      try {
        const result = await window.electronAPI.toggleFullscreen();
        setIsAppFullscreen(Boolean(result?.isFullscreen));
        return;
      } catch (_) {
        // Repli sur l'API navigateur ci-dessous.
      }
    }
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch (error) {
      triggerErrorAnimation("Le plein écran n’est pas disponible sur ce navigateur.");
    }
  };

const parseExtrasArray = (extrasPayload) => {
  if (!extrasPayload) return [];
  if (Array.isArray(extrasPayload)) return extrasPayload;
  if (typeof extrasPayload === "string") {
    try {
      const parsed = JSON.parse(extrasPayload);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  return [];
};

const normalizeDeliveryItemExtras = (item) => {
  if (!item) return null;
  const rawExtras =
    parseExtrasArray(item.extras) || parseExtrasArray(item.extras_json);
  const normalizedExtras = rawExtras
    .filter(Boolean)
    .map((extra) => {
      const resolvedType =
        extra.type ||
        extra.extra_type ||
        (extra.category === "accompaniment" ? "accompaniment" : null) ||
        "supplement";
      const resolvedPrice =
        Number(
          extra.price ??
            extra.unit_price ??
            extra.unitPrice ??
            extra.amount ??
            extra.total_price ??
            0,
        ) || 0;
      return {
        id: extra.id ?? extra.extra_id ?? extra.extraId ?? null,
        name: extra.name ?? extra.extra_name ?? extra.label ?? "",
        type: resolvedType,
        quantity: extra.quantity ?? extra.qty ?? 1,
        price: resolvedPrice,
      };
    });

  return {
    ...item,
    price:
      item.price ??
      item.unit_price ??
      item.unitPrice ??
      item.product_price ??
      item.basePrice ??
      item.totalPrice ??
      0,
    extras: normalizedExtras,
  };
};


  const handleSelectDeliveryTab = (tab, options = {}) => {
    if (!tab) return;
    const {
      focusMode = "view",
      overrideItems = null,
      groupOrderIds = null,
    } = options;
    const enrichedTab =
      Array.isArray(groupOrderIds) && groupOrderIds.length > 0
        ? { ...tab, groupOrderIds }
        : tab;
    setAutoSelectDeliveryTab(true);
    setActiveClientTab(enrichedTab);
    setSelectedTable(enrichedTab);
    if (
      tab.deliveryCustomer &&
      (tab.deliveryMethod || "Direct") === "Direct"
    ) {
      setDeliveryCustomer(tab.deliveryCustomer);
    } else if (!tab.isDelivery) {
      setDeliveryCustomer(tab.deliveryCustomer || null);
    } else {
      setDeliveryCustomer(null);
    }
    if (tab.isDelivery) {
      setDeliveryRef(tab.deliveryRef || "");
      setDeliveryMethod(tab.deliveryMethod || "Direct");
      if (tab.deliveryOrder) {
        setSelectedDeliveryOrder(tab.deliveryOrder);
      }
    } else {
      setSelectedDeliveryOrder(null);
    }
    const tabItems =
      (Array.isArray(overrideItems) ? overrideItems : null) ||
      (Array.isArray(tab.items) && tab.items) ||
      tab.deliveryOrder?.items ||
      [];
    const { existing, pending } = splitDeliveryItems(tabItems);
    setExistingDeliveryItems(existing);
    setHasExistingOrders(existing.length > 0);
    if (focusMode === "view") {
      setCurrentDeliveryOrder({
        items: pending,
        total: computeItemsTotal(pending),
      });
    }
    // Toujours mémoriser l'état dans le cache pour restauration après refresh
    updateTableStateCache(
      tab.id,
      {
        currentOrder: { items: pending, total: computeItemsTotal(pending) },
        existingItems: existing,
        hasExistingOrders: existing.length > 0,
      },
      { isDelivery: true },
    );
  };

const getDeliveryGroupingKey = (tab) => {
  if (!tab) return null;
  const methodValue = (tab.deliveryMethod || "Direct").toString().trim();
  if (methodValue !== "Direct") {
    const reference = getTabDeliveryReference(tab);
    if (reference) {
      return `${methodValue.toLowerCase()}-ref-${normalizeDeliveryReferenceValue(reference)}`;
    }
  }
  const customer =
    tab.deliveryCustomer || tab.customer || {
      phone: tab.customerPhone,
      id: tab.customerId,
    };
  if (customer?.id) return `${methodValue}-id-${customer.id}`;
  const phone =
    customer?.phone ||
    customer?.contact ||
    tab.customerPhone ||
    (tab.deliveryCustomerData &&
      (() => {
        try {
          const parsed = JSON.parse(tab.deliveryCustomerData);
          return parsed.phone;
        } catch (_) {
          return null;
        }
      })());
  if (phone) return `${methodValue}-phone-${phone}`;
  return `${methodValue}-tab-${tab.id}`;
};

const groupDeliveryTabs = (tabs = []) => {
  const groupedTabsMap = new Map();
  tabs.forEach((tab) => {
    if (!tab) return;
    const key = getDeliveryGroupingKey(tab) || `tab-${tab.id}`;
    const tabItems = Array.isArray(tab.items) ? tab.items : [];
    const entry =
      groupedTabsMap.get(key) || {
        key,
        tabs: [],
        mergedItems: [],
      };
    entry.tabs.push(tab);
    entry.mergedItems.push(...tabItems);
    groupedTabsMap.set(key, entry);
  });

  return Array.from(groupedTabsMap.values()).map((entry) => {
    const representative = entry.tabs[entry.tabs.length - 1] || entry.tabs[0];
    const mergedItems = entry.mergedItems;
    const orderIds = entry.tabs
      .map((tab) => tab?.orderId)
      .filter(Boolean);
    const tableIds = entry.tabs
      .map((tab) => tab?.originalTableId || tab?.tableId || tab?.id)
      .filter(Boolean);
    return {
      key: entry.key,
      tabs: entry.tabs,
      representative,
      mergedItems,
      orderIds,
      tableIds,
      label: getDeliveryTabLabel(representative),
      itemsCount: mergedItems.length,
      total: computeItemsTotal(mergedItems),
      deliveryMode: representative.deliveryMode,
      deliveryMethod: representative.deliveryMethod,
    };
  });
};

const getDeliveryCardsCount = () =>
  clientTabs.length > 0 ? groupDeliveryTabs(clientTabs).length : 0;

const getSelectedDeliveryOrderIds = (tab = selectedTable) => {
    if (!tab?.isDelivery) return [];
    if (Array.isArray(tab.groupOrderIds) && tab.groupOrderIds.length > 0) {
      return tab.groupOrderIds;
    }
    if (tab.orderId) return [tab.orderId];
    if (tab.deliveryOrder?.id) return [tab.deliveryOrder.id];
    const key = getDeliveryGroupingKey(tab);
    if (!key) return [];
    const grouped = groupDeliveryTabs(clientTabs);
    const targetGroup = grouped.find((group) => group.key === key);
    if (!targetGroup) return [];
    if (targetGroup.orderIds?.length) return targetGroup.orderIds;
    return targetGroup.tabs
      .map((groupTab) => groupTab.orderId)
      .filter(Boolean);
  };

  const renderDeliveryTabsList = (options = {}) => {
    const { focusMode = "view", scrollable = false } = options;
    if (!clientTabs.length) return null;

    const groupedTabs = groupDeliveryTabs(clientTabs);

    return (
      <div
        className={`space-y-3 ${
          scrollable
            ? "max-h-[30rem] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-400/40"
            : ""
        }`}
      >
        {groupedTabs.map((group) => {
          const label = group.label;
          const itemsCount = group.itemsCount;
          const totalFormatted = formatFCFA(group.total);
          const activeKey = getDeliveryGroupingKey(activeClientTab);
          const isActive =
            activeKey &&
            activeKey === getDeliveryGroupingKey(group.representative) &&
            !!activeClientTab;
          const deliveryTypeLabel =
            group.deliveryMode === "delivery" ? "Livraison" : "À emporter";
          const methodLabel =
            group.deliveryMethod && group.deliveryMethod !== "Direct"
              ? group.deliveryMethod
              : "Direct";

          return (
            <button
              key={group.key}
              onClick={() =>
                handleSelectDeliveryTab(group.representative, {
                  overrideItems: group.mergedItems,
                  groupOrderIds: group.orderIds,
                })
              }
              className={`w-full text-left rounded-2xl border-2 transition-all p-4 bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 ${
                isActive
                  ? "border-blue-400 ring-2 ring-blue-100"
                  : "border-gray-200"
              }`}
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="font-semibold text-gray-900">{label}</div>
                  <div className="text-xs text-gray-500">
                    {itemsCount} article{itemsCount > 1 ? "s" : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                      group.deliveryMode === "delivery"
                        ? "border-blue-400 text-blue-500 bg-blue-50"
                        : "border-green-400 text-green-600 bg-green-50"
                    }`}
                  >
                    {deliveryTypeLabel}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold border border-orange-300 text-orange-500 bg-orange-50">
                    {methodLabel}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {totalFormatted}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

const getTableIdentifier = (table) => {
  if (!table) return null;
  if (table.id !== undefined && table.id !== null) return table.id;
  if (table.table_id !== undefined && table.table_id !== null)
    return table.table_id;
  if (table.tableId !== undefined && table.tableId !== null)
    return table.tableId;
  if (table.originalTableId !== undefined && table.originalTableId !== null)
    return table.originalTableId;
  return null;
};

const getApiTableId = (table) => {
  if (!table) return null;
  if (
    table.isDelivery ||
    (typeof table.id === "string" && table.id.startsWith("delivery-"))
  ) {
    return (
      table.id ||
      table.tableId ||
      table.originalTableId ||
      table.table_id ||
      null
    );
  }
  const candidates = [
    table.originalTableId,
    table.tableId,
    table.table_id,
    table.id,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && !Number.isNaN(candidate)) {
      return candidate;
    }
    if (typeof candidate === "string" && candidate.trim()) {
      const direct = Number(candidate);
      if (!Number.isNaN(direct)) return direct;
      const match = candidate.match(/(\d+)/);
      if (match) {
        const parsed = Number(match[1]);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
  }
  return null;
};

const buildExistingItemStableKey = (item) => {
  if (!item) return "";
  const directId = item.order_item_id || item.id || null;
  if (directId !== null && directId !== undefined && String(directId).trim()) {
    return `item:${directId}`;
  }
  return [
    "fallback",
    item.orderId ?? item.order_id ?? "",
    item.product_id ?? item.productId ?? "",
    item.variation_id ?? item.variationId ?? "",
    item.quantity ?? 1,
    item.price ?? item.unit_price ?? "",
  ].join(":");
};

const mergeExistingItemsWithLatestFirst = (latestItems = [], previousItems = []) => {
  const mergeTimestamp = Date.now();
  const merged = [];
  const seen = new Set();
  [...(latestItems || []), ...(previousItems || [])].forEach((entry, index) => {
    const key = buildExistingItemStableKey(entry);
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push({
      ...entry,
      __localSentAt:
        entry?.__localSentAt ||
        entry?.local_sent_at ||
        (index < (latestItems || []).length ? mergeTimestamp : null),
    });
  });
  return merged;
};

const compareExistingItemsByRecency = (a, b) => {
  const aLocalSentAt = Number(a?.__localSentAt || a?.local_sent_at || 0) || 0;
  const bLocalSentAt = Number(b?.__localSentAt || b?.local_sent_at || 0) || 0;
  if (aLocalSentAt !== bLocalSentAt) {
    return bLocalSentAt - aLocalSentAt;
  }

  const aTime = Date.parse(
    a?.created_at || a?.createdAt || a?.updated_at || a?.updatedAt || "",
  );
  const bTime = Date.parse(
    b?.created_at || b?.createdAt || b?.updated_at || b?.updatedAt || "",
  );
  if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) {
    return bTime - aTime;
  }

  const aOrder = Number(a?.orderId ?? a?.order_id ?? a?.order_item_id ?? 0) || 0;
  const bOrder = Number(b?.orderId ?? b?.order_id ?? b?.order_item_id ?? 0) || 0;
  if (aOrder !== bOrder) return bOrder - aOrder;

  const aFallback = Number(a?.id ?? a?.product_id ?? 0) || 0;
  const bFallback = Number(b?.id ?? b?.product_id ?? 0) || 0;
  return bFallback - aFallback;
};

const applyExistingItemsRecency = (incomingItems = [], previousItems = []) => {
  const recencyByKey = new Map();
  (previousItems || []).forEach((item) => {
    const key = buildExistingItemStableKey(item);
    if (!key) return;
    const marker = Number(item?.__localSentAt || item?.local_sent_at || 0) || 0;
    if (marker > 0) {
      recencyByKey.set(key, marker);
    }
  });

  return (incomingItems || []).map((item) => {
    const key = buildExistingItemStableKey(item);
    const existingMarker = key ? recencyByKey.get(key) : 0;
    const fallbackTime = Date.parse(
      item?.created_at || item?.createdAt || item?.updated_at || item?.updatedAt || "",
    );
    return {
      ...item,
      __localSentAt:
        existingMarker ||
        Number(item?.__localSentAt || item?.local_sent_at || 0) ||
        (Number.isFinite(fallbackTime) ? fallbackTime : 0),
    };
  });
};

const buildSentCartIntegritySignature = (items = []) =>
  (Array.isArray(items) ? items : [])
    .filter(
      (item) =>
        item &&
        item.status !== "cancelled" &&
        !item.cancelled_at &&
        getExplicitOrderItemId(item) !== null,
    )
    .map((item) => {
      const extras = (Array.isArray(item.extras) ? item.extras : [])
        .map((extra) =>
          [
            extra?.id ?? extra?.extra_id ?? extra?.product_id ?? "",
            extra?.name ?? extra?.extra_name ?? "",
            Number(extra?.quantity || 1),
            Number(extra?.price ?? extra?.unit_price ?? 0),
          ].join(":"),
        )
        .sort()
        .join(",");
      return [
        String(getExplicitOrderItemId(item)),
        String(item?.orderId ?? item?.order_id ?? ""),
        Number(item?.quantity || 0),
        Number(item?.price ?? item?.unit_price ?? 0),
        String(item?.status || "pending"),
        String(item?.variation_id ?? item?.variationId ?? ""),
        extras,
      ].join("|");
    })
    .sort()
    .join(";");

  const normalizeItemsForExisting = (items) => {
    const timestamp = Date.now();
    const normalized = (items || []).map((item, index) => {
      const extrasPrice = item.extras
        ? item.extras.reduce(
            (sum, extra) =>
              sum + (extra.price || 0) * (extra.quantity || 1),
            0,
          )
        : 0;
      const resolvedProductId =
        item.product_id ??
        item.productId ??
        (typeof item.id === "number" ? item.id : null) ??
        (typeof item.id === "string" ? Number(item.id) : null) ??
        item?.product?.id ??
        null;
      const hasVariationDetails =
        item.variation_id ||
        item.variationId ||
        item.variation_name ||
        item.variationName ||
        item.variation_label ||
        item.variationLabel ||
        item.variation_attributes ||
        item.variationAttributes;
      const variationSource = hasVariationDetails
        ? {
            variation_id: item.variation_id ?? item.variationId,
            variation_name:
              item.variation_name ??
              item.variationName ??
              item.variation_label ??
              item.variationLabel,
            variation_attributes:
              item.variation_attributes ?? item.variationAttributes,
            variation_label: item.variation_label ?? item.variationLabel,
          }
        : null;
      let variationMetadata = resolveVariationMetadata(variationSource);
      if (
        (!variationMetadata.variationLabel ||
          !String(variationMetadata.variationLabel).trim()) &&
        resolvedProductId
      ) {
        const product = products.find(
          (productItem) => productItem?.id === resolvedProductId,
        );
        if (
          product &&
          Array.isArray(product.variations) &&
          product.variations.length > 0
        ) {
          const variationIdFromItem =
            item.variation_id ??
            item.variationId ??
            variationMetadata.variation_id ??
            variationMetadata.variationId ??
            null;
          const matchedVariation =
            product.variations.find((variation) => {
              const vId =
                variation?.id ??
                variation?.variation_id ??
                variation?.variationId ??
                null;
              return (
                variationIdFromItem !== null &&
                vId !== null &&
                Number(vId) === Number(variationIdFromItem)
              );
            }) ||
            product.variations.find(
              (variation) =>
                variation?.id === product.default_variation_id ||
                variation?.variation_id === product.default_variation_id,
            ) ||
            product.variations.find(
              (variation) => variation && variation.is_active !== false,
            ) ||
            product.variations[0];
          if (matchedVariation) {
            const parsedVariationAttributes = parseVariationAttributesValue(
              matchedVariation.attributes,
            );
            const derivedVariationLabel = buildVariationLabelFromParts(
              matchedVariation.name,
              parsedVariationAttributes,
            );
            variationMetadata = {
              ...variationMetadata,
              variationId:
                variationMetadata.variationId ??
                matchedVariation.id ??
                matchedVariation.variation_id ??
                matchedVariation.variationId ??
                variationIdFromItem ??
                null,
              variation_id:
                variationMetadata.variation_id ??
                matchedVariation.id ??
                matchedVariation.variation_id ??
                matchedVariation.variationId ??
                variationIdFromItem ??
                null,
              variationName:
                variationMetadata.variationName ??
                matchedVariation.name ??
                "",
              variation_name:
                variationMetadata.variation_name ??
                matchedVariation.name ??
                "",
              variationAttributes:
                variationMetadata.variationAttributes ??
                parsedVariationAttributes ??
                null,
              variation_attributes:
                variationMetadata.variation_attributes ??
                parsedVariationAttributes ??
                null,
              variationLabel:
                derivedVariationLabel ||
                variationMetadata.variationLabel ||
                "",
              variation_label:
                derivedVariationLabel ||
                variationMetadata.variation_label ||
                "",
            };
          }
        }
      }
      return {
        ...item,
        product_id:
          resolvedProductId ?? item.product_id ?? item.productId ?? null,
        totalPrice: (item.price + extrasPrice) * (item.quantity || 1),
        status: item.status || "pending",
        isPaid: false,
        order_item_id:
          item.order_item_id ||
          item.id ||
          `temp-existing-${timestamp}-${item.id || item.productId || "item"}-${index}`,
        ...variationMetadata,
      };
    });
    // Priorité visuelle: les ajouts les plus récents doivent rester en tête
    // (dernier lot envoyé en haut, ordre stable à l'intérieur du lot).
    return normalized
      .map((item, index) => ({ ...item, __originalIndex: index }))
      .sort((a, b) => {
        const aLocalSentAt = Number(a?.__localSentAt || a?.local_sent_at || 0) || 0;
        const bLocalSentAt = Number(b?.__localSentAt || b?.local_sent_at || 0) || 0;
        if (aLocalSentAt !== bLocalSentAt) {
          return bLocalSentAt - aLocalSentAt;
        }

        const aTime = Date.parse(
          a?.created_at || a?.createdAt || a?.updated_at || a?.updatedAt || "",
        );
        const bTime = Date.parse(
          b?.created_at || b?.createdAt || b?.updated_at || b?.updatedAt || "",
        );
        if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) {
          return bTime - aTime;
        }

        const aOrder = Number(a?.orderId ?? a?.order_id ?? a?.order_item_id ?? 0) || 0;
        const bOrder = Number(b?.orderId ?? b?.order_id ?? b?.order_item_id ?? 0) || 0;
        if (aOrder !== bOrder) return bOrder - aOrder;

        return a.__originalIndex - b.__originalIndex;
      })
      .map(({ __originalIndex, ...item }) => item);
  };

  const logVariationDiagnostics = (items = [], context = {}) => {
    if (!CASH_DEBUG_ENABLED) return;
    try {
      const diagnostics = (items || []).map((item) => ({
        orderItemId: item?.order_item_id || item?.id || null,
        product: item?.name || item?.product_name || "Article",
        variationId: item?.variation_id || null,
        variationName: item?.variation_name || null,
        variationLabel: item?.variation_label || null,
        computedLabel: getItemVariationLabel(item) || null,
        hasVariationData: Boolean(
          item?.variation_id ||
            item?.variation_name ||
            item?.variation_label ||
            item?.variation_attributes,
        ),
        rawAttributes: item?.variation_attributes || null,
        extrasCount: Array.isArray(item?.extras) ? item.extras.length : 0,
        status: item?.status || "pending",
      }));
      const summaryLines = diagnostics.map((diag, idx) =>
        [
          `#${idx + 1}`,
          `prod="${diag.product}"`,
          `id=${diag.orderItemId}`,
          `hasData=${diag.hasVariationData}`,
          `varId=${diag.variationId ?? "null"}`,
          `varName="${diag.variationName ?? ""}"`,
          `varLabel="${diag.variationLabel ?? ""}"`,
          `computed="${diag.computedLabel ?? ""}"`,
          `attrs=${JSON.stringify(diag.rawAttributes)}`,
          `extras=${diag.extrasCount}`,
          `status=${diag.status}`,
        ].join(" | "),
      );
      cashDebugLog("🧪 VARIATION DEBUG", {
        totalItems: items?.length || 0,
        diagnostics,
        ...context,
      });
      cashDebugLog(
        `🧪 VARIATION DEBUG :: lignes (${context?.tableId || "table inconnue"} - ${context?.source || "unknown"})`,
        summaryLines.length > 0 ? summaryLines : ["(aucun article)"],
      );
      diagnostics.forEach((diag, index) => {
        cashDebugLog(
          `🧪 VARIATION ITEM #${index + 1}`,
          JSON.parse(JSON.stringify(diag)),
        );
      });
    } catch (variationLogError) {
      console.warn(
        "⚠�? Impossible d'enregistrer le log de debug variation:",
        variationLogError,
        context,
      );
    }
  };

  const normalizedExistingDeliveryItems = useMemo(
    () => normalizeItemsForExisting(existingDeliveryItems),
    [existingDeliveryItems, products],
  );

  const normalizedExistingTableItems = useMemo(
    () => normalizeItemsForExisting(existingItemsForPayment),
    [existingItemsForPayment, products],
  );

  const normalizedPendingItems = useMemo(() => {
    const baseItems = selectedTable?.isDelivery
      ? currentDeliveryOrder.items
      : currentOrder.items;
    return (baseItems || []).map((item, index) => ({
      ...item,
      ...buildVariationMetadata(item),
      _pendingIndex: index,
    }));
  }, [
    selectedTable?.isDelivery,
    currentDeliveryOrder.items,
    currentOrder.items,
  ]);

  const cartViewModel = useMemo(() => {
    const isDeliveryOrder = !!selectedTable?.isDelivery;
    const normalizedExistingPool = isDeliveryOrder
      ? normalizedExistingDeliveryItems
      : normalizedExistingTableItems;

    const newItems = normalizedPendingItems.filter(
      (item) => !item?.order_item_id,
    );
    const existingInCart = normalizedPendingItems.filter(
      (item) => item?.order_item_id,
    );

    const existingMap = new Map();
    (normalizedExistingPool || []).forEach((item) => {
      if (!item || !isItemActive(item)) return;
      const mapKey =
        item.order_item_id ||
        `existing-${item.id || item.product_id || item._pendingIndex || ""}`;
      existingMap.set(mapKey, item);
    });

    existingInCart.forEach((item) => {
      if (!item || !isItemActive(item)) return;
      const mapKey =
        item.order_item_id ||
        `pending-${item.id || item.product_id || item._pendingIndex || ""}`;
      if (!existingMap.has(mapKey)) {
        existingMap.set(mapKey, item);
      }
    });

    return {
      isDeliveryOrder,
      newItems,
      existingItems: Array.from(existingMap.values()),
      existingInCart,
    };
  }, [
    selectedTable?.isDelivery,
    normalizedExistingDeliveryItems,
    normalizedExistingTableItems,
    normalizedPendingItems,
  ]);

  const cartNewItems = cartViewModel.newItems;
  const cartExistingItems = useMemo(
    () =>
      cartViewModel.existingItems
        .filter(isItemActive)
        .slice()
        .sort(compareExistingItemsByRecency),
    [cartViewModel.existingItems],
  );
  const cartIsEmpty = cartNewItems.length === 0 && cartExistingItems.length === 0;
  const cartNewItemsQuantity = useMemo(
    () => cartNewItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
    [cartNewItems],
  );
  const cartExistingItemsQuantity = useMemo(
    () => cartExistingItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
    [cartExistingItems],
  );
  const visibleCourseSummaries = useMemo(() => {
    if (!courseModeEnabled) return [];
    const byNumber = new Map();
    (orderCourses || []).forEach((course) => {
      if (String(course?.status || "held") === "cancelled") return;
      const number = Math.max(1, Number(course?.course_number || 1));
      byNumber.set(number, { ...course, course_number: number });
    });
    [...cartNewItems, ...cartExistingItems].forEach((item) => {
      const number = Math.max(
        1,
        Number(item?.courseNumber ?? item?.course_number ?? 1) || 1,
      );
      if (!byNumber.has(number)) {
        byNumber.set(number, {
          course_number: number,
          status: number === 1 && hasExistingOrders ? "fired" : "held",
        });
      }
    });
    if (!byNumber.size) {
      byNumber.set(1, { course_number: 1, status: "held" });
    }
    return Array.from(byNumber.values()).sort(
      (left, right) => Number(left.course_number) - Number(right.course_number),
    );
  }, [
    cartExistingItems,
    cartNewItems,
    courseModeEnabled,
    hasExistingOrders,
    orderCourses,
  ]);
  const activeCourseIsEditable = useMemo(() => {
    const activeCourse = visibleCourseSummaries.find(
      (course) =>
        Number(course?.course_number || 1) === Number(activeCourseNumber),
    );
    return Boolean(
      activeCourse && String(activeCourse?.status || "held") === "held",
    );
  }, [activeCourseNumber, visibleCourseSummaries]);
  const displayedCourseNumber = useMemo(() => {
    return resolveDisplayedCourseNumber(
      visibleCourseSummaries,
      activeCourseNumber,
    );
  }, [activeCourseNumber, visibleCourseSummaries]);
  const selectedDraftCourseNumber =
    courseModeEnabled && courseModeContextForTable(selectedTable)
      ? Math.max(1, Number(displayedCourseNumber) || 1)
      : 1;

  useEffect(() => {
    if (!courseModeEnabled || visibleCourseSummaries.length === 0) return;
    const selectedCourse = visibleCourseSummaries.find(
      (course) =>
        Number(course?.course_number || 1) === Number(activeCourseNumber),
    );
    if (
      selectedCourse &&
      String(selectedCourse?.status || "held") === "held"
    ) {
      return;
    }
    const resolvedNumber = resolveSelectedCourseNumber(
      visibleCourseSummaries,
      activeCourseNumber,
    );
    if (resolvedNumber !== Number(activeCourseNumber)) {
      setActiveCourseNumber(resolvedNumber);
    }
  }, [activeCourseNumber, courseModeEnabled, visibleCourseSummaries]);
  const cartHasPendingItems = cartNewItems.length > 0;
  const cartHasExistingItems = cartExistingItems.length > 0;
  const cartHasAnyItems = cartHasPendingItems || cartHasExistingItems;
  const isSelectedTableCacheReconciling = Boolean(
    selectedTable?.id &&
      reconcilingVisibleTableId !== null &&
      String(selectedTable.id) === String(reconcilingVisibleTableId),
  );
  const cartAddDisabled =
    isFastFoodMode ||
    isAggregatorOrderMode ||
    !selectedTable ||
    !cartHasPendingItems ||
    (hasExistingOrders && !cartHasPendingItems) ||
    (isOrderActionInProgress ||
      isSelectedTableCacheReconciling ||
      Boolean(selectedTableAccessConflict));
  const cartPaymentDisabled =
    (!selectedTable && !canOrderWithoutTable) ||
    !cartHasAnyItems ||
    (isOrderActionInProgress || isPreparingPayment) ||
    isSelectedTableCacheReconciling ||
    Boolean(selectedTableAccessConflict);
  const cartPrintDisabled =
    isFastFoodMode ||
    isAggregatorOrderMode ||
    !selectedTable ||
    (selectedTable?.isDelivery
      ? !cartHasAnyItems
      : !cartHasPendingItems && !hasExistingOrders) ||
    isPrintActionInProgress;
  const cartAdjustDisabled = cartPrintDisabled;
  const shouldUseCompactCartActions =
    Boolean(selectedTable) &&
    cartBottomCollapsed &&
    !isServerParticipationMode &&
    !isBoutique &&
    !isFastFoodMode;
  const shouldShowMainCartActions = !shouldUseCompactCartActions;

  const pendingVariationFetchesRef = useRef(new Set());

  const ensureProductVariationsLoaded = useCallback(
    async (productId) => {
      if (!productId) return;
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/products/${productId}/variations`,
        );
        if (!response.ok) {
          console.warn(
            `⚠�? Impossible de charger les variations du produit ${productId}:`,
            response.status,
          );
          return;
        }
        const variationsData = await response.json();
        const normalizedVariations = Array.isArray(variationsData)
          ? variationsData
              .filter(Boolean)
              .filter((variation) => variation && variation.is_active !== false)
              .map((variation) => ({
                ...variation,
                attributes: parseVariationAttributesValue(variation.attributes),
              }))
          : [];
        if (normalizedVariations.length === 0) {
          return;
        }
        setProducts((prev) =>
          Array.isArray(prev)
            ? prev.map((product) =>
                product?.id === productId
                  ? {
                      ...product,
                      product_type: product.product_type || "variable",
                      variations: normalizedVariations,
                    }
                  : product,
              )
            : prev,
        );
      } catch (error) {
        console.warn(
          `⚠�? Erreur lors du chargement des variations produit ${productId}:`,
          error,
        );
      }
    },
    [API_BASE],
  );

  useEffect(() => {
    const candidateItems = [
      ...(normalizedExistingDeliveryItems || []),
      ...(normalizedExistingTableItems || []),
      ...(normalizedPendingItems || []),
    ];
    const missingProductIds = new Set();
    candidateItems.forEach((item) => {
      if (!item) return;
      const productId =
        item.product_id ??
        item.productId ??
        (typeof item.id === "number" ? item.id : null);
      if (!productId) return;
      const hasVariationReference =
        item.variation_id ||
        item.variationId ||
        (item.variation_attributes &&
          Object.keys(item.variation_attributes).length > 0) ||
        (item.variationAttributes &&
          Object.keys(item.variationAttributes).length > 0);
      if (!hasVariationReference) return;
      const product = Array.isArray(products)
        ? products.find((productItem) => productItem?.id === productId)
        : null;
      const hasLoadedVariations =
        product &&
        Array.isArray(product.variations) &&
        product.variations.length > 0;
      if (!hasLoadedVariations) {
        missingProductIds.add(productId);
      }
    });
    missingProductIds.forEach((productId) => {
      if (pendingVariationFetchesRef.current.has(productId)) return;
      pendingVariationFetchesRef.current.add(productId);
      ensureProductVariationsLoaded(productId).finally(() => {
        pendingVariationFetchesRef.current.delete(productId);
      });
    });
  }, [
    products,
    normalizedExistingDeliveryItems,
    normalizedExistingTableItems,
    normalizedPendingItems,
    ensureProductVariationsLoaded,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__mooState = {
      selectedTable,
      cartViewModel,
      existingDeliveryItems,
      currentDeliveryOrder,
      existingItemsForPayment,
      currentOrder,
    };
  }, [
    selectedTable,
    cartViewModel,
    existingDeliveryItems,
    currentDeliveryOrder,
    existingItemsForPayment,
    currentOrder,
  ]);

  useEffect(() => {
    selectedTableRef.current = selectedTable || null;
    currentOrderRef.current = currentOrder || { items: [], total: 0 };
    existingItemsForPaymentRef.current = Array.isArray(existingItemsForPayment)
      ? existingItemsForPayment
      : [];
    currentDeliveryOrderRef.current = currentDeliveryOrder || { items: [], total: 0 };
    existingDeliveryItemsRef.current = Array.isArray(existingDeliveryItems)
      ? existingDeliveryItems
      : [];
  }, [
    selectedTable,
    currentOrder,
    existingItemsForPayment,
    currentDeliveryOrder,
    existingDeliveryItems,
  ]);

  useEffect(() => {
    if (!selectedTable || selectedTable.isDelivery) {
      setSelectedTableAccessConflict(null);
      return;
    }
    const freshTable = (tables || []).find(
      (table) => String(table?.id) === String(selectedTable.id),
    );
    if (!freshTable) return;
    const managementType = String(freshTable.management_type || "").toLowerCase();
    const managedByUserId =
      Number(
        freshTable.managed_by_user_id ||
          freshTable.active_owner_user_id ||
          0,
      ) || null;
    const currentUserId = Number(currentUser?.id || 0) || null;
    const currentDeviceId = getPersistentDeviceId();
    const reservationUserId = Number(freshTable.reservation_user_id || 0) || null;
    const reservationDeviceId = String(
      freshTable.reservation_device_id || "",
    ).trim();
    const sameUserOtherDevice = Boolean(
      reservationUserId &&
        reservationUserId === currentUserId &&
        reservationDeviceId &&
        reservationDeviceId !== currentDeviceId,
    );
    const hasForeignReservation = Boolean(
      reservationUserId &&
        (reservationUserId !== currentUserId ||
          (reservationDeviceId && reservationDeviceId !== currentDeviceId)),
    );
    const hasForeignManagement =
      isServerParticipationMode &&
      (managementType === "cashier" ||
        Boolean(
          (managementType || managedByUserId) &&
            (!managedByUserId || managedByUserId !== currentUserId),
        ));
    const isForeign = hasForeignReservation || hasForeignManagement;
    if (!isForeign) {
      setSelectedTableAccessConflict(null);
      return;
    }
    const managedBy = String(
      (hasForeignReservation ? freshTable.reservation_name : null) ||
        freshTable.managed_by_first_name ||
        freshTable.active_owner_first_name ||
        freshTable.active_owner_name ||
        "un autre utilisateur",
    )
      .trim()
      .split(/\s+/)[0];
    setSelectedTableAccessConflict((previous) => {
      if (
        previous &&
        String(previous.tableId) === String(freshTable.id) &&
        previous.managedBy === managedBy &&
        previous.sameUserOtherDevice === sameUserOtherDevice
      ) {
        return previous;
      }
      return { tableId: freshTable.id, managedBy, sameUserOtherDevice };
    });
  }, [
    currentUser?.id,
    isServerParticipationMode,
    selectedTable,
    tables,
  ]);

  const reclaimOwnSelectedTable = async () => {
    const tableId = Number(selectedTableRef.current?.id);
    if (
      !Number.isInteger(tableId) ||
      tableId <= 0 ||
      !selectedTableAccessConflict?.sameUserOtherDevice ||
      isReclaimingOwnTable
    ) {
      return;
    }
    setIsReclaimingOwnTable(true);
    try {
      const deviceId = getPersistentDeviceId();
      const response = await fetchWithTimeout(
        `${API_BASE}/tables/${tableId}/reservation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deviceId, takeoverOwnDevice: true }),
        },
        6500,
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.access?.allowed === false) {
        throw new Error(
          payload?.error || "Impossible de reprendre cette table sur cet appareil.",
        );
      }
      setTables((previous) =>
        Array.isArray(previous)
          ? previous.map((table) =>
              Number(table?.id) === tableId
                ? {
                    ...table,
                    reservation_user_id: currentUser?.id || null,
                    reservation_device_id: deviceId,
                    reservation_name:
                      currentUser?.first_name || currentUser?.username || null,
                  }
                : table,
            )
          : previous,
      );
      setSelectedTableAccessConflict(null);
      void loadTablesRef.current({ forceUnpaidRefresh: true });
    } catch (error) {
      triggerErrorAnimation(
        error?.message || "Impossible de reprendre cette table sur cet appareil.",
      );
    } finally {
      setIsReclaimingOwnTable(false);
    }
  };

  useEffect(() => {
    const tableId = Number(selectedTable?.id);
    const role = String(currentUser?.role || "").trim().toLowerCase();
    const canReserve =
      Number.isInteger(tableId) &&
      tableId > 0 &&
      !selectedTable?.isDelivery &&
      ["cashier", "caissier", "caisse", "server", "serveur", "manager", "admin"].includes(role);
    if (!canReserve) return undefined;

    let stopped = false;
    let heartbeatInFlight = false;
    const buildHeaders = () => {
      const token = getStoredAuthToken();
      return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "X-Moo-Device-Id": getPersistentDeviceId(),
      };
    };
    const heartbeat = async () => {
      if (stopped || heartbeatInFlight) return;
      heartbeatInFlight = true;
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/tables/${tableId}/reservation`,
          {
            method: "POST",
            headers: buildHeaders(),
            body: JSON.stringify({
              deviceId: getPersistentDeviceId(),
              renewOnly: true,
            }),
          },
          6500,
        );
        const payload = await response.json().catch(() => null);
        if (response.ok && !stopped) {
          const reservation = payload?.reservation || {};
          const deviceId = getPersistentDeviceId();
          setTables((previous) =>
            Array.isArray(previous)
              ? previous.map((table) =>
                  Number(table?.id) === tableId
                    ? {
                        ...table,
                        reservation_user_id:
                          reservation.userId || currentUser?.id || null,
                        reservation_role:
                          reservation.role || currentUser?.role || null,
                        reservation_device_id:
                          reservation.deviceId || deviceId,
                        reservation_name:
                          reservation.displayName ||
                          currentUser?.first_name ||
                          currentUser?.username ||
                          null,
                      }
                    : table,
                )
              : previous,
          );
          setSelectedTableAccessConflict((previous) =>
            previous && String(previous.tableId) === String(tableId)
              ? null
              : previous,
          );
          return;
        }
        if (!response.ok && !stopped) {
          if (response.status === 409) {
            if (payload?.code === "TABLE_RESERVATION_REQUIRED") {
              // Le paiement (ou une fermeture distante) a supprimé le bail.
              // Un heartbeat ne doit jamais rouvrir silencieusement la table,
              // mais il ne doit surtout pas détruire une saisie locale.
              if (
                stageTableDraftRecovery({
                  tableId,
                  pendingItems: getSnapshotPendingItems(tableId),
                  occupancyCycle: resolveTableOccupancyCycle(
                    selectedTableRef.current,
                  ),
                  reason: "reservation-closed-during-entry",
                })
              ) {
                return;
              }
              forgetKnownExistingItems(tableId, false, {
                preservePending: false,
              });
              optimisticTableStatusRef.current.delete(tableId);
              optimisticTableStatusRef.current.delete(String(tableId));
              commitTableStatesCache((previous) => {
                const next = new Map(previous);
                next.delete(tableId);
                next.delete(String(tableId));
                return next;
              });
              const selectedNow = selectedTableRef.current;
              if (
                selectedNow &&
                !selectedNow.isDelivery &&
                String(selectedNow.id) === String(tableId)
              ) {
                selectedTableRef.current = null;
                setSelectedTable(null);
                setCurrentOrder({ items: [], total: 0 });
                setExistingItemsForPayment([]);
                setHasExistingOrders(false);
                setActiveClientTab(null);
                setActiveTab(
                  isBoutique
                    ? isServerParticipationMode
                      ? "profile"
                      : "customers"
                    : "tables",
                );
              }
              setSelectedTableAccessConflict(null);
              void loadTablesRef.current({ forceUnpaidRefresh: true });
              return;
            }
            setSelectedTableAccessConflict({
              tableId,
              managedBy:
                payload?.reserved_by_first_name ||
                payload?.managed_by_first_name ||
                "un autre utilisateur",
              sameUserOtherDevice:
                Number(payload?.reserved_by_user_id || 0) ===
                Number(currentUser?.id || 0),
            });
          }
        }
      } catch (_error) {
        // Best effort only: the write-time ownership guard remains authoritative.
      } finally {
        heartbeatInFlight = false;
      }
    };
    const heartbeatWhenUsable = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }
      void heartbeat();
    };
    void heartbeat();
    const timer = window.setInterval(() => {
      if (!stopped) void heartbeat();
    }, 30000);
    window.addEventListener("focus", heartbeatWhenUsable);
    window.addEventListener("online", heartbeatWhenUsable);
    document.addEventListener("visibilitychange", heartbeatWhenUsable);

    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", heartbeatWhenUsable);
      window.removeEventListener("online", heartbeatWhenUsable);
      document.removeEventListener("visibilitychange", heartbeatWhenUsable);
      fetch(`${API_BASE}/tables/${tableId}/reservation`, {
        method: "DELETE",
        headers: buildHeaders(),
        body: JSON.stringify({ deviceId: getPersistentDeviceId() }),
      }).catch(() => {});
    };
  }, [
    selectedTable?.id,
    selectedTable?.isDelivery,
    currentUser?.first_name,
    currentUser?.id,
    currentUser?.role,
    currentUser?.username,
    fetchWithTimeout,
    isBoutique,
    isServerParticipationMode,
  ]);

  const buildCartSnapshotHash = (items = []) =>
    (items || [])
      .map((item) =>
        [
          item?.order_item_id || "",
          item?.id || item?.product_id || "",
          item?.quantity || 0,
          item?.status || "",
          item?.totalPrice || item?.total_price || item?.price || 0,
        ].join(":"),
      )
      .sort()
      .join("|");

  const buildFastFoodCartSnapshot = (items = currentOrderRef.current?.items || []) => {
    const activeItems = (items || []).filter(isItemActive);
    if (activeItems.length === 0) return null;
    const previous = fastFoodCartSnapshotRef.current || {};
    return {
      ...previous,
      mode: "counter",
      stage: previous.stage || "editing",
      counter_transaction_id:
        previous.counter_transaction_id ||
        window.crypto?.randomUUID?.() ||
        `counter-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
      origin_user_id: currentUser?.id || null,
      origin_user_name:
        currentUser?.first_name || currentUser?.firstName || currentUser?.name || "",
      previous_table_id:
        previous.previous_table_id || previousTableBeforeCounterRef.current?.id || null,
      timestamp: Date.now(),
      items: activeItems,
      total: computeItemsTotal(activeItems),
      adjustments: Array.isArray(tempAdjustments) ? tempAdjustments : [],
      legacy_adjustments: {
        discountAmount: Number(discountAmount) || 0,
        discountType,
        additionalFees: Number(additionalFees) || 0,
        feesType,
      },
      hash: buildCartSnapshotHash(activeItems),
    };
  };

  const restoreFastFoodCartSnapshot = (reason = "fastfood-cart-restore") => {
    if (!isFastFoodMode) return false;
    const snapshot =
      fastFoodCartSnapshotRef.current || readFastFoodCartSnapshotFromStorage();
    const activeItems = (snapshot?.items || []).filter(isItemActive);
    if (activeItems.length === 0) return false;
    if (
      snapshot?.origin_user_id &&
      currentUser?.id &&
      String(snapshot.origin_user_id) !== String(currentUser.id)
    ) {
      setForeignCounterRecovery(snapshot);
      return false;
    }

    fastFoodCartSnapshotRef.current = {
      ...snapshot,
      items: activeItems,
      total: computeItemsTotal(activeItems),
      timestamp: Number(snapshot?.timestamp || 0) || Date.now(),
      hash: snapshot?.hash || buildCartSnapshotHash(activeItems),
    };
    setCurrentOrder({
      items: activeItems,
      total: computeItemsTotal(activeItems),
    });
    setExistingItemsForPayment([]);
    setHasExistingOrders(false);
    setTempAdjustments(
      Array.isArray(snapshot?.adjustments) ? snapshot.adjustments : [],
    );
    if (snapshot?.legacy_adjustments) {
      setDiscountAmount(Number(snapshot.legacy_adjustments.discountAmount) || 0);
      setDiscountType(snapshot.legacy_adjustments.discountType || "fixed");
      setAdditionalFees(Number(snapshot.legacy_adjustments.additionalFees) || 0);
      setFeesType(snapshot.legacy_adjustments.feesType || "fixed");
    }
    setCartRenderKey((prev) => prev + 1);
    cashDebugLog("Panier fast-food restaure depuis le snapshot local", {
      reason,
      count: activeItems.length,
      hash: fastFoodCartSnapshotRef.current.hash,
    });
    return true;
  };

  useEffect(() => {
    if (!hasCounterService || !currentUser?.id) return;
    const snapshot = readFastFoodCartSnapshotFromStorage();
    if (!snapshot?.items?.length) {
      setForeignCounterRecovery(null);
      return;
    }
    const belongsToCurrentUser =
      !snapshot.origin_user_id ||
      String(snapshot.origin_user_id) === String(currentUser.id);
    if (!belongsToCurrentUser) {
      setForeignCounterRecovery(snapshot);
      return;
    }
    setForeignCounterRecovery(null);
    fastFoodCartSnapshotRef.current = snapshot;
    setActiveOrderContext("counter");
    window.setTimeout(
      () => restoreFastFoodCartSnapshot("authenticated-counter-recovery"),
      0,
    );
  }, [currentUser?.id, hasCounterService]);

  const clearFastFoodCartSnapshot = () => {
    fastFoodCartSnapshotRef.current = null;
    clearFastFoodCartSnapshotFromStorage();
  };

  const hasActiveCounterCart = () =>
    isCounterContext &&
    [
      ...(currentOrderRef.current?.items || []),
      ...(currentOrder?.items || []),
    ].filter(isItemActive).length > 0;

  const getCounterMutationBlockMessage = () => {
    if (!isCounterContext) return "";
    if (counterRecoveryStorageError) {
      return "Sauvegarde de secours indisponible. Libérez de l’espace ou réactivez le stockage avant de continuer.";
    }
    const recovery =
      fastFoodCartSnapshotRef.current || readFastFoodCartSnapshotFromStorage();
    if (counterDispatchPending || recovery?.payment_confirmed) {
      return "Le paiement est confirmé. Finalisez l’envoi Cuisine/Bar avant une nouvelle vente.";
    }
    if (recovery?.stage && recovery.stage !== "editing") {
      return "Cette vente Comptoir est déjà engagée. Reprenez son paiement ou annulez-la avec un manager.";
    }
    const recoveryOrderId = recovery?.created_order_id;
    const hasUnmatchedOrder = openCounterOrders.some(
      (entry) =>
        String(entry?.created_by || "") === String(currentUser?.id || "") &&
        (!recoveryOrderId || String(entry.id) !== String(recoveryOrderId)),
    );
    return hasUnmatchedOrder
      ? "Une commande Comptoir orpheline doit être annulée par un manager avant une nouvelle vente."
      : "";
  };

  useEffect(() => {
    const handleStorageError = () => {
      setCounterRecoveryStorageError(true);
      triggerErrorAnimation(
        "Le panier Comptoir ne peut pas être sauvegardé. Aucun nouvel article n’est autorisé.",
      );
    };
    window.addEventListener("moo-counter-recovery-storage-error", handleStorageError);
    return () =>
      window.removeEventListener(
        "moo-counter-recovery-storage-error",
        handleStorageError,
      );
  }, []);

  const enterCounterContext = () => {
    if (!hasCounterService || isServerParticipationMode) return;
    if (foreignCounterRecovery) {
      triggerErrorAnimation(
        "Une transaction Comptoir d’un autre caissier doit être annulée par un manager.",
      );
      return;
    }
    if (isCounterContext) return;
    const selectedNow = selectedTableRef.current;
    if (selectedNow) {
      persistCartStateForTable(
        selectedNow,
        currentOrder,
        existingItemsForPayment,
      );
      previousTableBeforeCounterRef.current = { ...selectedNow };
    }
    setSelectedTable(null);
    selectedTableRef.current = null;
    setExistingItemsForPayment([]);
    setHasExistingOrders(false);
    setCurrentOrder({ items: [], total: 0 });
    setActiveOrderContext("counter");
    setVisiblePanels((prev) => ({
      ...prev,
      tables: false,
      delivery: false,
      reservations: false,
    }));
    setActiveTab("");
    void loadOpenCounterOrders();
    const recovery =
      fastFoodCartSnapshotRef.current || readFastFoodCartSnapshotFromStorage();
    const recoveryItems = (recovery?.items || []).filter(isItemActive);
    if (recovery?.previous_table_id && !previousTableBeforeCounterRef.current) {
      previousTableBeforeCounterRef.current = { id: recovery.previous_table_id };
    }
    if (recoveryItems.length > 0) {
      setCurrentOrder({
        items: recoveryItems,
        total: computeItemsTotal(recoveryItems),
      });
      setTempAdjustments(
        Array.isArray(recovery?.adjustments) ? recovery.adjustments : [],
      );
      if (recovery?.legacy_adjustments) {
        setDiscountAmount(Number(recovery.legacy_adjustments.discountAmount) || 0);
        setDiscountType(recovery.legacy_adjustments.discountType || "fixed");
        setAdditionalFees(Number(recovery.legacy_adjustments.additionalFees) || 0);
        setFeesType(recovery.legacy_adjustments.feesType || "fixed");
      }
    }
  };

  const leaveCounterContext = () => {
    if (!isCounterContext) return true;
    if (counterDispatchPending) {
      triggerErrorAnimation(
        "Finalisez l’envoi Cuisine/Bar de la vente payée avant de quitter le Comptoir.",
      );
      return false;
    }
    if (hasActiveCounterCart()) {
      triggerErrorAnimation(
        "Terminez la vente Comptoir : continuer, payer ou annuler le panier.",
      );
      return false;
    }
    const recovery =
      fastFoodCartSnapshotRef.current || readFastFoodCartSnapshotFromStorage();
    clearFastFoodCartSnapshot();
    setActiveOrderContext("table");
    const previousId =
      previousTableBeforeCounterRef.current?.id || recovery?.previous_table_id;
    const previous = previousId
      ? tables.find((table) => String(table.id) === String(previousId)) ||
        previousTableBeforeCounterRef.current
      : null;
    previousTableBeforeCounterRef.current = null;
    if (previous) {
      window.setTimeout(() => handleTableCardSelect(previous), 0);
    } else {
      setVisiblePanels((prev) => ({ ...prev, tables: true }));
      setActiveTab("tables");
    }
    return true;
  };

  useEffect(() => {
    if (!isFastFoodMode) return;
    const snapshot = buildFastFoodCartSnapshot(currentOrder?.items || []);
    if (!snapshot) return;
    fastFoodCartSnapshotRef.current = snapshot;
    writeFastFoodCartSnapshotToStorage(snapshot);
  }, [
    isFastFoodMode,
    currentOrder.items,
    tempAdjustments,
    discountAmount,
    discountType,
    additionalFees,
    feesType,
  ]);

  useEffect(() => {
    if (!isFastFoodMode) return;
    if (showPaymentInterface || currentOrderForPayment) return;
    const activeCount = (currentOrder?.items || []).filter(isItemActive).length;
    if (activeCount > 0) return;
    restoreFastFoodCartSnapshot("fastfood-empty-cart-guard");
  }, [
    isFastFoodMode,
    currentOrder.items,
    showPaymentInterface,
    currentOrderForPayment,
  ]);

  const snapshotHasItems = (snapshot) => {
    if (!snapshot) return false;
    if (snapshot.mode === "fast-food" || snapshot.mode === "counter") {
      return Array.isArray(snapshot.items) && snapshot.items.length > 0;
    }
    const pendingCount = Array.isArray(snapshot.pendingItems)
      ? snapshot.pendingItems.length
      : 0;
    const existingCount = Array.isArray(snapshot.existingItems)
      ? snapshot.existingItems.length
      : 0;
    return pendingCount + existingCount > 0;
  };

  const getSnapshotTableKey = (snapshot) => {
    if (snapshot?.mode === "fast-food" || snapshot?.mode === "counter") return "counter:current";
    const tableId = snapshot?.tableId || "none";
    return `${snapshot?.isDelivery ? "delivery" : "table"}:${tableId}`;
  };

  const buildCurrentCartSnapshot = (table = selectedTableRef.current) => {
    if (!table && isFastFoodMode) {
      return buildFastFoodCartSnapshot();
    }
    if (!table) return null;
    const isDelivery = Boolean(table?.isDelivery);
    const pendingItems = isDelivery
      ? [...(currentDeliveryOrderRef.current?.items || [])]
      : [...(currentOrderRef.current?.items || [])];
    const existingItems = isDelivery
      ? [...(existingDeliveryItemsRef.current || [])]
      : [...(existingItemsForPaymentRef.current || [])];
    const filteredPending = pendingItems.filter(isItemActive);
    const filteredExisting = existingItems.filter(isItemActive);
    const resolvedCourseMode = resolveCartCourseManagement({
      explicitEnabled: courseModeEnabled,
      courses: orderCourses,
      pendingItems: filteredPending,
      existingItems: filteredExisting,
    });
    return {
      tableId: table?.id || null,
      isDelivery,
      tableLabel: table?.name || table?.number || null,
      timestamp: Date.now(),
      pendingItems: filteredPending,
      existingItems: filteredExisting,
      hash: buildCartSnapshotHash([...filteredPending, ...filteredExisting]),
      courseManagementEnabled: resolvedCourseMode,
      activeCourseNumber,
      courses: orderCourses,
    };
  };

  const restoreCartFromSnapshot = (snapshot) => {
    if (!snapshot || !snapshotHasItems(snapshot)) return false;
    if (snapshot.mode === "fast-food") {
      return restoreFastFoodCartSnapshot("generic-snapshot-restore");
    }
    if (snapshot.isDelivery) {
      setCurrentDeliveryOrder({
        items: [...(snapshot.pendingItems || [])],
        total: computeItemsTotal(snapshot.pendingItems || []),
      });
      setExistingDeliveryItems([...(snapshot.existingItems || [])]);
      setHasExistingOrders((snapshot.existingItems || []).length > 0);
      rememberKnownExistingItems(snapshot.tableId, snapshot.existingItems || [], true);
    } else {
      setCurrentOrder({
        items: [...(snapshot.pendingItems || [])],
        total: computeItemsTotal(snapshot.pendingItems || []),
      });
      setExistingItemsForPayment([...(snapshot.existingItems || [])]);
      setHasExistingOrders((snapshot.existingItems || []).length > 0);
      rememberKnownExistingItems(snapshot.tableId, snapshot.existingItems || [], false);
    }
    setCourseModeEnabled(
      resolveCartCourseManagement({
        explicitEnabled: snapshot.courseManagementEnabled,
        courses: snapshot.courses,
        pendingItems: snapshot.pendingItems,
        existingItems: snapshot.existingItems,
      }),
    );
    const snapshotCourses = Array.isArray(snapshot.courses)
      ? snapshot.courses
      : [];
    orderCoursesRef.current = snapshotCourses;
    setActiveCourseNumber(
      resolveSelectedCourseNumber(
        snapshotCourses,
        snapshot.activeCourseNumber,
      ),
    );
    setOrderCourses(snapshotCourses);
    setCartRenderKey((prev) => prev + 1);
    return true;
  };

  const rememberKnownExistingItems = (tableId, items = [], isDelivery = false) => {
    if (!tableId) return [];
    const activeItems = normalizeItemsForExisting(items || []).filter(isItemActive);
    if (activeItems.length === 0) return [];
    const timestamp = Date.now();
    const hash = buildCartSnapshotHash(activeItems);
    try {
      const memoryEntry = {
        items: activeItems,
        isDelivery: Boolean(isDelivery),
        timestamp,
        hash,
      };
      lastExistingItemsRef.current.set(tableId, memoryEntry);
      lastExistingItemsRef.current.set(String(tableId), memoryEntry);
      upsertPersistentCartCacheEntry(
        persistentCartCacheRef.current,
        buildPersistentCartKey(tableId, isDelivery),
        {
          items: activeItems,
          isDelivery: Boolean(isDelivery),
          timestamp,
          hash,
        },
      );
      writePersistentCartCache(persistentCartCacheRef.current);
      emptyOrdersStreakRef.current.delete(tableId);
    } catch (_) {}
    return activeItems;
  };

  const forgetKnownExistingItems = (
    tableId,
    isDelivery = null,
    { preservePending = true } = {},
  ) => {
    if (!tableId) return;
    try {
      lastExistingItemsRef.current.delete(tableId);
      lastExistingItemsRef.current.delete(String(tableId));
      emptyOrdersStreakRef.current.delete(tableId);
      emptyOrdersStreakRef.current.delete(String(tableId));
      const targetKeys =
        isDelivery === null
          ? [
              buildPersistentCartKey(tableId, false),
              buildPersistentCartKey(tableId, true),
            ]
          : [buildPersistentCartKey(tableId, Boolean(isDelivery))];
      targetKeys.forEach((cacheKey) => {
        if (preservePending) {
          clearPersistentSentItems(persistentCartCacheRef.current, cacheKey);
        } else {
          persistentCartCacheRef.current.delete(cacheKey);
        }
      });
      writePersistentCartCache(persistentCartCacheRef.current);
    } catch (error) {
      console.warn(
        "[cart-cache] Nettoyage sélectif du cache impossible:",
        error?.message || error,
      );
    }
  };

  const getKnownExistingItemsForTable = (
    tableId,
    { isDelivery = false, includeStateCache = true } = {},
  ) => {
    if (!tableId) return null;
    const normalizeCandidate = (items) =>
      normalizeItemsForExisting(items || []).filter(isItemActive);

    try {
      const cached = lastExistingItemsRef.current.get(tableId);
      if (cached && Boolean(cached.isDelivery) === Boolean(isDelivery)) {
        const items = normalizeCandidate(cached.items);
        if (items.length > 0) {
          return {
            items,
            isDelivery: Boolean(isDelivery),
            source: "known-existing-cache",
          };
        }
      }
    } catch (_) {}

    if (includeStateCache) {
      try {
        const state =
          tableStatesCacheRef.current.get(tableId) ||
          tableStatesCacheRef.current.get(String(tableId));
        const items = normalizeCandidate(state?.existingItems || []);
        if (items.length > 0) {
          return {
            items,
            isDelivery: Boolean(isDelivery),
            source: "table-state-cache",
          };
        }
      } catch (_) {}
    }

    try {
      const persisted = persistentCartCacheRef.current.get(
        buildPersistentCartKey(tableId, isDelivery),
      );
      const age = Date.now() - Number(persisted?.timestamp || 0);
      const items = normalizeCandidate(persisted?.items || []);
      if (
        items.length > 0 &&
        age >= 0 &&
        age < POS_CART_CACHE_MAX_AGE_MS
      ) {
        return {
          items,
          isDelivery: Boolean(isDelivery),
          source: "persistent-cart-cache",
        };
      }
      if (persisted && age >= POS_CART_CACHE_MAX_AGE_MS) {
        clearPersistentSentItems(
          persistentCartCacheRef.current,
          buildPersistentCartKey(tableId, isDelivery),
        );
        writePersistentCartCache(persistentCartCacheRef.current);
      }
    } catch (_) {}

    return null;
  };

  const restoreKnownExistingItemsForTable = (table, reason = "restore-known-cart") => {
    if (!table?.id) return [];
    const isDelivery = Boolean(table?.isDelivery);
    const known = getKnownExistingItemsForTable(table.id, { isDelivery });
    const items = known?.items || [];
    if (items.length === 0) return [];

    if (isDelivery) {
      setExistingDeliveryItems(items);
    } else {
      setExistingItemsForPayment(items);
    }
    setHasExistingOrders(true);
    setCartRenderKey((prev) => prev + 1);
    try {
      optimisticTableStatusRef.current.set(table.id, {
        status: "occupied",
        ts: Date.now(),
      });
    } catch (_) {}
    cashDebugLog("Panier local restaure depuis le dernier etat connu", {
      tableId: table.id,
      count: items.length,
      reason,
      source: known?.source,
    });
    return items;
  };

  const reconcileEmptyTable = async (tableId, options = {}) => {
    if (!tableId) return null;
    const { deselect = false, reason = "empty-cart" } = options || {};
    const requestedGeneration = Number.isFinite(Number(options?.expectedGeneration))
      ? Number(options.expectedGeneration)
      : getTableCartGeneration(tableId, { isDelivery: false });
    const protection = getTableClearProtection(tableId, { isDelivery: false });
    const forcedFree =
      forcedFreeTablesRef.current.has(tableId) ||
      forcedFreeTablesRef.current.has(String(tableId));
    const recentlyPaid =
      recentlyPaidTablesRef.current.has(tableId) ||
      recentlyPaidTablesRef.current.has(String(tableId));
    if (
      protection.shouldProtect &&
      protection.pendingCount <= 0 &&
      !forcedFree &&
      !recentlyPaid
    ) {
      cashDebugLog("Réconciliation ignorée: panier local protégé", {
        tableId,
        reason,
        activeLocalCount: protection.activeLocalCount,
      });
      return {
        reconciled: false,
        status: "occupied",
        protected_local_cart: true,
      };
    }
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/tables/${tableId}/reconcile-empty`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reason,
            client_generation: requestedGeneration,
          }),
        },
      );
      if (!response.ok) return null;
      const payload = await response.json().catch(() => null);
      const activeItemsCount = Number(payload?.active_items_count || 0);
      const serverConfirmedFree =
        payload?.reconciled === true &&
        String(payload?.status || "").toLowerCase() === "free" &&
        activeItemsCount === 0;
      if (!serverConfirmedFree) {
        return payload;
      }

      const pendingItemsAfterResponse = getSnapshotPendingItems(tableId);
      const hasProtectedPendingItems = pendingItemsAfterResponse.length > 0;
      if (
        hasProtectedPendingItems &&
        stageTableDraftRecovery({
          tableId,
          pendingItems: pendingItemsAfterResponse,
          occupancyCycle: payload?.occupancy_cycle,
          reason: "draft-found-during-empty-reconcile",
        })
      ) {
        return {
          ...(payload || {}),
          reconciled: false,
          status: "free",
          protected_local_draft: true,
          recovery_required: true,
        };
      }

      const isSelectedTarget =
        String(selectedTableRef.current?.id) === String(tableId) &&
        !selectedTableRef.current?.isDelivery;
      optimisticTableStatusRef.current.delete(tableId);
      optimisticTableStatusRef.current.delete(String(tableId));
      setTables((prev) =>
        Array.isArray(prev)
          ? prev.map((table) =>
              String(table.id) === String(tableId)
                ? {
                    ...table,
                    status: "free",
                    customer_count: null,
                    customerCount: null,
                  }
                : table,
            )
          : prev,
      );
      if (isSelectedTarget) {
        setExistingItemsForPayment([]);
        setCurrentOrder({ items: [], total: 0 });
        setHasExistingOrders(false);
      }
      forgetKnownExistingItems(tableId, null, { preservePending: false });
      commitTableStatesCache((prev) => {
        const next = new Map(prev);
        next.delete(tableId);
        next.delete(String(tableId));
        return next;
      });
      setTableCustomerCounts((prevCounts) => {
        if (!Object.prototype.hasOwnProperty.call(prevCounts, tableId)) {
          return prevCounts;
        }
        const nextCounts = { ...prevCounts };
        delete nextCounts[tableId];
        return nextCounts;
      });
      if (isSelectedTarget) {
        if (deselect) {
          selectedTableRef.current = null;
          setSelectedTable(null);
        } else {
          const freeSelectedTable = {
            ...selectedTableRef.current,
            status: "free",
            customer_count: null,
            customerCount: null,
          };
          selectedTableRef.current = freeSelectedTable;
          setSelectedTable(freeSelectedTable);
        }
      }
      return payload;
    } catch (error) {
      cashDebugLog("Réconciliation table vide ignorée", {
        tableId,
        error: error?.message || String(error),
      });
      return null;
    }
  };

  const closeEmptyTableOnExit = (table, snapshot, reason) => {
    const hasActiveItems = snapshotHasItems(snapshot);
    if (table?.id && table.status === "free" && !hasActiveItems) {
      setTableCustomerCounts((prevCounts) => {
        if (!Object.prototype.hasOwnProperty.call(prevCounts, table.id)) {
          return prevCounts;
        }
        const nextCounts = { ...prevCounts };
        delete nextCounts[table.id];
        return nextCounts;
      });
    }
    if (
      !table?.id ||
      table.isDelivery ||
      isFastFoodMode ||
      table.status === "free" ||
      hasActiveItems
    ) {
      return false;
    }

    const tableKey = String(table.id);
    if (tableCloseInFlightRef.current.has(tableKey)) return true;

    const expectedGeneration = getTableCartGeneration(table.id, {
      isDelivery: false,
    });
    tableCloseInFlightRef.current.set(tableKey, expectedGeneration);
    Promise.resolve(
      reconcileEmptyTable(table.id, {
        deselect: false,
        reason,
        expectedGeneration,
      }),
    )
      .catch((error) => {
        cashDebugLog("Fermeture de table vide ignorée", {
          tableId: table.id,
          reason,
          error: error?.message || String(error),
        });
      })
      .finally(() => {
        if (tableCloseInFlightRef.current.get(tableKey) === expectedGeneration) {
          tableCloseInFlightRef.current.delete(tableKey);
        }
      });
    return true;
  };

const splitDeliveryItems = (items = []) => {
    const existing = [];
    const pending = [];
    (items || []).forEach((item) => {
      if (!item) return;
      if (item.order_item_id) {
        existing.push(item);
      } else {
        pending.push(item);
      }
    });
    return { existing, pending };
  };

const markDeliveryItemsAsSent = (items = [], contextId = "delivery") => {
  const timestamp = Date.now();
  return (items || []).map((item, index) => {
    if (item?.order_item_id) {
      return {
        ...item,
        __localSentAt: item.__localSentAt || item.local_sent_at || timestamp,
        // Ne pas forcer "preparing" côté caisse; laisser la cuisine/bar faire évoluer l'état
        status: item.status && item.status !== "pending" ? item.status : "pending",
      };
      }
      return {
        ...item,
        order_item_id: `temp-delivery-${contextId}-${timestamp}-${index}`,
        __localSentAt: timestamp,
        // Les nouveaux items envoyés doivent apparaître en "En attente"
        status: "pending",
      };
    });
  };

  const buildOptimisticOrderItemIdPrefix = (kind = "send") =>
    `temp-${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}-`;

  const hasOptimisticPrefix = (value, prefix) =>
    typeof value === "string" && value.startsWith(prefix);

  const computeItemExtrasTotal = (item) =>
    Array.isArray(item?.extras)
      ? item.extras.reduce(
          (sum, extra) =>
            sum +
            (extra && extra.type === "accompaniment"
              ? 0
              : (extra?.price || 0) * (extra?.quantity || 1)),
          0,
        )
      : 0;

const markCartItemsOptimistically = (items = [], prefix, shouldMark = null) => {
  const timestamp = Date.now();
  return (items || []).map((item, index) => {
    if (!item) return item;
    if (item.order_item_id) return item;
    if (typeof shouldMark === "function" && !shouldMark(item)) return item;
    const originalStatus = item.status ?? null;
    const extrasTotal = computeItemExtrasTotal(item);
    return {
      ...item,
      order_item_id: `${prefix}${index}`,
      __localSentAt: item.__localSentAt || item.local_sent_at || timestamp,
      status: item.status ?? "pending",
      totalPrice:
        item.totalPrice ??
        (item.price + extrasTotal) * (item.quantity || 1),
      __optimistic_original_status: originalStatus,
    };
  });
};

  const rollbackOptimisticCartItems = (items = [], prefix) =>
    (items || []).map((item) => {
      if (!item) return item;
      if (!hasOptimisticPrefix(item.order_item_id, prefix)) return item;
      const originalStatus = item.__optimistic_original_status;
      const next = { ...item };
      delete next.order_item_id;
      delete next.__optimistic_original_status;
      if (originalStatus) {
        next.status = originalStatus;
      } else {
        delete next.status;
      }
      return next;
    });

  const filterPersistedDeliveryItems = (items = []) =>
    (items || []).filter((item) => item && item.order_item_id);

  const normalizeDeliveryReferenceValue = (value) =>
    value !== undefined && value !== null
      ? value.toString().trim().toLowerCase()
      : "";

  const getDeliveryMethodValue = (source, fallback = null) => {
    if (!source) return fallback;
    return (
      source.delivery_method ||
      source.deliveryMethod ||
      source.method ||
      source.type ||
      fallback
    );
  };

  const getDeliveryReferenceValue = (source) => {
    if (!source) return "";
    const fields = [
      source.delivery_reference,
      source.deliveryRef,
      source.reference,
      source.ref,
    ];
    for (const field of fields) {
      if (typeof field === "string" && field.trim()) {
        return field.trim();
      }
    }
    return "";
  };

  const getTabDeliveryReference = (tab) => {
    if (!tab) return "";
    const directRef = getDeliveryReferenceValue(tab);
    if (directRef) return directRef;
    const orderRef = getDeliveryReferenceValue(tab.deliveryOrder);
    if (orderRef) return orderRef;
    const customerRef = getDeliveryReferenceValue(tab.deliveryCustomer);
    if (customerRef) return customerRef;
    return (
      (tab.deliveryCustomer && tab.deliveryCustomer.phone) ||
      tab.customerPhone ||
      ""
    );
  };

  const serializeDeliveryCustomer = (customer) => {
    if (!customer) return null;
    const method = getDeliveryMethodValue(customer);
    const reference = getDeliveryReferenceValue(customer);
    return {
      id: customer.id || null,
      first_name: customer.first_name || "",
      last_name: customer.last_name || "",
      phone: customer.phone || "",
      delivery_method: method || (customer.isDelivery ? "Direct" : null),
      delivery_reference: reference || null,
    };
  };

  const buildAggregatorCustomer = (method, referenceValue) => {
    const ref = (referenceValue || "").toString().trim();
    if (!ref || !method || method === "Direct") return null;
    return {
      id: null,
      first_name: method,
      last_name: ref,
      phone: ref,
      delivery_method: method,
      delivery_reference: ref,
      displayName: `${method} ${ref}`.trim(),
    };
  };

  const resolveDeliveryCustomer = (options = {}) => {
    const table = options.table ?? selectedTable;
    const method =
      options.method ??
      table?.deliveryMethod ??
      deliveryMethod ??
      "Direct";
    const referenceValue = (
      options.reference ??
      table?.deliveryRef ??
      table?.deliveryOrder?.deliveryRef ??
      deliveryRef ??
      ""
    )
      .toString()
      .trim();

    const isAggregatorMethod = method !== "Direct";
    if (isAggregatorMethod) {
      const effectiveReference =
        referenceValue || getSelectedDeliveryReference() || "";
      const aggCustomer = buildAggregatorCustomer(method, effectiveReference);
      return serializeDeliveryCustomer(aggCustomer);
    }

    if (deliveryCustomer) return serializeDeliveryCustomer(deliveryCustomer);
    if (selectedCustomerForOrder)
      return serializeDeliveryCustomer(selectedCustomerForOrder);
    if (table?.deliveryCustomer)
      return serializeDeliveryCustomer(table.deliveryCustomer);

    return null;
  };

  const getSelectedDeliveryReference = () => {
    if (!selectedTable?.isDelivery) return "";
    return (
      selectedTable.deliveryRef ||
      selectedTable.deliveryOrder?.deliveryRef ||
      getDeliveryReferenceValue(
        selectedTable.deliveryCustomer || selectedDeliveryOrder?.customer,
      ) ||
      ""
    );
  };

  const getDeliveryCartTitle = () => {
    if (!selectedTable?.isDelivery) return "En cours";
    return "Commande à livrer";
  };

  const isCategoryActive = useCallback((rawValue) => {
    if (rawValue === undefined || rawValue === null || rawValue === "") return true;
    if (rawValue === true || rawValue === 1) return true;
    if (rawValue === false || rawValue === 0) return false;
    const normalized = String(rawValue).trim().toLowerCase();
    if (normalized === "1" || normalized === "true") return true;
    if (normalized === "0" || normalized === "false") return false;
    return true;
  }, []);

  const computeActiveCategoryIds = (sourceCategories) => {
    const base = Array.isArray(sourceCategories)
      ? sourceCategories
      : categories;
    if (!Array.isArray(base)) {
      return new Set();
    }
    return new Set(
      base
        .filter((category) => category && isCategoryActive(category.is_active))
        .map((category) => category.id)
        .filter((id) => id !== undefined && id !== null),
    );
  };

  const normalizeProductForCaisse = (product) => {
    if (!product) return null;
    const normalizedAvailability =
      product.is_available === undefined || product.is_available === null
        ? true
        : product.is_available === true ||
          product.is_available === 1 ||
          product.is_available === "1" ||
          product.is_available === "true";
    const activeAccomp = product.active_accompaniments ?? 0;
    const activeSupp = product.active_supplements ?? 0;
    const supportsAccomp = !!product.has_accompaniments;
    const supportsSupp = !!product.has_supplements;
    const rawVariations = Array.isArray(product.variations)
      ? product.variations.filter(Boolean)
      : [];
    const normalizedVariations = rawVariations
      .filter((variation) => variation && variation.is_active !== false)
      .map((variation) => {
        const promoCandidate =
          variation.effective_price_for_day !== undefined && variation.effective_price_for_day !== null
            ? variation.effective_price_for_day
            : variation.daily_menu_price !== undefined && variation.daily_menu_price !== null
              ? variation.daily_menu_price
              : null;
        const effective =
          promoCandidate !== null
            ? parseMoney(promoCandidate)
            : parseMoney(variation.price);
        return {
          ...variation,
          attributes: parseVariationAttributesValue(variation.attributes),
          display_price: effective,
        };
      });
    const defaultVariation =
      normalizedVariations.find((variation) => variation.is_default) ||
      normalizedVariations[0] ||
      null;
    const isDailyMenuActive =
      product.daily_menu_active_today !== undefined && product.daily_menu_active_today !== null
        ? Boolean(product.daily_menu_active_today)
        : !!(
            product.daily_menu_active &&
            (
              !product.daily_menu_date ||
              String(product.daily_menu_date).slice(0, 10) ===
                new Date().toISOString().slice(0, 10)
            )
          );
    let displayPrice = parseMoney(product.price);
    if (product.product_type === "variable" && normalizedVariations.length) {
      const prices = normalizedVariations.map((v) =>
        v.display_price ??
        (v.effective_price_for_day !== undefined && v.effective_price_for_day !== null
          ? parseMoney(v.effective_price_for_day)
          : parseMoney(v.price)),
      );
      if (
        product.daily_menu_active_today &&
        product.daily_menu_price !== null &&
        product.daily_menu_price !== undefined
      ) {
        prices.push(parseMoney(product.daily_menu_price));
      }
      displayPrice = Math.min(...prices);
    } else {
      const promoPrice =
        product.daily_menu_price !== null && product.daily_menu_price !== undefined
          ? parseMoney(product.daily_menu_price)
          : null;
      const basePriceRaw =
        product.effective_price_for_day !== undefined && product.effective_price_for_day !== null
          ? parseMoney(product.effective_price_for_day)
          : parseMoney(product.price);
      displayPrice = isDailyMenuActive && promoPrice && promoPrice > 0 ? promoPrice : basePriceRaw;
    }
    if (
      product.price_min_today !== undefined &&
      product.price_min_today !== null
    ) {
      displayPrice = parseMoney(product.price_min_today) || displayPrice;
    }
    const priceRange = {
      min:
        product.price_min_today !== undefined && product.price_min_today !== null
          ? parseMoney(product.price_min_today) || displayPrice
          : displayPrice,
      max:
        product.price_max_today !== undefined && product.price_max_today !== null
          ? parseMoney(product.price_max_today) || displayPrice
          : Math.max(displayPrice, parseMoney(product.price)),
    };

    return {
      ...product,
      is_available: normalizedAvailability,
      is_daily_menu_active: !!isDailyMenuActive,
      supports_accompaniments: supportsAccomp,
      supports_supplements: supportsSupp,
      has_accompaniments:
        typeof activeAccomp === "number"
          ? activeAccomp > 0
          : supportsAccomp,
      has_supplements:
        typeof activeSupp === "number" ? activeSupp > 0 : supportsSupp,
      variations: normalizedVariations,
      price_min_today:
        product.price_min_today !== undefined && product.price_min_today !== null
          ? parseMoney(product.price_min_today) || displayPrice
          : priceRange.min,
      price_max_today:
        product.price_max_today !== undefined && product.price_max_today !== null
          ? parseMoney(product.price_max_today) || displayPrice
          : priceRange.max,
      default_variation_id: defaultVariation?.id || null,
      display_price: displayPrice,
    };
  };

  const shouldDisplayProduct = (product, activeCategoryIds) => {
    if (!product || !product.is_available) return false;
    if (
      product.category_id &&
      activeCategoryIds instanceof Set &&
      activeCategoryIds.size > 0 &&
      !activeCategoryIds.has(product.category_id)
    ) {
      return false;
    }
    return true;
  };

  const refreshProductsList = async (overrideCategories = null) => {
    const activeCategoryIds = computeActiveCategoryIds(
      overrideCategories || categories,
    );
    const productsResponse = await fetchWithTimeout(
      `${API_BASE}/products?_=${Date.now()}`,
    );
    if (!productsResponse.ok) {
      throw new Error("Erreur chargement produits");
    }
    const productsData = await productsResponse.json();
    const normalizedProducts = Array.isArray(productsData)
      ? productsData
          .map((product) => normalizeProductForCaisse(product))
          .filter((product) => shouldDisplayProduct(product, activeCategoryIds))
      : [];
    setProducts((prev) =>
      areProductListsEquivalent(prev, normalizedProducts) ? prev : normalizedProducts,
    );
    cashDebugLog(
      "✅ Produits chargés:",
      normalizedProducts.length,
      "/",
      Array.isArray(productsData) ? productsData.length : 0,
    );
    return normalizedProducts;
  };

  refreshProductsRef.current = refreshProductsList;

  const CATALOG_CACHE_KEY = "moo_catalog_cache_v1";
  const TABLES_CACHE_KEY = "moo_tables_cache_v1";
  const DELIVERY_CACHE_KEY = "moo_delivery_cache_v1";

  const areProductListsEquivalent = (prevList = [], nextList = []) => {
    if (!Array.isArray(prevList) || !Array.isArray(nextList)) return false;
    if (prevList.length !== nextList.length) return false;
    for (let index = 0; index < nextList.length; index += 1) {
      const prev = prevList[index] || {};
      const next = nextList[index] || {};
      if (String(prev.id || "") !== String(next.id || "")) return false;
      if (String(prev.name || "") !== String(next.name || "")) return false;
      if (
        Number(prev.price || prev.product_price || 0) !==
        Number(next.price || next.product_price || 0)
      ) {
        return false;
      }
      if (Number(prev.is_available || 0) !== Number(next.is_available || 0)) {
        return false;
      }
      if (String(prev.sku || "") !== String(next.sku || "")) return false;
      if (
        Array.isArray(prev.variations) &&
        Array.isArray(next.variations) &&
        prev.variations.length !== next.variations.length
      ) {
        return false;
      }
    }
    return true;
  };

  const areCategoryListsEquivalent = (prevList = [], nextList = []) => {
    if (!Array.isArray(prevList) || !Array.isArray(nextList)) return false;
    if (prevList.length !== nextList.length) return false;
    for (let index = 0; index < nextList.length; index += 1) {
      const prev = prevList[index] || {};
      const next = nextList[index] || {};
      if (String(prev.id || "") !== String(next.id || "")) return false;
      if (String(prev.name || "") !== String(next.name || "")) return false;
      if (Number(prev.sort_order || 0) !== Number(next.sort_order || 0)) return false;
      if (Number(prev.is_active || 0) !== Number(next.is_active || 0)) return false;
      if (String(prev.color || "") !== String(next.color || "")) return false;
    }
    return true;
  };

  const areTableListsEquivalent = (prevList = [], nextList = []) => {
    if (!Array.isArray(prevList) || !Array.isArray(nextList)) return false;
    if (prevList.length !== nextList.length) return false;
    for (let index = 0; index < nextList.length; index += 1) {
      const prev = prevList[index] || {};
      const next = nextList[index] || {};
      if (String(prev.id || "") !== String(next.id || "")) return false;
      if (String(prev.status || "") !== String(next.status || "")) return false;
      if (String(prev.name || "") !== String(next.name || "")) return false;
      if (Number(prev.number || 0) !== Number(next.number || 0)) return false;
      if (Number(prev.customerCount || 0) !== Number(next.customerCount || 0)) {
        return false;
      }
    }
    return true;
  };

  const areClientTabsEquivalent = (prevList = [], nextList = []) => {
    if (!Array.isArray(prevList) || !Array.isArray(nextList)) return false;
    if (prevList.length !== nextList.length) return false;
    for (let index = 0; index < nextList.length; index += 1) {
      const prev = prevList[index] || {};
      const next = nextList[index] || {};
      if (String(prev.id || "") !== String(next.id || "")) return false;
      if (String(prev.orderId || "") !== String(next.orderId || "")) return false;
      if (String(prev.status || "") !== String(next.status || "")) return false;
      if (String(prev.deliveryMethod || "") !== String(next.deliveryMethod || "")) {
        return false;
      }
      if (Number(prev.total || 0) !== Number(next.total || 0)) return false;
      if ((prev.items || []).length !== (next.items || []).length) return false;
    }
    return true;
  };

  const areDeliveryOrdersEquivalent = (prevList = [], nextList = []) => {
    if (!Array.isArray(prevList) || !Array.isArray(nextList)) return false;
    if (prevList.length !== nextList.length) return false;
    for (let index = 0; index < nextList.length; index += 1) {
      const prev = prevList[index] || {};
      const next = nextList[index] || {};
      if (String(prev.orderId || "") !== String(next.orderId || "")) return false;
      if (String(prev.status || "") !== String(next.status || "")) return false;
      if (String(prev.deliveryMethod || "") !== String(next.deliveryMethod || "")) {
        return false;
      }
      if (Number(prev.total || 0) !== Number(next.total || 0)) return false;
      if ((prev.items || []).length !== (next.items || []).length) return false;
    }
    return true;
  };

  const readCatalogCache = () => {
    try {
      const raw = localStorage.getItem(CATALOG_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      const cachedCategories = Array.isArray(parsed.categories)
        ? parsed.categories.filter(Boolean)
        : [];
      const cachedProducts = Array.isArray(parsed.products)
        ? parsed.products.filter(Boolean)
        : [];
      if (cachedCategories.length === 0 && cachedProducts.length === 0) {
        return null;
      }
      return {
        categories: cachedCategories,
        products: cachedProducts,
        ts: parsed.ts,
      };
    } catch (_) {
      return null;
    }
  };

  const writeCatalogCache = (cachedCategories, cachedProducts) => {
    try {
      const payload = {
        ts: Date.now(),
        categories: Array.isArray(cachedCategories) ? cachedCategories : [],
        products: Array.isArray(cachedProducts) ? cachedProducts : [],
      };
      localStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify(payload));
    } catch (_) {}
  };

  const readTablesCache = () => {
    try {
      const raw = localStorage.getItem(TABLES_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const cachedTables = Array.isArray(parsed?.tables) ? parsed.tables : [];
      if (cachedTables.length === 0) return null;
      return {
        ts: Number(parsed?.ts || 0),
        tables: cachedTables,
      };
    } catch (_error) {
      return null;
    }
  };

  const writeTablesCache = (cachedTables) => {
    try {
      const payload = {
        ts: Date.now(),
        tables: Array.isArray(cachedTables) ? cachedTables : [],
      };
      localStorage.setItem(TABLES_CACHE_KEY, JSON.stringify(payload));
    } catch (_error) {}
  };

  const readDeliveryCache = () => {
    try {
      const raw = localStorage.getItem(DELIVERY_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return {
        ts: Number(parsed?.ts || 0),
        tabs: Array.isArray(parsed?.tabs) ? parsed.tabs : [],
        orders: Array.isArray(parsed?.orders) ? parsed.orders : [],
      };
    } catch (_error) {
      return null;
    }
  };

  const writeDeliveryCache = (tabs = [], orders = []) => {
    try {
      const payload = {
        ts: Date.now(),
        tabs: Array.isArray(tabs) ? tabs : [],
        orders: Array.isArray(orders) ? orders : [],
      };
      localStorage.setItem(DELIVERY_CACHE_KEY, JSON.stringify(payload));
    } catch (_error) {}
  };

  const resetApiRetry = () => {
    if (apiRetryTimerRef.current) {
      clearTimeout(apiRetryTimerRef.current);
      apiRetryTimerRef.current = null;
    }
    apiRetryAttemptsRef.current = 0;
  };

  const scheduleApiRetry = (reason) => {
    if (apiRetryTimerRef.current) return;
    const attempt = apiRetryAttemptsRef.current + 1;
    apiRetryAttemptsRef.current = attempt;
    const delay = Math.min(1000 * attempt, 10000);
    console.warn("🟠 API indisponible, nouvelle tentative", {
      reason: reason || "unknown",
      attempt,
      delay,
    });
    apiRetryTimerRef.current = setTimeout(() => {
      apiRetryTimerRef.current = null;
      if (typeof loadBasicDataRef.current === "function") {
        loadBasicDataRef.current({ reason: "retry" });
      }
    }, delay);
  };

  const handleApiFailure = (context, error) => {
    console.error("�?� Erreur chargement données de base:", error);
    const hasCachedData =
      (categories?.length || 0) > 0 ||
      (products?.length || 0) > 0 ||
      (tables?.length || 0) > 0;
    const cachedCatalog = readCatalogCache();
    const hasCatalogCache =
      (cachedCatalog?.categories?.length || 0) > 0 ||
      (cachedCatalog?.products?.length || 0) > 0;
    if (!hasCachedData && hasCatalogCache) {
      if ((categories?.length || 0) === 0 && cachedCatalog?.categories?.length) {
        setCategories(cachedCatalog.categories);
      }
      if ((products?.length || 0) === 0 && cachedCatalog?.products?.length) {
        setProducts(cachedCatalog.products);
      }
      setApiStatus("disconnected");
    } else if (!hasCachedData) {
      loadFallbackData();
      setApiStatus("standalone");
    } else {
      setApiStatus("disconnected");
    }
    scheduleApiRetry(context);
  };

  // Fonction pour charger les données de base (catégories et produits)
  const loadBasicData = async (options = {}) => {
    if (basicDataLoadingRef.current) return;
    basicDataLoadingRef.current = true;
    const reason = options?.reason ? ` (${options.reason})` : "";
    const stopCatalogTimer = startPerfTimer("caisse.catalog_sync_ms", {
      reason: options?.reason || "initial",
    });
    let lastError = null;
    let categoriesOk = false;
    let productsOk = false;
    let sortedCategories = Array.isArray(categories) ? categories : [];

    try {
      cashDebugLog(`🔄 Chargement des données de base${reason}...`);
      const markApiConnected = () => {
        setApiStatus((prev) => (prev === "connected" ? prev : "connected"));
        resetApiRetry();
        if (!firstApiConnectedMarkedRef.current) {
          firstApiConnectedMarkedRef.current = true;
          const now =
            typeof performance !== "undefined" ? performance.now() : Date.now();
          recordPerfMetric(
            "caisse.time_to_api_connected_ms",
            now - appBootPerfRef.current,
            { reason: options?.reason || "initial" },
          );
        }
      };

      const cachedCatalog = readCatalogCache();
      const cachedTables = readTablesCache();
      const cachedDeliveries = readDeliveryCache();
      const hasCachedCatalog = Boolean(
        (cachedCatalog?.categories?.length || 0) > 0 ||
          (cachedCatalog?.products?.length || 0) > 0,
      );
      if (
        (!Array.isArray(categories) || categories.length === 0) &&
        cachedCatalog?.categories?.length
      ) {
        sortedCategories = cachedCatalog.categories;
        setCategories((prev) =>
          areCategoryListsEquivalent(prev, sortedCategories) ? prev : sortedCategories,
        );
      }
      if (
        (!Array.isArray(products) || products.length === 0) &&
        cachedCatalog?.products?.length
      ) {
        setProducts(cachedCatalog.products);
      }
      if (
        (!Array.isArray(tables) || tables.length === 0) &&
        cachedTables?.tables?.length
      ) {
        const visibleCachedTables = cachedTables.tables.filter(
          (table) =>
            table?.number !== 99 &&
            table?.name !== "T99" &&
            table?.id !== 99 &&
            table?.status !== "maintenance",
        );
        setTables((prev) =>
          areTableListsEquivalent(prev, visibleCachedTables)
            ? prev
            : visibleCachedTables,
        );
      }
      if (
        (!Array.isArray(clientTabs) || clientTabs.length === 0) &&
        (cachedDeliveries?.tabs?.length || 0) > 0
      ) {
        setClientTabs((prev) =>
          areClientTabsEquivalent(prev, cachedDeliveries.tabs)
            ? prev
            : cachedDeliveries.tabs,
        );
      }
      if (
        (!Array.isArray(deliveryOrders) || deliveryOrders.length === 0) &&
        (cachedDeliveries?.orders?.length || 0) > 0
      ) {
        setDeliveryOrders((prev) =>
          areDeliveryOrdersEquivalent(prev, cachedDeliveries.orders)
            ? prev
            : cachedDeliveries.orders,
        );
      }

      // Le catalogue local est utilisable immédiatement. Ne pas maintenir le
      // plein écran de démarrage pendant que sa version réseau se rafraîchit.
      // Le résultat serveur reste autoritaire et remplacera ce cache ci-dessous.
      if (hasCachedCatalog) {
        setInitialDataHydrated(true);
      }

      // Catégories et produits sont indépendants côté HTTP. Les demander en
      // parallèle évite de cumuler leurs latences au premier démarrage.
      const [categoriesResult, productsResult] = await Promise.allSettled([
        fetchWithTimeout(`${API_BASE}/categories?_=${Date.now()}`),
        fetchWithTimeout(`${API_BASE}/products?_=${Date.now()}`),
      ]);

      // Charger les catégories
      try {
        if (categoriesResult.status === "rejected") {
          throw categoriesResult.reason;
        }
        const categoriesResponse = categoriesResult.value;
        if (categoriesResponse.ok) {
          const categoriesData = await categoriesResponse.json();
          sortedCategories = Array.isArray(categoriesData)
            ? [...categoriesData].map((c, idx) => {
                return applyCategoryDefaults({
                  ...c,
                  sort_order: Number.isFinite(Number(c.sort_order))
                    ? Number(c.sort_order)
                    : idx,
                  is_active: isCategoryActive(c?.is_active) ? 1 : 0,
                });
              })
            : [];
          sortedCategories.sort((a, b) => {
            if (a.sort_order !== b.sort_order) {
              return a.sort_order - b.sort_order;
            }
            return (a.id || 0) - (b.id || 0);
          });
	          setCategories((prev) =>
	            areCategoryListsEquivalent(prev, sortedCategories)
	              ? prev
	              : sortedCategories,
	          );
          categoriesOk = true;
          markApiConnected();
          cashDebugLog(
            "✅ Catégories chargées:",
            sortedCategories.length,
            sortedCategories.map((c) => `${c.sort_order}:${c.name}`),
          );
        } else {
          lastError = new Error(
            `Erreur chargement catégories (${categoriesResponse.status})`,
          );
          console.warn("⚠�? Chargement catégories:", categoriesResponse.status);
        }
      } catch (error) {
        lastError = error;
        console.warn("⚠�? Chargement catégories:", error);
      }

      // Charger les produits
      try {
        if (productsResult.status === "rejected") {
          throw productsResult.reason;
        }
        const productsResponse = productsResult.value;
        if (!productsResponse.ok) {
          throw new Error(
            `Erreur chargement produits (${productsResponse.status})`,
          );
        }
        const productsData = await productsResponse.json();
        const activeCategoryIds = computeActiveCategoryIds(sortedCategories);
        const refreshedProducts = Array.isArray(productsData)
          ? productsData
              .map((product) => normalizeProductForCaisse(product))
              .filter((product) =>
                shouldDisplayProduct(product, activeCategoryIds),
              )
          : [];
        setProducts((prev) =>
          areProductListsEquivalent(prev, refreshedProducts)
            ? prev
            : refreshedProducts,
        );
        cashDebugLog(
          "✅ Produits chargés:",
          refreshedProducts.length,
          "/",
          Array.isArray(productsData) ? productsData.length : 0,
        );
        productsOk = true;
        markApiConnected();
        if (categoriesOk || sortedCategories.length > 0) {
          writeCatalogCache(
            sortedCategories,
            Array.isArray(refreshedProducts) ? refreshedProducts : [],
          );
        }
      } catch (error) {
        lastError = error;
        console.warn("⚠�? Chargement produits:", error);
      }

      // Charger les données non critiques en arrière-plan pour accélérer le premier affichage
      if (!nonCriticalHydrationInFlightRef.current) {
        nonCriticalHydrationInFlightRef.current = true;
        const stopNonCriticalTimer = startPerfTimer(
          "caisse.non_critical_sync_ms",
          { reason: options?.reason || "initial" },
        );
        void (async () => {
          let hasOccupiedTables = false;
          try {
            hasOccupiedTables = await loadTables();
          } catch (error) {
            console.warn("⚠�? Chargement tables (bg):", error);
          }
          try {
            await loadPendingDeliveries({ allowAutoSelect: !hasOccupiedTables });
          } catch (error) {
            console.warn("⚠�? Chargement livraisons (bg):", error);
          } finally {
            stopNonCriticalTimer({ hasOccupiedTables });
            nonCriticalHydrationInFlightRef.current = false;
          }
        })();
      }

      if (categoriesOk || productsOk) {
        cashDebugLog("✅ API connectée avec succès");
        stopCatalogTimer({ ok: true });
      } else {
        handleApiFailure(options?.reason || "loadBasicData", lastError);
        stopCatalogTimer({
          ok: false,
          error: lastError?.message || "loadBasicData_failed",
        });
      }
    } finally {
      basicDataLoadingRef.current = false;
      setInitialDataHydrated(true);
    }
  };

  loadBasicDataRef.current = loadBasicData;

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      return undefined;
    }
    const channel = new BroadcastChannel("moo-products-sync");
    const handleMessage = (event) => {
      try {
        const data = event?.data || event;
        if (!data || typeof data !== "object") return;
        const { type } = data;
        if (
          type === "PRODUCT_AVAILABILITY_CHANGED" ||
          type === "PRODUCT_UPDATED" ||
          type === "PRODUCT_CREATED" ||
          type === "PRODUCT_DELETED" ||
          type === "PRODUCTS_REFRESH_REQUESTED" ||
          type === "PRODUCT_DAILY_MENU" ||
          type === "PRODUCT_DAILY_MENU_DISABLED"
        ) {
          cashDebugLog("�? Synchronisation produits reçue:", data);
          const refresh = refreshProductsRef.current;
          if (typeof refresh === "function") {
            refresh().catch((error) =>
              console.error("�?� Rafraîchissement catalogue échoué:", error),
            );
          }
        }
      } catch (error) {
        console.error("�?� Erreur BroadcastChannel produits:", error);
      }
    };
    channel.onmessage = handleMessage;
    return () => {
      channel.close();
    };
  }, []);

  const scheduleTableCartRefresh = ({
    tableId,
    reason = "cart-mutated",
    orderId = null,
    orderItemId = null,
    confirmedEvidence = null,
    isDelivery: explicitIsDelivery,
    contextKnown: explicitContextKnown,
    targetTableId: explicitTargetTableId,
  } = {}) => {
    if (tableId === null || tableId === undefined || tableId === "") return;

    const runtime = socketRuntimeRef.current || {};
    const resolvedContext =
      typeof explicitIsDelivery === "boolean"
        ? {
            known: explicitContextKnown !== false,
            isDelivery: explicitIsDelivery,
            targetTableId: explicitTargetTableId ?? tableId,
          }
        : resolveRealtimeTableContext(
            { tableId, orderId },
            {
              selectedTable: selectedTableRef.current,
              tables: runtime.tables || [],
              clientTabs: runtime.clientTabs || [],
            },
          );
    const contextKnown = resolvedContext.known === true;
    const isDelivery = contextKnown
      ? Boolean(resolvedContext.isDelivery)
      : null;
    const targetTableId =
      resolvedContext.targetTableId ?? explicitTargetTableId ?? tableId;
    const eventPolicy = resolveTableCartEventPolicy(reason);
    const eventKind = eventPolicy.kind;
    const evidenceRegistry = destructiveCartEvidenceRef.current;
    const previousEvidence = evidenceRegistry.peek(tableId);
    const cachedSentItems =
      contextKnown &&
      !isDelivery &&
      eventKind === TABLE_CART_EVENT_KIND.DESTRUCTIVE
        ? getSnapshotCachedExistingItems(tableId).filter(
            (item) => getExplicitOrderItemId(item) !== null,
          )
        : [];
    const matchingCachedItem = cachedSentItems.find((item) => {
      if (orderItemId !== null && orderItemId !== undefined) {
        return isSameOrderItemId(getExplicitOrderItemId(item), orderItemId);
      }
      if (orderId !== null && orderId !== undefined) {
        return isSameOrderItemId(item?.orderId ?? item?.order_id, orderId);
      }
      return true;
    });
    const generation = contextKnown
      ? bumpTableCartGeneration(targetTableId, { isDelivery })
      : null;

    if (eventPolicy.shouldMarkOccupied && contextKnown && !isDelivery) {
      evidenceRegistry.invalidate(tableId);
      markTableOccupiedInUi(tableId);
    } else if (
      eventKind === TABLE_CART_EVENT_KIND.UNKNOWN &&
      contextKnown &&
      !isDelivery
    ) {
      evidenceRegistry.invalidate(tableId);
    } else if (
      contextKnown &&
      !isDelivery &&
      eventKind === TABLE_CART_EVENT_KIND.DESTRUCTIVE
    ) {
      const confirmedList = Array.isArray(confirmedEvidence)
        ? confirmedEvidence
        : confirmedEvidence
          ? [confirmedEvidence]
          : [];
      const confirmedOrderIds = confirmedList
        .map((entry) => entry?.orderId)
        .filter((value) => value !== null && value !== undefined && value !== "");
      const confirmedOrderItemIds = confirmedList
        .map((entry) => entry?.orderItemId)
        .filter((value) => value !== null && value !== undefined && value !== "");

      if (confirmedOrderIds.length || confirmedOrderItemIds.length) {
        evidenceRegistry.record(
          tableId,
          {
            orderIds: confirmedOrderIds,
            orderItemIds: confirmedOrderItemIds,
            source: "confirmed-local-mutation",
          },
          generation,
        );
      } else if (matchingCachedItem) {
        evidenceRegistry.record(
          tableId,
          {
            orderId: matchingCachedItem?.orderId ?? matchingCachedItem?.order_id ?? orderId,
            orderItemId: getExplicitOrderItemId(matchingCachedItem),
            source: "cached-at-event",
          },
          generation,
        );
      } else if (previousEvidence) {
        const rebased = evidenceRegistry.rebaseMatching(
          tableId,
          { orderId, orderItemId },
          generation,
          { source: "matching-destructive-event" },
        );
        if (!rebased) evidenceRegistry.invalidate(tableId);
      }
    }

    const scopeKey = `${isDelivery ? "delivery" : contextKnown ? "table" : "unknown"}:${String(
      targetTableId,
    )}`;
    tableCartEventCoordinatorRef.current.enqueue(scopeKey, {
      tableId,
      targetTableId,
      reason,
      orderId,
      orderItemId,
      kind: eventKind,
      contextKnown,
      isDelivery,
    });

    if (!contextKnown) {
      metadataRefreshCoordinatorRef.current.enqueue("metadata", "*");
    } else if (
      !isDelivery &&
      ["order-created", "order-created-legacy"].includes(
        String(reason || "").toLowerCase(),
      )
    ) {
      metadataRefreshCoordinatorRef.current.enqueue("metadata", tableId);
    }
  };

  const getSnapshotPendingItems = (tableId) => {
    const tableKey = String(tableId);
    const selectedNow = selectedTableRef.current;
    if (
      selectedNow &&
      !selectedNow.isDelivery &&
      String(selectedNow.id) === tableKey
    ) {
      return (currentOrderRef.current?.items || []).filter(isItemActive);
    }
    const cachedState =
      tableStatesCacheRef.current.get(tableId) ||
      tableStatesCacheRef.current.get(tableKey);
    if (Array.isArray(cachedState?.currentOrder?.items)) {
      return cachedState.currentOrder.items.filter(isItemActive);
    }
    const persisted = persistentCartCacheRef.current.get(
      buildPersistentCartKey(tableId, false),
    );
    return (persisted?.pendingItems || []).filter(isItemActive);
  };

  const stageTableDraftRecovery = ({
    tableId,
    pendingItems = [],
    occupancyCycle = null,
    reason = "table-settled",
  }) => {
    const activePendingItems = (pendingItems || []).filter(isItemActive);
    const tableKey = String(tableId);
    if (activePendingItems.length === 0) {
      draftRecoveryTableIdsRef.current.delete(tableKey);
      return false;
    }

    draftRecoveryTableIdsRef.current.add(tableKey);
    forgetKnownExistingItems(tableId, false);
    const cacheKey = buildPersistentCartKey(tableId, false);
    const originEntry = persistentCartCacheRef.current.get(cacheKey);
    const originTable = selectedTableRef.current && String(selectedTableRef.current.id) === tableKey
      ? selectedTableRef.current : tables.find(table => String(table.id) === tableKey);
    const originCycle = originEntry?.occupancyCycle ?? resolveTableOccupancyCycle(originTable);
    const originContext = originEntry?.cacheContextId ?? originTable?.cache_context_id ?? "";
    const detached = !hasCompatibleCartOrigin(
      { occupancyCycle: originCycle, cacheContextId: originContext },
      { ...originTable, occupancy_cycle: Number(occupancyCycle) || originTable?.occupancy_cycle },
    );
    if (detached) {
      preserveDetachedCart(persistentCartCacheRef.current, cacheKey,
        { ...originEntry, pendingItems: activePendingItems, occupancyCycle: originCycle, cacheContextId: originContext });
      persistentCartCacheRef.current.delete(cacheKey);
      commitTableStatesCache(previous => {
        const next = new Map(previous); next.delete(tableId); next.delete(tableKey); return next;
      });
      draftRecoveryTableIdsRef.current.delete(tableKey);
    }
    if (!detached) {
    upsertPersistentCartCacheEntry(
      persistentCartCacheRef.current,
      cacheKey,
      {
        items: [],
        pendingItems: activePendingItems,
        recoveryRequired: true,
        recoveryReason: reason,
        occupancyCycle: originCycle,
        cacheContextId: originContext,
        timestamp: Date.now(),
      },
    );
    }
    writePersistentCartCache(persistentCartCacheRef.current);
    optimisticTableStatusRef.current.delete(tableId);
    optimisticTableStatusRef.current.delete(tableKey);
    setTables((previous) =>
      Array.isArray(previous)
        ? previous.map((table) =>
            String(table?.id) === tableKey
              ? {
                  ...table,
                  status: "free",
                  occupancy_cycle:
                    Number(occupancyCycle) ||
                    resolveTableOccupancyCycle(table) ||
                    1,
                }
              : table,
          )
        : previous,
    );

    const selectedNow = selectedTableRef.current;
    if (
      selectedNow &&
      !selectedNow.isDelivery &&
      String(selectedNow.id) === tableKey
    ) {
      const nextSelected = {
        ...selectedNow,
        status: "free",
        occupancy_cycle:
          Number(occupancyCycle) ||
          resolveTableOccupancyCycle(selectedNow) ||
          1,
      };
      selectedTableRef.current = nextSelected;
      currentOrderRef.current = {
        items: detached ? [] : activePendingItems,
        total: detached ? 0 : computeItemsTotal(activePendingItems),
      };
      existingItemsForPaymentRef.current = [];
      setSelectedTable(nextSelected);
      setCurrentOrder(currentOrderRef.current);
      setExistingItemsForPayment([]);
      setHasExistingOrders(false);
    }
    if (draftPreservedNoticeTimerRef.current) {
      clearTimeout(draftPreservedNoticeTimerRef.current);
    }
    const pendingQuantity = activePendingItems.reduce(
      (sum, item) => sum + Math.max(1, Number(item?.quantity) || 1),
      0,
    );
    setDraftPreservedNotice(
      `${pendingQuantity} article${pendingQuantity > 1 ? "s" : ""} non envoyé${pendingQuantity > 1 ? "s" : ""} ${pendingQuantity > 1 ? "ont" : "a"} été conservé${pendingQuantity > 1 ? "s" : ""}.`,
    );
    draftPreservedNoticeTimerRef.current = setTimeout(() => {
      setDraftPreservedNotice("");
      draftPreservedNoticeTimerRef.current = null;
    }, 3500);
    cashDebugLog("Brouillon isolé après clôture du cycle de table", {
      tableId,
      pendingCount: activePendingItems.length,
      occupancyCycle,
      reason,
    });
    return true;
  };

  const getSnapshotCachedExistingItems = (tableId) => {
    const tableKey = String(tableId);
    const selectedNow = selectedTableRef.current;
    if (
      selectedNow &&
      !selectedNow.isDelivery &&
      String(selectedNow.id) === tableKey &&
      (existingItemsForPaymentRef.current?.length || 0) > 0
    ) {
      return existingItemsForPaymentRef.current.filter(isItemActive);
    }
    const cachedState =
      tableStatesCacheRef.current.get(tableId) ||
      tableStatesCacheRef.current.get(tableKey);
    if ((cachedState?.existingItems?.length || 0) > 0) {
      return cachedState.existingItems.filter(isItemActive);
    }
    const known =
      lastExistingItemsRef.current.get(tableId) ||
      lastExistingItemsRef.current.get(tableKey);
    if (known && !known.isDelivery && (known.items?.length || 0) > 0) {
      return known.items.filter(isItemActive);
    }
    const persisted = persistentCartCacheRef.current.get(
      buildPersistentCartKey(tableId, false),
    );
    return (persisted?.items || []).filter(isItemActive);
  };

  const normalizeCartSnapshotItems = (snapshot) => {
    const allItems = [];
    (snapshot?.orders || []).forEach((order) => {
      (order?.items || []).forEach((item) => {
        const variationMeta = buildVariationMetadata(item);
        const rawCourseNumber = item.course_number ?? item.courseNumber ?? null;
        const normalizedCourseNumber =
          rawCourseNumber === null || rawCourseNumber === ""
            ? null
            : Number(rawCourseNumber);
        allItems.push({
          id: item.product_id,
          product_id: item.product_id,
          order_item_id: item.order_item_id ?? item.id,
          client_line_id: item.client_line_id ?? item.clientLineId ?? null,
          client_request_id:
            item.client_request_id ?? item.clientRequestId ?? null,
          origin_terminal_id:
            item.origin_terminal_id ?? item.originTerminalId ?? null,
          name: item.product_name,
          price: item.unit_price,
          quantity: item.quantity,
          totalPrice: item.total_price,
          emoji: item.emoji,
          image_path: item.image_path,
          image_url: item.image_url,
          destination: item.destination,
          status: item.status,
          orderId: order.id,
          order_number: order.order_number,
          server_name: order.server_name || order.created_by_name || "",
          serverName: order.server_name || order.created_by_name || "",
          created_by_name: order.created_by_name || order.server_name || "",
          createdByName: order.created_by_name || order.server_name || "",
          payment_status: order.payment_status,
          isPaid: false,
          extras: item.extras || [],
          variation_id: item.variation_id ?? null,
          variationId: item.variation_id ?? null,
          variation_name: item.variation_name ?? "",
          variationName: item.variation_name ?? "",
          variation_attributes: item.variation_attributes ?? null,
          variationAttributes: item.variation_attributes ?? null,
          course_id: item.course_id ?? item.courseId ?? null,
          courseId: item.course_id ?? item.courseId ?? null,
          course_number: Number.isInteger(normalizedCourseNumber)
            ? normalizedCourseNumber
            : null,
          courseNumber: Number.isInteger(normalizedCourseNumber)
            ? normalizedCourseNumber
            : null,
          course_management_enabled: Number(
            order?.course_management_enabled || 0,
          ),
          ...variationMeta,
        });
      });
    });
    return normalizeItemsForExisting(allItems).filter(isItemActive);
  };

  const fetchCartSnapshotForReconciliation = async (tableId, deadline) => {
    const remaining = Math.max(0, deadline - Date.now());
    if (remaining < 250) throw new Error("reconciliation-timeout");
    const response = await fetchWithTimeout(
      `${API_BASE}/tables/${tableId}/cart-snapshot`,
      {},
      remaining,
    );
    if (!response.ok) throw new Error(`snapshot-http-${response.status}`);
    const snapshot = await response.json().catch(() => null);
    if (
      !snapshotHasCanonicalItems(snapshot) ||
      String(snapshot?.table_id) !== String(tableId)
    ) {
      throw new Error("snapshot-incomplete");
    }
    return snapshot;
  };

  const applyCartSnapshotReconciliation = ({
    tableId,
    readToken,
    snapshot,
    hadSentItemAtStart,
    closeVisibleIfEmpty = false,
  }) => {
    if (!tableCartSyncRef.current.isCurrentRead(readToken)) return false;
    if (orderActionInFlightRef.current) return false;

    let localPendingItems = getSnapshotPendingItems(tableId);
    const cacheKey = buildPersistentCartKey(tableId, false);
    const localOrigin = persistentCartCacheRef.current.get(cacheKey);
    const snapshotTable = { occupancy_cycle: snapshot.occupancy_cycle, cache_context_id: snapshot.cache_context_id };
    if (localOrigin && !hasCompatibleCartOrigin(localOrigin, snapshotTable)) {
      preserveDetachedCart(persistentCartCacheRef.current, cacheKey,
        { ...localOrigin, pendingItems: localPendingItems.length ? localPendingItems : localOrigin.pendingItems });
      persistentCartCacheRef.current.delete(cacheKey);
      localPendingItems = [];
      writePersistentCartCache(persistentCartCacheRef.current);
    }
    if (selectedTableRef.current && String(selectedTableRef.current.id) === String(tableId)) {
      selectedTableRef.current = { ...selectedTableRef.current, ...snapshotTable };
      setSelectedTable(selectedTableRef.current);
    }
    const pendingReconciliation = reconcileDraftsWithSettledLines(
      localPendingItems,
      snapshot?.settled_client_lines,
    );
    const pendingItems = pendingReconciliation.remaining;
    const cachedExistingItems = getSnapshotCachedExistingItems(tableId);
    const authoritativeItems = normalizeCartSnapshotItems(snapshot);
    const effectiveHadSentItemAtStart =
      hadSentItemAtStart || pendingReconciliation.settled.length > 0;
    const reconciliation = buildTableCartReconciliationState({
      cachedExistingItems,
      serverItems: authoritativeItems,
      pendingItems,
      activeItemsCount: snapshot.active_items_count,
      hadSentItemAtStart: effectiveHadSentItemAtStart,
    });
    const nextExistingItems = reconciliation.items.filter(isItemActive);
    const selectedNow = selectedTableRef.current;
    const isVisible = Boolean(
      selectedNow &&
        !selectedNow.isDelivery &&
        String(selectedNow.id) === String(tableId),
    );

    if (reconciliation.anomalousItems.length > 0) {
      preserveDetachedCart(persistentCartCacheRef.current, cacheKey, {
        items: reconciliation.anomalousItems,
        pendingItems: [],
        occupancyCycle: localOrigin?.occupancyCycle,
        cacheContextId: localOrigin?.cacheContextId || "",
      }, "unidentified-sent-lines");
      writePersistentCartCache(persistentCartCacheRef.current);
      console.warn("⚠️ Cache panier envoyé sans order_item_id préservé", {
        tableId,
        count: reconciliation.anomalousItems.length,
      });
    }

    if (
      Number(snapshot.active_items_count) === 0 &&
      String(snapshot.table_status || "").toLowerCase() === "free" &&
      stageTableDraftRecovery({
        tableId,
        pendingItems,
        occupancyCycle: snapshot.occupancy_cycle,
        reason: pendingReconciliation.unresolved.length > 0
          ? "legacy-draft-after-reconnect"
          : "new-draft-after-reconnect",
      })
    ) {
      return true;
    }

    // Un snapshot vide n'est destructeur que si cette passe a commencé avec
    // la preuve d'au moins un article réellement envoyé. Une table simplement
    // ouverte (nombre de personnes, mais aucun article) reste donc intacte.
    if (
      Number(snapshot.active_items_count) === 0 &&
      !effectiveHadSentItemAtStart
    ) {
      return true;
    }

    const snapshotTableStatus = String(snapshot.table_status || "").toLowerCase();
    const canReleaseTable = canReleaseTableFromSnapshot({
      tableStatus: snapshotTableStatus,
      shouldClear: reconciliation.shouldClear,
      isVisible,
      visibleDoubleConfirmed: closeVisibleIfEmpty,
    });
    if (reconciliation.shouldClear && isVisible && !closeVisibleIfEmpty) {
      return false;
    }
    if (canReleaseTable) {
      tableCartSyncRef.current.grantClear(tableId, {
        isDelivery: false,
        generation: readToken.generation,
        reason: isVisible
          ? "reconnect-visible-double-empty"
          : "reconnect-background-empty",
      });
      if (!tableCartSyncRef.current.isCurrentRead(readToken)) return false;

      forgetKnownExistingItems(tableId, false, { preservePending: false });
      commitTableStatesCache((prev) => {
        const next = new Map(prev);
        next.delete(tableId);
        next.delete(String(tableId));
        return next;
      });
      optimisticTableStatusRef.current.delete(tableId);
      optimisticTableStatusRef.current.delete(String(tableId));
      setTables((prev) =>
        Array.isArray(prev)
          ? prev.map((table) =>
              String(table.id) === String(tableId)
                ? {
                    ...table,
                    status: "free",
                    customerCount: null,
                    customer_count: null,
                  }
                : table,
            )
          : prev,
      );

      if (isVisible) {
        selectedTableRef.current = null;
        currentOrderRef.current = { items: [], total: 0 };
        existingItemsForPaymentRef.current = [];
        setSelectedTable(null);
        setCurrentOrder({ items: [], total: 0 });
        setExistingItemsForPayment([]);
        setHasExistingOrders(false);
        setActiveClientTab(null);
        const runtime = socketRuntimeRef.current || {};
        setActiveTab(
          runtime.isBoutique
            ? runtime.isServerParticipationMode
              ? "profile"
              : "customers"
            : "tables",
        );
      }
      tableCartSyncRef.current.consumeClearGrant(tableId, {
        isDelivery: false,
      });
      return true;
    }

    const nextOrder = {
      items: pendingItems,
      total: computeItemsTotal(pendingItems),
    };
    if (
      reconciliation.shouldPurgeSentItems &&
      nextExistingItems.length === 0
    ) {
      // Un brouillon local peut rester, mais aucun ancien article envoyé ne doit
      // survivre dans la mémoire collante ou le cache persistant.
      forgetKnownExistingItems(tableId, false);
    }
    updateTableStateCache(
      tableId,
      {
        currentOrder: nextOrder,
        occupancyCycle: snapshot.occupancy_cycle,
        cacheContextId: snapshot.cache_context_id || "",
        existingItems: nextExistingItems,
        hasExistingOrders: nextExistingItems.length > 0,
      },
      { isDelivery: false },
    );

    if (isVisible) {
      currentOrderRef.current = nextOrder;
      existingItemsForPaymentRef.current = nextExistingItems;
      setCurrentOrder(nextOrder);
      setExistingItemsForPayment(nextExistingItems);
      setHasExistingOrders(nextExistingItems.length > 0);
      setCartRenderKey((prev) => prev + 1);
    }
    if (Number(snapshot.active_items_count) > 0) {
      markTableOccupiedInUi(tableId);
    } else if (snapshotTableStatus === "occupied") {
      // État transitoire sûr : le serveur peut avoir supprimé le dernier
      // article avant d'avoir encore libéré la table. On purge le fantôme,
      // mais on ne ferme jamais la table sur cette seule observation.
      markTableOccupiedInUi(tableId);
    }

    if (
      snapshotTableStatus === "occupied" &&
      snapshot.customer_count !== null &&
      snapshot.customer_count !== undefined
    ) {
      const serverCustomerCount = snapshot.customer_count;
      const addServerCountOnlyIfMissing = (table) =>
        addServerCustomerCountIfMissing(
          table,
          tableId,
          serverCustomerCount,
        );
      setTables((prev) =>
        Array.isArray(prev) ? prev.map(addServerCountOnlyIfMissing) : prev,
      );
      if (isVisible) {
        selectedTableRef.current = addServerCountOnlyIfMissing(
          selectedTableRef.current,
        );
        setSelectedTable((prev) => addServerCountOnlyIfMissing(prev));
      }
    }
    return true;
  };

  const scheduleCartCacheReconciliationRetry = (tableId) => {
    const key = String(tableId);
    if (cartCacheReconciliationRetryRef.current.has(key)) return;
    const timer = setTimeout(() => {
      cartCacheReconciliationRetryRef.current.delete(key);
      const generation = getTableCartGeneration(tableId, { isDelivery: false });
      const useDestructiveEvidence = Boolean(
        destructiveCartEvidenceRef.current.getValid(tableId, generation),
      );
      void reconcileCachedTable(tableId, {
        reason: "single-retry",
        allowRetry: false,
        useDestructiveEvidence,
      });
    }, 2000);
    cartCacheReconciliationRetryRef.current.set(key, timer);
  };

  const reconcileCachedTable = async (
    tableId,
    {
      reason = "cache-validation",
      allowRetry = true,
      useDestructiveEvidence = false,
    } = {},
  ) => {
    const selectedAtStart = selectedTableRef.current;
    const isVisible = Boolean(
      selectedAtStart &&
        !selectedAtStart.isDelivery &&
        String(selectedAtStart.id) === String(tableId),
    );
    if (orderActionInFlightRef.current) {
      if (allowRetry) scheduleCartCacheReconciliationRetry(tableId);
      return false;
    }

    const deadline = Date.now() + 2500;
    const visibleRunId = isVisible
      ? reconcilingVisibleRunSequenceRef.current + 1
      : null;
    if (isVisible) {
      reconcilingVisibleRunSequenceRef.current = visibleRunId;
      reconcilingVisibleTableIdRef.current = {
        tableId: String(tableId),
        runId: visibleRunId,
      };
      setReconcilingVisibleTableId(tableId);
    }

    try {
      const cachedExistingItemsAtStart =
        getSnapshotCachedExistingItems(tableId);
      const cachedHadSentItemAtStart = cachedExistingItemsAtStart.some(
        (item) => getExplicitOrderItemId(item) !== null,
      );
      const generationAtStart = getTableCartGeneration(tableId, {
        isDelivery: false,
      });
      const destructiveEvidence = useDestructiveEvidence
        ? destructiveCartEvidenceRef.current.getValid(
            tableId,
            generationAtStart,
          )
        : null;
      const hadSentItemAtStart =
        cachedHadSentItemAtStart || Boolean(destructiveEvidence);
      let readToken = tableCartSyncRef.current.beginRead(tableId, {
        isDelivery: false,
      });
      let snapshot = await fetchCartSnapshotForReconciliation(tableId, deadline);
      if (!tableCartSyncRef.current.isCurrentRead(readToken)) return false;

      const pendingItems = getSnapshotPendingItems(tableId);
      const firstReconciliation = buildTableCartReconciliationState({
        cachedExistingItems: cachedExistingItemsAtStart,
        serverItems: normalizeCartSnapshotItems(snapshot),
        pendingItems,
        activeItemsCount: snapshot.active_items_count,
        hadSentItemAtStart,
      });
      const requiresVisibleDoubleConfirmation =
        isVisible &&
        firstReconciliation.shouldClear;

      if (requiresVisibleDoubleConfirmation) {
        const remainingBeforeDelay = deadline - Date.now();
        if (remainingBeforeDelay <= 450) throw new Error("reconciliation-timeout");
        await new Promise((resolve) => setTimeout(resolve, 200));
        if (!tableCartSyncRef.current.isCurrentRead(readToken)) return false;
        readToken = tableCartSyncRef.current.beginRead(tableId, {
          isDelivery: false,
        });
        snapshot = await fetchCartSnapshotForReconciliation(tableId, deadline);
      }

      if (!tableCartSyncRef.current.isCurrentRead(readToken)) return false;
      const applied = applyCartSnapshotReconciliation({
        tableId,
        readToken,
        snapshot,
        hadSentItemAtStart,
        closeVisibleIfEmpty: requiresVisibleDoubleConfirmation,
      });
      if (applied) {
        if (destructiveEvidence) {
          destructiveCartEvidenceRef.current.consume(
            tableId,
            readToken.generation,
          );
        }
        const retryKey = String(tableId);
        const retryTimer = cartCacheReconciliationRetryRef.current.get(retryKey);
        if (retryTimer) clearTimeout(retryTimer);
        cartCacheReconciliationRetryRef.current.delete(retryKey);
      }
      return applied;
    } catch (error) {
      console.warn("⚠️ Réconciliation cache panier différée", {
        tableId,
        reason,
        error: error?.message || String(error),
      });
      if (allowRetry) scheduleCartCacheReconciliationRetry(tableId);
      return false;
    } finally {
      if (
        isVisible &&
        reconcilingVisibleTableIdRef.current?.runId === visibleRunId
      ) {
        reconcilingVisibleTableIdRef.current = null;
        setReconcilingVisibleTableId(null);
      }
    }
  };

  const collectCartCacheReconciliationCandidates = () => {
    const candidates = new Set();
    const selectedNow = selectedTableRef.current;
    if (selectedNow?.id && !selectedNow.isDelivery) {
      candidates.add(String(selectedNow.id));
    }
    const addIfSentItemsExist = (tableId, items, isDelivery = false) => {
      if (isDelivery || tableId === null || tableId === undefined) return;
      if ((items || []).length > 0) {
        candidates.add(String(tableId));
      }
    };

    persistentCartCacheRef.current.forEach((entry, key) => {
      if (!String(key).startsWith("table:")) return;
      addIfSentItemsExist(String(key).slice("table:".length), entry?.items, false);
    });
    lastExistingItemsRef.current.forEach((entry, key) => {
      addIfSentItemsExist(key, entry?.items, Boolean(entry?.isDelivery));
    });
    tableStatesCacheRef.current.forEach((state, key) => {
      addIfSentItemsExist(key, state?.existingItems, false);
    });

    return Array.from(candidates).filter(
      (tableId) => Number.isInteger(Number(tableId)) && Number(tableId) > 0,
    );
  };

  const runCartCacheReconciliationPass = async (reason = "socket-connect") => {
    const runState = cartCacheReconciliationRef.current;
    if (runState.inFlight) {
      runState.pending = true;
      runState.pendingReason = reason;
      return runState.promise;
    }
    runState.inFlight = true;
    runState.promise = (async () => {
      let passReason = reason;
      try {
        do {
          runState.pending = false;
          runState.pendingReason = null;
          const candidates = collectCartCacheReconciliationCandidates();
          const selectedId = selectedTableRef.current?.isDelivery
            ? null
            : selectedTableRef.current?.id;
          const visibleCandidate = candidates.find(
            (tableId) =>
              selectedId !== null &&
              selectedId !== undefined &&
              String(tableId) === String(selectedId),
          );
          if (visibleCandidate) {
            await reconcileCachedTable(visibleCandidate, { reason: passReason });
          }

          const background = candidates.filter(
            (tableId) => String(tableId) !== String(visibleCandidate ?? ""),
          );
          let cursor = 0;
          const worker = async () => {
            while (cursor < background.length) {
              const index = cursor;
              cursor += 1;
              const jitter = 150 + Math.floor(Math.random() * 601);
              await new Promise((resolve) => setTimeout(resolve, jitter));
              await reconcileCachedTable(background[index], {
                reason: passReason,
              });
            }
          };
          await Promise.all(
            Array.from({ length: Math.min(3, background.length) }, () => worker()),
          );
          passReason = runState.pendingReason || "coalesced-reconciliation";
        } while (runState.pending);
      } finally {
        runState.inFlight = false;
        runState.pending = false;
        runState.pendingReason = null;
        runState.promise = null;
      }
      return true;
    })();
    return runState.promise;
  };
  runCartCacheReconciliationPassRef.current = runCartCacheReconciliationPass;

  // Stubs de fonctions manquantes (sécurisées) pour éviter no-undef et garder l'UI fonctionnelle
  const initializeConnections = async () => {
    try {
      // Charger les données de base au démarrage
      await loadBasicData();

      // Initialiser la connexion WebSocket si pas déjà fait
      if (!socketRef.current) {
        const newSocket = io();
        socketRef.current = newSocket;
        setSocket(newSocket);

        const handleSocketConnect = () => {
          const reason = cartCacheInitialPassDoneRef.current
            ? "socket-reconnect"
            : "initial-hydration";
          cartCacheInitialPassDoneRef.current = true;
          scheduleLoadTables(reason, { forceUnpaidRefresh: true });
          void runCartCacheReconciliationPass(reason);
          void loadOpenCounterOrdersRef.current?.();
          void loadFloorPlan();
        };
        newSocket.on("connect", handleSocketConnect);
        newSocket.on("counterOrderChanged", () => {
          void loadOpenCounterOrdersRef.current?.();
        });
        if (newSocket.connected) handleSocketConnect();

        // tableFreed est une invalidation: le serveur est relu avant tout nettoyage.
        newSocket.on("tableFreed", ({
          tableId,
          table: freedTable = null,
          occupancyCycle = null,
          settledClientLines = [],
          clientGeneration = null,
          client_generation: legacyClientGeneration = null,
        }) => {
          if (tableId === null || tableId === undefined || tableId === "") return;
          const rawEventGeneration = clientGeneration ?? legacyClientGeneration;
          const validationGeneration =
            tableCartSyncRef.current.beginFreeValidation(tableId, {
              isDelivery: false,
              eventGeneration: rawEventGeneration,
            });

          const verifyFreedTable = async (expectedGeneration, attempt = 0) => {
            let activeItemsCount;
            try {
              const response = await fetchWithTimeout(
                `${API_BASE}/tables/${tableId}/occupancy`,
                {},
                2500,
              );
              if (!response.ok) return;
              const occupancy = await response.json().catch(() => null);
              activeItemsCount = Number(
                occupancy?.active_items_count ??
                  occupancy?.activeItemsCount ??
                  occupancy?.active_count,
              );
              if (!Number.isFinite(activeItemsCount)) return;
            } catch (_) {
              return;
            }

            if (
              getTableCartGeneration(tableId, { isDelivery: false }) !==
              expectedGeneration
            ) {
              if (attempt < 2) {
                return verifyFreedTable(
                  getTableCartGeneration(tableId, { isDelivery: false }),
                  attempt + 1,
                );
              }
              return;
            }

            if (activeItemsCount > 0) {
              markTableOccupiedInUi(tableId);
              scheduleTableCartRefresh({
                tableId,
                reason: "table-freed-still-occupied",
              });
              return;
            }

            if (orderActionInFlightRef.current && attempt < 4) {
              window.setTimeout(
                () => void verifyFreedTable(expectedGeneration, attempt + 1),
                150,
              );
              return;
            }

            const pendingReconciliation = reconcileDraftsWithSettledLines(
              getSnapshotPendingItems(tableId),
              settledClientLines,
            );
            if (
              stageTableDraftRecovery({
                tableId,
                pendingItems: pendingReconciliation.remaining,
                occupancyCycle:
                  Number(occupancyCycle) ||
                  resolveTableOccupancyCycle(freedTable),
                reason: pendingReconciliation.unresolved.length > 0
                  ? "legacy-draft-after-settlement"
                  : "new-draft-after-settlement",
              })
            ) {
              tableCartSyncRef.current.revokeClear(tableId, {
                isDelivery: false,
              });
              return;
            }

            tableCartSyncRef.current.grantClear(tableId, {
              isDelivery: false,
              generation: expectedGeneration,
              reason: "table-freed-confirmed-empty",
            });
            const items = await loadTableItemsForPayment(tableId, false);
            const generationChanged =
              getTableCartGeneration(tableId, { isDelivery: false }) !==
              expectedGeneration;
            if (generationChanged) {
              if (attempt < 2) {
                return verifyFreedTable(
                  getTableCartGeneration(tableId, { isDelivery: false }),
                  attempt + 1,
                );
              }
              return;
            }
            if (
              (items || []).filter(isItemActive).length > 0 ||
              orderActionInFlightRef.current
            ) {
              if (orderActionInFlightRef.current && attempt < 4) {
                window.setTimeout(
                  () => void verifyFreedTable(expectedGeneration, attempt + 1),
                  150,
                );
              } else {
                markTableOccupiedInUi(tableId);
              }
              return;
            }

            forgetKnownExistingItems(tableId, false, {
              preservePending: false,
            });
            optimisticTableStatusRef.current.delete(tableId);
            optimisticTableStatusRef.current.delete(String(tableId));
            commitTableStatesCache((prev) => {
              const next = new Map(prev);
              next.delete(tableId);
              next.delete(String(tableId));
              return next;
            });
            setTables((prev) =>
              Array.isArray(prev)
                ? prev.map((table) =>
                    String(table.id) === String(tableId)
                      ? {
                          ...table,
                          status: "free",
                          customer_count: null,
                          customerCount: null,
                          management_type: null,
                          managed_by_user_id: null,
                          managed_by_first_name: null,
                          managed_by_role: null,
                          active_owner_user_id: null,
                          active_owner_role: null,
                          active_owner_name: null,
                          active_owner_first_name: null,
                        }
                      : table,
                  )
                : prev,
            );
            metadataRefreshCoordinatorRef.current.enqueue("metadata", tableId);

            const selectedNow = selectedTableRef.current;
            if (
              selectedNow &&
              !selectedNow.isDelivery &&
              String(selectedNow.id) === String(tableId)
            ) {
              const runtime = socketRuntimeRef.current || {};
              selectedTableRef.current = null;
              setSelectedTable(null);
              setCurrentOrder({ items: [], total: 0 });
              setExistingItemsForPayment([]);
              setHasExistingOrders(false);
              setActiveClientTab(null);
              setActiveTab(
                runtime.isBoutique
                  ? runtime.isServerParticipationMode
                    ? "profile"
                    : "customers"
                  : "tables",
              );
            }
          };
          void verifyFreedTable(validationGeneration);
        });

        const handleTableCartChanged = (event = {}) => {
          const runtime = socketRuntimeRef.current || {};
          const context = resolveRealtimeTableContext(event, {
            selectedTable: selectedTableRef.current,
            tables: runtime.tables || [],
            clientTabs: runtime.clientTabs || [],
          });
          const guard = getRecentlyPaidGuard(event.tableId);
          if (guard) {
            if (shouldInvalidateRecentPaymentGuard(guard, event)) {
              clearRecentlyPaidGuard(event.tableId);
              forcedFreeTablesRef.current.delete(event.tableId);
              forcedFreeTablesRef.current.delete(String(event.tableId));
              if (context.known && context.isDelivery === false) {
                markTableOccupiedInUi(event.tableId);
              }
              cashDebugLog("Nouvelle mutation après paiement: garde levée", {
                tableId: event.tableId,
                orderId: event.orderId ?? null,
                reason: event.reason,
              });
            }
          }
          scheduleTableCartRefresh({
            tableId: event.tableId,
            reason: event.reason || "table-cart-changed",
            orderId: event.orderId ?? null,
            orderItemId: event.orderItemId ?? event.itemId ?? null,
            isDelivery:
              context.known && typeof context.isDelivery === "boolean"
                ? context.isDelivery
                : undefined,
            contextKnown: context.known,
            targetTableId: context.targetTableId,
          });
        };
        newSocket.on("tableCartChanged", handleTableCartChanged);
        const handleOrderCourseFired = (event = {}) => {
          const selectedNow = selectedTableRef.current;
          if (
            !selectedNow ||
            selectedNow.isDelivery ||
            String(selectedNow.id) !== String(event.tableId)
          ) {
            return;
          }
          const courseNumber = Math.max(1, Number(event.courseNumber) || 1);
          const authoritativeCourse = event.course || {
            id: event.courseId,
            order_id: event.orderId,
            course_number: courseNumber,
            status: event.status || "fired",
            production_dispatch_status:
              event.productionDispatchStatus || "sent",
            fired_at: event.firedAt || null,
          };
          const nextCourses = upsertAuthoritativeCourseState(
            orderCoursesRef.current,
            authoritativeCourse,
          );
          orderCoursesRef.current = nextCourses;
          setOrderCourses(nextCourses);
          setActiveCourseNumber((previous) =>
            resolveDisplayedCourseNumber(nextCourses, previous),
          );
          const actionKey = `${event.orderId}:${courseNumber}`;
          setCourseDispatchStates((previous) => {
            if (!previous[actionKey]) return previous;
            const next = { ...previous };
            delete next[actionKey];
            return next;
          });
          courseFireRequestIdsRef.current.delete(actionKey);
          scheduleTableCartRefresh({
            tableId: event.tableId,
            orderId: event.orderId,
            reason: "course-fired",
          });
        };
        newSocket.on("order-course-fired", handleOrderCourseFired);
        // Compatibilité avec un serveur qui n'émettrait que l'ancien événement.
        newSocket.on("orderCreated", (event = {}) => {
          if (event.tableId !== null && event.tableId !== undefined) {
            handleTableCartChanged({ ...event, reason: "order-created-legacy" });
          }
        });

        // Écouter les autres événements WebSocket existants
        newSocket.on("paymentCreated", ({ orderId, orderIds, tableId, paymentId }) => {
          cashDebugLog(`💳 WebSocket: Paiement créé pour commande ${orderId}`);
          if (tableId !== null && tableId !== undefined) {
            markRecentlyPaidGuard(
              tableId,
              Array.isArray(orderIds) && orderIds.length ? orderIds : [orderId],
            );
          } else {
            void loadOpenCounterOrdersRef.current?.();
          }
          // Recharger les tables après un court délai
          scheduleLoadTables("ws-payment-created", {
            delayMs: 500,
            forceUnpaidRefresh: true,
          });
        });

        // Mise à jour de table (ex: maintenance, réservation, etc.)
        newSocket.on("tableUpdated", ({ tableId, table }) => {
          try {
            const nextStatus = table?.status;
            if (nextStatus === "maintenance") {
              setTables((prev) =>
                Array.isArray(prev) ? prev.filter((t) => t.id !== tableId) : prev,
              );
              const protection = getTableClearProtection(tableId, { isDelivery: false });
              if (protection.shouldProtect) {
                cashDebugLog("🛡�? tableUpdated maintenance différé: panier local protégé", {
                  tableId,
                  activeLocalCount: protection.activeLocalCount,
                });
                setTimeout(async () => {
                  const confirmedEmpty = await confirmTableEmptyForUiClear(tableId);
                  if (confirmedEmpty) {
                    const selectedNow = selectedTableRef.current;
                    if (selectedNow && String(selectedNow.id) === String(tableId)) {
                      setSelectedTable(null);
                      setCurrentOrder({ items: [], total: 0 });
                      setExistingItemsForPayment([]);
                      setHasExistingOrders(false);
                    }
                  }
                }, 1000);
              } else {
                const selectedNow = selectedTableRef.current;
                if (selectedNow && String(selectedNow.id) === String(tableId)) {
                  setSelectedTable(null);
                  setCurrentOrder({ items: [], total: 0 });
                  setExistingItemsForPayment([]);
                  setHasExistingOrders(false);
                }
              }
              return;
            }

            setTables((prev) => {
              if (!Array.isArray(prev)) return prev;
              const exists = prev.some((t) => t.id === tableId);
              if (!exists) return prev;
              return prev.map((t) => {
                if (t.id !== tableId) return t;
                const mergedTable = { ...t, ...(table || {}) };
                const protection = getTableClearProtection(tableId, { isDelivery: false });
                if (mergedTable.status === "free" && protection.activeLocalCount > 0) {
                  mergedTable.status = "occupied";
                }
                const rawCount = mergedTable.customerCount ?? mergedTable.customer_count;
                const parsedCount =
                  typeof rawCount === "number"
                    ? rawCount
                    : rawCount !== undefined && rawCount !== null
                      ? Number(rawCount)
                      : null;
                return {
                  ...mergedTable,
                  customerCount:
                    mergedTable.status === "occupied" &&
                    parsedCount &&
                    !Number.isNaN(parsedCount)
                      ? parsedCount
                      : null,
                };
              });
            });
          } catch (error) {
            console.error("�?� WebSocket tableUpdated error:", error);
          }
        });

        newSocket.on("tableReservationUpdated", (reservation = {}) => {
          const tableId = Number(reservation.tableId);
          if (!Number.isInteger(tableId) || tableId <= 0) return;
          setTables((prev) =>
            Array.isArray(prev)
              ? prev.map((table) =>
                  Number(table.id) === tableId
                    ? {
                        ...table,
                        reservation_user_id: reservation.released
                          ? null
                          : reservation.userId || null,
                        reservation_role: reservation.released
                          ? null
                          : reservation.role || null,
                        reservation_device_id: reservation.released
                          ? null
                          : reservation.deviceId || null,
                        reservation_name: reservation.released
                          ? null
                          : reservation.displayName || null,
                      }
                    : table,
                )
              : prev,
          );
        });

        // �? Réglages KDS mis à jour (cuisine/bar/double écran)
        newSocket.on("kdsSettingsUpdated", (settings) => {
          try {
            const allowed = Boolean(settings?.dual_screen_enabled);
            setIsDualScreenAllowed(allowed);
            if (
              !allowed &&
              Boolean(socketRuntimeRef.current?.isDualScreenActive)
            ) {
              closeDualScreenWindow();
              setIsDualScreenActive(false);
              localStorage.setItem("moo_dual_screen_active", "false");
            }
          } catch (e) {
            console.warn("⚠�? kdsSettingsUpdated handler:", e);
          }
        });

        newSocket.on("online-order-updated", () => {
          if (!socketRuntimeRef.current?.onlineMenuOrderingEnabled) return;
          void refreshOnlineOrdersQuick();
        });

        newSocket.on("kds-action-requested", (event = {}) => {
          if (String(event?.action || "").toLowerCase() !== "reject") return;
          const items = Array.isArray(event.items) ? event.items : [];
          setKdsActionNotice({
            ...event,
            items,
            receivedAt: Date.now(),
          });
          if (event.tableId !== null && event.tableId !== undefined) {
            scheduleTableCartRefresh({
              tableId: event.tableId,
              orderId: event.orderId ?? null,
              reason: "kds-item-rejected",
              contextKnown: true,
              isDelivery: false,
            });
          }
        });
      }
    } catch (e) {
      console.error("initializeConnections error:", e);
    }
  };

  const loadTables = async (options = {}) => {
    const requestSequence = loadTablesRequestSequenceRef.current + 1;
    loadTablesRequestSequenceRef.current = requestSequence;
    const forceUnpaidRefresh = options?.forceUnpaidRefresh === true;
    try {
      const response = await fetchWithTimeout(`${API_BASE}/tables`);
      if (response.ok) {
        const data = await response.json();
        // 🔎 Snapshot des commandes impayées pour surclasser les statuts "free" trop agressifs
        const unpaidTableIds = new Set();
        const apiOccupiedTableIds = new Set();
        const addApiOccupiedTableId = (value) => {
          if (value === null || value === undefined) return;
          const num = Number(value);
          if (!Number.isNaN(num)) apiOccupiedTableIds.add(num);
          apiOccupiedTableIds.add(String(value));
        };
        const addUnpaidTableId = (value) => {
          if (value === null || value === undefined) return;
          const num = Number(value);
          if (!Number.isNaN(num)) unpaidTableIds.add(num);
          unpaidTableIds.add(String(value));
        };
        data.forEach((table) => {
          if (!table) return;
          const activeOrdersCount = Number(
            table.active_orders ?? table.activeOrders ?? 0,
          );
          const hasActiveOrders =
            Number.isFinite(activeOrdersCount) && activeOrdersCount > 0;
          if (table.status === "occupied" || hasActiveOrders) {
            addApiOccupiedTableId(table.id);
          }
        });
        const snapshot =
          unpaidTableSnapshotRef.current || { ts: 0, ids: new Set() };
        let nextUnpaidSnapshot = null;
        const snapshotAge = Date.now() - Number(snapshot.ts || 0);
        const canUseCachedSnapshot =
          !forceUnpaidRefresh &&
          snapshot.ids instanceof Set &&
          snapshotAge >= 0 &&
          snapshotAge < UNPAID_SNAPSHOT_TTL_MS;

        if (canUseCachedSnapshot) {
          snapshot.ids.forEach((value) => addUnpaidTableId(value));
        } else {
          try {
            const unpaidRes = await fetchWithTimeout(`${API_BASE}/orders/unpaid`);
            if (unpaidRes.ok) {
              const unpaidOrders = await unpaidRes.json();
              unpaidOrders
                .filter((o) => o && o.table_id != null)
                .forEach((o) => {
                  const hasActiveCountField =
                    Object.prototype.hasOwnProperty.call(
                      o,
                      "active_items_count",
                    ) ||
                    Object.prototype.hasOwnProperty.call(
                      o,
                      "activeItemsCount",
                    ) ||
                    Object.prototype.hasOwnProperty.call(o, "active_items");
                  const activeCount = Number(
                    o.active_items_count ??
                      o.activeItemsCount ??
                      o.active_items ??
                      0,
                  );
                  const hasActiveFromCount =
                    hasActiveCountField &&
                    Number.isFinite(activeCount) &&
                    activeCount > 0;
                  const hasItemsField = Array.isArray(o.items);
                  const hasActiveFromItems = hasItemsField
                    ? (o.items || []).some(
                        (it) => it && it.status !== "cancelled",
                      )
                    : false;
                  const hasUnknownItems =
                    !hasActiveCountField && !hasItemsField;
                  const hasApiOccupiedFlag =
                    apiOccupiedTableIds.has(o.table_id) ||
                    apiOccupiedTableIds.has(String(o.table_id));
                  if (
                    hasActiveFromCount ||
                    hasActiveFromItems ||
                    (hasUnknownItems && hasApiOccupiedFlag)
                  ) {
                    addUnpaidTableId(o.table_id);
                  }
                });
              nextUnpaidSnapshot = {
                ts: Date.now(),
                ids: new Set(unpaidTableIds),
              };
              cashDebugLogLazy(() => [
                "🧠 Snapshot impayés rafraîchi",
                { size: unpaidTableIds.size, force: forceUnpaidRefresh },
              ]);
            } else if (snapshot.ids instanceof Set) {
              snapshot.ids.forEach((value) => addUnpaidTableId(value));
            }
          } catch (unpaidErr) {
            console.warn("⚠�? Snapshot commandes impayées indisponible:", unpaidErr);
            if (snapshot.ids instanceof Set) {
              snapshot.ids.forEach((value) => addUnpaidTableId(value));
            }
          }
        }
        // Une réponse plus ancienne ne doit jamais écraser une vue obtenue
        // après un paiement, une fermeture ou une reprise sur un autre poste.
        if (requestSequence !== loadTablesRequestSequenceRef.current) {
          return false;
        }
        if (nextUnpaidSnapshot) {
          unpaidTableSnapshotRef.current = nextUnpaidSnapshot;
        }
        // Fusionner avec les statuts optimistes et le cache d'items
        const now = Date.now();
        const tablesToClearCache = new Set();
        const merged = data.map((t) => {
          const optimistic = optimisticTableStatusRef.current.get(t.id);
          const tableKey = String(t.id);
          const recentlyPaid =
            recentlyPaidTablesRef.current.has(t.id) ||
            recentlyPaidTablesRef.current.has(tableKey);
          const forceFree =
            forcedFreeTablesRef.current.has(t.id) ||
            forcedFreeTablesRef.current.has(tableKey);
          let status = t.status;
          // Une table en maintenance doit rester en maintenance.
          if (status !== "maintenance") {
            const localCartSignal = getActiveLocalCartSignalForTable(t.id, {
              isDelivery: Boolean(t.isDelivery),
            });
            const isDraftRecovery =
              draftRecoveryTableIdsRef.current.has(tableKey);
            const hasCanonicalActiveCount =
              Object.prototype.hasOwnProperty.call(t, "active_items_count") ||
              Object.prototype.hasOwnProperty.call(t, "activeItemsCount");
            const canonicalActiveCount = Number(
              t.active_items_count ?? t.activeItemsCount ?? 0,
            );
            const hasCanonicalActiveItems =
              hasCanonicalActiveCount &&
              Number.isFinite(canonicalActiveCount) &&
              canonicalActiveCount > 0;
            const hasRecentLocalMutation = Boolean(
              orderActionInFlightRef.current ||
                (optimistic &&
                  optimistic.status === "occupied" &&
                  now - Number(optimistic.ts || 0) < 20000),
            );
            // `/api/tables` fournit désormais le compteur canonique. Un ancien
            // snapshot local d'impayés ou des articles déjà envoyés en cache ne
            // doivent jamais surclasser une table que le serveur confirme vide.
            const hasUnpaidServerOrder = hasCanonicalActiveCount
              ? hasCanonicalActiveItems
              : unpaidTableIds.has(t.id) || unpaidTableIds.has(String(t.id));
            const hasProtectedLocalCart =
              !isDraftRecovery &&
              resolveLocalCartOccupancyCount(t, localCartSignal) > 0;

            if (
              !isDraftRecovery &&
              shouldClearCanonicalEmptyLocalCart({
                table: t,
                signal: localCartSignal,
                hasRecentLocalMutation,
              })
            ) {
              tablesToClearCache.add(String(t.id));
            }

            if (hasProtectedLocalCart || hasUnpaidServerOrder) {
              status = "occupied";
            } else if (
              optimistic &&
              optimistic.status === "occupied" &&
              now - optimistic.ts < 20000
            ) {
              status = "occupied";
            } else if (recentlyPaid || forceFree) {
              status = "free";
            }
          }
          const rawCount =
            t.customerCount ??
            t.customer_count ??
            (status === "occupied" ? tableCustomerCounts[t.id] : null);
          const parsedCount =
            typeof rawCount === "number"
              ? rawCount
              : rawCount !== undefined && rawCount !== null
                ? Number(rawCount)
                : null;
          return {
            ...t,
            status,
            customerCount:
              status === "occupied" && parsedCount && !Number.isNaN(parsedCount)
                ? parsedCount
                : null,
          };
        });
        // ✅ Exclure la T99 de la liste des tables
        const filtered = merged.filter(
          (t) =>
            t?.number !== 99 &&
            t?.name !== "T99" &&
            t?.id !== 99 &&
            t?.status !== "maintenance",
        );
        setTables((prev) =>
          areTableListsEquivalent(prev, filtered) ? prev : filtered,
        );
        writeTablesCache(filtered);

        if (isBoutique) {
          const boutiqueCandidate =
            merged.find(
              (t) =>
                t?.number === 999 ||
                t?.name === "T99" ||
                t?.id === 999,
            ) ||
            filtered[0] ||
            merged[0] ||
            null;
          if (boutiqueCandidate) {
            setSelectedTable((prev) =>
              prev && String(prev.id) === String(boutiqueCandidate.id)
                ? prev
                : boutiqueCandidate,
            );
          }
        }

        // 🚫 Ne pas autoselectionner une livraison si des tables sont occupées
        const hasOccupiedTables =
          filtered.some((t) => t && t.status === "occupied") ||
          unpaidTableIds.size > 0;
        if (hasOccupiedTables && !isBoutique) {
          setAutoSelectDeliveryTab(false);
          if (activeTab !== "online") {
            setActiveTab("tables");
          }
        }

        if (tablesToClearCache.size > 0) {
          tablesToClearCache.forEach((id) => {
            forgetKnownExistingItems(id, false, { preservePending: false });
            optimisticTableStatusRef.current.delete(id);
            const numericId = Number(id);
            if (Number.isFinite(numericId)) {
              optimisticTableStatusRef.current.delete(numericId);
            }
          });
          commitTableStatesCache((prev) => {
            const next = new Map(prev);
            tablesToClearCache.forEach((id) => {
              const numericId = Number(id);
              next.delete(id);
              next.delete(String(id));
              if (Number.isFinite(numericId)) next.delete(numericId);
            });
            return next;
          });

          const selectedNow = selectedTableRef.current;
          if (
            selectedNow &&
            !selectedNow.isDelivery &&
            tablesToClearCache.has(String(selectedNow.id)) &&
            !orderActionInFlightRef.current
          ) {
            const runtime = socketRuntimeRef.current || {};
            selectedTableRef.current = null;
            currentOrderRef.current = { items: [], total: 0 };
            existingItemsForPaymentRef.current = [];
            setSelectedTable(null);
            setCurrentOrder({ items: [], total: 0 });
            setExistingItemsForPayment([]);
            setHasExistingOrders(false);
            setActiveClientTab(null);
            setActiveTab(
              runtime.isBoutique
                ? runtime.isServerParticipationMode
                  ? "profile"
                  : "customers"
                : "tables",
            );
          }
        }

	        // Si la table sélectionnée passe en maintenance, la masquer et nettoyer l'UI caisse.
	        try {
	          if (
	            selectedTable &&
	            !selectedTable?.isDelivery &&
	            !filtered.some((tb) => tb?.id === selectedTable.id)
	          ) {
	            const selectedFromApi = merged.find(
	              (tb) => tb?.id === selectedTable.id,
            );
            if (selectedFromApi?.status === "maintenance" || !selectedFromApi) {
              setSelectedTable(null);
              setCurrentOrder({ items: [], total: 0 });
              setExistingItemsForPayment([]);
              setHasExistingOrders(false);
            }
          }
        } catch (_) {}
        try {
          filtered.forEach((t) => {
            if (t.status !== "free") {
              forcedFreeTablesRef.current.delete(t.id);
            }
          });
        } catch (_) {}

        // ✅ Mettre à jour le mapping des nombres de clients depuis l'API
        setTableCustomerCounts((prev) => {
          const next = { ...prev };
          filtered.forEach((t) => {
            if (t.status === "occupied") {
              const apiCount = Number(t.customerCount || t.customer_count);
              if (!Number.isNaN(apiCount) && apiCount > 0) {
                next[t.id] = apiCount;
              }
            } else {
              delete next[t.id];
            }
          });
          return next;
        });

        return hasOccupiedTables;
      }
    } catch (e) {
      console.error("loadTables error:", e);
    }
    return false;
  };

  loadTablesRef.current = loadTables;

  const scheduleLoadTables = useCallback(
    (reason = "unknown", options = {}) => {
      const delayMs = Number(options?.delayMs) || 0;
      const requestForceUnpaidRefresh = options?.forceUnpaidRefresh === true;
      if (requestForceUnpaidRefresh) {
        loadTablesForceRefreshPendingRef.current = true;
      }

      const run = async () => {
        if (loadTablesInFlightRef.current) {
          loadTablesPendingRef.current = true;
          return;
        }
        const forceUnpaidRefresh = loadTablesForceRefreshPendingRef.current;
        loadTablesForceRefreshPendingRef.current = false;
        loadTablesInFlightRef.current = true;
        loadTablesPendingRef.current = false;
        try {
          await loadTablesRef.current({ forceUnpaidRefresh });
          lastLoadTablesRunRef.current = Date.now();
        } catch (error) {
          console.error(`scheduleLoadTables failed (${reason}):`, error);
        } finally {
          loadTablesInFlightRef.current = false;
          if (loadTablesPendingRef.current) {
            loadTablesPendingRef.current = false;
            const nextDelay = Math.max(
              0,
              LOAD_TABLES_COOLDOWN_MS - (Date.now() - lastLoadTablesRunRef.current),
            );
            if (loadTablesScheduleTimerRef.current) {
              clearTimeout(loadTablesScheduleTimerRef.current);
            }
            loadTablesScheduleTimerRef.current = setTimeout(
              () => void run(),
              nextDelay,
            );
          }
        }
      };

      const elapsed = Date.now() - lastLoadTablesRunRef.current;
      const cooldownDelay = Math.max(0, LOAD_TABLES_COOLDOWN_MS - elapsed);
      const effectiveDelay = Math.max(delayMs, cooldownDelay);
      if (loadTablesScheduleTimerRef.current) {
        clearTimeout(loadTablesScheduleTimerRef.current);
      }
      loadTablesScheduleTimerRef.current = setTimeout(
        () => void run(),
        effectiveDelay,
      );
    },
    [LOAD_TABLES_COOLDOWN_MS],
  );

  useEffect(
    () => () => {
      if (loadTablesScheduleTimerRef.current) {
        clearTimeout(loadTablesScheduleTimerRef.current);
        loadTablesScheduleTimerRef.current = null;
      }
      loadTablesForceRefreshPendingRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!isServerParticipationMode) return undefined;

    const refreshAuthoritativeTables = (reason) => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }
      scheduleLoadTables(reason, { forceUnpaidRefresh: true });
      // `/api/tables` rafraîchit les métadonnées, mais ne doit pas laisser un
      // ancien article envoyé du cache surclasser indéfiniment un snapshot vide.
      // La passe canonique préserve les vrais brouillons et purge uniquement
      // les articles envoyés que le serveur confirme absents.
      void runCartCacheReconciliationPassRef.current(reason);
    };
    const handleFocus = () => refreshAuthoritativeTables("server-window-focus");
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        refreshAuthoritativeTables("server-screen-wakeup");
      }
    };
    const convergenceTimer = window.setInterval(
      () => refreshAuthoritativeTables("server-periodic-convergence"),
      10000,
    );
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(convergenceTimer);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [isServerParticipationMode, scheduleLoadTables]);

  useEffect(() => {
    if (!isServerParticipationMode) return undefined;
    const timer = window.setInterval(() => {
      const now = Date.now();
      const expiredTableIds = new Set();
      optimisticTableStatusRef.current.forEach((entry, tableId) => {
        if (
          entry?.status === "occupied" &&
          Number.isFinite(Number(entry?.ts)) &&
          now - Number(entry.ts) >= 20000
        ) {
          expiredTableIds.add(String(tableId));
        }
      });
      expiredTableIds.forEach((tableId) => {
        optimisticTableStatusRef.current.delete(tableId);
        const numericId = Number(tableId);
        if (Number.isFinite(numericId)) {
          optimisticTableStatusRef.current.delete(numericId);
        }
        metadataRefreshCoordinatorRef.current.enqueue("metadata", tableId);
      });
      if (expiredTableIds.size > 0) {
        void runCartCacheReconciliationPassRef.current(
          "server-optimistic-status-expired",
        );
      }
    }, 2500);
    return () => window.clearInterval(timer);
  }, [isServerParticipationMode]);

  const mergeTargetedTableMetadata = (tableId, access = {}) => {
    const applyMetadata = (table) => {
      if (!table || String(table.id) !== String(tableId)) return table;
      const protection = getTableClearProtection(tableId, {
        isDelivery: false,
      });
      const serverStatus = access.table_status || table.status;
      const status =
        serverStatus === "free" && protection.activeLocalCount > 0
          ? "occupied"
          : serverStatus;
      return {
        ...table,
        status,
        management_type: access.management_type ?? null,
        managed_by_user_id: access.managed_by_user_id ?? null,
        managed_by_first_name: access.managed_by_first_name ?? null,
        managed_by_role: access.managed_by_role ?? null,
        active_owner_user_id: access.managed_by_user_id ?? null,
        active_owner_first_name: access.managed_by_first_name ?? null,
        active_owner_role: access.managed_by_role ?? null,
        active_owner_name: access.managed_by_first_name ?? null,
        active_items_count: Number(access.active_items_count || 0),
        ...(Object.prototype.hasOwnProperty.call(access, "customer_count")
          ? {
              customer_count: access.customer_count,
              customerCount: access.customer_count,
            }
          : {}),
      };
    };

    setTables((previous) =>
      Array.isArray(previous) ? previous.map(applyMetadata) : previous,
    );
    const selectedNow = selectedTableRef.current;
    if (
      selectedNow &&
      !selectedNow.isDelivery &&
      String(selectedNow.id) === String(tableId)
    ) {
      const nextSelected = applyMetadata(selectedNow);
      selectedTableRef.current = nextSelected;
      setSelectedTable(nextSelected);
    }
  };

  const refreshTargetedTableMetadata = async (tableId) => {
    const response = await fetchWithTimeout(
      `${API_BASE}/tables/${tableId}/access`,
      {},
      2500,
    );
    if (!response.ok) {
      throw new Error(`table-access-${response.status}`);
    }
    const access = await response.json().catch(() => null);
    if (!access || String(access.table_id) !== String(tableId)) {
      throw new Error("table-access-invalid");
    }
    mergeTargetedTableMetadata(tableId, access);
  };

  const flushMetadataRefresh = async (tableIds = []) => {
    const decision = chooseMetadataRefreshMode(tableIds, 8);
    if (decision.mode === "global") {
      scheduleLoadTables("ws-metadata-batch-global", {
        delayMs: 0,
        forceUnpaidRefresh: true,
      });
      return;
    }

    const targets = decision.tableIds;
    let cursor = 0;
    let failed = false;
    const worker = async () => {
      while (cursor < targets.length) {
        const index = cursor;
        cursor += 1;
        try {
          await refreshTargetedTableMetadata(targets[index]);
        } catch (_) {
          failed = true;
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(3, targets.length) }, () => worker()),
    );
    if (failed) {
      scheduleLoadTables("ws-metadata-targeted-fallback", {
        delayMs: 0,
        forceUnpaidRefresh: true,
      });
    }
  };

  const flushRealtimeTableIntent = async (_scopeKey, intent = {}) => {
    if (!intent.contextKnown || intent.isDelivery === null) return;
    const tableId = intent.tableId;
    const targetTableId = intent.targetTableId ?? tableId;
    const isDelivery = Boolean(intent.isDelivery);
    const selectedNow = selectedTableRef.current;
    const isVisible =
      selectedNow &&
      Boolean(selectedNow.isDelivery) === isDelivery &&
      String(selectedNow.id) === String(targetTableId);
    const reasons = (intent.reasons || []).join("+") || "cart-mutated";

    if (intent.hasDestructive && !isDelivery) {
      const currentGeneration = getTableCartGeneration(tableId, {
        isDelivery: false,
      });
      const evidence =
        destructiveCartEvidenceRef.current.getValid(
          tableId,
          currentGeneration,
        );
      if (evidence) {
        await reconcileCachedTable(tableId, {
          reason: `ws-${reasons}`,
          useDestructiveEvidence: true,
        });
        return;
      }
    }

    if (intent.needsFullRefresh && isVisible) {
      await loadTableItemsForPaymentRef.current(
        targetTableId,
        isDelivery,
      ).catch((error) => {
        console.warn("Synchronisation panier distante échouée:", error);
        return [];
      });
      return;
    }

    if (intent.needsIntegrityCheck && isVisible && !isDelivery) {
      await validateVisibleTableCartIntegrity(targetTableId);
    }
  };

  const loadPendingDeliveries = async (options = {}) => {
    const { allowAutoSelect, forceNeutral = false } = options || {};
    const shouldAutoSelect =
      typeof allowAutoSelect === "boolean"
        ? allowAutoSelect
        : autoSelectDeliveryTab;
    const selectionCandidate = forceNeutral ? null : selectedTable;
    const occupiedTablesExist = tables.some(
      (t) => t && t.status === "occupied",
    );

    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/delivery/orders/pending`,
      );
      if (!response.ok) {
        throw new Error("Erreur chargement livraisons en attente");
      }

      const deliveries = await response.json();
      const stillHiddenIds = hiddenDeliveryOrderIds.filter((id) =>
        deliveries.some(
          (delivery) =>
            (delivery.order_id ?? delivery.orderId ?? null) === id,
        ),
      );
      if (stillHiddenIds.length !== hiddenDeliveryOrderIds.length) {
        setHiddenDeliveryOrderIds(stillHiddenIds);
      }
      const filteredDeliveries = deliveries.filter(
        (delivery) =>
          !hiddenDeliveryOrderIds.includes(
            delivery.order_id ?? delivery.orderId,
          ),
      );

      const normalizedDeliveries = filteredDeliveries.map((delivery) => {
        const normalizedItems = (delivery.items || [])
          .map((item) => normalizeDeliveryItemExtras(item))
          .filter(Boolean);
        return {
          ...delivery,
          items: normalizedItems,
        };
      });

      const tabs = normalizedDeliveries.map((delivery) => {
        const computedTotal = computeItemsTotal(delivery.items || []);
        const displayNumber =
          delivery.internal_bar_number ||
          delivery.internal_kitchen_number ||
          delivery.order_number;
        const normalizedCustomer = delivery.customer
          ? serializeDeliveryCustomer({
              ...delivery.customer,
              delivery_method:
                delivery.customer.delivery_method ||
                delivery.delivery_method,
              delivery_reference:
                delivery.customer.delivery_reference ||
                delivery.delivery_reference,
            })
          : null;
        const deliveryReference =
          normalizedCustomer?.delivery_reference ||
          delivery.delivery_reference ||
          null;
        return {
          id: `delivery-${delivery.order_id}`,
          orderId: delivery.order_id,
          tableId: delivery.table_id || null,
          originalTableId: delivery.table_id || null,
          number: displayNumber,
          name: displayNumber,
          isDelivery: true,
          deliveryMode: delivery.delivery_mode || "delivery",
          deliveryMethod: delivery.delivery_method || "Direct",
          deliveryRef: deliveryReference,
          deliveryCustomer: normalizedCustomer,
          status: "occupied",
          isTemporary: false,
          items: delivery.items || [],
          total: computedTotal,
          deliveryOrder: {
            id: delivery.order_id,
            orderNumber: delivery.order_number,
            tableId: delivery.table_id || null,
            deliveryMethod: delivery.delivery_method || "Direct",
            deliveryRef: deliveryReference,
            customer: normalizedCustomer,
            items: delivery.items || [],
            total: computedTotal,
            status: delivery.status,
          },
        };
      });

      const deliveryOrdersPayload = normalizedDeliveries.map((delivery) => {
        const normalizedCustomer = delivery.customer
          ? serializeDeliveryCustomer({
              ...delivery.customer,
              delivery_method:
                delivery.customer.delivery_method ||
                delivery.delivery_method,
              delivery_reference:
                delivery.customer.delivery_reference ||
                delivery.delivery_reference,
            })
          : null;
        return {
          orderId: delivery.order_id,
          tableId: delivery.table_id || null,
          orderNumber: delivery.order_number,
          customer: normalizedCustomer,
          deliveryMethod: delivery.delivery_method || "Direct",
          deliveryRef:
            normalizedCustomer?.delivery_reference ||
            delivery.delivery_reference ||
            null,
          items: delivery.items || [],
          total: computeItemsTotal(delivery.items || []),
          status: delivery.status,
        };
      });

      setClientTabs((prev) =>
        areClientTabsEquivalent(prev, tabs) ? prev : tabs,
      );
      setDeliveryOrders((prev) =>
        areDeliveryOrdersEquivalent(prev, deliveryOrdersPayload)
          ? prev
          : deliveryOrdersPayload,
      );
      writeDeliveryCache(tabs, deliveryOrdersPayload);

      const groupedTabs = groupDeliveryTabs(tabs);
      if (!groupedTabs.length) {
        if (selectionCandidate?.isDelivery) {
          setExistingDeliveryItems([]);
          setCurrentDeliveryOrder({ items: [], total: 0 });
          setHasExistingOrders(false);
          setDeliveryCustomer(null);
        }
        return;
      }

      const findGroupForTabId = (tabId) =>
        groupedTabs.find((group) =>
          group.tabs.some((groupTab) => groupTab.id === tabId),
        );

      const selectedGroupingKey =
        selectionCandidate?.isDelivery && selectionCandidate
          ? getDeliveryGroupingKey(selectionCandidate)
          : null;

      let selectedDeliveryGroup =
        selectionCandidate?.isDelivery && selectionCandidate?.id
          ? findGroupForTabId(selectionCandidate.id)
          : null;
      if (!selectedDeliveryGroup && selectedGroupingKey) {
        selectedDeliveryGroup = groupedTabs.find(
          (group) => group.key === selectedGroupingKey,
        );
      }

      if (forceNeutral) {
        setSelectedTable(null);
        setActiveClientTab(null);
        setSelectedDeliveryOrder(null);
        setExistingDeliveryItems([]);
        setCurrentDeliveryOrder({ items: [], total: 0 });
        setHasExistingOrders(false);
      }

      if (
        !selectionCandidate ||
        !selectionCandidate.isDelivery ||
        !selectedDeliveryGroup
      ) {
        if (shouldAutoSelect && !occupiedTablesExist) {
          const targetGroup =
            (selectedGroupingKey &&
              groupedTabs.find((group) => group.key === selectedGroupingKey)) ||
            groupedTabs[0];
          handleSelectDeliveryTab(targetGroup.representative, {
            overrideItems: targetGroup.mergedItems,
            groupOrderIds: targetGroup.orderIds,
          });
        } else {
          setExistingDeliveryItems([]);
          setCurrentDeliveryOrder({ items: [], total: 0 });
          setHasExistingOrders(false);
        }
      } else if (selectedDeliveryGroup) {
        if (shouldAutoSelect && !occupiedTablesExist) {
          handleSelectDeliveryTab(selectedDeliveryGroup.representative, {
            overrideItems: selectedDeliveryGroup.mergedItems,
            groupOrderIds: selectedDeliveryGroup.orderIds,
          });
        } else {
          const { existing, pending } = splitDeliveryItems(
            selectedDeliveryGroup.mergedItems,
          );
          setExistingDeliveryItems(existing);
          setCurrentDeliveryOrder({
            items: pending,
            total: computeItemsTotal(pending),
          });
          setHasExistingOrders(existing.length > 0);
        }
      } else {
        setHasExistingOrders(false);
      }
    } catch (error) {
      console.error("�?� Erreur chargement livraisons en attente:", error);
    }
  };

  const loadTableOrders = async (tableId) => {
    try {
      if (!tableId) return;
      const response = await fetchWithTimeout(
        `${API_BASE}/tables/${tableId}/orders`,
      );
      if (response.ok) {
        const data = await response.json();
        setTableOrders(data);
      } else {
        setTableOrders([]);
      }
    } catch (e) {
      console.error("loadTableOrders error:", e);
      setTableOrders([]);
    }
  };

  const loadAllData = async () => {
    await loadBasicData();
  };

  const getFusionStats = () => {
    const totalFusions = Object.values(tableMerges || {}).reduce(
      (sum, arr) => sum + (arr?.length || 0),
      0,
    );
    return { hasActiveFusions: totalFusions > 0, totalFusions };
  };

  // Défussionner la table actuellement sélectionnée (réafficher les tables sources)
  const handleTableUnmerge = () => {
    if (!selectedTable || !tableMerges[selectedTable.id]) return;

    const mergedNumbers = tableMerges[selectedTable.id] || [];

    // Libérer les tables "cachées" côté UI
    setFusedAwayTables((prev) => {
      const updated = new Set(prev);
      mergedNumbers.forEach((tableNumber) => {
        const table = tables.find((t) => t.number === tableNumber);
        if (table) {
          updated.delete(table.id);
        }
      });
      return updated;
    });

    // Supprimer l'information de fusion pour cette table
    setTableMerges((prev) => {
      const next = { ...prev };
      delete next[selectedTable.id];
      return next;
    });
  };

  const verifyTableSelectionAccess = async (table) => {
    const tableId = Number(table?.id);
    const role = String(currentUser?.role || "").trim().toLowerCase();
    const usesEditingLease = [
      "cashier",
      "caissier",
      "caisse",
      "server",
      "serveur",
      "manager",
      "admin",
    ].includes(role);
    if (!usesEditingLease || table?.isDelivery || !Number.isFinite(tableId)) {
      return table;
    }
    const requestToken = tableSelectionRequestRef.current + 1;
    tableSelectionRequestRef.current = requestToken;
    tableAccessCheckingIdRef.current = String(tableId);
    setTableAccessCheckingId(tableId);
    try {
      const currentDeviceId = getPersistentDeviceId();
      const sameUserOtherDevice = Boolean(
        Number(table?.reservation_user_id || 0) ===
          Number(currentUser?.id || 0) &&
          String(table?.reservation_device_id || "").trim() &&
          String(table?.reservation_device_id || "").trim() !== currentDeviceId,
      );
      const response = await fetchWithTimeout(
        `${API_BASE}/tables/${tableId}/reservation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            deviceId: currentDeviceId,
            takeoverOwnDevice: sameUserOtherDevice,
          }),
        },
        6500,
      );
      const payload = await response.json().catch(() => null);
      if (requestToken !== tableSelectionRequestRef.current) return null;
      if (!response.ok) {
        throw new Error(
          payload?.error || "Impossible de vérifier l'accès à cette table.",
        );
      }

      const access = payload?.access || payload;
      if (!access?.allowed) {
        const managedBy = String(
          payload?.reserved_by_first_name ||
            payload?.managed_by_first_name ||
            "un autre utilisateur",
        ).trim();
        triggerErrorAnimation(
          `Cette table est déjà ouverte par ${managedBy}. Votre brouillon est conservé.`,
        );
        return null;
      }
      return {
        ...table,
        status: access?.table_status || table.status,
        occupancy_cycle:
          Number(access?.occupancy_cycle) ||
          resolveTableOccupancyCycle(table) ||
          1,
        management_type: access?.management_type ?? null,
        managed_by_user_id: access?.managed_by_user_id ?? null,
        managed_by_first_name: access?.managed_by_first_name ?? null,
        managed_by_role: access?.managed_by_role ?? null,
        active_owner_user_id: access?.managed_by_user_id ?? null,
        active_owner_first_name: access?.managed_by_first_name ?? null,
        reservation_user_id: currentUser?.id || null,
        reservation_device_id: currentDeviceId,
        reservation_name:
          currentUser?.first_name || currentUser?.username || null,
      };
    } catch (error) {
      if (requestToken === tableSelectionRequestRef.current) {
        triggerErrorAnimation(
          error?.message ||
            "Connexion indisponible. Réessayez pour ouvrir cette table.",
        );
      }
      return null;
    } finally {
      if (requestToken === tableSelectionRequestRef.current) {
        tableAccessCheckingIdRef.current = null;
        setTableAccessCheckingId(null);
      }
    }
  };

  const releasePendingTableReservation = async (table) => {
    const tableId = Number(table?.id);
    if (!Number.isInteger(tableId) || tableId <= 0 || table?.isDelivery) return;
    try {
      await fetchWithTimeout(
        `${API_BASE}/tables/${tableId}/reservation`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deviceId: getPersistentDeviceId() }),
        },
        3500,
      );
    } catch (_error) {
      // Le bail expire automatiquement; aucun panier n'est détruit ici.
    }
  };

  const selectTable = async (table) => {
    const previousSelectedTable = selectedTableRef.current
      ? { ...selectedTableRef.current }
      : null;
    const previousCartSnapshot = buildCurrentCartSnapshot(previousSelectedTable);
    try {
      if (!table) return;

      if (
        tableAccessCheckingIdRef.current !== null &&
        tableAccessCheckingIdRef.current !== String(table.id)
      ) {
        tableSelectionRequestRef.current += 1;
        tableAccessCheckingIdRef.current = null;
        setTableAccessCheckingId(null);
      }

      if (tableCloseInFlightRef.current.has(String(table.id))) {
        triggerErrorAnimation("Fermeture de cette table en cours...");
        return;
      }

      cashDebugLog("🔧 DIGITALIVORE FIX: Sélection table", {
        tableId: table.id,
        tableNumber: table.number,
        status: table.status,
        isDelivery: table.isDelivery,
      });

      // ✅ UX: en mode "Tables", re-cliquer sur la table sélectionnée => désélectionner
      // (le panier affiche "aucune table sélectionnée").
      if (
        activeTab === "tables" &&
        !table.isDelivery &&
        previousSelectedTable &&
        String(previousSelectedTable.id) === String(table.id)
      ) {
        // Sauvegarder l'état courant dans le cache (comme lors d'un switch),
        // pour ne pas perdre un brouillon si l'utilisateur re-sélectionne ensuite.
        if (previousCartSnapshot) {
          updateTableStateCache(
            previousCartSnapshot.tableId,
            {
              currentOrder: {
                items: previousCartSnapshot.pendingItems || [],
                total: computeItemsTotal(previousCartSnapshot.pendingItems || []),
              },
              existingItems: previousCartSnapshot.existingItems || [],
              hasExistingOrders:
                (previousCartSnapshot.existingItems || []).length > 0,
            },
            { isDelivery: Boolean(previousCartSnapshot.isDelivery) },
          );
        }
        closeEmptyTableOnExit(
          previousSelectedTable,
          previousCartSnapshot,
          "deselect-from-empty-table",
        );

        setSelectedTable(null);
        selectedTableRef.current = null;
        setActiveClientTab(null);
        setPendingTableSelection(null);
        setShowCustomerCountModal(false);
        setCustomerCount("");
        setExistingItemsForPayment([]);
        setCurrentOrder({ items: [], total: 0 });
        setHasExistingOrders(false);
        return;
      }

      if (!table.isDelivery) {
        if (
          tableAccessCheckingIdRef.current === String(table.id)
        ) {
          return;
        }
        const verifiedTable = await verifyTableSelectionAccess(table);
        if (!verifiedTable) return;
        table = verifiedTable;
      }

      // Sauvegarder les données de la table actuelle avant de changer
      if (
        previousSelectedTable &&
        String(previousSelectedTable.id) !== String(table.id)
      ) {
        cashDebugLog(
          "💾 Sauvegarde des données de la table précédente:",
          previousSelectedTable.id,
        );
        // Le snapshot est construit depuis les refs atomiques avant toute mutation.
        // Il ne peut donc pas être attribué à la nouvelle table par un rendu retardé.
        if (previousCartSnapshot) {
          updateTableStateCache(
            previousCartSnapshot.tableId,
            {
              currentOrder: {
                items: previousCartSnapshot.pendingItems || [],
                total: computeItemsTotal(previousCartSnapshot.pendingItems || []),
              },
              existingItems: previousCartSnapshot.existingItems || [],
              hasExistingOrders:
                (previousCartSnapshot.existingItems || []).length > 0,
            },
            { isDelivery: Boolean(previousCartSnapshot.isDelivery) },
          );
        }
        closeEmptyTableOnExit(
          previousSelectedTable,
          previousCartSnapshot,
          "switch-from-empty-table",
        );
      }

      const readRecoverableTableCart = (targetTable) => {
        const cacheKey = buildPersistentCartKey(targetTable.id, Boolean(targetTable.isDelivery));
        const memoryCache =
          tableStatesCacheRef.current.get(targetTable.id) ||
          tableStatesCacheRef.current.get(String(targetTable.id));
        const persistedOrigin = persistentCartCacheRef.current.get(cacheKey);
        const candidate = memoryCache || persistedOrigin;
        if (!targetTable.isDelivery && candidate && !hasCompatibleCartOrigin(candidate, targetTable)) {
          preserveDetachedCart(persistentCartCacheRef.current, cacheKey, candidate);
          if (persistedOrigin && persistedOrigin !== candidate) {
            preserveDetachedCart(persistentCartCacheRef.current, cacheKey, persistedOrigin);
          }
          persistentCartCacheRef.current.delete(cacheKey);
          commitTableStatesCache(previous => {
            const next = new Map(previous); next.delete(targetTable.id); next.delete(String(targetTable.id)); return next;
          });
          forgetKnownExistingItems(targetTable.id, false);
          draftRecoveryTableIdsRef.current.delete(String(targetTable.id));
          writePersistentCartCache(persistentCartCacheRef.current);
          return null;
        }
        if (countActiveCartStateItems(memoryCache || {}) > 0) {
          return memoryCache;
        }

        const persisted = persistentCartCacheRef.current.get(
          buildPersistentCartKey(targetTable.id, Boolean(targetTable.isDelivery)),
        );
        const pendingItems = (persisted?.pendingItems || []).filter(isItemActive);
        const existingItems = normalizeItemsForExisting(
          persisted?.items || [],
        ).filter(isItemActive);
        if (pendingItems.length + existingItems.length === 0) return null;

        return {
          occupancyCycle: persisted?.occupancyCycle,
          cacheContextId: persisted?.cacheContextId,
          currentOrder: {
            items: pendingItems,
            total: computeItemsTotal(pendingItems),
          },
          existingItems,
          hasExistingOrders: existingItems.length > 0,
          timestamp: Number(persisted?.timestamp || 0) || Date.now(),
          courseManagementEnabled: resolveCartCourseManagement({
            explicitEnabled: persisted?.courseManagementEnabled,
            courses: persisted?.courses,
            pendingItems,
            existingItems,
          }),
          activeCourseNumber: resolveSelectedCourseNumber(
            Array.isArray(persisted?.courses) ? persisted.courses : [],
            persisted?.activeCourseNumber,
          ),
          courses: Array.isArray(persisted?.courses) ? persisted.courses : [],
        };
      };

      const cachedDataForSelection = readRecoverableTableCart(table);
      const cachedActiveCountForSelection = countActiveCartStateItems(
        cachedDataForSelection || {},
      );
      const persistedRecovery = persistentCartCacheRef.current.get(
        buildPersistentCartKey(table.id, Boolean(table.isDelivery)),
      );
      if (
        !table.isDelivery &&
        String(table.status || "free").toLowerCase() === "free" &&
        persistedRecovery?.recoveryRequired &&
        (persistedRecovery.pendingItems || []).filter(isItemActive).length > 0
      ) {
        const pendingItems = persistedRecovery.pendingItems.filter(isItemActive);
        const recoveredTable = {
          ...table,
          status: "free",
          occupancy_cycle:
            Number(persistedRecovery.occupancyCycle) ||
            resolveTableOccupancyCycle(table) ||
            1,
        };
        selectedTableRef.current = recoveredTable;
        currentOrderRef.current = {
          items: pendingItems,
          total: computeItemsTotal(pendingItems),
        };
        existingItemsForPaymentRef.current = [];
        setSelectedTable(recoveredTable);
        setCurrentOrder(currentOrderRef.current);
        setExistingItemsForPayment([]);
        setHasExistingOrders(false);
        cashDebugLog("Brouillon local restauré dans le panier de la table", {
          tableId: table.id,
          pendingCount: pendingItems.length,
          reason:
            persistedRecovery.recoveryReason || "restored-after-restart",
        });
        return;
      }

      // Si la table est vraiment libre, demander le nombre de personnes.
      // Un brouillon local/persisté est prioritaire, meme si le statut visuel dit "Libre".
      if (
        table.status === "free" &&
        !isBoutique &&
        cachedActiveCountForSelection === 0
      ) {
        setPendingTableSelection(table);
        setCustomerCount("");
        setCourseModeEnabled(false);
        setActiveCourseNumber(1);
        orderCoursesRef.current = [];
        setOrderCourses([]);
        setShowCustomerCountModal(true);
      } else {
        // Table occupée, sélectionner directement
        bumpTableCartGeneration(table.id, {
          isDelivery: Boolean(table.isDelivery),
        });
        setSelectedTable(table);
        selectedTableRef.current = table;
        if (isServerParticipationMode) {
          switchMobileServerTab("menu");
        }
        try {
          lastSelectAtRef.current.set(table.id, Date.now());
        } catch (_) {}

        // ✅ Hydrater le nombre de clients si présent côté API et manquant en mapping
        setTableCustomerCounts((prev) => {
          const has = Object.prototype.hasOwnProperty.call(prev, table.id);
          const apiCount = Number(table.customerCount || table.customer_count);
          if (!has && !Number.isNaN(apiCount) && apiCount > 0) {
            return { ...prev, [table.id]: apiCount };
          }
          return prev;
        });

        // Vérifier si on a des données en cache pour cette table
        const cachedData = cachedDataForSelection;
        const knownExistingItems = restoreKnownExistingItemsForTable(
          table,
          "select-table-preload",
        );
        const lastSendTs = lastSendAtRef.current.get(table.id);
        const withinGrace = lastSendTs && Date.now() - lastSendTs < 7000;
        const cachedActiveCount = cachedActiveCountForSelection;
        if (cachedData && cachedActiveCount > 0) {
          // Cache local actif: restaurer meme si la pastille table est temporairement "libre".
          cashDebugLog(
            "📦 Restauration des données en cache pour table:",
            table.id,
          );

          const normalizedCachedExisting = normalizeItemsForExisting(
            cachedData.existingItems || [],
          );

          if (table.isDelivery) {
            setCurrentDeliveryOrder(cachedData.currentOrder);
            setExistingDeliveryItems(normalizedCachedExisting);
            currentDeliveryOrderRef.current = cachedData.currentOrder;
            existingDeliveryItemsRef.current = normalizedCachedExisting;
          } else {
            setCurrentOrder(cachedData.currentOrder);
            setExistingItemsForPayment(normalizedCachedExisting);
            currentOrderRef.current = cachedData.currentOrder;
            existingItemsForPaymentRef.current = normalizedCachedExisting;
          }
          setHasExistingOrders(cachedData.hasExistingOrders);
          setCourseModeEnabled(
            resolveCartCourseManagement({
              explicitEnabled: cachedData.courseManagementEnabled,
              courses: cachedData.courses,
              pendingItems: cachedData.currentOrder?.items,
              existingItems: normalizedCachedExisting,
            }),
          );
          const cachedCourses = Array.isArray(cachedData.courses)
            ? cachedData.courses
            : [];
          orderCoursesRef.current = cachedCourses;
          setActiveCourseNumber(
            resolveSelectedCourseNumber(
              cachedCourses,
              cachedData.activeCourseNumber,
            ),
          );
          setOrderCourses(cachedCourses);
          rememberKnownExistingItems(
            table.id,
            normalizedCachedExisting,
            Boolean(table.isDelivery),
          );
          // Le cache sert à afficher immédiatement, mais le serveur reste
          // l'autorité sur le mode multi-tour et les cours de la commande.
          await loadTableItemsForPayment(table.id, false);
        } else {
          if (cachedData && cachedActiveCount === 0) {
            updateTableStateCache(
              table.id,
              { currentOrder: { items: [], total: 0 }, existingItems: [] },
              { isDelivery: Boolean(table.isDelivery) },
            );
          }
          // Le panier visible appartenait encore à la table précédente. Avant
          // toute lecture réseau, aligner aussi les refs synchrones sur la
          // table cible; sinon une réponse « aucune commande ouverte » peut
          // reconstruire ses tours depuis les brouillons de l'autre table.
          const emptyTargetOrder = { items: [], total: 0 };
          const targetKnownItems = normalizeItemsForExisting(
            knownExistingItems,
          ).filter(isItemActive);
          if (table.isDelivery) {
            currentDeliveryOrderRef.current = emptyTargetOrder;
            existingDeliveryItemsRef.current = targetKnownItems;
            setCurrentDeliveryOrder(emptyTargetOrder);
            setExistingDeliveryItems(targetKnownItems);
          } else {
            currentOrderRef.current = emptyTargetOrder;
            existingItemsForPaymentRef.current = targetKnownItems;
            setCurrentOrder(emptyTargetOrder);
            setExistingItemsForPayment(targetKnownItems);
          }
          orderCoursesRef.current = [];
          setOrderCourses([]);
          setActiveCourseNumber(1);
          setCourseModeEnabled(false);
          setHasExistingOrders(targetKnownItems.length > 0);

          // Pas de cache, charger depuis la base de données
          cashDebugLog(
            "🔄 Chargement depuis la base de données pour table:",
            table.id,
          );

          // La table précédente reste sauvegardée dans son propre cache. Pour
          // la cible, seuls une grâce post-envoi ou ses éléments connus peuvent
          // annoncer une commande existante avant la réponse serveur.
          if (withinGrace || knownExistingItems.length > 0) {
            setHasExistingOrders(true);
          }
          if (withinGrace || knownExistingItems.length > 0 || table.status === "occupied") {
            cashDebugLog(
              "�?� Sélection table: occupation conservée avant relecture",
            );

            // ✅ Marquer la table comme occupée de façon optimiste
            try {
              optimisticTableStatusRef.current.set(table.id, {
                status: "occupied",
                ts: Date.now(),
              });
            } catch (_) {}
            // Rafraîchir la liste des tables pour refléter l'occupation
            scheduleLoadTables("table-selected-optimistic");
          }

          // CORRECTION: Toujours charger les données existantes pour éviter les tables "vides"
          await loadTableItemsForPayment(table.id, false);
        }
      }
    } catch (e) {
      console.error("selectTable error:", e);
      if (previousSelectedTable) {
        setSelectedTable(previousSelectedTable);
        restoreCartFromSnapshot(previousCartSnapshot);
        triggerErrorAnimation(
          "Resynchronisation de la table en cours. Le panier local a ete conserve.",
        );
      }
    }
  };

  useEffect(() => {
    if (invoiceSessionRestoreRef.current) return;
    if (showInvoiceGenerationModal) {
      invoiceSessionRestoreRef.current = true;
      return;
    }
    if (!currentUser) return;

    let stored = null;
    try {
      stored = sessionStorage.getItem(INVOICE_SESSION_STORAGE_KEY);
    } catch (_) {
      return;
    }
    if (!stored) return;

    let payload = null;
    try {
      payload = JSON.parse(stored);
    } catch (_) {
      clearInvoiceSession();
      invoiceSessionRestoreRef.current = true;
      return;
    }
    if (!payload?.open) {
      clearInvoiceSession();
      invoiceSessionRestoreRef.current = true;
      return;
    }
    if (
      payload.cashierId &&
      currentUser?.id &&
      payload.cashierId !== currentUser.id
    ) {
      clearInvoiceSession();
      invoiceSessionRestoreRef.current = true;
      return;
    }
    if (
      payload.timestamp &&
      Date.now() - payload.timestamp > MAX_INVOICE_SESSION_AGE_MS
    ) {
      clearInvoiceSession();
      invoiceSessionRestoreRef.current = true;
      return;
    }

    if (payload.isDelivery) {
      if (!clientTabs || clientTabs.length === 0) return;
      const deliveryTab = clientTabs.find(
        (tab) =>
          tab.id === payload.tableId ||
          tab.orderId === payload.deliveryOrderId ||
          tab.deliveryRef === payload.deliveryRef ||
          tab.number === payload.tableNumber,
      );
      if (!deliveryTab) {
        clearInvoiceSession();
        invoiceSessionRestoreRef.current = true;
        return;
      }
      handleSelectDeliveryTab(deliveryTab);
      setShowInvoiceGenerationModal(true);
      invoiceSessionRestoreRef.current = true;
      return;
    }

    if (!tables || tables.length === 0) return;
    const table = tables.find(
      (item) =>
        item.id === payload.tableId || item.number === payload.tableNumber,
    );
    if (!table) {
      clearInvoiceSession();
      invoiceSessionRestoreRef.current = true;
      return;
    }
    if (table.status === "free" && !table.isDelivery) {
      clearInvoiceSession();
      invoiceSessionRestoreRef.current = true;
      return;
    }

    Promise.resolve(selectTable(table))
      .catch(() => {})
      .finally(() => {
        setShowInvoiceGenerationModal(true);
        invoiceSessionRestoreRef.current = true;
      });
  }, [
    clientTabs,
    clearInvoiceSession,
    currentUser,
    handleSelectDeliveryTab,
    selectTable,
    showInvoiceGenerationModal,
    tables,
  ]);

  const handleUnfuseTable = async (tableId) => {
    try {
      setTableMerges((prev) => {
        const next = { ...prev };
        delete next[tableId];
        return next;
      });
    } catch (e) {
      console.error("handleUnfuseTable error:", e);
    }
  };

  const getTableDisplayName = (table) => {
    if (!table) return "";
    const tableNumber = table.number;
    const fused = tableMerges?.[table.id] || [];
    if (fused.length > 0) {
      const fusedNumbers = [...fused].sort((a, b) => a - b).join(" - ");
      return `${tableNumber} - ${fusedNumbers}`;
    }
    return `${tableNumber}`;
  };

  const getTableOwnerFirstName = (table) => {
    if (!table) return "";
    if (typeof table.managed_by_first_name === "string" && table.managed_by_first_name.trim()) {
      return table.managed_by_first_name.trim();
    }
    if (typeof table.active_owner_first_name === "string" && table.active_owner_first_name.trim()) {
      return table.active_owner_first_name.trim();
    }
    if (typeof table.active_owner_name === "string" && table.active_owner_name.trim()) {
      return table.active_owner_name.trim().split(/\s+/)[0] || "";
    }
    return "";
  };

  const getSelectedTableServerDisplayName = () => {
    const toFirstName = (value) => {
      const normalized = String(value || "").trim();
      return normalized ? normalized.split(/\s+/)[0] || normalized : "";
    };
    const itemCandidates = [
      ...(Array.isArray(existingItemsForPayment) ? existingItemsForPayment : []),
      ...(Array.isArray(currentOrder?.items) ? currentOrder.items : []),
    ];
    for (const item of itemCandidates) {
      const candidate =
        String(item?.server_name || item?.serverName || "").trim() ||
        String(item?.created_by_name || item?.createdByName || "").trim() ||
        String(item?.user_name || item?.userName || "").trim();
      if (candidate) return toFirstName(candidate);
    }

    if (stewardModeEnabled && stewardReplacesServer && selectedTable?.id) {
      const assignedSteward = (stewardOverview.stewards || []).find((group) =>
        (group.tables || []).some(
          (table) => String(table.id) === String(selectedTable.id),
        ),
      );
      const stewardName = String(assignedSteward?.display_name || "").trim();
      if (stewardName) return stewardName;
    }

    const tableOwner = getTableOwnerFirstName(selectedTable);
    if (tableOwner) return tableOwner;

    return isServerParticipationMode
      ? toFirstName(getUserShortLabel(currentUser))
      : "";
  };

  const resolveServiceStarterName = ({
    table = null,
    order = null,
    fallbackUser = null,
  } = {}) => {
    const fromOrder =
      String(order?.server_name || order?.serverName || "").trim() ||
      String(order?.created_by_name || order?.createdByName || "").trim();
    if (fromOrder) return fromOrder.split(/\s+/)[0] || fromOrder;

    if (
      table?.id &&
      selectedTable?.id &&
      String(table.id) === String(selectedTable.id)
    ) {
      const selectedTableServer = getSelectedTableServerDisplayName();
      if (selectedTableServer) return selectedTableServer;
    }

    const fromTable = getTableOwnerFirstName(table);
    if (fromTable) return fromTable;

    return getUserShortLabel(fallbackUser || currentUser);
  };

  const removeCustomerFromOrder = () => {
    setSelectedCustomerForOrder(null);
  };

  const getVisitorCountForTable = (table) => {
    if (!table || table.id == null) {
      return null;
    }
    const candidates = [];
    if (table.customerCount !== undefined) {
      candidates.push(table.customerCount);
    }
    if (table.customer_count !== undefined) {
      candidates.push(table.customer_count);
    }
    if (tableCustomerCounts[table.id] !== undefined) {
      candidates.push(tableCustomerCounts[table.id]);
    }
    if (selectedTable && table.id === selectedTable.id && customerCount) {
      candidates.push(customerCount);
    }
    for (const candidate of candidates) {
      if (candidate === null || candidate === undefined) continue;
      const parsed = Number(candidate);
      if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return null;
  };

  const confirmTableSelection = async () => {
    try {
      if (!pendingTableSelection) {
        setShowCustomerCountModal(false);
        return;
      }
      const selected = { ...pendingTableSelection };
      if (customerCount && Number(customerCount) > 0) {
        selected.customerCount = Number(customerCount);
      }
      bumpTableCartGeneration(selected.id, {
        isDelivery: Boolean(selected.isDelivery),
      });
      setSelectedTable(selected);
      selectedTableRef.current = selected;
      if (isServerParticipationMode) {
        switchMobileServerTab("menu");
      }
      try {
        lastSelectAtRef.current.set(selected.id, Date.now());
      } catch (_) {}
      setShowCustomerCountModal(false);

      // Une nouvelle ouverture de table part toujours d'un cycle de service
      // vierge. La référence est vidée immédiatement (sans attendre l'effet
      // React) afin qu'une lecture réseau concurrente ne puisse pas réutiliser
      // les tours d'une ancienne commande déjà payée sur cette table.
      orderCoursesRef.current = [];
      setOrderCourses([]);
      setActiveCourseNumber(1);

      // Le reset React seul n'est pas suffisant: loadTableItemsForPayment lit
      // immédiatement les refs. Elles doivent donc être vidées dans le même
      // tour JavaScript, avant la requête serveur.
      const emptyFreshOrder = { items: [], total: 0 };
      if (selected.isDelivery) {
        currentDeliveryOrderRef.current = emptyFreshOrder;
        existingDeliveryItemsRef.current = [];
        setExistingDeliveryItems([]);
        setCurrentDeliveryOrder(emptyFreshOrder);
      } else {
        currentOrderRef.current = emptyFreshOrder;
        existingItemsForPaymentRef.current = [];
        setExistingItemsForPayment([]);
        setCurrentOrder(emptyFreshOrder);
      }
      setHasExistingOrders(false);
      forgetKnownExistingItems(selected.id, Boolean(selected.isDelivery), {
        preservePending: false,
      });
      updateTableStateCache(
        selected.id,
        { currentOrder: emptyFreshOrder, existingItems: [] },
        { isDelivery: Boolean(selected.isDelivery) },
      );

      // ✅ Sauvegarder le nombre de clients pour l'affichage de la grille
      setTableCustomerCounts((prev) => {
        const count = Number(selected.customerCount || customerCount);
        if (!Number.isNaN(count) && count > 0) {
          return { ...prev, [selected.id]: count };
        }
        return prev;
      });

      await loadTableItemsForPayment(selected.id, false);
    } catch (e) {
      console.error("confirmTableSelection error:", e);
    }
  };

  useEffect(() => {
    socketRuntimeRef.current = {
      tables,
      clientTabs,
      selectedTable,
      hasExistingOrders,
      isBoutique,
      isServerParticipationMode,
      isDualScreenActive,
      onlineMenuOrderingEnabled,
      hasCounterService,
    };
    loadOpenCounterOrdersRef.current = loadOpenCounterOrders;
    tableCartFlushHandlerRef.current = flushRealtimeTableIntent;
    metadataFlushHandlerRef.current = flushMetadataRefresh;
    loadTableItemsForPaymentRef.current = loadTableItemsForPayment;
    selectTableRef.current = selectTable;
  });

  useEffect(() => {
    // Initialiser la connexion API et WebSocket
    const cartCacheRetryTimers = cartCacheReconciliationRetryRef.current;
    initializeConnections();

    return () => {
      resetApiRetry();
      const activeSocket = socketRef.current;
      if (activeSocket) {
        activeSocket.off("connect");
        activeSocket.off("counterOrderChanged");
        activeSocket.off("tableFreed");
        activeSocket.off("tableCartChanged");
        activeSocket.off("order-course-fired");
        activeSocket.off("orderCreated");
        activeSocket.off("paymentCreated");
        activeSocket.off("tableUpdated");
        activeSocket.off("tableReservationUpdated");
        activeSocket.off("kdsSettingsUpdated");
        activeSocket.off("online-order-updated");
        activeSocket.off("kds-action-requested");
        activeSocket.disconnect();
        socketRef.current = null;
      }
      tableCartEventCoordinatorRef.current.clear();
      metadataRefreshCoordinatorRef.current.clear();
      cartCacheRetryTimers.forEach((timer) => clearTimeout(timer));
      cartCacheRetryTimers.clear();
      destructiveCartEvidenceRef.current.clear();
    };
  }, []);

  useEffect(() => {
    setTableCustomerCounts((prev) => {
      const next = { ...prev };
      let hasChanges = false;
      tables.forEach((table) => {
        if (!table || table.id == null) {
          return;
        }
        const rawValue =
          table.customerCount ?? table.customer_count ?? next[table.id];
        const parsed =
          rawValue !== null && rawValue !== undefined
            ? Number(rawValue)
            : null;
        if (
          table.status === "occupied" &&
          parsed !== null &&
          !Number.isNaN(parsed) &&
          parsed > 0
        ) {
          if (next[table.id] !== parsed) {
            next[table.id] = parsed;
            hasChanges = true;
          }
        } else if (table.status !== "occupied") {
          if (Object.prototype.hasOwnProperty.call(next, table.id)) {
            delete next[table.id];
            hasChanges = true;
          }
        }
      });
      return hasChanges ? next : prev;
    });
  }, [tables]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "moo_hidden_delivery_orders",
        JSON.stringify(hiddenDeliveryOrderIds),
      );
    } catch (_) {}
  }, [hiddenDeliveryOrderIds]);

  useEffect(() => {
    if (!selectedTable?.isDelivery) return;
    if (!currentDeliveryOrder.items?.length) return;
    const lastSendTs = lastSendAtRef.current.get(selectedTable.id);
    if (!lastSendTs || Date.now() - lastSendTs > 3000) return;
    if (existingDeliveryItems.length === 0) return;
    cashDebugLog(
      "🧹 Auto-clear pending delivery items after send (grace window hit)",
      {
        tableId: selectedTable.id,
        pendingItems: currentDeliveryOrder.items.length,
        existingItems: existingDeliveryItems.length,
      },
    );
    setCurrentDeliveryOrder({ items: [], total: 0 });
  }, [existingDeliveryItems, selectedTable, currentDeliveryOrder.items]);

  // CORRECTION CRITIQUE: Filtrage automatique des articles annulés - ROBUSTE
  useEffect(() => {
    const hasCancelledCurrent = (currentOrder.items || []).some(
      (item) => !isItemActive(item),
    );
    const hasCancelledExisting = (existingItemsForPayment || []).some(
      (item) => !isItemActive(item),
    );
    if (!hasCancelledCurrent && !hasCancelledExisting) return;

    // 1. Nettoyer currentOrder.items
    if (currentOrder.items && currentOrder.items.length > 0) {
      const activeItems = currentOrder.items.filter(isItemActive);

      if (activeItems.length !== currentOrder.items.length) {
        cashDebugLog(
          "🧹 NETTOYAGE AUTO - Articles annulés détectés dans currentOrder",
        );
        const newTotal = activeItems.reduce(
          (sum, item) => sum + (item.totalPrice || item.price * item.quantity),
          0,
        );

        setCurrentOrder({
          items: activeItems,
          total: newTotal,
        });
      }
    }

    // 2. Nettoyer existingItemsForPayment
    if (existingItemsForPayment && existingItemsForPayment.length > 0) {
      const activeExistingItems = existingItemsForPayment.filter(isItemActive);

      if (activeExistingItems.length !== existingItemsForPayment.length) {
        cashDebugLog(
          "🧹 NETTOYAGE AUTO - Articles annulés détectés dans existingItems",
        );
        setExistingItemsForPayment(activeExistingItems);
      }
    }
    cashDebugLog(
      "✅ Interface nettoyée automatiquement - Articles annulés supprimés",
    );
    // Forcer re-render
    setCartRenderKey((prev) => prev + 1);
  }, [currentOrder.items, existingItemsForPayment]); // Surveiller les deux états

  // Charger les articles des commandes existantes pour le paiement
  const loadTableItemsForPayment = async (
    tableId,
    loadIntoCurrentOrder = false,
  ) => {
    let authoritativeCourses = null;
    let authoritativeCourseNumber = null;
    try {
      cashDebugLog("🧪 VARIATION DEBUG → loadTableItemsForPayment:start", {
        tableId,
        loadIntoCurrentOrder,
        timestamp: new Date().toISOString(),
      });
      if (!tableId) {
        console.warn(
          "⚠�? DIGITALIVORE FIX: Tentative de chargement sans tableId",
        );
        logVariationDiagnostics([], {
          tableId,
          source: "loadTableItemsForPayment:delivery-empty",
        });
        return [];
      }

      // Détecter le type de table
      const selectedCurrent = selectedTableRef.current;
      const runtime = socketRuntimeRef.current || {};
      const targetTable =
        selectedCurrent && String(selectedCurrent.id) === String(tableId)
          ? selectedCurrent
          : (runtime.tables || []).find(
              (t) => String(t.id) === String(tableId),
            ) || null;
      const isDeliveryTable = targetTable?.isDelivery || false;
      const readToken = tableCartSyncRef.current.beginRead(tableId, {
        isDelivery: isDeliveryTable,
      });
      const isCurrentRead = () =>
        tableCartSyncRef.current.isCurrentRead(readToken);
      const canMutateVisibleCart = () => {
        const selectedNow = selectedTableRef.current;
        return Boolean(
          selectedNow &&
            String(selectedNow.id) === String(tableId) &&
            Boolean(selectedNow.isDelivery) === Boolean(isDeliveryTable),
        );
      };
      const logSkippedVisibleCartMutation = (source) => {
        cashDebugLog("🛡�? Panier visible non modifié: chargement d'une autre table", {
          source,
          requestedTableId: tableId,
          selectedTableId: selectedTableRef.current?.id,
        });
      };

      cashDebugLog(
        `🔧 DIGITALIVORE FIX: Chargement articles pour table ${tableId}`,
        {
          isDeliveryTable,
          loadIntoCurrentOrder,
          tableStatus: targetTable?.status,
        },
      );

      // Une garde de paiement ne constitue jamais une preuve que la table est
      // vide. Le serveur reste l'autorité et peut déjà contenir une nouvelle
      // commande créée par un autre poste.
      const numericTableId = Number(tableId);
      const isRecentlyPaidTable =
        recentlyPaidTablesRef.current.has(tableId) ||
        recentlyPaidTablesRef.current.has(String(tableId)) ||
        (Number.isFinite(numericTableId) &&
          recentlyPaidTablesRef.current.has(numericTableId));
      if (isRecentlyPaidTable) {
        cashDebugLog(
          `🧾 Paiement récent détecté pour table ${tableId} — validation serveur obligatoire`,
        );
      }

      // Gestion spécifique des tables de livraison (virtuelles)
      if (isDeliveryTable) {
        const tabData = (runtime.clientTabs || []).find(
          (tab) => tab.id === targetTable?.id,
        );
        if (tabData) {
          const { existing, pending } = splitDeliveryItems(tabData.items || []);
          const aggregatedItems = normalizeItemsForExisting([
            ...existing,
            ...pending,
          ]);
          const normalizedExisting = aggregatedItems.filter(
            (item) => item.order_item_id,
          );
          const normalizedPending = aggregatedItems.filter(
            (item) => !item.order_item_id,
          );
          if (canMutateVisibleCart()) {
            setExistingDeliveryItems(normalizedExisting);
            if (loadIntoCurrentOrder) {
              setCurrentDeliveryOrder({
                items: normalizedPending,
                total: computeItemsTotal(normalizedPending),
              });
            }
            setHasExistingOrders(normalizedExisting.length > 0);
          } else {
            logSkippedVisibleCartMutation("delivery-cache");
          }
          logVariationDiagnostics(aggregatedItems, {
            tableId,
            source: "loadTableItemsForPayment:delivery-cache",
          });
          return aggregatedItems;
        }
        if (targetTable?.orderId) {
          cashDebugLog(
            `🚚 Table de livraison ${tableId} sans état local - récupération API`,
          );
          const deliveryRes = await fetchWithTimeout(
            `${API_BASE}/delivery/orders/${targetTable.orderId}`,
          );
          if (deliveryRes.ok) {
            const deliveryData = await deliveryRes.json();
            if (!isCurrentRead()) {
              cashDebugLog("Lecture livraison obsolète ignorée", { tableId });
              return [];
            }
            const items = deliveryData.items || [];
            const { existing, pending } = splitDeliveryItems(items);
            const aggregatedItems = normalizeItemsForExisting([
              ...existing,
              ...pending,
            ]);
            const normalizedExisting = aggregatedItems.filter(
              (item) => item.order_item_id,
            );
            const normalizedPending = aggregatedItems.filter(
              (item) => !item.order_item_id,
            );
            if (canMutateVisibleCart()) {
              setExistingDeliveryItems(normalizedExisting);
              if (loadIntoCurrentOrder) {
                setCurrentDeliveryOrder({
                  items: normalizedPending,
                  total: computeItemsTotal(normalizedPending),
                });
              }
              setHasExistingOrders(normalizedExisting.length > 0);
            } else {
              logSkippedVisibleCartMutation("delivery-api");
            }
            logVariationDiagnostics(aggregatedItems, {
              tableId,
              source: "loadTableItemsForPayment:delivery-api",
            });
            return aggregatedItems;
          }
          console.warn(
            "⚠�? Impossible de récupérer la commande livraison - état laissé vide",
          );
        }
        return [];
      }

      const response = await fetchWithTimeout(`${API_BASE}/tables/${tableId}/orders`);

      if (!response.ok) {
        throw new Error(
          `Erreur HTTP ${response.status}: ${response.statusText}`,
        );
      }

      const orders = await response.json();
      if (!isCurrentRead()) {
        cashDebugLog("Lecture panier obsolète ignorée", {
          tableId,
          sequence: readToken.sequence,
          generation: readToken.generation,
        });
        return [];
      }
      cashDebugLog(
        `🔧 DIGITALIVORE FIX: ${orders.length} commandes trouvées pour table ${tableId}`,
      );

      const coursedOrder = (orders || []).find(
        (order) =>
          order?.status !== "cancelled" &&
          order?.payment_status !== "paid" &&
          Boolean(
            order?.course_management_enabled ?? order?.courseManagementEnabled,
          ),
      );
      const hasOpenOrder = (orders || []).some(
        (order) =>
          order?.status !== "cancelled" && order?.payment_status !== "paid",
      );
      if (canMutateVisibleCart()) {
        if (coursedOrder) {
          setCourseModeEnabled(true);
          try {
            const coursesResponse = await fetchWithTimeout(
              `${API_BASE}/orders/${coursedOrder.id}/courses`,
              {},
              5000,
            );
            if (
              coursesResponse.ok &&
              isCurrentRead() &&
              canMutateVisibleCart()
            ) {
              const payload = await coursesResponse.json();
              const nextCourses = mergeAuthoritativeCourseStates(
                orderCoursesRef.current,
                Array.isArray(payload?.courses) ? payload.courses : [],
              );
              authoritativeCourses = nextCourses;
              authoritativeCourseNumber = resolveSelectedCourseNumber(
                nextCourses,
              );
              orderCoursesRef.current = nextCourses;
              setOrderCourses(nextCourses);
              setActiveCourseNumber(authoritativeCourseNumber);
            }
          } catch (error) {
            console.warn("Synchronisation des tours différée:", {
              orderId: coursedOrder.id,
              tableId,
              error: error?.message || String(error),
            });
          }
        } else {
          // Le serveur est l'autorité sur l'existence des tours. Sans commande
          // multi-tour ouverte, aucun tour d'une ancienne commande ne doit
          // survivre sur la tablette. Si la table est réellement vide, on
          // conserve seulement le choix du toggle effectué dans le modal.
          const retainedDraftCourses = retainLocalDraftCourseStates(
            orderCoursesRef.current,
            currentOrderRef.current?.items || [],
          );
          orderCoursesRef.current = retainedDraftCourses;
          setActiveCourseNumber(
            resolveSelectedCourseNumber(retainedDraftCourses, 1),
          );
          setOrderCourses(retainedDraftCourses);
          if (hasOpenOrder) {
            setCourseModeEnabled(false);
          }
        }
      }

      const allItems = [];
      let totalAmount = 0;

      orders.forEach((order) => {
        // CORRECTION: Charger SEULEMENT les commandes NON PAYÉES et NON ANNULÉES
        if (order.status !== "cancelled" && order.payment_status !== "paid") {
          cashDebugLog(`🔧 Traitement commande ${order.order_number}`);

          order.items.forEach((item) => {
            // CORRECTION CRITIQUE: Filtrage renforcé des articles annulés et null
            if (isItemActive(item)) {
              // Calculer le prix avec extras pour validation
              const extrasPrice = item.extras
                ? item.extras.reduce(
                    (sum, extra) =>
                      sum +
                      (extra.type === "accompaniment" ? 0 : extra.price || 0) *
                        (extra.quantity || 1),
                    0,
                  )
                : 0;

              // Enrichir les données de l'article avec les extras + métadonnées de variation
              const variationMeta = buildVariationMetadata(item);
              const itemData = {
                id: item.product_id,
                product_id: item.product_id,
                order_item_id: item.id,
                client_line_id: item.client_line_id ?? null,
                client_request_id: item.client_request_id ?? null,
                origin_terminal_id: item.origin_terminal_id ?? null,
                name: item.product_name,
                price: item.unit_price,
                quantity: item.quantity,
                totalPrice: item.total_price,
                emoji: item.emoji,
                image_path: item.image_path,
                image_url: item.image_url,
                destination: item.destination,
                status: item.status,
                orderId: order.id,
                order_number: order.order_number,
                server_name: order.server_name || order.created_by_name || "",
                serverName: order.server_name || order.created_by_name || "",
                created_by_name: order.created_by_name || order.server_name || "",
                createdByName: order.created_by_name || order.server_name || "",
                payment_status: order.payment_status,
                isPaid: false,
                extras: item.extras || [],
                variation_id: item.variation_id ?? null,
                variationId: item.variation_id ?? null,
                variation_name: item.variation_name ?? "",
                variationName: item.variation_name ?? "",
                variation_attributes: item.variation_attributes ?? null,
                variationAttributes: item.variation_attributes ?? null,
                variation_label: item.variation_label ?? "",
                variationLabel: item.variation_label ?? "",
                course_id: item.course_id ?? null,
                courseId: item.course_id ?? null,
                course_number: Number(item.course_number || 1),
                courseNumber: Number(item.course_number || 1),
                ...variationMeta,
              };

              allItems.push(itemData);
              totalAmount += item.total_price;
            }
          });
        }
      });

      const normalizedItems = normalizeItemsForExisting(allItems);
      logVariationDiagnostics(normalizedItems, {
        tableId,
        source: "loadTableItemsForPayment:normalized",
      });
      const serverActiveItemCandidates = (orders || []).reduce(
        (count, order) => {
          if (!order || order.status === "cancelled" || order.payment_status === "paid") {
            return count;
          }
          return (
            count +
            (order.items || []).filter(
              (item) => item && item.status !== "cancelled" && !item.cancelled_at,
            ).length
          );
        },
        0,
      );
      const normalizedTotalAmount = computeItemsTotal(normalizedItems);
      const forceAllowClear =
        normalizedItems.length === 0 &&
        tableCartSyncRef.current.hasClearGrant(tableId, {
          isDelivery: isDeliveryTable,
        });
      if (normalizedItems.length > 0) {
        tableCartSyncRef.current.revokeClear(tableId, {
          isDelivery: isDeliveryTable,
        });
      }
      const knownCache = getKnownExistingItemsForTable(tableId, {
        isDelivery: isDeliveryTable,
      });
      const knownCacheItems = knownCache?.items || [];
      const serverConfirmsNoActiveItems =
        Array.isArray(orders) &&
        orders.length > 0 &&
        serverActiveItemCandidates === 0;
      const snapshot = refreshCartSnapshotRef.current;
      const selectedTableNow = selectedTableRef.current;
      const selectedMatchesTable =
        selectedTableNow &&
        String(selectedTableNow.id) === String(tableId) &&
        Boolean(selectedTableNow.isDelivery) === isDeliveryTable;
      const snapshotMatchesTable =
        snapshot &&
        String(snapshot.tableId) === String(tableId) &&
        Boolean(snapshot.isDelivery) === isDeliveryTable &&
        snapshotHasItems(snapshot);
      const localPendingItems = selectedMatchesTable
        ? isDeliveryTable
          ? currentDeliveryOrderRef.current?.items || []
          : currentOrderRef.current?.items || []
        : [];
      const localExistingItems = selectedMatchesTable
        ? isDeliveryTable
          ? existingDeliveryItemsRef.current || []
          : existingItemsForPaymentRef.current || []
        : [];
      const localHasCoherentItems =
        localPendingItems.filter(isItemActive).length +
          localExistingItems.filter(isItemActive).length +
          knownCacheItems.length >
        0;
      const shouldProtectFromEmptyOverwrite =
        normalizedItems.length === 0 &&
        !forceAllowClear &&
        !recentlyPaidTablesRef.current.has(tableId) &&
        !forcedFreeTablesRef.current.has(tableId) &&
        !serverConfirmsNoActiveItems &&
        (snapshotMatchesTable || localHasCoherentItems);
      if (shouldProtectFromEmptyOverwrite) {
        const protectedPending = snapshotMatchesTable
          ? snapshot.pendingItems || []
          : localPendingItems;
        const protectedExisting = snapshotMatchesTable
          ? snapshot.existingItems || []
          : localExistingItems.length > 0
            ? localExistingItems
            : knownCacheItems;
        const activeProtectedPending = (protectedPending || []).filter(isItemActive);
        const activeProtectedExisting = normalizeItemsForExisting(
          protectedExisting || [],
        ).filter(isItemActive);
        const protectedItems = [
          ...activeProtectedPending,
          ...activeProtectedExisting,
        ];
        if (protectedItems.length > 0) {
          if (activeProtectedExisting.length > 0) {
            rememberKnownExistingItems(
              tableId,
              activeProtectedExisting,
              isDeliveryTable,
            );
          }
          const protectedOrder = {
            items: activeProtectedPending,
            total: computeItemsTotal(activeProtectedPending),
          };
          updateTableStateCache(
            tableId,
            {
              currentOrder: protectedOrder,
              existingItems: activeProtectedExisting,
              hasExistingOrders: activeProtectedExisting.length > 0,
            },
            { isDelivery: isDeliveryTable },
          );
          if (canMutateVisibleCart()) {
            if (isDeliveryTable) {
              currentDeliveryOrderRef.current = protectedOrder;
              existingDeliveryItemsRef.current = activeProtectedExisting;
              setCurrentDeliveryOrder(protectedOrder);
              setExistingDeliveryItems(activeProtectedExisting);
            } else {
              currentOrderRef.current = protectedOrder;
              existingItemsForPaymentRef.current = activeProtectedExisting;
              setCurrentOrder(protectedOrder);
              setExistingItemsForPayment(activeProtectedExisting);
            }
            setHasExistingOrders(activeProtectedExisting.length > 0);
          } else {
            logSkippedVisibleCartMutation("empty-overwrite-protection");
          }
          try {
            optimisticTableStatusRef.current.set(tableId, {
              status: "occupied",
              ts: Date.now(),
            });
          } catch (_) {}
          cashDebugLog(
            "🛡�? Protection anti-panier fantôme: réponse vide ignorée",
            {
              tableId,
              isDeliveryTable,
              protectedCount: protectedItems.length,
              source: snapshotMatchesTable
                ? "snapshot"
                : knownCacheItems.length > 0
                  ? knownCache?.source || "known-cache"
                  : "local-state",
            },
          );
          return protectedItems;
        }
      }
      if (normalizedItems.length > 0) {
        refreshEmptyConfirmRef.current.delete(
          `${isDeliveryTable ? "delivery" : "table"}:${tableId}`,
        );
      }

      // ⚠�? Cas observé: après verrouillage/changement de compte, le premier appel peut renvoyer vide alors que des commandes existent.
      let occupancyActiveItemsCount = 0;
      if (
        !isDeliveryTable &&
        normalizedItems.length === 0 &&
        !forceAllowClear &&
        !recentlyPaidTablesRef.current.has(tableId) &&
        !forcedFreeTablesRef.current.has(tableId)
      ) {
        try {
          const occupancyRes = await fetchWithTimeout(
            `${API_BASE}/tables/${tableId}/occupancy`,
            {},
            2500,
          );
          if (occupancyRes.ok) {
            const occupancy = await occupancyRes.json().catch(() => null);
            occupancyActiveItemsCount = Number(
              occupancy?.active_items_count ??
                occupancy?.activeItemsCount ??
                occupancy?.active_count ??
                0,
            );
          }
        } catch (_) {}
      }

      // A full order read can transiently return empty after idle/auth refresh.
      // If any server signal says the table still has active items, retry without clearing UI state.
      const shouldRetryEmptyOccupied =
        !isDeliveryTable &&
        normalizedItems.length === 0 &&
        (serverActiveItemCandidates > 0 || occupancyActiveItemsCount > 0);

      if (shouldRetryEmptyOccupied) {
        if (canMutateVisibleCart()) {
          setHasExistingOrders(true);
        }
        try {
          optimisticTableStatusRef.current.set(tableId, {
            status: "occupied",
            ts: Date.now(),
          });
        } catch (_) {}
        const recoveryItems = normalizeItemsForExisting([
          ...(localExistingItems || []),
          ...(knownCacheItems || []),
        ]).filter(isItemActive);
        if (recoveryItems.length > 0) {
          rememberKnownExistingItems(tableId, recoveryItems, isDeliveryTable);
          if (canMutateVisibleCart()) {
            setExistingItemsForPayment((prev) =>
              applyExistingItemsRecency(recoveryItems, prev || []),
            );
          } else {
            logSkippedVisibleCartMutation("empty-occupied-retry");
          }
          cashDebugLog(
            "🧲 Protection anti-panier fantôme: restauration cache pendant retry",
            { tableId, restoredCount: recoveryItems.length },
          );
        }
        const nowTs = Date.now();
        const lastRetry = loadItemsRetryRef.current.get(tableId) || 0;
        if (nowTs - lastRetry > 800) {
          loadItemsRetryRef.current.set(tableId, nowTs);
          setTimeout(() => {
            loadTableItemsForPayment(tableId, loadIntoCurrentOrder).catch(
              () => {},
            );
          }, 350);
        }
        return recoveryItems.length > 0 ? recoveryItems : normalizedItems;
      }

      if (
        loadIntoCurrentOrder &&
        canMutateVisibleCart() &&
        (normalizedItems.length > 0 || forceAllowClear)
      ) {
        if (isDeliveryTable) {
          setCurrentDeliveryOrder({
            items: normalizedItems,
            total: normalizedTotalAmount,
          });
          cashDebugLog(`📋 Articles chargés dans le panier de livraison`);
        } else {
          setCurrentOrder({
            items: normalizedItems,
            total: normalizedTotalAmount,
          });
          cashDebugLog(`📋 Articles chargés dans le panier normal`);
        }
      } else if (loadIntoCurrentOrder) {
        logSkippedVisibleCartMutation("load-current-order");
      }

      // 🔧 DIGITALIVORE FIX: Respecter une fenêtre de grâce juste après envoi
      const lastSendTs = lastSendAtRef.current.get(tableId);
      const withinGrace = lastSendTs && Date.now() - lastSendTs < 7000;

      // 🔧 DIGITALIVORE FIX: Stockage dans le bon état selon le type (en évitant d'écraser pendant la grâce)
      if (normalizedItems.length > 0) {
        try {
          rememberKnownExistingItems(tableId, normalizedItems, isDeliveryTable);
        } catch (_) {}
      }

      if (!canMutateVisibleCart()) {
        logSkippedVisibleCartMutation("existing-items-apply");
      } else if (isDeliveryTable) {
        if (forceAllowClear && normalizedItems.length === 0) {
          cashDebugLog("🧹 Clear forcé des articles delivery après double confirmation serveur", {
            tableId,
          });
          setExistingDeliveryItems([]);
          existingDeliveryItemsRef.current = [];
          setHasExistingOrders(false);
          forgetKnownExistingItems(tableId, true, { preservePending: false });
        } else if (normalizedItems.length === 0 && withinGrace) {
          cashDebugLog(
            "�?� Skip clearing existingDeliveryItems within grace period",
          );
          // Si la liste actuelle est vide mais qu'on a un cache, restaurer pour éviter le spinner
          setExistingDeliveryItems((prev) => {
            if ((prev?.length || 0) > 0) return prev;
            const cached = lastExistingItemsRef.current.get(tableId);
            if (
              cached &&
              cached.items &&
              cached.items.length > 0 &&
              cached.isDelivery
            ) {
              cashDebugLog(
                "💾 Grace restore delivery from cache to avoid spinner",
              );
              return cached.items;
            }
            return prev;
          });
        } else if (normalizedItems.length === 0) {
          // Collant: si on a déjà des items existants, ne pas vider sur réponse vide
          setExistingDeliveryItems((prev) => {
            if (
              (prev?.length || 0) > 0 ||
              socketRuntimeRef.current?.hasExistingOrders
            ) {
              cashDebugLog(
                "🧲 Sticky existingDeliveryItems: keep previous on empty API",
              );
              return prev;
            }
            // Fallback sur cache si disponible
            const cached = lastExistingItemsRef.current.get(tableId);
            if (
              cached &&
              cached.items &&
              cached.items.length > 0 &&
              cached.isDelivery
            ) {
              cashDebugLog(
                "💾 Fallback cache delivery → restauration items existants",
              );
              return cached.items;
            }
            cashDebugLog("🧹 Aucun existant, set liste vide (delivery)");
            return [];
          });
        } else {
          setExistingDeliveryItems((prev) =>
            applyExistingItemsRecency(normalizedItems, prev),
          );
          try {
            const mergedItems = applyExistingItemsRecency(
              normalizedItems,
              lastExistingItemsRef.current.get(tableId)?.items || [],
            );
            rememberKnownExistingItems(tableId, mergedItems, true);
          } catch (_) {}
          cashDebugLog(
            `✅ ${normalizedItems.length} articles stockés dans existingDeliveryItems`,
          );
        }
      } else {
        if (forceAllowClear && normalizedItems.length === 0) {
          cashDebugLog("🧹 Clear forcé des articles table après double confirmation serveur", {
            tableId,
          });
          setExistingItemsForPayment([]);
          existingItemsForPaymentRef.current = [];
          setHasExistingOrders(false);
          forgetKnownExistingItems(tableId, false, { preservePending: false });
        } else if (normalizedItems.length === 0 && withinGrace) {
          cashDebugLog(
            "�?� Skip clearing existingItemsForPayment within grace period",
          );
          // Si la liste actuelle est vide mais qu'on a un cache, restaurer pour éviter le spinner
          setExistingItemsForPayment((prev) => {
            if ((prev?.length || 0) > 0) return prev;
            const cached = lastExistingItemsRef.current.get(tableId);
            if (
              cached &&
              cached.items &&
              cached.items.length > 0 &&
              !cached.isDelivery
            ) {
              cashDebugLog("💾 Grace restore table from cache to avoid spinner");
              return cached.items;
            }
            return prev;
          });
        } else if (normalizedItems.length === 0) {
          // Collant: si on a déjà des items existants, ne pas vider sur réponse vide
          setExistingItemsForPayment((prev) => {
            if (
              (prev?.length || 0) > 0 ||
              socketRuntimeRef.current?.hasExistingOrders
            ) {
              cashDebugLog(
                "🧲 Sticky existingItemsForPayment: keep previous on empty API",
              );
              return prev;
            }
            // Fallback sur cache si disponible
            const cached = lastExistingItemsRef.current.get(tableId);
            if (
              cached &&
              cached.items &&
              cached.items.length > 0 &&
              !cached.isDelivery
            ) {
              cashDebugLog(
                "💾 Fallback cache table → restauration items existants",
              );
              return cached.items;
            }
            cashDebugLog("🧹 Aucun existant, set liste vide (table)");
            return [];
          });
        } else {
          setExistingItemsForPayment((prev) =>
            applyExistingItemsRecency(normalizedItems, prev),
          );
          try {
            const mergedItems = applyExistingItemsRecency(
              normalizedItems,
              lastExistingItemsRef.current.get(tableId)?.items || [],
            );
            rememberKnownExistingItems(tableId, mergedItems, false);
          } catch (_) {}
          cashDebugLog(
            `✅ ${normalizedItems.length} articles stockés dans existingItemsForPayment`,
          );
        }
      }

      // CORRECTION: Définir hasExistingOrders en tenant compte de la grâce et du sticky
      setHasExistingOrders((prev) => {
        if (forceAllowClear && normalizedItems.length === 0) return false;
        if (withinGrace) return true;
        if (normalizedItems.length > 0) return true;
        // Si API vide mais on conserve les existants (sticky), rester à true
        return prev;
      });
      cashDebugLog(
        "🔧 DIGITALIVORE FIX: hasExistingOrders défini à:",
        withinGrace ? true : normalizedItems.length > 0,
      );

      // ✅ Si des articles existent réellement pour cette table, marquer la table occupée immédiatement
      if (normalizedItems.length > 0) {
        markTableOccupiedInUi(tableId);
      }

      if (forceAllowClear) {
        tableCartSyncRef.current.consumeClearGrant(tableId, {
          isDelivery: isDeliveryTable,
        });
      }

      // Nettoyer l'horodatage de retry une fois un chargement réussi/passé
      loadItemsRetryRef.current.delete(tableId);

      if (
        authoritativeCourses &&
        isCurrentRead() &&
        canMutateVisibleCart()
      ) {
        const pendingItems = (currentOrderRef.current?.items || []).filter(
          isItemActive,
        );
        updateTableStateCache(
          tableId,
          {
            currentOrder: {
              items: pendingItems,
              total: computeItemsTotal(pendingItems),
            },
            existingItems: normalizedItems,
            hasExistingOrders: normalizedItems.length > 0,
            courseManagementEnabled: true,
            activeCourseNumber: authoritativeCourseNumber,
            courses: authoritativeCourses,
          },
          { isDelivery: false },
        );
      }

      // Retourner les articles pour permettre la vérification
      return normalizedItems;
    } catch (error) {
      console.error("�?� DIGITALIVORE FIX: Erreur chargement articles:", error);
      // IMPORTANT: ne jamais vider les états locaux sur erreur réseau/auth.
      // On conserve les articles déjà chargés pour éviter toute perte visuelle/opérationnelle.
      const selectedNow = selectedTableRef.current;
      const fallbackIsDelivery =
        Boolean(selectedNow?.id === tableId && selectedNow?.isDelivery) ||
        Boolean(
          (socketRuntimeRef.current?.tables || []).find(
            (t) => t.id === tableId,
          )?.isDelivery,
        );
      const known =
        getKnownExistingItemsForTable(tableId, {
          isDelivery: fallbackIsDelivery,
        }) ||
        getKnownExistingItemsForTable(tableId, {
          isDelivery: !fallbackIsDelivery,
        });
      if (known?.items?.length > 0) {
        const fallbackCanMutateVisibleCart =
          selectedNow &&
          String(selectedNow.id) === String(tableId) &&
          Boolean(selectedNow.isDelivery) === Boolean(known.isDelivery);
        if (fallbackCanMutateVisibleCart) {
          if (known.isDelivery) {
            setExistingDeliveryItems(known.items);
          } else {
            setExistingItemsForPayment(known.items);
          }
          setHasExistingOrders(true);
        } else {
          cashDebugLog("🛡�? Fallback cache ignoré pour le panier visible d'une autre table", {
            requestedTableId: tableId,
            selectedTableId: selectedNow?.id,
          });
        }
        return known.items;
      }
      if (String(error?.message || "").includes("401")) {
        triggerErrorAnimation("Session expirée. Reconnectez-vous pour resynchroniser la table.");
      }
      if (
        selectedNow &&
        String(selectedNow.id) === String(tableId) &&
        !selectedNow.isDelivery &&
        selectedNow.status === "occupied" &&
        !recentlyPaidTablesRef.current.has(tableId) &&
        !forcedFreeTablesRef.current.has(tableId)
      ) {
        setHasExistingOrders(true);
        try {
          optimisticTableStatusRef.current.set(tableId, {
            status: "occupied",
            ts: Date.now(),
          });
        } catch (_) {}
        const nowTs = Date.now();
        const lastRetry = loadItemsRetryRef.current.get(tableId) || 0;
        if (nowTs - lastRetry > 1800) {
          loadItemsRetryRef.current.set(tableId, nowTs);
          setTimeout(() => {
            loadTableItemsForPayment(tableId, false).catch(() => {});
          }, 900);
        }
      }
      return [];
    }
  };

  const validateVisibleTableCartIntegrity = async (tableId) => {
    const selectedNow = selectedTableRef.current;
    if (
      !selectedNow ||
      selectedNow.isDelivery ||
      String(selectedNow.id) !== String(tableId) ||
      orderActionInFlightRef.current ||
      reconcilingVisibleTableIdRef.current !== null ||
      selectedTableAccessConflict
    ) {
      return;
    }
    try {
      const expectedGeneration = getTableCartGeneration(tableId, {
        isDelivery: false,
      });
      const response = await fetchWithTimeout(
        `${API_BASE}/tables/${tableId}/cart-snapshot`,
        {},
        2500,
      );
      if (!response.ok) return;
      const snapshot = await response.json().catch(() => null);
      const selectedAfterRead = selectedTableRef.current;
      if (
        snapshot?.complete !== true ||
        !selectedAfterRead ||
        selectedAfterRead.isDelivery ||
        String(selectedAfterRead.id) !== String(tableId) ||
        getTableCartGeneration(tableId, { isDelivery: false }) !==
          expectedGeneration ||
        orderActionInFlightRef.current
      ) {
        return;
      }
      const serverItems = (snapshot.orders || []).flatMap(
        (order) =>
          (order?.items || []).map((item) => ({
            ...item,
            orderId: order?.id ?? item?.order_id ?? null,
          })),
      );
      const coursedOrderIds = new Set(
        (snapshot.orders || [])
          .filter((order) =>
            Boolean(
              order?.course_management_enabled ??
                order?.courseManagementEnabled,
            ),
          )
          .map((order) => String(order.id)),
      );
      const snapshotHasCourseState = Array.isArray(snapshot.courses);
      const serverCourses = mergeAuthoritativeCourseStates(
        orderCoursesRef.current,
        (snapshotHasCourseState ? snapshot.courses : []).filter((course) =>
          coursedOrderIds.has(String(course?.order_id ?? course?.orderId)),
        ),
      );
      const courseStateChanged =
        buildCourseStateSignature(orderCoursesRef.current) !==
        buildCourseStateSignature(serverCourses);
      if (
        snapshotHasCourseState &&
        coursedOrderIds.size > 0 &&
        courseStateChanged
      ) {
        orderCoursesRef.current = serverCourses;
        setCourseModeEnabled(true);
        setOrderCourses(serverCourses);
        setActiveCourseNumber((previous) =>
          resolveDisplayedCourseNumber(serverCourses, previous),
        );
        setCourseDispatchStates((previous) => {
          const next = { ...previous };
          serverCourses.forEach((course) => {
            const status = String(course.status || "");
            const dispatchStatus = String(
              course.production_dispatch_status || "",
            );
            if (
              status === "completed" ||
              (status === "fired" &&
                (["sent", "failed"].includes(dispatchStatus) ||
                  Number(course.dispatch_stale) === 1))
            ) {
              delete next[
                `${course.order_id ?? course.orderId}:${course.course_number}`
              ];
            }
          });
          return next;
        });
      } else if (
        snapshotHasCourseState &&
        coursedOrderIds.size === 0 &&
        orderCoursesRef.current.length > 0
      ) {
        // Un snapshot complet sans commande multi-tour ouverte invalide tout
        // reliquat local. Cela couvre notamment le retour d'une tablette après
        // veille ou la réouverture d'une table dont l'ancienne commande a été
        // payée sur un autre terminal.
        const retainedDraftCourses = retainLocalDraftCourseStates(
          orderCoursesRef.current,
          currentOrderRef.current?.items || [],
        );
        orderCoursesRef.current = retainedDraftCourses;
        setOrderCourses(retainedDraftCourses);
        setActiveCourseNumber(
          resolveSelectedCourseNumber(retainedDraftCourses, 1),
        );
      }
      const localSignature = buildSentCartIntegritySignature(
        existingItemsForPaymentRef.current || [],
      );
      const serverSignature = buildSentCartIntegritySignature(serverItems);
      if (localSignature !== serverSignature) {
        await loadTableItemsForPaymentRef.current(tableId, false);
      }
    } catch (_) {
      // Échec ouvert : le panier visible est conservé intégralement.
    }
  };

  // Socket.IO reste le chemin rapide. Cette validation discrète est le filet
  // de sécurité d'une table actuellement visible lorsqu'un événement réseau
  // a été manqué ou qu'une lecture concurrente a rendu sa réponse obsolète.
  // Elle ne touche jamais aux brouillons et réutilise toutes les protections
  // de séquençage/vidage de loadTableItemsForPayment.
  useEffect(() => {
    const tableId = selectedTable?.id;
    const isPhysicalTable = Boolean(tableId && !selectedTable?.isDelivery);
    if (!isPhysicalTable) return undefined;

    let disposed = false;
    const syncState = visibleTableCartSyncRef.current;
    syncState.tableId = String(tableId);

    const scheduleNext = (delayMs = 2000) => {
      if (disposed) return;
      if (syncState.timer) clearTimeout(syncState.timer);
      syncState.timer = setTimeout(runValidation, delayMs);
    };

    const runValidation = async () => {
      if (disposed) return;
      const selectedNow = selectedTableRef.current;
      if (
        !selectedNow ||
        selectedNow.isDelivery ||
        String(selectedNow.id) !== String(tableId)
      ) {
        return;
      }
      if (
        (typeof document !== "undefined" && document.visibilityState === "hidden") ||
        orderActionInFlightRef.current ||
        reconcilingVisibleTableIdRef.current !== null ||
        selectedTableAccessConflict
      ) {
        scheduleNext(750);
        return;
      }

      tableCartEventCoordinatorRef.current.enqueue(`table:${tableId}`, {
        tableId,
        targetTableId: tableId,
        reason: "visible-integrity-poll",
        contextKnown: true,
        isDelivery: false,
        needsIntegrityCheck: true,
      });
      scheduleNext(2000);
    };

    const handleVisibleOrFocused = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }
      scheduleNext(50);
    };

    scheduleNext(500);
    if (typeof window !== "undefined") {
      window.addEventListener("focus", handleVisibleOrFocused);
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibleOrFocused);
    }

    return () => {
      disposed = true;
      if (syncState.timer) {
        clearTimeout(syncState.timer);
        syncState.timer = null;
      }
      if (String(syncState.tableId) === String(tableId)) {
        syncState.tableId = null;
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", handleVisibleOrFocused);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibleOrFocused);
      }
    };
  }, [
    fetchWithTimeout,
    selectedTable?.id,
    selectedTable?.isDelivery,
    selectedTableAccessConflict,
  ]);

  useEffect(() => {
    const table = selectedTable;
    if (!table || table.isDelivery || table.status !== "occupied") return;
    if (loading || isCheckingTableFree) return;
    if (recentlyPaidTablesRef.current.has(table.id)) return;
    if (forcedFreeTablesRef.current.has(table.id)) return;

    const currentCount = (currentOrder.items || []).filter(isItemActive).length;
    const existingCount = (existingItemsForPayment || []).filter(isItemActive).length;
    if (currentCount + existingCount > 0) {
      occupiedCartHydrationLastAtRef.current.delete(String(table.id));
      return;
    }

    const restored = restoreKnownExistingItemsForTable(
      table,
      "occupied-empty-cart-auto-restore",
    );
    if (restored.length > 0) return;

    const nowTs = Date.now();
    const tableKey = String(table.id);
    const lastHydration = occupiedCartHydrationLastAtRef.current.get(tableKey) || 0;
    if (nowTs - lastHydration < 1800) return;
    occupiedCartHydrationLastAtRef.current.set(tableKey, nowTs);
    setHasExistingOrders(true);
    try {
      optimisticTableStatusRef.current.set(table.id, {
        status: "occupied",
        ts: nowTs,
      });
    } catch (_) {}

    if (occupiedCartHydrationTimerRef.current) {
      clearTimeout(occupiedCartHydrationTimerRef.current);
    }
    occupiedCartHydrationTimerRef.current = setTimeout(() => {
      loadTableItemsForPayment(table.id, false).catch((error) => {
        cashDebugLog("Hydratation panier occupee ignoree", {
          tableId: table.id,
          error: error?.message || String(error),
        });
      });
    }, 450);

    return () => {
      if (occupiedCartHydrationTimerRef.current) {
        clearTimeout(occupiedCartHydrationTimerRef.current);
        occupiedCartHydrationTimerRef.current = null;
      }
    };
  }, [
    selectedTable?.id,
    selectedTable?.status,
    selectedTable?.isDelivery,
    loading,
    isCheckingTableFree,
    currentOrder.items,
    existingItemsForPayment,
  ]);

	  // Ajouter à une commande existante (ajout optimiste + retry reload)
	  const addToExistingOrder = async (orderId, options = {}) => {
    if (selectedTableAccessConflict) {
      triggerErrorAnimation(
        formatTableAccessConflictMessage(selectedTableAccessConflict),
      );
      return false;
    }
      const suppressDispatch = options?.suppressDispatch === true;
      const dispatchAddition = options?.dispatchAddition !== false;
      const courseNumberFilter = Number(options?.courseNumber || 0) || null;
      let clientRequestId = options?.clientRequestId
        ? String(options.clientRequestId)
        : null;
	    const separatedItems = getSeparatedItems();
      const newItems = courseNumberFilter
        ? separatedItems.newItems.filter(
            (item) =>
              Number(item?.courseNumber ?? item?.course_number ?? 1) ===
              courseNumberFilter,
          )
        : separatedItems.newItems;

    if (newItems.length === 0) {
      triggerErrorAnimation(
        selectedTable?.isDelivery
          ? "Aucun nouvel article à ajouter à la livraison !"
          : isBoutique
            ? "Aucun nouvel article à ajouter au panier !"
            : "Aucun nouvel article à ajouter à la table !",
      );
      return false;
    }

    const finalTable = selectedTable;
    if (!finalTable) {
      triggerErrorAnimation(
        isBoutique
          ? "Veuillez ajouter des articles au panier."
          : "Sélectionnez une table ou une livraison active !",
      );
      return false;
    }

    const resolvedTableId = getTableIdentifier(finalTable);
    if (
      resolvedTableId === null ||
      resolvedTableId === undefined ||
      resolvedTableId === ""
    ) {
      console.error(
        "�?� Impossible de déterminer l'ID de la table pour l'ajout:",
        finalTable,
      );
      triggerErrorAnimation(
        isBoutique
          ? "Impossible d'identifier le panier. Veuillez réessayer."
          : "Impossible d'identifier la table sélectionnée. Veuillez la re-sélectionner.",
      );
      return false;
    }

    const isDeliveryOrder =
      !!finalTable?.isDelivery ||
      (typeof resolvedTableId === "string" &&
        resolvedTableId.startsWith("delivery-"));

    const apiTableId = getApiTableId(finalTable);
    if (!isDeliveryOrder && (apiTableId === null || apiTableId === undefined)) {
      triggerErrorAnimation(
        isBoutique
          ? "Impossible d'identifier le panier pour l'ajout. Veuillez réessayer."
          : "Impossible d'identifier la table pour l'ajout. Veuillez réessayer.",
      );
      return false;
    }

    let resolvedOrderId = (() => {
      const candidates = [];
      if (orderId) candidates.push(orderId);
      const sourceItems = isDeliveryOrder
        ? existingDeliveryItems
        : existingItemsForPayment;
      (sourceItems || []).forEach((item) => {
        const id = item?.order_id ?? item?.orderId ?? null;
        if (id !== null && id !== undefined) candidates.push(id);
      });
      if (finalTable?.orderId) candidates.push(finalTable.orderId);
      if (finalTable?.order_id) candidates.push(finalTable.order_id);
      if (finalTable?.deliveryOrder?.orderId) {
        candidates.push(finalTable.deliveryOrder.orderId);
      } else if (finalTable?.deliveryOrder?.order_id) {
        candidates.push(finalTable.deliveryOrder.order_id);
      } else if (finalTable?.deliveryOrder?.id) {
        candidates.push(finalTable.deliveryOrder.id);
      }
      if (selectedDeliveryOrder?.orderId) {
        candidates.push(selectedDeliveryOrder.orderId);
      } else if (selectedDeliveryOrder?.order_id) {
        candidates.push(selectedDeliveryOrder.order_id);
      } else if (selectedDeliveryOrder?.id) {
        candidates.push(selectedDeliveryOrder.id);
      }
      const numeric = candidates
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
      if (numeric.length === 0) {
        const raw =
          (typeof resolvedTableId === "string" ? resolvedTableId : null) ||
          (typeof finalTable?.id === "string" ? finalTable.id : null);
        if (raw && raw.startsWith("delivery-") && !finalTable?.isTemporary) {
          const parsed = Number(raw.replace("delivery-", ""));
          if (Number.isFinite(parsed) && parsed > 0) return parsed;
        }
        return null;
      }
      return Math.max(...numeric);
    })();

    if (!resolvedOrderId) {
      const resolveOrderIdFromApi = async () => {
        const targetApiTableId = apiTableId ?? resolvedTableId;
        if (
          isDeliveryOrder ||
          targetApiTableId === null ||
          targetApiTableId === undefined ||
          targetApiTableId === ""
        ) {
          return null;
        }
        try {
          const res = await fetch(`${API_BASE}/tables/${targetApiTableId}/orders`);
          if (!res.ok) return null;
          const orders = await res.json();
          const openOrders = Array.isArray(orders)
            ? orders.filter(
                (order) =>
                  order &&
                  order.status !== "cancelled" &&
                  order.payment_status !== "paid",
              )
            : [];
          if (openOrders.length === 0) return null;
          const ranked = openOrders
            .map((order) => {
              const createdAt =
                order.created_at ||
                order.createdAt ||
                order.updated_at ||
                order.updatedAt ||
                0;
              const createdTs = createdAt ? new Date(createdAt).getTime() : 0;
              return {
                id: Number(order.id),
                ts: Number.isFinite(createdTs) ? createdTs : 0,
              };
            })
            .filter((entry) => Number.isFinite(entry.id) && entry.id > 0)
            .sort((a, b) => {
              if (a.ts && b.ts) return b.ts - a.ts;
              return b.id - a.id;
            });
          return ranked[0]?.id || null;
        } catch (error) {
          console.warn(
            "⚠�? Impossible de recharger les commandes pour cette table:",
            error?.message || error,
          );
          return null;
        }
      };

      resolvedOrderId = await resolveOrderIdFromApi();
    }

    if (!resolvedOrderId) {
      if (apiStatus === "connected") {
        console.warn(
          "⚠�? Aucune commande active trouvée, création d'une nouvelle commande.",
          { tableId: resolvedTableId },
        );
        const known = getKnownExistingItemsForTable(resolvedTableId, {
          isDelivery: isDeliveryOrder,
        });
        if (known?.items?.length > 0) {
          setHasExistingOrders(true);
          if (isDeliveryOrder) {
            setExistingDeliveryItems(known.items);
          } else {
            setExistingItemsForPayment(known.items);
          }
        } else {
          setHasExistingOrders(false);
        }
        setIsAddingToExistingOrder(false);
        if (!orderActionInFlightRef.current) {
          await sendToCuisineBar({ suppressDispatch, clientRequestId });
        }
      } else {
        triggerErrorAnimation(
          isBoutique
            ? "Connexion API instable. Rechargez le panier et réessayez."
            : "Connexion API instable. Rechargez la table et réessayez.",
        );
      }
      return false;
    }

    if (!clientRequestId) {
      clientRequestId = buildOrderSendRequestId({
        operation: "add",
        deviceId: getPersistentDeviceId(),
        tableId: resolvedTableId,
        orderId: resolvedOrderId,
        items: newItems,
      });
    }

    const effectiveDeliveryMode =
      finalTable?.deliveryMode || deliveryMode || "delivery";
    const effectiveDeliveryMethod =
      finalTable?.deliveryMethod || deliveryMethod || "Direct";
    const effectiveCustomerRaw = resolveDeliveryCustomer({
      table: finalTable,
      method: effectiveDeliveryMethod,
      reference:
        effectiveDeliveryMethod !== "Direct"
          ? deliveryRef ||
            finalTable?.deliveryRef ||
            finalTable?.deliveryOrder?.deliveryRef ||
            null
          : null,
    });
    const effectiveCustomer = serializeDeliveryCustomer(effectiveCustomerRaw);

    if (orderActionInFlightRef.current) {
      return false;
    }
	    orderActionInFlightRef.current = true;
	    setIsOrderActionInProgress(true);
	    setOrderActionKind("add");
	    const optimisticPrefix = buildOptimisticOrderItemIdPrefix("add");
	    const currentItemsSnapshot = isDeliveryOrder
	      ? currentDeliveryOrder.items
	      : currentOrder.items;
	    const existingItemsSnapshot = isDeliveryOrder
	      ? [...(existingDeliveryItems || [])]
	      : [...(existingItemsForPayment || [])];
	    const shouldPromoteItem = courseNumberFilter
        ? (item) =>
            Number(item?.courseNumber ?? item?.course_number ?? 1) ===
            courseNumberFilter
        : null;
	    const optimisticMarkedSnapshotItems = markCartItemsOptimistically(
	      currentItemsSnapshot,
	      optimisticPrefix,
        shouldPromoteItem,
	    );
	    const optimisticSentItems = optimisticMarkedSnapshotItems.filter((item) =>
	      hasOptimisticPrefix(item?.order_item_id, optimisticPrefix),
	    );
	    const remainingDraftItems = optimisticMarkedSnapshotItems.filter(
        (item) => !hasOptimisticPrefix(item?.order_item_id, optimisticPrefix),
      );
	    const optimisticStateItems = isDeliveryOrder
	      ? markDeliveryItemsAsSent(
	          optimisticSentItems,
	          resolvedOrderId ||
	            finalTable?.orderId ||
	            resolvedTableId ||
	            "delivery",
	        )
	      : optimisticSentItems;

		    try {
		      const transformedItems = newItems
		        .map((item, index) => {
		          const productId =
	            item.id ??
	            item.product_id ??
	            item.productId ??
	            item.product?.id ??
	            null;
	          if (!productId) {
	            console.warn(
	              "⚠�? Impossible de déterminer le productId pour un article, exclusion",
	              { itemIndex: index, item },
	            );
	            return null;
	          }
	          const quantity = item.quantity && item.quantity > 0 ? item.quantity : 1;
	          const catalogProduct = Array.isArray(products)
	            ? products.find((p) => Number(p?.id) === Number(productId))
	            : null;
	          const resolvedVariation = resolveVariationForPayload(item, catalogProduct);
	          let unitPrice = parseMoney(
	            item.unitPrice ??
	              item.unit_price ??
	              item.price ??
	              item.product_price ??
	              0,
	          );
	          if (
	            catalogProduct?.product_type === "variable" &&
	            resolvedVariation.variation
	          ) {
            const vPrice = getUnitPriceForSelectionMode(
              catalogProduct,
              resolvedVariation.variation,
              activeAggregator,
            );
	            if (vPrice) unitPrice = vPrice;
	          }
	          return {
	            productId,
	            client_line_id:
	              item?.client_line_id ?? item?.clientLineId ?? null,
	            quantity,
	            unitPrice,
	            destination: normalizeDestination(item.destination),
	            extras: Array.isArray(item.extras) ? item.extras : [],
	            variationId: resolvedVariation.variationId ?? null,
	            variationName: resolvedVariation.variationName ?? null,
	            variationAttributes: resolvedVariation.variationAttributes ?? null,
	            variationLabel: resolvedVariation.variationLabel ?? null,
	            courseNumber: Number(item?.courseNumber ?? item?.course_number ?? 1),
	          };
	        })
	        .filter(Boolean);

	      if (transformedItems.length === 0) {
	        triggerErrorAnimation(
	          "Aucun article valide à ajouter. Veuillez réessayer.",
	        );
	        return;
	      }

	      // ⚡ UX: basculer instantanément les items en "Déjà Envoyé(s)" et vider le panier,
	      // pour éviter les doubles clics pendant la persistance serveur.
	      try {
	        setHasExistingOrders(true);
	        if (isDeliveryOrder) {
	          setExistingDeliveryItems((prev) => {
	            const persistedPrev = filterPersistedDeliveryItems(prev);
	            const next = mergeExistingItemsWithLatestFirst(
	              optimisticStateItems,
	              persistedPrev,
	            );
	            try {
	              lastExistingItemsRef.current.set(resolvedTableId, {
	                items: next,
	                isDelivery: true,
	              });
	            } catch (_) {}
	            return next;
	          });
	          setCurrentDeliveryOrder((prev) => {
	            const marked = markCartItemsOptimistically(
                prev.items,
                optimisticPrefix,
                shouldPromoteItem,
              );
	            const remaining = (marked || []).filter(
	              (item) => !hasOptimisticPrefix(item?.order_item_id, optimisticPrefix),
	            );
	            return { items: remaining, total: computeItemsTotal(remaining) };
	          });
	        } else {
	          setExistingItemsForPayment((prev) => {
	            const next = mergeExistingItemsWithLatestFirst(
	              optimisticStateItems,
	              prev,
	            );
	            try {
	              lastExistingItemsRef.current.set(resolvedTableId, {
	                items: next,
	                isDelivery: false,
	              });
	            } catch (_) {}
	            try {
	                updateTableStateCache(
	                resolvedTableId,
	                {
	                  currentOrder: {
                      items: remainingDraftItems,
                      total: computeItemsTotal(remainingDraftItems),
                    },
	                  existingItems: next,
	                  hasExistingOrders: true,
	                },
	                { isDelivery: false },
	              );
	            } catch (_) {}
	            return next;
	          });
	          setCurrentOrder((prev) => {
	            const marked = markCartItemsOptimistically(
                prev.items,
                optimisticPrefix,
                shouldPromoteItem,
              );
	            const remaining = (marked || []).filter(
	              (item) => !hasOptimisticPrefix(item?.order_item_id, optimisticPrefix),
	            );
	            return { items: remaining, total: computeItemsTotal(remaining) };
	          });
	        }
	        setIsAddingToExistingOrder(false);
	      } catch (_) {}
	      cashDebugLog(
	        "🧭 DEBUG destinations (addToExisting):",
	        transformedItems.reduce((acc, it) => {
          acc[it.destination] = (acc[it.destination] || 0) + 1;
          return acc;
        }, {}),
      );

    try {
      forcedFreeTablesRef.current.delete(finalTable.id);
    } catch (_) {}
      const response = await sendOrderMutationWithRecovery({
        url: `${API_BASE}/orders/${resolvedOrderId}/items`,
        options: {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: transformedItems,
            suppressDispatch,
            dispatchAddition,
            client_request_id: clientRequestId,
            occupancy_cycle: resolveTableOccupancyCycle(finalTable),
          }),
        },
        clientRequestId,
        operation: "add-order-items",
        orderId: resolvedOrderId,
      });

      if (!response.ok) {
        let errorMessage = "Erreur lors de l'ajout";
        let errorCode = "";
        let currentCycle = null;
        try {
          const errorData = await response.json();
          errorCode = String(errorData?.code || "").toUpperCase();
          currentCycle = Number(errorData?.currentCycle) || null;
          errorMessage =
            errorData?.details || errorData?.error || errorMessage;
        } catch (_) {
          try {
            const text = await response.text();
            if (text) {
              errorMessage = `${errorMessage}: ${text}`;
            }
          } catch (_) {}
        }
        throw Object.assign(new Error(errorMessage), {
          code: errorCode || null,
          currentCycle,
        });
      }

	      const result = await response.json();
	      if (!isDeliveryOrder && resolvedTableId) {
	        draftRecoveryTableIdsRef.current.delete(String(resolvedTableId));
	        const recoveryCacheKey = buildPersistentCartKey(
	          resolvedTableId,
	          false,
	        );
	        if (persistentCartCacheRef.current.has(recoveryCacheKey)) {
	          upsertPersistentCartCacheEntry(
	            persistentCartCacheRef.current,
	            recoveryCacheKey,
	            { recoveryRequired: false, recoveryReason: "" },
	          );
	          writePersistentCartCache(persistentCartCacheRef.current);
	        }
	      }
	      const resolvedDisplayNumber =
	        result.barNumber ||
	        result.kitchenNumber ||
	        finalTable?.number ||
	        finalTable?.name;

	      const deliveryReadyNewItems = optimisticStateItems;
	      const addedTotal = computeItemsTotal(deliveryReadyNewItems);

      triggerSuccessAnimation(
        finalTable?.isDelivery
          ? "Articles ajoutés à la livraison !"
          : isBoutique
            ? "Articles ajoutés au panier !"
            : "Articles ajoutés à la table !",
      );
      // Ferme immédiatement le modal côté caisse pour garder une sensation fluide,
      // puis on laisse les mises à jour d'état se poursuivre en arrière-plan.
      setIsAddingToExistingOrder(false);
      const runHeavyUpdates = async () => {
        try {
          // Laisser le navigateur repeindre avant de poursuivre les traitements lourds
          await new Promise((resolve) => setTimeout(resolve, 0));

          if (!isDeliveryOrder) {
            bumpTableCartGeneration(resolvedTableId, { isDelivery: false });
            markTableOccupiedInUi(resolvedTableId);
          }
          scheduleLoadTables("send-to-kitchen-bar");

          try {
            lastSendAtRef.current.set(resolvedTableId, Date.now());
          } catch (_) {}
          setHasExistingOrders(true);

          setSelectedTable((prev) => {
            if (!prev || getTableIdentifier(prev) !== resolvedTableId) return prev;
            const updated = {
              ...prev,
              deliveryCustomer: effectiveCustomer || prev.deliveryCustomer,
              deliveryMode: effectiveDeliveryMode,
              deliveryMethod: effectiveDeliveryMethod,
            };
            if (isDeliveryOrder) {
              updated.number = resolvedDisplayNumber;
              updated.name = resolvedDisplayNumber;
              updated.deliveryRef =
                effectiveDeliveryMethod !== "Direct"
                  ? deliveryRef || prev.deliveryRef
                  : prev.deliveryRef;
            }
            return updated;
          });
          if (isDeliveryOrder) {
            setActiveClientTab((prev) =>
              prev && getTableIdentifier(prev) === resolvedTableId
                ? {
                    ...prev,
                    number: resolvedDisplayNumber,
                    name: resolvedDisplayNumber,
                    deliveryCustomer: effectiveCustomer || prev.deliveryCustomer,
                    deliveryMode: effectiveDeliveryMode,
                    deliveryMethod: effectiveDeliveryMethod,
                  }
                : prev,
            );
          }

	          if (isDeliveryOrder) {
	            setClientTabs((prev) => {
	              let found = false;
	              const next = prev.map((tab) => {
	                if (getTableIdentifier(tab) !== resolvedTableId) return tab;
                found = true;
                const persistedItems = filterPersistedDeliveryItems(tab.items);
                const mergedItems = [...persistedItems, ...deliveryReadyNewItems];
                return {
                  ...tab,
                  number: resolvedDisplayNumber,
                  name: resolvedDisplayNumber,
                  deliveryCustomer: effectiveCustomer || tab.deliveryCustomer,
                  deliveryMode: effectiveDeliveryMode,
                  deliveryMethod: effectiveDeliveryMethod,
                  deliveryRef:
                    effectiveDeliveryMethod !== "Direct"
                      ? deliveryRef
                      : tab.deliveryRef,
                  orderNumber: result.orderNumber,
                  items: mergedItems,
                  total: computeItemsTotal(mergedItems),
                };
              });
              if (!found) {
                next.push({
                  id: resolvedTableId,
                  orderId: result.orderId || finalTable.orderId,
                  number: resolvedDisplayNumber,
                  name: resolvedDisplayNumber,
                  isDelivery: true,
                  deliveryCustomer: effectiveCustomer,
                  deliveryMode: effectiveDeliveryMode,
                  deliveryMethod: effectiveDeliveryMethod,
                  status: "occupied",
                  orderNumber: result.orderNumber,
                  items: deliveryReadyNewItems,
                  total: computeItemsTotal(deliveryReadyNewItems),
                });
              }
              return next;
            });

            const newDeliveryOrder = {
              id: result.orderId || result.id || Date.now(),
              orderId: result.orderId,
              orderNumber: result.orderNumber,
              type: effectiveDeliveryMode,
              deliveryMethod: effectiveDeliveryMethod,
              deliveryRef:
                effectiveDeliveryMethod !== "Direct"
                  ? deliveryRef || finalTable?.deliveryRef || null
                  : finalTable?.deliveryRef || null,
              customer: effectiveCustomer,
              items: deliveryReadyNewItems,
              total: computeItemsTotal(deliveryReadyNewItems),
              status: "pending",
              createdAt: new Date(),
            };

            setDeliveryOrders((prev) => {
              const existingOrderIndex = prev.findIndex(
                (order) =>
                  order.status === "pending" &&
                  isSameDeliveryCustomer(order.customer, effectiveCustomer),
              );

              if (existingOrderIndex !== -1) {
                const updatedOrders = [...prev];
                const existingOrder = updatedOrders[existingOrderIndex];

                updatedOrders[existingOrderIndex] = {
                  ...existingOrder,
                  items: [...existingOrder.items, ...deliveryReadyNewItems],
                  total: existingOrder.total + addedTotal,
                  orderNumber: result.orderNumber,
                  orderId: result.orderId,
                  deliveryMethod: effectiveDeliveryMethod,
                  deliveryRef:
                    effectiveDeliveryMethod !== "Direct"
                      ? deliveryRef ||
                        existingOrder.deliveryRef ||
                        finalTable?.deliveryRef ||
                        null
                      : existingOrder.deliveryRef || null,
                  updatedAt: new Date(),
                };

                cashDebugLog(
                  `🔄 Commande fusionnée pour ${effectiveCustomer?.first_name || ""} ${effectiveCustomer?.last_name || ""}`,
                );
                return updatedOrders;
              } else {
                cashDebugLog(
                  `✨ Nouvelle commande pour ${
                    effectiveCustomer
                      ? `${effectiveCustomer.first_name || ""} ${effectiveCustomer.last_name || ""}`
                      : "client anonyme"
                  }`,
                );
                return [...prev, newDeliveryOrder];
              }
            });

            if (
              selectedTable?.isDelivery &&
              getTableIdentifier(selectedTable) === resolvedTableId
            ) {
              setSelectedDeliveryOrder((prev) => {
                if (!prev) return newDeliveryOrder;
                return {
                  ...prev,
                  items: [...prev.items, ...deliveryReadyNewItems],
                  total: prev.total + computeItemsTotal(deliveryReadyNewItems),
                };
              });
            }

            setTimeout(() => {
              loadPendingDeliveries();
	            }, 500);
	          } else {
	          }

	          const retryReload = async (attempt = 1) => {
	            try {
              const items = await loadTableItemsForPayment(
                resolvedTableId,
                false,
              );
              if ((items?.length || 0) === 0 && attempt < 3) {
                setTimeout(() => retryReload(attempt + 1), 600);
              }
            } catch (_) {}
          };
          setTimeout(() => retryReload(1), 400);

          if (!suppressDispatch && socket) {
            socket.emit("order-created", {
              orderId: resolvedOrderId || null,
              tableId: resolvedTableId,
              items: transformedItems,
            });
          }
        } catch (err) {
          console.error("�?� Erreur mise à jour après ajout existant:", err);
        }
      };

      if (
        typeof window !== "undefined" &&
        typeof window.requestAnimationFrame === "function"
      ) {
        window.requestAnimationFrame(() => runHeavyUpdates());
      } else {
        setTimeout(() => runHeavyUpdates(), 0);
      }
	      return true;
	    } catch (error) {
	      console.error("�?� Erreur ajout à commande existante:", error);
	      if (
	        ["TABLE_CYCLE_REQUIRED", "TABLE_CYCLE_CHANGED"].includes(
	          String(error?.code || ""),
	        ) &&
	        !isDeliveryOrder
	      ) {
	        const recoveredItems = rollbackOptimisticCartItems(
	          currentItemsSnapshot,
	          optimisticPrefix,
	        );
	        stageTableDraftRecovery({
	          tableId: resolvedTableId,
	          pendingItems: recoveredItems,
	          occupancyCycle:
	            Number(error?.currentCycle) ||
	            resolveTableOccupancyCycle(finalTable),
	          reason: "cycle-changed-during-addition",
	        });
	        return false;
	      }
	      const outcomeUnknown =
	        String(error?.code || "") === "REQUEST_OUTCOME_UNKNOWN";
	      if (outcomeUnknown) {
	        scheduleLoadTables("add-order-items-outcome-unknown");
	        setTimeout(() => {
	          void loadTableItemsForPayment(resolvedTableId, false);
	        }, 1200);
	        triggerErrorAnimation(error.message);
	        return false;
	      }
	      // Rollback UX optimiste: remettre les items en "Nouveaux Ajouts" si l'API échoue
	      try {
	        const restoredItems = rollbackOptimisticCartItems(
	          currentItemsSnapshot,
	          optimisticPrefix,
	        );
	        const restoredOrder = {
	          items: restoredItems,
	          total: computeItemsTotal(restoredItems),
	        };
	        const restoredExistingItems = existingItemsSnapshot.filter(
	          (item) =>
	            !hasOptimisticPrefix(item?.order_item_id, optimisticPrefix),
	        );
	        if (isDeliveryOrder) {
	          currentDeliveryOrderRef.current = restoredOrder;
	          existingDeliveryItemsRef.current = restoredExistingItems;
	          setCurrentDeliveryOrder(restoredOrder);
	          setExistingDeliveryItems(restoredExistingItems);
	        } else {
	          currentOrderRef.current = restoredOrder;
	          existingItemsForPaymentRef.current = restoredExistingItems;
	          setCurrentOrder(restoredOrder);
	          setExistingItemsForPayment(restoredExistingItems);
	        }
	        persistCartStateForTable(
	          finalTable,
	          restoredOrder,
	          restoredExistingItems,
	        );
	      } catch (_) {}
	      triggerErrorAnimation(
	        error?.message || "Erreur lors de l'ajout à la commande existante",
	      );
        return false;
    } finally {
      orderActionInFlightRef.current = false;
      setIsOrderActionInProgress(false);
      setOrderActionKind(null);
      setIsAddingToExistingOrder(false);
    }
  };

  const loadFallbackData = () => {
    cashDebugLog("⚠�? Utilisation des données de fallback");

    // Tables de fallback
    const fallbackTables = Array.from({ length: 12 }, (_, i) => ({
      id: i + 1,
      number: i + 1,
      status: "free",
      capacity: 4,
    }));

    // Catégories de fallback
    const fallbackCategories = [];

    // Produits de fallback
    const fallbackProducts = [];

    setTables(fallbackTables);
    setCategories(fallbackCategories);
    setProducts(fallbackProducts);
    setApiStatus("standalone");
  };

  // Fonction pour vérifier s'il y a des nouveaux articles dans le panier (après normalisation)
  const hasNewItems = useCallback(() => {
    const newCount = cartViewModel.newItems?.length || 0;
    const result = newCount > 0;
    cashDebugLog("�? hasNewItems() résultat:", {
      isDeliveryOrder: cartViewModel.isDeliveryOrder,
      newItemsCount: newCount,
      hasNew: result,
    });
    return result;
  }, [cartViewModel]);

  // Séparer les articles existants des nouveaux via le ViewModel unifié
  const getSeparatedItems = useCallback(() => {
    cashDebugLog("🔧 DIGITALIVORE FIX: getSeparatedItems (ViewModel):", {
      isDeliveryOrder: cartViewModel.isDeliveryOrder,
      newItemsCount: cartViewModel.newItems.length,
      existingItemsCount: cartViewModel.existingItems.length,
      existingInCartCount: cartViewModel.existingInCart.length,
      tableId: selectedTable?.id,
    });
    return {
      existingItems: cartViewModel.existingItems,
      newItems: cartViewModel.newItems,
    };
  }, [cartViewModel, selectedTable?.id]);

  // Fonction pour ajouter un produit (NOUVELLE VERSION - évite les incrémentations sur articles envoyés)
  const addNewItemToOrder = (product) => {
    const counterBlockMessage = getCounterMutationBlockMessage();
    if (counterBlockMessage) {
      triggerErrorAnimation(counterBlockMessage);
      return;
    }
    // Vérifier si une table est sélectionnée
    if (!selectedTable && !canOrderWithoutTable) {
      triggerErrorAnimation(
        isBoutique
          ? "Veuillez ajouter des articles au panier."
          : "Veuillez d'abord sélectionner une table !",
      );
      return;
    }

    if (
      courseModeEnabled &&
      courseModeContextForTable(selectedTable) &&
      !isCourseEditable(orderCourses, selectedDraftCourseNumber)
    ) {
      const nextCourseNumber =
        resolveNextCourseNumber(orderCourses, selectedDraftCourseNumber - 1);
      triggerErrorAnimation(
        `Le tour ${selectedDraftCourseNumber} est déjà envoyé. Créez le tour ${nextCourseNumber} avant d’ajouter des articles.`,
      );
      return;
    }

    // ✅ UX: une table ne doit devenir "occupée" qu'à partir du 1er article ajouté (même non envoyé).
    if (!canOrderWithoutTable && !selectedTable.isDelivery && selectedTable.id != null) {
      const tableFromList = Array.isArray(tables)
        ? tables.find((t) => String(t?.id) === String(selectedTable.id))
        : null;
      const currentStatus = tableFromList?.status ?? selectedTable.status;

      if (currentStatus === "free") {
        const resolvedVisitors = getVisitorCountForTable(selectedTable);

        // Mise à jour immédiate UI (grille + état sélectionné)
        setTables((prev) =>
          (prev || []).map((t) =>
            String(t?.id) === String(selectedTable.id)
              ? {
                  ...t,
                  status: "occupied",
                  customer_count:
                    resolvedVisitors ?? t.customer_count ?? t.customerCount,
                  customerCount:
                    resolvedVisitors ?? t.customerCount ?? t.customer_count,
                }
              : t,
          ),
        );
        setSelectedTable((prev) =>
          prev && String(prev.id) === String(selectedTable.id)
            ? {
                ...prev,
                status: "occupied",
                customerCount:
                  resolvedVisitors ?? prev.customerCount ?? prev.customer_count,
                customer_count:
                  resolvedVisitors ?? prev.customer_count ?? prev.customerCount,
              }
            : prev,
        );

        // Persister côté API (sans bloquer l'ajout)
        const expectedCycle =
          resolveTableOccupancyCycle(tableFromList) ??
          resolveTableOccupancyCycle(selectedTable);
        occupyTableWithLatestCycle({
          expectedCycle,
          // An article has already been drafted. A changed cycle must preserve
          // that draft, not silently attach it to the next customer's table.
          allowCycleRetry: false,
          requestOccupation: (occupancyCycle) =>
            fetch(`${API_BASE}/tables/${selectedTable.id}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                status: "occupied",
                customer_count: resolvedVisitors,
                expected_occupancy_cycle: occupancyCycle,
              }),
            }),
        })
          .then((result) => {
            if (result.ok) {
              const authoritativeCycle = Number(result.occupancyCycle);
              if (Number.isInteger(authoritativeCycle) && authoritativeCycle > 0) {
                setTables((previous) =>
                  (previous || []).map((table) =>
                    String(table?.id) === String(selectedTable.id)
                      ? {
                          ...table,
                          status: "occupied",
                          occupancy_cycle: authoritativeCycle,
                        }
                      : table,
                  ),
                );
                setSelectedTable((previous) =>
                  previous && String(previous.id) === String(selectedTable.id)
                    ? {
                        ...previous,
                        status: "occupied",
                        occupancy_cycle: authoritativeCycle,
                      }
                    : previous,
                );
              }
              return;
            }
            const payload = result.payload || {};
            if (
              ["TABLE_CYCLE_REQUIRED", "TABLE_CYCLE_CHANGED"].includes(
                String(payload?.code || ""),
              )
            ) {
              stageTableDraftRecovery({
                tableId: selectedTable.id,
                pendingItems: currentOrderRef.current?.items || [],
                occupancyCycle:
                  Number(payload?.currentCycle) ||
                  resolveTableOccupancyCycle(selectedTable),
                reason: "cycle-changed-during-entry",
              });
              return;
            }
            throw new Error(
              payload?.error || "Impossible de mettre la table à jour.",
            );
          })
          .catch((e) => {
            console.error("�?� Erreur mise à jour table occupée:", e);
          });
      }
    }

    // 🔒 Sécurité: en livraison, on a vu des produits variables perdre la variante sélectionnée.
    // Si le produit n'embarque pas la variation, on la ré-injecte depuis l'état UI (selectedVariation)
    // au moment de l'ajout (quand selectedProduct/selectedProductForExtras pointe encore sur ce produit).
    const catalogProductForAdd =
      Array.isArray(products) && product?.id != null
        ? products.find((p) => Number(p?.id) === Number(product.id))
        : null;
    const shouldInferVariationFromUi =
      product?.product_type === "variable" &&
      !(
        product?.variation_id ??
        product?.variationId ??
        product?.variation_label ??
        product?.variationLabel
      ) &&
      !!selectedVariation &&
      (Number(selectedProduct?.id) === Number(product?.id) ||
        Number(selectedProductForExtras?.id) === Number(product?.id));

    const inferredVariationPayload = shouldInferVariationFromUi
      ? resolveVariationMetadata(selectedVariation)
      : null;
    const productForAdd = inferredVariationPayload
      ? { ...product, ...inferredVariationPayload }
      : product;

    // Calculer le prix total du produit avec ses extras
    // ✅ CORRECTION: Calculer seulement le prix des suppléments payants
    const variationId =
      productForAdd.variation_id ?? productForAdd.variationId ?? null;
    const variationLabel =
      productForAdd.variation_label ??
      productForAdd.variationLabel ??
      "";
    const variationAttributes =
      productForAdd.variation_attributes ??
      productForAdd.variationAttributes ??
      null;

    const extrasPrice = productForAdd.extras
      ? productForAdd.extras.reduce((sum, extra) => {
          // Seuls les suppléments sont facturés
          if (extra.type === "supplement") {
            return (
              sum +
              (extra.type === "accompaniment" ? 0 : parseMoney(extra.price)) *
                (extra.quantity || 1)
            );
          }
          return sum; // Accompagnements gratuits
        }, 0)
      : 0;

    let productDisplayPrice = parseMoney(
      productForAdd?.price ?? productForAdd?.product_price ?? 0,
    );
    if (!productDisplayPrice) {
      productDisplayPrice = getProductDisplayPriceForMode(productForAdd, activeAggregator);
    }
    if (
      productForAdd?.product_type === "variable" &&
      (variationId || variationLabel)
    ) {
      const variations = Array.isArray(productForAdd?.variations)
        ? productForAdd.variations
        : Array.isArray(catalogProductForAdd?.variations)
          ? catalogProductForAdd.variations
          : [];
      let matchedVariation = null;

      if (variationId !== null && variationId !== undefined) {
        const targetId = Number(variationId);
        matchedVariation =
          variations.find((v) => {
            const vId = v?.variation_id ?? v?.variationId ?? v?.id ?? null;
            return vId !== null && vId !== undefined && Number(vId) === targetId;
          }) || null;
      }

      if (!matchedVariation && variationLabel) {
        const targetLabel = String(variationLabel).trim().toLowerCase();
        matchedVariation =
          variations.find((v) => {
            const name = (v?.variation_name ?? v?.variationName ?? v?.name ?? "").toString();
            return name.trim().toLowerCase() === targetLabel;
          }) || null;
      }

      if (matchedVariation) {
        productDisplayPrice = getUnitPriceForSelectionMode(
          catalogProductForAdd || productForAdd,
          matchedVariation,
          activeAggregator,
        );
      } else if (
        productForAdd?.price !== undefined &&
        productForAdd?.price !== null
      ) {
        // Si le prix a déjà été injecté par le flow de sélection, on le respecte.
        productDisplayPrice =
          parseMoney(productForAdd.price) || productDisplayPrice;
      }
    }
    if (activeAggregator && !variationId && !variationLabel) {
      productDisplayPrice = getUnitPriceForSelectionMode(
        catalogProductForAdd || productForAdd,
        null,
        activeAggregator,
      );
    }
    const totalProductPrice = productDisplayPrice + extrasPrice;

    // Créer une clé unique pour l'article incluant les extras
    const extrasKey = productForAdd.extras
      ? productForAdd.extras
          .map((extra) => `${extra.id}:${extra.quantity}`)
          .sort()
          .join("|")
      : "";
    const variationKey = variationId
      ? `var:${variationId}`
      : variationLabel
        ? `var:${variationLabel}`
        : "var:base";
    const draftCourseNumber =
      courseModeEnabled && courseModeContextForTable(selectedTable)
        ? selectedDraftCourseNumber
        : 1;
    const itemKey = `${product.id}_${variationKey}_${extrasKey}_course:${draftCourseNumber}`;

    // Séparer la logique selon le type de commande
    const isDeliveryOrder = selectedTable?.isDelivery;

    if (isDeliveryOrder) {
      // Logique pour commandes de livraison
      const identifiedDeliveryItems = ensureDraftLineIds(
        currentDeliveryOrder.items || [],
      );
      const existingNewItemIndex = identifiedDeliveryItems.findIndex(
        (item) =>
          item.itemKey === itemKey &&
          !item.order_item_id &&
          (item.isLocalDraft || !item.status),
      );

      if (existingNewItemIndex !== -1) {
        // Incrémenter la quantité de l'article existant
        const incrementBy = product.quantity || 1;
        const updatedItems = identifiedDeliveryItems.map((item, index) => {
          if (index === existingNewItemIndex) {
            return {
              ...item,
              quantity: (Number(item.quantity) || 1) + incrementBy,
            };
          }
          return item;
        });

        // Recalculer le total pour la commande de livraison
        const newTotal = updatedItems.reduce((sum, item) => {
          if (!item.isPaid) {
            const itemExtrasPrice = item.extras
              ? item.extras.reduce(
                  (extSum, ext) =>
                    extSum + (ext.price || 0) * (ext.quantity || 1),
                  0,
                )
              : 0;
            return sum + (item.price + itemExtrasPrice) * item.quantity;
          }
          return sum;
        }, 0);

        const nextDeliveryOrder = {
          ...currentDeliveryOrder,
          items: updatedItems,
          total: newTotal,
        };
        setCurrentDeliveryOrder(nextDeliveryOrder);
        persistCartStateForTable(
          selectedTable,
          nextDeliveryOrder,
          existingDeliveryItems,
        );

        // Mettre à jour l'onglet client correspondant
        if (activeClientTab) {
          setClientTabs((prev) =>
            prev.map((tab) =>
              tab.id === activeClientTab.id
                ? { ...tab, items: updatedItems, total: newTotal }
                : tab,
            ),
          );
        }
      } else {
        // Ajouter nouvel article à la commande de livraison
        const newItem = {
          ...productForAdd,
          // Un produit du catalogue peut transporter des métadonnées d'une
          // sélection précédente. L'identité serveur n'existe qu'après
          // persistance et ne doit jamais faire passer ce brouillon pour un
          // article déjà enregistré.
          orderId: null,
          order_id: null,
          orderItemId: null,
          order_item_id: null,
          isLocalDraft: true,
          price: productDisplayPrice,
          product_price: productDisplayPrice,
          quantity: productForAdd.quantity || 1,
          isPaid: false,
          status: "pending",
          itemKey: itemKey,
          client_line_id: createDraftClientLineId(),
          displayPrice: totalProductPrice,
          extras: productForAdd.extras || [],
          courseNumber: draftCourseNumber,
          course_number: draftCourseNumber,
        };
        const newItems = [...currentDeliveryOrder.items, newItem];

        const newTotal = newItems.reduce((sum, item) => {
          if (!item.isPaid) {
            const itemExtrasPrice = item.extras
              ? item.extras.reduce(
                  (extSum, ext) =>
                    extSum + (ext.price || 0) * (ext.quantity || 1),
                  0,
                )
              : 0;
            const base = item.price ?? item.product_price ?? item.displayPrice ?? 0;
            return sum + (base + itemExtrasPrice) * item.quantity;
          }
          return sum;
        }, 0);

        const nextDeliveryOrder = { items: newItems, total: newTotal };
        setCurrentDeliveryOrder(nextDeliveryOrder);
        persistCartStateForTable(
          selectedTable,
          nextDeliveryOrder,
          existingDeliveryItems,
        );

        // Mettre à jour l'onglet client correspondant
        if (activeClientTab) {
          setClientTabs((prev) =>
            prev.map((tab) =>
              tab.id === activeClientTab.id
                ? { ...tab, items: newItems, total: newTotal }
                : tab,
            ),
          );
        }
      }
    } else {
      // Logique pour commandes de tables (existante)
      const identifiedCurrentItems = ensureDraftLineIds(
        currentOrder.items || [],
      );
      const existingNewItemIndex = identifiedCurrentItems.findIndex(
        (item) =>
          item.itemKey === itemKey && !item.order_item_id && !item.status,
      );

      if (existingNewItemIndex !== -1) {
        // Incrémenter la quantité de l'article existant
        const incrementBy = product.quantity || 1;
        const updatedItems = identifiedCurrentItems.map((item, index) => {
          if (index === existingNewItemIndex) {
            return {
              ...item,
              quantity: (Number(item.quantity) || 1) + incrementBy,
            };
          }
          return item;
        });

        // Recalculer le total dynamiquement pour tous les articles non payés
        const newTotal = updatedItems.reduce((sum, item) => {
          if (!item.isPaid) {
            const itemExtrasPrice = item.extras
              ? item.extras.reduce(
                  (extSum, ext) =>
                    extSum + (ext.price || 0) * (ext.quantity || 1),
                  0,
                )
              : 0;
            return sum + (item.price + itemExtrasPrice) * item.quantity;
          }
          return sum;
        }, 0);

        cashDebugLog("🔄 Mise à jour total après ajout:", {
          itemAdded: product.name,
          newQuantity:
            updatedItems.find((item) => item.itemKey === itemKey)?.quantity ??
            incrementBy,
          newTotal,
        });

        const nextOrder = { ...currentOrder, items: updatedItems, total: newTotal };
        setCurrentOrder(nextOrder);
        persistCartStateForTable(
          selectedTable,
          nextOrder,
          existingItemsForPayment,
        );
      } else {
        // Ajouter comme nouvel article avec ses extras
        const newItem = {
          ...productForAdd,
          orderId: null,
          order_id: null,
          orderItemId: null,
          order_item_id: null,
          isLocalDraft: true,
          price: productDisplayPrice,
          product_price: productDisplayPrice,
          quantity: productForAdd.quantity || 1,
          isPaid: false,
          itemKey: itemKey,
          client_line_id: createDraftClientLineId(),
          displayPrice: totalProductPrice,
          extras: productForAdd.extras || [],
          courseNumber: draftCourseNumber,
          course_number: draftCourseNumber,
        };
        const newItems = [...currentOrder.items, newItem];

        // Recalculer le total de tous les articles non payés
        const newTotal = newItems.reduce((sum, item) => {
          if (!item.isPaid) {
            const itemExtrasPrice = item.extras
              ? item.extras.reduce(
                  (extSum, ext) =>
                    extSum + (ext.price || 0) * (ext.quantity || 1),
                  0,
                )
              : 0;
            const base = item.price ?? item.product_price ?? item.displayPrice ?? 0;
            return sum + (base + itemExtrasPrice) * item.quantity;
          }
          return sum;
        }, 0);

        cashDebugLog("➕ Nouvel article ajouté:", {
          name: newItem.name,
          price: newItem.price,
          extrasPrice:
            newItem.extras?.reduce(
              (sum, ext) => sum + (ext.price || 0) * (ext.quantity || 1),
              0,
            ) || 0,
          quantity: newItem.quantity,
          newTotal,
        });

        const nextOrder = {
          items: newItems,
          total: newTotal,
        };
        setCurrentOrder(nextOrder);
        persistCartStateForTable(
          selectedTable,
          nextOrder,
          existingItemsForPayment,
        );
      }
    }

    // Message de confirmation avec extras
    const orderType = selectedTable?.isDelivery
      ? "à la commande de livraison"
      : "au panier";
    let confirmMessage = `${product.name} ajouté ${orderType}`;
    if (product.extras && product.extras.length > 0) {
      const extrasNames = product.extras.map((extra) => extra.name).join(", ");
      confirmMessage += ` avec ${extrasNames}`;
    }
    triggerSuccessAnimation(confirmMessage);
  };

  // Fonction pour supprimer un article du panier (modifiée pour gérer la séparation)
  const removeFromOrder = (itemOrProductId, orderItemId = null) => {
    // Si c'est un article existant (avec order_item_id), on ne peut pas le modifier directement
    if (orderItemId) {
      triggerErrorAnimation(
        "Utilisez le bouton corbeille pour supprimer cet article",
      );
      return;
    }

    const isDeliveryOrder = selectedTable?.isDelivery;
    const removeOneDraftLine = (items) => {
      const identifiedItems = ensureDraftLineIds(items || []);
      const target =
        itemOrProductId && typeof itemOrProductId === "object"
          ? itemOrProductId
          : { id: itemOrProductId };
      const indexToRemove = findDraftLineIndex(identifiedItems, target);
      if (indexToRemove === -1) return identifiedItems;
      const nextItems = [...identifiedItems];
      nextItems.splice(indexToRemove, 1);
      return nextItems;
    };

    if (isDeliveryOrder) {
      // Supprimer toute la ligne pour les articles non envoyés (livraison)
      const updatedItems = removeOneDraftLine(currentDeliveryOrder.items);
      const newTotal = updatedItems.reduce((sum, currentItem) => {
        if (!currentItem.isPaid) {
          const extrasPrice = currentItem.extras
            ? currentItem.extras.reduce(
                (extSum, ext) =>
                  extSum + (ext.price || 0) * (ext.quantity || 1),
                0,
              )
            : 0;
          return (
            sum +
            (currentItem.price + extrasPrice) * (currentItem.quantity || 1)
          );
        }
        return sum;
      }, 0);

      const nextDeliveryOrder = {
        ...currentDeliveryOrder,
        items: updatedItems,
        total: newTotal,
      };
      setCurrentDeliveryOrder(nextDeliveryOrder);
      persistCartStateForTable(
        selectedTable,
        nextDeliveryOrder,
        existingDeliveryItems,
      );

      if (activeClientTab) {
        setClientTabs((prev) =>
          prev.map((tab) =>
            tab.id === activeClientTab.id
              ? { ...tab, items: updatedItems, total: newTotal }
              : tab,
          ),
        );
      }
    } else {
      // Supprimer toute la ligne pour les articles non envoyés (table)
      const updatedItems = removeOneDraftLine(currentOrder.items);
      const newTotal = updatedItems.reduce((sum, currentItem) => {
        if (!currentItem.isPaid) {
          const extrasPrice = currentItem.extras
            ? currentItem.extras.reduce(
                (extSum, ext) =>
                  extSum + (ext.price || 0) * (ext.quantity || 1),
                0,
              )
            : 0;
          return (
            sum +
            (currentItem.price + extrasPrice) * (currentItem.quantity || 1)
          );
        }
        return sum;
      }, 0);

      if (isFastFoodMode && updatedItems.filter(isItemActive).length === 0) {
        clearFastFoodCartSnapshot();
      }
      const nextOrder = { items: updatedItems, total: newTotal };
      setCurrentOrder(nextOrder);
      persistCartStateForTable(
        selectedTable,
        nextOrder,
        existingItemsForPayment,
      );
    }
  };

  // Helpers quantités/total pour les brouillons (articles non envoyés)
  const getExtrasPrice = (extras = []) =>
    extras.reduce(
      (sum, ext) =>
        sum +
        (ext.type === "accompaniment" ? 0 : (ext.price || 0)) *
          (ext.quantity || 1),
      0,
    );

  const recomputeDraftTotal = (items = []) =>
    items.reduce((sum, currentItem) => {
      if (currentItem?.isPaid) return sum;
      const extrasPrice = getExtrasPrice(currentItem.extras || []);
      return (
        sum + ((currentItem.price || 0) + extrasPrice) * (currentItem.quantity || 1)
      );
    }, 0);

  const updateDraftItemQuantity = (targetItem, delta, isDeliveryOrder) => {
    if (!targetItem || delta === 0) return;

    const nextRequestedQuantity =
      (Number(targetItem.quantity) || 1) + Number(delta);
    if (nextRequestedQuantity > 0) {
      const catalogProduct = (products || []).find(
        (product) =>
          Number(product?.id) ===
          Number(targetItem?.productId ?? targetItem?.product_id ?? targetItem?.id),
      );
      const variationId = Number(
        targetItem?.variation_id ?? targetItem?.variationId,
      );
      const catalogVariation = (catalogProduct?.variations || []).find(
        (variation) => Number(variation?.id) === variationId,
      );
      const selectedCount = countSelectedAccompaniments(targetItem.extras);
      const capacity = getLineAccompanimentCapacity({
        product: catalogProduct || targetItem,
        variation: catalogVariation,
        quantity: nextRequestedQuantity,
      });
      if (selectedCount > capacity) {
        triggerErrorAnimation(
          `Retirez ${selectedCount - capacity} accompagnement(s).`,
        );
        return;
      }
    }

    const updateFn = (prev) => {
      const sourceItems = ensureDraftLineIds(prev.items || []);
      const idx = findDraftLineIndex(sourceItems, targetItem);

      if (idx === -1) return prev;

      const items = [...sourceItems];
      const currentQty = Number(items[idx].quantity) || 1;
      const nextQty = currentQty + delta;

      if (nextQty <= 0) {
        items.splice(idx, 1);
      } else {
        items[idx] = { ...items[idx], quantity: nextQty };
      }

      const total = recomputeDraftTotal(items);
      return { ...prev, items, total };
    };

    if (isDeliveryOrder) {
      setCurrentDeliveryOrder((prev) => {
        const next = updateFn(prev);
        persistCartStateForTable(
          selectedTableRef.current,
          next,
          existingDeliveryItemsRef.current,
        );
        return next;
      });
    } else {
      setCurrentOrder((prev) => {
        const next = updateFn(prev);
        if (isFastFoodMode && (next.items || []).filter(isItemActive).length === 0) {
          clearFastFoodCartSnapshot();
        }
        if (!isFastFoodMode) {
          persistCartStateForTable(
            selectedTableRef.current,
            next,
            existingItemsForPaymentRef.current,
          );
        }
        return next;
      });
    }
  };

  // Fonction pour supprimer complètement un article (utilisée par la modal de suppression)
  const removeItemFromOrder = (itemToRemove) => {
    cashDebugLog("🔥 [DIGITALIVORE DEBUG] removeItemFromOrder DÉBUT", {
      item: itemToRemove?.name,
      status: itemToRemove?.status,
      order_item_id: itemToRemove?.order_item_id,
      id: itemToRemove?.id,
      timestamp: new Date().toISOString(),
    });
    cashDebugLog("�? removeItemFromOrder appelé avec:", itemToRemove);
    cashDebugLog("�? Statut de l'article:", itemToRemove.status);

    // Cas 1: L'article est nouveau et n'a pas encore été envoyé en cuisine/bar
    if (!itemToRemove.status) {
      cashDebugLog("✅ Suppression locale :", itemToRemove.name);
      // Filtre plus robuste pour éviter de supprimer plusieurs lignes neuves du même produit
      const identifiedItems = ensureDraftLineIds(currentOrder.items || []);
      const indexToRemove = findDraftLineIndex(
        identifiedItems,
        itemToRemove,
      );
      if (indexToRemove > -1) {
        const newItems = [...identifiedItems];
        newItems.splice(indexToRemove, 1);

        // Calculer le nouveau total avec les extras
        const newTotal = newItems.reduce((sum, item) => {
          if (!item.isPaid) {
            const itemExtrasPrice = item.extras
              ? item.extras.reduce(
                  (extSum, ext) =>
                    extSum + (ext.price || 0) * (ext.quantity || 1),
                  0,
                )
              : 0;
            return sum + (item.price + itemExtrasPrice) * item.quantity;
          }
          return sum;
        }, 0);

        if (isFastFoodMode && newItems.filter(isItemActive).length === 0) {
          clearFastFoodCartSnapshot();
        }
        const nextOrder = { items: newItems, total: newTotal };
        setCurrentOrder(nextOrder);
        persistCartStateForTable(
          selectedTable,
          nextOrder,
          existingItemsForPayment,
        );
        triggerSuccessAnimation(`${itemToRemove.name} retiré de la commande.`);
        // La vérification automatique se fera via le useEffect
      }
      return;
    }

    // Cas 2: L'article a été envoyé et est "en attente"
    if (itemToRemove.status === "pending") {
      cashDebugLog(
        "✅ Annulation d'un article en attente :",
        itemToRemove.order_item_id,
      );
      cancelOrderItem(itemToRemove);
      return;
    }

    // Cas 3: L'article est en cours de préparation ou prêt
    if (["preparing", "ready", "served"].includes(itemToRemove.status)) {
      cashDebugLog(
        "⚠�? Demande d'annulation OAA pour :",
        itemToRemove.name,
        "statut:",
        itemToRemove.status,
      );
      addDebugLog(
        "INFO",
        `Annulation OAA requise pour ${itemToRemove.name} (statut: ${itemToRemove.status})`,
      );

      const orderForCancellation = {
        id: itemToRemove.orderId,
        table_number: selectedTable.number,
        items: [{ ...itemToRemove, product_name: itemToRemove.name }],
        isPartialCancellation: true,
        requiresManagerAuth: true,
        itemToCancel: itemToRemove,
      };
      initiateCancellation(orderForCancellation);
      return;
    }

    // Cas 4: Statut inattendu
    console.error(
      "�?� Statut inattendu pour l'article:",
      itemToRemove.name,
      "statut:",
      itemToRemove.status,
    );
    triggerErrorAnimation(
      `Impossible d'annuler cet article (statut: ${itemToRemove.status})`,
    );
  };

  const cancelOrderItem = async (item) => {
    if (selectedTableAccessConflict) {
      triggerErrorAnimation(
        formatTableAccessConflictMessage(selectedTableAccessConflict),
      );
      return;
    }
    cashDebugLog("🚀 cancelOrderItem appelé avec:", item);
    addDebugLog("INFO", "Début cancelOrderItem", {
      hasItem: !!item,
      hasOrderItemId: !!item?.order_item_id,
      hasId: !!item?.id,
      status: item?.status,
      name: item?.name,
      isNewItem: !item?.order_item_id && !item?.id,
    });
    cashDebugLog("�? Diagnostic article:", {
      hasItem: !!item,
      hasOrderItemId: !!item?.order_item_id,
      hasId: !!item?.id,
      status: item?.status,
      name: item?.name,
      isNewItem: !item?.order_item_id && !item?.id,
    });

    // Vérifier si c'est un nouvel article (pas encore en base) ou un article existant
    if (!item) {
      console.error("Tentative d'annulation d'un article invalide.", item);
      triggerErrorAnimation("Impossible d'annuler cet article.");
      return;
    }

    // Si c'est un nouvel article (pas d'order_item_id), on le supprime directement du panier
    if (!item.order_item_id && !item.id) {
      cashDebugLog("✅ Suppression d'un nouvel article du panier");
      removeFromOrder(item);
      triggerSuccessAnimation(`"${item.name}" retiré du panier`);
      return;
    }

    // Pour les articles existants en base de données
    const itemId = item.order_item_id || item.id;
    if (!itemId) {
      console.error("ID d'article manquant pour l'annulation.", item);
      triggerErrorAnimation("Impossible d'identifier l'article à annuler.");
      return;
    }

    try {
      // �? VÉRIFICATION DU STATUT ACTUEL EN BASE DE DONNÉES
      cashDebugLog(`�? Vérification du statut actuel de l'article ${itemId}...`);

      const statusResponse = await fetch(
        `${API_BASE}/order-items/${itemId}/status`,
      );
      if (!statusResponse.ok) {
        throw new Error(`Impossible de vérifier le statut de l'article`);
      }

      const statusData = await statusResponse.json();
      cashDebugLog("📊 Statut actuel de l'article:", statusData);
      addDebugLog("INFO", "Statut vérifié pour article", statusData);

      // Vérifier si l'article est déjà annulé
      if (statusData.is_cancelled || statusData.status === "cancelled") {
        cashDebugLog("ℹ�? Article déjà annulé");
        addDebugLog(
          "INFO",
          "Article déjà annulé détecté - Nettoyage interface immédiat",
        );

        // CORRECTION CRITIQUE: Supprimer IMMÉDIATEMENT de tous les états React
        cashDebugLog("🗑�? NETTOYAGE IMMÉDIAT - Article:", item.name);

        // 1. Supprimer du panier actuel avec setState fonctionnel
        setCurrentOrder((prevOrder) => {
          if (!prevOrder.items || prevOrder.items.length === 0)
            return prevOrder;

          const updatedItems = prevOrder.items.filter((currentItem) => {
            const isMatchingItem = isSameOrderItemId(
              getExplicitOrderItemId(currentItem),
              item.order_item_id || item.id,
            );

            if (isMatchingItem) {
              cashDebugLog(
                "🗑�? Suppression immédiate du panier:",
                currentItem.name,
              );
              addDebugLog(
                "SUCCESS",
                `Article supprimé immédiatement du panier: ${currentItem.name}`,
              );
            }
            return !isMatchingItem;
          });

          if (updatedItems.length !== prevOrder.items.length) {
            const newTotal = updatedItems.reduce(
              (sum, item) =>
                sum + (item.totalPrice || item.price * item.quantity),
              0,
            );

            cashDebugLog("📊 Panier immédiatement mis à jour:", {
              avant: prevOrder.items.length,
              apres: updatedItems.length,
              nouveauTotal: newTotal,
            });

            return { items: updatedItems, total: newTotal };
          }

          return prevOrder;
        });

        // 2. Supprimer des articles existants
        setExistingItemsForPayment((prevItems) => {
          if (!prevItems || prevItems.length === 0) return prevItems;

          const updatedItems = prevItems.filter((existingItem) => {
            const isMatchingItem = isSameOrderItemId(
              getExplicitOrderItemId(existingItem),
              item.order_item_id || item.id,
            );

            if (isMatchingItem) {
              cashDebugLog(
                "🗑�? Suppression immédiate des existants:",
                existingItem.product_name,
              );
              addDebugLog(
                "SUCCESS",
                `Article supprimé immédiatement des existants: ${existingItem.product_name}`,
              );
            }
            return !isMatchingItem;
          });

          if (updatedItems.length !== prevItems.length) {
            cashDebugLog("📊 Articles existants immédiatement mis à jour:", {
              avant: prevItems.length,
              apres: updatedItems.length,
            });
            return updatedItems;
          }

          return prevItems;
        });

        // 3. Rechargement asynchrone sans bloquer
        setTimeout(async () => {
          if (selectedTable) {
            try {
              await loadTableItemsForPayment(selectedTable.id, false);
              await cleanupCancelledItems(selectedTable.id);
            } catch (error) {
              addDebugLog(
                "ERROR",
                "Erreur rechargement après nettoyage immédiat",
                error,
              );
            }
          }
        }, 100);

        // FORCER LE REFRESH VISUEL IMMÉDIAT
        setTimeout(() => {
          // Force React re-render
          setCurrentOrder((prev) => ({ ...prev }));
          setExistingItemsForPayment((prev) => [...prev]);
        }, 100);

        triggerSuccessAnimation("Article déjà annulé - Interface mise à jour");
        return;
      }

      // �? POLITIQUE D'ANNULATION SELON LE STATUT RÉEL
      if (statusData.requires_manager_auth) {
        // 🔒 POLITIQUE: Après validation en cuisine/bar - Autorisation manager requise
        cashDebugLog(
          "🔒 Article validé en cuisine/bar - Autorisation code manager requise",
        );
        cashDebugLog(`📊 Statut article: ${statusData.status}`);

        const cancellationOrder = {
          id: item.order_id || statusData.order_id,
          order_number: item.order_number || statusData.order_number,
          isPartialCancellation: true,
          items: [
            {
              ...item,
              id: itemId,
              status: statusData.status,
              product_name: statusData.product_name,
            },
          ],
          requiresManagerAuth: true,
          reason: `Annulation après validation (statut: ${statusData.status})`,
        };

        // Lancer le processus d'annulation OAA avec authentification manager
        cashDebugLog("🚀 Lancement processus OAA pour article validé");
        initiateCancellation(cancellationOrder);
        return; // Important: arrêter l'exécution ici
      } else {
        // ✅ POLITIQUE: Avant validation - Annulation directe par le caissier
        cashDebugLog("✅ Article non validé - Annulation directe autorisée");
        cashDebugLog(`📊 Statut article: ${statusData.status}`);

        // Confirmer l'annulation avec une simple confirmation
        const confirmMessage =
          `🗑�? ANNULATION DIRECTE\n\n` +
          `Article: ${statusData.product_name}\n` +
          `Quantité: ${item.quantity}\n` +
          `Statut: ${statusData.status}\n` +
          `Prix: ${(item.price * item.quantity).toFixed(2)} FCFA\n\n` +
          `✅ Cette annulation ne nécessite pas d'autorisation manager\n` +
          `car l'article n'a pas encore été validé en cuisine/bar.\n\n` +
          `Confirmer l'annulation ?`;

        if (window.confirm(confirmMessage)) {
          cashDebugLog(
            `📡 Envoi requête PUT vers: ${API_BASE}/order-items/${itemId}/status`,
          );
          const response = await fetch(
            `${API_BASE}/order-items/${itemId}/status`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                action: "cancel",
                reason: "Annulation caissier avant validation cuisine/bar",
              }),
            },
          );

          cashDebugLog(
            "📡 Réponse reçue:",
            response.status,
            response.statusText,
          );

          if (response.ok) {
            cashDebugLog("✅ Article annulé avec succès (annulation directe)");

            // 🧠 MÉMORISER L'ANNULATION POUR LE SYSTÈME INTELLIGENT
            memorizeRecentCancellation(itemId, selectedTable?.id, {
              productName: statusData.product_name,
              orderNumber: statusData.order_number,
              annulationType: "direct",
              reason: "Annulation caissier avant validation cuisine/bar",
            });

            triggerSuccessAnimation(
              `✅ "${statusData.product_name}" annulé avec succès`,
            );

            // Recharger les articles de la table pour affichage ET mise à jour du panier
            if (selectedTable) {
              await loadTableItemsForPayment(selectedTable.id, false);

              // CORRECTION CRITIQUE: Mettre à jour TOUS les états React
              // 1. Supprimer de existingItemsForPayment
              const updatedExistingItems = existingItemsForPayment.filter(
                (existingItem) =>
                  !isSameOrderItemId(existingItem.order_item_id, itemId),
              );
              setExistingItemsForPayment(updatedExistingItems);

              // 2. CORRECTION MANQUANTE: Supprimer aussi de currentOrder.items
              setCurrentOrder((prevOrder) => {
                const updatedCurrentItems = prevOrder.items.filter(
                  (currentItem) => {
                    const isMatchingItem = isSameOrderItemId(
                      getExplicitOrderItemId(currentItem),
                      itemId,
                    );
                    return !isMatchingItem;
                  },
                );

                const newTotal = updatedCurrentItems.reduce(
                  (sum, item) =>
                    sum + (item.totalPrice || item.price * item.quantity),
                  0,
                );

                cashDebugLog(
                  "🔄 CORRECTION: currentOrder.items mis à jour après annulation",
                  {
                    avant: prevOrder.items.length,
                    apres: updatedCurrentItems.length,
                    articleAnnule: statusData.product_name,
                  },
                );

                return {
                  items: updatedCurrentItems,
                  total: newTotal,
                };
              });

              // 3. Forcer le re-render immédiat
              setCartRenderKey((prev) => prev + 1);

            }
          } else {
            let errorMessage = "Erreur serveur";
            try {
              const errorData = await response.json();
              console.error("�?� Erreur API:", errorData);

              // GESTION SPÉCIALE: Authentification manager requise
              if (
                errorData.requiresManagerAuth ||
                errorData.error === "Authentification manager requise"
              ) {
                cashDebugLog("🔒 Authentification manager requise détectée");
                cashDebugLog("📋 Données d'erreur complètes:", errorData);
                addDebugLog(
                  "INFO",
                  "Authentification manager requise - Ouverture modal OAA",
                );

                // Créer un objet commande pour l'annulation OAA
                const oaaCancellationOrder = {
                  id: item.order_id || statusData.order_id,
                  order_number: item.order_number || statusData.order_number,
                  isPartialCancellation: true,
                  items: [
                    {
                      ...item,
                      id: itemId,
                      status: errorData.itemStatus || statusData.status,
                      product_name: statusData.product_name,
                    },
                  ],
                  requiresManagerAuth: true,
                  reason: `Annulation OAA requise (statut: ${errorData.itemStatus || statusData.status})`,
                };

                cashDebugLog(
                  "🎯 Objet commande OAA créé:",
                  oaaCancellationOrder,
                );
                addDebugLog(
                  "INFO",
                  "Objet commande OAA créé",
                  oaaCancellationOrder,
                );

                // FORCER l'ouverture du modal OAA en commençant par le motif
                setCancellationOrder(oaaCancellationOrder);
                setCancellationStep("reason");
                setCancellationReason("");
                setShowCancellationModal(true);

                addDebugLog("SUCCESS", "Modal OAA forcé à s'ouvrir");
                cashDebugLog(
                  "🚀 Modal OAA forcé - États mis à jour directement",
                );

                return; // Important: arrêter l'exécution ici
              }

              errorMessage =
                errorData.error ||
                `Erreur ${response.status}: ${response.statusText}`;
            } catch (jsonError) {
              console.error("�?� Erreur parsing JSON:", jsonError);
              errorMessage = `Erreur ${response.status}: ${response.statusText}`;

              // Si c'est une erreur 404, c'est probablement une route non trouvée
              if (response.status === 404) {
                errorMessage = `Route non trouvée: ${API_BASE}/order-items/${itemId}/status. Vérifiez que le serveur est démarré.`;
              }
            }
            throw new Error(errorMessage);
          }
        } else {
          cashDebugLog("�?� Annulation directe annulée par l'utilisateur");
        }
      }
    } catch (error) {
      console.error(
        `�?� Erreur lors de l'annulation de l'article ${itemId}:`,
        error,
      );
      triggerErrorAnimation(`Erreur! ${error.message}`);
    }
  };

  // updateItemQuantity supprimée car non utilisée

  const addToOrderWithQuantity = (quantity) => {
    if (!selectedProduct) return;

    // Vérifier si une table est sélectionnée
    if (!selectedTable && !canOrderWithoutTable) {
      triggerErrorAnimation(
        isBoutique
          ? "Veuillez ajouter des articles au panier."
          : "Veuillez d'abord sélectionner une table !",
      );
      return;
    }

    const requiresVariationSelection =
      selectedProduct.product_type === "variable" &&
      Array.isArray(selectedProduct.variations) &&
      selectedProduct.variations.length > 0;

    const variationPayload =
      selectedProduct.product_type === "variable" && selectedVariation
        ? resolveVariationMetadata(selectedVariation)
        : null;

    if (requiresVariationSelection && !variationPayload) {
      triggerErrorAnimation("Veuillez sélectionner une variante");
      return;
    }

    const basePrice =
      selectedProduct.product_type === "variable"
        ? getUnitPriceForSelectionMode(
            selectedProduct,
            selectedVariation,
            activeAggregator,
          )
        : getProductDisplayPriceForMode(selectedProduct, activeAggregator);

    // Créer le produit avec ses extras sélectionnés
    const productToAdd = {
      ...selectedProduct,
      price: basePrice,
      product_price: basePrice,
      quantity: quantity,
      extras: selectedExtras.map((extra) => ({
        ...extra,
        quantity: extra.selectedQuantity || 1,
      })),
    };

    if (variationPayload) {
      Object.assign(productToAdd, variationPayload);
    }

    // Utiliser la fonction addNewItemToOrder qui gère déjà les extras
    addNewItemToOrder(productToAdd);

    setShowQuantityModal(false);
    setSelectedProduct(null);
    setSelectedExtras([]); // Réinitialiser les extras sélectionnés
  };

  const normalizedSearchQuery = useMemo(
    () => (deferredSearchTerm ? deferredSearchTerm.trim().toLowerCase() : ""),
    [deferredSearchTerm],
  );

  const filteredProducts = useMemo(() => {
    let filtered = Array.isArray(products)
      ? products.filter((product) => product?.is_available)
      : [];

    if (normalizedSearchQuery) {
      filtered = filtered.filter((product) => {
        const name = String(product?.name || "").toLowerCase();
        const productSku = String(
          product?.sku || product?.product_sku || "",
        ).toLowerCase();
        const variationMatch = Array.isArray(product?.variations)
          ? product.variations.some((variation) => {
              const variationSku = String(
                variation?.sku ||
                  variation?.variation_sku ||
                  variation?.sku_variation ||
                  "",
              ).toLowerCase();
              return (
                variationSku && variationSku.includes(normalizedSearchQuery)
              );
            })
          : false;

        return (
          name.includes(normalizedSearchQuery) ||
          (productSku && productSku.includes(normalizedSearchQuery)) ||
          variationMatch
        );
      });
    }

    if (!showAllCategories && selectedCategory !== "all") {
      filtered = filtered.filter(
        (product) => product.category_id === selectedCategory,
      );
    }

    return filtered;
  }, [products, normalizedSearchQuery, showAllCategories, selectedCategory]);

  const activeCategories = useMemo(
    () => categories.filter((category) => isCategoryActive(category?.is_active)),
    [categories, isCategoryActive],
  );

  const stewardGroups = stewardModeEnabled
    ? [
        ...(stewardOverview.stewards || []).map((group) => ({
          ...group,
          key: `steward:${group.id}`,
        })),
        ...(stewardOverview.unassigned
          ? [{ ...stewardOverview.unassigned, key: "unassigned" }]
          : []),
      ]
    : [];
  const selectedStewardGroup = stewardModeEnabled
    ? stewardGroups.find((group) => group.key === selectedStewardKey) || null
    : null;
  const selectedStewardTableIds = new Set(
    (selectedStewardGroup?.tables || []).map((table) => String(table.id)),
  );
  const stewardTransferTargets = getStewardTransferTargets({
    enabled: stewardModeEnabled,
    groups: stewardGroups,
    sourceTableId: selectedTable?.id,
  });

  const visibleTableCards = (floorPlanHydrated ? tables : [])
    .filter((table) => !fusedAwayTables.has(table.id) && table.number !== 999)
    .filter(
      (table) =>
        !stewardModeEnabled ||
        (selectedStewardGroup && selectedStewardTableIds.has(String(table.id))),
    )
    .filter(
      (table) =>
        !floorPlanEnabled ||
        stewardModeEnabled ||
        (selectedZoneId &&
          String(table.zone_id || "") === String(selectedZoneId)),
    )
    .map((table) => {
      if (!table?.id || table.status === "maintenance") return table;

      const tableId = table.id;
      const tableKey = String(tableId);
      const isDelivery = Boolean(table.isDelivery);

      const selectedMatches =
        selectedTable &&
        String(selectedTable.id) === tableKey &&
        Boolean(selectedTable.isDelivery) === isDelivery;
      const selectedPendingItems = selectedMatches
        ? isDelivery
          ? currentDeliveryOrder.items || []
          : currentOrder.items || []
        : [];
      const selectedExistingItems = selectedMatches
        ? isDelivery
          ? existingDeliveryItems || []
          : existingItemsForPayment || []
        : [];
      const cachedState =
        tableStatesCache.get(tableId) ||
        tableStatesCache.get(tableKey) ||
        tableStatesCacheRef.current.get(tableId) ||
        tableStatesCacheRef.current.get(tableKey);
      const cachedPendingItems = (cachedState?.currentOrder?.items || []).filter(
        isItemActive,
      );
      const cachedExistingItems = normalizeItemsForExisting(
        cachedState?.existingItems || [],
      ).filter(isItemActive);
      const persistedState = persistentCartCacheRef.current.get(
        buildPersistentCartKey(tableId, isDelivery),
      );
      const persistedPendingItems = (persistedState?.pendingItems || []).filter(
        isItemActive,
      );
      const persistedExistingItems = normalizeItemsForExisting(
        persistedState?.items || [],
      ).filter(isItemActive);
      const isRecoveredFreeTable =
        !isDelivery &&
        Boolean(
          persistedState?.recoveryRequired ||
            draftRecoveryTableIdsRef.current.has(tableKey),
        );
      const recoveryPendingCount = isRecoveredFreeTable
        ? persistedPendingItems.reduce(
            (sum, item) => sum + Math.max(1, Number(item?.quantity) || 1),
            0,
          )
        : 0;
      const pendingStateCount =
        selectedPendingItems.filter(isItemActive).length +
        cachedPendingItems.length +
        persistedPendingItems.length;
      const existingStateCount =
        normalizeItemsForExisting(selectedExistingItems).filter(isItemActive).length +
        cachedExistingItems.length +
        persistedExistingItems.length;
      const protection = getTableClearProtection(tableId, {
        isDelivery,
      });
      const activeLocalCount = resolveLocalCartOccupancyCount(table, {
        activeCount:
          pendingStateCount +
          existingStateCount +
          protection.activeLocalCount,
        pendingCount: protection.pendingCount,
      });
      if (recoveryPendingCount > 0) {
        return {
          ...table,
          recovery_pending_count: recoveryPendingCount,
        };
      }
      if (activeLocalCount <= 0 || table.status === "occupied") {
        return table;
      }

      return { ...table, status: "occupied" };
    })
    .sort((a, b) => Number(a.number || 0) - Number(b.number || 0));

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage, productsPerPage]);

  const totalPages = useMemo(
    () => Math.ceil(filteredProducts.length / productsPerPage),
    [filteredProducts.length, productsPerPage],
  );

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, showAllCategories]);

  // Calculs de paiement
  const getSubtotal = () => subtotalValue;

  // Fonction de diagnostic pour le panier
  const diagnoseCartTotal = () => {
    const subtotalCalculated = getSubtotal();
    const subtotalWithExisting = getSubtotalWithExisting();

    const isDeliveryOrder = selectedTable?.isDelivery;
    const currentItems = isDeliveryOrder
      ? currentDeliveryOrder.items
      : currentOrder.items;
    const existingItems = isDeliveryOrder
      ? normalizedExistingDeliveryItems
      : existingItemsForPayment;

    cashDebugLog("�? DIAGNOSTIC PANIER TOTAL:", {
      orderType: isDeliveryOrder ? "livraison" : "table",
      currentOrderTotal: isDeliveryOrder
        ? currentDeliveryOrder.total
        : currentOrder.total,
      calculatedSubtotal: subtotalCalculated,
      subtotalWithExisting: subtotalWithExisting,
      itemsCount: currentItems?.length || 0,
      existingItemsCount: existingItems?.length || 0,
      detailedCurrentItems:
        currentOrder.items?.map((item) => ({
          name: item.name,
          quantity: item.quantity,
          basePrice: item.price,
          extras:
            item.extras?.map((ext) => ({
              name: ext.name,
              price: ext.price,
              quantity: ext.quantity || 1,
              total: (ext.price || 0) * (ext.quantity || 1),
            })) || [],
          extrasTotal:
            item.extras?.reduce(
              (sum, ext) => sum + (ext.price || 0) * (ext.quantity || 1),
              0,
            ) || 0,
          itemTotal:
            (item.price +
              (item.extras?.reduce(
                (sum, ext) => sum + (ext.price || 0) * (ext.quantity || 1),
                0,
              ) || 0)) *
            item.quantity,
          isPaid: item.isPaid,
        })) || [],
      detailedExistingItems:
        existingItemsForPayment?.map((item) => ({
          name: item.name,
          quantity: item.quantity,
          basePrice: item.price,
          dbTotalPrice: item.totalPrice || item.total_price,
          extras:
            item.extras?.map((ext) => ({
              name: ext.name,
              price: ext.price,
              quantity: ext.quantity || 1,
              total: (ext.price || 0) * (ext.quantity || 1),
            })) || [],
          extrasTotal:
            item.extras?.reduce(
              (sum, ext) => sum + (ext.price || 0) * (ext.quantity || 1),
              0,
            ) || 0,
          calculatedTotal:
            (item.price +
              (item.extras?.reduce(
                (sum, ext) => sum + (ext.price || 0) * (ext.quantity || 1),
                0,
              ) || 0)) *
            item.quantity,
          discrepancy: Math.abs(
            (item.totalPrice || item.total_price || 0) -
              (item.price +
                (item.extras?.reduce(
                  (sum, ext) => sum + (ext.price || 0) * (ext.quantity || 1),
                  0,
                ) || 0)) *
                item.quantity,
          ),
        })) || [],
      expectedTotal:
        currentOrder.items?.reduce((sum, item) => {
          if (!item.isPaid) {
            const extrasTotal =
              item.extras?.reduce(
                (extSum, ext) =>
                  extSum + (ext.price || 0) * (ext.quantity || 1),
                0,
              ) || 0;
            return sum + (item.price + extrasTotal) * item.quantity;
          }
          return sum;
        }, 0) || 0,
    });
  };

  const subtotalValue = useMemo(() => {
    const isDeliveryOrder = selectedTable?.isDelivery;
    const items = isDeliveryOrder
      ? currentDeliveryOrder.items
      : currentOrder.items;

    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      if (item.isPaid) return sum;
      const itemExtrasPrice = item.extras
        ? item.extras.reduce(
            (extSum, ext) => extSum + (ext.price || 0) * (ext.quantity || 1),
            0,
          )
        : 0;
      const itemTotal = (item.price + itemExtrasPrice) * item.quantity;
      return sum + itemTotal;
    }, 0);
  }, [selectedTable?.isDelivery, currentDeliveryOrder.items, currentOrder.items]);

  // Quantité totale d'articles (toutes lignes confondues) pour la table courante
  const cartTotalQuantity = useMemo(() => {
    const isDeliveryOrder = !!selectedTable?.isDelivery;
    const baseItems = isDeliveryOrder
      ? currentDeliveryOrder.items || []
      : currentOrder.items || [];
    const existingItems = isDeliveryOrder
      ? existingDeliveryItems || []
      : existingItemsForPayment || [];
    return [...baseItems, ...existingItems].reduce(
      (sum, item) => sum + (Number(item.quantity) || 1),
      0,
    );
  }, [
    selectedTable?.isDelivery,
    currentDeliveryOrder.items,
    currentOrder.items,
    existingDeliveryItems,
    existingItemsForPayment,
  ]);

  // Calculer le total incluant les articles existants non payés
  const subtotalWithExistingValue = useMemo(() => {
    const newItemsTotal = subtotalValue;
    const isDeliveryOrder = selectedTable?.isDelivery;
    const existingItems = isDeliveryOrder
      ? normalizedExistingDeliveryItems
      : existingItemsForPayment;

    const existingItemsTotal = existingItems.reduce((sum, item) => {
      const dbTotal = item.totalPrice || item.total_price || 0;
      const extrasPrice = item.extras
        ? item.extras.reduce(
            (extSum, ext) => extSum + (ext.price || 0) * (ext.quantity || 1),
            0,
          )
        : 0;
      const calculatedTotal = (item.price + extrasPrice) * item.quantity;
      const finalAmount =
        dbTotal > 0 && Math.abs(dbTotal - calculatedTotal) < 10
          ? dbTotal
          : calculatedTotal;
      return sum + finalAmount;
    }, 0);

    let total = newItemsTotal + existingItemsTotal;
    if (currentOrder.items.length > 0 && existingItemsForPayment.length > 0) {
      const hasExistingItemsInCart = currentOrder.items.some(
        (item) => item.order_item_id,
      );
      if (hasExistingItemsInCart) {
        total = newItemsTotal;
      }
    }
    return total;
  }, [
    subtotalValue,
    selectedTable?.isDelivery,
    normalizedExistingDeliveryItems,
    existingItemsForPayment,
    currentOrder.items,
  ]);

  const getCartTotalQuantity = useCallback(
    () => cartTotalQuantity,
    [cartTotalQuantity],
  );

  const getSubtotalWithExisting = useCallback(
    () => subtotalWithExistingValue,
    [subtotalWithExistingValue],
  );

  const getDiscountValue = () => {
    if (discountType === "percentage") {
      return (getSubtotalWithExisting() * discountAmount) / 100;
    }
    return discountAmount;
  };

  const getFeesValue = () => {
    if (feesType === "percentage") {
      return (getSubtotalWithExisting() * additionalFees) / 100;
    }
    return additionalFees;
  };

  const getTotal = () => {
    const subtotal = getSubtotalWithExisting();
    const legacyDiscount = Math.max(0, Number(getDiscountValue()) || 0);
    const adjustmentsDiscount = Math.max(0, Number(getDiscountTotal()) || 0);
    const fees = Math.max(0, Number(getFeesValue()) || 0);
    const total = subtotal - legacyDiscount - adjustmentsDiscount + fees;
    return Math.max(0, total);
  };

  // Génération de facture
  const generateInvoice = () => {
    const invoice = {
      number: `FAC-${Date.now()}`,
      date: new Date(),
      table: selectedTable,
      items: currentOrder.items,
      subtotal: getSubtotal(),
      discount: getDiscountValue(),
      fees: getFeesValue(),
      total: getTotal(),
    };

    // Ici on pourrait générer un PDF ou imprimer
    cashDebugLog("Facture générée:", invoice);
    setShowInvoiceModal(true);
  };

  const removeDraftItemsAfterSave = (itemsToRemove, isDeliveryContext) => {
    if (!itemsToRemove || itemsToRemove.length === 0) {
      return;
    }
    const removalSet = new Set(itemsToRemove);

    if (isDeliveryContext) {
      setCurrentDeliveryOrder((prev) => {
        if (!prev) return prev;
        const remainingItems = (prev.items || []).filter(
          (item) => !removalSet.has(item),
        );
        return {
          ...prev,
          items: remainingItems,
          total: computeItemsTotal(remainingItems),
        };
      });
    } else {
      setCurrentOrder((prev) => {
        if (!prev) return prev;
        const remainingItems = (prev.items || []).filter(
          (item) => !removalSet.has(item),
        );
        return {
          ...prev,
          items: remainingItems,
          total: computeItemsTotal(remainingItems),
        };
      });
    }
  };

  // Traitement du paiement - FONCTION SIMPLIFIÉE ET CORRIGÉE
	  const processPayment = async ({ splitPaymentContext = null } = {}) => {
	    cashDebugLog("🚀 Début processPayment");
	    cashDebugLog("📊 État actuel:", {
	      selectedTable: selectedTable?.id,
	      currentOrderItems: currentOrder.items.length,
	      total: getTotal(),
	    });

    if (paymentPreparationInFlightRef.current || showPaymentInterface) {
      return false;
    }

    if (selectedTableAccessConflict) {
      triggerErrorAnimation(
        formatTableAccessConflictMessage(selectedTableAccessConflict),
      );
      return false;
    }

    if (isSelectedTableReconciliationInFlight()) {
      triggerErrorAnimation("Resynchronisation de la table en cours…");
      return false;
    }

    if (orderActionInFlightRef.current || isOrderActionInProgress) {
      triggerErrorAnimation(
        "Veuillez patienter pendant l'envoi/ajout des articles avant de lancer le paiement.",
      );
      return false;
    }

    // Validations de base
    if (!selectedTable && !canOrderWithoutTable) {
      triggerErrorAnimation(
        isBoutique
          ? "Veuillez ajouter des articles au panier."
          : "Veuillez sélectionner une table !",
      );
      return false;
    }

    const isAggregatorPayment = Boolean(activeAggregator);
    const isDeliveryOrder =
      isAggregatorPayment || (!isFastFoodMode && selectedTable?.isDelivery);
    const currentItems = selectedTable?.isDelivery
      ? currentDeliveryOrder.items
      : currentOrder.items;
    const visibleExistingItems = selectedTable?.isDelivery
      ? existingDeliveryItems
      : existingItemsForPayment;
    const hasVisibleExistingItems = (visibleExistingItems || []).some(
      isItemActive,
    );
    const selectedTableApiId =
      isFastFoodMode || isAggregatorPayment ? null : getApiTableId(selectedTable);
    const effectiveDeliveryMethod = isDeliveryOrder
      ? activeAggregator || selectedTable?.deliveryMethod || deliveryMethod || "Direct"
      : null;
    const effectiveDeliveryMode = isDeliveryOrder
      ? selectedTable?.deliveryMode || deliveryMode || "delivery"
      : null;
    const effectiveDeliveryCustomer = isDeliveryOrder
      ? resolveDeliveryCustomer({
          table: selectedTable,
          method: effectiveDeliveryMethod,
        })
      : null;

    if (
      (currentItems || []).filter(isItemActive).length === 0 &&
      !hasVisibleExistingItems &&
      !hasExistingOrders
    ) {
      triggerErrorAnimation("Aucune commande à payer !");
      return false;
    }

    paymentPreparationInFlightRef.current = true;
    setIsPreparingPayment(true);

	    try {
	      // ✅ IMPORTANT POS: ne pas “envoyer�?/persister les drafts à l’ouverture du modal.
	      // La création d’une commande pour les drafts se fait au moment de la confirmation du paiement
	      // (dans PaymentInterface), afin de garder les items en “draft�? si le caissier annule.
	      let allOrderIds = [];

	      // Ajouter les IDs des commandes non payées (local + API pour fiabilité)
	      const existingOrderIdsLocal = (
	        isDeliveryOrder
          ? normalizedExistingDeliveryItems
          : existingItemsForPayment
      )
        .map((item) => item.order_id || item.orderId)
        .filter(Boolean);

      let existingOrderIdsApi = [];
      if (isDeliveryOrder && !isAggregatorPayment) {
        existingOrderIdsApi = getSelectedDeliveryOrderIds(selectedTable);
      } else if (!isFastFoodMode && selectedTableApiId) {
        try {
          const resp = await fetchWithTimeout(
            `${API_BASE}/tables/${selectedTableApiId}/orders`,
            {},
            5000,
          );
          if (resp.ok) {
            const orders = await resp.json();
            existingOrderIdsApi = orders
              .filter(
                (o) => o.payment_status !== "paid" && o.status !== "cancelled",
              )
              .map((o) => o.id);
          }
        } catch (e) {
          console.warn(
            "⚠�? Impossible de charger les commandes non payées via l'API:",
            e?.message || e,
          );
        }
      }

      allOrderIds = Array.from(
        new Set([
          ...allOrderIds,
          ...existingOrderIdsLocal,
          ...existingOrderIdsApi,
        ]),
      );

      if (isDeliveryOrder && !isAggregatorPayment && allOrderIds.length === 0) {
        const fallbackDeliveryOrderIds = getSelectedDeliveryOrderIds(
          selectedTable,
        );
        allOrderIds = fallbackDeliveryOrderIds.length
          ? fallbackDeliveryOrderIds
          : allOrderIds;
      }

      cashDebugLog("📋 IDs des commandes à payer:", allOrderIds);

      // Préparer l'ordre pour le paiement avec le nouveau PaymentInterface
      const isDeliveryPayment =
        isAggregatorPayment || (!isFastFoodMode && Boolean(selectedTable?.isDelivery));
      const deliveryCustomerForPayment = isDeliveryPayment
        ? resolveDeliveryCustomer({
            table: selectedTable,
            method:
              activeAggregator || selectedTable?.deliveryMethod || deliveryMethod || "Direct",
            reference:
              selectedTable?.deliveryRef ||
              deliveryRef ||
              selectedTable?.deliveryOrder?.deliveryRef ||
              null,
          })
        : null;
      const fallbackCustomer = deliveryCustomerForPayment || selectedCustomerForOrder || null;
      const resolvedCustomerName =
        formatDeliveryCustomerName(fallbackCustomer) || null;
      const resolvedCustomerPhone =
        (fallbackCustomer &&
          (fallbackCustomer.phone ||
            fallbackCustomer.contact ||
            fallbackCustomer.tel)) ||
        null;
      const tableLabelForPayment = isAggregatorPayment
        ? AGGREGATOR_ORDER_META[activeAggregator]?.label || "Agrégateur"
        : isFastFoodMode
        ? "Comptoir"
        : isDeliveryPayment
        ? "Livraison"
        : `Table ${getTableDisplayName(selectedTable)}`;
      const deliveryReference =
        selectedTable?.deliveryRef ||
        selectedTable?.delivery_reference ||
        deliveryRef ||
        null;
      const getKnownPaymentItemKey = (item) => {
        if (!item || typeof item !== "object") return null;
        const orderItemId =
          item.order_item_id ??
          item.orderItemId ??
          item.order_itemId ??
          null;
        if (orderItemId != null && `${orderItemId}`.trim() !== "") {
          return `oi:${orderItemId}`;
        }

        const stableItemKey =
          item.itemKey ?? item.item_key ?? item.client_item_key ?? null;
        if (stableItemKey) return `k:${stableItemKey}`;

        return null;
      };

      const dedupeItemsForPayment = (items = []) => {
        const keyedItems = new Map();
        const unkeyedItems = [];

        (items || []).forEach((item) => {
          if (!item) return;
          const key = getKnownPaymentItemKey(item);
          if (!key) {
            unkeyedItems.push(item);
            return;
          }
          if (!keyedItems.has(key)) {
            keyedItems.set(key, item);
          }
        });

        return [...keyedItems.values(), ...unkeyedItems];
      };

	      const gatherItemsForPayment = () => {
	        if (isDeliveryPayment && !isAggregatorPayment) {
	          const sources = [
	            currentDeliveryOrder?.items,
	            existingDeliveryItems,
	            selectedTable?.items,
	            selectedTable?.deliveryOrder?.items,
	          ];
	          const deduped = new Map();
	          const buildDeliveryItemKey = (item) => {
	            if (!item || typeof item !== "object") return null;
	
	            const orderItemId =
	              item.order_item_id ??
	              item.orderItemId ??
	              item.order_itemId ??
	              null;
	            if (orderItemId != null) return `oi:${orderItemId}`;
	
	            const stableItemKey =
	              item.itemKey ?? item.item_key ?? item.client_item_key ?? null;
	            if (stableItemKey) return `k:${stableItemKey}`;
	
	            const localId = item.id ?? item.local_id ?? item.temp_id ?? null;
	            if (localId != null) return `id:${localId}`;
	
	            const productId =
	              item.product_id ??
	              item.productId ??
	              item.productID ??
	              item.product ??
	              null;
	            const variationId =
	              item.variation_id ??
	              item.variationId ??
	              item.variationID ??
	              extractVariationKeyFromItemKey(item.itemKey) ??
	              null;
	            const variationAttrs =
	              item.variation_attributes ??
	              item.variationAttributes ??
	              null;
	            const variationAttrsKey =
	              variationAttrs && typeof variationAttrs === "object"
	                ? JSON.stringify(variationAttrs)
	                : variationAttrs != null
	                  ? String(variationAttrs)
	                  : "";
	            const extrasKey = Array.isArray(item.extras)
	              ? JSON.stringify(
	                  item.extras.map((e) => ({
	                    id: e?.id ?? null,
	                    name: e?.name ?? null,
	                    type: e?.type ?? null,
	                    quantity: e?.quantity ?? 1,
	                  })),
	                )
	              : "";
	
	            const createdAt =
	              item.created_at ?? item.createdAt ?? item.created ?? "";
	            const status = item.status ?? item.item_status ?? "pending";
	            const name = item.name || item.product_name || item.label || "item";
	
	            return `p:${productId ?? name}|v:${variationId ?? ""}|a:${variationAttrsKey}|e:${extrasKey}|s:${status}|t:${createdAt}`;
	          };
	          sources.forEach((list) => {
	            if (Array.isArray(list)) {
	              list.forEach((item) => {
	                if (!item) return;
	                const key = buildDeliveryItemKey(item);
	                if (!deduped.has(key)) {
	                  deduped.set(key, {
	                    ...item,
	                    quantity: item.quantity || 1,
                    price:
                      item.price !== undefined
                        ? item.price
                        : item.unitPrice !== undefined
                          ? item.unitPrice
                          : item.product_price || 0,
                    name: item.name || item.product_name || item.label || "",
                    extras: Array.isArray(item.extras)
                      ? item.extras
                      : item.extras_json
                        ? (() => {
                            try {
                              return JSON.parse(item.extras_json);
                            } catch (_) {
                              return [];
                            }
                          })()
                        : [],
                  });
                }
              });
            }
          });
	          if (deduped.size > 0) {
	            return Array.from(deduped.values());
	          }
	        }
        return dedupeItemsForPayment([
	          ...(currentOrder.items || []),
	          ...(existingItemsForPayment || []),
	        ]);
	      };

	      const gatheredItemsForPayment = gatherItemsForPayment();
        const isDraftPaymentItem = (item) =>
          item &&
          !item.order_item_id &&
          !item.orderItemId &&
          !item.order_itemId;
        const splitQuoteItems = splitPaymentContext
          ? (splitPaymentContext.quote?.items || []).map((item) => ({
              ...item,
              id: item.id,
              order_item_id: item.id,
              name: item.display_name || item.product_name || "Article",
              price: Number(item.unit_price || 0),
              totalPrice: Number(item.line_total || 0),
              total_price: Number(item.line_total || 0),
            }))
          : [];
        const draftItemsForSplit = splitPaymentContext
          ? gatheredItemsForPayment.filter(isDraftPaymentItem)
          : [];
        const paymentItemSource = splitPaymentContext
          ? [...splitQuoteItems, ...draftItemsForSplit]
          : gatheredItemsForPayment;
	      const itemsForPayment = paymentItemSource.map((item) => ({
          ...item,
          client_line_id:
            item?.client_line_id ||
            item?.clientLineId ||
            (isDraftPaymentItem(item) ? createDraftClientLineId() : null),
        }));

	      const draftSubtotalForSplit = computeItemsTotal(draftItemsForSplit);
	      const subtotalForPayment = splitPaymentContext
          ? Number(splitPaymentContext.quote?.subtotal || 0) + draftSubtotalForSplit
          : computeItemsTotal(itemsForPayment);
        const explicitPaymentAdjustments = (tempAdjustments || [])
          .filter(
            (adjustment) =>
              !adjustment?.draftContext ||
              areInvoiceAdjustmentContextsCompatible(
                adjustment.draftContext,
                invoiceAdjustmentContext,
              ),
          )
          .map(
          (adjustment) => {
            const target =
              adjustment?.scope === "item"
                ? itemsForPayment.find(
                    (item, index) =>
                      getInvoiceItemKey(item, index) === adjustment.targetItemId,
                  )
                : null;
            return {
              type: adjustment?.type || "discount",
              scope: adjustment?.scope || "cart",
              unit: adjustment?.unit || "amount",
              value: Number(adjustment?.value) || 0,
              amount: Number(adjustment?.amount) || 0,
              target_quantity: Number(adjustment?.targetQuantity) || null,
              reason: adjustment?.reason || "",
              target_label: adjustment?.targetLabel || null,
              requires_reason: true,
              order_item_id:
                target?.order_item_id ??
                target?.orderItemId ??
                target?.order_itemId ??
                null,
              client_line_id:
                target?.client_line_id ?? target?.clientLineId ?? null,
            };
          },
        );
        if (Number(discountAmount) > 0) {
          explicitPaymentAdjustments.push({
            type: "discount",
            scope: "cart",
            unit: discountType === "percentage" ? "percentage" : "amount",
            value: Number(discountAmount) || 0,
            amount: Math.max(0, Number(getDiscountValue()) || 0),
            reason: "",
            requires_reason: false,
          });
        }
        if (Number(additionalFees) > 0) {
          explicitPaymentAdjustments.push({
            type: "fee",
            scope: "cart",
            unit: feesType === "percentage" ? "percentage" : "amount",
            value: Number(additionalFees) || 0,
            amount: Math.max(0, Number(getFeesValue()) || 0),
            reason: "",
            requires_reason: false,
          });
        }
	      const draftClientLineIdsForSplit = new Set(
	        draftItemsForSplit
	          .map((item) => String(item?.client_line_id ?? item?.clientLineId ?? ""))
	          .filter(Boolean),
	      );
	      const draftAdjustmentsForSplit = splitPaymentContext
	        ? explicitPaymentAdjustments.filter(
	            (adjustment) =>
	              adjustment?.scope === "item" &&
	              !adjustment?.order_item_id &&
	              draftClientLineIdsForSplit.has(
	                String(adjustment?.client_line_id || ""),
	              ),
	          )
	        : [];
	      const draftDiscountsForSplit = draftAdjustmentsForSplit
	        .filter((adjustment) => adjustment?.type === "discount")
	        .reduce((sum, adjustment) => sum + Math.max(0, Number(adjustment?.amount) || 0), 0);
	      const draftFeesForSplit = draftAdjustmentsForSplit
	        .filter((adjustment) => adjustment?.type === "fee")
	        .reduce((sum, adjustment) => sum + Math.max(0, Number(adjustment?.amount) || 0), 0);
	      const splitAdjustmentsForPayment = splitPaymentContext
	        ? [
	            ...(splitPaymentContext.quote?.adjustments || []),
	            ...draftAdjustmentsForSplit,
	          ]
	        : [];
	      const totalDiscountsForPayment =
	        Math.max(0, Number(getDiscountTotal()) || 0) +
	        Math.max(0, Number(getDiscountValue()) || 0);
        const totalFeesForPayment = Math.max(
          0,
          Number(getFeesValue()) || 0,
        );
	      const totalAmountForPayment = splitPaymentContext
          ? Math.max(
              0,
              Number(splitPaymentContext.quote?.amount_due || 0) +
                draftSubtotalForSplit -
	              draftDiscountsForSplit +
	              draftFeesForSplit,
            )
          : Math.max(
	            0,
	            subtotalForPayment - totalDiscountsForPayment + totalFeesForPayment,
	          );

        const counterRecovery = isFastFoodMode
          ? buildFastFoodCartSnapshot(itemsForPayment)
          : null;
        if (counterRecovery) {
          fastFoodCartSnapshotRef.current = counterRecovery;
          writeFastFoodCartSnapshotToStorage(counterRecovery);
        }

	      const deliveryOrderIds = isDeliveryPayment && !isAggregatorPayment
	        ? getSelectedDeliveryOrderIds(selectedTable)
	        : [];
	      const resolvedOrderIds = splitPaymentContext
          ? splitPaymentContext.quote?.order_ids || allOrderIds
          : allOrderIds.length
            ? allOrderIds
            : deliveryOrderIds;
      const primaryDeliveryOrderId =
        resolvedOrderIds[0] ||
        selectedTable?.orderId ||
        selectedTable?.deliveryOrder?.id ||
        null;

		      const orderForPayment = {
		        id: isDeliveryPayment ? primaryDeliveryOrderId : null,
		        order_number: isAggregatorPayment
              ? `${activeAggregator.toUpperCase()}-${Date.now().toString(36)}`
              : isFastFoodMode
              ? `Comptoir-${Date.now().toString(36)}`
              : isDeliveryPayment
		          ? selectedTable?.deliveryOrder?.orderNumber ||
		            selectedTable?.orderNumber ||
	            selectedTable?.number ||
	            "Livraison"
	          : `Table-${selectedTable.number}`,
	        table_id: isAggregatorPayment ? null : selectedTableApiId,
	        occupancy_cycle: isAggregatorPayment
	          ? null
	          : resolveTableOccupancyCycle(selectedTable),
	        table_number: isAggregatorPayment
            ? AGGREGATOR_ORDER_META[activeAggregator]?.label || "Agrégateur"
            : isFastFoodMode
            ? "Comptoir"
            : selectedTable.number,
	        visitor_count: isFastFoodMode || isAggregatorPayment ? null : getVisitorCountForTable(selectedTable),
		        table_label: tableLabelForPayment,
		        subtotal: subtotalForPayment,
		        total_adjustments: splitPaymentContext
              ? -Math.max(0, Number(splitPaymentContext.quote?.discount_total) || 0) +
	              Math.max(0, Number(splitPaymentContext.quote?.fee_total) || 0) -
	              draftDiscountsForSplit +
	              draftFeesForSplit
              : -Math.max(0, Number(getDiscountTotal()) || 0),
		        total_amount: totalAmountForPayment,
		        adjustments: splitPaymentContext
              ? splitAdjustmentsForPayment
              : explicitPaymentAdjustments,
		        items: itemsForPayment,
		        orderIds: resolvedOrderIds,
		        is_delivery: isDeliveryPayment,
		        delivery_customer: deliveryCustomerForPayment,
	        delivery_mode: isDeliveryPayment
	          ? selectedTable?.deliveryMode || deliveryMode || "delivery"
          : null,
        delivery_method: isDeliveryPayment
          ? activeAggregator || selectedTable?.deliveryMethod || deliveryMethod || "Direct"
          : null,
        delivery_reference: isDeliveryPayment ? deliveryReference : null,
        customer_name: resolvedCustomerName,
        customer_phone: resolvedCustomerPhone,
        fast_food_mode: isFastFoodMode && !isAggregatorPayment,
        counter_transaction_id:
          isFastFoodMode && !isAggregatorPayment
            ? counterRecovery?.counter_transaction_id || null
            : null,
        origin_zone_id:
          isFastFoodMode && !isAggregatorPayment
            ? Number(selectedZoneId) || null
            : null,
        counter_recovery: counterRecovery,
        ...(splitPaymentContext ? {
          splitSelection: splitPaymentContext.splitSelection,
          splitBill: splitPaymentContext.split,
          splitPaymentName: "Paiement du reste",
          isSplit: true,
        } : {}),
        order_type: isAggregatorPayment ? "delivery" : isFastFoodMode ? "takeaway" : null,
        activeAggregator,
        delivery_address:
          deliveryCustomerForPayment?.address ||
          deliveryCustomerForPayment?.address_line ||
          deliveryCustomerForPayment?.location ||
          null,
      };

      setCurrentOrderForPayment(orderForPayment);
      setShowPaymentInterface(true);
      cashDebugLog("✅ Nouveau PaymentInterface ouvert avec:", orderForPayment);
      return true;
    } catch (error) {
      console.error("�?� Erreur lors de la préparation du paiement:", error);
      triggerErrorAnimation(`Erreur: ${error.message}`);
      return false;
    } finally {
      paymentPreparationInFlightRef.current = false;
      setIsPreparingPayment(false);
    }
  };

  // Fonction pour gérer la completion du paiement depuis PaymentInterface
  const handlePaymentComplete = async (paymentResult) => {
    if (!firstPaymentMetricMarkedRef.current) {
      firstPaymentMetricMarkedRef.current = true;
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      recordPerfMetric(
        "caisse.time_to_first_payment_ms",
        now - appBootPerfRef.current,
        { role: currentRole || "unknown" },
      );
    }
    cashDebugLog("✅ Paiement complété:", paymentResult);
    cashDebugLog("�? Détails paymentResult:", {
      amount: paymentResult?.amount,
      amountReceived: paymentResult?.amountReceived,
      changeGiven: paymentResult?.changeGiven,
      method: paymentResult?.method,
      currentOrderForPayment: currentOrderForPayment?.total,
    });
    const isFastFoodPayment = currentOrderForPayment?.fast_food_mode === true;
    const isAggregatorPayment = isStandaloneAggregatorPayment(
      currentOrderForPayment,
    );
    const isPartialSplitPayment = Boolean(
      paymentResult?.splitSettlement && !paymentResult.splitSettlement.completed,
    );

    // Identifier la table depuis la commande payée, pas depuis la sélection UI.
    // Pendant les traitements post-paiement, l'utilisateur peut déjà ouvrir une
    // autre table: aucun nettoyage différé ne doit alors toucher cette nouvelle table.
    const paymentTableId =
      currentOrderForPayment?.table_id ??
      currentOrderForPayment?.tableId ??
      selectedTableRef.current?.id ??
      null;
    const currentTable =
      (paymentTableId != null
        ? tables.find((table) => String(table.id) === String(paymentTableId))
        : null) ||
      (selectedTableRef.current &&
      (paymentTableId == null ||
        String(selectedTableRef.current.id) === String(paymentTableId))
        ? { ...selectedTableRef.current }
        : null);
    const isPaymentTableCurrentlySelected = () => {
      if (!currentTable?.id) return isFastFoodPayment;
      const selectedNow = selectedTableRef.current;
      return Boolean(
        selectedNow &&
          String(selectedNow.id) === String(currentTable.id) &&
          Boolean(selectedNow.isDelivery) === Boolean(currentTable.isDelivery),
      );
    };
    const clearPaymentTableUiIfSelected = ({
      clearDelivery = false,
      deselect = false,
    } = {}) => {
      if (!isPaymentTableCurrentlySelected()) return false;
      setCurrentOrder({ items: [], total: 0 });
      setExistingItemsForPayment([]);
      setHasExistingOrders(false);
      setIsAddingToExistingOrder(false);
      if (clearDelivery) {
        setCurrentDeliveryOrder({ items: [], total: 0 });
        setExistingDeliveryItems([]);
        setSelectedDeliveryOrder(null);
      }
      if (deselect) {
        selectedTableRef.current = null;
        setSelectedTable(null);
        setActiveClientTab(null);
      }
      return true;
    };
    const currentAdjustmentsStorageKey =
      buildInvoiceAdjustmentsStorageKey(currentTable);
    const paidDeliveryOrderIds =
      currentTable?.isDelivery ? getSelectedDeliveryOrderIds(currentTable) : [];

    // Variable pour savoir si la table sera libérée (définie en premier)
    let tableWillBeFreed = false;

    // Extraire les informations de paiement
    const paidAmount = Number(
      paymentResult?.totalPaid ??
        paymentResult?.amount ??
        paymentResult?.amountPaid ??
        currentOrderForPayment?.total_amount ??
        currentOrderForPayment?.total ??
        0,
    );
    const amountReceived = Number(
      paymentResult?.amountReceived ?? paymentResult?.totalPaid ?? paidAmount,
    );
    const changeGiven = paymentResult?.changeGiven || 0;
    const serviceTotal =
      currentOrderForPayment?.total_amount ||
      currentOrderForPayment?.total ||
      paidAmount;
    const paymentMethod =
      paymentResult?.paymentMethods?.[0]?.method ||
      paymentResult?.method ||
      "espèces";

    lastPaymentAtRef.current = Date.now();
    const paymentEntries = Array.isArray(paymentResult?.paymentMethods)
      ? paymentResult.paymentMethods
      : [{ method: paymentMethod, amount: paidAmount }];
    let localSalesDelta = 0;
    paymentEntries.forEach((entry) => {
      const methodKey = normalizeMethodLabel(entry?.method || paymentMethod);
      if (!methodKey) return;
      const existing = sessionSalesByMethodRef.current.get(methodKey) || {
        method: entry?.method || paymentMethod,
        amount: 0,
        count: 0,
      };
      const entryAmount = Number(entry?.amount || 0);
      existing.amount += entryAmount;
      existing.count += 1;
      sessionSalesByMethodRef.current.set(methodKey, existing);
      localSalesDelta += entryAmount;
    });
    if (!localSalesDelta && !paymentResult?.reserve && Number.isFinite(paidAmount)) {
      localSalesDelta = paidAmount;
    }
    if (localSalesDelta > 0) {
      localSalesTotalRef.current += localSalesDelta;
      hasLocalSalesRef.current = true;
    }
    const resolvedSessionId =
      paymentResult?.sessionId || currentSession?.id || null;
    if (resolvedSessionId) {
      lastSessionIdRef.current = resolvedSessionId;
    }

    const resolvedOrderIds =
      Array.isArray(paymentResult?.orderIds) &&
      paymentResult.orderIds.length > 0
        ? paymentResult.orderIds
        : currentOrderForPayment?.orderIds || [];
	    const primaryOrderId =
	      paymentResult?.anchorOrderId ||
	      resolvedOrderIds[0] ||
	      currentOrderForPayment?.id ||
	      null;

    if (isFastFoodPayment) {
      const confirmedCounterOrderIds = [
        ...resolvedOrderIds,
        primaryOrderId,
        currentOrderForPayment?.counter_recovery?.created_order_id,
      ].map((value) => Number(value)).filter(
        (value) => Number.isInteger(value) && value > 0,
      );
      confirmedCounterOrderIds.forEach((orderId) => {
        confirmedCounterOrderIdsRef.current.add(orderId);
      });
      // Le paiement est confirmé par le serveur : retirer synchroniquement cette
      // commande de la liste locale avant de fermer le modal. Le rechargement
      // autoritaire qui suit conserve toutes les autres transactions à traiter.
      // Le registre empêche aussi une ancienne réponse réseau, partie avant le
      // paiement, de réinjecter temporairement la commande déjà réglée.
      setOpenCounterOrders((previous) =>
        filterConfirmedCounterOrders(previous, confirmedCounterOrderIds),
      );
    }

    if (currentTable?.id && !currentTable.isDelivery && !isFastFoodPayment) {
      markRecentlyPaidGuard(currentTable.id, resolvedOrderIds);
    }

    cashDebugLog("💰 Montants calculés:", {
      paidAmount,
      amountReceived,
      changeGiven,
      paymentMethod,
      currentTable: currentTable?.number,
    });

    // Nettoyer immédiatement l'interface de paiement
    setShowPaymentInterface(false);
    if (!isPartialSplitPayment) setCurrentOrderForPayment(null);
    setTempAdjustments([]);
    if (isFastFoodPayment) {
      setDiscountAmount(0);
      setAdditionalFees(0);
    }
    setActiveAggregator(null);
    if (isAggregatorPayment) {
      // Une commande agrégateur n'est rattachée ni à une vraie table ni à un
      // onglet de livraison historique. Son panier doit donc être clôturé
      // explicitement dès que le serveur confirme le paiement.
      setCurrentOrder({ items: [], total: 0 });
      setCurrentDeliveryOrder({ items: [], total: 0 });
      setExistingItemsForPayment([]);
      setExistingDeliveryItems([]);
      setHasExistingOrders(false);
      setIsAddingToExistingOrder(false);
      setSelectedDeliveryOrder(null);
      setSelectedCustomerForOrder(null);
      setDeliveryCustomer(null);
      setDeliveryRef("");
      setDeliveryMode("");
      setDeliveryMethod("Direct");
      setDiscountAmount(0);
      setAdditionalFees(0);
    }
    try {
      if (currentAdjustmentsStorageKey) {
        localStorage.removeItem(currentAdjustmentsStorageKey);
      }
    } catch (_) {}

    // ✅ UX: proposer l'impression du reçu après paiement (USB/Ethernet)
    try {
      const snapshotSource =
        currentOrderForPayment?.items?.length > 0
          ? currentOrderForPayment.items
          : [];
      const itemsSnapshot = snapshotSource.map((item) => ({
        ...item,
        extras: Array.isArray(item.extras)
          ? item.extras.map((extra) => ({ ...extra }))
          : [],
      }));

      const receiptPayload = {
        reserve: paymentResult?.reserve || null,
        amount: serviceTotal,
        totalPaid: amountReceived,
        amountReceived: amountReceived,
        changeGiven: changeGiven,
        paymentNumber:
          paymentResult?.paymentNumber || currentOrderForPayment?.payment_number || null,
        paymentMethods: paymentResult?.paymentMethods || [
          { method: paymentMethod, amount: paidAmount },
        ],
        creditAmount: Number(paymentResult?.creditAmount || 0),
        creditCustomerName: paymentResult?.creditCustomerName || null,
        creditCustomerPhone: paymentResult?.creditCustomerPhone || null,
        table: currentTable,
        order: currentOrderForPayment
          ? {
              ...currentOrderForPayment,
              payment_number:
                paymentResult?.paymentNumber || currentOrderForPayment.payment_number,
              total_amount: currentOrderForPayment.total_amount || serviceTotal,
            }
          : null,
        itemsSnapshot,
        orderId: primaryOrderId,
        primaryOrderId,
        cashierId: currentUser?.id || null,
        sessionId: currentSession?.id || null,
        fastFoodMode: isFastFoodPayment,
        serverName: paymentResult?.serverName || paymentResult?.server_name || null,
        server_name: paymentResult?.server_name || paymentResult?.serverName || null,
      };

      setPendingReceiptData(receiptPayload);
      setShowPrintReceiptModal(true);
      if (isFastFoodPayment) {
        setTimeout(() => {
          void printReceiptWithoutClosing(receiptPayload, { silent: true });
        }, 0);
      }
    } catch (receiptError) {
      console.warn(
        "⚠�? Impossible de préparer le reçu immédiatement:",
        receiptError,
      );
    }

    if (paymentResult?.splitSettlement?.completed) {
      // La dernière quantité vient d'être encaissée : cette scission appartient
      // désormais à l'historique et ne doit jamais contaminer la prochaine
      // ouverture de la même table.
      resetSplitUiState();
      setShowInvoiceGenerationModal(false);
    } else if (paymentResult?.splitSettlement) {
      try {
        const splitResponse = await fetch(
          `${API_BASE}/split-bills/${paymentResult.splitSettlement.groupId}`,
          { cache: "no-store" },
        );
        const splitPayload = await splitResponse.json().catch(() => ({}));
        if (splitResponse.ok && splitPayload?.split) {
          const refreshed = splitPayload.split;
          setPersistedSplitBill(refreshed);
          const mergeStatuses = (invoices = []) => invoices.map((invoice) => {
            const stored = refreshed.parts?.find(
              (part) => String(part.id) === String(invoice.id),
            );
            return stored
              ? {
                  ...invoice,
                  status: stored.status,
                  receipt_number: stored.receipt_number,
                  settlement_id: stored.settlement_id,
                  settlement_kind: stored.settlement_kind,
                }
              : invoice;
          });
          setSplitInvoices(mergeStatuses);
          setCurrentOrderForPayment((previous) => previous ? {
            ...previous,
            splitBill: refreshed,
            splitSelection: null,
            splitInvoices: mergeStatuses(previous.splitInvoices || []),
          } : previous);
          setShowInvoiceGenerationModal(false);
          setIsSplitMode(false);
          setShowSplitModal(true);
        }
      } catch (error) {
        console.warn(
          "Scission payée, rafraîchissement visuel différé:",
          error?.message || error,
        );
      }
    }

    // Rafraîchir le journal de caisse pour refléter le paiement
    loadCashRegisterLogWithFallback();
    loadCashRegisterSummary();
    if (typeof window !== "undefined") {
      setTimeout(() => {
        window.dispatchEvent(new Event("moo-cash-journal-refresh"));
      }, 600);
    }

    // Ne jamais peindre une table vide avant confirmation serveur.
    // S'il reste des commandes actives non payées, elles doivent rester visibles.
    cashDebugLog("🧾 Paiement validé: attente de confirmation serveur avant nettoyage panier...");
    if (isFastFoodPayment) {
      if (paymentResult?.productionDispatch?.success === false) {
        const pendingDispatchSnapshot =
          fastFoodCartSnapshotRef.current ||
          currentOrderForPayment?.counter_recovery ||
          buildFastFoodCartSnapshot(currentOrderForPayment?.items || []);
        if (pendingDispatchSnapshot) {
          const next = {
            ...pendingDispatchSnapshot,
            stage: "payment_submitted",
            payment_confirmed: true,
            created_order_id:
              paymentResult?.anchorOrderId ||
              pendingDispatchSnapshot.created_order_id ||
              null,
            timestamp: Date.now(),
          };
          fastFoodCartSnapshotRef.current = next;
          writeFastFoodCartSnapshotToStorage(next);
          setCounterDispatchPending(next);
        }
      } else {
        clearFastFoodCartSnapshot();
        setCounterDispatchPending(null);
      }
    }
    if (isFastFoodPayment) {
      setCurrentOrder({ items: [], total: 0 });
      setExistingItemsForPayment([]);
      void loadOpenCounterOrders();
    } else {
      cashDebugLog("🛡�? Nettoyage panier différé jusqu'à la vérification post-paiement");
    }

    // Les caches de la table ne sont nettoyés qu'après confirmation qu'il ne
    // reste aucune commande active. Nettoyer ici provoquait des paniers vides
    // transitoires jusqu'au prochain rafraîchissement manuel.
    if (currentTable && !currentTable.isDelivery && tableWillBeFreed) {
      try {
        forcedFreeTablesRef.current.add(currentTable.id);
      } catch (_) {}
      cashDebugLog(
        `🧹 Nettoyage complet du cache pour table ${currentTable.number} (ID: ${currentTable.id})`,
      );

      // 1. Nettoyer le cache des items existants
      forgetKnownExistingItems(currentTable.id, null, {
        preservePending: false,
      });

      // 2. Nettoyer le cache des états de table
      commitTableStatesCache((prev) => {
        const next = new Map(prev);
        next.delete(currentTable.id);
        next.delete(String(currentTable.id));
        return next;
      });

      // 3. Nettoyer le statut optimiste et marquer comme libre
      try {
        optimisticTableStatusRef.current.set(currentTable.id, {
          status: "free",
          ts: Date.now(),
        });
      } catch (_) {}

      // 4. Mettre à jour immédiatement l'état local des tables
      setTables((prev) =>
        Array.isArray(prev)
          ? prev.map((t) =>
              t.id === currentTable.id ? { ...t, status: "free" } : t,
            )
          : prev,
      );

      // Marquer la table comme récemment payée et annuler toute "grâce" d'envoi
      try {
        markRecentlyPaidGuard(currentTable.id, resolvedOrderIds);
      } catch (_) {}
      try {
        lastSendAtRef.current.delete(currentTable.id);
      } catch (_) {}
      cashDebugLog(
        `✅ Cache nettoyé et table ${currentTable.number} marquée comme libre et protégée`,
      );
    }

    // Vérifier s'il reste des commandes non payées pour cette table
    if (currentTable && !currentTable.isDelivery) {
      try {
        // Le paiement a déjà été validé transactionnellement par le serveur.
        // Une courte cession à la boucle suffit pour laisser passer les événements socket.
        await new Promise((resolve) => setTimeout(resolve, 80));

        // Forcer le rechargement du statut de la table depuis le serveur
        const tableResponse = await fetch(
          `${API_BASE}/tables/${currentTable.id}`,
        );
        if (tableResponse.ok) {
          const tableData = await tableResponse.json();
          cashDebugLog(
            `📊 Statut table ${currentTable.number} depuis serveur:`,
            tableData.status,
          );

          // Mettre à jour immédiatement l'état local
          setTables((prev) =>
            Array.isArray(prev)
              ? prev.map((t) =>
                  t.id === currentTable.id
                    ? { ...t, status: tableData.status }
                    : t,
                )
              : prev,
          );
        }

        const response = await fetch(
          `${API_BASE}/tables/${currentTable.id}/orders`,
        );
        if (response.ok) {
          const orders = await response.json();

          const unpaidOrders = orders.filter(
            (order) =>
              order.payment_status !== "paid" && order.status !== "cancelled",
          );

          const hasActiveItems = unpaidOrders.some((order) =>
            order.items.some((item) => item.status !== "cancelled"),
          );

          cashDebugLog(`📊 Table ${currentTable.number} après paiement:`, {
            totalOrders: orders.length,
            unpaidOrders: unpaidOrders.length,
            hasActiveItems,
            orders: orders.map((o) => ({
              id: o.id,
              payment_status: o.payment_status,
              status: o.status,
              items: o.items?.length || 0,
            })),
          });

          if (hasActiveItems) {
            // Il reste des commandes non payées, recharger les articles SANS les mettre dans le panier
            cashDebugLog(
              "🔄 Rechargement des articles restants pour affichage...",
            );
            if (isPaymentTableCurrentlySelected()) {
              setHasExistingOrders(true);
              await loadTableItemsForPayment(currentTable.id, false);
            }
          } else {
            // Plus de commandes non payées, libérer complètement la table
            cashDebugLog(
              "🆓 Toutes les commandes payées, libération complète de la table...",
            );
            tableWillBeFreed = true;
            try {
              forcedFreeTablesRef.current.add(currentTable.id);
            } catch (_) {}
            forgetKnownExistingItems(currentTable.id, null, {
              preservePending: false,
            });
            commitTableStatesCache((prev) => {
              const next = new Map(prev);
              next.delete(currentTable.id);
              next.delete(String(currentTable.id));
              return next;
            });
            try {
              markRecentlyPaidGuard(currentTable.id, resolvedOrderIds);
              lastSendAtRef.current.delete(currentTable.id);
            } catch (_) {}
            // Libération immédiate de l'état local
            try {
              optimisticTableStatusRef.current.set(currentTable.id, {
                status: "free",
                ts: Date.now(),
              });
            } catch (_) {}
            setTables((prev) =>
              Array.isArray(prev)
                ? prev.map((t) =>
                    t.id === currentTable.id ? { ...t, status: "free" } : t,
                  )
                : prev,
            );

            // Nettoyer complètement l'interface
            clearPaymentTableUiIfSelected({ deselect: true });

            // Nettoyer données table
            setTableCustomerCounts((prev) => {
              const newCounts = { ...prev };
              delete newCounts[currentTable.id];
              return newCounts;
            });

            // Restaurer les tables qui étaient fusionnées dans cette table AVANT de nettoyer tableMerges
            const fusedTables = tableMerges[currentTable.id] || [];
            const tableIdsToRestore = tables
              .filter((table) => fusedTables.includes(table.number))
              .map((table) => table.id);

            // Libérer les tables fusionnées en base de données
            for (const tableId of tableIdsToRestore) {
              try {
                await fetch(`${API_BASE}/tables/${tableId}`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ status: "free" }),
                });
                cashDebugLog(`✅ Table ${tableId} libérée après fusion`);
              } catch (error) {
                console.error(`�?� Erreur libération table ${tableId}:`, error);
              }
            }

            // Restaurer les tables fusionnées dans l'interface
            setFusedAwayTables((prev) => {
              const newSet = new Set(prev);
              tableIdsToRestore.forEach((id) => newSet.delete(id));
              return newSet;
            });

            // Nettoyer les informations de fusion APRÈS avoir restauré les tables
            setTableMerges((prev) => {
              const newMerges = { ...prev };
              delete newMerges[currentTable.id];
              return newMerges;
            });

            // Libérer la table côté serveur (forcer la libération)
            cashDebugLog(
              `🆓 Libération FORCÉE de la table ${currentTable.number} (ID: ${currentTable.id})`,
            );

            try {
              const freeResponse = await fetch(
                `${API_BASE}/tables/${currentTable.id}`,
                {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ status: "free" }),
                },
              );

              if (freeResponse.ok) {
                cashDebugLog(
                  `✅ Table ${currentTable.number} libérée avec succès côté serveur`,
                );

                // Forcer immédiatement la mise à jour dans l'état local
                setTables((prev) =>
                  Array.isArray(prev)
                    ? prev.map((t) =>
                        t.id === currentTable.id ? { ...t, status: "free" } : t,
                      )
                    : prev,
                );
              } else {
                console.error(
                  `�?� Erreur libération table ${currentTable.number}:`,
                  await freeResponse.text(),
                );
              }
            } catch (freeError) {
              console.error(`�?� Erreur lors de la libération:`, freeError);
            }
          }
        }
      } catch (error) {
        console.error(
          "�?� Erreur lors de la vérification post-paiement:",
          error,
        );
        // Ne jamais nettoyer une autre table ouverte pendant le traitement différé.
        clearPaymentTableUiIfSelected({ deselect: true });
      }
    } else {
      // Comptoir sans table: le panier concerné peut être nettoyé directement.
      if (isFastFoodPayment) {
        setCurrentOrder({ items: [], total: 0 });
        setExistingItemsForPayment([]);
        setHasExistingOrders(false);
        setIsAddingToExistingOrder(false);
      }
    }

    // Si c'est une commande de livraison, la supprimer de la liste après paiement
    if (tableWillBeFreed && currentTable && !currentTable.isDelivery) {
      // Neutraliser uniquement si l'interface affiche encore la table payée.
      clearPaymentTableUiIfSelected({ deselect: true });
      setTableCustomerCounts((prev) => {
        const next = { ...prev };
        delete next[currentTable.id];
        return next;
      });
      setTables((prev) =>
        Array.isArray(prev)
          ? prev.map((t) =>
              t.id === currentTable.id
                ? { ...t, status: "free", customerCount: null }
                : t,
            )
          : prev,
      );
    }

    if (currentTable && currentTable.isDelivery) {
      const orderIdsToRemove =
        paidDeliveryOrderIds.length > 0
          ? Array.from(new Set(paidDeliveryOrderIds))
          : currentTable.deliveryOrder
              ? [currentTable.deliveryOrder.id]
              : currentTable.orderId
                ? [currentTable.orderId]
                : [];

      if (orderIdsToRemove.length > 0) {
        cashDebugLog(
          "🚚 Suppression commandes de livraison payées:",
          orderIdsToRemove,
        );
        setHiddenDeliveryOrderIds((prev) => {
          const nextSet = new Set(prev);
          orderIdsToRemove.forEach((id) => {
            if (id !== undefined && id !== null) {
              nextSet.add(id);
            }
          });
          return Array.from(nextSet);
        });
        setDeliveryOrders((prev) =>
          prev.filter(
            (order) =>
              !orderIdsToRemove.includes(order.orderId || order.order_id),
          ),
        );
        setClientTabs((prev) =>
          Array.isArray(prev)
            ? prev.filter((tab) => {
                const tabOrderId =
                  tab.orderId || tab.deliveryOrder?.id || null;
                return (
                  tab.id !== currentTable.id &&
                  !orderIdsToRemove.includes(tabOrderId)
                );
              })
            : prev,
        );
      }

      // Nettoyer les données de livraison (purge complète)
      setAutoSelectDeliveryTab(false);
      clearPaymentTableUiIfSelected({
        clearDelivery: true,
        deselect: true,
      });
    }

    // Recharger les données des tables pour mettre à jour l'affichage
    cashDebugLog("🔄 Rechargement COMPLET des tables après paiement...");
    // Les deux lectures sont indépendantes: les lancer en parallèle évite un délai
    // visible sur un serveur local tout en gardant la vérification complète.
    await Promise.all([
      loadTables({ forceUnpaidRefresh: true }),
      loadPendingDeliveries({
        allowAutoSelect: false,
        forceNeutral: isPaymentTableCurrentlySelected(),
      }),
    ]);
    setAutoSelectDeliveryTab(false);

    // Vérifier à nouveau le statut de la table traitée
    if (currentTable && !currentTable.isDelivery) {
      try {
        const finalCheck = await fetch(`${API_BASE}/tables/${currentTable.id}`);
        if (finalCheck.ok) {
          const finalTableData = await finalCheck.json();
          cashDebugLog(
            `�? Vérification finale table ${currentTable.number}: ${finalTableData.status}`,
          );

          // Mettre à jour une dernière fois pour être sûr
          setTables((prev) =>
            Array.isArray(prev)
              ? prev.map((t) =>
                  t.id === currentTable.id
                    ? { ...t, status: finalTableData.status }
                    : t,
                )
              : prev,
          );

          if (finalTableData.status === "free") {
            cashDebugLog(`✅✅✅ Table ${currentTable.number} confirmée LIBRE`);
          } else {
            console.warn(
              `⚠�? Table ${currentTable.number} toujours: ${finalTableData.status}`,
            );
          }
        }
      } catch (checkError) {
        console.error("Erreur vérification finale:", checkError);
      }
    }

    cashDebugLog("✅ Tables rechargées après paiement");

    // Message de succès détaillé
    let successMessage = `Paiement de ${paidAmount} F effectué avec succès !`;

    // Ajouter les détails du paiement si disponibles
    if (amountReceived > 0 && amountReceived !== paidAmount) {
      successMessage += ` Reçu: ${amountReceived} F,`;
    }

    if (changeGiven > 0) {
      successMessage += ` Rendu: ${changeGiven} F.`;
    }

    // Ajouter l'information sur la libération de table
    if (currentTable?.isDelivery) {
      successMessage += " (Livraison)";
    } else if (tableWillBeFreed && currentTable) {
      successMessage += `\n✓ Table ${currentTable.number} libérée`;
    }

    cashDebugLog("�? Message final:", successMessage);
    // Sécurité supplémentaire: si la table est libérée, refléter immédiatement dans l'interface
    if (tableWillBeFreed && currentTable && !currentTable.isDelivery) {
      setTables((prev) =>
        Array.isArray(prev)
          ? prev.map((t) =>
              t.id === currentTable.id ? { ...t, status: "free" } : t,
            )
          : prev,
      );
      cashDebugLog(
        `🟢 Table ${currentTable.number} marquée comme LIBRE dans l'interface`,
      );
    }

    // Animation de succès
    triggerSuccessAnimation(successMessage);

    // Sécurité: forcer la libération serveur si aucune commande active n'existe plus après paiement
    setTimeout(async () => {
      try {
        if (currentTable?.id && !currentTable.isDelivery) {
          const res = await fetch(`${API_BASE}/tables/${currentTable.id}/orders`);
          if (res.ok) {
            const orders = await res.json();
            const unpaidOrders = orders.filter(
              (o) => o.payment_status !== "paid" && o.status !== "cancelled",
            );
            const hasActiveItems = unpaidOrders.some((o) =>
              (o.items || []).some((it) => it.status !== "cancelled"),
            );
            if (!hasActiveItems) {
              cashDebugLog(
                `🆓 Vérification finale: libération définitive de la table ${currentTable.number}`,
              );
              await fetch(`${API_BASE}/tables/${currentTable.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: "free" }),
              });
              // Forcer la mise à jour de l'interface
              setTables((prev) =>
                Array.isArray(prev)
                  ? prev.map((t) =>
                      t.id === currentTable.id ? { ...t, status: "free" } : t,
                    )
                  : prev,
              );
            }
          }
        }
      } catch (_) {}
    }, 2500);
  };

	  // Fonction pour fermer PaymentInterface sans paiement
	  const handlePaymentClose = () => {
	    setShowPaymentInterface(false);
	    setCurrentOrderForPayment(null);
	  };

  const fetchReceiptPrinterSettingsSafe = async () => {
    try {
      const res = await fetch(`${API_BASE}/receipt-printer-settings`);
      if (!res.ok) return null;
      return (await res.json()) || null;
    } catch (_) {
      return null;
    }
  };

  const buildUsbPrintCss = (paperWidthMm = 72) => {
    const clamped = resolveThermalPrintableWidthMm(paperWidthMm);
    return `
      @page { margin: 0; }
      html, body { margin: 0; padding: 0; }
      body { width: ${clamped}mm; max-width: ${clamped}mm; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    `;
  };

  const injectUsbPrintCss = (html, paperWidthMm = 80) => {
    const source = String(html || "");
    if (!source.trim()) return source;
    const styleTag = `<style>${buildUsbPrintCss(paperWidthMm)}</style>`;
    if (source.includes("</head>")) {
      return source.replace("</head>", `${styleTag}</head>`);
    }
    if (source.includes("<html")) {
      return source.replace("<html>", `<html><head>${styleTag}</head>`);
    }
    return `<html><head>${styleTag}</head><body>${source}</body></html>`;
  };

  const resolveUsbPrintOptions = (settings = {}) => {
    const normalizeCopies = (value) => {
      const copies = Math.floor(Number(value));
      return Number.isFinite(copies) && copies >= 1 ? copies : 1;
    };
    const configuredName = String(settings?.printer_name || "").trim();
    const sanitizedName =
      configuredName &&
      configuredName.toLowerCase() !== "ticket caisse"
        ? configuredName
        : undefined;
    const configuredWidth = Number(settings?.paper_width_mm);
    return {
      silent: true,
      paperWidthMm:
        Number.isFinite(configuredWidth) && configuredWidth > 0
          ? configuredWidth
          : 80,
      copies: normalizeCopies(settings?.copies),
      deviceName: sanitizedName,
    };
  };

  const printHtmlInHiddenIframe = (html, options = {}) =>
    new Promise((resolve, reject) => {
      try {
        const paperWidthMm = Number(options?.paperWidthMm) || 80;
        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.left = "-10000px";
        iframe.style.top = "0";
        iframe.style.width = "820px";
        iframe.style.height = "1200px";
        iframe.style.border = "0";
        iframe.style.opacity = "0";
        iframe.style.pointerEvents = "none";
        iframe.setAttribute("aria-hidden", "true");

        let settled = false;
        let fallbackTimer = null;
        const cleanup = () => {
          if (settled) return;
          settled = true;
          if (fallbackTimer) window.clearTimeout(fallbackTimer);
          try {
            iframe.remove();
          } catch (_) {}
          resolve(true);
        };

        iframe.onload = async () => {
          try {
            const w = iframe.contentWindow;
            if (!w) throw new Error("Fenêtre d'impression indisponible.");
            try {
              if (w.document?.fonts?.ready) await w.document.fonts.ready;
            } catch (_) {}
            await new Promise((ready) =>
              w.requestAnimationFrame(() => w.requestAnimationFrame(ready)),
            );
            const body = w.document.body;
            const bodyRect = body.getBoundingClientRect();
            const children = Array.from(body.children || []);
            const contentHeightPx = Math.max(
              1,
              ...children.map((child) => {
                const rect = child.getBoundingClientRect();
                return rect.bottom - bodyRect.top;
              }),
            );
            const thermalPage = calculateThermalPageSize({
              contentHeightPx,
              paperWidthMm,
            });
            const pageStyle = w.document.createElement("style");
            pageStyle.setAttribute("data-moo-thermal-page", "true");
            pageStyle.textContent = `@page { size: ${thermalPage.widthMm}mm ${thermalPage.heightMm}mm; margin: 0 !important; }`;
            w.document.head.appendChild(pageStyle);
            w.addEventListener("afterprint", cleanup, { once: true });
            // Secours pour les anciens Chromium qui n'émettent pas afterprint.
            fallbackTimer = window.setTimeout(cleanup, 60000);
            w.focus();
            w.print();
          } catch (err) {
            if (!settled) {
              settled = true;
              if (fallbackTimer) window.clearTimeout(fallbackTimer);
              try {
                iframe.remove();
              } catch (_) {}
              reject(err);
            }
          }
        };

        iframe.srcdoc = injectUsbPrintCss(html, paperWidthMm);
        document.body.appendChild(iframe);
      } catch (err) {
        reject(err);
      }
    });

  const printHtmlViaUsbPrinter = async (html, settings = {}) => {
    const options = resolveUsbPrintOptions(settings);
    let electronError = null;
    if (window?.electronAPI?.printHtml) {
      try {
        await window.electronAPI.printHtml({ html, ...options });
        return;
      } catch (error) {
        electronError = error;
        console.warn(
          "Impression Electron échouée, fallback iframe:",
          error?.message || error,
        );
      }
    }
    try {
      await printHtmlInHiddenIframe(html, options);
    } catch (fallbackError) {
      if (!electronError) throw fallbackError;
      throw new Error(
        `Electron: ${electronError?.message || electronError}; secours navigateur: ${fallbackError?.message || fallbackError}`,
      );
    }
  };

  // Fonction pour imprimer le reçu après paiement
  const printReceipt = async (paymentData, tableData) => {
    const settings = await fetchReceiptPrinterSettingsSafe();

    const itemsForReceipt =
      (Array.isArray(paymentData?.itemsSnapshot) &&
      paymentData.itemsSnapshot.length > 0
        ? paymentData.itemsSnapshot
        : paymentData?.order?.items?.map((i) => ({
            ...i,
            variationLabel:
              i.variationLabel ||
              i.variation_label ||
              i.variationName ||
              i.variation_name ||
              null,
            variation_attributes:
              i.variation_attributes ||
              i.variationAttributes ||
              i.attributes ||
              null,
            variation_name: i.variation_name || i.variationName || null,
            product_type:
              i.product_type ||
              i.type ||
              ((i.variation_name ||
                i.variationName ||
                i.variation_attributes ||
                i.variationAttributes)
                ? "variable"
                : null),
          })) || []) || [];

    const paymentTotal =
      paymentData?.amount ?? paymentData?.order?.total_amount ?? 0;
    const paymentPerceived =
      paymentData?.amountReceived ?? paymentData?.totalPaid ?? paymentTotal;
    const paymentChange =
      paymentData?.changeGiven ??
      (paymentPerceived > paymentTotal ? paymentPerceived - paymentTotal : 0);

    const paymentMethods =
      Array.isArray(paymentData?.paymentMethods) &&
      paymentData.paymentMethods.length > 0
        ? paymentData.paymentMethods
        : paymentData?.reserve ? [] : [
            {
              method: paymentData?.method || "Espèces",
              amount: paymentPerceived || paymentTotal,
            },
          ];

    const formatReceiptAmount = (value) =>
      String(Math.round(Number(value || 0))).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    const computeReceiptItemTotal = (item) => {
      const quantity = Number(item?.quantity) || 1;
      const basePrice = parseMoney(item?.price ?? item?.unit_price ?? 0);
      const extrasPrice = Array.isArray(item?.extras)
        ? item.extras.reduce((sum, extra) => {
            const extraQty =
              Number(extra?.quantity ?? extra?.selectedQuantity) || 1;
            const extraPrice = parseMoney(extra?.price ?? extra?.unit_price ?? 0);
            if (extra?.type === "accompaniment") return sum;
            return sum + extraPrice * extraQty;
          }, 0)
        : 0;
      const explicitTotal = Number(item?.total_price_with_extras) || 0;
      if (explicitTotal > 0) return explicitTotal;
      return (basePrice + extrasPrice) * quantity;
    };

    const itemsSubtotal = itemsForReceipt.reduce(
      (sum, item) => sum + computeReceiptItemTotal(item),
      0,
    );

    const orderSubtotalRaw = parseMoney(paymentData?.order?.subtotal);
    const receiptSubtotal =
      Number.isFinite(orderSubtotalRaw) && orderSubtotalRaw > 0
        ? orderSubtotalRaw
        : itemsSubtotal > 0
          ? itemsSubtotal
          : paymentTotal;

    const adjustmentsDiscount = Array.isArray(paymentData?.order?.adjustments)
      ? paymentData.order.adjustments.reduce((sum, adj) => {
          if (adj?.type !== "discount") return sum;
          return sum + (Number(adj.amount) || 0);
        }, 0)
      : 0;
    const totalAdjustmentsRaw = Number(paymentData?.order?.total_adjustments);
    const totalAdjustmentsDiscount =
      Number.isFinite(totalAdjustmentsRaw) && totalAdjustmentsRaw < 0
        ? Math.abs(totalAdjustmentsRaw)
        : 0;
    const receiptDiscount = Math.max(
      adjustmentsDiscount,
      totalAdjustmentsDiscount,
    );
    const showReceiptDiscount = receiptDiscount > 0.01;

    const cashierName =
      paymentData?.cashier_name ||
      paymentData?.cashierName ||
      getUserShortLabel(currentUser);
    const serverName =
      paymentData?.server_name ||
      paymentData?.serverName ||
      resolveServiceStarterName({
        table: tableData,
        order: paymentData?.order,
        fallbackUser: currentUser,
      });

    const restaurantTitle =
      paymentData?.restaurant_name ||
      (restaurantInfo?.name && restaurantInfo.name.trim()
        ? restaurantInfo.name.trim()
        : "Restaurant");
    const restaurantAddress =
      paymentData?.restaurant_address ||
      (restaurantInfo?.address && restaurantInfo.address.trim()
        ? restaurantInfo.address.trim()
        : "");
    const restaurantPhone =
      paymentData?.restaurant_phone ||
      (restaurantInfo?.phone && restaurantInfo.phone.trim()
        ? restaurantInfo.phone.trim()
        : "");

    const inferTableFromName = (name) => {
      if (!name) return null;
      const match = String(name).match(/(\d+)/);
      if (match && match[1]) return match[1];
      return String(name);
    };

    const formatReceiptItemName = (item) => {
      const baseName = item?.name || "Produit";
      const variationLabel = normalizeVariationLabel(
        getItemVariationDisplay(item),
      );
      const hasMeaningfulVariation =
        variationLabel &&
        variationLabel !== baseName &&
        variationLabel.toLowerCase() !== "variation" &&
        !variationLabel.toLowerCase().startsWith("variation ");
      if (hasMeaningfulVariation) return `${baseName} · ${variationLabel}`;
      return baseName;
    };

    const resolvedTableNumber = tableData?.isDelivery
      ? "Livraison"
      : tableData?.number
        ? String(tableData.number)
        : inferTableFromName(tableData?.name) || "—";
    const areaName = printAreaOnDocuments ? String(
      paymentData?.area_name || paymentData?.origin_zone_name ||
      paymentData?.order?.origin_zone_name || tableData?.zone_name ||
      tableData?.zone || "",
    ).trim() : "";

    const paidAt = paymentData?.paidAt || paymentData?.timestamp
      ? new Date(paymentData.paidAt || paymentData.timestamp)
      : new Date();
    const orderNumber =
      paymentData?.paymentNumber ||
      paymentData?.order?.payment_number ||
      paymentData?.order?.order_number ||
      null;

    const receiptPrintPayload = {
      reserve: paymentData?.reserve || null,
      restaurant_name: restaurantTitle,
      restaurant_address: restaurantAddress,
      restaurant_phone: restaurantPhone,
      table_number: resolvedTableNumber,
      area_name: areaName,
      cashier_name: cashierName,
      server_name: serverName,
      payment_number: orderNumber,
      timestamp: paidAt,
      items: itemsForReceipt.map((item) => ({
        ...item,
        display_name: formatReceiptItemName(item),
      })),
      subtotal: receiptSubtotal,
      discount: receiptDiscount,
      total: paymentTotal,
      amount_received: paymentPerceived,
      change_given: paymentChange,
      payment_methods: paymentMethods,
      credit_amount: Number(paymentData?.creditAmount || 0),
      credit_customer_name: paymentData?.creditCustomerName || null,
      credit_customer_phone: paymentData?.creditCustomerPhone || null,
    };

    const receiptConnectionType = String(
      settings?.connection_type || "",
    ).toLowerCase();
    if (
      receiptConnectionType === "ethernet" ||
      receiptConnectionType === "direct_rj45"
    ) {
      const res = await fetch(`${API_BASE}/receipt-printer/print-receipt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(receiptPrintPayload),
      });
      if (!res.ok) {
        let message = "Erreur d'impression (reçu).";
        try {
          const data = await res.json();
          message = data?.error || message;
        } catch (_) {}
        throw new Error(message);
      }
      return;
    }

    const receiptContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <title>Reçu</title>
          <style>
            body {
              font-family: Arial, Helvetica, sans-serif;
              margin: 0;
              padding: 20px;
              font-size: 12px;
              color: #000;
              background: #fff;
              font-weight: 700;
            }
            .receipt {
              max-width: 420px;
              margin: 0 auto;
              background: #fff;
              border-radius: 12px;
              padding: 16px;
              border: 1px solid #000;
            }
            .header { text-align: center; margin-bottom: 14px; }
            .brand { font-size: 18px; letter-spacing: 0.02em; }
            .subtitle { font-size: 11px; color: #000; margin-top: 3px; font-weight: 600; }
            .header-contact { font-size: 13px; color: #000; margin-top: 3px; font-weight: 900; }
            .section { margin-bottom: 12px; }
            .section-title {
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.08em;
              color: #000;
              margin-bottom: 6px;
              font-weight: 700;
            }
            .item {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 8px;
              padding: 6px 0;
              border-bottom: 1px dashed #000;
              font-size: 15px;
              font-weight: 900;
            }
            .item:last-child { border-bottom: none; }
            .item-name { color: #000; flex: 1 1 auto; min-width: 0; font-weight: 900; font-size: 15px; }
            .item-extras { font-size: 12px; color: #000; margin-top: 2px; font-weight: 700; }
            .price {
              color: #000;
              font-weight: 900;
              font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
              min-width: 100px;
              text-align: right;
              white-space: nowrap;
              flex: 0 0 auto;
              font-size: 15px;
              font-variant-numeric: tabular-nums;
            }
            .totals .row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 14px; }
            .totals .row span:last-child {
              min-width: 100px;
              text-align: right;
              font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
              font-variant-numeric: tabular-nums;
            }
            .totals .row.total { font-size: 16px; color: #000; }
            .divider { height: 1px; background: #000; opacity: 0.25; margin: 10px 0; }
            .footer { text-align: center; font-size: 10px; color: #000; margin-top: 12px; font-weight: 600; }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="header">
              <div class="brand">${restaurantTitle}</div>
              ${restaurantAddress ? `<div class="header-contact">${restaurantAddress}</div>` : ""}
              ${restaurantPhone ? `<div class="header-contact">Tel: ${restaurantPhone}</div>` : ""}
            </div>

              <div class="section">
              <div class="section-title">COMMANDE${orderNumber ? ` N° ${orderNumber}` : ""}</div>
              ${isBoutique ? "" : `<div class="subtitle">Table : ${resolvedTableNumber}</div>`}
              ${!isBoutique && areaName ? `<div class="subtitle">Aire : ${escapeHtml(areaName)}</div>` : ""}
              <div class="subtitle">Caissier : ${cashierName}</div>
              ${isBoutique ? "" : `<div class="subtitle">Serveur : ${serverName}</div>`}
              <div class="subtitle">Date : ${paidAt.toLocaleString("fr-FR").replace(/\\u202F|\\u00A0/g, " ")}</div>
            </div>

            <div class="section">
              <div class="section-title">ARTICLES</div>
              ${itemsForReceipt
                .map((item) => {
                  const itemTotal = computeReceiptItemTotal(item);
                  const extras =
                    Array.isArray(item.extras) && item.extras.length > 0
                      ? item.extras
                          .map((extra) => {
                            const qty = Number(extra.quantity || extra.selectedQuantity || 1) || 1;
                            const price = Number(extra.price || extra.unit_price || 0) || 0;
                            const amount = price * qty;
                            return `${extra.name}${qty > 1 ? ` ×${qty}` : ""} (${formatReceiptAmount(amount)} F)`;
                          })
                          .join(", ")
                      : "";
                  const displayName = formatReceiptItemName(item);
                  return `
                    <div class="item">
                      <div>
                        <div class="item-name">${item.quantity || 1}× ${displayName}</div>
                        ${extras ? `<div class="item-extras">Suppléments : ${extras}</div>` : ""}
                      </div>
                      <div class="price">${formatReceiptAmount(itemTotal)} F</div>
                    </div>
                  `;
                })
                .join("")}
            </div>

              ${paymentData?.reserve ? `<div class="section">${require("../shared/reserveReceipt").reserveReceiptHtml(paymentData.reserve)}</div>` : ""}
	            <div class="section totals">
	              <div class="row">
	                <span>Sous-total</span>
	                <span>${formatReceiptAmount(receiptSubtotal)} F</span>
	              </div>
              ${
                showReceiptDiscount
                  ? `
              <div class="row">
                <span>Réduction</span>
                <span>-${formatReceiptAmount(receiptDiscount)} F</span>
              </div>
              `
                  : ""
              }
	              <div class="row total">
	                <span>Total</span>
	                <span>${formatReceiptAmount(paymentTotal)} F</span>
	              </div>
	              <div style="margin-top: 10px;"></div>
	              <div class="section-title">MODE DE PAIEMENT</div>
	              ${
	                paymentMethods.length > 0
	                  ? paymentMethods
	                      .map((method) => {
	                        const label = method?.method || "Paiement";
	                        const amount = Number(method?.amount || 0);
	                        return `<div style="display:flex;justify-content:space-between;margin:4px 0;"><span>${label}</span><span class="price" style="min-width:auto">${formatReceiptAmount(amount)} F</span></div>`;
	                      })
	                      .join("")
	                  : `<div style="display:flex;justify-content:space-between;margin:4px 0;"><span>Espèces</span><span class="price" style="min-width:auto">${formatReceiptAmount(paymentTotal)} F</span></div>`
	              }
	              <div class="divider"></div>
	              <div class="row">
	                <span>Reçu</span>
	                <span>${formatReceiptAmount(paymentPerceived)} F</span>
	              </div>
	              <div class="row">
	                <span>Rendu</span>
	                <span>${formatReceiptAmount(paymentChange)} F</span>
	              </div>
	            </div>

	            <div class="footer">
	              <div>Merci pour votre visite !</div>
	              <div>Powered by MOO Sync - ${new Date().getFullYear()}</div>
            </div>
          </div>
        </body>
      </html>
    `;

    void receiptContent; // Modèle historique conservé temporairement; rendu USB canonique ci-dessous.
    await printHtmlViaUsbPrinter(
      renderUsbTicketHtml({
        kind: "receipt",
        payload: receiptPrintPayload,
        paperWidthMm: resolveUsbPrintOptions(settings).paperWidthMm,
      }),
      settings,
    );
  };

  // Fonction pour gérer l'impression du reçu sans fermer le modal.
  const printReceiptWithoutClosing = async (receiptData, options = {}) => {
    if (!receiptData || receiptPrintingRef.current) return;
    receiptPrintingRef.current = true;
    setIsReceiptPrinting(true);
    try {
      await printReceipt(receiptData, receiptData.table);
      cashDebugLog("?? Reçu envoyé à l'impression");
      if (!options.silent) {
        triggerSuccessAnimation("Reçu envoyé à l'imprimante !");
      }
    } catch (error) {
      console.error("�?� Erreur impression reçu:", error);
      triggerErrorAnimation("Erreur lors de l'impression du reçu");
    } finally {
      receiptPrintingRef.current = false;
      setIsReceiptPrinting(false);
    }
  };

  // Fonction pour gérer l'impression confirmée
  const handleConfirmPrintReceipt = async () => {
    await printReceiptWithoutClosing(pendingReceiptData);
  };

  // Fonction pour fermer le modal d'impression sans imprimer
  const handleCancelPrintReceipt = () => {
    setShowPrintReceiptModal(false);
    setPendingReceiptData(null);
    receiptPrintingRef.current = false;
    setIsReceiptPrinting(false);
  };

  // Fonction pour initier le transfert de table
  const handleTableTransfer = async () => {
    cashDebugLog("🔄 Début handleTableTransfer - État actuel:", {
      selectedTable: selectedTable
        ? {
            id: selectedTable.id,
            number: selectedTable.number,
            status: selectedTable.status,
            customerCount: selectedTable.customerCount,
          }
        : null,
      currentOrderItems: currentOrder.items.length,
      existingItemsForPayment: existingItemsForPayment.length,
    });

    if (!selectedTable) {
      triggerErrorAnimation(
        isBoutique ? "Aucun panier actif." : "Aucune table sélectionnée !",
      );
      return;
    }

    try {
      // Charger les deux destinations du même geste : nouvelle table ou
      // nouveau serveur. Le choix reste explicite dans le modal.
      const [tablesResult, usersResult] = await Promise.allSettled([
        fetch(`${API_BASE}/tables`),
        fetch(`${API_BASE}/users`),
      ]);
      const tablesResponse =
        tablesResult.status === "fulfilled" ? tablesResult.value : null;
      const usersResponse =
        usersResult.status === "fulfilled" ? usersResult.value : null;
      if (tablesResponse?.ok) {
        const allTables = await tablesResponse.json();
        const allUsers = usersResponse?.ok ? await usersResponse.json() : [];
        const freeTables = allTables.filter(
          (table) => table.status === "free" && table.id !== selectedTable.id,
        );
        const sortedFreeTables = [...freeTables].sort(
          (a, b) => Number(a.number) - Number(b.number),
        );
        const servers = (Array.isArray(allUsers) ? allUsers : [])
          .filter(
            (user) =>
              Boolean(user?.is_active ?? user?.isActive ?? 1) &&
              ["serveur", "server"].includes(
                String(user?.role || "").trim().toLowerCase(),
              ),
          )
          .sort((a, b) =>
            String(a.first_name || a.username || "").localeCompare(
              String(b.first_name || b.username || ""),
              "fr",
            ),
          );
        setAvailableTablesForTransfer(sortedFreeTables);
        setAvailableServersForTransfer(servers);
        if (stewardModeEnabled) await loadStewardOverview();
        setTableTransferMode("table");
        setShowTableTransferModal(true);
      } else {
        triggerErrorAnimation(
          isBoutique
            ? "Erreur lors du chargement."
            : "Erreur lors du chargement des tables !",
        );
      }
    } catch (error) {
      console.error("�?� Erreur lors du chargement des tables:", error);
      triggerErrorAnimation(
        isBoutique
          ? "Erreur lors du chargement."
          : "Erreur lors du chargement des tables !",
      );
    }
  };

  const confirmServerTransfer = async (server) => {
    if (!selectedTable || !server || isTableOperationInProgress) return;
    setIsTableOperationInProgress(true);
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/tables/${selectedTable.id}/takeover`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            newOwnerUserId: server.id,
            reason: "Transfert depuis la caisse",
          }),
        },
        10000,
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || "Le transfert n'a pas été enregistré.");
      }
      const ownerName = payload?.newOwnerName || server.first_name || server.username;
      setSelectedTable((previous) => {
        const next = {
          ...previous,
          managed_by_user_id: server.id,
          managed_by_first_name: ownerName,
          managed_by_role: server.role,
          active_owner_user_id: server.id,
          active_owner_first_name: ownerName,
          active_owner_role: server.role,
          active_owner_name: ownerName,
        };
        selectedTableRef.current = next;
        return next;
      });
      setShowTableTransferModal(false);
      setAvailableServersForTransfer([]);
      await loadTables({ forceRefresh: true });
      triggerSuccessAnimation(
        `${ownerName} est désormais en charge de la table ${selectedTable.number}.`,
      );
    } catch (error) {
      triggerErrorAnimation(
        `Transfert non effectué : ${error?.message || "erreur serveur"}`,
      );
    } finally {
      setIsTableOperationInProgress(false);
    }
  };

  // Fonction pour confirmer le transfert vers une nouvelle table
  const confirmTableTransfer = async (targetTable, options = {}) => {
    if (!selectedTable || !targetTable || isTableOperationInProgress) return;
    const sourceTable = selectedTable;
    const isStewardTransfer = options?.mode === "steward";
    const draftItems = (currentOrder.items || []).filter((item) => !item.isPaid);
    const targetDraftItems = isStewardTransfer
      ? getSnapshotPendingItems(targetTable.id)
      : [];
    setIsTableOperationInProgress(true);
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/tables/transfer`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            buildTableTransferPayload({
              sourceTable,
              targetTable,
              stewardModeEnabled,
              mode: isStewardTransfer ? "steward" : "table",
            }),
          ),
        },
        10000,
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          payload?.error || "Le transfert n'a pas été enregistré.",
        );
      }

      // La remise suit le service déplacé, jamais le simple numéro de table.
      // Elle est réécrite avec l'identité de la cible avant la sélection UI.
      try {
        const sourceKey = buildInvoiceAdjustmentsStorageKey(sourceTable);
        const targetKey = buildInvoiceAdjustmentsStorageKey(targetTable);
        const stored = sourceKey ? localStorage.getItem(sourceKey) : null;
        const draft = readInvoiceAdjustmentDraft(
          stored,
          invoiceAdjustmentContext,
        );
        if (
          targetKey &&
          stored &&
          !draft.discard &&
          draft.adjustments.length > 0
        ) {
          const targetContext = {
            ...invoiceAdjustmentContext,
            kind: "table",
            tableRef: String(targetTable.id ?? targetTable.number),
          };
          localStorage.setItem(
            targetKey,
            serializeInvoiceAdjustmentDraft({
              context: targetContext,
              adjustments: draft.adjustments.map((adjustment) => ({
                ...adjustment,
                draftContext: targetContext,
              })),
            }),
          );
        }
        if (sourceKey) localStorage.removeItem(sourceKey);
      } catch (error) {
        console.warn("Remise temporaire non transférée:", error);
      }

      forgetKnownExistingItems(sourceTable.id, false, {
        preservePending: false,
      });
      updateTableStateCache(
        sourceTable.id,
        {
          currentOrder: { items: [], total: 0 },
          existingItems: [],
          hasExistingOrders: false,
        },
        { isDelivery: false },
      );
      const updatedTargetTable = {
        ...targetTable,
        status: "occupied",
        customerCount: isStewardTransfer
          ? Number(targetTable.customerCount || targetTable.customer_count || 0) +
            Number(sourceTable.customerCount || sourceTable.customer_count || 0)
          : sourceTable.customerCount ?? null,
      };
      setSelectedTable(updatedTargetTable);
      selectedTableRef.current = updatedTargetTable;
      const transferredDraftOrder = {
        items: [...targetDraftItems, ...draftItems],
        total: computeItemsTotal([...targetDraftItems, ...draftItems]),
      };
      setCurrentOrder(transferredDraftOrder);
      setExistingItemsForPayment([]);
      setHasExistingOrders(false);
      persistCartStateForTable(
        updatedTargetTable,
        transferredDraftOrder,
        [],
      );
      setShowTableTransferModal(false);
      setAvailableTablesForTransfer([]);

      await loadTables();
      await loadTableItemsForPayment(targetTable.id, false);
      if (isStewardTransfer) await loadStewardOverview();
      triggerSuccessAnimation(
        `Commande transférée de la table ${sourceTable.number} vers la table ${targetTable.number} !`,
      );
    } catch (error) {
      console.error("❌ Erreur lors du transfert:", error);
      triggerErrorAnimation(
        `Transfert non effectué : ${error?.message || "erreur serveur"}`,
      );
    } finally {
      setIsTableOperationInProgress(false);
    }
  };

  // Fonction pour initier la fusion de tables
  const handleTableMerge = async () => {
    cashDebugLog("🔄 Début handleTableMerge - État actuel:", {
      selectedTable: selectedTable
        ? {
            id: selectedTable.id,
            number: selectedTable.number,
            status: selectedTable.status,
            customerCount: selectedTable.customerCount,
          }
        : null,
      currentOrderItems: currentOrder.items.length,
      existingItemsForPayment: existingItemsForPayment.length,
    });

    if (!selectedTable) {
      triggerErrorAnimation(
        isBoutique ? "Aucun panier actif." : "Aucune table sélectionnée !",
      );
      return;
    }

    try {
      // Charger les tables occupées disponibles pour fusion
      const response = await fetch(`${API_BASE}/tables`);
      if (response.ok) {
        const allTables = await response.json();
        const occupiedTables = allTables.filter(
          (table) =>
            table.status === "occupied" &&
            table.id !== selectedTable.id &&
            !fusedAwayTables.has(table.id),
        );

        if (occupiedTables.length === 0) {
          triggerErrorAnimation(
            "Aucune table occupée disponible pour fusion !",
          );
          return;
        }

        setAvailableTablesForMerge(occupiedTables);
        setShowTableMergeModal(true);
      } else {
        triggerErrorAnimation(
          isBoutique
            ? "Erreur lors du chargement."
            : "Erreur lors du chargement des tables !",
        );
      }
    } catch (error) {
      console.error("�?� Erreur lors du chargement des tables:", error);
      triggerErrorAnimation(
        isBoutique
          ? "Erreur lors du chargement."
          : "Erreur lors du chargement des tables !",
      );
    }
  };

  // Fonction pour confirmer la fusion de tables
  const confirmTableMerge = async (sourceTable) => {
    if (!selectedTable || !sourceTable || isTableOperationInProgress) return;
    const targetTable = selectedTable;
    const targetDraftItems = getSnapshotPendingItems(targetTable.id);
    const sourceDraftItems = getSnapshotPendingItems(sourceTable.id);
    const mergedDraftItems = mergeDraftLines(
      targetDraftItems,
      sourceDraftItems,
    );
    setIsTableOperationInProgress(true);
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/tables/merge`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            targetTableId: targetTable.id,
            sourceTableId: sourceTable.id,
          }),
        },
        10000,
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          payload?.error || "La fusion n'a pas été enregistrée.",
        );
      }

      // Deux services peuvent porter des remises calculées sur des bases
      // différentes. Les additionner silencieusement serait faux : on les
      // retire et l'opérateur les revalide sur le panier fusionné.
      let mergeDiscardedAdjustments = false;
      try {
        const targetAdjustmentKey = buildInvoiceAdjustmentsStorageKey(targetTable);
        const sourceAdjustmentKey = buildInvoiceAdjustmentsStorageKey(sourceTable);
        mergeDiscardedAdjustments = Boolean(
          (targetAdjustmentKey && localStorage.getItem(targetAdjustmentKey)) ||
            (sourceAdjustmentKey && localStorage.getItem(sourceAdjustmentKey)),
        );
        if (targetAdjustmentKey) localStorage.removeItem(targetAdjustmentKey);
        if (sourceAdjustmentKey) localStorage.removeItem(sourceAdjustmentKey);
        setTempAdjustments([]);
      } catch (error) {
        console.warn("Nettoyage des remises de fusion incomplet:", error);
      }

      const updatedTargetTable = {
        ...targetTable,
        status: "occupied",
        customerCount:
          payload?.mergedCustomerCount ??
          Number(targetTable.customerCount || 0) +
            Number(sourceTable.customerCount || 0),
      };
      setSelectedTable(updatedTargetTable);
      selectedTableRef.current = updatedTargetTable;
      const mergedDraftOrder = {
        items: mergedDraftItems,
        total: computeItemsTotal(mergedDraftItems),
      };
      setCurrentOrder(mergedDraftOrder);
      currentOrderRef.current = mergedDraftOrder;
      persistCartStateForTable(
        updatedTargetTable,
        mergedDraftOrder,
        existingItemsForPaymentRef.current,
      );

      // La cible est persistée avant la purge de la source : une interruption
      // peut au pire laisser un doublon récupérable, jamais perdre un brouillon.
      forgetKnownExistingItems(sourceTable.id, false, {
        preservePending: false,
      });
      updateTableStateCache(
        sourceTable.id,
        {
          currentOrder: { items: [], total: 0 },
          existingItems: [],
          hasExistingOrders: false,
        },
        { isDelivery: false },
      );
      setExistingItemsForPayment([]);
      setHasExistingOrders(false);
      setTableMerges((previous) => ({
        ...previous,
        [targetTable.id]: [
          ...(previous[targetTable.id] || []),
          sourceTable.number,
        ],
      }));
      setFusedAwayTables((previous) =>
        new Set([...previous, sourceTable.id]),
      );
      setShowTableMergeModal(false);
      setAvailableTablesForMerge([]);

      await loadTables();
      await loadTableItemsForPayment(targetTable.id, false);
      triggerSuccessAnimation(
        mergeDiscardedAdjustments
          ? `Tables fusionnées. Les remises temporaires ont été retirées : vérifiez le nouveau total.`
          : `Table ${sourceTable.number} fusionnée avec succès dans la table ${targetTable.number} !`,
      );
    } catch (error) {
      console.error("❌ Erreur lors de la fusion:", error);
      triggerErrorAnimation(
        `Fusion non effectuée : ${error?.message || "erreur serveur"}`,
      );
    } finally {
      setIsTableOperationInProgress(false);
    }
  };

  // Nouvelle fonction pour traiter le paiement réel depuis le modal

  const sendToCuisineBar = async (options = {}) => {
    const suppressDispatch = options?.suppressDispatch === true;
    let clientRequestId = options?.clientRequestId
      ? String(options.clientRequestId)
      : null;
    cashDebugLog("🚚 DEBUG: sendToCuisineBar - selectedTable:", {
      id: selectedTable?.id,
      isDelivery: selectedTable?.isDelivery,
      deliveryMode: selectedTable?.deliveryMode,
      name: selectedTable?.name,
      number: selectedTable?.number,
    });

    if (selectedTableAccessConflict) {
      triggerErrorAnimation(
        formatTableAccessConflictMessage(selectedTableAccessConflict),
      );
      return;
    }

    if (isSelectedTableReconciliationInFlight()) {
      triggerErrorAnimation("Resynchronisation de la table en cours…");
      return;
    }

    let isDeliveryOrder =
      !!selectedTable?.isDelivery ||
      (typeof selectedTable?.id === "string" &&
        selectedTable.id.startsWith("delivery-"));
    const currentItems = isDeliveryOrder
      ? currentDeliveryOrder.items
      : currentOrder.items;

    cashDebugLog("🚚 DEBUG: sendToCuisineBar - détection:", {
      isDeliveryOrder,
      currentItemsCount: currentItems.length,
      deliveryItemsCount: currentDeliveryOrder.items.length,
      regularItemsCount: currentOrder.items.length,
    });

    if (currentItems.length === 0 || !selectedTable) {
      triggerErrorAnimation(
        isBoutique
          ? "Ajoutez des articles au panier."
          : "Sélectionnez une table et ajoutez des articles !",
      );
      return;
    }

    if (!clientRequestId) {
      clientRequestId = buildOrderSendRequestId({
        operation: "send",
        deviceId: getPersistentDeviceId(),
        tableId: getTableIdentifier(selectedTable),
        items: currentItems,
      });
    }

    if (orderActionInFlightRef.current) {
      return;
    }
    orderActionInFlightRef.current = true;
    setIsOrderActionInProgress(true);
    setOrderActionKind("send");
    const optimisticPrefix = buildOptimisticOrderItemIdPrefix("send");
    const optimisticMarkedSnapshotItems = markCartItemsOptimistically(
      currentItems,
      optimisticPrefix,
    );
    const initialDeliveryOrder = isDeliveryOrder;

    // ⚡ UX: dès le clic, basculer les items en "Déjà Envoyé(s)" pour éviter les doubles clics
    if (initialDeliveryOrder) {
      setCurrentDeliveryOrder((prev) => ({
        ...prev,
        items: markCartItemsOptimistically(prev.items, optimisticPrefix),
      }));
    } else {
      setCurrentOrder((prev) => ({
        ...prev,
        items: markCartItemsOptimistically(prev.items, optimisticPrefix),
      }));
    }

    try {
      // Si c'est une table de livraison temporaire, générer le numéro définitif maintenant
      let finalTable = selectedTable;
      if (selectedTable.isDelivery && selectedTable.isTemporary) {
        const orderNumber = generateDeliveryOrderNumber();
        finalTable = {
          ...selectedTable,
          number: orderNumber,
          name: orderNumber,
          isTemporary: false,
        };
        setSelectedTable(finalTable);
        cashDebugLog("🔢 Numéro de commande de livraison généré:", orderNumber);
      }

      try {
        forcedFreeTablesRef.current.delete(finalTable.id);
      } catch (_) {}

      isDeliveryOrder =
        !!finalTable?.isDelivery ||
        (typeof finalTable?.id === "string" &&
          finalTable.id.startsWith("delivery-"));

      const effectiveDeliveryMode =
        finalTable.deliveryMode || deliveryMode || "delivery";
      const effectiveDeliveryMethod =
        finalTable.deliveryMethod || deliveryMethod || "Direct";
      const effectiveCustomerRaw = resolveDeliveryCustomer({
        table: finalTable,
        method: effectiveDeliveryMethod,
        reference:
          effectiveDeliveryMethod !== "Direct"
            ? deliveryRef ||
              finalTable?.deliveryRef ||
              finalTable?.deliveryOrder?.deliveryRef ||
              null
            : null,
      });
      const effectiveCustomer = serializeDeliveryCustomer(effectiveCustomerRaw);

      // Transformer les articles pour correspondre au format attendu par l'API
      const transformedItems = currentItems
        .map((item) => {
          const productId =
            item.product_id ?? item.productId ?? item.id ?? item.product?.id ?? null;
          if (!productId) return null;

          const catalogProduct = Array.isArray(products)
            ? products.find((p) => Number(p?.id) === Number(productId))
            : null;
          const resolvedVariation = resolveVariationForPayload(item, catalogProduct);
          let unitPrice = parseMoney(
            item.unitPrice ??
              item.unit_price ??
              item.price ??
              item.product_price ??
              0,
          );
          if (
            catalogProduct?.product_type === "variable" &&
            resolvedVariation.variation
          ) {
            const vPrice = getUnitPriceForSelectionMode(
              catalogProduct,
              resolvedVariation.variation,
              activeAggregator,
            );
            if (vPrice) unitPrice = vPrice;
          }

          return {
            productId,
            client_line_id:
              item?.client_line_id ?? item?.clientLineId ?? null,
            quantity: item.quantity,
            unitPrice,
            destination: normalizeDestination(item.destination),
            extras: item.extras || [],
            variationId: resolvedVariation.variationId ?? null,
            variationName: resolvedVariation.variationName ?? null,
            variationAttributes: resolvedVariation.variationAttributes ?? null,
            variationLabel: resolvedVariation.variationLabel ?? null,
            courseNumber: Number(item?.courseNumber ?? item?.course_number ?? 1),
          };
        })
        .filter(Boolean);
      cashDebugLog(
        "🧭 DEBUG destinations (sendToCuisineBar):",
        transformedItems.reduce((acc, it) => {
          acc[it.destination] = (acc[it.destination] || 0) + 1;
          return acc;
        }, {}),
        {
          tableId: finalTable.id,
          itemsCount: transformedItems.length,
          apiBase: API_BASE,
        },
      );

      const visitorCountPayload = getVisitorCountForTable(finalTable);

      // Créer la commande via API
      const response = await sendOrderMutationWithRecovery({
        url: `${API_BASE}/orders`,
        options: {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tableId: getApiTableId(finalTable),
            items: transformedItems,
            sessionId: currentSession?.id,
            customerId: selectedCustomerForOrder?.id || effectiveCustomer?.id,
            customerPhone:
              selectedCustomerForOrder?.phone || effectiveCustomer?.phone,
            isDelivery: isDeliveryOrder,
            deliveryMode: isDeliveryOrder ? effectiveDeliveryMode : undefined,
            deliveryMethod: isDeliveryOrder ? effectiveDeliveryMethod : undefined,
            deliveryCustomer: isDeliveryOrder
              ? serializeDeliveryCustomer(effectiveCustomer)
              : undefined,
            visitorCount: visitorCountPayload,
            courseManagementEnabled:
              courseModeEnabled && courseModeEligibleForTable(finalTable),
            courses:
              courseModeEnabled && courseModeEligibleForTable(finalTable)
                ? Array.from(
                    new Set(
                      transformedItems.map((item) => Number(item.courseNumber || 1)),
                    ),
                  ).map((courseNumber) => ({ courseNumber }))
                : undefined,
            suppressDispatch,
            client_request_id: clientRequestId,
            occupancy_cycle:
              Number(options?.occupancyCycle) ||
              resolveTableOccupancyCycle(finalTable),
          }),
        },
        clientRequestId,
        operation: "create-order",
      });

      if (response.ok) {
        const result = await response.json();
        if (!isDeliveryOrder && finalTable?.id) {
          draftRecoveryTableIdsRef.current.delete(String(finalTable.id));
          const recoveryCacheKey = buildPersistentCartKey(finalTable.id, false);
          if (persistentCartCacheRef.current.has(recoveryCacheKey)) {
            upsertPersistentCartCacheEntry(
              persistentCartCacheRef.current,
              recoveryCacheKey,
              { recoveryRequired: false, recoveryReason: "" },
            );
            writePersistentCartCache(persistentCartCacheRef.current);
          }
        }
        cashDebugLog("✅ Commande créée avec succès:", result);
        if (
          courseModeEnabled &&
          Array.isArray(result?.courses) &&
          result.courses.length > 0
        ) {
          orderCoursesRef.current = result.courses;
          setOrderCourses(result.courses);
          const latestSavedCourse = Math.max(
            ...result.courses
              .filter((course) => String(course?.status || "") !== "cancelled")
              .map((course) => Number(course?.course_number) || 1),
          );
          await advanceAfterCourseMutation({
            orderId: result.orderId,
            courses: result.courses,
            courseNumber: latestSavedCourse,
            table: finalTable,
          });
        }

        const optimisticSentItems = optimisticMarkedSnapshotItems.filter(
          (item) => hasOptimisticPrefix(item?.order_item_id, optimisticPrefix),
        );
        const addedTotal = computeItemsTotal(optimisticSentItems);
        const deliveryStateItems =
          finalTable.isDelivery
            ? markDeliveryItemsAsSent(
                optimisticSentItems,
                result.orderId || finalTable.orderId || finalTable.id || "delivery",
              )
            : optimisticSentItems;
        const resolvedDisplayNumber =
          result.kitchenNumber || result.barNumber || finalTable.number;

        setSelectedTable((prev) => {
          if (!prev || prev.id !== finalTable.id) return prev;
          const updated = {
            ...prev,
            isTemporary: false,
          };
          if (finalTable.isDelivery) {
            updated.number = resolvedDisplayNumber;
            updated.deliveryRef =
              finalTable.deliveryMethod !== "Direct"
                ? deliveryRef || prev.deliveryRef
                : prev.deliveryRef;
          }
          return updated;
        });
        finalTable = {
          ...finalTable,
          isTemporary: false,
        };
        if (finalTable.isDelivery) {
          finalTable = {
            ...finalTable,
            number: resolvedDisplayNumber,
            deliveryRef:
              finalTable.deliveryMethod !== "Direct"
                ? deliveryRef || finalTable.deliveryRef || null
                : finalTable.deliveryRef || null,
          };
        }

        // WebSocket sera géré automatiquement par le serveur lors de la création de commande
        // Pas besoin d'émettre manuellement ici pour éviter les doublons

        // 🔒 Marquer l'heure d'envoi pour éviter libération/vidage prématurés
        try {
          lastSendAtRef.current.set(finalTable.id, Date.now());
        } catch (_) {}

        // Message de succès adapté selon le type de commande
        const successMessage = finalTable.isDelivery
          ? `Commande de ${finalTable.deliveryMode === "delivery" ? "livraison" : "à emporter"} envoyée avec succès!`
          : `Commande envoyée avec succès pour Table ${finalTable.number}!`;

        triggerSuccessAnimation(successMessage);

        // ✅ L'état sélectionné et sa ref doivent refléter l'occupation immédiatement.
        if (!finalTable.isDelivery) {
          bumpTableCartGeneration(finalTable.id, { isDelivery: false });
          markTableOccupiedInUi(finalTable.id);
        }
        scheduleLoadTables("order-created");

        // Si c'est une commande de livraison, gérer intelligemment les duplicatas
        if (finalTable.isDelivery) {
          const normalizedTableId = `delivery-${result.orderId}`;
        const newDeliveryOrder = {
          id: result.id || Date.now(),
          orderId: result.orderId,
          orderNumber: result.orderNumber,
          type: effectiveDeliveryMode,
          deliveryMethod: effectiveDeliveryMethod,
          deliveryRef:
            effectiveDeliveryMethod !== "Direct"
              ? deliveryRef || finalTable?.deliveryRef || null
              : finalTable?.deliveryRef || null,
          customer: effectiveCustomer,
            items: deliveryStateItems,
            total: addedTotal,
            status: "pending",
            createdAt: new Date(),
          };

          const updatedTable = {
            ...finalTable,
            id: normalizedTableId,
            orderId: result.orderId,
            number: resolvedDisplayNumber,
            name: resolvedDisplayNumber,
            isTemporary: false,
            deliveryCustomer: effectiveCustomer,
            deliveryMode: effectiveDeliveryMode,
            deliveryMethod: effectiveDeliveryMethod,
            deliveryRef:
              effectiveDeliveryMethod !== "Direct"
                ? deliveryRef || finalTable.deliveryRef || null
                : finalTable.deliveryRef || null,
            deliveryOrder: newDeliveryOrder,
          };
          setSelectedTable(updatedTable);
          setSelectedDeliveryOrder(newDeliveryOrder);
          setActiveClientTab((prev) =>
            prev && prev.id === finalTable.id ? { ...prev, ...updatedTable } : prev,
          );

          setCurrentDeliveryOrder((prev) => {
            const remaining = (prev.items || []).filter(
              (item) => !hasOptimisticPrefix(item?.order_item_id, optimisticPrefix),
            );
            return { items: remaining, total: computeItemsTotal(remaining) };
          });

          setClientTabs((prev) => {
            let found = false;
            const next = prev.map((tab) => {
              if (tab.id !== finalTable.id) return tab;
              found = true;
              const persistedItems = filterPersistedDeliveryItems(tab.items);
              const mergedItems = [...persistedItems, ...deliveryStateItems];
              return {
                ...tab,
                id: normalizedTableId,
                orderId: result.orderId,
                number: resolvedDisplayNumber,
                name: resolvedDisplayNumber,
                isTemporary: false,
                deliveryCustomer: effectiveCustomer,
                deliveryMode: effectiveDeliveryMode,
                deliveryMethod: effectiveDeliveryMethod,
                orderNumber: result.orderNumber,
                items: mergedItems,
                total: computeItemsTotal(mergedItems),
                deliveryOrder: newDeliveryOrder,
              };
            });
            if (!found) {
              next.push({
                id: normalizedTableId,
                orderId: result.orderId,
                number: resolvedDisplayNumber,
                name: resolvedDisplayNumber,
                isDelivery: true,
                deliveryCustomer: effectiveCustomer,
                deliveryMode: effectiveDeliveryMode,
                deliveryMethod: effectiveDeliveryMethod,
                status: "occupied",
                isTemporary: false,
                orderNumber: result.orderNumber,
                items: deliveryStateItems,
                total: computeItemsTotal(deliveryStateItems),
                deliveryOrder: newDeliveryOrder,
              });
            }
            return next;
          });

          finalTable = updatedTable;
          try {
            lastSendAtRef.current.set(finalTable.id, Date.now());
          } catch (_) {}

          setDeliveryOrders((prev) => {
            // Chercher si ce client a déjà une commande en cours non payée
            const existingOrderIndex = prev.findIndex(
              (order) =>
                order.status === "pending" &&
                isSameDeliveryCustomer(order.customer, effectiveCustomer),
            );

            if (existingOrderIndex !== -1) {
              // Client existe déjà avec commande non payée - fusionner les articles
              const updatedOrders = [...prev];
              const existingOrder = updatedOrders[existingOrderIndex];

              updatedOrders[existingOrderIndex] = {
                ...existingOrder,
                items: [...existingOrder.items, ...deliveryStateItems],
                total: existingOrder.total + addedTotal,
                orderNumber: result.orderNumber,
                orderId: result.orderId,
                deliveryMethod: effectiveDeliveryMethod,
                deliveryRef:
                  effectiveDeliveryMethod !== "Direct"
                    ? deliveryRef ||
                      existingOrder.deliveryRef ||
                      finalTable?.deliveryRef ||
                      null
                    : existingOrder.deliveryRef || null,
                updatedAt: new Date(),
              };

              cashDebugLog(
                `🔄 Commande fusionnée pour ${effectiveCustomer?.first_name || ""} ${effectiveCustomer?.last_name || ""}`,
              );
              return updatedOrders;
            } else {
              // Nouveau client ou client sans commande en cours - ajouter nouvelle entrée
              cashDebugLog(
                `✨ Nouvelle commande pour ${
                  effectiveCustomer
                    ? `${effectiveCustomer.first_name || ""} ${effectiveCustomer.last_name || ""}`
                    : "client anonyme"
                }`,
              );
              return [...prev, newDeliveryOrder];
            }
          });

          if (
            selectedTable?.isDelivery &&
            getTableIdentifier(selectedTable) === normalizedTableId
          ) {
            setSelectedDeliveryOrder(newDeliveryOrder);
          }

        setTimeout(() => {
          loadPendingDeliveries();
        }, 500);

          // Pour les livraisons, garder la table sélectionnée et recharger comme pour les tables normales
          // Ne pas réinitialiser deliveryMode et deliveryCustomer pour permettre les ajouts successifs

          // Marquer que la table a maintenant des commandes existantes
          setHasExistingOrders(true);

          // ✅ OPTIMISTE: Injecter immédiatement les articles envoyés dans existingDeliveryItems
          try {
            setExistingDeliveryItems((prev) => {
              const persistedPrev = filterPersistedDeliveryItems(prev);
              const next = mergeExistingItemsWithLatestFirst(
                deliveryStateItems,
                persistedPrev,
              );
              try {
                rememberKnownExistingItems(finalTable.id, next, true);
              } catch (_) {}
              return next;
            });
            // Filet de sécurité: ne jamais perdre visuellement les articles envoyés
            setTimeout(() => {
              setExistingDeliveryItems((prev) => {
                if ((prev?.length || 0) > 0) return prev;
                if ((deliveryStateItems?.length || 0) === 0) return prev;
                try {
                  rememberKnownExistingItems(finalTable.id, deliveryStateItems, true);
                } catch (_) {}
                setHasExistingOrders(true);
                return deliveryStateItems;
              });
            }, 900);
          } catch (_) {}
        } else {
          // Tables normales - Garder le client sélectionné pour la prochaine commande
          // setSelectedCustomerForOrder(null); // Commenté pour garder le client

          // Recharger les tables (déjà marqué en occupé plus haut)
          scheduleLoadTables("send-to-kitchen-bar-non-delivery");

          // Marquer que la table a maintenant des commandes existantes
          setHasExistingOrders(true);

          // ✅ OPTIMISTE: Injecter immédiatement les articles envoyés dans existingItemsForPayment
          try {
            setExistingItemsForPayment((prev) => {
              const next = mergeExistingItemsWithLatestFirst(
                deliveryStateItems,
                prev,
              );
              try {
                if (finalTable?.id) {
                  rememberKnownExistingItems(finalTable.id, next, false);
                }
                updateTableStateCache(
                  finalTable.id,
                  {
                    currentOrder: { items: [], total: 0 },
                    existingItems: next,
                    hasExistingOrders: true,
                  },
                  { isDelivery: false },
                );
              } catch (_) {}
              return next;
            });
            // Filet de sécurité: si le refresh API échoue, garder la preuve locale des items envoyés
            setTimeout(() => {
              setExistingItemsForPayment((prev) => {
                if ((prev?.length || 0) > 0) return prev;
                if ((deliveryStateItems?.length || 0) === 0) return prev;
                try {
                  if (finalTable?.id) {
                    rememberKnownExistingItems(finalTable.id, deliveryStateItems, false);
                  }
                } catch (_) {}
                setHasExistingOrders(true);
                return deliveryStateItems;
              });
            }, 900);
          } catch (_) {}
        }

        // 🔧 CORRECTION PANIER - Gestion unifiée du state après envoi
        if (isDeliveryOrder) {
          // Marquer qu'il y a maintenant des commandes existantes
          setHasExistingOrders(true);
          cashDebugLog("✅ hasExistingOrders mis à jour pour livraison");

          // CORRECTION TIMING: Rechargement avec retries pour attendre la persistance
          const retryReloadDelivery = async (attempt = 1) => {
            try {
              const items = await loadTableItemsForPayment(
                finalTable.id,
                false,
              );
              if ((items?.length || 0) === 0 && attempt < 3) {
                cashDebugLog(
                  `�?� Livraison: items encore vides (tentative ${attempt}) → retry...`,
                );
                setTimeout(() => retryReloadDelivery(attempt + 1), 600);
              } else {
                cashDebugLog(
                  `🔄 Livraison: rechargement terminé (items=${items?.length || 0})`,
                );
              }
            } catch (error) {
              console.error(
                "�?� Erreur rechargement articles livraison:",
                error,
              );
            }
          };
          setTimeout(() => retryReloadDelivery(1), 500);
        } else {
          // Tables normales - marquer hasExistingOrders AVANT de vider le panier
          cashDebugLog("🔧 DIGITALIVORE DEBUG: Avant vidage panier - États:", {
            hasExistingOrders,
            currentOrderItems: currentOrder.items.length,
            existingItemsForPayment: existingItemsForPayment.length,
          });
          setHasExistingOrders(true);

          // Retirer seulement les items envoyés (et laisser les ajouts faits pendant l'envoi)
          setCurrentOrder((prev) => {
            const remaining = (prev.items || []).filter(
              (item) => !hasOptimisticPrefix(item?.order_item_id, optimisticPrefix),
            );
            return { items: remaining, total: computeItemsTotal(remaining) };
          });
          cashDebugLog("🧹 Panier de table vidé après envoi");

          // CORRECTION TIMING: Rechargement avec retries pour les tables normales
          const retryReloadTable = async (attempt = 1) => {
            try {
              const items = await loadTableItemsForPayment(
                finalTable.id,
                false,
              );
              if ((items?.length || 0) === 0 && attempt < 3) {
                cashDebugLog(
                  `�?� Table: items encore vides (tentative ${attempt}) → retry...`,
                );
                setTimeout(() => retryReloadTable(attempt + 1), 600);
              } else {
                cashDebugLog(
                  `🔄 Table: rechargement terminé (items=${items?.length || 0})`,
                );
              }
            } catch (error) {
              console.error("�?� Erreur rechargement articles table:", error);
            }
          };
          setTimeout(() => retryReloadTable(1), 500);
        }

        cashDebugLog(
          "🔧 DIGITALIVORE FIX: Commande envoyée avec succès - État final:",
          {
            tableId: finalTable.id,
            isDelivery: finalTable.isDelivery,
            hasExistingOrders: true,
            panierVide: isDeliveryOrder
              ? currentDeliveryOrder.items.length === 0
              : currentOrder.items.length === 0,
          },
        );
      } else {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Erreur serveur" }));
        console.error("�?� Erreur API:", response.status, errorData);
        const errorCode = String(errorData?.code || "").toUpperCase();
        if (errorCode === "TABLE_ASSIGNED") {
          throw new Error(
            errorData?.error || "Cette table est gérée par un autre serveur.",
          );
        }
        throw Object.assign(
          new Error(
            `Erreur ${response.status}: ${errorData.error || errorData.message || "Erreur serveur"}`,
          ),
          {
            code: errorData?.code || null,
            currentCycle: Number(errorData?.currentCycle) || null,
          },
        );
      }
    } catch (error) {
      console.error("�?� DIGITALIVORE FIX: Erreur lors de l'envoi:", error);

      const outcomeUnknown =
        String(error?.code || "") === "REQUEST_OUTCOME_UNKNOWN";
      if (outcomeUnknown) {
        scheduleLoadTables("create-order-outcome-unknown");
        setTimeout(() => {
          if (selectedTable?.id) {
            void loadTableItemsForPayment(selectedTable.id, false);
          }
        }, 1200);
        triggerErrorAnimation(error.message);
        return;
      }

      if (
        ["TABLE_CYCLE_REQUIRED", "TABLE_CYCLE_CHANGED"].includes(
          String(error?.code || ""),
        ) &&
        !initialDeliveryOrder &&
        selectedTable?.id
      ) {
        const recoveredItems = rollbackOptimisticCartItems(
          optimisticMarkedSnapshotItems,
          optimisticPrefix,
        );
        stageTableDraftRecovery({
          tableId: selectedTable.id,
          pendingItems: recoveredItems,
          occupancyCycle:
            Number(error?.currentCycle) ||
            resolveTableOccupancyCycle(selectedTable),
          reason: "cycle-changed-during-send",
        });
        return;
      }

      // Rollback: remettre les articles en "Nouveaux Ajouts" si l'API échoue
      try {
        if (initialDeliveryOrder) {
          setCurrentDeliveryOrder((prev) => {
            const nextItems = rollbackOptimisticCartItems(
              prev.items,
              optimisticPrefix,
            );
            return { items: nextItems, total: computeItemsTotal(nextItems) };
          });
        } else {
          setCurrentOrder((prev) => {
            const nextItems = rollbackOptimisticCartItems(
              prev.items,
              optimisticPrefix,
            );
            return { items: nextItems, total: computeItemsTotal(nextItems) };
          });
        }
      } catch (_) {}

      // Message d'erreur plus descriptif
      let errorMessage = "Erreur lors de l'envoi de la commande";
      if (error.message?.includes("Failed to fetch")) {
        errorMessage =
          "Impossible de joindre le serveur. Vérifiez votre connexion.";
      } else if (error.message?.includes("500")) {
        errorMessage = "Erreur interne du serveur. Contactez l'administrateur.";
      } else if (error.message?.includes("400")) {
        errorMessage =
          "�?� Erreur! Données de commande invalides. Vérifiez les articles sélectionnés.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      triggerErrorAnimation(errorMessage);
    } finally {
      orderActionInFlightRef.current = false;
      setIsOrderActionInProgress(false);
      setOrderActionKind(null);
    }
  };

  // Fonction pour traiter les paiements via le modal PaymentInterface
  const handlePaymentProcess = async (paymentData) => {
    try {
      cashDebugLog("🔧 DIGITALIVORE FIX: Traitement paiement:", paymentData);
      // Cette fonction sera implémentée selon les besoins spécifiques du paiement
      return true;
    } catch (error) {
      console.error("�?� Erreur traitement paiement:", error);
      return false;
    }
  };

  const addCashRegisterEntry = async (
    orderId,
    tableNumber,
    totalAmount,
    paymentMethod = "cash",
    cashierName = "Caisseur",
    notes = "",
    amountIn = null,
    amountOut = null,
  ) => {
    try {
      const response = await fetch(`${API_BASE}/cash-register/entry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderId,
          tableNumber,
          totalAmount,
          paymentMethod,
          cashierName,
          notes,
          amountIn,
          amountOut,
        }),
      });

      if (response.ok) {
        cashDebugLog("✅ Entrée journal de caisse ajoutée avec succès");
        loadCashRegisterLogWithFallback();
        loadCashRegisterSummary();
        triggerSuccessAnimation("Vente directe enregistrée avec succès !");
      } else {
        console.error(
          "�?� Erreur réponse API journal de caisse:",
          response.status,
        );
      }
    } catch (error) {
      console.error("�?� Erreur ajout entrée journal de caisse:", error);
    }
  };

  // Fonction pour vérifier si le panier est vide et libérer la table si nécessaire
  const checkAndFreeTableIfEmpty = async (tableOverride = null) => {
    const targetTable = tableOverride || selectedTable;
    if (!targetTable || isCheckingTableFree) return;
    if (targetTable.status === "free" || targetTable.isDelivery) return;
    const tableId = targetTable.id;
    if (!tableId) return;

    const lastSendTs = lastSendAtRef.current.get(tableId);
    if (lastSendTs && Date.now() - lastSendTs < 12000) {
      return;
    }

    setIsCheckingTableFree(true);

    try {
      cashDebugLog(`�? Vérification de la table ${targetTable.number}...`);

      // Vérifier les commandes non payées
      const response = await fetch(`${API_BASE}/tables/${tableId}/orders`);
      if (response.ok) {
        const orders = await response.json();

        const unpaidOrders = orders.filter(
          (order) =>
            order.payment_status !== "paid" && order.status !== "cancelled",
        );

        const hasUnpaidOrders = unpaidOrders.length > 0;
        const hasActiveItems = unpaidOrders.some((order) =>
          (order.items || []).some(
            (item) => item && item.status !== "cancelled" && !item.cancelled_at,
          ),
        );

        cashDebugLog(
          `📊 Table ${targetTable.number}: ${unpaidOrders.length} commandes non payées, ${hasActiveItems ? "avec" : "sans"} articles actifs`,
        );

        const isSelected = selectedTable?.id === tableId;
        const selectedSnapshot = isSelected
          ? buildCurrentCartSnapshot(targetTable)
          : null;
        const cachedState =
          tableStatesCacheRef.current.get(tableId) ||
          tableStatesCacheRef.current.get(String(tableId));
        const cachedOrderItems = cachedState?.currentOrder?.items || [];
        const cachedExistingItems = cachedState?.existingItems || [];
        const cachedRefItems = lastExistingItemsRef.current.get(tableId)?.items || [];
        const hasLocalItems = isSelected
          ? currentOrder.items.length > 0
          : cachedOrderItems.length > 0;
        const hasExistingItems = isSelected
          ? existingItemsForPayment.length > 0
          : cachedExistingItems.length > 0 || cachedRefItems.length > 0;

        // CORRECTION: Ne libérer que si AUCUNE commande en cours (ni locale ni existante)
        if (!hasActiveItems && !hasLocalItems && !hasExistingItems) {
          cashDebugLog(
            `🆓 Libération automatique de la table ${targetTable.number}`,
          );

          const reconciliation = await reconcileEmptyTable(tableId, {
            deselect: isSelected,
            reason: hasUnpaidOrders ? "stale-unpaid-empty-orders" : "empty-table",
          });

          if (
            reconciliation?.reconciled !== true ||
            reconciliation?.status !== "free"
          ) {
            cashDebugLog(
              `🛡️ Nettoyage complémentaire ignoré pour la table ${targetTable.number}: réconciliation non confirmée`,
              reconciliation,
            );
            return;
          }

          // Mettre à jour l'interface
          if (isSelected) {
            if (snapshotHasItems(selectedSnapshot)) {
              cashDebugLog(
                "🛡�? Anti-panier fantôme: auto-free ignoré, snapshot local restauré",
                {
                  tableId,
                  snapshotItems:
                    (selectedSnapshot.pendingItems?.length || 0) +
                    (selectedSnapshot.existingItems?.length || 0),
                },
              );
              restoreCartFromSnapshot(selectedSnapshot);
              return;
            }
            setCurrentOrder({ items: [], total: 0 });
            setExistingItemsForPayment([]);
            setHasExistingOrders(false);
          }

          // Nettoyer le nombre de clients
          setTableCustomerCounts((prevCounts) => {
            const newCounts = { ...prevCounts };
            delete newCounts[tableId];
            return newCounts;
          });

          // Restaurer les tables qui étaient fusionnées dans cette table AVANT de nettoyer tableMerges
          const fusedTables = tableMerges[tableId] || [];
          const tableIdsToRestore = tables
            .filter((table) => fusedTables.includes(table.number))
            .map((table) => table.id);

          // Libérer les tables fusionnées en base de données
          for (const tableId of tableIdsToRestore) {
            try {
              await fetch(`${API_BASE}/tables/${tableId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: "free" }),
              });
              cashDebugLog(`✅ Table ${tableId} libérée après fusion`);
            } catch (error) {
              console.error(`�?� Erreur libération table ${tableId}:`, error);
            }
          }

          // Restaurer les tables fusionnées dans l'interface
          setFusedAwayTables((prev) => {
            const newSet = new Set(prev);
            tableIdsToRestore.forEach((id) => newSet.delete(id));
            return newSet;
          });

          // Nettoyer les informations de fusion APRÈS avoir restauré les tables
          setTableMerges((prevMerges) => {
            const newMerges = { ...prevMerges };
            delete newMerges[tableId];
            return newMerges;
          });

          scheduleLoadTables("check-and-free-table", {
            forceUnpaidRefresh: true,
          });

          // SUPPRESSION DU POPUP PROBLÉMATIQUE
          cashDebugLog(
            `✅ Table ${targetTable.number} libérée automatiquement`,
          );
        }
      }
    } catch (error) {
      console.error("�?� Erreur vérification table:", error);
    } finally {
      setIsCheckingTableFree(false);
    }
  };

  const clearOrder = () => {
    // Séparation stricte: ne jamais déplacer les articles existants dans le panier
    if (isFastFoodMode) {
      const counterRecovery =
        fastFoodCartSnapshotRef.current || readFastFoodCartSnapshotFromStorage();
      if (counterRecovery?.stage && counterRecovery.stage !== "editing") {
        triggerErrorAnimation(
          "Cette commande Comptoir existe déjà en base. Un manager doit l’annuler ou vous devez reprendre le paiement.",
        );
        return;
      }
      const activeCounterItems = (currentOrderRef.current?.items || []).filter(isItemActive);
      if (
        activeCounterItems.length > 0 &&
        !window.confirm("Annuler définitivement ce panier Comptoir ?")
      ) {
        return;
      }
      clearFastFoodCartSnapshot();
      setTempAdjustments([]);
      setDiscountAmount(0);
      setAdditionalFees(0);
    }
    const emptyOrder = { items: [], total: 0 };
    setCurrentOrder(emptyOrder);
    const selectedNow = selectedTableRef.current;
    if (selectedNow && !selectedNow.isDelivery && !isFastFoodMode) {
      persistCartStateForTable(
        selectedNow,
        emptyOrder,
        existingItemsForPaymentRef.current,
      );
    }
    cashDebugLog(
      "🧹 Panier vidé (séparation stricte: les existants restent séparés)",
    );
    // La vérification automatique se fera via le useEffect
  };

  // Fonction de fallback pour le journal de caisse
  const loadCashRegisterLogWithFallback = async () => {
    try {
      cashDebugLog("📋 Chargement journal de caisse avec fallback...");

      // Méthode 1: Essayer l'endpoint standard
      try {
        const cashierId = currentUser?.id || currentSession?.cashier_id;
        const cashierQuery = cashierId ? `&cashierId=${cashierId}` : "";
        const response = await fetch(
          `${API_BASE}/cash-register/log?startDate=${selectedDate}&endDate=${selectedDate}${cashierQuery}`,
        );
        if (response.ok) {
          const log = await response.json();
          setCashRegisterLog(Array.isArray(log) ? log : []);
          cashDebugLog("✅ Journal chargé via endpoint standard");
          return;
        }
      } catch (error) {
        cashDebugLog(
          "⚠�? Endpoint standard échoué, essai méthode alternative...",
        );
      }

      // Méthode 2: Utiliser les transactions de la session actuelle
      if (currentSession && currentSession.id) {
        try {
          const cashierId = currentUser?.id || currentSession?.cashier_id;
          const cashierQuery = cashierId ? `?cashierId=${cashierId}` : "";
          const response = await fetch(
            `${API_BASE}/cash-journal/transactions/${currentSession.id}${cashierQuery}`,
          );
          if (response.ok) {
            const transactions = await response.json();
            // Convertir les transactions en format journal
            const journalEntries = transactions.map((t) => ({
              id: t.id,
              type: "sale",
              amount: t.amount,
              description: `Vente - Commande`,
              created_at: t.created_at,
              cashier_id: currentSession.cashier_id,
            }));
            setCashRegisterLog(journalEntries);
            cashDebugLog("✅ Journal chargé via transactions de session");
            return;
          }
        } catch (error) {
          cashDebugLog("⚠�? Méthode alternative échouée");
        }
      }

      // Méthode 3: Journal vide avec message informatif
      setCashRegisterLog([
        {
          id: 0,
          type: "info",
          amount: 0,
          description:
            "Aucune donnée disponible - Session peut-être non initialisée",
          created_at: new Date().toISOString(),
          cashier_id: currentUser?.id || 0,
        },
      ]);
      cashDebugLog("ℹ�? Journal initialisé avec message informatif");
    } catch (error) {
      console.error("�?� Erreur chargement journal avec fallback:", error);
      setCashRegisterLog([]);
    }
  };

  // Fonction pour charger le journal de caisse
  const loadCashRegisterLog = async () => {
    try {
      const cashierId = currentUser?.id || currentSession?.cashier_id;
      const cashierQuery = cashierId ? `&cashierId=${cashierId}` : "";
      const response = await fetch(
        `${API_BASE}/cash-register/log?startDate=${selectedDate}&endDate=${selectedDate}${cashierQuery}`,
      );
      if (response.ok) {
        const log = await response.json();
        // S'assurer que log est un tableau
        setCashRegisterLog(Array.isArray(log) ? log : []);
      } else {
        console.error(
          "�?� Erreur réponse API journal de caisse:",
          response.status,
        );
        setCashRegisterLog([]);
      }
    } catch (error) {
      console.error("�?� Erreur chargement journal de caisse:", error);
      setCashRegisterLog([]);
    }
  };

  // Fonction pour charger le résumé du journal de caisse
  const loadCashRegisterSummary = async () => {
    try {
      const response = await fetch(`${API_BASE}/cash-register/summary`);
      if (response.ok) {
        const data = await response.json();
        setCashRegisterSummary(data);
      }
    } catch (error) {
      console.error("Erreur lors du chargement du résumé:", error);
    }
  };

  // Fonction pour initier l'annulation d'une commande
  const initiateCancellation = (order) => {
    cashDebugLog("🔥 [DIGITALIVORE DEBUG] initiateCancellation DÉBUT", {
      isPartialCancellation: order?.isPartialCancellation,
      requiresManagerAuth: order?.requiresManagerAuth,
      itemToCancel: order?.itemToCancel?.name,
      itemStatus: order?.itemToCancel?.status,
      timestamp: new Date().toISOString(),
    });
    cashDebugLog("🎬 initiateCancellation démarrée avec:", order);
    cashDebugLog("�? Type d'annulation:", {
      isPartialCancellation: order.isPartialCancellation,
      isCartCancellation: order.isCartCancellation,
      requiresManagerAuth: order.requiresManagerAuth,
    });

    setCancellationOrder(order);

    // Si c'est une annulation OAA, aller directement à l'étape auth
    cashDebugLog("�? Toute annulation commence par le motif");
    setCancellationStep("reason");

    setShowCancellationModal(true);
    cashDebugLog("✅ Modal d'annulation activée - État:", {
      showModal: true,
      step:
        order.requiresManagerAuth && order.isPartialCancellation
          ? "auth"
          : "reason",
      orderSet: !!order,
    });

    // FORCE DEBUG: Si la modal ne s'affiche pas, vérifier React state
    setTimeout(() => {
      cashDebugLog("�? VÉRIFICATION POST-SET:", {
        showCancellationModal,
        cancellationOrder,
        cancellationStep,
      });
    }, 100);
  };

  // Fonctions pour la gestion des logs de débogage
  const addDebugLog = (level, message, data = null) => {
    if (!CASH_DEBUG_ENABLED && !showDebugLogs) return;
    const timestamp = new Date().toISOString();
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp,
      level,
      message,
      data: data ? JSON.stringify(data, null, 2) : null,
    };

    setDebugLogs((prev) => [...prev, logEntry].slice(-100)); // Garder seulement les 100 derniers logs
    cashDebugLog(`[${level}] ${message}`, data);
  };

  const copyLogsToClipboard = async () => {
    const logsText = debugLogs
      .map((log) => {
        let logLine = `[${log.timestamp}] [${log.level}] ${log.message}`;
        if (log.data) {
          logLine += `\n${log.data}`;
        }
        return logLine;
      })
      .join("\n\n");

    try {
      await navigator.clipboard.writeText(logsText);
      triggerSuccessAnimation("Logs copiés dans le presse-papiers");
    } catch (err) {
      console.error("Erreur copie:", err);
      triggerErrorAnimation("Erreur lors de la copie");
    }
  };

  const clearDebugLogs = () => {
    setDebugLogs([]);
    triggerSuccessAnimation("Logs vidés");
  };

  const summarizeUiLogItem = (item = {}) => {
    const quantity = Number(item.quantity ?? item.qty ?? 1) || 1;
    const unitPrice = parseMoney(
      item.unit_price ??
        item.unitPrice ??
        item.price ??
        item.product_price ??
        item.productPrice ??
        item.basePrice ??
        0,
    );
    const totalRaw = parseMoney(
      item.totalPrice ??
        item.total_price ??
        item.total ??
        item.total_amount ??
        item.amount ??
        0,
    );
    const totalPrice = totalRaw || unitPrice * quantity;
    const productId = item.product_id ?? item.productId ?? item.product?.id ?? null;
    const orderItemId = item.order_item_id ?? item.id ?? null;

    return {
      orderItemId,
      productId,
      itemKey: item.itemKey ?? null,
      name: item.name ?? item.product_name ?? item.productName ?? "",
      status: item.status ?? item.state ?? null,
      quantity,
      unitPrice,
      totalPrice,
      raw: {
        price: item.price ?? null,
        product_price: item.product_price ?? null,
        unit_price: item.unit_price ?? null,
        totalPrice: item.totalPrice ?? null,
        total_price: item.total_price ?? null,
      },
      variation: {
        variation_id: item.variation_id ?? item.variationId ?? null,
        variation_name: item.variation_name ?? item.variationName ?? null,
        variation_label: item.variation_label ?? item.variationLabel ?? null,
        variation_attributes:
          item.variation_attributes ?? item.variationAttributes ?? null,
        display: getItemVariationDisplay(item) || null,
      },
      extras: Array.isArray(item.extras)
        ? item.extras.map((extra) => ({
            id: extra.id ?? extra.extra_id ?? extra.extraId ?? null,
            name: extra.name ?? extra.extra_name ?? extra.label ?? "",
            type: extra.type ?? null,
            quantity: Number(extra.quantity ?? extra.qty ?? 1) || 1,
            price: parseMoney(
              extra.price ??
                extra.unit_price ??
                extra.unitPrice ??
                extra.amount ??
                extra.total_price ??
                0,
            ),
          }))
        : [],
    };
  };

  const formatDebugLogEntry = (log) => {
    if (!log) return "";
    const dataText = log.data
      ? typeof log.data === "string"
        ? log.data
        : JSON.stringify(log.data, null, 2)
      : "";
    const header = `[${log.timestamp}] [${log.level}] ${log.message}`;
    return dataText ? `${header}\n${dataText}` : header;
  };

  const summarizeTablesForLog = (tablesList = []) => {
    const list = Array.isArray(tablesList) ? tablesList : [];
    const counts = {
      total: list.length,
      occupied: 0,
      free: 0,
      reserved: 0,
      maintenance: 0,
      other: 0,
    };
    const entries = list.map((table) => {
      const status = table?.status || "unknown";
      if (status in counts) {
        counts[status] += 1;
      } else {
        counts.other += 1;
      }
      return {
        id: table?.id ?? null,
        number: table?.number ?? table?.name ?? null,
        status,
        active_orders: table?.active_orders ?? table?.activeOrders ?? null,
        customer_count:
          table?.customerCount ?? table?.customer_count ?? null,
      };
    });
    return { counts, entries };
  };

  const buildUiDebugSnapshot = () => {
    const uiTables = summarizeTablesForLog(tables);
    const selectedSummary = selectedTable
      ? {
          id: selectedTable?.id ?? null,
          number: selectedTable?.number ?? selectedTable?.name ?? null,
          status: selectedTable?.status ?? null,
          isDelivery: !!selectedTable?.isDelivery,
          orderId:
            selectedTable?.orderId ??
            selectedTable?.deliveryOrder?.id ??
            null,
        }
      : null;

    const cacheSize = (() => {
      try {
        return tableStatesCache?.size ?? 0;
      } catch (_) {
        return 0;
      }
    })();
    const existingItemsCacheSize = (() => {
      try {
        return lastExistingItemsRef?.current?.size ?? 0;
      } catch (_) {
        return 0;
      }
    })();
    const cashSessionSummary = currentSession
      ? {
          id: currentSession?.id ?? null,
          status: currentSession?.status ?? null,
          cashier_id: currentSession?.cashier_id ?? null,
          session_start_time:
            currentSession?.session_start_time ??
            currentSession?.start_time ??
            currentSession?.created_at ??
            null,
          session_end_time:
            currentSession?.session_end_time ??
            currentSession?.end_time ??
            currentSession?.closed_at ??
            null,
          initial_amount: currentSession?.initial_amount ?? null,
          final_amount: currentSession?.final_amount ?? null,
          total_transactions: currentSession?.total_transactions ?? null,
        }
      : null;
    const localSalesEntries = Array.from(
      sessionSalesByMethodRef.current.values(),
    ).map((entry) => ({
      method: entry?.method ?? null,
      amount: Number(entry?.amount || 0),
      count: entry?.count || 0,
    }));
    const lastPaymentAt = lastPaymentAtRef.current || 0;

    return {
      location: window?.location?.pathname || null,
      user: {
        id: currentUser?.id ?? null,
        role: currentUser?.role ?? null,
        name: currentUser?.name ?? currentUser?.username ?? null,
      },
      cashSession: cashSessionSummary,
      localSales: {
        lastSessionId: lastSessionIdRef.current ?? null,
        hasLocalSales: !!hasLocalSalesRef.current,
        total: Number(localSalesTotalRef.current || 0),
        lastPaymentAt: lastPaymentAt || null,
        lastPaymentAgeMs: lastPaymentAt ? Date.now() - lastPaymentAt : null,
        byMethod: localSalesEntries,
      },
      activeTab,
      autoSelectDeliveryTab,
      selectedTable: selectedSummary,
      cart: {
        currentOrderItems: currentOrder?.items?.length || 0,
        existingItems: existingItemsForPayment?.length || 0,
        currentDeliveryItems: currentDeliveryOrder?.items?.length || 0,
        existingDeliveryItems: existingDeliveryItems?.length || 0,
        hasExistingOrders,
      },
      tables: uiTables,
      clientTabs: {
        total: clientTabs?.length || 0,
        deliveryTabs: (clientTabs || []).filter((t) => t?.isDelivery).length,
        activeClientTab:
          activeClientTab?.id ??
          activeClientTab?.orderId ??
          activeClientTab?.name ??
          null,
      },
      deliveries: {
        pendingCount: deliveryOrders?.length || 0,
        hiddenCount: hiddenDeliveryOrderIds?.length || 0,
      },
      cache: {
        tableStatesCacheSize: cacheSize,
        lastExistingItemsSize: existingItemsCacheSize,
        forcedFreeTables: forcedFreeTablesRef?.current?.size ?? 0,
        recentlyPaidTables: recentlyPaidTablesRef?.current?.size ?? 0,
      },
    };
  };

  const fetchJsonSnapshot = async (url) => {
    try {
      const response = await fetchWithTimeout(url);
      const status = response.status;
      if (!response.ok) {
        return { ok: false, status };
      }
      const data = await response.json();
      return { ok: true, status, data };
    } catch (error) {
      return { ok: false, error: error?.message || String(error) };
    }
  };

  const collectDebugSnapshot = async () => {
    const timestamp = new Date().toISOString();
    const ui = buildUiDebugSnapshot();
    const [tablesRes, unpaidRes, deliveriesRes] = await Promise.all([
      fetchJsonSnapshot(`${API_BASE}/tables`),
      fetchJsonSnapshot(`${API_BASE}/orders/unpaid`),
      fetchJsonSnapshot(`${API_BASE}/delivery/orders/pending`),
    ]);

    const api = {
      tables: tablesRes.ok
        ? summarizeTablesForLog(tablesRes.data)
        : { error: tablesRes.error || `HTTP ${tablesRes.status}` },
      unpaidOrders: unpaidRes.ok
        ? (unpaidRes.data || []).map((order) => {
            const hasActiveCountField =
              Object.prototype.hasOwnProperty.call(
                order || {},
                "active_items_count",
              ) ||
              Object.prototype.hasOwnProperty.call(
                order || {},
                "activeItemsCount",
              ) ||
              Object.prototype.hasOwnProperty.call(
                order || {},
                "active_items",
              );
            const hasItemsField = Array.isArray(order?.items);
            return {
              id: order?.id ?? order?.order_id ?? null,
              table_id: order?.table_id ?? null,
              status: order?.status ?? null,
              payment_status: order?.payment_status ?? null,
              active_items_count:
                order?.active_items_count ??
                order?.activeItemsCount ??
                order?.active_items ??
                0,
              active_items_count_present: hasActiveCountField,
              items_count: hasItemsField ? order.items.length : null,
            };
          })
        : { error: unpaidRes.error || `HTTP ${unpaidRes.status}` },
      deliveries: deliveriesRes.ok
        ? (deliveriesRes.data || []).map((delivery) => ({
            order_id: delivery?.order_id ?? delivery?.orderId ?? null,
            order_number: delivery?.order_number ?? null,
            table_id: delivery?.table_id ?? null,
            status: delivery?.status ?? null,
            items_count: Array.isArray(delivery?.items)
              ? delivery.items.length
              : 0,
          }))
        : { error: deliveriesRes.error || `HTTP ${deliveriesRes.status}` },
    };

    return { timestamp, ui, api };
  };

  const copyTargetedLogsToClipboard = async () => {
    try {
      const snapshot = await collectDebugSnapshot();
      const snapshotLog = {
        id: Date.now() + Math.random(),
        timestamp: snapshot.timestamp,
        level: "SNAPSHOT",
        message: "Snapshot caisse",
        data: snapshot,
      };
      const logsText = [
        "=== MOO SYNC DEBUG LOGS ===",
        ...[...debugLogs, snapshotLog].map(formatDebugLogEntry),
      ]
        .filter(Boolean)
        .join("\n\n");

      await navigator.clipboard.writeText(logsText);
      addDebugLog("INFO", "Snapshot caisse capturée", snapshot);
      triggerSuccessAnimation("Logs copiés dans le presse-papiers");
    } catch (err) {
      console.error("Erreur copie logs ciblés:", err);
      triggerErrorAnimation("Erreur lors de la copie");
    }
  };

  // (Diagnostic UI retiré)

  // Fonction pour initier l'annulation complète du panier - CORRECTION COMPLÈTE OAA
  const initiateCartCancellation = async () => {
    addDebugLog("INFO", "Début de l'annulation du panier complet");

    // CORRECTION CRITIQUE: Inclure TOUS les articles (panier + existants)
    const allCartItems = [
      ...(currentOrder.items || []),
      ...(existingItemsForPayment || []),
    ];

    // Supprimer les doublons basés sur l'ID de l'article
    const uniqueItems = allCartItems.filter((item, index, array) => {
      const firstIndex = array.findIndex(
        (i) => (i.order_item_id || i.id) === (item.order_item_id || item.id),
      );
      return index === firstIndex;
    });

    if (uniqueItems.length === 0) {
      triggerErrorAnimation("Aucun article dans le panier à annuler !");
      return;
    }

    // CORRECTION CRITIQUE: Vérification en temps réel des statuts depuis la base
    cashDebugLog("�? Vérification des statuts en temps réel...");
    let itemsToCancel = [];

    try {
      const response = await fetch(
        `${API_BASE}/tables/${selectedTable?.id}/orders`,
      );
      if (!response.ok) {
        throw new Error("Impossible de vérifier les statuts des articles");
      }
      const realTimeOrders = await response.json();

      // Extraire les statuts réels de tous les articles NON ANNULÉS
      const realTimeItems = [];
      realTimeOrders.forEach((order) => {
        if (order.status !== "cancelled" && order.payment_status !== "paid") {
          order.items.forEach((item) => {
            // CORRECTION: Exclure strictement les articles annulés
            if (isItemActive(item)) {
              cashDebugLog(
                `✅ Article actif trouvé: ${item.product_name} (ID: ${item.id}, Status: ${item.status})`,
              );
              realTimeItems.push({
                id: item.id,
                product_name: item.product_name,
                status: item.status,
                unit_price: item.unit_price,
                quantity: item.quantity,
                total_price: item.total_price,
                order_id: order.id,
                order_number: order.order_number,
              });
            } else {
              cashDebugLog(
                `�?� Article annulé ignoré: ${item.product_name} (ID: ${item.id}, Status: ${item.status})`,
              );
            }
          });
        }
      });

      if (realTimeItems.length === 0) {
        triggerErrorAnimation(
          isBoutique
            ? "Aucun article actif trouvé."
            : "Aucun article actif trouvé sur cette table !",
        );
        return;
      }

      cashDebugLog(
        `📊 Statuts temps réel: ${realTimeItems.length} articles trouvés`,
      );
      realTimeItems.forEach((item) => {
        cashDebugLog(`  - ${item.product_name}: ${item.status}`);
      });

      // Utiliser les données temps réel au lieu des données locales
      itemsToCancel = realTimeItems;
    } catch (error) {
      console.error("�?� Erreur vérification statuts:", error);
      triggerErrorAnimation(
        "Impossible de vérifier les statuts. Veuillez réessayer.",
      );
      return;
    }

    // NORMALISER LES DONNÉES DES ARTICLES avec les statuts temps réel
    const normalizedItems = itemsToCancel.map((item) => ({
      id: item.id || `temp_${Date.now()}_${Math.random()}`,
      order_item_id: item.id,
      product_name: item.product_name || "Article inconnu",
      name: item.product_name || "Article inconnu",
      price: item.unit_price || 0,
      unit_price: item.unit_price || 0,
      quantity: item.quantity || 1,
      totalPrice: item.total_price || item.unit_price * item.quantity || 0,
      total_price: item.total_price || item.unit_price * item.quantity || 0,
      status: item.status || "pending", // STATUT TEMPS RÉEL
      image_path: null,
      image_url: null,
      destination: "cuisine",
      orderId: item.order_id || null,
      order_id: item.order_id || null,
      order_number: item.order_number || null,
      extras: [],
    }));

    // VÉRIFICATION OAA avec statuts temps réel
    cashDebugLog("�? Analyse OAA avec statuts temps réel:", {
      totalItems: normalizedItems.length,
      statuts: normalizedItems.map((item) => ({
        nom: item.product_name,
        status: item.status,
      })),
    });

    // Vérifier si des articles sont déjà validés en cuisine/bar (OAA requis)
    const validatedItems = normalizedItems.filter((item) =>
      ["preparing", "ready", "served"].includes(item.status),
    );

    const hasValidatedItems = validatedItems.length > 0;

    cashDebugLog("🔒 Analyse OAA pour annulation panier:", {
      totalItems: normalizedItems.length,
      validatedItems: validatedItems.length,
      requiresManagerAuth: hasValidatedItems,
      validatedItemsList: validatedItems.map((item) => ({
        name: item.product_name,
        status: item.status,
      })),
    });

    // Calculer le montant total
    const totalAmount = normalizedItems.reduce(
      (sum, item) => sum + (item.totalPrice || 0),
      0,
    );

    // Créer un objet commande pour l'annulation complète avec TOUS les articles
    const cartCancellationOrder = {
      id: `CART_${Date.now()}`,
      order_number: `PANIER_${selectedTable?.name || selectedTable?.number || "TEMP"}`,
      items: normalizedItems,
      total_amount: totalAmount,
      isCartCancellation: true,
      requiresManagerAuth: hasValidatedItems,
      table_id: selectedTable?.id || null,
      table_name:
        selectedTable?.name || selectedTable?.number || "Table inconnue",
    };

    cashDebugLog("🗑�? Initiation annulation complète du panier OAA:", {
      itemsCount: normalizedItems.length,
      totalAmount: totalAmount,
      hasValidatedItems,
      requiresManagerAuth: hasValidatedItems,
      validatedItemsCount: validatedItems.length,
    });

    setCancellationOrder(cartCancellationOrder);
    setCancellationStep("reason");
    setCancellationReason("");
    setManagerPassword("");
    setShowCancellationModal(true);
  };

  // Fonction de nettoyage automatique du panier
  const cleanCartFromCancelledItems = () => {
    const isDeliveryOrder = selectedTable?.isDelivery;

    if (isDeliveryOrder) {
      if (
        !currentDeliveryOrder.items ||
        currentDeliveryOrder.items.length === 0
      )
        return;

      const activeItems = currentDeliveryOrder.items.filter(isItemActive);

      if (activeItems.length !== currentDeliveryOrder.items.length) {
        cashDebugLog(
          "🧹 AUTO-CLEAN: Suppression articles annulés du panier de livraison",
        );

        const newTotal = activeItems.reduce(
          (sum, item) => sum + (item.totalPrice || item.price * item.quantity),
          0,
        );

        setCurrentDeliveryOrder({
          items: activeItems,
          total: newTotal,
        });

        cashDebugLog(
          `🧹 Panier de livraison nettoyé: ${currentDeliveryOrder.items.length - activeItems.length} articles annulés supprimés`,
        );
      }
    } else {
      if (!currentOrder.items || currentOrder.items.length === 0) return;

      const activeItems = currentOrder.items.filter(isItemActive);

      if (activeItems.length !== currentOrder.items.length) {
        cashDebugLog("🧹 AUTO-CLEAN: Suppression articles annulés du panier");

        const newTotal = activeItems.reduce(
          (sum, item) => sum + (item.totalPrice || item.price * item.quantity),
          0,
        );

        setCurrentOrder({
          items: activeItems,
          total: newTotal,
        });

        cashDebugLog(
          `🧹 Panier nettoyé: ${currentOrder.items.length - activeItems.length} articles annulés supprimés`,
        );
      }
    }
  };

  // useEffect pour nettoyage différé et validation finale
  useEffect(() => {
    const timer = setTimeout(() => {
      let hasChanges = false;

      // Nettoyage différé currentOrder
      if (currentOrder.items && currentOrder.items.length > 0) {
        const activeItems = currentOrder.items.filter(isItemActive);

        if (activeItems.length !== currentOrder.items.length) {
          cashDebugLog(
            "🧹 NETTOYAGE DIFFÉRÉ: Articles annulés détectés dans panier",
          );
          const newTotal = activeItems.reduce(
            (sum, item) =>
              sum + (item.totalPrice || item.price * item.quantity),
            0,
          );

          setCurrentOrder({
            items: activeItems,
            total: newTotal,
          });
          hasChanges = true;
        }
      }

      // Nettoyage différé existingItems
      if (existingItemsForPayment && existingItemsForPayment.length > 0) {
        const activeExisting = existingItemsForPayment.filter(isItemActive);

        if (activeExisting.length !== existingItemsForPayment.length) {
          cashDebugLog(
            "🧹 NETTOYAGE DIFFÉRÉ: Articles annulés détectés dans existingItems",
          );
          setExistingItemsForPayment(activeExisting);
          hasChanges = true;
        }
      }

      if (hasChanges) {
        triggerSuccessAnimation(
          "Interface synchronisée - Articles annulés supprimés",
        );
        setCartRenderKey((prev) => prev + 1);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [currentOrder.items, existingItemsForPayment]);

  // Fonction pour ajouter à une table existante
  const addToExistingTable = () => {
    if (hasExistingOrders && hasNewItems()) {
      setIsAddingToExistingOrder(true);
      // Ne plus désactiver le bouton après premier ajout
    } else if (hasExistingOrders && !hasNewItems()) {
      triggerErrorAnimation(
        selectedTable?.isDelivery
          ? "Aucun nouvel article à ajouter à la livraison !"
          : isBoutique
            ? "Aucun nouvel article à ajouter au panier !"
            : "Aucun nouvel article à ajouter à la table !",
      );
    } else {
      sendToCuisineBar();
    }
  };

  // Fonction pour réinitialiser la modale d'annulation
  const resetCancellationModal = () => {
    cashDebugLog("🔄 Réinitialisation modal d'annulation");
    setShowCancellationModal(false);
    setCancellationOrder(null);
    setCancellationStep("reason");
    setCancellationReason("");
    setManagerPassword("");
  };

  // Fonction pour gérer les étapes d'annulation avec politique intelligente
  const handleCancellationStep = async () => {
    if (cancellationStep === "reason") {
      // Validation de la raison
      if (!cancellationReason.trim() || cancellationReason.length < 10) {
        triggerErrorAnimation(
          "Veuillez saisir une raison détaillée (minimum 10 caractères)",
        );
        return;
      }

      // Vérifier si l'autorisation manager est requise
      if (cancellationOrder?.requiresManagerAuth) {
        cashDebugLog("🔒 Autorisation manager requise pour cette annulation");
        setCancellationStep("auth");
      } else {
        cashDebugLog("✅ Annulation directe autorisée");
        setCancellationStep("confirm");
      }
    } else if (cancellationStep === "auth") {
      // Vérification du code manager via le serveur
      cashDebugLog("�? [DEBUG] Début validation code manager:", {
        step: cancellationStep,
        codeLength: managerPassword.length,
        codeProvided: Boolean(managerPassword),
        apiBase: API_BASE,
      });

      if (!managerPassword.trim()) {
        cashDebugLog("�?� [DEBUG] Code manager vide ou espaces uniquement");
        triggerErrorAnimation("🔒 Veuillez saisir votre code manager");
        return;
      }

      try {
        const requestUrl = `${API_BASE}/validate-manager-code`;
        const requestBody = {
          managerCode: managerPassword,
        };

        cashDebugLog("�? [DEBUG] Envoi requête validation:", {
          url: requestUrl,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: { managerCode: "[REDACTED]" },
        });

        // Utiliser l'endpoint dédié pour valider le code manager
        const response = await fetch(requestUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });

        cashDebugLog("�? [DEBUG] Réponse serveur:", {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          headers: Object.fromEntries(response.headers.entries()),
        });

        if (response.ok) {
          const validationData = await response.json();
          cashDebugLog("✅ [DEBUG] Authentification manager réussie:", {
            manager: validationData.manager,
            fullResponse: validationData,
          });
          triggerSuccessAnimation(
            `✅ Code validé - ${validationData.manager.name}`,
          );
          setCancellationStep("confirm");
        } else {
          const errorData = await response.json();
          console.error("�?� [DEBUG] Code manager refusé:", {
            status: response.status,
            errorData: errorData,
            codeProvided: Boolean(managerPassword),
          });
          triggerErrorAnimation("🔒 Code manager incorrect - Accès refusé");
          setManagerPassword("");
        }
      } catch (error) {
        console.error("�?� [DEBUG] Erreur vérification code manager:", {
          error: error.message,
          stack: error.stack,
          codeProvided: Boolean(managerPassword),
          apiBase: API_BASE,
        });
        triggerErrorAnimation("🔒 Erreur de vérification - Réessayez");
        setManagerPassword("");
      }
    } else if (cancellationStep === "confirm") {
      // Procéder à l'annulation réelle
      await executeCancellation();
    }
  };

  // Nouvelle fonction pour exécuter l'annulation - CORRECTION COMPLÈTE OAA
  const executeCancellation = async () => {
    try {
      cashDebugLog("🔥 [DIGITALIVORE DEBUG] executeCancellation DÉBUT", {
        type: cancellationOrder.isCartCancellation
          ? "PANIER_COMPLET"
          : "ARTICLE_INDIVIDUEL",
        requiresAuth: cancellationOrder.requiresManagerAuth,
        reason: cancellationReason,
        itemsCount: cancellationOrder.items?.length,
        timestamp: new Date().toISOString(),
      });
      cashDebugLog("🚀 Exécution de l'annulation OAA...");
      addDebugLog("INFO", "Début exécution annulation OAA", {
        type: cancellationOrder.isCartCancellation
          ? "PANIER_COMPLET"
          : "ARTICLE_INDIVIDUEL",
        requiresAuth: cancellationOrder.requiresManagerAuth,
        reason: cancellationReason,
      });

      if (cancellationOrder.isCartCancellation) {
        // Annulation complète du panier en cours - CORRECTION COMPLÈTE
        cashDebugLog("🗑�? Annulation complète du panier avec OAA:", {
          itemsCount: cancellationOrder.items.length,
          totalAmount: cancellationOrder.total_amount,
          requiresManagerAuth: cancellationOrder.requiresManagerAuth,
          tableId: selectedTable?.id,
        });

        let successCount = 0;
        let errorCount = 0;
        const cancellationResults = [];

        // ANNULER CHAQUE ARTICLE INDIVIDUELLEMENT AVEC RÈGLES OAA RENFORCÉES
        for (const item of cancellationOrder.items) {
          try {
            const itemName = item.name || item.product_name;
            cashDebugLog(`🔄 Annulation article: ${itemName}`);

            // NOUVELLE VÉRIFICATION: Re-vérifier le statut juste avant l'annulation
            let currentItemStatus = item.status;
            let itemRequiresAuth = ["preparing", "ready", "served"].includes(
              item.status,
            );

            // Vérification en temps réel du statut
            try {
              const statusCheck = await fetch(
                `${API_BASE}/order-items/${item.order_item_id || item.id}/status`,
              );
              if (statusCheck.ok) {
                const statusData = await statusCheck.json();
                currentItemStatus = statusData.status;
                itemRequiresAuth =
                  statusData.requires_manager_auth ||
                  ["preparing", "ready", "served"].includes(statusData.status);

                // Si l'article a été annulé entre-temps
                if (statusData.status === "cancelled") {
                  addDebugLog(
                    "INFO",
                    `Article ${itemName} déjà annulé - Ignorer`,
                  );
                  successCount++; // Compter comme succès
                  cancellationResults.push({
                    item: itemName,
                    status: "already_cancelled",
                    requiresAuth: false,
                  });
                  continue;
                }
              }
            } catch (statusError) {
              addDebugLog(
                "WARNING",
                `Erreur vérification statut final ${itemName}`,
                statusError,
              );
            }

            // Construire la requête d'annulation
            const cancellationData = {
              action: "cancel",
              quantity: Number(item.quantity) || 1,
              reason: cancellationReason,
              oaaType: itemRequiresAuth
                ? "MANAGER_AUTH_REQUIRED"
                : "DIRECT_CANCELLATION",
              cashierId: currentUser?.id,
              cashierName: currentUser?.name,
              isPartOfCartCancellation: true,
              cartCancellationId: cancellationOrder.id,
              finalStatusCheck: currentItemStatus, // Inclure le statut vérifié
            };

            // Ajouter l'authentification manager si requise
            if (itemRequiresAuth && managerPassword) {
              cancellationData.managerCode = managerPassword;
            } else if (itemRequiresAuth && !managerPassword) {
              // Si auth requise mais pas de code, erreur
              throw new Error(
                "Code manager requis pour cet article mais non fourni",
              );
            }

            addDebugLog("INFO", `Annulation article ${itemName}`, {
              itemId: item.order_item_id || item.id,
              originalStatus: item.status,
              currentStatus: currentItemStatus,
              requiresAuth: itemRequiresAuth,
              hasManagerCode: !!managerPassword,
            });

            // APPEL API POUR ANNULER L'ARTICLE RÉEL EN BASE
            const response = await fetch(
              `${API_BASE}/order-items/${item.order_item_id || item.id}/status`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(cancellationData),
              },
            );

            if (response.ok) {
              const responseData = await response.json();
              cashDebugLog(
                `✅ Article ${item.name || item.product_name} annulé avec succès`,
              );
              addDebugLog(
                "SUCCESS",
                `Article annulé: ${item.name || item.product_name}`,
                responseData,
              );

              cancellationResults.push({
                item: item.name || item.product_name,
                status: "success",
                requiresAuth: itemRequiresAuth,
              });
              successCount++;

              // Journalisation individuelle de l'article dans les logs admin
              try {
                await fetch(`${API_BASE}/logs/oaa-cancellation`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    action: "OAA_CART_ITEM_CANCELLATION",
                    item_id: item.order_item_id || item.id,
                    product_name: item.name || item.product_name,
                    order_id: item.orderId,
                    order_number: item.order_number,
                    table_id: selectedTable?.id,
                    table_name: selectedTable?.name,
                    reason: cancellationReason,
                    cashier_id: currentUser?.id,
                    cashier_name: currentUser?.name,
                    manager_auth: itemRequiresAuth,
                    manager_code_used: !!managerPassword,
                    item_status_before: item.status,
                    is_cart_cancellation: true,
                    cart_cancellation_id: cancellationOrder.id,
                    timestamp: new Date().toISOString(),
                  }),
                });
              } catch (logError) {
                console.warn("⚠�? Erreur journalisation article:", logError);
              }
            } else {
              const errorData = await response.json();
              console.error(
                `�?� Erreur annulation ${item.name || item.product_name}:`,
                errorData,
              );
              addDebugLog(
                "ERROR",
                `Erreur annulation ${item.name || item.product_name}`,
                errorData,
              );

              cancellationResults.push({
                item: item.name || item.product_name,
                status: "error",
                error: errorData.error || "Erreur inconnue",
              });
              errorCount++;
            }
          } catch (itemError) {
            console.error(
              `�?� Erreur critique article ${item.name || item.product_name}:`,
              itemError,
            );
            addDebugLog(
              "ERROR",
              `Erreur critique article ${item.name || item.product_name}`,
              {
                error: itemError.message,
                stack: itemError.stack,
              },
            );
            cancellationResults.push({
              item: item.name || item.product_name,
              status: "error",
              error: itemError.message,
            });
            errorCount++;
          }
        }

        // Journalisation globale de l'annulation du panier complet
        try {
          const globalLogData = {
            action: "CART_CANCELLATION_COMPLETE",
            table_id: cancellationOrder.table_id,
            table_name: selectedTable?.name,
            items_count: cancellationOrder.items.length,
            success_count: successCount,
            error_count: errorCount,
            total_amount: cancellationOrder.total_amount,
            reason: cancellationReason,
            cashier_id: currentUser?.id,
            cashier_name: currentUser?.name,
            manager_auth: cancellationOrder.requiresManagerAuth,
            manager_code_used: !!managerPassword,
            cancellation_results: cancellationResults,
            timestamp: new Date().toISOString(),
          };

          const logResponse = await fetch(
            `${API_BASE}/logs/cart-cancellation`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(globalLogData),
            },
          );

          if (logResponse.ok) {
            cashDebugLog("�? Log global d'annulation panier enregistré");
            addDebugLog(
              "SUCCESS",
              "Log global annulation panier enregistré",
              globalLogData,
            );
          } else {
            const logError = await logResponse.json();
            addDebugLog("ERROR", "Erreur enregistrement log global", logError);
          }
        } catch (logError) {
          console.warn("⚠�? Erreur journalisation globale:", logError);
          addDebugLog("ERROR", "Erreur journalisation globale", {
            error: logError.message,
            stack: logError.stack,
          });
        }

        // VIDER LE PANIER ACTUEL ET RAFRAÎCHIR L'AFFICHAGE - CORRECTION CRITIQUE
        cashDebugLog("🧹 Nettoyage panier après annulation...");
        addDebugLog("INFO", "Nettoyage panier après annulation");

        // CORRECTION IMMÉDIATE: Filtrage des articles actifs au lieu de vidage complet
        setCurrentOrder((prevOrder) => {
          const updatedItems = prevOrder.items.filter((item) => {
            const isNotCancelled = item.status !== "cancelled";
            return isNotCancelled;
          });

          const newTotal = updatedItems.reduce(
            (sum, item) =>
              sum + (item.totalPrice || item.price * item.quantity),
            0,
          );

          return { items: updatedItems, total: newTotal };
        });

        // Nettoyer aussi les articles existants
        setExistingItemsForPayment((prevItems) =>
          prevItems.filter(isItemActive),
        );

        // FORCER LE RE-RENDER IMMÉDIAT
        setCartRenderKey((prev) => prev + 1);

        // FORCER LE RAFRAÎCHISSEMENT COMPLET DE LA TABLE
        if (selectedTable) {
          addDebugLog("INFO", "Rechargement données table après annulation");

          // Recharger complètement les données de la table
          await loadTableOrders(selectedTable.id);
          await loadTableItemsForPayment(selectedTable.id, false);
          await cleanupCancelledItems(selectedTable.id);

          // Forcer la mise à jour de l'affichage
          setCartRenderKey((prev) => prev + 1);
        }

        // Émettre événement WebSocket pour synchroniser toutes les interfaces
        if (socket) {
          socket.emit("cart-cancelled", {
            tableId: selectedTable?.id,
            itemsCount: cancellationOrder.items.length,
            successCount,
            errorCount,
            reason: cancellationReason,
            timestamp: new Date().toISOString(),
          });
        }

        // Message de succès détaillé et logs de débogage
        const finalResults = {
          successCount,
          errorCount,
          totalItems: cancellationOrder.items.length,
          cancellationResults,
        };

        if (errorCount === 0) {
          const successMsg = `✅ Panier annulé avec succès (${successCount} articles)`;
          triggerSuccessAnimation(successMsg);
          addDebugLog("SUCCESS", "Annulation panier réussie", finalResults);
        } else if (successCount > 0) {
          const partialMsg = `⚠�? Panier partiellement annulé (${successCount}/${cancellationOrder.items.length} articles)`;
          triggerSuccessAnimation(partialMsg);
          addDebugLog("WARNING", "Annulation panier partielle", finalResults);
        } else {
          const errorMsg = `�?� Échec annulation panier (${errorCount} erreurs)`;
          triggerErrorAnimation(errorMsg);
          addDebugLog("ERROR", "Échec annulation panier", finalResults);
        }
      } else if (cancellationOrder.isPartialCancellation) {
        // Annulation d'un article spécifique avec authentification manager (OAA)
        const itemToCancel = cancellationOrder.items[0];

        cashDebugLog(
          "🔒 OAA Annulation:",
          itemToCancel.product_name,
          "ID:",
          itemToCancel.id,
        );

        cashDebugLog(
          "🔥 [DIGITALIVORE DEBUG] API CALL ANNULATION OAA PRÉPARATION",
          {
            endpoint: `${API_BASE}/order-items/${itemToCancel.id}/status`,
            itemId: itemToCancel.id,
            itemName: itemToCancel.product_name,
            itemStatus: itemToCancel.status,
            reason: cancellationReason,
            managerCodeProvided: !!managerPassword,
            timestamp: new Date().toISOString(),
          },
        );

        const response = await fetch(
          `${API_BASE}/order-items/${itemToCancel.id}/status`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "cancel",
              reason: cancellationReason,
              managerCode: managerPassword,
              oaaType: "MANAGER_AUTH_REQUIRED",
              cashierId: currentUser?.id,
              cashierName: currentUser?.name,
            }),
          },
        );

        cashDebugLog("🔥 [DIGITALIVORE DEBUG] RÉPONSE API ANNULATION OAA", {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          timestamp: new Date().toISOString(),
        });

        if (response.ok) {
          const responseData = await response.json();
          cashDebugLog("✅ Annulation OAA réussie:", responseData);
          cashDebugLog("🔥 [DIGITALIVORE DEBUG] DONNÉES RÉPONSE API", {
            responseData,
            itemId: itemToCancel.id,
            success: true,
            timestamp: new Date().toISOString(),
          });

          // Journaliser l'annulation OAA dans les logs admin
          try {
            await fetch(`${API_BASE}/logs/oaa-cancellation`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                action: "OAA_ITEM_CANCELLATION",
                item_id: itemToCancel.id,
                product_name: itemToCancel.product_name,
                order_id: cancellationOrder.id,
                order_number: cancellationOrder.order_number,
                table_id: selectedTable?.id,
                table_name: selectedTable?.name,
                reason: cancellationReason,
                cashier_id: currentUser?.id,
                cashier_name: currentUser?.name,
                manager_auth: true,
                manager_code_used: !!managerPassword,
                item_status_before: itemToCancel.status,
                timestamp: new Date().toISOString(),
              }),
            });
            cashDebugLog("�? Log OAA enregistré dans l'espace admin");
          } catch (logError) {
            console.warn("⚠�? Erreur journalisation OAA:", logError);
          }

          // 🧠 MÉMORISER L'ANNULATION OAA POUR LE SYSTÈME INTELLIGENT
          memorizeRecentCancellation(itemToCancel.id, selectedTable?.id, {
            productName: itemToCancel.product_name,
            orderNumber: cancellationOrder.order_number,
            annulationType: "oaa_manager",
            reason: cancellationReason,
            managerAuth: true,
          });

          triggerSuccessAnimation(
            `Article "${itemToCancel.product_name}" annulé avec succès (OAA)`,
          );

          // Recharger les articles de la table et forcer la mise à jour visuelle
          if (selectedTable) {
            // Recharger complètement les données après annulation
            await loadTableOrders(selectedTable.id);
            await loadTableItemsForPayment(selectedTable.id, false);
            await cleanupCancelledItems(selectedTable.id);

            // Forcer la mise à jour immédiate sans écraser les items rechargés entre-temps.
            setExistingItemsForPayment((prevItems) =>
              (prevItems || []).filter(
                (existingItem) =>
                  !isSameOrderItemId(
                    existingItem.order_item_id,
                    itemToCancel.id,
                  ) &&
                  existingItem.status !== "cancelled",
              ),
            );

            // CORRECTION CRITIQUE : Mise à jour FORCÉE de tous les états
            if (currentOrder.items && currentOrder.items.length > 0) {
              const avant = currentOrder.items.length;
              const updatedCurrentItems = currentOrder.items.filter(
                (currentItem) => {
                  const isMatchingItem = isSameOrderItemId(
                    getExplicitOrderItemId(currentItem),
                    itemToCancel.id,
                  );
                  return !isMatchingItem;
                },
              );

              cashDebugLog(
                "🔄 Suppression:",
                itemToCancel.product_name,
                avant,
                "→",
                updatedCurrentItems.length,
              );

              const newTotal = updatedCurrentItems.reduce(
                (sum, item) =>
                  sum + (item.totalPrice || item.price * item.quantity),
                0,
              );

              setCurrentOrder({
                items: updatedCurrentItems,
                total: newTotal,
              });
            }

            setCartRenderKey((prev) => prev + 1);

          }
        } else {
          const errorData = await response.json();
          cashDebugLog("🔥 [DIGITALIVORE DEBUG] ERREUR API ANNULATION OAA", {
            status: response.status,
            statusText: response.statusText,
            errorData,
            itemId: itemToCancel.id,
            itemName: itemToCancel.product_name,
            timestamp: new Date().toISOString(),
          });
          addDebugLog(
            "ERROR",
            "Erreur annulation OAA article individuel",
            errorData,
          );
          console.error("�?� Erreur annulation OAA:", errorData);

          // Gestion d'erreur améliorée pour l'annulation individuelle
          let errorMessage =
            errorData.error || "Erreur lors de l'annulation OAA";

          if (
            errorData.alreadyCancelled ||
            errorData.itemStatus === "cancelled" ||
            errorData.error === "Article déjà annulé"
          ) {
            // Article déjà annulé, traiter comme succès et nettoyer l'interface
            addDebugLog(
              "INFO",
              `Article ${itemToCancel.product_name} déjà annulé - VIDAGE FORCÉ IMMÉDIAT`,
            );

            // CORRECTION CRITIQUE: VIDAGE COMPLET ET FORCÉ
            cashDebugLog(
              "🗑�? VIDAGE FORCÉ - Article déjà annulé:",
              itemToCancel.product_name,
            );

            // FORCER LA SUPPRESSION PAR ID EXACT DE L'ARTICLE DÉJÀ ANNULÉ
            setCurrentOrder((prevOrder) => {
              const activeItems = prevOrder.items.filter((item) => {
                const isMatchingItem = isSameOrderItemId(
                  getExplicitOrderItemId(item),
                  itemToCancel.id,
                );
                return !isMatchingItem;
              });
              const newTotal = activeItems.reduce(
                (sum, item) =>
                  sum + (item.totalPrice || item.price * item.quantity),
                0,
              );
              cashDebugLog(
                "🗑�? SUPPRESSION FORCÉE PAR ID:",
                itemToCancel.product_name,
                prevOrder.items.length,
                "→",
                activeItems.length,
              );
              return { items: activeItems, total: newTotal };
            });
            setExistingItemsForPayment((prevItems) =>
              prevItems.filter(
                (item) =>
                  !isSameOrderItemId(
                    getExplicitOrderItemId(item),
                    itemToCancel.id,
                  ),
              ),
            );

            // FORCER LE RE-RENDER COMPLET
            setCartRenderKey((prev) => prev + 1);

            addDebugLog(
              "SUCCESS",
              "VIDAGE FORCÉ - Panier et articles existants vidés immédiatement",
            );
            cashDebugLog("🧹 VIDAGE FORCÉ TERMINÉ - Interface nettoyée");

            // FERMER LE MODAL IMMÉDIATEMENT
            setShowCancellationModal(false);
            setCancellationOrder(null);
            setCancellationReason("");
            setManagerPassword("");

            triggerSuccessAnimation(
              `Article annulé supprimé - Interface nettoyée`,
            );

            // Ne pas lancer d'erreur, traiter comme succès
            return;
          } else if (errorData.code === "INVALID_MANAGER_CODE") {
            errorMessage =
              "Code manager invalide. Vérifiez votre authentification.";
            triggerErrorAnimation(errorMessage);
          } else if (errorData.code === "STATUS_CHANGED") {
            errorMessage =
              "Le statut de l'article a changé. Veuillez recharger et réessayer.";
            triggerErrorAnimation(errorMessage);
          } else {
            triggerErrorAnimation(errorMessage);
          }

          throw new Error(errorMessage);
        }
      } else {
        // Annulation de la commande complète
        const response = await fetch(
          `${API_BASE}/orders/${cancellationOrder.id}`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              reason: cancellationReason,
            }),
          },
        );

        if (response.ok) {
          triggerSuccessAnimation(`Commande annulée avec succès`);

          // Recharger les données pour affichage seulement après annulation
          if (selectedTable) {
            const reloadedItems = await loadTableItemsForPayment(selectedTable.id, false);
            // Ne pas vider aveuglément le panier: d'autres commandes actives peuvent exister
            // sur la même table après l'annulation de cette commande.
            if (!Array.isArray(reloadedItems) || reloadedItems.length === 0) {
              setExistingItemsForPayment((prevItems) =>
                (prevItems || []).filter(
                  (item) =>
                    item.orderId !== cancellationOrder.id &&
                    item.order_id !== cancellationOrder.id,
                ),
              );
            }
          }
          scheduleLoadTables("cart-cancellation-full-order", {
            forceUnpaidRefresh: true,
          });
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || "Erreur lors de l'annulation");
        }
      }

      resetCancellationModal();
    } catch (error) {
      console.error("�?� Erreur annulation OAA:", error);
      triggerErrorAnimation(`Erreur: ${error.message}`);
    }
  };

  // Fonction de nettoyage automatique des articles annulés - AMÉLIORÉE
  const cleanupCancelledItems = async (tableId = null) => {
    try {
      const targetTableId = tableId || selectedTable?.id;
      if (!targetTableId) return;

      cashDebugLog(
        `🧹 Nettoyage articles annulés pour table ${targetTableId}...`,
      );
      addDebugLog(
        "INFO",
        `Début nettoyage articles annulés table ${targetTableId}`,
      );

      // Recharger les données en temps réel
      const response = await fetch(
        `${API_BASE}/tables/${targetTableId}/orders`,
      );
      if (!response.ok) {
        addDebugLog(
          "WARNING",
          "Impossible de charger les commandes pour nettoyage",
        );
        return;
      }

      const orders = await response.json();
      let cleanedCount = 0;

      // Identifier les articles annulés qui sont encore affichés
      const cancelledItems = [];
      orders.forEach((order) => {
        order.items.forEach((item) => {
          // Détecter tous les types d'annulation possibles
          if (
            item.status === "cancelled" ||
            item.cancelled_at !== null ||
            item.cancellation_reason
          ) {
            cancelledItems.push(item);
            cleanedCount++;
          }
        });
      });

      if (cleanedCount > 0) {
        cashDebugLog(
          `🧹 ${cleanedCount} articles annulés détectés - Nettoyage...`,
        );
        addDebugLog(
          "INFO",
          `${cleanedCount} articles annulés détectés - Nettoyage en cours`,
        );

        // CORRECTION CRITIQUE: VIDAGE COMPLET ET FORCÉ
        if (cleanedCount > 0) {
          addDebugLog(
            "WARNING",
            `${cleanedCount} articles annulés détectés - VIDAGE FORCÉ IMMÉDIAT`,
          );

          // FILTRER LES ARTICLES ANNULÉS IMMÉDIATEMENT
          setCurrentOrder((prevOrder) => {
            const activeItems = prevOrder.items.filter(isItemActive);
            const newTotal = activeItems.reduce(
              (sum, item) =>
                sum + (item.totalPrice || item.price * item.quantity),
              0,
            );
            return { items: activeItems, total: newTotal };
          });
          setExistingItemsForPayment((prevItems) =>
            prevItems.filter(isItemActive),
          );

          // FORCER LE RE-RENDER COMPLET
          setCartRenderKey((prev) => prev + 1);

          cashDebugLog("🧹 VIDAGE FORCÉ APPLIQUÉ - Tous les articles supprimés");
          addDebugLog(
            "SUCCESS",
            "VIDAGE FORCÉ - Panier et existants vidés complètement",
          );
        }

        cashDebugLog(
          `✅ Nettoyage terminé: ${cleanedCount} articles annulés supprimés de l'affichage`,
        );
        addDebugLog(
          "SUCCESS",
          `Nettoyage terminé: ${cleanedCount} articles annulés supprimés`,
        );
      } else {
        addDebugLog("INFO", "Aucun article annulé à nettoyer");
      }
    } catch (error) {
      console.error("�?� Erreur nettoyage articles annulés:", error);
      addDebugLog("ERROR", "Erreur nettoyage articles annulés", {
        error: error.message,
        stack: error.stack,
      });
    }
  };

  // Fonction pour gérer les tentatives d'annulation d'articles déjà annulés
  const handleAlreadyCancelledItem = (itemName) => {
    const message = `L'article "${itemName}" a déjà été annulé et a été retiré automatiquement de l'affichage.`;
    triggerErrorAnimation(message);

    // Forcer un rechargement des données pour synchroniser l'affichage
    if (selectedTable) {
      setTimeout(() => {
        loadTableItemsForPayment(selectedTable.id, false);
        cleanupCancelledItems(selectedTable.id);
      }, 1000);
    }
  };

  // Fonction de synchronisation forcée pour corriger les incohérences
  const forceSyncInterface = async () => {
    if (!selectedTable) {
      addDebugLog(
        "WARNING",
        isBoutique
          ? "Aucun panier actif pour synchronisation"
          : "Aucune table sélectionnée pour synchronisation",
      );
      return;
    }

    addDebugLog("INFO", "Début synchronisation forcée interface");

    try {
      // 1. Sauvegarder l'état actuel pour debug
      const currentState = {
        currentOrderItems: currentOrder.items?.length || 0,
        existingItems: existingItemsForPayment?.length || 0,
        tableId: selectedTable.id,
      };
      addDebugLog("INFO", "État avant synchronisation", currentState);

      // 2. Recharger complètement les données de la table
      await loadTableOrders(selectedTable.id);
      await loadTableItemsForPayment(selectedTable.id, false);

      // 3. Nettoyer les articles annulés
      await cleanupCancelledItems(selectedTable.id);

      // 4. Vérifier l'état après synchronisation
      const newState = {
        currentOrderItems: currentOrder.items?.length || 0,
        existingItems: existingItemsForPayment?.length || 0,
        tableId: selectedTable.id,
      };
      addDebugLog("SUCCESS", "Synchronisation terminée", {
        avant: currentState,
        apres: newState,
      });

      triggerSuccessAnimation("Interface synchronisée");
    } catch (error) {
      addDebugLog("ERROR", "Erreur synchronisation forcée", {
        error: error.message,
        stack: error.stack,
      });
      triggerErrorAnimation("Erreur lors de la synchronisation");
    }
  };

  // Fonction d'urgence pour vider immédiatement le panier des articles annulés
  const forceCleanCancelledItems = () => {
    addDebugLog(
      "INFO",
      "🚨 NETTOYAGE D'URGENCE - Vidage immédiat de tous les articles",
    );

    const isDeliveryOrder = selectedTable?.isDelivery;
    let currentItems, existingItems, totalCount;

    if (isDeliveryOrder) {
      // Nettoyage pour les commandes de livraison
      currentItems = currentDeliveryOrder.items?.length || 0;
      existingItems = existingDeliveryItems?.length || 0;
      totalCount = currentItems + existingItems;

      // FILTRAGE INTELLIGENT DES ARTICLES ANNULÉS POUR LIVRAISON
      setCurrentDeliveryOrder((prevOrder) => {
        const activeItems = prevOrder.items.filter(isItemActive);
        const newTotal = activeItems.reduce(
          (sum, item) => sum + (item.totalPrice || item.price * item.quantity),
          0,
        );
        return { items: activeItems, total: newTotal };
      });

      setExistingDeliveryItems((prevItems) => prevItems.filter(isItemActive));
    } else {
      // Nettoyage pour les commandes de tables (logique existante)
      currentItems = currentOrder.items?.length || 0;
      existingItems = existingItemsForPayment?.length || 0;
      totalCount = currentItems + existingItems;

      // FILTRAGE INTELLIGENT DES ARTICLES ANNULÉS
      setCurrentOrder((prevOrder) => {
        const activeItems = prevOrder.items.filter(isItemActive);
        const newTotal = activeItems.reduce(
          (sum, item) => sum + (item.totalPrice || item.price * item.quantity),
          0,
        );
        return { items: activeItems, total: newTotal };
      });

      setExistingItemsForPayment((prevItems) => prevItems.filter(isItemActive));
    }

    // FORCER LE RE-RENDER COMPLET
    setCartRenderKey((prev) => prev + 1);

    // VALIDATION SUPPLÉMENTAIRE APRÈS UN DÉLAI
    setTimeout(() => {
      if (isDeliveryOrder) {
        setCurrentDeliveryOrder((prevOrder) => {
          const activeItems = prevOrder.items.filter(isItemActive);
          if (activeItems.length !== prevOrder.items.length) {
            const newTotal = activeItems.reduce(
              (sum, item) =>
                sum + (item.totalPrice || item.price * item.quantity),
              0,
            );
            return { items: activeItems, total: newTotal };
          }
          return prevOrder;
        });
      } else {
        setCurrentOrder((prevOrder) => {
          const activeItems = prevOrder.items.filter(isItemActive);
          if (activeItems.length !== prevOrder.items.length) {
            const newTotal = activeItems.reduce(
              (sum, item) =>
                sum + (item.totalPrice || item.price * item.quantity),
              0,
            );
            return { items: activeItems, total: newTotal };
          }
          return prevOrder;
        });
      }
    }, 50);

    // FERMER TOUS LES MODALS
    setShowCancellationModal(false);
    setCancellationOrder(null);
    setCancellationReason("");
    setManagerPassword("");

    cashDebugLog("🗑�? VIDAGE D'URGENCE APPLIQUÉ:", {
      panierVide: currentItems,
      existantsVides: existingItems,
      totalSupprime: totalCount,
    });

    addDebugLog(
      "SUCCESS",
      `🚨 VIDAGE D'URGENCE: ${totalCount} articles supprimés immédiatement`,
    );
    triggerSuccessAnimation(
      `🚨 Nettoyage d'urgence: ${totalCount} articles supprimés`,
    );

    // Rechargement propre après vidage
    if (selectedTable) {
      setTimeout(async () => {
        try {
          await loadTableItemsForPayment(selectedTable.id, false);
          addDebugLog("SUCCESS", "Interface rechargée après vidage d'urgence");
        } catch (error) {
          addDebugLog(
            "ERROR",
            "Erreur rechargement après vidage d'urgence",
            error,
          );
        }
      }, 300);
    }
  };

  // Fonction utilitaire pour l'affichage des statuts
  const getStatusInfo = (status) => {
    const statusMap = {
      pending: {
        text: "En attente",
        color: "bg-yellow-500",
        textColor: "text-yellow-800",
        bgColor: "bg-yellow-50",
        borderColor: "border-yellow-200",
      },
      preparing: {
        text: "En préparation",
        color: "bg-blue-500",
        textColor: "text-blue-800",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200",
      },
      ready: {
        text: "Prêt",
        color: "bg-green-500",
        textColor: "text-green-800",
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
      },
      served: {
        text: "Servi",
        color: "bg-purple-500",
        textColor: "text-purple-800",
        bgColor: "bg-purple-50",
        borderColor: "border-purple-200",
      },
      cancelled: {
        text: "Annulé",
        color: "bg-red-500",
        textColor: "text-red-800",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
      },
    };
    return (
      statusMap[status] || {
        text: "Inconnu",
        color: "bg-gray-500",
        textColor: "text-gray-800",
        bgColor: "bg-gray-50",
        borderColor: "border-gray-200",
      }
    );
  };

  // Fonctions pour les animations spéciales
  const dismissTransientNotification = () => {
    if (successAnimationTimerRef.current) {
      clearTimeout(successAnimationTimerRef.current);
      successAnimationTimerRef.current = null;
    }
    if (errorAnimationTimerRef.current) {
      clearTimeout(errorAnimationTimerRef.current);
      errorAnimationTimerRef.current = null;
    }
    notificationSwipeRef.current = null;
    setNotificationSwipeDragging(false);
    setNotificationSwipeOffset(0);
    setShowSuccessAnimation(false);
    setShowErrorAnimation(false);
    setSuccessAnimationMessage("");
    setErrorAnimationMessage("");
  };

  const beginNotificationSwipe = (event) => {
    notificationSwipeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
    };
    setNotificationSwipeOffset(0);
    setNotificationSwipeDragging(true);
    event.currentTarget?.setPointerCapture?.(event.pointerId);
  };

  const moveNotificationSwipe = (event) => {
    const swipe = notificationSwipeRef.current;
    if (!swipe || swipe.pointerId !== event.pointerId) return;
    swipe.lastX = event.clientX;
    swipe.lastY = event.clientY;
    const deltaX = swipe.lastX - swipe.startX;
    const deltaY = swipe.lastY - swipe.startY;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      setNotificationSwipeOffset(deltaX);
    }
  };

  const finishNotificationSwipe = (event, cancelled = false) => {
    const swipe = notificationSwipeRef.current;
    if (!swipe || swipe.pointerId !== event.pointerId) return;
    const deltaX = swipe.lastX - swipe.startX;
    const deltaY = swipe.lastY - swipe.startY;
    notificationSwipeRef.current = null;
    setNotificationSwipeDragging(false);
    event.currentTarget?.releasePointerCapture?.(event.pointerId);
    if (
      !cancelled &&
      Math.abs(deltaX) >= 64 &&
      Math.abs(deltaX) > Math.abs(deltaY)
    ) {
      dismissTransientNotification();
      return;
    }
    setNotificationSwipeOffset(0);
  };

  const triggerSuccessAnimation = (message) => {
    if (errorAnimationTimerRef.current) {
      clearTimeout(errorAnimationTimerRef.current);
      errorAnimationTimerRef.current = null;
    }
    if (successAnimationTimerRef.current) {
      clearTimeout(successAnimationTimerRef.current);
      successAnimationTimerRef.current = null;
    }
    setShowErrorAnimation(false);
    setErrorAnimationMessage("");
    setNotificationSwipeOffset(0);
    setSuccessAnimationMessage(message);
    setShowSuccessAnimation(true);
    successAnimationTimerRef.current = setTimeout(() => {
      setShowSuccessAnimation(false);
      setSuccessAnimationMessage("");
      successAnimationTimerRef.current = null;
    }, 3000);
  };

  const triggerErrorAnimation = (message) => {
    if (successAnimationTimerRef.current) {
      clearTimeout(successAnimationTimerRef.current);
      successAnimationTimerRef.current = null;
    }
    if (errorAnimationTimerRef.current) {
      clearTimeout(errorAnimationTimerRef.current);
      errorAnimationTimerRef.current = null;
    }
    setShowSuccessAnimation(false);
    setSuccessAnimationMessage("");
    setNotificationSwipeOffset(0);
    setErrorAnimationMessage(getSafeNetworkErrorMessage(message));
    setShowErrorAnimation(true);
    errorAnimationTimerRef.current = setTimeout(() => {
      setShowErrorAnimation(false);
      setErrorAnimationMessage("");
      errorAnimationTimerRef.current = null;
    }, 3000);
  };

  const toggleAggregatorOrder = (code) => {
    const normalizedCode = normalizeAggregatorCode(code);
    if (!normalizedCode) return;
    if (!aggregatorSettings.aggregators || !aggregatorSettings[normalizedCode]) {
      triggerErrorAnimation("Ce service agrégateur n'est pas activé.");
      return;
    }
    const hasCartContext = cartHasAnyItems || hasExistingOrders || Boolean(selectedTable);
    if (hasCartContext) {
      triggerErrorAnimation(
        "Terminez ou videz la commande en cours avant de changer de mode.",
      );
      return;
    }
    setActiveAggregator((current) =>
      current === normalizedCode ? null : normalizedCode,
    );
    setSelectedTable(null);
    setHasExistingOrders(false);
    setExistingItemsForPayment([]);
    setExistingDeliveryItems([]);
    setCurrentDeliveryOrder({ items: [], total: 0 });
  };

  const getCurrentRole = () => {
    const raw =
      currentUser?.role ||
      currentUser?.user?.role ||
      currentUser?.user?.role_name ||
      "";
    const lower = normalizeRoleKey(raw);
    const roleMap = {
      cashier: "caissier",
      caissier: "caissier",
      caissiere: "caissier",
      server: "serveur",
      serveur: "serveur",
      manager: "manager",
      admin: "admin",
      superadmin: "admin",
    };
    return roleMap[lower] || lower;
  };

  const canCancelItem = (status = "pending") => {
    const role = getCurrentRole();
    const normalizedStatus = status ? status.toString().toLowerCase() : "pending";
    // Si des politiques sont stockées, on les utilise en priorité
    const policyRole = permissionPolicies?.find(
      (r) => r.name?.toLowerCase() === role,
    );
    if (policyRole?.cancelStatuses) {
      return policyRole.cancelStatuses.includes(normalizedStatus);
    }

    // Fallback si aucune politique stockée
    const rules = {
      caissier: ["pending"],
      cashier: ["pending"],
      serveur: ["pending"],
      server: ["pending"],
      manager: ["pending", "preparing", "ready", "served"],
      admin: ["pending", "preparing", "ready", "served", "done"],
      superadmin: ["pending", "preparing", "ready", "served", "done"],
    };
    const allowed = rules[role];
    if (!allowed) return true;
    return allowed.includes(normalizedStatus);
  };

  const getCartItemsForCancellation = () => {
    if (selectedTable?.isDelivery) {
      return [
        ...(currentDeliveryOrder?.items || []),
        ...(existingDeliveryItems || []),
      ];
    }
    return [
      ...(currentOrder?.items || []),
      ...(existingItemsForPayment || []),
    ];
  };

  const getCartItemsForCancellationCount = () => {
    if (selectedTable?.isDelivery) {
      return (
        (currentDeliveryOrder?.items?.length || 0) +
        (existingDeliveryItems?.length || 0)
      );
    }
    return (
      (currentOrder?.items?.length || 0) +
      (existingItemsForPayment?.length || 0)
    );
  };

  const canCancelCart = () => {
    const allItems = getCartItemsForCancellation();
    if (allItems.length === 0) return false;
    return allItems.every((item) => canCancelItem(item.status || "pending"));
  };

  const openManagerApproval = (
    mode,
    item = null,
    { requiresManagerAuth = true } = {},
  ) => {
    if (isSelectedTableReconciliationInFlight()) {
      triggerErrorAnimation("Resynchronisation de la table en cours…");
      return;
    }
    const maxQty = item?.quantity || 1;
    setManagerApproval({
      open: true,
      mode,
      item,
      maxQty,
      qty: Math.min(1, maxQty) || 1,
      reason: "",
      pin: "",
      requiresManagerAuth,
    });
  };

  const openItemCancellation = async (item) => {
    const itemId = item?.order_item_id || item?.id;
    if (!itemId) {
      removeFromOrder(item);
      return;
    }
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/order-items/${itemId}/status`,
        {},
        5000,
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Vérification impossible.");
      }
      openManagerApproval("item", item, {
        requiresManagerAuth: payload?.requires_manager_auth === true,
      });
    } catch (error) {
      triggerErrorAnimation(
        error?.message || "Impossible de vérifier l’autorisation.",
      );
    }
  };

  const handlePermissionDenied = (message, context) => {
    setPermissionMessage(
      message ||
        "Vous ne pouvez pas procéder à l'annulation, faites appel au manager.",
    );
    setPendingApprovalContext(context);
    setShowPermissionModal(true);
  };

  const [isManagerValidating, setIsManagerValidating] = useState(false);

  const cancelOrderItemInBackend = async (
    orderItemId,
    reason,
    managerCode,
    options = {},
    meta = {},
  ) => {
    const res = await fetch(`${API_BASE}/order-items/${orderItemId}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "cancel",
        reason: reason || "Annulé",
        managerCode: managerCode || null,
        quantity: options.quantity,
        cashierId: meta.cashierId,
        cashierName: meta.cashierName,
        cashierRole: meta.cashierRole,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.error) {
      throw new Error(
        data?.error ||
          data?.details ||
          "Impossible d'annuler l'article côté serveur",
      );
    }
    return data;
  };

  const handleManagerApprove = async () => {
    if (selectedTableAccessConflict) {
      triggerErrorAnimation(
        formatTableAccessConflictMessage(selectedTableAccessConflict),
      );
      return;
    }
    const { mode, item, qty, pin, reason, requiresManagerAuth } =
      managerApproval;

    if (!reason || reason.trim().length === 0) {
      triggerErrorAnimation("Le motif d'annulation est obligatoire");
      return;
    }

    const cleanedPin = requiresManagerAuth ? (pin || "").trim() : "";
    if (requiresManagerAuth && !/^[0-9]{6}$/.test(cleanedPin)) {
      triggerErrorAnimation("Code manager invalide (6 chiffres requis)");
      return;
    }

    setIsManagerValidating(true);
    let managerInfo = null;
    if (requiresManagerAuth) {
      try {
        const res = await fetch(`${API_BASE}/validate-manager-code`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ managerCode: cleanedPin }),
        });

        const payload = await res.json().catch(() => null);
        if (!res.ok || !payload?.valid) {
          const message =
            payload?.error ||
            "Code manager invalide ou utilisateur non autorisé";
          throw new Error(message);
        }
        managerInfo = payload.manager || null;
      } catch (err) {
        console.error("�?� Validation manager échouée:", err);
        triggerErrorAnimation(
          err?.message ||
            "Code manager invalide ou utilisateur non autorisé",
        );
        setIsManagerValidating(false);
        return;
      }
    }

    const isDeliveryOrder = selectedTable?.isDelivery;
    const confirmedDestructiveEvidence = [];

    if (mode === "cart") {
      // Annuler côté serveur les articles déjà envoyés
      const existingItems = isDeliveryOrder
        ? existingDeliveryItems
        : existingItemsForPayment;

      const cancellableExisting = existingItems.filter(
        (it) => it.status !== "cancelled",
      );
      let failures = [];
      for (const it of cancellableExisting) {
        try {
          await cancelOrderItemInBackend(it.order_item_id, reason, cleanedPin, {
            quantity: Number(it.quantity) || 1,
          }, {
            cashierId: currentUser?.id,
            cashierName: currentUser?.name || currentUser?.first_name || currentUser?.username,
            cashierRole: currentUser?.role,
          });
          confirmedDestructiveEvidence.push({
            orderId: it.orderId ?? it.order_id ?? null,
            orderItemId: it.order_item_id,
          });
        } catch (err) {
          console.error("�?� Annulation backend échouée pour", it, err);
          failures.push(it.name || `Article ${it.order_item_id}`);
        }
      }
      if (failures.length) {
        triggerErrorAnimation(
          `Impossible d'annuler: ${failures.join(", ")}`,
        );
        setIsManagerValidating(false);
        return;
      }

      if (isDeliveryOrder) {
        setCurrentDeliveryOrder({ items: [], total: 0 });
        setExistingDeliveryItems([]);
        currentDeliveryOrderRef.current = { items: [], total: 0 };
        existingDeliveryItemsRef.current = [];
      } else {
        if (isFastFoodMode) {
          clearFastFoodCartSnapshot();
        }
        setCurrentOrder({ items: [], total: 0 });
        setExistingItemsForPayment([]);
        currentOrderRef.current = { items: [], total: 0 };
        existingItemsForPaymentRef.current = [];
      }
      const selectedNow = selectedTableRef.current;
      if (selectedNow) {
        persistCartStateForTable(selectedNow, { items: [], total: 0 }, []);
        if (!selectedNow.isDelivery && confirmedDestructiveEvidence.length > 0) {
          scheduleTableCartRefresh({
            tableId: selectedNow.id,
            reason: "item-cancelled",
            orderId: confirmedDestructiveEvidence[0]?.orderId ?? null,
            orderItemId: confirmedDestructiveEvidence[0]?.orderItemId ?? null,
            confirmedEvidence: confirmedDestructiveEvidence,
          });
        }
      }
      triggerSuccessAnimation(
        `Panier annulé${managerInfo ? ` par ${managerInfo.name}` : ""}`,
      );
    } else if (mode === "item" && item) {
      const targetQty = Math.max(1, Math.min(qty || 1, item.quantity || 1));
      if (item.order_item_id) {
        const isPartial = targetQty < (item.quantity || 1);
        try {
          await cancelOrderItemInBackend(
            item.order_item_id,
            reason,
            cleanedPin,
            {
              partial: isPartial,
              quantity: targetQty,
            },
            {
              cashierId: currentUser?.id,
              cashierName:
                currentUser?.name ||
                currentUser?.first_name ||
                currentUser?.username,
              cashierRole: currentUser?.role,
            },
          );
        } catch (err) {
          console.error("�?� Annulation backend échouée:", err);
          triggerErrorAnimation(
            err?.message ||
              "Impossible d'annuler cet article côté serveur",
          );
          setIsManagerValidating(false);
          return;
        }

        // article déjà envoyé
        const updateExistingSent = (list) =>
          list
            .map((it) => {
              if (!isSameOrderItemId(it.order_item_id, item.order_item_id)) return it;
              const unitPrice =
                (it.total_price || it.price * it.quantity) / it.quantity || 0;
              const newQty = Math.max(0, it.quantity - targetQty);
              if (newQty === 0) return null;
              return {
                ...it,
                quantity: newQty,
                total_price: unitPrice * newQty,
              };
            })
            .filter(Boolean);

        if (isDeliveryOrder) {
          setExistingDeliveryItems((prev) => {
            const next = updateExistingSent(prev || []);
            existingDeliveryItemsRef.current = next;
            const selectedNow = selectedTableRef.current;
            if (selectedNow) {
              persistCartStateForTable(
                selectedNow,
                currentDeliveryOrderRef.current,
                next,
              );
            }
            return next;
          });
        } else {
          setExistingItemsForPayment((prev) => {
            const next = updateExistingSent(prev || []);
            existingItemsForPaymentRef.current = next;
            const selectedNow = selectedTableRef.current;
            if (selectedNow) {
              persistCartStateForTable(
                selectedNow,
                currentOrderRef.current,
                next,
              );
            }
            return next;
          });
        }
        const selectedNow = selectedTableRef.current;
        if (selectedNow && !selectedNow.isDelivery) {
          scheduleTableCartRefresh({
            tableId: selectedNow.id,
            reason: isPartial
              ? "item-quantity-updated"
              : "item-cancelled",
            orderId: item.orderId ?? item.order_id ?? null,
            orderItemId: item.order_item_id,
            confirmedEvidence: {
              orderId: item.orderId ?? item.order_id ?? null,
              orderItemId: item.order_item_id,
            },
          });
        }
      } else {
        // article nouveau/pas encore envoyé
        const sourceOrder = isDeliveryOrder
          ? currentDeliveryOrderRef.current
          : currentOrderRef.current;
        const identifiedItems = ensureDraftLineIds(sourceOrder?.items || []);
        const targetIndex = findDraftLineIndex(identifiedItems, item);
        if (targetIndex !== -1) {
          const updatedItems = [...identifiedItems];
          const target = updatedItems[targetIndex];
          const unitPrice =
            (target.totalPrice || target.price * target.quantity) /
              target.quantity || 0;
          const newQty = Math.max(0, target.quantity - targetQty);
          if (newQty > 0) {
            updatedItems[targetIndex] = {
              ...target,
              quantity: newQty,
              totalPrice: unitPrice * newQty,
            };
          } else {
            updatedItems.splice(targetIndex, 1);
          }
          const nextOrder = {
            items: updatedItems,
            total: computeItemsTotal(updatedItems),
          };
          const selectedNow = selectedTableRef.current;
          if (isDeliveryOrder) {
            currentDeliveryOrderRef.current = nextOrder;
            setCurrentDeliveryOrder(nextOrder);
            if (selectedNow) {
              persistCartStateForTable(
                selectedNow,
                nextOrder,
                existingDeliveryItemsRef.current,
              );
            }
          } else {
            currentOrderRef.current = nextOrder;
            setCurrentOrder(nextOrder);
            if (selectedNow) {
              persistCartStateForTable(
                selectedNow,
                nextOrder,
                existingItemsForPaymentRef.current,
              );
            }
          }
        }
      }
      triggerSuccessAnimation(
        `Article annulé${managerInfo ? ` par ${managerInfo.name}` : ""}`,
      );
    }
    setManagerApproval((prev) => ({ ...prev, open: false }));
    setPendingApprovalContext(null);
    setIsManagerValidating(false);
  };

  const [pendingApprovalContext, setPendingApprovalContext] = useState(null);
  const [managerApproval, setManagerApproval] = useState({
    open: false,
    mode: null, // 'cart' | 'item'
    item: null,
    maxQty: 1,
    qty: 1,
    reason: "",
    pin: "",
    requiresManagerAuth: true,
  });

  useEffect(() => {
    let active = true;
    const loadFromStorage = () => {
      try {
        const stored = localStorage.getItem("moo_permissions_policy");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.roles) {
            setPermissionPolicies(parsed.roles);
          }
        }
      } catch (err) {
        console.warn("Impossible de charger les permissions stockées:", err);
      }
    };

    const loadPolicy = async () => {
      try {
        const response = await fetch(`${API_BASE}/permissions/policy`);
        if (response.ok) {
          const data = await response.json();
          if (active && data?.policy?.roles) {
            setPermissionPolicies(data.policy.roles);
            try {
              localStorage.setItem(
                "moo_permissions_policy",
                JSON.stringify(data.policy),
              );
            } catch (_e) {}
            return;
          }
        }
      } catch (_err) {
        // ignore
      }
      if (active) loadFromStorage();
    };

    loadPolicy();
    return () => {
      active = false;
    };
  }, [API_BASE]);

  const buildInvoiceAdjustmentsStorageKey = (table) => {
    if (!table) return null;
    const isDelivery = !!table.isDelivery;
    if (isDelivery) {
      const ref =
        table.deliveryRef ||
        table.orderNumber ||
        table.order_number ||
        table.name ||
        table.id;
      return ref ? `moo_invoice_adjustments:delivery:${ref}` : null;
    }
    const ref = table.id ?? table.number ?? table.name;
    return ref != null ? `moo_invoice_adjustments:table:${ref}` : null;
  };

  const invoiceAdjustmentsStorageKey = useMemo(
    () => buildInvoiceAdjustmentsStorageKey(selectedTable),
    [
      selectedTable?.id,
      selectedTable?.number,
      selectedTable?.name,
      selectedTable?.isDelivery,
      selectedTable?.deliveryRef,
      selectedTable?.orderNumber,
      selectedTable?.order_number,
    ],
  );

  const buildInvoiceItemKey = (item = {}, index = 0) =>
    item.__invoiceKey ||
    item.order_item_id ||
    item.orderItemId ||
    item.id ||
    item.product_id ||
    item._pendingIndex ||
    (item.name ? `name-${item.name}-${index}` : `item-${index}`);

  const invoiceItems = useMemo(() => {
    const isDeliveryOrder = !!selectedTable?.isDelivery;
    const pendingItems = isDeliveryOrder
      ? currentDeliveryOrder.items || []
      : currentOrder.items || [];
    const existingPool = isDeliveryOrder
      ? normalizedExistingDeliveryItems || []
      : normalizedExistingTableItems || [];

    const normalizedPending = pendingItems.filter((item) => !item.isPaid);
    const unpaidExisting = existingPool.filter((item) => !item.isPaid);
    const hasExistingInCart = normalizedPending.some(
      (item) => item?.order_item_id,
    );

    const baseList = hasExistingInCart
      ? normalizedPending
      : [...unpaidExisting, ...normalizedPending];

    return baseList.map((item, index) => ({
      ...item,
      __invoiceKey: buildInvoiceItemKey(item, index),
    }));
  }, [
    currentDeliveryOrder.items,
    currentOrder.items,
    normalizedExistingDeliveryItems,
    normalizedExistingTableItems,
    selectedTable?.isDelivery,
  ]);

  const invoiceAdjustmentContext = useMemo(() => {
    const items = [
      ...(invoiceItems || []),
      ...(existingItemsForPayment || []),
      ...(currentOrder?.items || []),
    ].filter(isItemActive);
    const orderIds = items
      .map((item) => item?.order_id ?? item?.orderId)
      .filter((value) => value !== null && value !== undefined);
    const orderNumber =
      items.find((item) => item?.order_number || item?.orderNumber)
        ?.order_number ||
      items.find((item) => item?.order_number || item?.orderNumber)
        ?.orderNumber ||
      selectedTable?.orderNumber ||
      selectedTable?.order_number ||
      selectedTable?.deliveryRef ||
      "";
    const lineIds = items
      .map(
        (item) =>
          item?.client_line_id ||
          item?.clientLineId ||
          item?.order_item_id ||
          item?.orderItemId ||
          item?.id,
      )
      .filter((value) => value !== null && value !== undefined);
    return buildInvoiceAdjustmentContext({
      table: selectedTable,
      occupancy_cycle: resolveTableOccupancyCycle(selectedTable),
      orderIds,
      orderNumber,
      lineIds,
    });
  }, [
    currentOrder?.items,
    existingItemsForPayment,
    invoiceItems,
    selectedTable,
  ]);
  const invoiceAdjustmentContextSignature = JSON.stringify(
    invoiceAdjustmentContext || null,
  );

  const persistInvoiceAdjustments = (
    nextAdjustments,
    context = invoiceAdjustmentContext,
  ) => {
    if (!invoiceAdjustmentsStorageKey) return;
    try {
      if (
        Array.isArray(nextAdjustments) &&
        nextAdjustments.length > 0 &&
        context
      ) {
        localStorage.setItem(
          invoiceAdjustmentsStorageKey,
          serializeInvoiceAdjustmentDraft({ context, adjustments: nextAdjustments }),
        );
      } else {
        localStorage.removeItem(invoiceAdjustmentsStorageKey);
      }
    } catch (err) {
      console.warn("Impossible de persister les remises temporaires:", err);
    }
  };

  // Recharger les remises en cours quand on change de table/livraison (évite pertes si re-mount)
  useEffect(() => {
    skipInvoiceAdjustmentPersistRef.current = true;
    invoiceAdjustmentHydratedKeyRef.current = null;
    if (!invoiceAdjustmentsStorageKey || !invoiceAdjustmentContext) {
      setTempAdjustments([]);
      return;
    }
    const hasServiceIdentity = Boolean(
      invoiceAdjustmentContext.orderIds?.length ||
        invoiceAdjustmentContext.orderNumber ||
        invoiceAdjustmentContext.lineIds?.length,
    );
    if (!hasServiceIdentity) {
      setTempAdjustments([]);
      return;
    }
    try {
      const stored = localStorage.getItem(invoiceAdjustmentsStorageKey);
      if (stored) {
        const parsed = readInvoiceAdjustmentDraft(
          stored,
          invoiceAdjustmentContext,
        );
        if (parsed.discard) localStorage.removeItem(invoiceAdjustmentsStorageKey);
        invoiceAdjustmentHydratedKeyRef.current = invoiceAdjustmentsStorageKey;
        setTempAdjustments(parsed.adjustments);
        return;
      }
    } catch (err) {
      console.warn("Impossible de charger les remises temporaires:", err);
    }
    invoiceAdjustmentHydratedKeyRef.current = invoiceAdjustmentsStorageKey;
    setTempAdjustments([]);
  }, [invoiceAdjustmentsStorageKey, invoiceAdjustmentContextSignature]);

  // Sauvegarder en continu les remises en cours
  useEffect(() => {
    if (skipInvoiceAdjustmentPersistRef.current) {
      skipInvoiceAdjustmentPersistRef.current = false;
      return;
    }
    if (
      invoiceAdjustmentHydratedKeyRef.current !== invoiceAdjustmentsStorageKey
    ) {
      return;
    }
    persistInvoiceAdjustments(tempAdjustments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    invoiceAdjustmentsStorageKey,
    invoiceAdjustmentContextSignature,
    tempAdjustments,
  ]);

  // Empêche la fermeture "parasite" pendant l'application d'une remise
  useEffect(() => {
    if (
      !showInvoiceGenerationModal &&
      invoiceModalCloseLockRef.current &&
      !invoiceModalForceCloseRef.current
    ) {
      setShowInvoiceGenerationModal(true);
    }
  }, [showInvoiceGenerationModal]);

  useEffect(() => {
    if (!showInvoiceGenerationModal) {
      clearInvoiceSession();
      return;
    }
    persistInvoiceSession({
      open: true,
      tableId: selectedTable?.id ?? null,
      tableNumber: selectedTable?.number ?? null,
      isDelivery: !!selectedTable?.isDelivery,
      deliveryRef: selectedTable?.deliveryRef ?? null,
      deliveryOrderId:
        selectedTable?.orderId ??
        selectedTable?.deliveryOrder?.id ??
        null,
      cashierId: currentUser?.id ?? null,
      timestamp: Date.now(),
    });
  }, [
    clearInvoiceSession,
    currentUser?.id,
    persistInvoiceSession,
    selectedTable?.deliveryOrder?.id,
    selectedTable?.deliveryRef,
    selectedTable?.id,
    selectedTable?.isDelivery,
    selectedTable?.number,
    selectedTable?.orderId,
    showInvoiceGenerationModal,
  ]);

  // === FONCTIONS POUR LA FACTURATION ET LES RÉDUCTIONS ===

  const invoiceTotalQuantity = useMemo(
    () =>
      invoiceItems.reduce(
        (sum, item) => sum + (Number(item.quantity) || 1),
        0,
      ),
    [invoiceItems],
  );

  const getInvoiceItemKey = (item = {}, index = 0) =>
    item.__invoiceKey || buildInvoiceItemKey(item, index);

  const [showItemDiscountModal, setShowItemDiscountModal] = useState(false);
  const [itemDiscountError, setItemDiscountError] = useState("");
  const hasItemDiscounts = tempAdjustments.some(
    (adjustment) => adjustment?.type === "discount" && adjustment?.scope === "item",
  );
  const [itemDiscountDraft, setItemDiscountDraft] = useState({
    item: null,
    quantity: 1,
    unit: "amount",
    value: "",
    reason: "",
  });

  const formatInvoiceItemName = (item = {}) => {
    const baseName =
      item.name || item.product_name || item.productName || "Article";
    const variationLabel = normalizeVariationLabel(getItemVariationLabel(item));
    if (variationLabel) {
      const baseNorm = baseName.toString().trim();
      const varNorm = variationLabel.toString().trim();
      const lowerBase = baseNorm.toLowerCase();
      const lowerVar = varNorm.toLowerCase();

      // Si la variation est identique au nom du produit, ne pas dupliquer
      if (lowerVar === lowerBase) {
        return baseNorm;
      }

      // Si la variation commence par le nom du produit (ex: "Pizza 4 Fromages Pizza 4 Fromages")
      if (lowerVar.startsWith(lowerBase)) {
        const stripped = varNorm.slice(baseNorm.length).trim();
        if (stripped.length === 0) {
          return baseNorm;
        }
        return `${baseNorm} ${stripped}`.trim();
      }

      return `${baseNorm} ${varNorm}`.trim();
    }
    return baseName;
  };

  const getItemExtrasUnitTotal = (item = {}) =>
    (item.extras || []).reduce((sum, extra) => {
      const qty =
        extra?.selectedQuantity ?? extra?.quantity ?? extra?.qty ?? 1;
      const price = Number(extra?.price) || 0;
      return sum + price * qty;
    }, 0);

  const computeItemTotalWithExtras = (item = {}) => {
    const unitPrice = Number(item.price || item.unit_price || 0);
    const extrasUnit = getItemExtrasUnitTotal(item);
    const quantity = Number(item.quantity) || 1;
    return (unitPrice + extrasUnit) * quantity;
  };

  const canApplyDiscount = () => {
    const role = getCurrentRole();
    const policyRole = permissionPolicies?.find(
      (r) => r.name?.toLowerCase() === role,
    );
    if (typeof policyRole?.canDiscount === "boolean") {
      return policyRole.canDiscount;
    }
    const defaultRules = {
      caissier: true,
      cashier: true,
      serveur: false,
      server: false,
      manager: true,
      admin: true,
      superadmin: true,
    };
    if (role in defaultRules) return defaultRules[role];
    return true;
  };

  const getDiscountTotal = () =>
    tempAdjustments.reduce((total, adj) => {
      if (adj?.type === "discount") {
        return total + (Number(adj.amount) || 0);
      }
      return total;
    }, 0);

  const getActiveOrderNumber = () => {
    const pool = [
      ...(invoiceItems || []),
      ...(existingItemsForPayment || []),
      ...(currentOrder.items || []),
    ];
    const withOrderNumber = pool.find(
      (item) => item?.order_number || item?.orderNumber,
    );
    return (
      withOrderNumber?.order_number ||
      withOrderNumber?.orderNumber ||
      selectedTable?.orderNumber ||
      selectedTable?.order_number ||
      selectedTable?.deliveryRef ||
      selectedTable?.name ||
      ""
    );
  };

  const getActiveOrderId = () => {
    const pool = [
      ...(invoiceItems || []),
      ...(existingItemsForPayment || []),
      ...(currentOrder.items || []),
    ];
    const withOrderId = pool.find((item) => item?.order_id || item?.orderId);
    return (
      withOrderId?.order_id ||
      withOrderId?.orderId ||
      selectedTable?.orderId ||
      selectedTable?.deliveryOrder?.id ||
      null
    );
  };

  // Ouvrir le modal de génération de facture
  const openInvoiceGenerationModal = () => {
    if (!selectedTable) {
      triggerErrorAnimation(
        isBoutique
          ? "Veuillez ajouter des articles au panier."
          : "Veuillez sélectionner une table !",
      );
      return;
    }

    const hasItems = invoiceItems.length > 0 || hasExistingOrders;
    if (!hasItems) {
      triggerErrorAnimation("Aucune commande à facturer !");
      return;
    }

    setDiscountDraft({
      scope: "cart",
      unit: "amount",
      value: "",
      reason: "",
      targetItemId: null,
    });
    setShowInvoiceGenerationModal(true);
    cashDebugLog("✅ Modal génération facture ouvert");
  };

  const getUserShortLabel = (user) => {
    const firstName = String(user?.first_name || user?.firstName || "").trim();
    if (firstName) return firstName;

    const username = String(user?.username || "").trim();
    if (username) return username;

    const name = String(user?.name || "").trim();
    if (name) return name.split(/\s+/)[0] || name;

    const composed = [
      user?.first_name || user?.firstName,
      user?.last_name || user?.lastName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
    return composed || "Caissier";
  };

  // Construire les données de facture prêtes à imprimer
  const buildInvoicePrintData = () => {
    if (!selectedTable) {
      triggerErrorAnimation(
        isBoutique ? "Aucun panier actif." : "Aucune table sélectionnée !",
      );
      return null;
    }

    const userLabel = getUserShortLabel(currentUser);
    const serviceStarterLabel = resolveServiceStarterName({
      table: selectedTable,
      fallbackUser: currentUser,
    });

    const tableLabelRaw = normalizeTableLabel(selectedTable);
    const tableNumberOnly =
      selectedTable?.number !== undefined && selectedTable?.number !== null
        ? String(selectedTable.number)
        : (() => {
            const raw = String(tableLabelRaw || "");
            const m = raw.match(/(\d+)/);
            if (m?.[1]) return m[1];
            return raw.replace(/^table\s*:?\s*/i, "").trim() || raw;
          })();
    const mergedInvoiceItems = invoiceItems.map((item, index) => {
      const displayName = formatInvoiceItemName(item);
      return {
        ...item,
        name: displayName,
        display_name: displayName,
        _printKey: getInvoiceItemKey(item, index),
      };
    });

    return {
      table_number: tableNumberOnly,
      area_name: printAreaOnDocuments ? (selectedTable?.zone_name || selectedTable?.zone || "") : "",
      cashier_name: userLabel,
      server_name: serviceStarterLabel,
      subtotal: getSubtotalWithExisting(),
      total_adjustments: -getDiscountTotal(),
      final_total: getTotalWithAdjustments(),
      items: mergedInvoiceItems,
      adjustments: tempAdjustments,
      restaurant_name: restaurantInfo?.name || "Restaurant",
      restaurant_address: restaurantInfo?.address || "",
      restaurant_phone: restaurantInfo?.phone || "",
      timestamp: new Date(),
    };
  };

  const buildPrintPromotionSignature = (items = [], table = null) => {
    const tableId = getTableIdentifier(table) ?? "no-table";
    const normalizedItems = (items || [])
      .map((item) => {
        const productId = Number(item?.product_id ?? item?.productId ?? item?.id ?? 0) || 0;
        const quantity = Number(item?.quantity || 0) || 0;
        const unitPrice = Number(
          item?.unitPrice ?? item?.unit_price ?? item?.price ?? item?.product_price ?? 0,
        ) || 0;
        const variationKey = String(
          item?.variation_id ??
            item?.variationId ??
            item?.variation_name ??
            item?.variationName ??
            item?.variationLabel ??
            "",
        );
        const extrasKey = Array.isArray(item?.extras)
          ? item.extras
              .map((extra) =>
                [
                  extra?.id ?? "",
                  extra?.name ?? "",
                  Number(extra?.quantity ?? extra?.qty ?? 1) || 1,
                  Number(extra?.price ?? extra?.unit_price ?? extra?.unitPrice ?? 0) || 0,
                ].join(":"),
              )
              .sort()
              .join("|")
          : "";
        return [productId, quantity, unitPrice, variationKey, extrasKey].join("#");
      })
      .sort()
      .join(";");
    return `${tableId}|${normalizedItems}`;
  };

  const makeClientRequestId = (scope = "print") => {
    const rand = Math.random().toString(36).slice(2, 8);
    return `${scope}-${currentUser?.id || "anon"}-${Date.now()}-${rand}`;
  };

  // Imprimer une facture rapidement sans passer par les ajustements
  const quickPrintInvoice = async () => {
    if (printActionInFlightRef.current) return;

    const invoiceData = buildInvoicePrintData();
    if (!invoiceData) return;

    const draftItems = cartViewModel?.newItems || [];
    const hasDraftItems = draftItems.length > 0;
    const draftSignature = hasDraftItems
      ? buildPrintPromotionSignature(draftItems, selectedTable)
      : "";

    const now = Date.now();
    if (
      hasDraftItems &&
      draftSignature &&
      lastPrintPromotionSignatureRef.current === draftSignature &&
      now - lastPrintPromotionAtRef.current < 3000
    ) {
      cashDebugLog("⛔ Impression ignorée: brouillon déjà promu récemment", {
        tableId: getTableIdentifier(selectedTable),
      });
      return;
    }

    printActionInFlightRef.current = true;
    setIsPrintActionInProgress(true);
    const clientRequestId = makeClientRequestId("print");
    printPromotionContextRef.current = { signature: draftSignature, clientRequestId };

    let printFailed = false;
    let printFailureMessage = "Erreur inconnue";
    try {
      await printInvoice(invoiceData, { skipDraftPromotion: true });
    } catch (error) {
      printFailed = true;
      printFailureMessage = String(error?.message || error || "Erreur inconnue");
      console.warn("Impression facture impossible (promotion conservée):", error?.message || error);
    } finally {
      try {
        promoteDraftItemsAfterInvoicePrint({
          suppressDispatch: true,
          force: true,
        });
      } finally {
        if (printFailed) {
          const conciseFailure =
            printFailureMessage.length > 140
              ? `${printFailureMessage.slice(0, 137)}...`
              : printFailureMessage;
          triggerErrorAnimation(
            `Impression impossible : ${conciseFailure}. Les articles sont conservés en attente.`,
          );
        }
        printActionInFlightRef.current = false;
        setIsPrintActionInProgress(false);
      }
    }
  };

  const promoteDraftItemsAfterInvoicePrint = (options = {}) => {
    const draftCount = cartViewModel.newItems?.length || 0;
    if (!selectedTable || draftCount === 0) return;
    if (orderActionInFlightRef.current) return;

    const suppressDispatch = options?.suppressDispatch === true;
    const forced = options?.force === true;
    const ctx = printPromotionContextRef.current || {};
    const signature =
      ctx?.signature || buildPrintPromotionSignature(cartViewModel.newItems || [], selectedTable);
    const now = Date.now();

    if (
      !forced &&
      signature &&
      lastPrintPromotionSignatureRef.current === signature &&
      now - lastPrintPromotionAtRef.current < 3000
    ) {
      cashDebugLog("⛔ Promotion brouillon ignorée (déduplication)", {
        tableId: getTableIdentifier(selectedTable),
      });
      return;
    }
    if (signature) {
      lastPrintPromotionSignatureRef.current = signature;
      lastPrintPromotionAtRef.current = now;
    }

    window.setTimeout(() => {
      if (orderActionInFlightRef.current) return;
      const promoteClientRequestId = ctx?.clientRequestId || makeClientRequestId("print");
      if (hasExistingOrders) {
        void addToExistingOrder(undefined, {
          suppressDispatch,
          clientRequestId: promoteClientRequestId,
        });
        printPromotionContextRef.current = null;
        return;
      }
      void sendToCuisineBar({
        suppressDispatch,
        clientRequestId: promoteClientRequestId,
      });
      printPromotionContextRef.current = null;
    }, 0);
  };

  // Imprimer en utilisant le template de la caisse (sans modifier l'état du panier/remises)
  const printInvoice = async (invoiceData, options = {}) => {
    const skipDraftPromotion = options?.skipDraftPromotion === true;
    const settings = await fetchReceiptPrinterSettingsSafe();
    const receiptConnectionType = String(
      settings?.connection_type || "",
    ).toLowerCase();
    if (
      receiptConnectionType === "ethernet" ||
      receiptConnectionType === "direct_rj45"
    ) {
      try {
        const res = await fetch(`${API_BASE}/receipt-printer/print-invoice`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(invoiceData || {}),
        });
        if (!res.ok) {
          let message = "Erreur d'impression (facture).";
          try {
            const data = await res.json();
            message = data?.error || message;
          } catch (_) {}
          throw new Error(message);
        }
        triggerSuccessAnimation("Facture envoyée à l'imprimante !");
        if (!skipDraftPromotion) {
          promoteDraftItemsAfterInvoicePrint();
        }
        return;
      } catch (error) {
        if (receiptConnectionType === "direct_rj45") {
          throw error;
        }
        console.warn(
          "⚠�? Impression Ethernet échouée, fallback USB:",
          error?.message || error,
        );
      }
    }

    const invoiceDiscount = Math.max(0, Number(invoiceData.total_adjustments || 0) * -1);
    const showInvoiceDiscount = invoiceDiscount > 0.01;
    const formatInvoiceAmount = (value) =>
      String(Math.round(Number(value || 0))).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <title>Facture</title>
          <style>
            body {
              font-family: Arial, Helvetica, sans-serif;
              margin: 0;
              padding: 20px;
              font-size: 12px;
              color: #000;
              background: #fff;
              font-weight: 700;
            }
            .receipt {
              max-width: 420px;
              margin: 0 auto;
              background: #fff;
              border-radius: 12px;
              padding: 16px;
              border: 1px solid #000;
            }
            .header { text-align: center; margin-bottom: 14px; }
            .brand { font-size: 18px; letter-spacing: 0.02em; }
            .subtitle { font-size: 11px; color: #000; margin-top: 3px; font-weight: 600; }
            .header-contact { font-size: 13px; color: #000; margin-top: 3px; font-weight: 900; }
            .section { margin-bottom: 12px; }
            .section-title {
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.08em;
              color: #000;
              margin-bottom: 6px;
              font-weight: 700;
            }
            .item {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 8px;
              padding: 6px 0;
              border-bottom: 1px dashed #000;
              font-size: 15px;
              font-weight: 900;
            }
            .item:last-child { border-bottom: none; }
            .item-name { color: #000; flex: 1 1 auto; min-width: 0; font-weight: 900; font-size: 15px; }
            .item-extras { font-size: 12px; color: #000; margin-top: 2px; font-weight: 700; }
            .price {
              color: #000;
              font-weight: 900;
              font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
              min-width: 100px;
              text-align: right;
              white-space: nowrap;
              flex: 0 0 auto;
              font-size: 15px;
              font-variant-numeric: tabular-nums;
            }
            .totals .row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 14px; }
            .totals .row span:last-child {
              min-width: 100px;
              text-align: right;
              font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
              font-variant-numeric: tabular-nums;
            }
            .totals .row.total { font-size: 16px; color: #000; }
            .footer { text-align: center; font-size: 10px; color: #000; margin-top: 12px; font-weight: 600; }
            .no-print {
              margin-top: 20px;
              text-align: center;
            }
            .no-print button {
              padding: 10px 18px;
              border-radius: 10px;
              border: 1px solid #000;
              background: #fff;
              cursor: pointer;
              margin: 0 6px;
            }
            @media print {
              .no-print { display: none; }
              body { background: #fff; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="header">
              <div class="brand">${invoiceData.restaurant_name}</div>
              ${invoiceData.restaurant_address ? `<div class="header-contact">${invoiceData.restaurant_address}</div>` : ""}
              ${
                invoiceData.restaurant_phone
                  ? `<div class="header-contact">Tel: ${invoiceData.restaurant_phone}</div>`
                  : ""
              }
            </div>

            <div class="section">
              <div class="section-title">COMMANDE</div>
              ${invoiceData.is_unpaid ? '<div class="subtitle">FACTURE NON PAYÉE</div>' : ""}
              ${isBoutique ? "" : `<div class="subtitle">Table : ${invoiceData.table_number}</div>`}
              ${!isBoutique && invoiceData.area_name ? `<div class="subtitle">Aire : ${escapeHtml(invoiceData.area_name)}</div>` : ""}
              <div class="subtitle">Caissier : ${invoiceData.cashier_name}</div>
              ${isBoutique ? "" : `<div class="subtitle">Serveur : ${invoiceData.server_name}</div>`}
              <div class="subtitle">Date : ${new Date(invoiceData.timestamp).toLocaleString("fr-FR").replace(/\\u202F|\\u00A0/g, " ")}</div>
            </div>

            <div class="section">
              <div class="section-title">ARTICLES</div>
              ${invoiceData.items
                .map((item) => {
                  const extrasTotal = (item.extras || []).reduce(
                    (sum, ext) => sum + (ext.price || 0) * (ext.quantity || 1),
                    0,
                  );
                  const itemTotal =
                    (item.price + extrasTotal) * (item.quantity || 1);
                  const extras =
                    item.extras && item.extras.length > 0
                      ? item.extras
                          .map(
                            (ext) =>
                              `${ext.name}${
                                ext.quantity > 1 ? ` ×${ext.quantity}` : ""
                              } (${formatInvoiceAmount(Number(ext.price || 0))} F)`,
                          )
                          .join(", ")
                      : "";
                  return `
                    <div class="item">
                      <div>
                        <div class="item-name">${item.quantity || 1}× ${item.display_name || item.name}</div>
                        ${extras ? `<div class="item-extras">Suppléments : ${extras}</div>` : ""}
                      </div>
                      <div class="price">${formatInvoiceAmount(itemTotal)} F</div>
                    </div>
                  `;
                })
                .join("")}
            </div>

            <div class="section totals">
              <div class="row">
                <span>Sous-total</span>
                <span>${formatInvoiceAmount(invoiceData.subtotal)} F</span>
              </div>
              ${
                showInvoiceDiscount
                  ? `
              <div class="row">
                <span>Réduction</span>
                <span>-${formatInvoiceAmount(invoiceDiscount)} F</span>
              </div>
              `
                  : ""
              }
              <div class="row total">
                <span>Total</span>
                <span>${formatInvoiceAmount(invoiceData.final_total)} F</span>
              </div>
            </div>

            <div class="footer">
              <div>Merci pour votre visite !</div>
              <div>Powered by MOO Sync - ${new Date().getFullYear()}</div>
            </div>
          </div>
        </body>
      </html>
    `;
    void printContent; // Modèle historique conservé temporairement; rendu USB canonique ci-dessous.
    await printHtmlViaUsbPrinter(
      renderUsbTicketHtml({
        kind: "invoice",
        paperWidthMm: resolveUsbPrintOptions(settings).paperWidthMm,
        payload: {
          ...invoiceData,
          discount: invoiceDiscount,
          total: invoiceData.final_total,
        },
      }),
      settings,
    );
    triggerSuccessAnimation("Facture envoyée à l'imprimante !");
    if (!skipDraftPromotion) {
      promoteDraftItemsAfterInvoicePrint();
    }
  };

  // Fermer le modal de génération de facture
  const closeInvoiceGenerationModal = (options = {}) => {
    const { clearAdjustments = false, force = false } = options;
    if (!force && !clearAdjustments && invoiceModalCloseLockRef.current) {
      return;
    }
    if (force || clearAdjustments) {
      invoiceModalCloseLockRef.current = false;
      invoiceModalForceCloseRef.current = true;
      window.setTimeout(() => {
        invoiceModalForceCloseRef.current = false;
      }, 250);
    }
    if (clearAdjustments) {
      setTempAdjustments([]);
      try {
        const storageKey = buildInvoiceAdjustmentsStorageKey(selectedTable);
        if (storageKey) localStorage.removeItem(storageKey);
      } catch (_) {}
    }
    setDiscountDraft({
      scope: "cart",
      unit: "percentage",
      value: "",
      reason: "",
      targetItemId: null,
    });
    clearInvoiceSession();
    setShowInvoiceGenerationModal(false);
  };

  // Calculer le total avec ajustements
  const getTotalWithAdjustments = () => {
    const subtotal = getSubtotalWithExisting();
    const totalDiscounts = getDiscountTotal();
    const legacyDiscount = Math.max(0, Number(getDiscountValue()) || 0);
    return Math.max(0, subtotal - totalDiscounts - legacyDiscount);
  };

  const handleApplyDiscount = () => {
    if (persistedSplitBill?.status === "active") {
      triggerErrorAnimation("Pour diviser la table, réduisez chaque facture depuis « Diviser ».");
      return;
    }
    invoiceModalCloseLockRef.current = true;
    window.setTimeout(() => {
      invoiceModalCloseLockRef.current = false;
    }, 500);

    const allowed = canApplyDiscount();
    if (!allowed) {
      triggerErrorAnimation(
        "Vous n'avez pas les droits pour appliquer une remise.",
      );
      return;
    }

    if (!invoiceItems.length) {
      triggerErrorAnimation("Aucun article à remiser.");
      return;
    }

    if (hasItemDiscounts) {
      triggerErrorAnimation(
        "Retirez les remises par article avant d'appliquer une remise au panier.",
      );
      return;
    }

    const reason = discountDraft.reason.trim();
    if (!reason) {
      triggerErrorAnimation("Ajoutez un motif pour la réduction.");
      return;
    }

    const value = Number(discountDraft.value);
    if (!Number.isFinite(value) || value <= 0) {
      triggerErrorAnimation("Saisissez un montant ou un pourcentage valide.");
      return;
    }

    let baseAmount = getSubtotalWithExisting();
    let targetLabel = "Panier";
    let targetItemId = null;
    let targetItem = null;

    // Réduction panier uniquement ici

    if (!Number.isFinite(baseAmount) || baseAmount <= 0) {
      triggerErrorAnimation("Impossible d'appliquer une remise sur un total nul.");
      return;
    }

    if (discountDraft.unit === "percentage") {
      if (value > 100) {
        triggerErrorAnimation("Le pourcentage de remise ne peut pas dépasser 100%.");
        return;
      }
    } else if (value > baseAmount) {
      triggerErrorAnimation(
        `La remise dépasse le total remisable (max ${formatPrice(baseAmount)}).`,
      );
      return;
    }

    const computedAmount =
      discountDraft.unit === "percentage" ? (baseAmount * value) / 100 : value;
    const finalAmount = Math.max(0, Number(computedAmount) || 0);

    const activeOrderId = getActiveOrderId();
    const authorName =
      [
        currentUser?.first_name || currentUser?.firstName,
        currentUser?.last_name || currentUser?.lastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim() || currentUser?.name || "Caissier";

    const newAdjustment = {
      id: Date.now(),
      type: "discount",
      scope: discountDraft.scope,
      unit: discountDraft.unit,
      value,
      amount: finalAmount,
      reason,
      targetQuantity: discountDraft.targetQuantity || 1,
      targetItemId,
      targetLabel,
      createdAt: new Date().toISOString(),
      author: authorName,
      orderNumber: getActiveOrderNumber(),
      orderId: activeOrderId,
      tableLabel: getTableDisplayName(selectedTable),
      draftContext: invoiceAdjustmentContext,
    };

    setTempAdjustments((prev) => {
      const next = [
        newAdjustment,
        ...prev.filter(
          (adjustment) =>
            !(
              adjustment?.type === "discount" &&
              adjustment?.scope === "cart"
            ),
        ),
      ];
      persistInvoiceAdjustments(next);
      return next;
    });
    setDiscountDraft((prev) => ({
      ...prev,
      value: "",
      reason: "",
    }));

    closeInvoiceGenerationModal({ force: true });
    triggerSuccessAnimation("Réduction appliquée.");
  };

  const handleRemoveDiscount = (id) => {
    setTempAdjustments((prev) => {
      const next = prev.filter((adj) => adj.id !== id);
      persistInvoiceAdjustments(next);
      return next;
    });
  };

  const openItemDiscountModal = (item, adjustment = null) => {
    if (!item) return;
    setItemDiscountError("");
    const maxQty = Number(item.quantity) || 1;
    setItemDiscountDraft({
      item,
      quantity: Math.min(
        Math.max(1, Number(adjustment?.targetQuantity) || 1),
        maxQty,
      ),
      unit: adjustment?.unit || "amount",
      value: adjustment?.value ? String(adjustment.value) : "",
      reason: adjustment?.reason || "",
    });
    setShowItemDiscountModal(true);
  };

  const handleApplyItemDiscount = () => {
    setItemDiscountError("");
    invoiceModalCloseLockRef.current = true;
    window.setTimeout(() => {
      invoiceModalCloseLockRef.current = false;
    }, 500);

    const allowed = canApplyDiscount();
    if (!allowed) {
      setItemDiscountError(
        "Vous n'avez pas les droits pour appliquer une remise.",
      );
      return;
    }
    const item = itemDiscountDraft.item;
    if (!item) {
      setItemDiscountError("Sélectionnez un article.");
      return;
    }
    const reason = (itemDiscountDraft.reason || "").trim();
    if (!reason) {
      setItemDiscountError("Ajoutez un motif pour la réduction.");
      return;
    }
    const value = Number(itemDiscountDraft.value);
    if (!Number.isFinite(value) || value <= 0) {
      setItemDiscountError("Saisissez un montant ou un pourcentage valide.");
      return;
    }
    if (
      tempAdjustments.some(
        (adjustment) =>
          adjustment?.type === "discount" && adjustment?.scope === "cart",
      )
    ) {
      setItemDiscountError(
        "Retirez la remise au panier avant d'appliquer une remise par article.",
      );
      return;
    }
    const maxQty = Number(item.quantity) || 1;
    const chosenQty = Math.min(
      Math.max(1, Number(itemDiscountDraft.quantity) || 1),
      maxQty,
    );
    const unitWithExtras =
      computeItemTotalWithExtras({ ...item, quantity: 1 }) || 0;
    const baseAmount = unitWithExtras * chosenQty;

    if (!Number.isFinite(baseAmount) || baseAmount <= 0) {
      setItemDiscountError(
        "Impossible d'appliquer une remise sur un article à 0.",
      );
      return;
    }

    if (itemDiscountDraft.unit === "percentage") {
      if (value > 100) {
        setItemDiscountError("Le pourcentage de remise ne peut pas dépasser 100%.");
        return;
      }
    } else if (value > unitWithExtras) {
      setItemDiscountError(
        `La remise unitaire dépasse le prix remisable (max ${formatPrice(unitWithExtras)}).`,
      );
      return;
    }

    const computedAmount =
      itemDiscountDraft.unit === "percentage"
        ? (baseAmount * value) / 100
        : value * chosenQty;
    const finalAmount = Math.max(0, Number(computedAmount) || 0);

    const activeOrderId = getActiveOrderId();
    const authorName =
      [
        currentUser?.first_name || currentUser?.firstName,
        currentUser?.last_name || currentUser?.lastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim() || currentUser?.name || "Caissier";

    const newAdjustment = {
      id: Date.now(),
      type: "discount",
      scope: "item",
      unit: itemDiscountDraft.unit,
      value,
      amount: finalAmount,
      targetQuantity: chosenQty,
      reason,
      targetItemId: getInvoiceItemKey(item),
      targetLabel: `${formatInvoiceItemName(item)} · ${chosenQty}x`,
      createdAt: new Date().toISOString(),
      author: authorName,
      orderNumber: getActiveOrderNumber(),
      orderId: activeOrderId,
      tableLabel: getTableDisplayName(selectedTable),
      draftContext: invoiceAdjustmentContext,
    };

    setTempAdjustments((prev) => {
      const next = [
        newAdjustment,
        ...prev.filter(
          (adjustment) =>
            !(
              adjustment?.type === "discount" &&
              adjustment?.scope === "item" &&
              adjustment?.targetItemId === newAdjustment.targetItemId
            ),
        ),
      ];
      persistInvoiceAdjustments(next);
      return next;
    });
    setShowItemDiscountModal(false);
    setShowInvoiceGenerationModal(true);

    triggerSuccessAnimation("Réduction article appliquée.");
  };

  const handlePrintSplitInvoice = async (invoice) => {
    if (!invoice) return;
    if (invoice.settlement_id) {
      try {
        const response = await fetch(
          `${API_BASE}/split-bills/settlements/${invoice.settlement_id}/receipt`,
          { cache: "no-store" },
        );
        const receipt = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(receipt.error || "Reçu introuvable.");
        await printReceipt(
          {
            ...receipt,
            amount: Number(receipt.total) || 0,
            amountReceived: Number(receipt.amount_received) || 0,
            changeGiven: Number(receipt.change_given) || 0,
            paymentNumber: receipt.payment_number,
            paidAt: receipt.timestamp,
            itemsSnapshot: receipt.items || [],
            paymentMethods: [
              {
                method: receipt.payment_method || "Paiement",
                amount: Number(receipt.total) || 0,
              },
            ],
            order: {
              subtotal: Number(receipt.subtotal) || 0,
              total_amount: Number(receipt.total) || 0,
              total_adjustments: Number(receipt.total_adjustments) || 0,
              items: receipt.items || [],
            },
          },
          { number: receipt.table_number, zone_name: receipt.area_name },
        );
        triggerSuccessAnimation(`Reçu ${receipt.payment_number} imprimé !`);
        return;
      } catch (error) {
        triggerErrorAnimation(error.message || "Impossible d’imprimer ce reçu.");
        return;
      }
    }
    const split = currentOrderForPayment?.splitBill || persistedSplitBill;
    if (!split?.id) return;
    let currentInvoice;
    try {
      const response = await fetch(`${API_BASE}/split-bills/${split.id}`, { cache: "no-store" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Facture introuvable.");
      currentInvoice = hydrateSplitInvoices(payload.split).find((part) => String(part.id) === String(invoice.id));
      if (!currentInvoice || currentInvoice.status !== "open" || !currentInvoice.pricing || currentInvoice.pricing_error) {
        throw new Error("Cette facture a changé. Rouvrez la division avant d’imprimer.");
      }
    } catch (error) {
      triggerErrorAnimation(error.message || "Impossible de vérifier cette facture.");
      return;
    }
    const userLabel = getUserShortLabel(currentUser);
    const serviceStarterLabel = resolveServiceStarterName({
      table: selectedTable,
      fallbackUser: currentUser,
    });
    const splitTableLabelRaw = normalizeTableLabel(selectedTable);
    const splitTableNumberOnly =
      selectedTable?.number !== undefined && selectedTable?.number !== null
        ? String(selectedTable.number)
        : (() => {
            const raw = String(splitTableLabelRaw || "");
            const m = raw.match(/(\d+)/);
            if (m?.[1]) return m[1];
            return raw.replace(/^table\s*:?\s*/i, "").trim() || raw;
          })();

    const enrichedInvoiceItems = (currentInvoice.items || []).map((item) => {
      const displayName = formatInvoiceItemName(item);
      return {
        ...item,
        name: displayName,
        display_name: displayName,
        total_price_with_extras: Number(item.totalPrice || item.total_price || 0),
      };
    });

    const invoiceData = {
      invoice_number: "",
      table_number: splitTableNumberOnly,
      area_name: printAreaOnDocuments ? (selectedTable?.zone_name || selectedTable?.zone || "") : "",
      cashier_name: userLabel,
      server_name: serviceStarterLabel,
      subtotal: currentInvoice.pricing.subtotal,
      total_adjustments: -currentInvoice.pricing.discount_total,
      total: currentInvoice.pricing.amount_due,
      final_total: currentInvoice.pricing.amount_due,
      adjustments: currentInvoice.pricing.priced_adjustments,
      is_unpaid: true,
      timestamp: new Date().toISOString(),
      items: enrichedInvoiceItems,
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
      restaurant_name: restaurantInfo?.name || "Restaurant",
      restaurant_address: restaurantInfo?.address || "",
      restaurant_phone: restaurantInfo?.phone || "",
    };

    try {
      await printInvoice(invoiceData, { skipDraftPromotion: true });
    } catch (error) {
      triggerErrorAnimation(error.message || "Impossible d’imprimer cette facture.");
    }
  };

  const requestStoredSplitQuote = async (split, invoice = null) => {
    const splitSelection = {
      groupId: split.id,
      ...(invoice ? { partId: invoice.id } : { mode: "global" }),
      expectedVersion: Number(split.version),
    };
    const response = await fetch(`${API_BASE}/payments/quote`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ splitSelection }),
    });
    const quote = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(quote.error || "Impossible de préparer ce paiement.");
    }
    return { quote, splitSelection };
  };

  const openStoredSplitPayment = async (invoice = null, splitOverride = null) => {
    const split = splitOverride || currentOrderForPayment?.splitBill || persistedSplitBill;
    if (!split?.id) {
      triggerErrorAnimation("La scission doit d’abord être enregistrée.");
      return;
    }
    if (invoice?.status === "paid") return;
    try {
      const { quote, splitSelection } = await requestStoredSplitQuote(split, invoice);
      const items = (quote.items || []).map((item) => ({
        ...item,
        id: item.id,
        order_item_id: item.id,
        name: item.display_name || item.product_name || "Article",
        price: Number(item.unit_price || 0),
        totalPrice: Number(item.line_total || 0),
        total_price: Number(item.line_total || 0),
      }));
      setCurrentOrderForPayment((previous) => ({
        ...(previous || {}),
        table: selectedTable,
        table_id: Number(getApiTableId(selectedTable)),
        table_number: selectedTable?.number,
        table_label: `Table ${getTableDisplayName(selectedTable)}`,
        items,
        orderIds: quote.order_ids || [],
        total_amount: Number(quote.amount_due) || 0,
        subtotal: Number(quote.subtotal) || 0,
        adjustments: quote.adjustments || [],
        authoritative_quote: quote,
        splitSelection,
        splitPaymentName: invoice?.name || "Paiement global",
        isSplit: true,
        splitBill: split,
      }));
      setShowInvoiceGenerationModal(false);
      setShowSplitModal(false);
      setShowPaymentInterface(true);
    } catch (error) {
      triggerErrorAnimation(error.message || "Impossible de préparer ce paiement.");
    }
  };

  const startPaymentFromCart = async () => {
    const tableId = Number(getApiTableId(selectedTable));
    if (
      Number.isInteger(tableId) &&
      tableId > 0 &&
      !selectedTable?.isDelivery &&
      !isFastFoodMode
    ) {
      try {
        const response = await fetch(
          `${API_BASE}/split-bills/active?tableId=${tableId}&_=${Date.now()}`,
          { cache: "no-store" },
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || "Solde indisponible");
        }
        const activeSplit = payload.split;
        if (activeSplit?.id) {
          const hydratedInvoices = hydrateSplitInvoices(activeSplit);
          setPersistedSplitBill(activeSplit);
          setSplitInvoices(hydratedInvoices);
          const hasDraftItems = (currentOrder.items || [])
            .filter(isItemActive)
            .some(
              (item) =>
                !item?.order_item_id &&
                !item?.orderItemId &&
                !item?.order_itemId,
            );
          if (hasDraftItems) {
            const { quote, splitSelection } = await requestStoredSplitQuote(
              activeSplit,
              null,
            );
            return processPayment({
              splitPaymentContext: {
                split: activeSplit,
                splitSelection,
                quote,
              },
            });
          }
          await openStoredSplitPayment(null, activeSplit);
          return true;
        }
      } catch (error) {
        console.warn("Impossible de vérifier le solde de la table:", error);
        triggerErrorAnimation(
          "Le solde de la table n’a pas pu être vérifié. Réessayez.",
        );
        return false;
      }
    }
    return processPayment();
  };

  const persistSplitInvoiceAdjustments = async (invoiceId, adjustments, successMessage) => {
    if (splitDiscountInFlightRef.current) return;
    splitDiscountInFlightRef.current = true;
    const split = currentOrderForPayment?.splitBill || persistedSplitBill;
    try {
      if (!split?.id || !canApplyDiscount()) {
        throw new Error("Vous n’avez pas le droit de modifier les réductions.");
      }
      const response = await fetch(
        `${API_BASE}/split-bills/${split.id}/parts/${invoiceId}/adjustments`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            expectedVersion: split.version,
            adjustments,
          }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw Object.assign(
        new Error(payload.error || "Réduction refusée."),
        { status: response.status },
      );
      setPersistedSplitBill(payload.split);
      const storedPart = payload.split.parts?.find((part) => String(part.id) === String(invoiceId));
      const updateInvoices = (invoices = []) => invoices.map((entry) =>
        String(entry.id) === String(invoiceId)
          ? { ...entry, adjustments: storedPart?.adjustments || [], pricing: storedPart?.pricing }
          : entry,
      );
      setSplitInvoices(updateInvoices);
      setCurrentOrderForPayment((previous) => previous ? {
        ...previous,
        splitBill: payload.split,
        splitInvoices: updateInvoices(previous.splitInvoices || []),
      } : previous);
      setSplitDiscountEditor(null);
      setSplitItemDiscountDraft(null);
      triggerSuccessAnimation(successMessage);
    } catch (error) {
      if (error.status === 409) {
        await loadAuthoritativeSplitForEditor().catch(() => {});
        setSplitDiscountEditor(null);
        setSplitItemDiscountDraft(null);
        triggerErrorAnimation("Cette facture a changé sur un autre poste. Ouvrez sa réduction à nouveau.");
      } else {
        triggerErrorAnimation(error.message || "Impossible d’enregistrer la réduction.");
      }
    } finally {
      splitDiscountInFlightRef.current = false;
    }
  };

  const saveSplitInvoiceDiscount = async (draft = splitDiscountEditor) => {
    const split = currentOrderForPayment?.splitBill || persistedSplitBill;
    const invoice = hydrateSplitInvoices(split).find((entry) => String(entry.id) === String(draft?.invoiceId));
    const value = Number(draft?.value);
    const reason = String(draft?.reason || "").trim();
    if (!invoice || !Number.isFinite(value) || value <= 0 || !reason) {
      triggerErrorAnimation("Saisissez une réduction valide et son motif.");
      return;
    }
    const selectedItem = invoice.items?.find((item) => String(item.order_item_id || item.id) === String(draft.orderItemId));
    const adjustment = {
      scope: draft.scope,
      unit: draft.unit,
      value,
      reason,
      ...(draft.scope === "item" ? {
        orderItemId: Number(selectedItem?.order_item_id || selectedItem?.id),
        targetQuantity: Number(draft.quantity),
        targetLabel: selectedItem?.name || selectedItem?.product_name || "Article",
      } : {}),
    };
    const existing = (invoice.adjustments || [])
      .filter((entry) => draft.scope === "item" && entry.scope === "item" &&
        Number(entry.order_item_id) !== Number(adjustment.orderItemId))
      .map((entry) => ({
        id: entry.id, scope: entry.scope, unit: entry.unit,
        value: Number(entry.value), reason: entry.reason,
        orderItemId: Number(entry.order_item_id),
        targetQuantity: Number(entry.target_quantity), targetLabel: entry.target_label,
      }));
    await persistSplitInvoiceAdjustments(invoice.id, [...existing, adjustment], "Réduction enregistrée sur la sous-facture.");
  };

  const removeSplitInvoiceDiscount = async (invoiceId, adjustmentId) => {
    const split = currentOrderForPayment?.splitBill || persistedSplitBill;
    const invoice = split?.parts?.find((part) => String(part.id) === String(invoiceId));
    if (!invoice) return;
    const remaining = (invoice.adjustments || [])
      .filter((entry) => String(entry.id) !== String(adjustmentId))
      .map((entry) => ({
        id: entry.id, scope: entry.scope, unit: entry.unit,
        value: Number(entry.value), reason: entry.reason,
        orderItemId: entry.order_item_id, targetQuantity: entry.target_quantity,
        targetLabel: entry.target_label,
      }));
    await persistSplitInvoiceAdjustments(invoiceId, remaining, "Réduction retirée.");
  };

  const assignUnassignedItemToSplit = async (item) => {
    const split = currentOrderForPayment?.splitBill || persistedSplitBill;
    const draft = splitAssignmentDrafts[item?.id] || {};
    const partId = draft.partId || split?.parts?.find((part) => part.status === "open")?.id;
    const quantity = Number(draft.quantity || item?.available_quantity || 0);
    if (!split?.id || !partId || !Number.isInteger(quantity) || quantity <= 0) {
      triggerErrorAnimation("Choisissez une sous-facture et une quantité valide.");
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/split-bills/${split.id}/assign`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          partId,
          orderItemId: item.id,
          quantity,
          expectedVersion: split.version,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Affectation refusée.");
      const refreshed = payload.split;
      const hydratedInvoices = (refreshed.parts || []).map((part) => {
        const items = (part.lines || []).map((line) => {
          const extrasTotal = (line.extras || []).reduce(
            (sum, extra) => sum + Number(extra.total_price || 0),
            0,
          );
          const sourceQuantity = Number(line.source_quantity || line.quantity || 1);
          const baseTotal = Number(line.total_price || 0) *
            (Number(line.quantity || 0) / sourceQuantity);
          return {
            ...line,
            id: line.order_item_id,
            name: line.product_name,
            price: Number(line.unit_price || 0),
            totalPrice: baseTotal + extrasTotal,
            total_price: baseTotal + extrasTotal,
          };
        });
        return {
          ...part,
          items,
          subtotal: items.reduce((sum, entry) => sum + Number(entry.totalPrice || 0), 0),
        };
      });
      setPersistedSplitBill(refreshed);
      setSplitInvoices(hydratedInvoices);
      setSplitAssignmentDrafts((previous) => {
        const next = { ...previous };
        delete next[item.id];
        return next;
      });
      setCurrentOrderForPayment((previous) => previous ? {
        ...previous,
        splitBill: refreshed,
        splitInvoices: hydratedInvoices,
        items: hydratedInvoices.flatMap((invoice) => invoice.items),
      } : previous);
      triggerSuccessAnimation("Article affecté à la sous-facture.");
    } catch (error) {
      triggerErrorAnimation(error.message || "Impossible d’affecter cet article.");
    }
  };

  const hydrateSplitInvoices = (split) => (split?.parts || []).map((part) => {
    const items = (part.lines || []).map((line) => {
      const extrasTotal = (line.extras || []).reduce(
        (sum, extra) => sum + Number(extra.total_price || 0),
        0,
      );
      const sourceQuantity = Number(line.source_quantity || line.quantity || 1);
      const baseTotal = Number(line.total_price || 0) *
        (Number(line.quantity || 0) / sourceQuantity);
      return {
        ...line,
        id: line.order_item_id,
        order_item_id: line.order_item_id,
        name: line.product_name,
        price: Number(line.unit_price || 0),
        totalPrice: baseTotal + extrasTotal,
        total_price: baseTotal + extrasTotal,
      };
    });
    return {
      ...part,
      items,
      subtotal: items.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0),
    };
  });

  const hydrateSplitAvailableItems = (split) =>
    (split?.unassigned_items || []).map((item) => {
      const quantity = Number(item.available_quantity || 0);
      const sourceQuantity = Number(item.quantity || quantity || 1);
      const baseTotal = Number(item.total_price || 0) *
        (quantity / sourceQuantity);
      const extrasTotal = (item.extras || []).reduce(
        (sum, extra) => sum + Number(extra.total_price || 0),
        0,
      );
      return {
        ...item,
        id: item.id,
        order_item_id: item.id,
        name: item.product_name,
        price: Number(item.unit_price || 0),
        quantity,
        totalPrice: baseTotal + extrasTotal,
        total_price: baseTotal + extrasTotal,
      };
    });

  const loadAuthoritativeSplitForEditor = async () => {
    const tableId = Number(getApiTableId(selectedTable));
    if (!Number.isInteger(tableId) || tableId <= 0) {
      throw new Error("Sélectionnez une table avant de diviser l’addition.");
    }
    const response = await fetch(
      `${API_BASE}/split-bills/active?tableId=${tableId}&_=${Date.now()}`,
      { cache: "no-store" },
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || "Impossible d’actualiser la scission.");
    }
    const split = payload.split;
    if (!split?.id) {
      throw new Error("Cette scission n’est plus active. Actualisez la table.");
    }
    const hydratedInvoices = hydrateSplitInvoices(split);
    setPersistedSplitBill(split);
    setSplitInvoices(hydratedInvoices);
    setCurrentOrderForPayment((previous) => previous ? {
      ...previous,
      splitBill: split,
      splitInvoices: hydratedInvoices,
      items: hydratedInvoices.flatMap((entry) => entry.items || []),
    } : previous);
    return { split, hydratedInvoices };
  };

  const openNewProgressiveSplitInvoice = async () => {
    if (splitActionInFlightRef.current) return;
    splitActionInFlightRef.current = true;
    setIsSplitActionPending(true);
    try {
      const { split } = await loadAuthoritativeSplitForEditor();
      const available = hydrateSplitAvailableItems(split);
      if (!available.length) {
        throw new Error("Aucun article ne reste à facturer.");
      }
      const nextSequence = Math.max(0, ...(split.parts || []).map((part) => Number(part.sequence) || 0)) + 1;
      const draft = { id: `draft-${Date.now()}`, name: `Facture ${nextSequence}`, items: [], subtotal: 0 };
      setSplitEditorPartId(null);
      commitSplitEditorState({ invoices: [draft], unassignedItems: available });
      setSelectedSplitInvoiceId(draft.id);
      setShowSplitModal(true);
      setIsSplitMode(true);
    } catch (error) {
      triggerErrorAnimation(error.message || "Impossible d’ouvrir une nouvelle facture.");
    } finally {
      splitActionInFlightRef.current = false;
      setIsSplitActionPending(false);
    }
  };

  const openSplitInvoiceEditor = async (invoice) => {
    if (splitActionInFlightRef.current) return;
    if (!invoice || invoice.status !== "open") {
      triggerErrorAnimation("Une facture payée ne peut plus être modifiée.");
      return;
    }
    splitActionInFlightRef.current = true;
    setIsSplitActionPending(true);
    try {
      const { split, hydratedInvoices } = await loadAuthoritativeSplitForEditor();
      const authoritativeInvoice = hydratedInvoices.find(
        (entry) => String(entry.id) === String(invoice.id),
      );
      if (!authoritativeInvoice || authoritativeInvoice.status !== "open") {
        throw new Error("Cette facture a déjà été réglée ou supprimée.");
      }
      const available = hydrateSplitAvailableItems(split);
      const editableInvoice = {
        ...authoritativeInvoice,
        items: (authoritativeInvoice.items || []).map((item) => ({ ...item })),
      };
      setSplitEditorPartId(authoritativeInvoice.id);
      commitSplitEditorState({
        invoices: [editableInvoice],
        unassignedItems: available,
      });
      setSelectedSplitInvoiceId(authoritativeInvoice.id);
      setShowSplitModal(true);
      setIsSplitMode(true);
    } catch (error) {
      triggerErrorAnimation(error.message || "Impossible d’actualiser cette facture.");
    } finally {
      splitActionInFlightRef.current = false;
      setIsSplitActionPending(false);
    }
  };

  const deleteSplitInvoice = async (invoice) => {
    const split = currentOrderForPayment?.splitBill || persistedSplitBill;
    if (!split?.id || !invoice || invoice.status !== "open") return;
    try {
      const response = await fetch(
        `${API_BASE}/split-bills/${split.id}/parts/${invoice.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ expectedVersion: split.version }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Suppression refusée.");
      const hydratedInvoices = hydrateSplitInvoices(payload.split);
      setPersistedSplitBill(payload.split);
      setSplitInvoices(hydratedInvoices);
      setCurrentOrderForPayment((previous) => previous ? {
        ...previous,
        splitBill: payload.split,
        splitInvoices: hydratedInvoices,
        items: hydratedInvoices.flatMap((entry) => entry.items),
      } : previous);
      triggerSuccessAnimation(`${invoice.name} supprimée.`);
    } catch (error) {
      triggerErrorAnimation(error.message || "Impossible de supprimer cette facture.");
    }
  };

  // === FONCTIONS SCISSION DE FACTURE ===

  // Initialiser la scission de facture
  const initializeSplit = (authoritativeItems = null) => {
    cashDebugLog("🚀 DEBUT initializeSplit - État avant traitement:", {
      existingItemsForPayment: existingItemsForPayment?.length || 0,
      currentOrderItems: currentOrder.items?.length || 0,
      existingItems:
        existingItemsForPayment?.map((item) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          isPaid: item.isPaid,
          totalPrice: item.totalPrice || item.total_price,
        })) || [],
      currentItems:
        currentOrder.items?.map((item) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          isPaid: item.isPaid,
        })) || [],
    });

    // Récupérer TOUS les articles non payés - PRIORITE AUX EXISTING ITEMS
    let allItems = Array.isArray(authoritativeItems)
      ? authoritativeItems.filter((item) => !item.isPaid && isItemActive(item))
      : [];

    // PRIORITE 1: existingItemsForPayment (articles déjà commandés)
    if (!Array.isArray(authoritativeItems) && existingItemsForPayment?.length > 0) {
      const existingUnpaid = existingItemsForPayment.filter(
        (item) => !item.isPaid,
      );
      allItems = [...existingUnpaid];
      cashDebugLog("📋 Articles existants non payés:", {
        total: existingItemsForPayment.length,
        unpaid: existingUnpaid.length,
        filtered: existingUnpaid.map((item) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          isPaid: item.isPaid,
        })),
      });
    }

    // PRIORITE 2: currentOrder.items (nouveaux articles du panier)
    if (!Array.isArray(authoritativeItems) && currentOrder.items?.length > 0) {
      const newItems = currentOrder.items.filter((item) => !item.isPaid);
      allItems = [...allItems, ...newItems];
      cashDebugLog("🛒 Articles du panier non payés:", {
        total: currentOrder.items.length,
        unpaid: newItems.length,
        filtered: newItems.map((item) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          isPaid: item.isPaid,
        })),
      });
    }

    cashDebugLog("📦 TOTAL articles pour scission:", {
      count: allItems.length,
      items: allItems.map((item) => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        totalPrice: item.totalPrice || item.total_price,
        hasExtras: !!(item.extras && item.extras.length > 0),
      })),
    });

    // Vérifier qu'il y a des articles à scinder
    if (allItems.length === 0) {
      console.error("�?� Aucun article trouvé pour scission!");
      triggerErrorAnimation("Aucun article à scinder !");
      return;
    }

    // Calculer le total pour vérification
    const totalCalculated = allItems.reduce((sum, item) => {
      if (item.totalPrice || item.total_price) {
        return sum + (item.totalPrice || item.total_price);
      }
      const itemExtrasPrice = item.extras
        ? item.extras.reduce(
            (extSum, ext) => extSum + (ext.price || 0) * (ext.quantity || 1),
            0,
          )
        : 0;
      return sum + (item.price + itemExtrasPrice) * item.quantity;
    }, 0);

    cashDebugLog("🔄 Initialisation scission - Calculs finaux:", {
      context:
        existingItemsForPayment?.length > 0 ? "existingItems" : "currentOrder",
      totalItems: allItems.length,
      totalCalculated,
      subtotalComparison: getSubtotalWithExisting(),
      difference: Math.abs(totalCalculated - getSubtotalWithExisting()),
      items: allItems.map((item) => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        hasExtras: !!(item.extras && item.extras.length > 0),
        totalPrice: item.totalPrice || item.total_price,
        calculatedPrice:
          item.price +
          (item.extras
            ? item.extras.reduce(
                (sum, ext) => sum + (ext.price || 0) * (ext.quantity || 1),
                0,
              )
            : 0),
      })),
    });

    // Vérifier la cohérence avant de procéder
    const expectedTotal = getSubtotalWithExisting();
    const difference = Math.abs(totalCalculated - expectedTotal);

    if (difference > 50) {
      console.warn("⚠�? DIFFÉRENCE IMPORTANTE détectée:", {
        calcule: totalCalculated,
        attendu: expectedTotal,
        difference: difference,
      });
    }

    // Nettoyer les doublons dès le départ (mais pas les produits avec extras différents)
    const cleanedItems = allItems.reduce((acc, current) => {
      const currentKey = getItemKey(current);
      const existingIndex = acc.findIndex(
        (item) => getItemKey(item) === currentKey,
      );
      if (existingIndex !== -1) {
        // Fusionner les quantités et prix des vrais doublons (même produit, mêmes extras)
        acc[existingIndex].quantity += current.quantity;
        acc[existingIndex].totalPrice =
          (acc[existingIndex].totalPrice || 0) +
          (current.totalPrice || current.total_price || 0);
      } else {
        acc.push({
          ...current,
          totalPrice:
            current.totalPrice ||
            current.total_price ||
            current.price * current.quantity,
        });
      }
      return acc;
    }, []);

    cashDebugLog("✅ Articles nettoyés pour scission:", {
      avant: allItems.length,
      apres: cleanedItems.length,
      articles: cleanedItems.map((item) => ({
        name: item.name,
        quantity: item.quantity,
      })),
    });

    const draft = { id: `draft-${Date.now()}`, name: "Facture 1", items: [], subtotal: 0 };
    commitSplitEditorState({ invoices: [draft], unassignedItems: cleanedItems });
    setSplitEditorPartId(null);
    setSelectedSplitInvoiceId(draft.id);
    setShowSplitModal(true);
    setIsSplitMode(true);
  };

  const openSplitManagement = async () => {
    const candidateSplit = currentOrderForPayment?.splitBill || persistedSplitBill;
    const selectedTableId = Number(getApiTableId(selectedTable));
    if (!Number.isInteger(selectedTableId) || selectedTableId <= 0) {
      triggerErrorAnimation("Sélectionnez une table avant de diviser l’addition.");
      return;
    }

    setProcessingAction(true);
    let authoritativeItems = null;
    let activeSplit = null;
    try {
      const [splitResponse, loadedItems] = await Promise.all([
        fetch(
          `${API_BASE}/split-bills/active?tableId=${selectedTableId}&_=${Date.now()}`,
          { cache: "no-store" },
        ),
        loadTableItemsForPayment(selectedTableId, false),
      ]);
      const splitPayload = await splitResponse.json().catch(() => ({}));
      if (!splitResponse.ok) {
        throw new Error(splitPayload.error || "Scission indisponible.");
      }
      authoritativeItems = Array.isArray(loadedItems) ? loadedItems : [];
      activeSplit = splitPayload.split || null;
    } catch (error) {
      console.warn("Actualisation de la scission différée:", error?.message || error);
      activeSplit =
        candidateSplit?.status === "active" &&
        Number(candidateSplit?.table_id) === selectedTableId
          ? candidateSplit
          : null;
      if (!activeSplit) {
        triggerErrorAnimation(
          "Impossible d’actualiser la table. Réessayez dans un instant.",
        );
        setProcessingAction(false);
        return;
      }
    }
    setProcessingAction(false);

    if (!activeSplit?.id) {
      if (tempAdjustments.some((entry) => entry?.type === "discount" && entry?.scope === "cart")) {
        triggerErrorAnimation("Retirez la réduction sur toute l’addition avant de diviser.");
        setShowInvoiceGenerationModal(true);
        return;
      }
      if (candidateSplit?.id) resetSplitUiState();
      initializeSplit(authoritativeItems);
      return;
    }

    const hydratedInvoices = hydrateSplitInvoices(activeSplit);
    const outstandingTotal = hydratedInvoices
      .filter((invoice) => invoice.status !== "paid")
      .reduce((sum, invoice) => sum + Number(invoice.pricing?.amount_due ?? invoice.subtotal ?? 0), 0);
    setPersistedSplitBill(activeSplit);
    commitSplitEditorState({ invoices: hydratedInvoices, unassignedItems: [] });
    setCurrentOrderForPayment((previous) => ({
      ...(previous || {}),
      table: selectedTable,
      table_id: Number(getApiTableId(selectedTable)),
      table_number: selectedTable?.number,
      table_label: `Table ${getTableDisplayName(selectedTable)}`,
      isSplit: true,
      splitBill: activeSplit,
      splitInvoices: hydratedInvoices,
      items: hydratedInvoices.flatMap((invoice) => invoice.items || []),
      total_amount: outstandingTotal,
    }));
    setSplitEditorPartId(null);
    setSelectedSplitInvoiceId(null);
    setIsSplitMode(false);
    setShowSplitModal(true);
  };

  // Créer une clé unique pour chaque produit incluant ses extras
  const getItemKey = (item) => getSplitItemKey(item);

  const moveNatureInSplitInvoice = (invoiceId, natureKey, direction) => {
    const nextState = moveSplitEditorNatureItem(splitEditorStateRef.current, {
      invoiceId,
      natureKey,
      quantity: 1,
      direction,
    });
    if (!nextState.changed) return;
    commitSplitEditorState(nextState);
  };

  /*
   * Les mouvements +/- passent par splitEditorStateRef : chaque clic voit
   * immédiatement le résultat du clic précédent, même avant le prochain
   * rendu React. Cela évite les pertes et doublons pendant une rafale.
   */
  const assertSplitEditorCapacity = () => {
    const state = splitEditorStateRef.current;
    const quantities = new Map();
    [...(state.unassignedItems || []), ...(state.invoices || []).flatMap(
      (invoice) => invoice.items || [],
    )].forEach((entry) => {
      const key = getItemKey(entry);
      quantities.set(key, (quantities.get(key) || 0) + Number(entry.quantity || 0));
    });
    return quantities;
  };

  // Gardé ici comme invariant de diagnostic pour les journaux de caisse.
  // La validation serveur reste l'autorité finale sur les quantités.
  const logSplitEditorCapacity = () => {
    const quantities = assertSplitEditorCapacity();
    cashDebugLog("Quantités du brouillon de scission", {
      lines: Array.from(quantities.entries()),
    });
  };

  // Valider la scission
  const validateSplit = () => {
    logSplitEditorCapacity();
    const editorInvoices = splitEditorStateRef.current.invoices || [];
    cashDebugLog("�? Validation de la scission:", {
      unassignedItemsCount: splitEditorStateRef.current.unassignedItems?.length || 0,
      splitInvoicesCount: editorInvoices.length,
      invoicesWithItems: editorInvoices.filter((inv) => inv.items.length > 0)
        .length,
    });

    // Vérifier que chaque facture a au moins un article
    const emptyInvoices = editorInvoices.filter((inv) => inv.items.length === 0);
    if (emptyInvoices.length > 0) {
      console.warn(
        "⚠�? Factures vides:",
        emptyInvoices.map((inv) => inv.name),
      );
      triggerErrorAnimation(`La facture ${emptyInvoices[0].name} est vide !`);
      return false;
    }

    cashDebugLog("✅ Validation réussie");
    return true;
  };

  // Finaliser la scission et retourner au modal de génération de facture
  const finalizeSplit = async () => {
    if (splitActionInFlightRef.current) return;
    if (!(currentOrderForPayment?.splitBill || persistedSplitBill)?.id &&
        tempAdjustments.some((entry) => entry?.type === "discount" && entry?.scope === "cart")) {
      triggerErrorAnimation("Retirez la réduction sur toute l’addition avant de diviser.");
      return;
    }
    if (!validateSplit()) {
      return;
    }
    const editorInvoices = splitEditorStateRef.current.invoices || [];

    const tableId = Number(getApiTableId(selectedTable));
    if (!Number.isInteger(tableId) || tableId <= 0) {
      triggerErrorAnimation("La scission nécessite une table enregistrée.");
      return;
    }
    const persistentItemId = (item) => Number(
      item?.order_item_id ?? item?.orderItemId ?? item?.order_itemId ??
      (item?.order_id || item?.orderId ? item?.id : null),
    );
    const invalidItem = editorInvoices.flatMap((invoice) => invoice.items || [])
      .find((item) => !Number.isInteger(persistentItemId(item)) || persistentItemId(item) <= 0);
    if (invalidItem) {
      triggerErrorAnimation(
        "Envoyez d’abord les nouveaux articles en Cuisine/Bar, puis relancez la scission.",
      );
      return;
    }
    splitActionInFlightRef.current = true;
    setIsSplitActionPending(true);
    let storedSplit;
    try {
      const activeSplit = currentOrderForPayment?.splitBill || persistedSplitBill;
      const response = await fetch(splitEditorPartId
        ? `${API_BASE}/split-bills/${activeSplit.id}/parts/${splitEditorPartId}/lines`
        : activeSplit?.id
          ? `${API_BASE}/split-bills/${activeSplit.id}/parts`
          : `${API_BASE}/split-bills`, {
        method: splitEditorPartId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(activeSplit?.id
            ? { expectedVersion: activeSplit.version }
            : { tableId }),
          parts: editorInvoices.map((invoice) => ({
            name: invoice.name,
            lines: (invoice.items || []).map((item) => ({
              orderItemId: persistentItemId(item),
              quantity: Number(item.quantity) || 1,
            })),
          })),
          ...(activeSplit?.id ? {
            name: editorInvoices[0]?.name,
            lines: (editorInvoices[0]?.items || []).map((item) => ({
              orderItemId: persistentItemId(item),
              quantity: Number(item.quantity) || 1,
            })),
          } : {}),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Impossible d’enregistrer la scission.");
      storedSplit = payload.split;
      setPersistedSplitBill(storedSplit);
    } catch (error) {
      triggerErrorAnimation(error.message || "Impossible d’enregistrer la scission.");
      splitActionInFlightRef.current = false;
      setIsSplitActionPending(false);
      return;
    }

    const hydratedInvoices = hydrateSplitInvoices(storedSplit);
    const totalSplitAmount = hydratedInvoices
      .filter((invoice) => invoice.status !== "paid")
      .reduce((sum, invoice) => sum + Number(invoice.pricing?.amount_due ?? invoice.subtotal ?? 0), 0);

    // Créer un objet de commande avec les factures scindées
    const finalAmount = totalSplitAmount;

    const splitOrderData = {
      table: selectedTable,
      table_number: selectedTable?.number,
      table_label: selectedTable?.isDelivery
        ? "Livraison"
        : `Table ${getTableDisplayName(selectedTable)}`,
      is_delivery: !!selectedTable?.isDelivery,
      delivery_customer: selectedTable?.isDelivery
        ? selectedTable?.deliveryCustomer || deliveryCustomer || null
        : null,
      delivery_mode: selectedTable?.isDelivery
        ? selectedTable?.deliveryMode || deliveryMode || null
        : null,
      delivery_method: selectedTable?.isDelivery
        ? selectedTable?.deliveryMethod || deliveryMethod || null
        : null,
      delivery_reference: selectedTable?.isDelivery
        ? selectedTable?.deliveryRef || deliveryRef || null
        : null,
      delivery_address: selectedTable?.isDelivery
        ? selectedTable?.deliveryCustomer?.address ||
          selectedTable?.deliveryCustomer?.address_line ||
          selectedTable?.deliveryCustomer?.location ||
          null
        : null,
      splitInvoices: hydratedInvoices,
      splitBill: storedSplit,
      totalAmount: totalSplitAmount,
      total_amount: finalAmount, // Propriété attendue par PaymentInterface
      adjustments: [],
      finalTotal: finalAmount,
      isSplit: true,
      items: hydratedInvoices.flatMap((invoice) => invoice.items),
    };

    setSplitInvoices(hydratedInvoices);
    setCurrentOrderForPayment(splitOrderData);
    setShowSplitModal(true);
    // Après création, afficher directement le suivi simple des factures.
    setIsSplitMode(false);

    triggerSuccessAnimation(
      storedSplit.discount_notices?.length
        ? storedSplit.discount_notices.join(" ")
        : `${editorInvoices[0]?.name || "Facture"} ${splitEditorPartId ? "mise à jour" : "créée"}.`,
    );
    splitActionInFlightRef.current = false;
    setIsSplitActionPending(false);
  };

  // Annuler la scission
  const cancelSplit = () => {
    setShowSplitModal(false);
    setIsSplitMode(false);
    setSplitInvoices([]);
    setUnassignedItems([]);
    setSelectedSplitInvoiceId(null);
    setSplitEditorPartId(null);
  };

  const closeSplitEditor = () => {
    const activeSplit = currentOrderForPayment?.splitBill || persistedSplitBill;
    if (!activeSplit?.id) {
      cancelSplit();
      return;
    }
    setSplitInvoices(hydrateSplitInvoices(activeSplit));
    setUnassignedItems([]);
    setSelectedSplitInvoiceId(null);
    setSplitEditorPartId(null);
    setIsSplitMode(false);
    setShowSplitModal(true);
  };

  // Réinitialiser après validation de scission
  const resetAfterSplit = () => {
    setSplitInvoices([]);
    setUnassignedItems([]);
    setIsSplitMode(false);
    setSelectedSplitInvoiceId(null);
    setSplitEditorPartId(null);
    setPersistedSplitBill(null);
  };

  // Supprimer la scission et revenir à une facture unique
  const deleteExistingSplit = async () => {
    const split = currentOrderForPayment?.splitBill || persistedSplitBill;
    if (split?.id) {
      try {
        const response = await fetch(`${API_BASE}/split-bills/${split.id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ expectedVersion: split.version }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "Impossible de supprimer la scission.");
      } catch (error) {
        triggerErrorAnimation(error.message || "Impossible de supprimer la scission.");
        return;
      }
    }
    setSplitInvoices([]);
    setUnassignedItems([]);
    setIsSplitMode(false);
    setSelectedSplitInvoiceId(null);
    setPersistedSplitBill(null);
    setShowSplitModal(false);
    setCurrentOrderForPayment((prev) =>
      prev
        ? {
            ...prev,
            isSplit: false,
            splitInvoices: [],
            splitInvoicesCount: 0,
          }
        : prev,
    );
  };

  // Calculer le total assigné (factures scindées)
  const getAssignedTotal = () => {
    const total = splitInvoices.reduce((sum, inv) => sum + inv.subtotal, 0);
    cashDebugLog("💰 Total assigné:", {
      total,
      invoicesCount: splitInvoices.length,
      invoices: splitInvoices.map((inv) => ({
        id: inv.id,
        name: inv.name,
        subtotal: inv.subtotal,
        itemsCount: inv.items.length,
        items: inv.items.map((item) => `${item.name}(${item.quantity})`),
      })),
    });
    return total;
  };

  // Calculer le total non assigné (articles restants)
  const getUnassignedTotal = () => {
    cashDebugLog("�? Calcul total non assigné - Articles disponibles:", {
      count: unassignedItems.length,
      items: unassignedItems.map((item) => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        totalPrice: item.totalPrice || item.total_price,
        hasExtras: !!(item.extras && item.extras.length > 0),
      })),
    });

    const total = unassignedItems.reduce((sum, item) => {
      const extrasPrice = item.extras
        ? item.extras.reduce(
            (extSum, ext) => extSum + (ext.price || 0) * (ext.quantity || 1),
            0,
          )
        : 0;
      const itemTotal = (item.price + extrasPrice) * item.quantity;
      cashDebugLog(
        `📋 Article non assigné: ${item.name} = ${item.quantity}x(${item.price}+${extrasPrice}F) = ${itemTotal}F`,
      );
      return sum + itemTotal;
    }, 0);
    cashDebugLog("💰 Total non assigné final:", {
      total,
      itemsCount: unassignedItems.length,
    });
    return total;
  };

  // Calculer le total de scission (simple somme)
  const getTotalSplitAmount = () => {
    const assigned = getAssignedTotal();
    const unassigned = getUnassignedTotal();
    const total = assigned + unassigned;
    cashDebugLog("💰 Total scission:", { assigned, unassigned, total });
    return total;
  };

  // Vérifier si la scission est prête pour validation
  const isSplitReady = () => {
    const hasUnassigned = unassignedItems.length > 0;
    const hasAssigned = splitInvoices.some((inv) => inv.items.length > 0);
    const ready = !hasUnassigned && hasAssigned;
    cashDebugLog("�? Statut scission:", { hasUnassigned, hasAssigned, ready });
    return ready;
  };

  // Calculer le nombre total d'articles non assignés
  const getTotalUnassignedItems = () => {
    const total = unassignedItems.reduce((sum, item) => sum + item.quantity, 0);
    cashDebugLog("📊 Articles non assignés:", {
      types: unassignedItems.length,
      units: total,
      items: unassignedItems.map((item) => `${item.name}(${item.quantity})`),
    });
    return total;
  };

  // === FIN FONCTIONS SCISSION DE FACTURE ===

  // === FIN FONCTIONS AJUSTEMENTS ===

  // === FONCTIONS MOUVEMENTS DE CAISSE ===

  // Charger les données utilisateur et session
  const loadUserAndSession = async () => {
    try {
      isResolvingSessionRef.current = true;
      setLoading(true);
      setIsCurrentSessionUnlocked(false);

      // ProtectedRoute a déjà validé cette identité via /api/auth/me.
      // Aucun autre espace localStorage ni utilisateur de secours ne peut la remplacer.
      const user = authenticatedUser?.id ? authenticatedUser : currentUser;
      if (!user?.id) {
        throw new Error("Identité de caisse non vérifiée");
      }

      setCurrentUser(user);

      const roleLowerRuntime = normalizeRoleKey(user?.role || "");
      const serverParticipationRuntime = Boolean(serverParticipationEnabled);
      const isRestrictedServerRuntime =
        (roleLowerRuntime === "server" || roleLowerRuntime === "serveur") &&
        serverParticipationRuntime;

      if (isRestrictedServerRuntime) {
        setCurrentSession(null);
        setShowAdminCashDrawer(false);
        setIsCurrentSessionUnlocked(false);
        cashDebugLog(
          "✅ Mode serveur actif: pas de session/fond de caisse, accès commande uniquement",
        );
        return;
      }

      // Vérifier s'il s'agit d'une session verrouillée à restaurer
      migrateLegacyLockedSession();
      const lockedSession = getLockedSession(user?.id);
      const isUnlocking = lockedSession?.lockReason === "manual";

      // Récupérer ou créer une session active SEULEMENT si c'est un déverrouillage
      if (isUnlocking) {
        cashDebugLog(
          "🔓 Déverrouillage détecté - récupération session existante",
        );
        setIsCurrentSessionUnlocked(true);
        try {
          const sessionResponse = await fetchWithTimeout(
            `${API_BASE}/cash-sessions/active`,
          );
          if (sessionResponse.ok) {
            const session = await sessionResponse.json();
            setCurrentSession(session);
            cashDebugLog(
              "✅ Session active trouvée pour déverrouillage:",
              session.id,
            );
          }

          // Charger les tables seulement lors du déverrouillage
          await loadTables();
          cashDebugLog("✅ Tables chargées pour déverrouillage");
        } catch (sessionError) {
          console.error(
            "�?� Erreur gestion session lors déverrouillage:",
            sessionError,
          );
        }
      } else {
        setIsCurrentSessionUnlocked(false);
        // Nouvelle connexion - vérifier s'il y a une session active ou en créer une
        cashDebugLog("🆕 Nouvelle connexion - vérification session de caisse");

        try {
          const roleLower = normalizeRoleKey(user?.role || "");
          const isAdminRole = roleLower === "admin";
          const isCashierRole =
            roleLower === "cashier" ||
            roleLower === "caissier" ||
            roleLower === "caissiere" ||
            roleLower === "caisse";
          const isAdminLike = isAdminRole || roleLower === "superadmin";
          const shouldPromptCashDrawer =
            !isUnlocking &&
            (isCashierRole || isAdminRole);

          // Vérifier s'il y a déjà une session active
          const sessionResponse = await fetchWithTimeout(
            `${API_BASE}/cash-sessions/active`,
          );

          if (sessionResponse.ok) {
            // Session active trouvée
            const session = await sessionResponse.json();
            setCurrentSession(session);
            cashDebugLog(
              "✅ Session active trouvée pour nouvelle connexion:",
              session.id,
            );
            // Une session active signifie que le fond de caisse a déjà été traité
            // pour cette présence caisse. Ne pas redemander le modal quand l'admin
            // revient du tableau de bord sans s'être déconnecté.
            if (isAdminRole) {
              setShowAdminCashDrawer(false);
            }
          }

          if (!sessionResponse.ok && shouldPromptCashDrawer) {
            await fetchAdminLastAmount(user.id);
            setShowAdminCashDrawer(true);
          }

          if (!sessionResponse.ok && !shouldPromptCashDrawer) {
            // Pas de session active, en créer une nouvelle
            cashDebugLog("📊 Création d'une nouvelle session de caisse...");

            // IMPORTANT : une nouvelle session démarre avec un solde initial à 0.
            // Le "dernier montant de clôture" est une information utile (suggestion),
            // mais ne doit pas être appliqué automatiquement comme fond de caisse.
            const initialAmount = 0;

            // Créer une nouvelle session
            const createResponse = await fetchWithTimeout(
              `${API_BASE}/cash-sessions`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  cashierId: user.id,
                  terminalId: getPersistentDeviceId(),
                  initialAmount: initialAmount,
                  notes: isAdminLike
                    ? "Session admin (solde initial 0) - nouvelle connexion"
                    : "Session automatique (solde initial 0) - nouvelle connexion",
                }),
              },
            );

            if (createResponse.ok) {
              const newSession = await createResponse.json();
              setCurrentSession(newSession);
              cashDebugLog("✅ Nouvelle session créée:", newSession.id);
            } else {
              console.error("�?� Erreur création session de caisse");
              setCurrentSession(null);
            }
          } else if (!sessionResponse.ok && shouldPromptCashDrawer) {
            cashDebugLog(
              "🟡 Session en attente de validation du fond de caisse",
            );
          }

          // NE PAS charger les tables pour une nouvelle connexion - elles sont déjà initialisées vides dans loadBasicData
          cashDebugLog("✅ État vierge maintenu pour nouvelle connexion");
        } catch (error) {
          console.error("�?� Erreur gestion session nouvelle connexion:", error);
          setCurrentSession(null);
        }
      }
    } catch (error) {
      console.error("�?� Erreur chargement utilisateur/session:", error);
      handleApiFailure("loadUserAndSession", error);
    } finally {
      isResolvingSessionRef.current = false;
      setUserSessionHydrated(true);
      setLoading(false);
    }
  };

  // Restaurer la session si besoin, sinon nettoyer l'état pour une nouvelle connexion
  useEffect(() => {
    if (!currentUser) return;
    const didRestore = restoreLockedSessionState();
    if (!didRestore) {
      ensureCleanStateForNewConnection();
    }
  }, [currentUser]);

  useEffect(() => {
    const sessionId = currentSession?.id || null;
    if (!sessionId) {
      sessionSalesByMethodRef.current = new Map();
      localSalesTotalRef.current = 0;
      hasLocalSalesRef.current = false;
      lastPaymentAtRef.current = 0;
      lastSessionIdRef.current = null;
      return;
    }

    if (lastSessionIdRef.current && lastSessionIdRef.current !== sessionId) {
      sessionSalesByMethodRef.current = new Map();
      localSalesTotalRef.current = 0;
      hasLocalSalesRef.current = false;
      lastPaymentAtRef.current = 0;
    } else if (!lastSessionIdRef.current) {
      sessionSalesByMethodRef.current = new Map();
      localSalesTotalRef.current = 0;
      hasLocalSalesRef.current = false;
      lastPaymentAtRef.current = 0;
    }

    lastSessionIdRef.current = sessionId;
  }, [currentSession?.id]);

  useEffect(() => {
    if (!currentUser) return;
    if (apiStatus === "connected") return;
    if (typeof loadBasicDataRef.current === "function") {
      loadBasicDataRef.current({ reason: "user-change" });
    }
  }, [currentUser?.id]);

  // Fonction pour ouvrir le modal des mouvements de caisse
  const openCashMovementModal = () => {
    if (isServerParticipationMode) return;
    if (!currentUser || !currentSession) {
      triggerErrorAnimation("Session de caisse non initialisée !");
      return;
    }
    setShowCashMovementModal(true);
  };

  const handleAdminCashDrawerConfirm = async (initialAmount, notes) => {
    if (!currentUser) return;
    try {
      const roleLower = normalizeRoleKey(currentUser?.role || "");
      const isAdminLike = roleLower === "admin" || roleLower === "superadmin";
      const normalizedInitialAmount = Number(initialAmount || 0);
      let session = currentSession;
      if (!session) {
        session = await fetchActiveCashSession(currentUser.id);
      }

      if (session?.id) {
        const reportResponse = await fetchWithTimeout(
          `${API_BASE}/cash-sessions/${session.id}/x-report`,
          {},
          12000,
        ).catch(() => null);
        const reportData = reportResponse?.ok
          ? await reportResponse.json().catch(() => null)
          : null;
        const sessionAlreadyUsed =
          Number(reportData?.sales_summary?.net_sales || 0) > 0 ||
          Number(reportData?.statistics?.ticket_count || 0) > 0 ||
          Number(reportData?.adjustments?.cash_in || 0) > 0 ||
          Number(reportData?.adjustments?.cash_out || 0) > 0;

        if (sessionAlreadyUsed) {
          const summaryResponse = await fetchWithTimeout(
            `${API_BASE}/cash-journal/summary/${session.id}?cashierId=${currentUser.id}`,
            {},
            12000,
          ).catch(() => null);
          const summaryData = summaryResponse?.ok
            ? await summaryResponse.json().catch(() => null)
            : null;
          const finalAmount = Math.max(
            0,
            Math.round(
              Number(summaryData?.summary?.theoretical_balance) ||
                Number(session.initial_amount || 0),
            ),
          );
          const closeResponse = await fetchWithTimeout(
            `${API_BASE}/cash-sessions/${session.id}/close`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                finalAmount,
                notes: "Session fermee automatiquement avant nouvelle ouverture admin",
              }),
            },
            12000,
          );
          if (!closeResponse.ok) {
            throw new Error("Erreur fermeture ancienne session de caisse");
          }
          session = null;
          setCurrentSession(null);
          localStorage.removeItem("moo_cash_session");
        }
      }

      if (!session) {
        const createResponse = await fetchWithTimeout(
          `${API_BASE}/cash-sessions`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              cashierId: currentUser.id,
              terminalId: getPersistentDeviceId(),
              initialAmount,
              notes:
                notes ||
                (isAdminLike
                  ? "Session admin - fond de caisse validé"
                  : "Session caisse - fond de caisse validé"),
            }),
          },
        );

        if (createResponse.ok) {
          session = await createResponse.json();
        } else {
          const createError = await createResponse
            .json()
            .catch(() => ({ error: "Erreur création session de caisse" }));

          // Une réponse peut être perdue alors que l'insertion a bien été
          // validée. Toujours relire la session autoritaire avant d'afficher
          // un échec ou de permettre un second clic.
          session = await fetchActiveCashSession(currentUser.id);
          if (!session?.id) {
            const error = new Error(
              createError?.error ||
                createError?.message ||
                `Erreur création session (${createResponse.status})`,
            );
            error.status = createResponse.status;
            error.code = createError?.code;
            throw error;
          }
        }
      }

      if (session?.id) {
        const sessionInitialAmount = Number(session.initial_amount || 0);
        const hasDifference =
          Number.isFinite(normalizedInitialAmount) &&
          Math.abs(sessionInitialAmount - normalizedInitialAmount) > 0.001;

        if (hasDifference) {
          const updateInitialResponse = await fetchWithTimeout(
            `${API_BASE}/cash-sessions/${session.id}/initial-amount`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ initialAmount: normalizedInitialAmount }),
            },
          );

          if (updateInitialResponse.ok) {
            session = await updateInitialResponse.json();
          } else {
            const payload = await updateInitialResponse
              .json()
              .catch(() => ({ error: "Erreur mise à jour fond initial" }));
            const message = String(
              payload?.error || "Erreur mise à jour fond initial",
            );
            const isLockedBySales =
              message.toLowerCase().includes("impossible de modifier le fond initial") ||
              message.toLowerCase().includes("session déjà utilisée");
            if (!isLockedBySales) {
              throw new Error(message);
            }
            cashDebugLog(
              "ℹ�? Session déjà utilisée: fond initial non modifiable, session conservée",
            );
          }
        }
      }

      if (session) {
        setCurrentSession(session);
        localStorage.setItem("moo_cash_session", JSON.stringify(session));
      }

      setShowAdminCashDrawer(false);
    } catch (error) {
      console.error("�?� Erreur validation fond de caisse admin:", error);
      throw error;
    }
  };

  const handleAdminCashDrawerError = (error) => {
    console.error("Erreur modal fond de caisse admin:", error);
  };

  // Callback pour gérer la création d'un mouvement
  const handleMovementCreated = (movement) => {
    cashDebugLog("✅ Mouvement créé:", movement);
    triggerSuccessAnimation(`Mouvement de ${movement.amount} F enregistré !`);
    // Le modal se fermera automatiquement après création
  };

  // Fonction pour rafraîchir le journal de caisse (nouveau)
  const refreshCashJournal = () => {
    if (showCashJournal) {
      // Le composant CashJournalModal se rechargera automatiquement quand ces props changent
      setShowCashJournal(false);
      setTimeout(() => setShowCashJournal(true), 100);
    } else {
      setShowCashJournal(true);
    }
  };

  const openCashJournalWithRefresh = async () => {
    if (isServerParticipationMode) return;
    if (!currentUser?.id) {
      triggerErrorAnimation("Session de caisse non active");
      return;
    }
    let session = currentSession;
    if (!session?.id) {
      session = await fetchActiveCashSession(currentUser.id);
    }
    if (!session?.id) {
      triggerErrorAnimation("Session de caisse non active");
      return;
    }
    if (!currentSession || currentSession.id !== session.id) {
      setCurrentSession(session);
    }
    setShowCashJournal(true);
    if (typeof window !== "undefined") {
      setTimeout(() => {
        window.dispatchEvent(new Event("moo-cash-journal-refresh"));
      }, 400);
    }
  };

  // === FIN FONCTIONS MOUVEMENTS DE CAISSE ===

  // Charger les données utilisateur au démarrage
  // Gestionnaire de clic pour fermer le panneau uniquement depuis la zone menu produits
  useEffect(() => {
    const handleClickOutside = (event) => {
      const panelNode = panelRef.current;
      const menuNode = menuAreaRef.current;
      if (!panelNode || !menuNode) return;

      const clickedMenuArea = menuNode.contains(event.target);
      const clickedPanel = panelNode.contains(event.target);
      const isToggleButton = event.target.closest("[data-panel-toggle]");

      if (
        clickedMenuArea &&
        !clickedPanel &&
        !isToggleButton &&
        Object.values(visiblePanels).some(Boolean)
      ) {
        setVisiblePanels({
          tables: false,
          online: false,
          calculator: false,
          delivery: false,
          reservations: false,
          customers: false,
          profile: false,
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visiblePanels]);

  useEffect(() => {
    if (!packageConfigHydrated) return;
    loadUserAndSession();
  }, [packageConfigHydrated, serverParticipationEnabled]);

  useEffect(() => {
    if (bootHydrated) return;
    if (userSessionHydrated && initialDataHydrated) {
      setBootHydrated(true);
    }
  }, [bootHydrated, initialDataHydrated, userSessionHydrated]);

  useEffect(() => {
    if (!bootHydrated) {
      setShellMotionReady(false);
      return undefined;
    }

    let frameA = null;
    let frameB = null;
    let timer = null;

    frameA = window.requestAnimationFrame(() => {
      frameB = window.requestAnimationFrame(() => {
        timer = window.setTimeout(() => {
          setShellMotionReady(true);
        }, 180);
      });
    });

    return () => {
      if (frameA) window.cancelAnimationFrame(frameA);
      if (frameB) window.cancelAnimationFrame(frameB);
      if (timer) window.clearTimeout(timer);
    };
  }, [bootHydrated]);

  useEffect(() => {
    const handleAuthRefresh = () => {
      if (typeof loadBasicDataRef.current === "function") {
        loadBasicDataRef.current({ reason: "auth-refresh" });
      }
    };
    window.addEventListener("moo-auth-refresh", handleAuthRefresh);
    return () => {
      window.removeEventListener("moo-auth-refresh", handleAuthRefresh);
    };
  }, []);

  useEffect(() => {
    if (!currentUser || !isAdminUser || isCurrentSessionUnlocked) return;
    if (isResolvingSessionRef.current) return;
    if (currentSession?.id) {
      if (adminCashDrawerRequested) {
        setAdminCashDrawerRequested(false);
      }
      return;
    }
    if (showAdminCashDrawer) return;
    if (!adminCashDrawerRequested) {
      setAdminCashDrawerRequested(true);
      return;
    }
    if (adminCashDrawerRequested) {
      let cancelled = false;
      const verifySessionBeforePrompt = async () => {
        const session = await fetchActiveCashSession(currentUser.id);
        if (cancelled) return;
        if (session?.id) {
          setCurrentSession(session);
          localStorage.setItem("moo_cash_session", JSON.stringify(session));
          setShowAdminCashDrawer(false);
          setAdminCashDrawerRequested(false);
          return;
        }
        await fetchAdminLastAmount(currentUser.id);
        if (cancelled) return;
        setShowAdminCashDrawer(true);
        setAdminCashDrawerRequested(false);
      };
      verifySessionBeforePrompt();
      return () => {
        cancelled = true;
      };
    }
  }, [
    adminCashDrawerRequested,
    currentUser,
    isAdminUser,
    isCurrentSessionUnlocked,
    currentSession?.id,
    showAdminCashDrawer,
  ]);

  // Fonction pour s'assurer qu'une nouvelle connexion démarre proprement
  const ensureCleanStateForNewConnection = () => {
    migrateLegacyLockedSession();
    if (!currentUser?.id) return;

    cashDebugLog("🆕 Nouvelle connexion détectée - initialisation état vierge");

    // Reset tous les états à leur valeur initiale
    setSelectedTable(null);
    setCurrentOrder({ items: [], total: 0 });
    setCurrentDeliveryOrder({ items: [], total: 0 });
    setTableOrders([]);
    setShowTableHistory(false);
    setSelectedCustomerForOrder(null);
    setHasExistingOrders(false);
    setIsAddingToExistingOrder(false);
    setExistingItemsForPayment([]);
    setExistingDeliveryItems([]);
    setSelectedDeliveryOrder(null);
    setDeliveryCustomer(null);
    setDeliveryMode("");
    setDeliveryRef("");
    setDeliveryMethod("Direct");
    setActiveTab(isBoutique ? (isServerParticipationMode ? "profile" : "customers") : "tables");
    setActiveClientTab(null);
    setVisiblePanels({
      tables: !isBoutique ? false : false,
      online: false,
      calculator: false,
      delivery: false,
      reservations: false,
      customers: isBoutique && !isServerParticipationMode,
      profile: false,
    });

    try {
      forcedFreeTablesRef.current.clear();
      clearAllRecentlyPaidGuards();
      destructiveCartEvidenceRef.current.clear();
      lastExistingItemsRef.current.clear();
      commitTableStatesCache(new Map());
    } catch (_) {}

    cashDebugLog("✅ État vierge initialisé pour nouvelle connexion");
  };

  // Fonctions de la calculatrice
  const handleCalculatorNumber = (num) => {
    if (calculatorWaitingForOperand) {
      setCalculatorDisplay(String(num));
      setCalculatorWaitingForOperand(false);
    } else {
      setCalculatorDisplay(
        calculatorDisplay === "0" ? String(num) : calculatorDisplay + num,
      );
    }
  };

  const handleCalculatorOperation = (nextOperation) => {
    const inputValue = parseFloat(calculatorDisplay.replace(",", "."));

    if (calculatorPrevValue === null) {
      setCalculatorPrevValue(inputValue);
    } else if (calculatorOperation) {
      const currentValue = calculatorPrevValue || 0;
      const newValue = calculate(currentValue, inputValue, calculatorOperation);

      setCalculatorDisplay(String(newValue).replace(".", ","));
      setCalculatorPrevValue(newValue);
    }

    setCalculatorWaitingForOperand(true);
    setCalculatorOperation(nextOperation);
  };

  const calculate = (firstValue, secondValue, operation) => {
    switch (operation) {
      case "+":
        return firstValue + secondValue;
      case "-":
        return firstValue - secondValue;
      case "*":
        return firstValue * secondValue;
      case "/":
        return secondValue !== 0 ? firstValue / secondValue : 0;
      default:
        return secondValue;
    }
  };

  const handleCalculatorEquals = () => {
    const inputValue = parseFloat(calculatorDisplay.replace(",", "."));

    if (calculatorPrevValue !== null && calculatorOperation) {
      const newValue = calculate(
        calculatorPrevValue,
        inputValue,
        calculatorOperation,
      );
      setCalculatorDisplay(String(newValue).replace(".", ","));
      setCalculatorPrevValue(null);
      setCalculatorOperation(null);
      setCalculatorWaitingForOperand(true);
    }
  };

  const handleCalculatorClear = () => {
    setCalculatorDisplay("0");
    setCalculatorPrevValue(null);
    setCalculatorOperation(null);
    setCalculatorWaitingForOperand(false);
  };

  const handleCalculatorClearEntry = () => {
    setCalculatorDisplay("0");
    setCalculatorWaitingForOperand(false);
  };

  const handleCalculatorDecimal = () => {
    if (calculatorWaitingForOperand) {
      setCalculatorDisplay("0,");
      setCalculatorWaitingForOperand(false);
    } else if (calculatorDisplay.indexOf(",") === -1) {
      setCalculatorDisplay(calculatorDisplay + ",");
    }
  };

  const handleCalculatorPercentage = () => {
    const inputValue = parseFloat(calculatorDisplay.replace(",", "."));
    const newValue = inputValue / 100;
    setCalculatorDisplay(String(newValue).replace(".", ","));
  };

  const handleCalculatorQuickFunction = (func) => {
    const inputValue = parseFloat(calculatorDisplay.replace(",", "."));
    let newValue;

    switch (func) {
      case "sqrt":
        newValue = Math.sqrt(inputValue);
        break;
      case "square":
        newValue = inputValue * inputValue;
        break;
      default:
        return;
    }

    setCalculatorDisplay(String(newValue).replace(".", ","));
    setCalculatorWaitingForOperand(true);
  };

  // Fonction pour verrouiller la session
  const handleLockSession = async () => {
    // Libérer la session d'auth côté serveur (permet de se reconnecter ailleurs)
    const token = localStorage.getItem("moo_token");

    // Récupérer les données de session de caisse si elles existent
    const cashSessionData = localStorage.getItem("moo_cash_session");

    // Sauvegarder l'état de la session en cours
    const sessionState = {
      currentUser,
      currentSession,
      selectedTable,
      currentOrder,
      existingItemsForPayment,
      existingDeliveryItems,
      currentDeliveryOrder,
      selectedDeliveryOrder,
      hasExistingOrders,
      tableOrders,
      activeTab,
      visiblePanels,
      deliveryMode,
      deliveryMethod,
      deliveryRef,
      deliveryCustomer,
      tableStatesCache: (() => {
        try {
          return Array.from(tableStatesCache.entries());
        } catch (_) {
          return null;
        }
      })(),
      cashSessionData, // Inclure les données de session de caisse
      timestamp: new Date().toISOString(),
      lockReason: "manual",
    };

    // Stocker l'état dans localStorage pour le récupérer après déverrouillage
    if (currentUser?.id) {
      setLockedSession(currentUser.id, sessionState);
    }

    // Supprimer les données d'authentification pour déclencher l'affichage du login
    localStorage.removeItem("moo_user");
    localStorage.removeItem("moo_token");

    // Notifier ProtectedRoute (sans rechargement complet)
    try {
      window.dispatchEvent(new Event("moo-auth-refresh"));
    } catch (_e) {
      // ignore
    }

    // Appel logout en arrière-plan (ne bloque pas le verrouillage)
    if (token) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1500);
        fetch("/api/auth/logout", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
          signal: controller.signal,
        })
          .catch(() => {})
          .finally(() => clearTimeout(timeoutId));
      } catch (_e) {
        // ignore
      }
    }
  };

  // Verrouillage local (utilisé quand la session est prise sur un autre appareil)
  // Ne déclenche PAS /api/auth/logout pour ne pas impacter l'appareil qui a pris la main.
  const lockSessionLocally = () => {
    try {
      const cashSessionData = localStorage.getItem("moo_cash_session");
      const sessionState = {
        currentUser,
        currentSession,
        selectedTable,
        currentOrder,
        existingItemsForPayment,
        existingDeliveryItems,
        currentDeliveryOrder,
        selectedDeliveryOrder,
        hasExistingOrders,
        tableOrders,
        activeTab,
        visiblePanels,
        deliveryMode,
        deliveryMethod,
        deliveryRef,
        deliveryCustomer,
        tableStatesCache: (() => {
          try {
            return Array.from(tableStatesCache.entries());
          } catch (_) {
            return null;
          }
        })(),
        cashSessionData,
        timestamp: new Date().toISOString(),
        lockReason: "takeover",
      };

      if (currentUser?.id) {
        setLockedSession(currentUser.id, sessionState);
      }
      localStorage.removeItem("moo_user");
      localStorage.removeItem("moo_token");
	    } catch (_e) {
	      // ignore
	    } finally {
	      // Notifier ProtectedRoute (sans rechargement complet)
	      try {
	        window.dispatchEvent(new Event("moo-auth-refresh"));
	      } catch (_e2) {
	        // ignore
	      }
	    }
	  };

  // Si le compte est repris sur un autre appareil, on verrouille automatiquement cette caisse
  useEffect(() => {
    const handler = () => {
      cashDebugLog("🔒 Session prise sur un autre appareil - verrouillage auto");
      lockSessionLocally();
    };
    window.addEventListener("moo-session-taken-over", handler);
    return () => window.removeEventListener("moo-session-taken-over", handler);
  }, [currentUser, currentSession, selectedTable, currentOrder]);

  const triggerBackgroundManualCloudSync = useCallback((reason = "refresh") => {
    const now = Date.now();
    if (manualCloudSyncInFlightRef.current) return;
    if (now - Number(lastManualCloudSyncAtRef.current || 0) < 3500) return;
    manualCloudSyncInFlightRef.current = true;
    lastManualCloudSyncAtRef.current = now;

    void (async () => {
      try {
        await fetchWithTimeout(
          `${API_BASE}/cloud/sync/manual`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              scope: "full",
              pull: true,
              source: "caisse_refresh",
              reason,
            }),
          },
          2200,
        );
      } catch (_error) {
        // Sync cloud opportuniste: aucune interruption de la caisse.
      } finally {
        setTimeout(() => {
          manualCloudSyncInFlightRef.current = false;
        }, 400);
      }
    })();
  }, [API_BASE, fetchWithTimeout]);

  // Fonction pour rafraîchir l'interface complète (tables, panier, statuts)
  const handleRefreshInterface = async () => {
    let selectedSnapshot = selectedTableRef.current
      ? { ...selectedTableRef.current }
      : null;
    const preRefreshSnapshot = buildCurrentCartSnapshot(selectedSnapshot);
    if (preRefreshSnapshot) {
      refreshCartSnapshotRef.current = preRefreshSnapshot;
    }
    const snapshotKey = preRefreshSnapshot
      ? getSnapshotTableKey(preRefreshSnapshot)
      : null;

    try {
      cashDebugLog("🔥 [DIGITALIVORE DEBUG] handleRefreshInterface DÉBUT", {
        selectedTable: selectedSnapshot?.number,
        existingItemsCount: existingItemsForPaymentRef.current?.length,
        currentOrderItemsCount: currentOrderRef.current?.items?.length,
        snapshotHash: preRefreshSnapshot?.hash || null,
        snapshotItems:
          (preRefreshSnapshot?.pendingItems?.length || 0) +
          (preRefreshSnapshot?.existingItems?.length || 0),
        timestamp: new Date().toISOString(),
      });
      cashDebugLog("🔄 Rafraîchissement anti-bug articles annulés...");
      setLoading(true);
      triggerBackgroundManualCloudSync("manual-refresh-button");

      // Pull cloud catalogue pour tout utilisateur connecté.
      // Non-bloquant — le serveur a un mutex cloudAdminPullRunning
      // qui empêche les exécutions simultanées, cooldown inutile.
      if (Boolean(currentUser)) {
        try {
          const controller = new AbortController();
          const timeoutId = window.setTimeout(() => controller.abort(), 2200);
          await fetch(`${API_BASE}/cloud/sync/manual`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Cache-Control": "no-store",
              Authorization: `Bearer ${localStorage.getItem("moo_token") || ""}`,
            },
            body: JSON.stringify({
              scope: "catalog",
              pull: true,
              forcePull: true,
              source: "caisse_refresh",
            }),
            cache: "no-store",
            signal: controller.signal,
          }).catch(() => null);
          window.clearTimeout(timeoutId);
        } catch (_e) {
          // Pull cloud non bloquant: on ignore l'erreur et on continue le refresh local.
        }
      }

      // 1. RECHARGER TOUTES LES DONNÉES avec les fonctions existantes
      cashDebugLog("📡 Rechargement des données principales...");
      await loadAllData();

      // La flèche est une demande explicite de convergence. Une simple lecture
      // de `/tables` ne suffit pas car un ancien article envoyé, conservé dans
      // le cache anti-perte, peut surclasser le statut `free`. Le snapshot
      // canonique supprime seulement ce fantôme ; tout vrai brouillon reste
      // protégé. Pour une table visible vide, cette passe contient déjà sa
      // double confirmation espacée de 200 ms.
      await runCartCacheReconciliationPass(
        "manual-refresh-authoritative-cart-snapshot",
      );
      if (
        selectedSnapshot &&
        !selectedSnapshot.isDelivery &&
        (!selectedTableRef.current ||
          String(selectedTableRef.current.id) !== String(selectedSnapshot.id))
      ) {
        selectedSnapshot = null;
      }

      const handleEmptyArbitration = async ({
        isDeliveryTable = false,
        tableId,
        serverItemsCount = 0,
      }) => {
        if (!snapshotKey || !preRefreshSnapshot) return;
        const snapshotItemsCount =
          (preRefreshSnapshot.pendingItems?.length || 0) +
          (preRefreshSnapshot.existingItems?.length || 0);
        if (
          snapshotItemsCount === 0 ||
          serverItemsCount > 0 ||
          preRefreshSnapshot.tableId !== tableId ||
          Boolean(preRefreshSnapshot.isDelivery) !== Boolean(isDeliveryTable)
        ) {
          refreshEmptyConfirmRef.current.delete(snapshotKey);
          return;
        }

        const previousCount = refreshEmptyConfirmRef.current.get(snapshotKey) || 0;
        const nextCount = previousCount + 1;
        refreshEmptyConfirmRef.current.set(snapshotKey, nextCount);

        if (nextCount < 2) {
          const restored = restoreCartFromSnapshot(preRefreshSnapshot);
          if (restored) {
            triggerErrorAnimation(
              "Désync temporaire détectée: panier local conservé, nouvelle vérification...",
            );
          }
          setTimeout(async () => {
            try {
              if (isDeliveryTable) {
                await loadPendingDeliveries({ allowAutoSelect: false });
              } else {
                await loadTableOrders(tableId);
                await loadTableItemsForPayment(tableId, false);
              }
            } catch (_retryError) {
              // non bloquant
            }
          }, 800);
          return;
        }

        refreshEmptyConfirmRef.current.delete(snapshotKey);
        if (isDeliveryTable) {
          setCurrentDeliveryOrder({ items: [], total: 0 });
          setExistingDeliveryItems([]);
          setHasExistingOrders(false);
          forgetKnownExistingItems(tableId, true, { preservePending: false });
        } else {
          tableCartSyncRef.current.grantClear(tableId, {
            isDelivery: false,
            reason: "manual-refresh-confirmed-empty",
          });
          await loadTableItemsForPayment(tableId, false);
        }
        triggerSuccessAnimation("Panier resynchronisé: serveur confirmé vide");
      };

      // 2. SI UNE TABLE/LIVRAISON EST SÉLECTIONNÉE, recharger ses données
      if (selectedSnapshot) {
        const tableSnapshot = selectedSnapshot;
        if (tableSnapshot.isDelivery) {
          cashDebugLog(
            `🚚 Rafraîchissement sécurisé livraison ${tableSnapshot.name || tableSnapshot.number}...`,
          );
          // Ne pas forcer la neutralisation ici: on veut éviter un flash "aucune table sélectionnée".
          await loadPendingDeliveries({ allowAutoSelect: false });
          const postDeliverySnapshot = buildCurrentCartSnapshot(tableSnapshot);
          const serverItemsCount =
            (postDeliverySnapshot?.pendingItems?.length || 0) +
            (postDeliverySnapshot?.existingItems?.length || 0);
          await handleEmptyArbitration({
            isDeliveryTable: true,
            tableId: tableSnapshot.id,
            serverItemsCount,
          });
          cashDebugLog("✅ Livraison rafraîchie avec succès");
        } else {
          cashDebugLog(
            `🔄 Rafraîchissement sécurisé table ${tableSnapshot.number}...`,
          );

          setUpdatingStatus(true);

          // Utiliser les fonctions existantes qui fonctionnent correctement
          await loadTableOrders(tableSnapshot.id);
          const refreshedItems = await loadTableItemsForPayment(
            tableSnapshot.id,
            false,
          );
          await handleEmptyArbitration({
            isDeliveryTable: false,
            tableId: tableSnapshot.id,
            serverItemsCount: Array.isArray(refreshedItems)
              ? refreshedItems.length
              : 0,
          });
          if (
            Array.isArray(refreshedItems) &&
            refreshedItems.length === 0 &&
            !snapshotHasItems(preRefreshSnapshot)
          ) {
            const emptyConfirmKey = `manual-refresh-empty:${tableSnapshot.id}`;
            const confirmedEmpty = await confirmTableEmptyForUiClear(tableSnapshot.id);
            if (!confirmedEmpty) {
              refreshEmptyConfirmRef.current.delete(emptyConfirmKey);
              setHasExistingOrders(true);
              optimisticTableStatusRef.current.set(tableSnapshot.id, {
                status: "occupied",
                ts: Date.now(),
              });
              setTimeout(() => {
                loadTableItemsForPayment(tableSnapshot.id, false).catch(() => {});
              }, 500);
            } else {
              const previousCount =
                refreshEmptyConfirmRef.current.get(emptyConfirmKey) || 0;
              const nextCount = previousCount + 1;
              refreshEmptyConfirmRef.current.set(emptyConfirmKey, nextCount);
              if (nextCount >= 2) {
                refreshEmptyConfirmRef.current.delete(emptyConfirmKey);
                tableCartSyncRef.current.grantClear(tableSnapshot.id, {
                  isDelivery: false,
                  reason: "manual-refresh-confirmed-empty",
                });
                await loadTableItemsForPayment(tableSnapshot.id, false);
              } else {
                triggerErrorAnimation(
                  "Verification panier vide en cours. Cliquez a nouveau sur Actualiser si la table est vraiment vide.",
                );
                setTimeout(() => {
                  loadTableItemsForPayment(tableSnapshot.id, false).catch(() => {});
                }, 700);
              }
            }
          }

          cashDebugLog("🔥 [DIGITALIVORE DEBUG] APRÈS loadTableItemsForPayment", {
            existingItemsCount: existingItemsForPaymentRef.current?.length,
            items: (existingItemsForPaymentRef.current || []).map((item) => ({
              id: item.order_item_id || item.id,
              name: item.name,
              status: item.status,
            })),
            timestamp: new Date().toISOString(),
          });

          setUpdatingStatus(false);

          cashDebugLog(`✅ Table ${tableSnapshot.number} rafraîchie avec succès`);
        }
      } else {
        if (isFastFoodMode && snapshotHasItems(preRefreshSnapshot)) {
          const activeFastFoodItems = (currentOrderRef.current?.items || []).filter(
            isItemActive,
          );
          if (activeFastFoodItems.length === 0) {
            restoreCartFromSnapshot(preRefreshSnapshot);
            triggerErrorAnimation(
              "Panier comptoir conserve localement apres actualisation.",
            );
          } else {
            const snapshot = buildFastFoodCartSnapshot(activeFastFoodItems);
            if (snapshot) {
              fastFoodCartSnapshotRef.current = snapshot;
              writeFastFoodCartSnapshotToStorage(snapshot);
            }
          }
        } else {
          refreshEmptyConfirmRef.current.clear();
        }
      }

      // 3. NETTOYAGE FINAL: filtrer uniquement les items annulés (sans re-render forcé)
      if (selectedSnapshot?.isDelivery) {
        setExistingDeliveryItems((prev) => prev.filter(isItemActive));
        setCurrentDeliveryOrder((prevOrder) => ({
          ...prevOrder,
          items: (prevOrder.items || []).filter(isItemActive),
          total: computeItemsTotal((prevOrder.items || []).filter(isItemActive)),
        }));
      } else if (selectedSnapshot && !selectedSnapshot.isDelivery) {
        cashDebugLog("🧹 Nettoyage final anti-fantômes...");
        setExistingItemsForPayment((prev) => prev.filter(isItemActive));
        setCurrentOrder((prevOrder) => ({
          ...prevOrder,
          items: (prevOrder.items || []).filter(isItemActive),
        }));
      } else if (isFastFoodMode) {
        setCurrentOrder((prevOrder) => {
          const activeItems = (prevOrder.items || []).filter(isItemActive);
          if (activeItems.length === 0 && snapshotHasItems(preRefreshSnapshot)) {
            const restoredItems = (preRefreshSnapshot.items || []).filter(isItemActive);
            if (restoredItems.length > 0) {
              return {
                items: restoredItems,
                total: computeItemsTotal(restoredItems),
              };
            }
          }
          return {
            ...prevOrder,
            items: activeItems,
            total: computeItemsTotal(activeItems),
          };
        });
      }
      refreshCartSnapshotRef.current = null;
      try {
        await loadTables({ forceUnpaidRefresh: true });
      } catch (tablesRefreshError) {
        console.warn("Actualisation des tables impossible:", tablesRefreshError);
      }
      cashDebugLog("✅ Rafraîchissement anti-bug terminé");

      // Animation de succès
      triggerSuccessAnimation("Interface actualisée (sécurisée)");
    } catch (error) {
      console.error("�?� Erreur lors du rafraîchissement:", error);
      triggerErrorAnimation("Erreur lors du rafraîchissement");
    } finally {
      refreshCartSnapshotRef.current = null;
      setUpdatingStatus(false);
      setLoading(false);
    }
  };

  // (Diagnostic UI retiré)

  // Fonctions pour le système de livraison
  const generateDeliveryOrderNumber = () => {
    // CORRECTION: Ne plus générer de numéros temporaires
    // Le backend génère directement les bons numéros L{x}C{y} ou L{x}B{y}
    // Cette fonction retourne juste un placeholder pour l'affichage initial

    const destination = currentDeliveryOrder.items.some(
      (item) => item.destination === "bar",
    )
      ? "B"
      : "C";

    // Format simplifié pour l'affichage initial uniquement
    let tempNumber = `En préparation...`;

    // Ajouter l'indication de la méthode de livraison si applicable
    if (
      deliveryMethod &&
      deliveryMethod !== "Direct" &&
      deliveryMethodCodes[deliveryMethod]
    ) {
      tempNumber = `En préparation (${deliveryMethod})...`;
    }

    return tempNumber;
  };

  const searchDeliveryCustomers = async (searchTerm) => {
    try {
      cashDebugLog("�? Recherche de clients:", searchTerm);

      let url;
      if (!searchTerm || searchTerm.trim().length === 0) {
        // Si pas de terme de recherche, charger tous les clients récents
        url = `${API_BASE}/customers/search?limit=20&sort=recent`;
      } else {
        // Recherche en temps réel dès le premier caractère
        url = `${API_BASE}/customers/search?q=${encodeURIComponent(searchTerm.trim())}&limit=10`;
      }

      const response = await fetch(url);

      if (response.ok) {
        const customers = await response.json();
        cashDebugLog("✅ Clients trouvés:", customers);
        setDeliveryCustomers(customers);
      } else {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Erreur serveur" }));
        console.error("�?� Erreur recherche:", response.status, errorData);
        setDeliveryCustomers([]);
        triggerErrorAnimation(`Erreur recherche: ${errorData.error}`);
      }
    } catch (error) {
      console.error("�?� Erreur lors de la recherche de clients:", error);
      setDeliveryCustomers([]);
      triggerErrorAnimation("Erreur de connexion au serveur");
    }
  };

  // Fonction pour charger tous les clients récents
  const loadRecentCustomers = () => {
    searchDeliveryCustomers("");
  };

  const openDeliveryOrderForPayment = (deliveryOrder) => {
    // S'assurer qu'on ne modifie que les données de cette commande spécifique
    // Nettoyer d'abord les paniers pour éviter le mélange
    setCurrentOrder({
      items: [],
      total: 0,
    });
    setCurrentDeliveryOrder({
      items: [],
      total: 0,
    });

    // Définir les informations de commande pour le paiement avec les données isolées
    const normalizedCustomer = deliveryOrder.customer
      ? serializeDeliveryCustomer(deliveryOrder.customer)
      : null;
    const normalizedDeliveryMethod = deliveryOrder.deliveryMethod || "Direct";
    const normalizedDeliveryRef =
      deliveryOrder.deliveryRef ||
      normalizedCustomer?.delivery_reference ||
      null;

    setSelectedTable({
      id: `delivery-${deliveryOrder.id}`,
      number: deliveryOrder.orderNumber,
      name: deliveryOrder.orderNumber,
      isDelivery: true,
      deliveryMethod: normalizedDeliveryMethod,
      deliveryRef: normalizedDeliveryRef,
      deliveryCustomer: normalizedCustomer,
      deliveryOrder: {
        ...deliveryOrder,
        deliveryMethod: normalizedDeliveryMethod,
        deliveryRef: normalizedDeliveryRef,
        customer: normalizedCustomer,
        // Créer une copie isolée des items pour éviter les références partagées
        items: [...deliveryOrder.items],
      },
    });
    setDeliveryMethod(normalizedDeliveryMethod);
    setDeliveryRef(normalizedDeliveryRef || "");

    // Stocker la commande de livraison sélectionnée
    setSelectedDeliveryOrder({
      ...deliveryOrder,
      deliveryMethod: normalizedDeliveryMethod,
      deliveryRef: normalizedDeliveryRef,
      customer: normalizedCustomer,
    });

    // Charger les articles de cette commande spécifique dans le panier de livraison
    setTimeout(() => {
      setCurrentDeliveryOrder({
        items: [...deliveryOrder.items],
        total: deliveryOrder.total,
      });
      setExistingDeliveryItems([...deliveryOrder.items]);
    }, 100);

    // Ouvrir l'interface de paiement
    setShowPaymentInterface(true);
  };

  const createDeliveryOrder = (selectedCustomer = null) => {
    // Utiliser le client passé en paramètre ou celui du state
    const customerToUse = selectedCustomer || deliveryCustomer;

    if (!deliveryMode) {
      triggerErrorAnimation("Veuillez sélectionner un mode de service");
      return;
    }

    if (deliveryMode === "delivery") {
      if (deliveryMethod === "Direct" && !customerToUse) {
        triggerErrorAnimation(
          "Veuillez sélectionner un client pour la livraison",
        );
        return;
      }

      if (deliveryMethod !== "Direct" && !deliveryRef.trim()) {
        triggerErrorAnimation(
          `Veuillez saisir une référence ${deliveryMethod}`,
        );
        return;
      }
    }

    // Vérifier si un onglet pour ce client existe déjà
    const normalizedDeliveryMethod = (
      deliveryMethod || "Direct"
    )
      .toString()
      .trim();
    const normalizedDeliveryRef =
      normalizedDeliveryMethod !== "Direct"
        ? deliveryRef.toString().trim()
        : "";

    const existingTab = clientTabs.find((tab) => {
      if (!tab?.isDelivery) return false;
      if ((tab.deliveryMode || "delivery") !== deliveryMode) return false;
      const tabMethod = (tab.deliveryMethod || "Direct").toString().trim();
      if (tabMethod !== normalizedDeliveryMethod) return false;

      if (normalizedDeliveryMethod !== "Direct") {
        const tabRef = getTabDeliveryReference(tab);
        return (
          normalizeDeliveryReferenceValue(tabRef) ===
          normalizeDeliveryReferenceValue(normalizedDeliveryRef)
        );
      }

      if (customerToUse?.id && tab.deliveryCustomer?.id) {
        return tab.deliveryCustomer.id === customerToUse.id;
      }

      const tabPhone =
        (tab.deliveryCustomer?.phone || tab.customerPhone || "").trim();
      const newPhone = (customerToUse?.phone || "").trim();
      return tabPhone && newPhone && tabPhone === newPhone;
    });

    if (existingTab) {
      // Sélectionner l'onglet existant
      handleSelectDeliveryTab(existingTab);
      cashDebugLog("📋 Onglet client existant sélectionné:", existingTab.name);
    } else {
      // Créer un nouvel onglet client (table virtuelle)
      const baseLabel =
        deliveryMethod === "Direct" && customerToUse
          ? `${customerToUse.first_name} ${customerToUse.last_name}`
          : deliveryMethod !== "Direct" && deliveryRef
            ? `${
                deliveryMethodCodes[deliveryMethod] || deliveryMethod
              }${deliveryRef}`
            : deliveryMode === "delivery"
              ? "Commande livraison"
              : "Commande à emporter";

      const aggregatorCustomer =
        deliveryMethod !== "Direct" && deliveryRef
          ? buildAggregatorCustomer(deliveryMethod, deliveryRef)
          : null;
      const resolvedClientCustomer =
        deliveryMethod !== "Direct"
          ? aggregatorCustomer
          : customerToUse || null;

      const clientTab = {
        id: `delivery-${Date.now()}`,
        orderId: null,
        number: baseLabel,
        name: baseLabel,
        isDelivery: true,
        deliveryMode: deliveryMode,
        deliveryMethod: deliveryMethod,
        deliveryCustomer: serializeDeliveryCustomer(resolvedClientCustomer),
        deliveryRef: deliveryRef,
        status: "occupied",
        isTemporary: true,
        items: [], // Chaque onglet a ses propres articles
        total: 0,
      };

      // Ajouter le nouvel onglet à la liste
      setClientTabs((prev) => [...prev, clientTab]);
      setActiveClientTab(clientTab);
      setSelectedTable(clientTab);

      cashDebugLog("🚚 Nouvel onglet client créé:", {
        id: clientTab.id,
        name: clientTab.name,
        customer: customerToUse?.first_name + " " + customerToUse?.last_name,
      });
    }

    // Nettoyer les paniers pour cette nouvelle commande
    setCurrentDeliveryOrder({ items: [], total: 0 });
    setExistingDeliveryItems([]);

    // Fermer seulement le panneau de livraison, garder le menu accessible
    setVisiblePanels((prev) => ({
      ...prev,
      delivery: false,
    }));
    setActiveTab("");

    // Réinitialiser la sélection de client/ref pour permettre d'en sélectionner un autre
    setDeliveryCustomerSearch("");
    setDeliveryRef("");
  };

  const handleCustomerModalSelect = (customer) => {
    if (!customer) {
      setShowCustomerModal(false);
      resetCustomerModalState();
      return;
    }

    if (customerModalContext === "delivery") {
      setDeliveryCustomer(customer);
      setDeliveryCustomerSearch(
        `${customer.first_name} ${customer.last_name} - ${customer.phone}`,
      );
      setDeliveryCustomers([]);
      triggerSuccessAnimation(
        `Client ${customer.first_name} ${customer.last_name} sélectionné`,
      );

      if (deliveryMode === "delivery" && deliveryMethod === "Direct") {
        createDeliveryOrder(customer);
      }
    } else {
      setSelectedCustomerForOrder(customer);
    }

    setShowCustomerModal(false);
    resetCustomerModalState();
  };

  // Fonction pour restaurer l'état d'une session verrouillée
  const restoreLockedSessionState = () => {
    try {
      migrateLegacyLockedSession();
      const lockedSession = getLockedSession(currentUser?.id);
      const isManualLock = lockedSession?.lockReason === "manual";

      // IMPORTANT: Ne restaurer QUE si c'est un verrouillage, pas une déconnexion
      if (lockedSession && isManualLock) {
        const sessionData = lockedSession;
        const currentUserData = localStorage.getItem("moo_user");

        if (currentUserData) {
          const currentUser = JSON.parse(currentUserData);

          // Vérifier que c'est le même utilisateur qui se reconnecte
          if (
            sessionData.currentUser &&
            sessionData.currentUser.id === currentUser.id
          ) {
            // Restaurer l'état de la session
            if (sessionData.selectedTable) {
              setSelectedTable(sessionData.selectedTable);
              cashDebugLog(
                "🔓 Table restaurée:",
                sessionData.selectedTable.name,
              );
            }

            if (sessionData.currentOrder) {
              setCurrentOrder(sessionData.currentOrder);
              cashDebugLog(
                "🔓 Commande restaurée:",
                sessionData.currentOrder.total + "F",
              );
            }

            if (Array.isArray(sessionData.tableStatesCache)) {
              commitTableStatesCache(new Map(sessionData.tableStatesCache));
            }

            if (sessionData.activeTab && !isBoutique) {
              setActiveTab(sessionData.activeTab);
            } else if (sessionData.selectedTable?.isDelivery && !isBoutique) {
              setActiveTab("delivery");
            } else {
              setActiveTab(
                isBoutique
                  ? isServerParticipationMode
                    ? "profile"
                    : "customers"
                  : "tables",
              );
            }

            if (sessionData.visiblePanels) {
              setVisiblePanels((prev) => {
                const next = { ...prev, ...sessionData.visiblePanels };
                if (isBoutique) {
                  next.tables = false;
                  next.delivery = false;
                  next.reservations = false;
                  next.customers = isServerParticipationMode
                    ? false
                    : next.customers ?? true;
                }
                return next;
              });
            }

            if (sessionData.deliveryMode) {
              setDeliveryMode(sessionData.deliveryMode);
            }
            if (sessionData.deliveryMethod) {
              setDeliveryMethod(sessionData.deliveryMethod);
            }
            if (sessionData.deliveryRef !== undefined) {
              setDeliveryRef(sessionData.deliveryRef || "");
            }
            if (sessionData.deliveryCustomer) {
              setDeliveryCustomer(sessionData.deliveryCustomer);
            }

            if (sessionData.selectedTable?.isDelivery) {
              setCurrentDeliveryOrder(
                sessionData.currentDeliveryOrder || { items: [], total: 0 },
              );
              setExistingDeliveryItems(sessionData.existingDeliveryItems || []);
              setSelectedDeliveryOrder(sessionData.selectedDeliveryOrder || null);
              setHasExistingOrders(
                sessionData.hasExistingOrders ||
                  (sessionData.existingDeliveryItems || []).length > 0,
              );
            } else {
              setExistingItemsForPayment(
                sessionData.existingItemsForPayment || [],
              );
              setHasExistingOrders(
                sessionData.hasExistingOrders ||
                  (sessionData.existingItemsForPayment || []).length > 0,
              );
              setExistingDeliveryItems([]);
              setCurrentDeliveryOrder({ items: [], total: 0 });
              setSelectedDeliveryOrder(null);
              setDeliveryCustomer(null);
            }

            try {
              if (sessionData.selectedTable?.isDelivery) {
                const deliveryItems = sessionData.existingDeliveryItems || [];
                if (deliveryItems.length > 0) {
                  lastExistingItemsRef.current.set(
                    sessionData.selectedTable.id,
                    { items: deliveryItems, isDelivery: true },
                  );
                }
              } else {
                const restoredItems = sessionData.existingItemsForPayment || [];
                if (restoredItems.length > 0) {
                  lastExistingItemsRef.current.set(
                    sessionData.selectedTable.id,
                    { items: restoredItems, isDelivery: false },
                  );
                }
              }
            } catch (_) {}

            try {
              const selectedId = sessionData.selectedTable?.id;
              if (selectedId && !sessionData.selectedTable?.isDelivery) {
                setTimeout(() => {
                  loadTableItemsForPayment(selectedId, false).catch(() => {});
                }, 0);
              } else if (selectedId && sessionData.selectedTable?.isDelivery) {
                setTimeout(() => {
                  loadPendingDeliveries({ allowAutoSelect: true }).catch(() => {});
                }, 0);
              }
            } catch (_) {}

            // Restaurer les données de session de caisse si elles existaient
            if (sessionData.cashSessionData) {
              localStorage.setItem(
                "moo_cash_session",
                sessionData.cashSessionData,
              );
              cashDebugLog("🔓 Session de caisse restaurée");
            }

            // Nettoyer les données de verrouillage après restauration
            if (currentUser?.id) {
              clearLockedSession(currentUser.id);
            }

            cashDebugLog(
              "🔓 État de session restauré après déverrouillage pour:",
              currentUser.name,
            );
            return true;
          } else {
            cashDebugLog(
              "⚠�? Utilisateur différent détecté, nettoyage des données de verrouillage",
            );
            if (currentUser?.id) {
              clearLockedSession(currentUser.id);
            }
          }
        }
      } else if (lockedSession && !isManualLock) {
        if (currentUser?.id) {
          clearLockedSession(currentUser.id);
        }
      }
    } catch (error) {
      console.error("�?� Erreur restauration session:", error);
      // En cas d'erreur, nettoyer les données corrompues
      if (currentUser?.id) {
        clearLockedSession(currentUser.id);
      }
    }
    return false;
  };

  // Fonction pour vérifier si des tables ont des commandes en cours
  // Deconnexion: seules les lignes locales non enregistrees doivent bloquer.
  // Les commandes deja sauvegardees en base restent recuperables apres reconnexion.
  const checkTablesWithOrders = async () => {
    const activeDraftItems = (currentOrderRef.current?.items || []).filter(isItemActive);
    if (activeDraftItems.length === 0) {
      return [];
    }

    if (isFastFoodMode) {
      return [
        {
          id: "fast-food-counter",
          number: "Comptoir",
          status: "draft",
          activeItems: activeDraftItems.length,
        },
      ];
    }

    if (selectedTable?.id && !selectedTable?.isDelivery) {
      return [
        {
          id: selectedTable.id,
          number: selectedTable.number || selectedTable.name || "Table",
          status: "draft",
          activeItems: activeDraftItems.length,
        },
      ];
    }

    return [
      {
        id: "local-draft",
        number: "Commande",
        status: "draft",
        activeItems: activeDraftItems.length,
      },
    ];
  };

  const fetchPaymentMethodsSafe = async () => {
    try {
      const response = await fetchWithTimeout(`${API_BASE}/payment-methods`, {}, 8000);
      if (!response.ok) throw new Error("payment methods");
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        return data
          .filter((method) => method?.is_active !== false)
          .map((method) => ({
          code: method.code,
          name: method.name,
          is_active: method.is_active,
        }));
      }
    } catch (_) {}
    return [
      { code: "cash", name: "Espèces", is_active: true },
      { code: "card", name: "Carte bancaire", is_active: true },
      { code: "mobile", name: "Mobile Money", is_active: true },
    ];
  };

  const buildCashLogoutMethods = (paymentMethods = [], reportMethods = []) => {
    const reportMap = new Map();
    reportMethods.forEach((entry) => {
      const key = normalizeMethodLabel(entry?.name);
      if (!key) return;
      reportMap.set(key, Number(entry?.amount) || 0);
    });

    // Le crédit est un reliquat client, pas un moyen encaissé à compter lors
    // de la clôture. Il reste visible dans les rapports X/Z.
    const activeMethods = paymentMethods.filter(
      (method) =>
        method?.is_active !== false && !isCreditPaymentMethod(method),
    );
    const list = activeMethods.map((method) => {
      const nameKey = normalizeMethodLabel(method.name);
      let expected = reportMap.get(nameKey);
      if (expected === undefined) {
        const codeKey = normalizeMethodLabel(method.code);
        const synonyms = {
          cash: ["especes", "cash"],
          card: ["carte", "carte bancaire", "cb", "card"],
          mobile: ["mobile", "mobile money", "momo"],
        };
        const fallbackKeys = [codeKey, ...(synonyms[method.code] || [])]
          .map(normalizeMethodLabel)
          .filter(Boolean);
        for (const candidate of fallbackKeys) {
          if (reportMap.has(candidate)) {
            expected = reportMap.get(candidate);
            break;
          }
        }
      }
      return {
        key: method.code || nameKey || method.name,
        code: method.code || null,
        name: method.name || method.code || "Mode",
        expected: Number(expected || 0),
      };
    });
    return list.sort((a, b) => {
      const aCash = isPhysicalCashMethod(a);
      const bCash = isPhysicalCashMethod(b);
      if (aCash !== bCash) return aCash ? -1 : 1;
      return String(a.name || "").localeCompare(String(b.name || ""), "fr");
    });
  };

  const openCashLogoutValidation = async () => {
    try {
      setCashLogoutLoading(true);
      setCashLogoutError("");
      setCashLogoutReport(null);
      setCashLogoutMethods([]);
      setCashLogoutInputs({});
      cashLogoutInputsDirtyRef.current = false;

      let session = currentSession;
      if (!session && currentUser?.id) {
        session = await fetchActiveCashSession(currentUser.id);
      }

      const fetchXReport = async () => {
        const reportResponse = await fetchWithTimeout(
          `${API_BASE}/cash-sessions/${session.id}/x-report`,
          {},
          12000,
        );
        if (!reportResponse.ok) {
          throw new Error("Chargement rapport X impossible");
        }
        return reportResponse.json();
      };

      const hasReportSales = (report) =>
        Number(report?.sales_summary?.net_sales || 0) > 0 ||
        Number(report?.statistics?.ticket_count || 0) > 0;
      const resolveMethodName = (rawMethod, paymentMethods = []) => {
        const normalized = normalizeMethodLabel(rawMethod);
        if (!normalized) return rawMethod || "Autre";
        const matched = paymentMethods.find((method) => {
          const codeKey = normalizeMethodLabel(method?.code);
          const nameKey = normalizeMethodLabel(method?.name);
          return normalized === codeKey || normalized === nameKey;
        });
        return matched?.name || rawMethod || "Autre";
      };
      const pickFallbackMethodName = (paymentMethods = []) => {
        const cashMethod = paymentMethods.find((method) => {
          const codeKey = normalizeMethodLabel(method?.code);
          const nameKey = normalizeMethodLabel(method?.name);
          return (
            codeKey === "cash" ||
            nameKey.includes("espece") ||
            nameKey.includes("especes") ||
            nameKey.includes("cash")
          );
        });
        if (cashMethod?.name) return cashMethod.name;
        return paymentMethods[0]?.name || "Espèces";
      };
      const buildLocalReportData = (methodsSource = [], inferredTotal = 0) => {
        const ticketCount = methodsSource.reduce(
          (sum, entry) => sum + Number(entry?.count || 0),
          0,
        );
        return {
          sales_summary: {
            net_sales: Number(inferredTotal || 0),
            total_discounts: 0,
          },
          payment_methods: methodsSource,
          statistics: {
            ticket_count: ticketCount,
            cancelled_items: 0,
          },
          adjustments: {
            cash_in: 0,
            cash_out: 0,
          },
        };
      };

      const paymentMethods = await fetchPaymentMethodsSafe();
      const localFallbackMethods = Array.from(
        sessionSalesByMethodRef.current.values(),
      )
        .map((entry) => ({
          name: resolveMethodName(entry?.method, paymentMethods),
          amount: Number(entry?.amount || 0),
          count: entry?.count || 0,
        }))
        .filter((entry) => Number(entry.amount || 0) > 0);
      const localFallbackTotal = localFallbackMethods.reduce(
        (sum, entry) => sum + Number(entry?.amount || 0),
        0,
      );
      const localSalesTotal = Number(localSalesTotalRef.current || 0);
      const hasLocalSales =
        localFallbackTotal > 0 ||
        localSalesTotal > 0 ||
        hasLocalSalesRef.current;
      const allowLocalFastPath =
        Date.now() - (lastPaymentAtRef.current || 0) < 15000 &&
        (!lastSessionIdRef.current || lastSessionIdRef.current === session?.id);

      let fastPathApplied = false;
      if (allowLocalFastPath && hasLocalSales) {
        let methodsSource = localFallbackMethods;
        const inferredTotal = localFallbackTotal || localSalesTotal;
        if (methodsSource.length === 0 && inferredTotal > 0) {
          methodsSource = [
            {
              name: pickFallbackMethodName(paymentMethods),
              amount: inferredTotal,
              count: 0,
            },
          ];
        }
        const reportData = buildLocalReportData(
          methodsSource,
          inferredTotal,
        );
        const methods = buildCashLogoutMethods(
          paymentMethods,
          methodsSource,
        );
        const inputs = {};
        methods.forEach((method) => {
          inputs[method.key] = "";
        });

        setCashLogoutSession(session);
        setCashLogoutReport(reportData);
        setCashLogoutMethods(methods);
        setCashLogoutInputs(inputs);
        setShowCashLogoutValidation(true);
        fastPathApplied = true;

        if (!session?.id) {
          return;
        }
      } else if (!session?.id) {
        setShowLogoutModal(true);
        return;
      }

      let reportData = await fetchXReport();
      let hasSales = hasReportSales(reportData);

      const shouldRetry =
        Date.now() - (lastPaymentAtRef.current || 0) < 20000;
      if (!hasSales && shouldRetry) {
        const delays = [500, 900, 1400];
        for (const delay of delays) {
          await new Promise((resolve) => setTimeout(resolve, delay));
          reportData = await fetchXReport();
          hasSales = hasReportSales(reportData);
          if (hasSales) break;
        }
      }

      const reportMethods = Array.isArray(reportData?.payment_methods)
        ? reportData.payment_methods
        : [];
      const reportMethodsTotal = reportMethods.reduce(
        (sum, entry) => sum + Number(entry?.amount || 0),
        0,
      );
      if (!hasSales && (localFallbackTotal > 0 || localSalesTotal > 0)) {
        hasSales = true;
      }
      if (!hasSales && reportMethodsTotal > 0) {
        hasSales = true;
      }

      if (!hasSales) {
        if (!fastPathApplied) {
          setShowLogoutModal(true);
        }
        return;
      }

      const inferredTotal =
        Number(reportData?.sales_summary?.net_sales || 0) ||
        localFallbackTotal ||
        localSalesTotal;
      let methodsSource =
        reportMethodsTotal > 0 ? reportMethods : localFallbackMethods;
      if (methodsSource.length === 0 && inferredTotal > 0) {
        methodsSource = [
          {
            name: pickFallbackMethodName(paymentMethods),
            amount: inferredTotal,
            count: reportData?.statistics?.ticket_count || 0,
          },
        ];
      }
      if (methodsSource.length > 0 && reportMethodsTotal === 0) {
        reportData = {
          ...reportData,
          payment_methods: methodsSource,
        };
      }
      const methods = buildCashLogoutMethods(
        paymentMethods,
        methodsSource,
      );

      const inputs = {};
      methods.forEach((method) => {
        inputs[method.key] = "";
      });

      setCashLogoutSession(session);
      setCashLogoutReport(reportData);
      if (!fastPathApplied || !cashLogoutInputsDirtyRef.current) {
        setCashLogoutMethods(methods);
        setCashLogoutInputs(inputs);
      }
      if (!fastPathApplied) {
        setShowCashLogoutValidation(true);
      }
    } catch (error) {
      console.error("�?� Erreur ouverture validation encaissements:", error);
      setCashLogoutError(
        "Impossible de charger les montants. Réessayez ou forcez la déconnexion.",
      );
      setShowCashLogoutValidation(true);
    } finally {
      setCashLogoutLoading(false);
    }
  };

  const cashLogoutSnapshot = useMemo(() => {
    const methods = cashLogoutMethods.map((method) => {
      const actual = parseAmountInput(cashLogoutInputs[method.key]);
      const expected = Number(method.expected || 0);
      const diff = actual - expected;
      return {
        ...method,
        actual,
        diff,
      };
    });
    const totalExpected = methods.reduce(
      (sum, method) => sum + Number(method.expected || 0),
      0,
    );
    const totalActual = methods.reduce((sum, method) => sum + method.actual, 0);
    const totalDiff = totalActual - totalExpected;
    const isTotalBalanced = Math.abs(totalDiff) <= 0.01;
    return {
      methods,
      totalExpected,
      totalActual,
      totalDiff,
      isTotalBalanced,
    };
  }, [cashLogoutMethods, cashLogoutInputs]);

  const submitCashLogoutAudit = async ({
    forced = false,
    managerName = null,
    managerId = null,
    reason = "",
  }) => {
    if (!cashLogoutSession?.id || !currentUser?.id) return;
    const payload = {
      session_id: cashLogoutSession.id,
      cashier_id: currentUser.id,
      cashier_name: `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim(),
      expected_totals: cashLogoutSnapshot.methods.map((method) => ({
        code: method.code || method.key || null,
        name: method.name,
        amount: Number(method.expected || 0),
      })),
      actual_totals: cashLogoutSnapshot.methods.map((method) => ({
        code: method.code || method.key || null,
        name: method.name,
        amount: Number(method.actual || 0),
      })),
      difference_totals: cashLogoutSnapshot.methods.map((method) => ({
        code: method.code || method.key || null,
        name: method.name,
        amount: Number(method.diff || 0),
      })),
      total_expected: Number(cashLogoutSnapshot.totalExpected || 0),
      total_actual: Number(cashLogoutSnapshot.totalActual || 0),
      total_difference: Number(cashLogoutSnapshot.totalDiff || 0),
      adjustments: {
        cash_in: Number(cashLogoutReport?.adjustments?.cash_in || 0),
        cash_out: Number(cashLogoutReport?.adjustments?.cash_out || 0),
        total_discounts: Number(
          cashLogoutReport?.sales_summary?.total_discounts || 0,
        ),
        cancelled_items: Number(
          cashLogoutReport?.statistics?.cancelled_items || 0,
        ),
      },
      forced: !!forced,
      forced_by_id: managerId || null,
      forced_by_name: managerName || null,
      forced_reason: reason || null,
    };

    try {
      const response = await fetch(`${API_BASE}/cash-logout-audits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        const error = new Error(
          errorPayload?.error || "La validation de clôture a échoué.",
        );
        error.code = errorPayload?.code;
        error.totalExpected = errorPayload?.total_expected;
        throw error;
      }
      return await response.json().catch(() => ({ success: true }));
    } catch (error) {
      console.warn("⚠�? Validation de clôture refusée:", error);
      throw error;
    }
  };

  // Fonction pour ouvrir le modal de déconnexion avec vérification
  const getLogoutBlockedMessage = (blockedOrdersCount) => {
    if (isFastFoodMode) {
      return blockedOrdersCount > 1
        ? `Deconnexion impossible : ${blockedOrdersCount} commandes comptoir sont encore en cours`
        : 'Deconnexion impossible : une commande comptoir est encore en cours';
    }
    if (isBoutique) {
      return `Deconnexion impossible : ${blockedOrdersCount} ticket(s) ont encore des commandes en cours`;
    }
    return `Deconnexion impossible : ${blockedOrdersCount} table(s) ont encore des commandes en cours`;
  };

  const handleLogoutConfirmation = async () => {
    try {
      if (isServerParticipationMode) {
        setShowLogoutModal(true);
        return;
      }
      if (isCounterContext && hasActiveCounterCart()) {
        triggerErrorAnimation(
          "Terminez ou annulez le panier Comptoir avant de vous déconnecter.",
        );
        return;
      }
      const pendingCounterOrders = await loadOpenCounterOrders();
      if (
        foreignCounterRecovery ||
        counterDispatchPending ||
        pendingCounterOrders === null ||
        pendingCounterOrders.length > 0
      ) {
        triggerErrorAnimation(
          pendingCounterOrders === null
            ? "Clôture impossible : les commandes Comptoir n’ont pas pu être vérifiées. Réessayez avec le réseau disponible."
            : "Clôture impossible : une transaction Comptoir doit être finalisée ou annulée par un manager.",
        );
        return;
      }

      // Vérifier s'il y a des tables occupées
      const occupiedTables = await checkTablesWithOrders();

      if (occupiedTables.length > 0) {
        // Il y a des tables occupées, empêcher la déconnexion
        setOccupiedTablesDetails(occupiedTables);
        setShowOccupiedTablesModal(true);
        triggerErrorAnimation(getLogoutBlockedMessage(occupiedTables.length));
        return;
      }

      // Aucune table occupée, vérifier s'il faut valider les encaissements
      if (canUseCashSession && (currentSession?.id || currentUser?.id)) {
        await openCashLogoutValidation();
        return;
      }

      setShowLogoutModal(true);
    } catch (error) {
      console.error("�?� Erreur vérification avant déconnexion:", error);
      triggerErrorAnimation(
        isBoutique
          ? "Erreur lors de la vérification du panier"
          : "Erreur lors de la vérification des tables",
      );
    }
  };

  const handleAdminHome = () => {
    window.location.href = `${window.location.origin}/admin`;
  };

  // Fonction pour effectuer la déconnexion complète (remise à zéro)
  const performLogout = async (finalAmountOverride = null, notesOverride = null) => {
    try {
      cashDebugLog("🔴 Début de la fermeture de session complète...");

      // 1. Fermer la session de caisse active si elle existe
      const logoutUser = currentUser?.id
        ? currentUser
        : JSON.parse(localStorage.getItem("moo_user") || "{}");
      const authToken = localStorage.getItem("moo_token");

      // La session de caisse doit être fermée avant d'invalider le jeton.
      if (logoutUser.id && canUseCashSessionForRole(logoutUser?.role)) {
        await closeActiveCashSession(
          logoutUser.id,
          finalAmountOverride,
          notesOverride,
        );
      }

      if (authToken) {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 2500);
        try {
          await fetch("/api/auth/logout", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({}),
            signal: controller.signal,
          });
        } catch (logoutError) {
          // Le jeton est supprimé localement quoi qu'il arrive : une API lente
          // ne doit pas retenir l'utilisateur sur un poste partagé.
          console.warn("Déconnexion serveur non confirmée:", logoutError);
        } finally {
          window.clearTimeout(timeoutId);
        }
      }

      // 3. Supprimer uniquement les données de la session courante
      if (logoutUser?.id) {
        clearLockedSession(logoutUser.id);
        try {
          sessionStorage.removeItem(
            `moo_admin_cash_drawer_shown_${logoutUser.id}`,
          );
        } catch (_e) {}
      }
      localStorage.removeItem("moo_user");
      localStorage.removeItem("moo_token");
      localStorage.removeItem("currentUser");
      localStorage.removeItem("moo_cash_session");
      localStorage.removeItem("moo_admin_cash_drawer");

      cashDebugLog("✅ Session fermée avec succès");

      // La garde d'authentification revient immédiatement au login sans
      // recharger tout le bundle ni redémarrer les initialisations réseau.
      setCurrentUser(null);
      window.dispatchEvent(new Event("moo-auth-refresh"));
      navigate("/", { replace: true });
    } catch (error) {
      console.error("�?� Erreur lors de la fermeture de session:", error);
      triggerErrorAnimation(
        error?.message || "La fermeture de session a échoué. Vos données sont conservées.",
      );
      throw error;
    }
  };

  // Fonction pour fermer la session de caisse active
  const closeActiveCashSession = async (
    cashierId,
    finalAmountOverride = null,
    notesOverride = null,
  ) => {
    try {
      cashDebugLog(
        "🔒 Fermeture de la session de caisse pour le caissier:",
        cashierId,
      );

      const authToken = localStorage.getItem("moo_token") || "";
      const authHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};

      let session = currentSession?.id ? currentSession : null;
      if (!session?.id) {
        try {
          const storedSession = JSON.parse(
            localStorage.getItem("moo_cash_session") || "null",
          );
          if (storedSession?.id) {
            session = storedSession;
          }
        } catch (_error) {}
      }

      if (!session?.id) {
        const response = await fetch(
          `${API_BASE}/cash-sessions/active`,
          { headers: authHeaders },
        );
        if (response.ok) {
          session = await response.json();
        }
      }

      if (session?.id) {
        cashDebugLog("📊 Session active trouvée:", session.id);

        // Fermer la session avec le montant final saisi si fourni
        const overrideAmount = parseAmountInput(finalAmountOverride);
        const hasOverride = Number.isFinite(overrideAmount);
        let computedFinalAmount = hasOverride
          ? overrideAmount
          : Number(session.initial_amount) || 0;

        if (!hasOverride) {
          try {
            const cashierId = currentUser?.id || session?.cashier_id;
            const cashierQuery = cashierId ? `?cashierId=${cashierId}` : "";
            const summaryResponse = await fetch(
              `${API_BASE}/cash-journal/summary/${session.id}${cashierQuery}`,
            );
            if (summaryResponse.ok) {
              const summaryData = await summaryResponse.json();
              const theoretical =
                summaryData?.summary?.theoretical_balance ??
                (summaryData?.summary?.total_revenue || 0) +
                  (session.initial_amount || 0);
              if (Number.isFinite(theoretical)) {
                computedFinalAmount = Math.max(0, Math.round(theoretical));
              }
            }
          } catch (summaryError) {
            console.warn(
              "⚠�? Impossible de récupérer le résumé pour la clôture de session:",
              summaryError,
            );
          }
        }

        const closeResponse = await fetch(
          `${API_BASE}/cash-sessions/${session.id}/close`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...authHeaders,
            },
            body: JSON.stringify({
              finalAmount: computedFinalAmount,
              notes:
                notesOverride ||
                "Session fermée automatiquement lors de la déconnexion",
            }),
          },
        );

        if (closeResponse.ok) {
          cashDebugLog("✅ Session de caisse fermée avec succès");
        } else {
          const payload = await closeResponse.json().catch(() => ({}));
          throw new Error(payload?.error || "Fermeture de session refusée");
        }
      } else {
        cashDebugLog("ℹ�? Aucune session de caisse active trouvée");
      }
    } catch (error) {
      console.error("�?� Erreur fermeture session de caisse:", error);
      throw error;
    }
  };

  // Fonction pour déconnexion avec rapport X (caissiers/admin)
  const performLogoutWithXReport = () => {
    setShowLogoutModal(false);
    setIsXReportFromLogout(true);
    setShowXReport(true);
  };

  const handleConfirmCashLogout = async () => {
    if (!cashLogoutSnapshot.isTotalBalanced || cashLogoutLoading) return;
    try {
      setCashLogoutLoading(true);
      setCashLogoutError("");
      await submitCashLogoutAudit({ forced: false });
      setShowCashLogoutValidation(false);
      setCashLogoutReport(null);
      setCashLogoutMethods([]);
      setCashLogoutInputs({});
      setCashLogoutSession(null);
      const drawerFinalAmount = computeCashLogoutDrawerAmount({
        methods: cashLogoutSnapshot.methods,
        initialAmount: cashLogoutReport?.cash_fund?.initial_amount,
        cashIn: cashLogoutReport?.adjustments?.cash_in,
        cashOut: cashLogoutReport?.adjustments?.cash_out,
      });
      await performLogout(
        drawerFinalAmount,
        "Session fermée après validation des encaissements",
      );
    } catch (error) {
      setCashLogoutError(
        error?.code === "CASH_LOGOUT_TOTAL_CHANGED"
          ? "Les ventes ont changé. Rechargez les montants avant de confirmer."
          : error?.message || "La clôture n’a pas pu être validée.",
      );
    } finally {
      setCashLogoutLoading(false);
    }
  };

  const handleForceLogout = async () => {
    if (!forceLogoutReason.trim()) {
      setForceLogoutError("Veuillez saisir un motif.");
      return;
    }
    if (!forceLogoutManagerCode.trim()) {
      setForceLogoutError("Code manager requis.");
      return;
    }

    try {
      setForceLogoutSubmitting(true);
      setForceLogoutError("");
      const response = await fetch(`${API_BASE}/validate-manager-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ managerCode: forceLogoutManagerCode }),
      });
      if (!response.ok) {
        setForceLogoutError("Code manager incorrect.");
        return;
      }
      const data = await response.json();
      const managerName = data?.manager?.name || "Manager";
      const managerId = data?.manager?.id || null;

      await submitCashLogoutAudit({
        forced: true,
        managerName,
        managerId,
        reason: forceLogoutReason,
      });

      setShowForceLogoutModal(false);
      setShowCashLogoutValidation(false);
      setForceLogoutReason("");
      setForceLogoutManagerCode("");
      setCashLogoutReport(null);
      setCashLogoutMethods([]);
      setCashLogoutInputs({});
      setCashLogoutSession(null);

      const drawerFinalAmount = computeCashLogoutDrawerAmount({
        methods: cashLogoutSnapshot.methods,
        initialAmount: cashLogoutReport?.cash_fund?.initial_amount,
        cashIn: cashLogoutReport?.adjustments?.cash_in,
        cashOut: cashLogoutReport?.adjustments?.cash_out,
      });
      await performLogout(
        drawerFinalAmount,
        `Déconnexion forcée - ${forceLogoutReason}`,
      );
    } catch (error) {
      console.error("�?� Erreur déconnexion forcée:", error);
      setForceLogoutError("Erreur de validation, réessayez.");
    } finally {
      setForceLogoutSubmitting(false);
    }
  };

  // Fonction pour charger les extras d'un produit
  const loadProductExtras = async (productId) => {
    const cached = productExtrasCacheRef.current.get(String(productId));
    if (cached) {
      setProductExtras(cached);
      setLoadingExtras(false);
      return cached;
    }

    const requestSequence = ++extrasLoadSequenceRef.current;
    try {
      setLoadingExtras(true);
      const response = await fetchWithTimeout(
        `${API_BASE}/products/${productId}/extras`,
        {},
        5000,
      );
      if (!response.ok)
        throw new Error("Erreur lors du chargement des extras");
      const extras = await response.json();
      if (requestSequence !== extrasLoadSequenceRef.current) return [];
      const normalizedExtras = Array.isArray(extras) ? extras : [];
      productExtrasCacheRef.current.set(String(productId), normalizedExtras);
      setProductExtras(normalizedExtras);
      return normalizedExtras;
    } catch (error) {
      console.error("Erreur lors du chargement des extras:", error);
      return [];
    } finally {
      if (requestSequence === extrasLoadSequenceRef.current) {
        setLoadingExtras(false);
      }
    }
  };

  const hydrateProductWithVariations = async (product) => {
    if (!product || product.product_type !== "variable") {
      return product;
    }

    if (Array.isArray(product.variations) && product.variations.length > 0) {
      return product;
    }

    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/products/${product.id}/variations`,
      );
      if (!response.ok) {
        throw new Error("Impossible de charger les variations du produit");
      }
      const variationsData = await response.json();
      const normalizedVariations = Array.isArray(variationsData)
        ? variationsData
            .filter(Boolean)
            .filter((variation) => variation && variation.is_active !== false)
            .map((variation) => ({
              ...variation,
              attributes: parseVariationAttributesValue(variation.attributes),
            }))
        : [];

      const defaultVariation =
        normalizedVariations.find((variation) => variation.is_default) ||
        normalizedVariations[0] ||
        null;

      const hydratedProduct = {
        ...product,
        variations: normalizedVariations,
        default_variation_id: defaultVariation?.id || null,
        display_price:
          defaultVariation
            ? parseMoney(defaultVariation.price) ||
              parseMoney(product.price) ||
              parseMoney(product.display_price) ||
              0
            : product.display_price ??
              product.effective_price_for_day ??
              product.price,
      };

      setProducts((prev) =>
        Array.isArray(prev)
          ? prev.map((item) =>
              item.id === hydratedProduct.id ? hydratedProduct : item,
            )
          : prev,
      );

      return hydratedProduct;
    } catch (error) {
      console.error(
        `�?� Erreur chargement variations produit ${product?.id}:`,
        error,
      );
      return product;
    }
  };

  // Fonction pour ouvrir le modal avec gestion des extras
  const openQuantityModal = async (product) => {
    cashDebugLog(
      "�? openQuantityModal déclenché:",
      {
        id: product?.id,
        name: product?.name,
        type: product?.product_type,
        variations: Array.isArray(product?.variations)
          ? product.variations.length
          : 0,
      },
    );
    const hydratedProduct = await hydrateProductWithVariations(product);
    cashDebugLog(
      "✅ Produit hydraté:",
      {
        id: hydratedProduct?.id,
        name: hydratedProduct?.name,
        type: hydratedProduct?.product_type,
        variations: Array.isArray(hydratedProduct?.variations)
          ? hydratedProduct.variations.length
          : 0,
      },
    );
    setSelectedProduct(hydratedProduct);
    const hasVariations =
      hydratedProduct?.product_type === "variable" &&
      Array.isArray(hydratedProduct.variations) &&
      hydratedProduct.variations.length > 0;
    const defaultVariation = hasVariations
      ? hydratedProduct.variations.find(
          (variation) => variation && variation.is_active !== false,
        ) || hydratedProduct.variations[0]
      : null;
    if (hasVariations && defaultVariation) {
      setSelectedVariation({
        ...defaultVariation,
        attributes: parseVariationAttributesValue(defaultVariation.attributes),
      });
    } else {
      setSelectedVariation(null);
    }

    const supportsExtras =
      hydratedProduct.supports_accompaniments ||
      hydratedProduct.supports_supplements ||
      hydratedProduct.has_accompaniments ||
      hydratedProduct.has_supplements;

    cashDebugLog(
      "ℹ�? Statut extras produit:",
      {
        id: hydratedProduct?.id,
        name: hydratedProduct?.name,
        supportsExtras,
        hasVariations,
        flags: {
          supports_accompaniments: hydratedProduct?.supports_accompaniments,
          supports_supplements: hydratedProduct?.supports_supplements,
          has_accompaniments: hydratedProduct?.has_accompaniments,
          has_supplements: hydratedProduct?.has_supplements,
        },
      },
    );

    if (!supportsExtras && !hasVariations) {
      cashDebugLog(
        "➡�? Aucun extra ni variation: ouverture du sélecteur de quantité",
      );
      if (isBoutique) {
        const basePrice = getProductDisplayPrice(hydratedProduct);
        const productToAdd = {
          ...hydratedProduct,
          price: basePrice,
          product_price: basePrice,
          quantity: 1,
          extras: [],
        };
        addNewItemToOrder(productToAdd);
        setShowQuantityModal(false);
        setSelectedProduct(null);
        setSelectedExtras([]);
        return;
      }
      setShowQuantityModal(true);
      return;
    }

    setShowQuantityModal(false);
    setSelectedExtras([]);
    setSelectedProductForExtras({
      ...hydratedProduct,
      base_price: hydratedProduct.price,
      has_accompaniments: false,
      has_supplements: false,
      variations: Array.isArray(hydratedProduct.variations)
        ? hydratedProduct.variations
        : [],
    });
    setProductExtras([]);
    setShowExtrasModal(true);
    cashDebugLog("🟢 Ouverture modal extras pour", hydratedProduct?.name);

    // Une variation n'implique pas des extras. Dans ce cas le sélecteur de
    // variation est déjà complet et doit apparaître sans requête supplémentaire.
    if (!supportsExtras && hasVariations) {
      setLoadingExtras(false);
      return;
    }

    cashDebugLog(
      `🎯 Vérification des extras actifs pour ${hydratedProduct.name}...`,
    );
    const extras = await loadProductExtras(hydratedProduct.id);
    const hasActiveAccomp = extras.some(
      (extra) => extra.type === "accompaniment",
    );
    const hasActiveSupp = extras.some((extra) => extra.type === "supplement");

    if (!hasActiveAccomp && !hasActiveSupp && !hasVariations) {
      cashDebugLog(
        "⚠�? Aucun extra actif détecté après chargement - bascule vers quantité",
      );
      setProducts((prev) =>
        Array.isArray(prev)
          ? prev.map((p) =>
              p.id === hydratedProduct.id
                ? {
                    ...p,
                    has_accompaniments: false,
                    has_supplements: false,
                  }
                : p,
            )
          : prev,
      );
      setProductExtras([]);
      setSelectedProductForExtras(null);
      setShowExtrasModal(false);
      setShowQuantityModal(true);
      return;
    }

    setProducts((prev) =>
      Array.isArray(prev)
        ? prev.map((p) =>
            p.id === hydratedProduct.id
              ? {
                  ...p,
                  has_accompaniments: hasActiveAccomp,
                  has_supplements: hasActiveSupp,
                }
              : p,
          )
        : prev,
    );

    setSelectedProductForExtras((prev) =>
      prev && prev.id === hydratedProduct.id
        ? {
            ...prev,
            has_accompaniments: hasActiveAccomp,
            has_supplements: hasActiveSupp,
          }
        : prev,
    );
    setProductExtras(extras);
  };

  const handleTableCardSelect = useCallback(
    (table) => {
      if (activeAggregator) {
        setActiveAggregator(null);
      }
      void selectTable(table);
    },
    [activeAggregator, selectTable],
  );

  const handleProductCardSelect = useCallback(
    (product) => {
      const counterBlockMessage = getCounterMutationBlockMessage();
      if (counterBlockMessage) {
        triggerErrorAnimation(counterBlockMessage);
        return;
      }
      if (selectedTableAccessConflict) {
        triggerErrorAnimation(
          formatTableAccessConflictMessage(selectedTableAccessConflict, {
            withRecoveryHint: true,
          }),
        );
        return;
      }
      cashDebugLog("🖱�? CLIC PRODUIT CAISSE:", {
        id: product?.id,
        name: product?.name,
        type: product?.product_type,
        hasVariations:
          Array.isArray(product?.variations) && product.variations.length > 0,
        selectedTable: selectedTable?.id || selectedTable?.name || "aucune",
      });

      if (!selectedTable && !canOrderWithoutTable) {
        if (isBoutique) {
          const fallbackTable =
            tables.find(
              (t) => t?.number === 999 || t?.name === "T99" || t?.id === 999,
            ) ||
            tables[0] ||
            null;
          if (fallbackTable) {
            setSelectedTable(fallbackTable);
          }
        } else {
          triggerErrorAnimation(
            "Veuillez d'abord sélectionner une table ou créer une commande de livraison",
          );
          return;
        }
      }

      openQuantityModal(product);
    },
    [
      isBoutique,
      canOrderWithoutTable,
      isFastFoodMode,
      openQuantityModal,
      selectedTable,
      selectedTableAccessConflict,
      tables,
      triggerErrorAnimation,
    ],
  );

  // Fonction pour fermer le modal des extras
  const closeExtrasModal = () => {
    extrasLoadSequenceRef.current += 1;
    setShowExtrasModal(false);
    setSelectedProductForExtras(null);
    setProductExtras([]);
    setSelectedExtras([]);
    setVariationSelections({});
    setSelectedVariation(null);
  };

  const getModalBasePrice = () => {
    const variationForPrice = selectedVariation || effectiveVariation;
    if (variationForPrice) {
      return getUnitPriceForSelectionMode(
        selectedProductForExtras,
        variationForPrice,
        activeAggregator,
      );
    }
    if (selectedProductForExtras) {
      const aggregatorPrice = getAggregatorProductPrice(
        selectedProductForExtras,
        activeAggregator,
      );
      if (aggregatorPrice !== null) return aggregatorPrice;
      return (
        parseMoney(selectedProductForExtras.price) ||
        parseMoney(selectedProductForExtras.base_price) ||
        0
      );
    }
    return 0;
  };

  const getModalSupplementsTotal = () => computeSupplementTotal(selectedExtras);

  const handleVariationSelect = (variation) => {
    if (!variation) return;
    setSelectedVariation({
      ...variation,
      attributes: parseVariationAttributesValue(variation.attributes),
    });
  };

  const isVariableProduct =
    selectedProductForExtras?.product_type === "variable";
  const variationOptions =
    isVariableProduct && Array.isArray(selectedProductForExtras?.variations)
      ? selectedProductForExtras.variations
      : [];

  const variationAttributesMeta = useMemo(() => {
    if (!Array.isArray(variationOptions) || variationOptions.length === 0) {
      return [];
    }
    const order = [];
    const valueMap = {};
    variationOptions.forEach((variation) => {
      buildVariationAttributePairs(variation).forEach((pair, index) => {
        const attributeName = pair.name || `Option ${index + 1}`;
        if (!valueMap[attributeName]) {
          valueMap[attributeName] = new Set();
          order.push(attributeName);
        }
        if (pair.value) {
          valueMap[attributeName].add(pair.value);
        }
      });
    });
    return order.map((name) => ({
      name,
      values: Array.from(valueMap[name]),
    }));
  }, [variationOptions]);

  const resolveVariationFromSelections = useCallback(
    (selections) => {
      if (!Array.isArray(variationOptions) || variationOptions.length === 0) {
        return;
      }
      const cleanedSelections = Object.fromEntries(
        Object.entries(selections || {}).filter(([, value]) => Boolean(value)),
      );
      const requiredAttributeCount = variationAttributesMeta.length;
      if (
        requiredAttributeCount > 0 &&
        Object.keys(cleanedSelections).length < requiredAttributeCount
      ) {
        setSelectedVariation(null);
        return;
      }
      const matchedVariation = variationOptions.find((variation) => {
        const pairs = buildVariationAttributePairs(variation);
        const pairsCount = pairs.length;
        if (
          requiredAttributeCount > 0 &&
          pairsCount !== requiredAttributeCount
        ) {
          return false;
        }
        return Object.entries(cleanedSelections).every(([name, value]) =>
          pairs.some((pair) => pair.name === name && pair.value === value),
        );
      });
      if (matchedVariation) {
        setSelectedVariation({
          ...matchedVariation,
          attributes: parseVariationAttributesValue(matchedVariation.attributes),
        });
      } else {
        setSelectedVariation(null);
      }
    },
    [variationOptions, variationAttributesMeta],
  );

  const getAvailableOptionsForAttribute = useCallback(
    (attributeName, selections = variationSelections) => {
      const metaEntry = variationAttributesMeta.find(
        (attribute) => attribute.name === attributeName,
      );
      if (!metaEntry) return [];
      const filteredSelections = Object.fromEntries(
        Object.entries(selections || {}).filter(
          ([name, value]) => name !== attributeName && value,
        ),
      );
      if (Object.keys(filteredSelections).length === 0) {
        return metaEntry.values;
      }
      const allowedValues = new Set();
      variationOptions.forEach((variation) => {
        const pairs = buildVariationAttributePairs(variation);
        const matches = Object.entries(filteredSelections).every(
          ([name, value]) =>
            pairs.some((pair) => pair.name === name && pair.value === value),
        );
        if (matches) {
          const target = pairs.find((pair) => pair.name === attributeName);
          if (target?.value) {
            allowedValues.add(target.value);
          }
        }
      });
      if (allowedValues.size === 0) {
        return metaEntry.values;
      }
      return metaEntry.values.filter((value) => allowedValues.has(value));
    },
    [variationSelections, variationOptions, variationAttributesMeta],
  );

  const handleAttributeSelection = useCallback(
    (attributeName, optionValue, disabled) => {
      if (disabled) return;
      setVariationSelections((prev) => {
        const next = { ...prev };
        if (next[attributeName] === optionValue) {
          delete next[attributeName];
        } else {
          next[attributeName] = optionValue;
        }
        resolveVariationFromSelections(next);
        return next;
      });
    },
    [resolveVariationFromSelections],
  );

  useEffect(() => {
    if (!showExtrasModal) return;
    if (
      !selectedProductForExtras ||
      selectedProductForExtras.product_type !== "variable"
    ) {
      setVariationSelections({});
      return;
    }
    setVariationSelections({});
    setSelectedVariation(null);
  }, [showExtrasModal, selectedProductForExtras?.id]);

  useEffect(() => {
    if (
      !showExtrasModal ||
      !selectedProductForExtras ||
      selectedProductForExtras.product_type !== "variable"
    ) {
      return;
    }
    if (variationOptions.length === 1) {
      const singleVariation = variationOptions[0];
      const autoSelections = {};
      buildVariationAttributePairs(singleVariation).forEach((pair) => {
        if (pair.name && pair.value) {
          autoSelections[pair.name] = pair.value;
        }
      });
      setVariationSelections(autoSelections);
      resolveVariationFromSelections(autoSelections);
    }
  }, [
    showExtrasModal,
    selectedProductForExtras?.id,
    variationOptions,
    resolveVariationFromSelections,
  ]);
  const accompanimentExtras = productExtras.filter(
    (extra) => extra.type === "accompaniment",
  );
  const supplementExtras = productExtras.filter(
    (extra) => extra.type === "supplement",
  );
  const showVariationSection =
    showExtrasModal && variationOptions.length > 0;
  const hasAccompanimentExtras = accompanimentExtras.length > 0;
  const hasSupplementExtras = supplementExtras.length > 0;
  const shouldDisplayEmptyExtrasState =
    showExtrasModal &&
    !showVariationSection &&
    !hasAccompanimentExtras &&
    !hasSupplementExtras;

  const areAllVariationAttributesSelected =
    !showVariationSection ||
    variationAttributesMeta.every(
      (attribute) => !!variationSelections[attribute.name],
    );

  const computeEffectiveVariation = () => {
    if (!showVariationSection || variationOptions.length === 0) {
      return null;
    }
    if (selectedVariation) {
      return selectedVariation;
    }
    if (variationOptions.length === 1) {
      const single = variationOptions[0];
      return {
        ...single,
        attributes: parseVariationAttributesValue(single.attributes),
      };
    }
    return null;
  };

  const effectiveVariation = computeEffectiveVariation();
  const modalAccompanimentQuota = resolveAccompanimentQuota(
    selectedProductForExtras,
    effectiveVariation,
  );
  const selectedAccompanimentQuantity =
    countSelectedAccompaniments(selectedExtras);
  const modalBasePrice = getModalBasePrice();
  const modalTotalWithExtras = modalBasePrice + getModalSupplementsTotal();
  const isVariationSelectionRequired =
    showVariationSection && variationOptions.length > 0;
  const isVariationSelectionComplete =
    !isVariationSelectionRequired ||
    (areAllVariationAttributesSelected && effectiveVariation);
  const isConfirmExtrasDisabled =
    loadingExtras ||
    !isVariationSelectionComplete ||
    selectedAccompanimentQuantity > modalAccompanimentQuota;

  // Fonction pour confirmer la sélection des extras - MODIFIÉE
  const confirmExtrasSelection = () => {
    // Pour les produits avec extras, on ajoute directement au panier avec quantité 1
    // L'utilisateur devra refaire le parcours pour ajouter plus
    if (!selectedProductForExtras) return;

    const requiresVariationSelection =
      selectedProductForExtras.product_type === "variable" &&
      Array.isArray(selectedProductForExtras.variations) &&
      selectedProductForExtras.variations.length > 0;
    const selectedVariationPayload =
      selectedProductForExtras.product_type === "variable"
        ? effectiveVariation
        : null;

    if (
      selectedProductForExtras.product_type === "variable" &&
      !selectedVariationPayload
    ) {
      triggerErrorAnimation("Veuillez sélectionner une variante");
      return;
    }

    const basePrice = getModalBasePrice();
    const variationPayload = selectedVariationPayload
      ? resolveVariationMetadata(selectedVariationPayload)
      : null;

    const productToAdd = {
      ...selectedProductForExtras,
      price: basePrice,
      product_price: basePrice,
      quantity: 1, // Toujours 1 pour les produits avec extras
      extras: selectedExtras.map((extra) => ({
        ...extra,
        quantity: extra.selectedQuantity || 1,
        // ✅ S'assurer que le type est préservé pour la facturation
        type: extra.type, // "accompaniment" ou "supplement"
        // Forcer prix à 0 pour les accompagnements
        price: extra.type === "accompaniment" ? 0 : extra.price,
      })),
    };
    if (variationPayload) {
      Object.assign(productToAdd, variationPayload);
    }

    addNewItemToOrder(productToAdd);

    // Fermer le modal des extras
    setShowExtrasModal(false);
    setSelectedProductForExtras(null);
    setProductExtras([]);
    setSelectedExtras([]);
    // addNewItemToOrder affiche déjà le résultat réel. Ne jamais superposer
    // un succès au refus d'ajouter dans un tour déjà envoyé.
  };

  const toggleExtra = (extra, type) => {
    const isAccompaniment = type === "accompaniment";
    const alreadySelected = selectedExtras.some(
      (selected) => selected.id === extra.id,
    );
    if (alreadySelected) {
      setSelectedExtras(
        selectedExtras.filter((selected) => selected.id !== extra.id),
      );
    } else {
      if (isAccompaniment && selectedAccompanimentQuantity >= modalAccompanimentQuota) {
        return;
      }
      setSelectedExtras([
        ...selectedExtras,
        { ...extra, selectedQuantity: 1, type },
      ]);
    }
  };

  const updateExtraQuantity = (extra, delta) => {
    setSelectedExtras((prev) =>
      prev.map((selected) => {
        if (selected.id !== extra.id) return selected;
        const allowedMaximum = getSelectableExtraMaximum({
          extra: selected,
          selectedExtras: prev,
          quota: modalAccompanimentQuota,
        });
        const nextQuantity = Math.max(
          1,
          Math.min(
            allowedMaximum,
            (selected.selectedQuantity || 1) + delta,
          ),
        );
        return { ...selected, selectedQuantity: nextQuantity };
      }),
    );
  };

  // Fonction pour ajouter un produit avec ses extras (utilisée pour les produits sans extras)
  const addProductWithExtras = (quantity) => {
    if (!selectedProduct) return;

    const variationPayload =
      selectedProduct.product_type === "variable" && selectedVariation
        ? resolveVariationMetadata(selectedVariation)
        : null;

    const basePrice =
      selectedProduct.product_type === "variable"
        ? getUnitPriceForSelectionMode(
            selectedProduct,
            selectedVariation,
            activeAggregator,
          )
        : getProductDisplayPriceForMode(selectedProduct, activeAggregator);

    const productToAdd = {
      ...selectedProduct,
      price: basePrice,
      product_price: basePrice,
      quantity: quantity,
      extras: selectedExtras.map((extra) => ({
        ...extra,
        quantity: extra.selectedQuantity || 1,
      })),
    };
    if (variationPayload) {
      Object.assign(productToAdd, variationPayload);
    }

    addNewItemToOrder(productToAdd);
    setShowQuantityModal(false);
    setSelectedProduct(null);
    setSelectedExtras([]);
    setSelectedVariation(null);
  };

  // Fonction pour calculer le prix total avec extras (CORRIGÉE)
  const calculateTotalPriceWithExtras = () => {
    if (!selectedProduct) return 0;

    const variationForPricing =
      selectedProduct.product_type === "variable"
        ? showVariationSection
          ? effectiveVariation
          : selectedVariation
        : null;

    const basePrice = getUnitPriceForSelectionMode(
      selectedProduct,
      variationForPricing,
      activeAggregator,
    );

    // ✅ CORRECTION: Seuls les suppléments sont facturés, pas les accompagnements
    const extrasPrice = selectedExtras.reduce((sum, extra) => {
      // Ne facturer que les suppléments (type "supplement")
      if (extra.type === "supplement") {
        return sum + extra.price * (extra.selectedQuantity || 1);
      }
      // Les accompagnements (type "accompaniment") restent gratuits
      return sum;
    }, 0);

    cashDebugLog("💰 Calcul prix extras:", {
      basePrice,
      extrasPrice,
      selectedExtras: selectedExtras.map((e) => ({
        name: e.name,
        type: e.type,
        price: e.price,
        quantity: e.selectedQuantity || 1,
        isCharged: e.type === "supplement",
      })),
    });

    return basePrice + extrasPrice;
  };

  const getCurrentCourseOrderId = () => {
    const ids = [...(existingItemsForPayment || []), ...(currentOrder.items || [])]
      .map((item) => Number(item?.order_id ?? item?.orderId))
      .filter((id) => Number.isFinite(id) && id > 0);
    return ids.length > 0 ? Math.max(...ids) : null;
  };

  const toggleCourseModeForCurrentTable = () => {
    if (!courseModeEligibleForTable(selectedTable)) return;
    if (hasExistingOrders || getCurrentCourseOrderId()) {
      triggerErrorAnimation(
        "Le mode plusieurs tours se choisit avant le premier envoi de la table.",
      );
      return;
    }
    setCourseModeEnabled((enabled) => {
      const next = !enabled;
      if (!next) {
        setActiveCourseNumber(1);
        orderCoursesRef.current = [];
        setOrderCourses([]);
        setCurrentOrder((previous) => ({
          ...previous,
          items: (previous.items || []).map((item) => ({
            ...item,
            courseNumber: 1,
            course_number: 1,
          })),
        }));
      }
      return next;
    });
  };

  const createNextCourse = async (options = {}) => {
    const targetTable = options?.table || selectedTable;
    if (!courseModeEnabled || !courseModeContextForTable(targetTable)) return null;
    if (!courseManagementAvailable) {
      triggerErrorAnimation(
        "Le forfait actuel permet de terminer les tours existants, mais pas d’en créer un nouveau.",
      );
      return null;
    }
    const sourceCourses = Array.isArray(options?.courses)
      ? options.courses
      : orderCoursesRef.current?.length
        ? orderCoursesRef.current
        : orderCourses;
    const nextNumber = resolveNextCourseNumber(
      sourceCourses,
      Number(options?.afterCourseNumber || selectedDraftCourseNumber) - 1,
    );
    if (nextNumber > 20) {
      triggerErrorAnimation("Une commande ne peut pas dépasser 20 tours.");
      return null;
    }

    const orderId = Number(options?.orderId) || getCurrentCourseOrderId();
    if (!orderId) {
      setOrderCourses((previous) => {
        const next = [
          ...previous.filter(
          (course) => Number(course?.course_number) !== nextNumber,
        ),
          { course_number: nextNumber, status: "held" },
        ];
        orderCoursesRef.current = next;
        return next;
      });
      setActiveCourseNumber(nextNumber);
      if (!options?.silent) {
        triggerSuccessAnimation(`Tour ${nextNumber} prêt à être composé.`);
      }
      return nextNumber;
    }

    try {
      const requestId = `course-create-${getPersistentDeviceId()}-${orderId}-${nextNumber}-${Date.now()}`;
      const response = await fetchWithTimeout(
        `${API_BASE}/orders/${orderId}/courses`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            course_number: nextNumber,
            client_request_id: requestId,
            table_id: getApiTableId(targetTable),
          }),
        },
        ORDER_MUTATION_TIMEOUT,
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Création du tour impossible");
      const createdCourse = payload?.course || payload;
      const createdCourseNumber = Math.max(
        1,
        Number(createdCourse?.course_number || nextNumber) || nextNumber,
      );
      setOrderCourses((previous) => {
        const next = [
          ...previous.filter(
          (course) => Number(course?.course_number) !== createdCourseNumber,
        ),
          createdCourse,
        ];
        orderCoursesRef.current = next;
        return next;
      });
      setActiveCourseNumber(createdCourseNumber);
      if (!options?.silent) {
        triggerSuccessAnimation(`Tour ${createdCourseNumber} prêt à être composé.`);
      }
      return createdCourseNumber;
    } catch (error) {
      triggerErrorAnimation(
        options?.silent
          ? "Le tour précédent est enregistré, mais le suivant n’a pas pu être préparé. Touchez le bouton Tour pour réessayer."
          : error?.message || "Création du tour impossible",
      );
      return null;
    }
  };

  const advanceAfterCourseMutation = async ({
    orderId,
    courses,
    courseNumber,
    table = selectedTable,
  }) => {
    const sourceCourses = Array.isArray(courses)
      ? courses
      : orderCoursesRef.current;
    const target = resolveFollowingCourseTarget(sourceCourses, courseNumber);
    if (target.action === "select") {
      setActiveCourseNumber(target.courseNumber);
      return target.courseNumber;
    }
    return createNextCourse({
      orderId,
      courses: sourceCourses,
      afterCourseNumber: courseNumber,
      table,
      silent: true,
    });
  };

  const setCourseDispatchAction = (actionKey, status) => {
    setCourseDispatchStates((previous) => {
      if (!status) {
        if (!previous[actionKey]) return previous;
        const next = { ...previous };
        delete next[actionKey];
        return next;
      }
      return { ...previous, [actionKey]: status };
    });
  };

  const retryOrdinaryItemPrint = async (item) => {
    const itemId = Number(item?.order_item_id || item?.id);
    if (!Number.isInteger(itemId) || itemId <= 0) return;
    if (productionRetryingItemIds.has(itemId)) return;
    setProductionRetryingItemIds((previous) => {
      const next = new Set(previous);
      next.add(itemId);
      return next;
    });
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/order-items/${itemId}/production-retry`,
        { method: "POST", headers: { "Content-Type": "application/json" } },
        10000,
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Réimpression non confirmée.");
      }
      if (selectedTable?.id && !selectedTable?.isDelivery) {
        await loadTableItemsForPayment(selectedTable.id, false);
      }
      triggerSuccessAnimation("Bon réimprimé.");
    } catch (error) {
      triggerErrorAnimation(
        error?.message || "Réimpression non confirmée.",
      );
    } finally {
      setProductionRetryingItemIds((previous) => {
        const next = new Set(previous);
        next.delete(itemId);
        return next;
      });
    }
  };

  const readAuthoritativeCourse = async (orderId, courseNumber) => {
    const response = await fetchWithTimeout(
      `${API_BASE}/orders/${orderId}/courses`,
      {},
      5000,
    );
    if (!response.ok) {
      throw new Error(`Vérification impossible (${response.status})`);
    }
    const payload = await response.json().catch(() => ({}));
    const courses = mergeAuthoritativeCourseStates(
      orderCoursesRef.current,
      payload?.courses || [],
    );
    const course = courses.find(
      (entry) => Number(entry.course_number) === Number(courseNumber),
    );
    if (!course) throw new Error("Tour introuvable pendant la vérification");
    orderCoursesRef.current = courses;
    setOrderCourses(courses);
    return course;
  };

  const reconcileAmbiguousCourseDispatch = async (
    orderId,
    courseNumber,
    { attempts = 3 } = {},
  ) => {
    let lastError = null;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, attempt === 1 ? 450 : 1200),
        );
      }
      try {
        const course = await readAuthoritativeCourse(orderId, courseNumber);
        const dispatchStillRunning =
          String(course?.status || "") === "fired" &&
          String(course?.production_dispatch_status || "") === "in_progress" &&
          Number(course?.dispatch_stale) !== 1;
        if (!dispatchStillRunning) return course;
        lastError = new Error("L’impression est encore en cours");
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError || new Error("État du tour indisponible");
  };

  const finishCourseVerification = (actionKey, course) => {
    const status = String(course?.status || "held");
    const dispatchStatus = String(
      course?.production_dispatch_status || "",
    );
    const dispatchStale = Number(course?.dispatch_stale) === 1;
    if (
      status === "completed" ||
      (status === "fired" &&
        (["sent", "failed"].includes(dispatchStatus) || dispatchStale))
    ) {
      setCourseDispatchAction(actionKey, null);
      courseFireRequestIdsRef.current.delete(actionKey);
      setActiveCourseNumber(Number(course.course_number) || 1);
      if (dispatchStatus === "sent") {
        triggerSuccessAnimation(`Tour ${course.course_number} réclamé.`);
        void advanceAfterCourseMutation({
          orderId: Number(course?.order_id) || getCurrentCourseOrderId(),
          courses: orderCoursesRef.current,
          courseNumber: Number(course.course_number) || 1,
        });
      } else if (dispatchStatus === "failed") {
        triggerErrorAnimation(
          "Impression non confirmée. Vérifiez en cuisine/bar avant de relancer.",
        );
      } else if (dispatchStale) {
        triggerErrorAnimation(
          "L’envoi a été interrompu. Vérifiez en cuisine/bar avant de réessayer.",
        );
      }
      return true;
    }
    if (status === "held") {
      setCourseDispatchAction(actionKey, null);
      courseFireRequestIdsRef.current.delete(actionKey);
    }
    return false;
  };

  const verifyCourseDispatch = async (courseNumber) => {
    const normalizedCourseNumber = Math.max(1, Number(courseNumber) || 1);
    const orderId = getCurrentCourseOrderId();
    if (!orderId) return;
    const actionKey = `${orderId}:${normalizedCourseNumber}`;
    setCourseDispatchAction(actionKey, "verifying");
    try {
      const course = await reconcileAmbiguousCourseDispatch(
        orderId,
        normalizedCourseNumber,
      );
      if (!finishCourseVerification(actionKey, course)) {
        triggerErrorAnimation(
          `Le tour ${normalizedCourseNumber} n’a pas été réclamé. Vous pouvez réessayer.`,
        );
      }
    } catch (_error) {
      setCourseDispatchAction(actionKey, "uncertain");
      triggerErrorAnimation(
        "Envoi non confirmé. Vérifiez en cuisine/bar, puis relancez la vérification.",
      );
    }
  };

  const fireCourse = async (courseNumber) => {
    const normalizedCourseNumber = Math.max(1, Number(courseNumber) || 1);
    const orderId = getCurrentCourseOrderId();
    if (!orderId) {
      triggerErrorAnimation("Enregistrez d'abord le premier tour.");
      return;
    }
    const localItems = (currentOrder.items || []).filter(
      (item) =>
        Number(item?.courseNumber ?? item?.course_number ?? 1) ===
        normalizedCourseNumber,
    );
    if (localItems.length > 0) {
      const added = await addToExistingOrder(orderId, {
        suppressDispatch: true,
        dispatchAddition: false,
        courseNumber: normalizedCourseNumber,
      });
      if (!added) return;
    }
    const actionKey = `${orderId}:${normalizedCourseNumber}`;
    const courseAtDispatch = orderCoursesRef.current.find(
      (course) =>
        Number(course?.course_number) === normalizedCourseNumber,
    );
    let requestId = courseFireRequestIdsRef.current.get(actionKey);
    if (!requestId) {
      requestId = `course-fire-${getPersistentDeviceId()}-${orderId}-${normalizedCourseNumber}-${Date.now()}`;
      courseFireRequestIdsRef.current.set(actionKey, requestId);
    }
    setCourseDispatchAction(actionKey, "sending");
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/orders/${orderId}/courses/${normalizedCourseNumber}/fire`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            client_request_id: requestId,
            course_id: courseAtDispatch?.id ?? null,
            lifecycle_version:
              courseAtDispatch?.lifecycle_version ??
              courseAtDispatch?.lifecycleVersion ??
              1,
          }),
        },
        ORDER_MUTATION_TIMEOUT,
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(payload?.error || "Envoi du tour impossible");
        error.code = payload?.code;
        throw error;
      }
      setOrderCourses((previous) => {
        const next = upsertAuthoritativeCourseState(previous, {
          ...courseAtDispatch,
          ...(payload?.course || {}),
          course_number: normalizedCourseNumber,
          order_id: orderId,
          status: "fired",
        });
        orderCoursesRef.current = next;
        return next;
      });
      setCourseDispatchAction(actionKey, null);
      courseFireRequestIdsRef.current.delete(actionKey);
      triggerSuccessAnimation(`Tour ${normalizedCourseNumber} réclamé.`);
      await loadTableItemsForPayment(selectedTable.id, false);
      await advanceAfterCourseMutation({
        orderId,
        courses: orderCoursesRef.current,
        courseNumber: normalizedCourseNumber,
      });
    } catch (error) {
      setCourseDispatchAction(actionKey, "verifying");
      try {
        const authoritativeCourse = await reconcileAmbiguousCourseDispatch(
          orderId,
          normalizedCourseNumber,
        );
        if (!finishCourseVerification(actionKey, authoritativeCourse)) {
          triggerErrorAnimation(error?.message || "Envoi du tour impossible");
        }
      } catch (_verificationError) {
        setCourseDispatchAction(actionKey, "uncertain");
        triggerErrorAnimation(
          "Envoi non confirmé. Vérifiez en cuisine/bar avant toute nouvelle tentative.",
        );
      }
    }
  };

  const cancelEmptyCourse = async (courseNumber) => {
    const normalizedCourseNumber = Math.max(1, Number(courseNumber) || 1);
    if (
      hasLaterActiveCourse(orderCoursesRef.current, normalizedCourseNumber)
    ) {
      triggerErrorAnimation(
        "Annulez d’abord les derniers tours pour conserver une numérotation continue.",
      );
      return;
    }
    const courseItems = [...cartNewItems, ...cartExistingItems].filter(
      (item) =>
        Number(item?.courseNumber ?? item?.course_number ?? 1) ===
        normalizedCourseNumber,
    );
    if (courseItems.length > 0) {
      triggerErrorAnimation("Retirez d’abord les articles de ce tour.");
      return;
    }

    const orderId = getCurrentCourseOrderId();
    if (!orderId) {
      const remainingCourses = orderCourses.filter(
        (course) => Number(course?.course_number) !== normalizedCourseNumber,
      );
      setOrderCourses(remainingCourses);
      setActiveCourseNumber((previous) =>
        previous === normalizedCourseNumber
          ? resolveSelectedCourseNumber(remainingCourses)
          : previous,
      );
      triggerSuccessAnimation(`Tour ${normalizedCourseNumber} annulé.`);
      return;
    }

    try {
      const courseAtCancellation = orderCoursesRef.current.find(
        (course) =>
          Number(course?.course_number) === normalizedCourseNumber,
      );
      const requestId = `course-cancel-${getPersistentDeviceId()}-${orderId}-${normalizedCourseNumber}-${courseAtCancellation?.id || "new"}-${courseAtCancellation?.lifecycle_version || 1}-${Date.now()}`;
      const response = await fetchWithTimeout(
        `${API_BASE}/orders/${orderId}/courses/${normalizedCourseNumber}/cancel`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            client_request_id: requestId,
            reason: "Tour vide annulé depuis la caisse",
            course_id: courseAtCancellation?.id ?? null,
            lifecycle_version:
              courseAtCancellation?.lifecycle_version ??
              courseAtCancellation?.lifecycleVersion ??
              1,
          }),
        },
        ORDER_MUTATION_TIMEOUT,
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Annulation du tour impossible");
      }
      const remainingCourses = orderCourses.map((course) =>
        Number(course?.course_number) === normalizedCourseNumber
          ? { ...course, ...(payload?.course || {}), status: "cancelled" }
          : course,
      );
      setOrderCourses(remainingCourses);
      setActiveCourseNumber((previous) =>
        previous === normalizedCourseNumber
          ? resolveSelectedCourseNumber(remainingCourses)
          : previous,
      );
      triggerSuccessAnimation(`Tour ${normalizedCourseNumber} annulé.`);
    } catch (error) {
      triggerErrorAnimation(error?.message || "Annulation du tour impossible");
    }
  };

  const showInitialBootLoader = loading || !bootHydrated;

  if (showInitialBootLoader) {
    // Évite le double loader sur le cloud (SplashOverlay suffit)
    const isCloudHost = (() => {
      try {
        const host = window.location?.hostname || "";
        if (!host) return false;
        if (host === "localhost" || host === "127.0.0.1" || host === "::1") return false;
        return host.includes("moosync");
      } catch (_e) {
        return false;
      }
    })();

    if (isCloudHost) return null;

    return (
      <div
        className="moo-app-screen flex items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <img
              src="/moo-sync-insigne-logo.png"
              alt="MOO Sync Logo"
              className="w-16 h-16 object-contain"
            />
          </div>
          <div className="text-xl font-bold text-white">
            Chargement MOO Sync...
          </div>
          <div className="text-white/70 mt-2">Connexion à l'API...</div>
        </div>
      </div>
    );
  }

  const shouldRenderTablesPanel =
    !isBoutique &&
    !isFastFoodMode &&
    ((activeTab === "tables" && visiblePanels.tables) ||
      (isServerParticipationMode && mobileServerTab === "tables"));
  const shouldShowSidePanel =
    Object.values(visiblePanels).some(Boolean) || shouldRenderTablesPanel;

  return (
    <div
      className={`caisse-container ${themeMode === "dark" ? "theme-dark" : "theme-light"} ${
        shellMotionReady ? "caisse-shell-ready" : "caisse-shell-booting"
      }${isServerParticipationMode ? ` server-mode mobile-tab-${mobileServerTab}` : ""}`}
      style={{ backgroundColor: colors.background }}
    >
      {/* Header avec statut de connexion */}
      <header
        className="sticky top-0 z-50 relative overflow-hidden p-5 text-white rounded-b-3xl shadow-xl"
        style={{ background: "linear-gradient(135deg, #0b152d 0%, #0f1f3a 100%)" }}>
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-20 translate-x-24 -translate-y-24 bg-blue-500"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-15 -translate-x-16 translate-y-12 bg-cyan-400"></div>
        <div className="caisse-header-content relative z-10 flex flex-wrap items-center justify-between gap-4">
          <div className="caisse-header-brand flex items-center gap-4">
            <div className="caisse-header-logo h-14 w-14 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center">
              <img src="/moo-sync-insigne-logo.png" alt="MOO Sync Logo" className="h-10 w-10 object-contain" />
            </div>
            <div className="caisse-header-brand-copy">
              <p className="text-xs uppercase tracking-[0.35em] text-amber-300">Caisse</p>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-3xl font-bold">MOO Sync</h1>
                {isSuperPath && (
                  <span className="px-3 py-1 rounded-full bg-amber-400/20 text-amber-100 border border-amber-300/40 text-[11px] font-semibold">
                    Caisse Sandbox
                  </span>
                )}
              </div>
              <p className="text-white/70 text-sm">
                {restaurantInfo.name || "Restaurant"}
              </p>
            </div>
          </div>

          <div className="caisse-header-right flex items-center gap-6">
            <div className="caisse-header-clock">
              <HeaderLiveClock />
            </div>

            <div className="flex items-center gap-3">
              <div className={`px-3 py-1 rounded-full text-sm font-semibold border ${isSelectedTableCacheReconciling
                ? "bg-amber-500/20 border-amber-400/40 text-amber-100"
                : apiStatus === "connected"
                ? "bg-emerald-500/20 border-emerald-400/40 text-emerald-100"
                : apiStatus === "standalone"
                  ? "bg-amber-500/20 border-amber-400/40 text-amber-100"
                  : "bg-rose-500/20 border-rose-400/40 text-rose-100"
                }`}>
                {isSelectedTableCacheReconciling ? (
                  <><RefreshCw size={14} className="inline animate-spin" /><span className="hidden sm:inline"> Resynchronisation…</span></>
                ) : apiStatus === "connected" ? (
                  <><span>🟢</span><span className="hidden sm:inline"> API connectée</span></>
                ) : apiStatus === "standalone" ? (
                  <><span>⚡</span><span className="hidden sm:inline"> Mode local</span></>
                ) : (
                  <><span>🔴</span><span className="hidden sm:inline"> Déconnecté</span></>
                )}
              </div>

              <div className="caisse-header-actions flex gap-2">
                {["caissier", "cashier", "caisse", "manager", "admin"].includes(
                  getCurrentRole(),
                ) && (
                  <>
                    <HeaderButton title="Journal de caisse" onClick={openCashJournalWithRefresh}>
                      <Receipt size={20} />
                    </HeaderButton>

                    <HeaderButton title="Mouvement de caisse" onClick={openCashMovementModal}>
                      <Plus size={24} />
                    </HeaderButton>
                    <HeaderButton title="Rapports" onClick={() => setShowXReport(true)}>
                      <FileText size={20} />
                    </HeaderButton>
                  </>
                )}

                <HeaderButton title="Verrouiller la session" onClick={handleLockSession}>
                  <Lock size={20} />
                </HeaderButton>

                {isDualScreenAllowed && !isServerParticipationMode && (
                  <HeaderButton
                    title={
                      isDualScreenActive
                        ? "Désactiver le deuxième écran client"
                        : "Activer le deuxième écran client"
                    }
                    onClick={toggleDualScreen}
                    badge={isDualScreenActive ? "ON" : null}
                  >
                    <Monitor size={20} />
                  </HeaderButton>
                )}

                <HeaderButton
                  title={isAppFullscreen ? "Quitter le plein écran" : "Passer en plein écran"}
                  onClick={toggleAppFullscreen}
                >
                  {isAppFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </HeaderButton>

		                <HeaderButton
		                  title={
		                    recentCancellations.size > 0
		                      ? `Actualisation intelligente (${recentCancellations.size} annulation(s) en mémoire)`
		                      : "Actualiser l'interface et le panier"
		                  }
		                  onClick={handleRefreshInterface}
		                  badge={recentCancellations.size > 0 ? recentCancellations.size : null}
		                >
		                  <RefreshCw size={20} />
		                </HeaderButton>

                        <button
                          type="button"
                          className="server-mobile-only-logout hidden caisse-header-button relative p-3 rounded-2xl bg-red-500/20 hover:bg-red-500/40 border border-red-400/30 text-red-300 transition items-center justify-center"
                          title="Se déconnecter"
                          aria-label="Se déconnecter"
                          onClick={handleLogoutConfirmation}
                        >
                          <LogOut size={20} />
                        </button>

		              </div>
		            </div>
		          </div>
        </div>
      </header>

      <div className="caisse-main-content pt-4">
        {/* Menu Vertical - Gauche */}
        <div className="caisse-sidebar relative overflow-hidden rounded-3xl flex flex-col bg-gradient-to-br from-[#0b1120] via-[#0a1224] to-[#050814] border border-white/10 text-white shadow-2xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,184,0,0.06),transparent_30%),radial-gradient(circle_at_80%_10%,rgba(99,102,241,0.06),transparent_25%)] pointer-events-none" />
          <div className="relative z-10 flex flex-col h-full">
            {/* Onglets du menu */}
            <div className="flex-1 p-3 space-y-3">
              {/* Tables */}
              {!isBoutique && !isFastFoodMode && (
                <button
                  onClick={() => {
                    if (visiblePanels.tables && activeTab === "tables") {
                      togglePanel("tables");
                    } else {
                      showPanelExclusive("tables");
                    }
                  }}
                  className={`w-full h-14 rounded-2xl flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 ${
                    activeTab === "tables" && visiblePanels.tables
                      ? "bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/40"
                      : visiblePanels.tables
                        ? "bg-white/10 text-white"
                        : "bg-white/5 text-white/70 hover:bg-white/10"
                  }`}
                  title="Tables du Restaurant"
                >
                  <Users size={24} />
                </button>
              )}

              {!isBoutique && isHybridMode && !isServerParticipationMode && (
                <button
                  onClick={isCounterContext ? leaveCounterContext : enterCounterContext}
                  className={`w-full h-14 rounded-2xl flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 ${
                    isCounterContext
                      ? "bg-gradient-to-r from-emerald-400 to-cyan-500 text-white shadow-lg shadow-emerald-500/40"
                      : "bg-white/5 text-white/70 hover:bg-white/10"
                  }`}
                  title={isCounterContext ? "Revenir aux Tables" : "Vente Comptoir"}
                  aria-label="Comptoir"
                >
                  <ShoppingBag size={24} />
                </button>
              )}

              {!isBoutique && onlineMenuOrderingEnabled && (
                <button
                  onClick={() => {
                    if (visiblePanels.online && activeTab === "online") {
                      togglePanel("online");
                    } else {
                      showPanelExclusive("online");
                    }
                  }}
                  className={`relative w-full h-14 rounded-2xl flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 ${
                    activeTab === "online" && visiblePanels.online
                      ? "bg-gradient-to-r from-cyan-400 to-blue-500 text-white shadow-lg shadow-cyan-500/40"
                      : visiblePanels.online
                        ? "bg-white/10 text-white"
                        : "bg-white/5 text-white/70 hover:bg-white/10"
                  }`}
                  title="Commandes en ligne"
                >
                  <RestaurantWaiterIcon className="h-6 w-6 text-current" />
                  {onlineUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-bold">
                      {onlineUnreadCount > 99 ? "99+" : onlineUnreadCount}
                    </span>
                  )}
                </button>
              )}

              {!isBoutique &&
                !isServerParticipationMode &&
                aggregatorSettings.aggregators &&
                (["yango", "glovo"]).map((code) => {
                  if (!aggregatorSettings[code]) return null;
                  const meta = AGGREGATOR_ORDER_META[code];
                  const isActive = activeAggregator === code;
                  return (
                    <button
                      key={code}
                      onClick={() => toggleAggregatorOrder(code)}
                      className={`w-full h-14 rounded-2xl flex items-center justify-center overflow-hidden transition-all duration-300 hover:-translate-y-0.5 ${
                        isActive
                          ? "ring-2 ring-white/90 shadow-lg"
                          : "opacity-90 hover:opacity-100"
                      }`}
                      style={{ backgroundColor: meta.backgroundColor }}
                      title={`Commande ${meta.label}`}
                    >
                      <img
                        src={meta.icon}
                        alt={meta.label}
                        className="h-full w-full object-cover"
                        draggable="false"
                      />
                    </button>
                  );
                })}

            {/* Clients */}
            {!isServerParticipationMode && (
              <button
                onClick={() => {
                  if (visiblePanels.customers && activeTab === "customers") {
                    togglePanel("customers");
                  } else {
                    showPanelExclusive("customers");
                  }
                }}
                className={`w-full h-14 rounded-2xl flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 ${
                  activeTab === "customers" && visiblePanels.customers
                    ? "bg-gradient-to-r from-indigo-400 to-amber-500 text-white shadow-lg shadow-amber-500/30"
                    : visiblePanels.customers
                      ? "bg-white/10 text-white"
                      : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
                title="Gestion des Clients"
              >
                <UserPlus size={24} />
              </button>
            )}

            </div>

            {/* Toggle thème, séparé juste avant la zone profil */}
            <div className="px-3 pb-3">
              <button
                onClick={() =>
                  setThemeMode((prev) => (prev === "dark" ? "light" : "dark"))
                }
                className="w-full h-14 rounded-2xl flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 bg-white/5 text-white/80 hover:bg-white/10"
                title="Basculer thème"
              >
                {themeMode === "dark" ? <Sun size={24} /> : <Moon size={24} />}
              </button>
            </div>

            {/* Profil en bas */}
            <div className="p-3 border-t border-white/10 bg-white/5 rounded-b-3xl">
              <button
                onClick={() => {
                  if (visiblePanels.profile && activeTab === "profile") {
                    togglePanel("profile");
                  } else {
                    showPanelExclusive("profile");
                  }
                }}
                className={`w-full h-14 rounded-2xl flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 ${
                  activeTab === "profile" && visiblePanels.profile
                    ? "bg-gradient-to-r from-orange-400 to-red-500 text-white shadow-lg shadow-orange-500/30"
                    : visiblePanels.profile
                      ? "bg-white/10 text-white"
                      : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
                title="Profil utilisateur"
              >
                <User size={24} />
              </button>

              {isAdminUser && (
                <button
                  onClick={handleAdminHome}
                  className="w-full h-14 rounded-2xl flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 mt-3 bg-emerald-500/10 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/20"
                  title="Tableau de bord"
                >
                  <Home size={24} />
                </button>
              )}

              {/* Déconnexion */}
              <button
                onClick={handleLogoutConfirmation}
                className="w-full h-14 rounded-2xl flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 mt-3 bg-red-500/10 text-red-200 border border-red-500/30 hover:bg-red-500/20"
                title="Se déconnecter"
              >
                <LogOut size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Contenu des onglets avec animation de slide */}
        <div
          ref={panelRef}
          className={`caisse-panel transition-[opacity,transform,width,box-shadow] duration-300 ease-in-out rounded-3xl shadow-xl ${
            isDarkTheme
              ? "bg-[#0f172a] border border-white/5 text-white"
              : "bg-white"
          } ${
            shouldShowSidePanel
              ? "w-80 flex-shrink-0"
              : "w-0 opacity-0 pointer-events-none"
          }`}
        >
          {/* Onglet Tables */}
          {shouldRenderTablesPanel && (
            <div className="caisse-panel-content animate-fadeIn">
              <div className="p-4">
	                <div className="flex items-center justify-between mb-6">
	                  <h2
                      className={`text-xl font-bold flex items-center ${
                        isDarkTheme ? "text-white" : "text-gray-800"
                      }`}
                    >
	                    <Users
	                      className="mr-3"
	                      size={24}
	                      style={{ color: colors.primary }}
	                    />
	                    {stewardModeEnabled ? "Stewards" : "Tables"}
	                  </h2>
	                  <button
	                    onClick={() => togglePanel("tables")}
	                    className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
	                    title="Masquer le panneau des tables"
                  >
                    <ChevronLeft size={20} className="text-gray-600" />
                  </button>
                </div>

                {stewardModeEnabled && selectedStewardGroup && (
                  <button
                    type="button"
                    onClick={() => setSelectedStewardKey(null)}
                    className={`mb-4 flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-left font-semibold transition ${
                      isDarkTheme
                        ? "border-white/10 bg-white/5 text-white hover:border-amber-400/60"
                        : "border-slate-200 bg-slate-50 text-slate-900 hover:border-amber-400"
                    }`}
                  >
                    <ChevronLeft size={18} />
                    <span className="truncate">{selectedStewardGroup.display_name}</span>
                  </button>
                )}

                {stewardModeEnabled && !selectedStewardGroup && (
                  <StewardTableNavigator
                    groups={stewardGroups}
                    onSelect={setSelectedStewardKey}
                    isDarkTheme={isDarkTheme}
                  />
                )}

                {!stewardModeEnabled && floorPlanEnabled && visibleZones.length > 0 && (
                  <div
                    className="mb-5 flex gap-2 overflow-x-auto pb-1"
                    role="tablist"
                    aria-label="Aires du restaurant"
                  >
                    {visibleZones.map((zone) => {
                      const active =
                        String(zone.id) === String(selectedZoneId);
                      return (
                        <button
                          key={zone.id}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setSelectedZoneId(zone.id)}
                          className={`min-w-[8rem] flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${
                            active
                              ? "border-amber-400 bg-amber-400 text-slate-950 shadow-lg shadow-amber-500/20"
                              : isDarkTheme
                                ? "border-white/10 bg-white/10 text-slate-100 hover:border-amber-400/60"
                                : "border-slate-200 bg-slate-100 text-slate-800 hover:border-amber-400"
                          }`}
                        >
                          {zone.name}
                        </button>
                      );
                    })}
                  </div>
                )}

	                {(!stewardModeEnabled || selectedStewardGroup) && (
                    <div className="grid grid-cols-4 gap-3">
	                  {visibleTableCards.map((table) => (
                      <TableCard
                        key={table.id}
                        table={table}
                        displayName={getTableDisplayName(table)}
                        isSelected={selectedTable?.id === table.id}
                        isMerged={
                          Array.isArray(tableMerges[table.id]) &&
                          tableMerges[table.id].length > 0
                        }
                        customerCount={tableCustomerCounts[table.id]}
                        reservationLabel={
                          Number(table.reservation_user_id || 0) > 0 &&
                          Number(table.reservation_user_id) !== Number(currentUser?.id || 0)
                            ? table.reservation_name || "Autre utilisateur"
                            : ""
                        }
                        draftPendingCount={Number(
                          table.recovery_pending_count || 0,
                        )}
                        isChecking={
                          String(tableAccessCheckingId ?? "") ===
                          String(table.id)
                        }
                        onSelect={handleTableCardSelect}
                      />
                    ))}
                  {visibleTableCards.length === 0 && (
                    <div className="col-span-4 rounded-2xl border border-dashed border-white/15 px-4 py-8 text-center text-sm text-slate-400">
                      {!floorPlanHydrated && floorPlanLoadState === "error"
                        ? "Plan de salle momentanément indisponible. Reconnexion en cours…"
                        : floorPlanHydrated &&
                            floorPlanEnabled &&
                            visibleZones.length === 0
                          ? "Aucune salle n’est attribuée à votre profil."
                          : !floorPlanHydrated
                            ? "Chargement du plan de salle…"
                            : stewardModeEnabled
                              ? "Aucune table."
                              : "Aucune table dans cette aire."}
                    </div>
                  )}
                    </div>
                )}

                {/* Section des commandes à livrer */}
                {clientTabs.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-md font-semibold text-gray-700 flex items-center">
                        <Truck className="mr-2" size={16} />
                        Commandes à livrer
                      </h3>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200">
                        {getDeliveryCardsCount()}
                      </span>
                    </div>
                    {renderDeliveryTabsList({ focusMode: "view", scrollable: true })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Onglet En ligne */}
          {!isBoutique && activeTab === "online" && visiblePanels.online && (
            <div className="h-full p-4 animate-fadeIn flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2
                  className={`text-lg font-bold flex items-center ${
                    isDarkTheme ? "text-white" : "text-gray-800"
                  }`}
                >
                  <RestaurantWaiterIcon className="h-5 w-5 text-current mr-2" />
                  En ligne
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchOnlineOrders({ syncNow: true })}
                    className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                    title="Rafraîchir"
                  >
                    <RefreshCw
                      size={16}
                      className={onlineOrdersLoading ? "animate-spin text-gray-600" : "text-gray-600"}
                    />
                  </button>
                  <button
                    onClick={() => togglePanel("online")}
                    className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                    title="Masquer le panneau en ligne"
                  >
                    <ChevronLeft size={20} className="text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="mb-3 flex items-center justify-between">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDarkTheme
                      ? "bg-cyan-500/15 border border-cyan-400/30 text-cyan-100"
                      : "bg-cyan-50 border border-cyan-200 text-cyan-700"
                  }`}
                >
                  {onlineOrders.length} commande(s)
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    onlineUnreadCount > 0
                      ? "bg-rose-500/20 border border-rose-400/30 text-rose-100"
                      : isDarkTheme
                        ? "bg-white/5 border border-white/20 text-white/70"
                        : "bg-gray-50 border border-gray-200 text-gray-600"
                  }`}
                >
                  {onlineUnreadCount} en attente
                </span>
              </div>

              {onlineOrdersError && (
                <div className="mb-3 rounded-xl border border-red-300/40 bg-red-500/10 text-red-200 px-3 py-2 text-sm">
                  {onlineOrdersError}
                </div>
              )}

              <div className="flex-1 overflow-y-auto pr-1 space-y-3">
                {onlineOrders.length === 0 ? (
                  <div
                    className={`rounded-2xl border p-4 text-sm ${
                      isDarkTheme
                        ? "border-white/10 bg-white/5 text-white/70"
                        : "border-gray-200 bg-gray-50 text-gray-600"
                    }`}
                  >
                    Aucune commande en ligne active.
                  </div>
                ) : (
                  onlineOrders.map((order) => {
                    const orderId = Number(order?.id || 0);
                    const statusMeta = getOnlineOrderStatusMeta(order?.status_internal);
                    const itemCount = getOnlineOrderItemsCount(order);
                    const totalAmount = getOnlineOrderAmount(order);
                    const createdAt = order?.created_at
                      ? new Date(order.created_at).toLocaleString("fr-FR")
                      : "—";
                    const isPending =
                      String(order?.status_internal || "").toLowerCase() === "pending";
                    const isAccepted =
                      String(order?.status_internal || "").toLowerCase() === "accepted" ||
                      String(order?.status_internal || "").toLowerCase() === "in_kitchen";
                    const isReady =
                      String(order?.status_internal || "").toLowerCase() === "ready";

                    return (
                      <div
                        key={orderId}
                        className={`rounded-2xl border p-3 ${
                          isDarkTheme
                            ? "border-white/15 bg-white/5"
                            : "border-gray-200 bg-white"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-base font-bold text-white">
                              {getOnlineOrderDisplayCode(order)}
                            </p>
                            <p className="text-xs text-white/70 mt-1">
                              Table client: {order?.table_label_client || "Non précisée"}
                            </p>
                            <p className="text-xs text-white/60">{createdAt}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full font-semibold ${statusMeta.className}`}>
                            {statusMeta.label}
                          </span>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                          <div
                            className={`rounded-xl px-3 py-2 ${
                              isDarkTheme ? "bg-white/5 text-white/80" : "bg-gray-50 text-gray-700"
                            }`}
                          >
                            {itemCount} article(s)
                          </div>
                          <div
                            className={`rounded-xl px-3 py-2 text-right font-semibold ${
                              isDarkTheme ? "bg-white/5 text-emerald-200" : "bg-gray-50 text-emerald-700"
                            }`}
                          >
                            {totalAmount.toLocaleString("fr-FR")} F
                          </div>
                        </div>

                        {Array.isArray(order?.items) && order.items.length > 0 && (
                          <div
                            className={`mt-2 text-xs ${
                              isDarkTheme ? "text-white/70" : "text-gray-600"
                            }`}
                          >
                            {(order.items || [])
                              .slice(0, 2)
                              .map((item) => `${item?.quantity || 1}x ${getOnlineOrderItemName(item)}`)
                              .join(" • ")}
                            {order.items.length > 2 ? " • ..." : ""}
                          </div>
                        )}

                        <div className="mt-3 flex flex-wrap gap-2">
                          {isPending && canAcceptOnlineOrders && (
                            <button
                              onClick={() => openOnlineAcceptModal(order)}
                              disabled={onlineActionLoadingId === orderId}
                              className="px-3 py-2 rounded-xl bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 text-xs font-semibold hover:bg-emerald-500/30 transition disabled:opacity-60"
                            >
                              Accepter
                            </button>
                          )}
                          {isPending && canRejectOnlineOrders && (
                            <button
                              onClick={() =>
                                setOnlineRejectDraft({
                                  id: orderId,
                                  reason: onlineRejectDraft.id === orderId ? onlineRejectDraft.reason : "",
                                })
                              }
                              disabled={onlineActionLoadingId === orderId}
                              className="px-3 py-2 rounded-xl bg-rose-500/20 border border-rose-400/40 text-rose-100 text-xs font-semibold hover:bg-rose-500/30 transition disabled:opacity-60"
                            >
                              Refuser
                            </button>
                          )}
                          {isAccepted && canAcceptOnlineOrders && (
                            <button
                              onClick={() =>
                                submitOnlineOrderStatusChange(orderId, "mark-ready", "Commande marquée prête")
                              }
                              disabled={onlineActionLoadingId === orderId}
                              className="px-3 py-2 rounded-xl bg-indigo-500/20 border border-indigo-400/40 text-indigo-100 text-xs font-semibold hover:bg-indigo-500/30 transition disabled:opacity-60"
                            >
                              Marquer prêt
                            </button>
                          )}
                          {isReady && canAcceptOnlineOrders && (
                            <button
                              onClick={() =>
                                submitOnlineOrderStatusChange(orderId, "mark-served", "Commande marquée servie")
                              }
                              disabled={onlineActionLoadingId === orderId}
                              className="px-3 py-2 rounded-xl bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 text-xs font-semibold hover:bg-cyan-500/30 transition disabled:opacity-60"
                            >
                              Marquer servi
                            </button>
                          )}
                        </div>

                        {onlineRejectDraft.id === orderId && canRejectOnlineOrders && (
                          <div className="mt-3 space-y-2">
                            <textarea
                              value={onlineRejectDraft.reason}
                              onChange={(event) =>
                                setOnlineRejectDraft({
                                  id: orderId,
                                  reason: event.target.value,
                                })
                              }
                              rows={2}
                              placeholder="Motif du refus"
                              className={`w-full rounded-xl px-3 py-2 text-sm border focus:outline-none ${
                                isDarkTheme
                                  ? "bg-slate-900/70 border-white/20 text-white placeholder-white/40"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => setOnlineRejectDraft({ id: null, reason: "" })}
                                className="px-3 py-2 text-xs font-semibold rounded-xl border border-white/20 text-white/80 hover:bg-white/10"
                              >
                                Annuler
                              </button>
                              <button
                                onClick={() => submitOnlineOrderReject(orderId, onlineRejectDraft.reason)}
                                disabled={onlineActionLoadingId === orderId}
                                className="px-3 py-2 text-xs font-semibold rounded-xl bg-rose-500/25 border border-rose-400/40 text-rose-100 hover:bg-rose-500/35 disabled:opacity-60"
                              >
                                Confirmer le refus
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Onglet Calculatrice */}
          {activeTab === "calculator" && visiblePanels.calculator && (
            <div className="h-full p-6 animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <h2
                  className={`text-xl font-bold flex items-center ${
                    isDarkTheme ? "text-white" : "text-gray-800"
                  }`}
                >
                  <Calculator
                    className="mr-3"
                    size={24}
                    style={{ color: colors.primary }}
                  />
                  Calculatrice
                </h2>
                <button
                  onClick={() => togglePanel("calculator")}
                  className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                  title="Masquer le panneau calculatrice"
                >
                  <ChevronLeft size={20} className="text-gray-600" />
                </button>
              </div>
              {/* Écran de la calculatrice */}
              <div className="bg-gray-900 rounded-xl p-4 mb-4 shadow-inner">
                <div className="text-right text-white text-2xl font-mono min-h-[40px] flex items-center justify-end break-all">
                  {calculatorDisplay}
                </div>
              </div>

              {/* Boutons de la calculatrice */}
              <div className="grid grid-cols-4 gap-3">
                {/* Première ligne */}
                <button
                  onClick={() => handleCalculatorClear()}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  C
                </button>
                <button
                  onClick={() => handleCalculatorClearEntry()}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  CE
                </button>
                <button
                  onClick={() => handleCalculatorPercentage()}
                  className="bg-gray-600 hover:bg-gray-700 text-white rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  %
                </button>
                <button
                  onClick={() => handleCalculatorOperation("/")}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  ÷
                </button>

                {/* Deuxième ligne */}
                <button
                  onClick={() => handleCalculatorNumber("7")}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  7
                </button>
                <button
                  onClick={() => handleCalculatorNumber("8")}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  8
                </button>
                <button
                  onClick={() => handleCalculatorNumber("9")}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  9
                </button>
                <button
                  onClick={() => handleCalculatorOperation("*")}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  ×
                </button>

                {/* Troisième ligne */}
                <button
                  onClick={() => handleCalculatorNumber("4")}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  4
                </button>
                <button
                  onClick={() => handleCalculatorNumber("5")}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  5
                </button>
                <button
                  onClick={() => handleCalculatorNumber("6")}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  6
                </button>
                <button
                  onClick={() => handleCalculatorOperation("-")}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  −
                </button>

                {/* Quatrième ligne */}
                <button
                  onClick={() => handleCalculatorNumber("1")}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  1
                </button>
                <button
                  onClick={() => handleCalculatorNumber("2")}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  2
                </button>
                <button
                  onClick={() => handleCalculatorNumber("3")}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  3
                </button>
                <button
                  onClick={() => handleCalculatorOperation("+")}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  +
                </button>

                {/* Cinquième ligne */}
                <button
                  onClick={() => handleCalculatorNumber("0")}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95 col-span-2"
                >
                  0
                </button>
                <button
                  onClick={() => handleCalculatorDecimal()}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  ,
                </button>
                <button
                  onClick={() => handleCalculatorEquals()}
                  className="bg-green-500 hover:bg-green-600 text-white rounded-xl p-4 font-semibold transition-all duration-150 active:scale-95"
                >
                  =
                </button>
              </div>

              {/* Fonctions supplémentaires */}
              <div className="mt-4 p-3 bg-gray-50 rounded-xl">
                <div className="text-xs text-gray-600 mb-2 font-medium">
                  Raccourcis rapides :
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleCalculatorQuickFunction("sqrt")}
                    className="bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg p-2 text-sm font-medium transition-all duration-150 active:scale-95"
                  >
                    √
                  </button>
                  <button
                    onClick={() => handleCalculatorQuickFunction("square")}
                    className="bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg p-2 text-sm font-medium transition-all duration-150 active:scale-95"
                  >
                    x²
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Onglet Livraison/Sur Place */}
          {!isBoutique &&
            !isServerParticipationMode &&
            activeTab === "delivery" &&
            visiblePanels.delivery && (
            <div className="h-full p-6 overflow-y-auto animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <h2
                  className={`text-xl font-bold flex items-center ${
                    isDarkTheme ? "text-white" : "text-gray-800"
                  }`}
                >
                  <Truck
                    className="mr-3"
                    size={24}
                    style={{ color: colors.primary }}
                  />
                  Livraison
                </h2>
                <button
                  onClick={() => togglePanel("delivery")}
                  className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                  title="Masquer le panneau livraison"
                >
                  <ChevronLeft size={20} className="text-gray-600" />
                </button>
              </div>

              {/* Sélection du mode de service */}
              <div className="mb-6">
                <h3
                  className={`text-lg font-semibold mb-3 ${
                    isDarkTheme ? "text-white" : "text-gray-700"
                  }`}
                >
                  Mode de service
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleServiceModeSelect("delivery")}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center space-y-2 ${
                      deliveryMode === "delivery"
                        ? isDarkTheme
                          ? "border-blue-300 bg-blue-500/10 text-white"
                          : "border-blue-500 bg-blue-50 text-blue-700"
                        : isDarkTheme
                          ? "border-white/15 hover:border-white/25 text-white/70"
                          : "border-gray-200 hover:border-gray-300 text-gray-600"
                    }`}
                  >
                    <Truck size={24} />
                    <span className="font-medium">Livraison</span>
                  </button>
                  <button
                    onClick={() => handleServiceModeSelect("takeaway")}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center space-y-2 ${
                      deliveryMode === "takeaway"
                        ? isDarkTheme
                          ? "border-emerald-300 bg-emerald-500/10 text-white"
                          : "border-green-500 bg-green-50 text-green-700"
                        : isDarkTheme
                          ? "border-white/15 hover:border-white/25 text-white/70"
                          : "border-gray-200 hover:border-gray-300 text-gray-600"
                    }`}
                  >
                    <ShoppingCart size={24} />
                    <span className="font-medium">À emporter</span>
                  </button>
                </div>
              </div>

              {/* Configuration pour livraison */}
              {deliveryMode === "delivery" && (
                <div className="space-y-4">
                  {/* Méthode de livraison */}
                  <div>
                    <h4 className="text-md font-medium mb-2 text-gray-700">
                      Méthode de livraison
                    </h4>
                    <select
                      value={deliveryMethod}
                      onChange={(e) => {
                        setDeliveryMethod(e.target.value);
                        setDeliveryRef(""); // Reset ref when changing method
                      }}
                      className={`w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDarkTheme
                          ? "border-white/15 bg-slate-900/80 text-white"
                          : "border-gray-300 bg-white text-gray-900"
                      }`}
                    >
                      {deliveryMethods.map((method) => (
                        <option key={method} value={method}>
                          {method}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Champ Référence pour les méthodes non-Direct */}
                  {deliveryMethod !== "Direct" && (
                    <div className="space-y-3">
                      <h4 className="text-md font-medium mb-2 text-gray-700">
                        Référence {deliveryMethod}
                      </h4>
                      <input
                        type="text"
                        placeholder={`Saisir la référence ${deliveryMethod}...`}
                        value={deliveryRef}
                        onChange={(e) => setDeliveryRef(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter" && deliveryRef.trim()) {
                            createDeliveryOrder();
                          }
                        }}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />

                      {/* Bouton de validation pour créer l'onglet */}
                      <button
                        onClick={createDeliveryOrder}
                        disabled={!deliveryRef.trim()}
                        className={`w-full p-4 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
                          deliveryRef.trim()
                            ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        <Plus size={20} />
                        <span>
                          Créer commande{" "}
                          {deliveryMethodCodes[deliveryMethod] ||
                            deliveryMethod}
                          {deliveryRef}
                        </span>
                      </button>
                    </div>
                  )}

                  {/* Sélection du client (pour livraison Direct) */}
                  {deliveryMethod === "Direct" && (
                    <div>
                      <h4 className="text-md font-medium mb-2 text-gray-700">
                        Client
                      </h4>
                      <div className="space-y-3">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Rechercher un client par nom ou téléphone..."
                            value={deliveryCustomerSearch}
                            onChange={(e) => {
                              setDeliveryCustomerSearch(e.target.value);
                              // Debounce la recherche avec un délai plus court pour une meilleure réactivité
                              clearTimeout(window.searchTimeout);
                              window.searchTimeout = setTimeout(() => {
                                searchDeliveryCustomers(e.target.value);
                              }, 200);
                            }}
                            onFocus={() => {
                              if (
                                !deliveryCustomerSearch &&
                                deliveryCustomers.length === 0
                              ) {
                                loadRecentCustomers();
                              }
                            }}
                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                          />
                          <Search
                            className="absolute right-3 top-3 text-gray-400"
                            size={20}
                          />
                        </div>
                      </div>

                      {/* Résultats de recherche */}
                      {deliveryCustomers.length > 0 && (
                        <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-xl bg-white shadow-lg">
                          <div className="p-2 bg-gray-50 border-b text-xs text-gray-600 font-medium">
                            {deliveryCustomerSearch
                              ? `Résultats pour "${deliveryCustomerSearch}"`
                              : "Clients récents"}
                          </div>
                          {deliveryCustomers.map((customer) => (
                            <button
                              key={customer.id}
                              onClick={() => {
                                cashDebugLog("🎯 Client sélectionné:", customer);
                                setDeliveryCustomer(customer);
                                setDeliveryCustomerSearch(
                                  `${customer.first_name} ${customer.last_name} - ${customer.phone}`,
                                );
                                setDeliveryCustomers([]);
                                triggerSuccessAnimation(
                                  `Client ${customer.first_name} ${customer.last_name} sélectionné`,
                                );

                                // Automatiquement créer le panier de livraison pour la méthode directe
                                if (
                                  deliveryMode === "delivery" &&
                                  deliveryMethod === "Direct"
                                ) {
                                  // Passer directement le client sélectionné pour éviter les problèmes de state asynchrone
                                  createDeliveryOrder(customer);
                                }
                              }}
                              className="w-full p-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-all"
                            >
                              <div className="font-medium text-gray-800">
                                {customer.civility} {customer.first_name}{" "}
                                {customer.last_name}
                              </div>
                              <div className="text-sm text-gray-600">
                                {customer.phone}
                              </div>
                              {customer.email && (
                                <div className="text-xs text-gray-500">
                                  {customer.email}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Bouton pour ajouter un nouveau client */}
                      {deliveryCustomerSearch &&
                        deliveryCustomers.length === 0 &&
                        deliveryCustomerSearch.length >= 1 && (
                          <div className="mt-2 space-y-2">
                            <div className="text-sm text-gray-500 text-center">
                              Aucun client trouvé pour "{deliveryCustomerSearch}
                              "
                            </div>
                            <button
                              onClick={() =>
                                openCustomerModal("delivery", "add")
                              }
                              className="w-full p-3 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center space-x-2"
                            >
                              <UserPlus size={20} />
                              <span>Ajouter un nouveau client</span>
                            </button>
                          </div>
                        )}

                      {/* Bouton permanent pour créer un client */}
                      {!deliveryCustomerSearch &&
                        deliveryCustomers.length === 0 && (
                          <button
                            onClick={() =>
                              openCustomerModal("delivery", "add")
                            }
                            className="mt-2 w-full p-3 border-2 border-dashed border-green-300 rounded-xl text-green-600 hover:bg-green-50 transition-all flex items-center justify-center space-x-2"
                          >
                            <UserPlus size={20} />
                            <span>Créer un nouveau client</span>
                          </button>
                        )}

                    </div>
                  )}
                </div>
              )}

              {/* Bouton supprimé - cliquer sur le client crée maintenant directement un onglet */}

              {/* Section des commandes de livraison en cours */}
              {clientTabs.length > 0 && (
                <div className="mt-6">
                  <button
                    onClick={() => setShowDeliveryList((prev) => !prev)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-2xl border-2 border-gray-200 bg-white text-left hover:border-blue-300 transition-all"
                  >
                    <span className="font-semibold text-gray-700 flex items-center">
                      <Truck className="mr-2" size={16} />
                      Commandes à livrer
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200">
                        {getDeliveryCardsCount()}
                      </span>
                      <ChevronRight
                        size={18}
                        className={`transform transition-transform ${
                          showDeliveryList ? "rotate-90" : ""
                        }`}
                      />
                    </div>
                  </button>
                  {showDeliveryList && (
                    <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-3">
                      {renderDeliveryTabsList({
                        focusMode: "add",
                        scrollable: true,
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Onglet Réservations */}
          {!isBoutique &&
            !isServerParticipationMode &&
            activeTab === "reservations" &&
            visiblePanels.reservations && (
            <div className="h-full p-6 animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <h2
                  className={`text-xl font-bold flex items-center ${
                    isDarkTheme ? "text-white" : "text-gray-800"
                  }`}
                >
                  <Calendar
                    className="mr-3"
                    size={24}
                    style={{ color: colors.primary }}
                  />
                  Réservations
                </h2>
                <button
                  onClick={() => togglePanel("reservations")}
                  className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                  title="Masquer le panneau réservations"
                >
                  <ChevronLeft size={20} className="text-gray-600" />
                </button>
              </div>
              <div className="text-center py-12 text-gray-500">
                <Calendar size={64} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg">Système de réservation</p>
                <p className="text-sm">Bientôt disponible</p>
              </div>
            </div>
          )}

          {/* Onglet Clients */}
          {!isServerParticipationMode &&
            activeTab === "customers" &&
            visiblePanels.customers && (
            <div className="h-full p-6 animate-fadeIn flex flex-col">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-4">
                <div>
                <h2
                  className={`text-xl font-bold flex items-center ${
                    isDarkTheme ? "text-white" : "text-gray-800"
                  }`}
                >
                  <UserPlus
                    className="mr-3"
                    size={24}
                    style={{ color: colors.primary }}
                  />
                    Clients
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => togglePanel("customers")}
                    className="p-2 rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition"
                    title="Masquer le panneau clients"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={() => openCustomerModal("general", "list")}
                    className="px-3 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 text-white shadow-lg hover:shadow-xl transition flex items-center justify-center"
                    title="Répertoire clients"
                  >
                    <Search size={18} />
                    <span className="sr-only">Répertoire clients</span>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto pr-1 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      if (!creditSaleEnabled) return;
                      setShowCreditRecoveryModal(true);
                    }}
                    disabled={!creditSaleEnabled}
                    className={`w-full rounded-2xl border transition p-4 text-left ${
                      creditSaleEnabled
                        ? "bg-white border-gray-200 hover:border-emerald-300 hover:shadow-md"
                        : "bg-gray-100 border-gray-200 opacity-70 cursor-not-allowed"
                    }`}
                  >
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                      Crédit
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      Payer un crédit
                    </p>
                    {!creditSaleEnabled && (
                      <p className="text-xs text-gray-500 mt-1">
                        Activez d'abord la vente à crédit.
                      </p>
                    )}
                  </button>
                  <button
                    onClick={() => openCustomerModal("general", "add")}
                    className="w-full rounded-2xl bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:shadow-lg transition p-4 text-left"
                  >
                    <p className="text-xs uppercase tracking-wide text-white/80 mb-1">
                      Création
                    </p>
                    <p className="text-lg font-semibold">Créer un client</p>
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {selectedCustomerForOrder && (
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">
                            Client lié au ticket
                          </p>
                          <p className="text-base font-semibold text-gray-900">
                            {`${selectedCustomerForOrder.civility || ""} ${selectedCustomerForOrder.first_name || ""} ${selectedCustomerForOrder.last_name || ""}`.trim()}
                          </p>
                        </div>
                        <button
                          onClick={() => setSelectedCustomerForOrder(null)}
                          className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 px-3 py-1 rounded-full hover:bg-red-100 transition"
                        >
                          Retirer
                        </button>
                      </div>
                      <div className="space-y-2 text-sm text-gray-600">
                        <p>📞 {selectedCustomerForOrder.phone || "Non renseigné"}</p>
                        {selectedCustomerForOrder.email && (
                          <p>Email: {selectedCustomerForOrder.email}</p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                            Ticket en cours
                          </span>
                          <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">
                            Fidélité locale
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Onglet Profil */}
          {activeTab === "profile" && visiblePanels.profile && (
            <div className="h-full p-6 animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center text-gray-800">
                  <User
                    className="mr-3"
                    size={24}
                    style={{ color: colors.primary }}
                  />
                  Profil utilisateur
                </h2>
                <button
                  onClick={() => togglePanel("profile")}
                  className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                  title="Masquer le panneau profil"
                >
                  <ChevronLeft size={20} className="text-gray-600" />
                </button>
              </div>
              <div className="text-center py-12 text-gray-500">
                <User size={64} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg">Gestion du profil utilisateur</p>
                <p className="text-sm">Bientôt disponible</p>
              </div>
            </div>
          )}

          {/* Onglet Paramètres */}
          {activeTab === "settings" && visiblePanels.settings && (
            <div className="h-full animate-fadeIn">
              <Settings />
            </div>
          )}
        </div>

        {/* Section Centrale - Menu des Produits */}
        <div
          ref={menuAreaRef}
          className={`caisse-products-section transition-[opacity,transform,box-shadow] duration-300 ease-in-out bg-white rounded-3xl p-6 shadow-xl ${
            Object.values(visiblePanels).some(Boolean)
              ? "flex-1"
              : "flex-1 lg:flex-[2]"
          }`}
        >
          {/* Header avec boutons toggle pour tous les panneaux */}
          <div className="flex items-center justify-between mb-6">
	            <div className="flex items-center space-x-4">
	              <h2
                  className={`text-xl font-bold flex items-center ${
                    isDarkTheme ? "text-white" : "text-gray-800"
                  }`}
                >
                <ChefHat
                  className="mr-3"
                  size={24}
                  style={{ color: colors.primary }}
                />
                {isBoutique
                  ? "Catalogue Boutique"
                  : isCounterContext
                    ? "Comptoir"
                    : (
                      <>
                        <span className="restaurant-menu-label-desktop">
                          Menu du Restaurant
                        </span>
                        <span className="restaurant-menu-label-mobile">Menu</span>
                      </>
                    )}
              </h2>
              {/* Delivery indicator removed as requested */}
            </div>
            <div className="flex items-center space-x-2 animate-slide-in-top">
              {(courseModeEligibleForTable(selectedTable) ||
                (courseModeEnabled && courseModeContextForTable(selectedTable))) && (
                <>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={courseModeEnabled}
                    onClick={toggleCourseModeForCurrentTable}
                    className="toggle-button course-mode-switch flex items-center gap-2 rounded-xl bg-slate-700 px-2.5 py-2 text-white shadow-lg transition-all hover:bg-slate-600"
                    title="Activer ou désactiver la commande en plusieurs tours"
                  >
                    <RotateCcw
                      size={17}
                      className={courseModeEnabled ? "text-amber-300" : "text-white/75"}
                    />
                    <span
                      aria-hidden="true"
                      className={`relative h-5 w-9 rounded-full transition-colors ${
                        courseModeEnabled ? "bg-amber-400" : "bg-slate-500"
                      }`}
                    >
                      <span
                        className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                          courseModeEnabled ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </span>
                  </button>
                  {courseModeEnabled && (
                    <button
                      type="button"
                      onClick={createNextCourse}
                      disabled={!courseManagementAvailable}
                      className="toggle-button flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-500"
                      title={
                        courseManagementAvailable
                          ? "Passer à la sélection du tour suivant"
                          : "Création de nouveaux tours indisponible dans le forfait actuel"
                      }
                    >
                      <RotateCcw size={16} />
                      <span>Tour {displayedCourseNumber}</span>
                    </button>
                  )}
                </>
              )}
              {!isBoutique && !isFastFoodMode && !visiblePanels.tables && (
                <button
                  onClick={() => showPanelExclusive("tables")}
                  className="toggle-button server-menu-redundant-toggle flex items-center space-x-1 px-3 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl shadow-lg"
                  data-panel-toggle="tables"
                  title="Afficher les tables"
                >
                  <Users size={16} />
                  <span className="text-sm font-medium">Tables</span>
                </button>
              )}
              {!isBoutique && isHybridMode && !isServerParticipationMode && (
                <button
                  onClick={isCounterContext ? leaveCounterContext : enterCounterContext}
                  className={`toggle-button flex items-center space-x-1 px-3 py-2 rounded-xl shadow-lg ${
                    isCounterContext
                      ? "bg-gradient-to-r from-emerald-500 to-cyan-500 text-white"
                      : "bg-gradient-to-r from-teal-500 to-emerald-500 text-white"
                  }`}
                  title={isCounterContext ? "Revenir aux Tables" : "Ouvrir le Comptoir"}
                  data-panel-toggle="counter"
                >
                  <ShoppingBag size={16} />
                  <span className="text-sm font-medium">Comptoir</span>
                </button>
              )}
              {!isBoutique && onlineMenuOrderingEnabled && !visiblePanels.online && (
                <button
                  onClick={() => showPanelExclusive("online")}
                  className="toggle-button relative flex items-center space-x-1 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl shadow-lg"
                  data-panel-toggle="online"
                  title="Afficher commandes en ligne"
                >
                  <RestaurantWaiterIcon className="h-4 w-4 text-current" />
                  <span className="text-sm font-medium">En ligne</span>
                  {onlineUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-bold">
                      {onlineUnreadCount > 99 ? "99+" : onlineUnreadCount}
                    </span>
                  )}
                </button>
              )}
              {!isBoutique &&
                !isServerParticipationMode &&
                aggregatorSettings.aggregators &&
                ["yango", "glovo"].map((code) => {
                  if (!aggregatorSettings[code]) return null;
                  const meta = AGGREGATOR_ORDER_META[code];
                  const isActive = activeAggregator === code;
                  return (
                    <button
                      key={`top-${code}`}
                      onClick={() => toggleAggregatorOrder(code)}
                      className={`toggle-button flex items-center justify-center min-w-[92px] px-4 py-2 rounded-xl shadow-lg transition-all ${
                        isActive
                          ? "ring-2 ring-white/90 -translate-y-0.5"
                          : "hover:-translate-y-0.5"
                      }`}
                      style={{
                        backgroundColor: meta.backgroundColor,
                        color: meta.textColor,
                      }}
                      data-panel-toggle={`aggregator-${code}`}
                      title={`Commande ${meta.label}`}
                    >
                      <img
                        src={meta.logo}
                        alt={meta.label}
                        className="h-5 w-auto max-w-[68px] object-contain"
                        draggable="false"
                      />
                      <span className="sr-only">{meta.label}</span>
                    </button>
                  );
                })}
              {!isServerParticipationMode && !visiblePanels.customers && (
                <button
                  onClick={() => showPanelExclusive("customers")}
                  className="toggle-button flex items-center space-x-1 px-3 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl shadow-lg"
                  data-panel-toggle="customers"
                  title="Afficher gestion clients"
                >
                  <UserPlus size={16} />
                  <span className="text-sm font-medium">Clients</span>
                </button>
              )}
            </div>
          </div>

          {/* Barre de recherche */}
          <div className="mb-6">
            <div className="relative">
              <Search
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder={
                  isBoutique
                    ? "Rechercher un produit ou un SKU..."
                    : "Rechercher un plat ou une boisson..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-2xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none text-lg transition-all"
              />
            </div>
          </div>

          {/* Catégories */}
          <div className="caisse-categories">
            <div className="caisse-categories-scroll flex space-x-3 pb-2">
              {/* Bouton "Toutes les catégories" */}
              <button
                onClick={() => {
                  setShowAllCategories(!showAllCategories);
                  setSelectedCategory("all");
                }}
                className={`px-6 py-3 rounded-2xl font-semibold transition-all transform hover:scale-105 whitespace-nowrap ${
                  showAllCategories
                    ? isDarkTheme
                      ? "bg-amber-500 text-slate-900 shadow-lg shadow-amber-500/30"
                      : "text-white shadow-lg"
                    : isDarkTheme
                      ? "bg-[#111827] text-white/80"
                      : "opacity-80 text-white"
                }`}
                style={
                  showAllCategories || !isDarkTheme
                    ? {
                        backgroundColor: showAllCategories
                          ? colors.accent
                          : colors.dark,
                        boxShadow: showAllCategories
                          ? `0 8px 25px ${colors.accent}40`
                          : undefined,
                      }
                    : {
                        boxShadow: undefined,
                      }
                }
              >
                Toutes les catégories
              </button>

	              {activeCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setShowAllCategories(false);
                  }}
                  className={`px-6 py-3 rounded-2xl font-semibold transition-all transform hover:scale-105 whitespace-nowrap ${
                    selectedCategory === category.id && !showAllCategories
                      ? isDarkTheme
                        ? "bg-amber-500 text-slate-900 shadow-lg shadow-amber-500/30"
                        : "shadow-lg text-white"
                      : isDarkTheme
                        ? "bg-[#111827] text-white/80"
                        : "opacity-80 text-white"
                  }`}
                  style={
                    selectedCategory === category.id && !showAllCategories
                      ? {
                          backgroundColor: isDarkTheme
                            ? colors.accent
                            : category.color,
                          boxShadow: isDarkTheme
                            ? `0 8px 25px ${colors.accent}40`
                            : `0 8px 25px ${category.color}40`,
                        }
                      : isDarkTheme
                        ? {
                            border: `1px solid ${category.color}`,
                          }
                        : { backgroundColor: category.color }
                  }
                >
                  {!isBoutique && <span className="mr-2">{category.icon}</span>}
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Container avec scroll vertical pour les produits - hauteur fixe optimisée */}
          <div className="flex-1 flex flex-col min-h-0 relative overflow-hidden">
            {/* Grille des produits avec scroll vertical amélioré */}
            <div className="caisse-products-grid pr-2">
              <div
                className="caisse-server-products-grid grid grid-cols-5 gap-4 pb-16"
                style={{ minHeight: "100%" }}
              >
                {paginatedProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    isDarkTheme={isDarkTheme}
                    isBoutique={isBoutique}
                    isDisabled={!selectedTable && !isBoutique && !canOrderWithoutTable}
                    priceLabel={
                      product.product_type === "variable" &&
                      Array.isArray(product.variations) &&
                      product.variations.length > 0
                        ? (() => {
                            const { min, max } = getProductPriceRangeForMode(
                              product,
                              activeAggregator,
                            );
                            if (min === max) {
                              return `${min.toLocaleString("fr-FR")} F`;
                            }
                            return `${min.toLocaleString("fr-FR")} - ${max.toLocaleString("fr-FR")} F`;
                          })()
                        : `${getProductDisplayPriceForMode(product, activeAggregator).toLocaleString("fr-FR")} F`
                    }
                    primaryColor={colors.primary}
                    secondaryColor={colors.secondary}
                    tertiaryColor={colors.tertiary}
                    onSelect={handleProductCardSelect}
                  />
                ))}
              </div>
            </div>

            {/* Pagination améliorée en bas à droite - position absolue */}
            {totalPages > 1 && (
              <div
                className={`absolute bottom-2 right-2 rounded-2xl shadow-lg border p-3 z-10 ${
                  isDarkTheme
                    ? "bg-[#0f172a]/95 backdrop-blur-sm border-white/10 text-white"
                    : "bg-white/95 backdrop-blur-sm border-gray-200"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="text-xs font-medium text-gray-700">
                    <span className="bg-gradient-to-r from-orange-400 to-yellow-500 bg-clip-text text-transparent font-bold">
                      {filteredProducts.length}
                    </span>
                    <span className="mx-1 text-gray-500">•</span>
                    <span className="text-gray-600">
                      {currentPage}/{totalPages}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
	                    <button
	                      onClick={() =>
	                        setCurrentPage((prev) => Math.max(prev - 1, 1))
	                      }
	                      disabled={currentPage === 1}
	                      className="caisse-pagination-btn w-8 h-8 rounded-lg bg-white border border-gray-300 hover:border-orange-400 hover:bg-orange-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
	                      title="Page précédente"
	                    >
	                      <ChevronLeft size={14} className="text-gray-600" />
	                    </button>

                    <div className="flex space-x-0.5">
                      {[...Array(Math.min(5, totalPages))].map((_, index) => {
                        const pageNumber =
                          currentPage <= 3
                            ? index + 1
                            : currentPage >= totalPages - 2
                              ? totalPages - 4 + index
                              : currentPage - 2 + index;

                        if (pageNumber < 1 || pageNumber > totalPages)
                          return null;

                        return (
	                          <button
	                            key={pageNumber}
	                            onClick={() => setCurrentPage(pageNumber)}
	                            className={`caisse-pagination-btn w-8 h-8 rounded-lg text-xs font-bold transition-all duration-200 ${
	                              currentPage === pageNumber
	                                ? "text-white shadow-md"
	                                : "bg-white text-slate-900 border border-gray-300 hover:border-orange-400 hover:bg-orange-50"
	                            }`}
	                            style={
	                              currentPage === pageNumber
	                                ? {
	                                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                                    boxShadow: `0 2px 8px ${colors.primary}30`,
                                  }
                                : {}
                            }
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                    </div>

	                    <button
	                      onClick={() =>
	                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
	                      }
	                      disabled={currentPage === totalPages}
	                      className="caisse-pagination-btn w-8 h-8 rounded-lg bg-white border border-gray-300 hover:border-orange-400 hover:bg-orange-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
	                      title="Page suivante"
	                    >
	                      <ChevronRight size={14} className="text-gray-600" />
	                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Message si aucun produit trouvé */}
          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <Search size={56} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-2xl font-bold text-gray-600 mb-2">
                Aucun produit trouvé
              </h3>
              <p className="text-gray-500">
                {searchTerm
                  ? `Aucun produit ne correspond à "${searchTerm}"`
                  : "Aucun produit dans cette catégorie"}
              </p>
            </div>
          )}
        </div>

        {/* Section Droite - Menu et Panier */}
        <div
          className={`caisse-cart transition-[opacity,transform,width,box-shadow] duration-300 ease-in-out rounded-3xl shadow-xl ${
            isDarkTheme
              ? "bg-[#0f172a] border border-white/5 text-white"
              : "bg-white"
          } ${
            Object.values(visiblePanels).some(Boolean)
              ? "w-96"
              : "w-80 lg:w-96 xl:w-[28rem]"
          }`}
        >
          {/* Header fixe */}
          <div className="caisse-cart-header">
            {selectedTableAccessConflict && (
              <div className="mb-3 rounded-xl border border-red-400/40 bg-red-500/15 px-3 py-2 text-sm font-semibold text-red-200">
                <div>
                  {formatTableAccessConflictMessage(
                    selectedTableAccessConflict,
                    { withRecoveryHint: true },
                  )}
                </div>
                {selectedTableAccessConflict.sameUserOtherDevice && (
                  <button
                    type="button"
                    onClick={reclaimOwnSelectedTable}
                    disabled={isReclaimingOwnTable}
                    className="mt-2 rounded-lg border border-red-200/40 bg-red-100/10 px-3 py-1.5 text-xs font-bold text-red-100 disabled hover:bg-red-100/20 disabled:cursor-wait disabled:opacity-60"
                  >
                    {isReclaimingOwnTable ? "Reprise…" : "Reprendre sur cet appareil"}
                  </button>
                )}
              </div>
            )}
            <div className="flex justify-between items-center mb-4">
              <div className="flex flex-col">
                <h2
                  className={`text-xl font-bold flex items-center ${
                    isDarkTheme ? "text-white" : "text-gray-800"
                  }`}
                >
                  <ShoppingCart
                    className="mr-3"
                    size={24}
                    style={{ color: colors.primary }}
                  />
                  {selectedTable?.isDelivery
                    ? getDeliveryCartTitle()
          : "En cours"}
                </h2>
                {/* Indicateur de livraison */}
                {selectedTable?.isDelivery && (
                  <div
                    className={`mt-1 px-2 py-1 rounded-lg text-xs font-medium flex items-center space-x-1 ${
                      selectedTable.deliveryMode === "delivery"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    <Truck size={12} />
                    <span>
                      {selectedTable.deliveryMode === "delivery"
                        ? "Livraison"
                        : "À emporter"}
                    </span>
                    {selectedTable.deliveryMethod === "Direct" &&
                      (selectedTable.deliveryCustomer ||
                        selectedDeliveryOrder?.customer) && (
                        <span className="opacity-75">
                          •{" "}
                          {formatDeliveryCustomerName(
                            selectedTable.deliveryCustomer ||
                              selectedDeliveryOrder?.customer,
                          )}
                        </span>
                      )}
                    {selectedTable.deliveryMethod !== "Direct" &&
                      getSelectedDeliveryReference() && (
                        <span className="opacity-75">
                          •{" "}
                          {deliveryMethodCodes[selectedTable.deliveryMethod] ||
                            selectedTable.deliveryMethod}
                          {getSelectedDeliveryReference()}
                        </span>
                      )}
                  </div>
                )}
              </div>

              {/* Boutons d'action */}
              <div className="flex space-x-2">
                {/* Masquer le bouton d'ajout de contact pour les livraisons */}
                {!selectedTable?.isDelivery && !isServerParticipationMode && (
                  <button
                    onClick={() => openCustomerModal("general", "list")}
                    className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
                    title="Associer un client"
                  >
                    <UserPlus size={20} style={{ color: colors.dark }} />
                  </button>
                )}

                <button
                  onClick={() => {
                    const canCancel = canCancelCart();
                    const context = { mode: "cart", item: null };
                    if (!canCancel) {
                      handlePermissionDenied(
                        "Vous ne pouvez pas procéder à l'annulation, faites appel au manager.",
                        context,
                      );
                      return;
                    }
                    openManagerApproval("cart");
                  }}
                  className="p-2 rounded-xl bg-red-100 hover:bg-red-200 transition-colors"
                  title="Annuler tout le panier"
                  disabled={
                    getCartItemsForCancellationCount() === 0 ||
                    updatingStatus ||
                    Boolean(selectedTableAccessConflict)
                  }
                >
                  <X
                    size={20}
                    style={{
                      color:
                        getCartItemsForCancellationCount() > 0
                          ? "#dc2626"
                          : "#9ca3af",
                    }}
                  />
                </button>


              </div>
            </div>
          </div>

          {/* Contenu scrollable */}
          <div className="caisse-cart-content">
            {/* Indication si aucune table n'est sélectionnée */}
            {!isBoutique && !selectedTable && (!canOrderWithoutTable || cartIsEmpty) && (
              <div
                className={`mt-3 mb-6 p-4 rounded-2xl text-center animate-pulse ${
                  isDarkTheme
                    ? "bg-amber-500/10 border border-amber-400/40 text-amber-50"
                    : "bg-orange-50 border-2 border-orange-200"
                }`}
              >
                <div className="mb-2 flex justify-center" aria-hidden="true">
                  <Utensils size={38} />
                </div>
                <h3
                  className={`text-lg font-bold mb-1 ${
                    isDarkTheme ? "text-amber-100" : "text-orange-800"
                  }`}
                >
                  {isAggregatorOrderMode
                    ? `Commande ${AGGREGATOR_ORDER_META[activeAggregator]?.label || "agrégateur"}`
                    : isFastFoodMode
                      ? "Commande comptoir"
                      : "Aucune table sélectionnée"}
                </h3>
                <p
                  className={`text-sm ${
                    isDarkTheme ? "text-amber-100/80" : "text-orange-700"
                  }`}
                >
                  {isAggregatorOrderMode
                    ? `Ajoutez des articles pour encaisser une commande ${AGGREGATOR_ORDER_META[activeAggregator]?.label || "agrégateur"}.`
                    : isFastFoodMode
                    ? "Ajoutez des articles pour encaisser une commande fast-food."
                    : "Sélectionnez une table dans la liste à gauche pour commencer votre commande"}
                </p>
              </div>
            )}

            {/* Table info section - only for non-delivery tables */}
            {selectedTable && !selectedTable.isDelivery && !isBoutique && (
              <div
                className={`mt-3 mb-6 p-4 rounded-2xl shadow-lg flex items-center justify-between gap-3 ${
                  isDarkTheme
                    ? "bg-white/5 border border-white/10 text-white"
                    : "text-white"
                }`}
                style={
                  isDarkTheme
                    ? undefined
                    : {
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
                      }
                }
              >
                <div className="flex-1 space-y-2 mt-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-[0.25em] text-white/60">
                        Table
                      </div>
                      <div className="font-bold text-xl flex items-center gap-2">
                        <span>{getTableDisplayName(selectedTable)}</span>
                      </div>
                    </div>
                    <div className="text-right text-xs text-white/70 space-y-1">
                      <div className="flex items-center justify-end gap-1">
                        <Package className="w-3.5 h-3.5" />
                        <span>{getCartTotalQuantity()} article(s)</span>
                      </div>
                      {getSelectedTableServerDisplayName() && (
                        <div className="flex items-center justify-end gap-1">
                          <Users className="w-3.5 h-3.5" />
                          <span>Serveur : {getSelectedTableServerDisplayName()}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedTable.customerCount && (
                    <div className="text-xs text-white/80">
                      {selectedTable.customerCount} Pers.
                    </div>
                  )}

                  {tableMerges[selectedTable.id] &&
                    tableMerges[selectedTable.id].length > 0 && (
                      <div className="flex items-center flex-wrap gap-2 pt-1">
                        <button
                          type="button"
                          onClick={handleTableUnmerge}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-white/10 border border-white/15 hover:bg-white/15 hover:border-amber-300/60 transition"
                        >
                          🔗 Fusionnée avec T
                          {tableMerges[selectedTable.id].join(", T")}
                        </button>
                        <span className="inline-flex items-center text-[11px] px-2 py-1 rounded-full bg-amber-500/20 border border-amber-300/40 text-amber-100">
                          {tableMerges[selectedTable.id].length + 1} tables
                        </span>
                      </div>
                    )}
                </div>

                {["caissier", "cashier", "caisse", "manager", "admin"].includes(
                  getCurrentRole(),
                ) && (
                  <div className="flex space-x-2">
                    {/* Masquer le bouton de fusion pour les livraisons */}
                    {!selectedTable?.isDelivery && (
                      <button
                        onClick={handleTableMerge}
                        className="p-2 rounded-xl bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all duration-200 group"
                        title="Fusionner avec une autre table"
                      >
                        <GitMerge className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      </button>
                    )}
                    <button
                      onClick={handleTableTransfer}
                      className="p-2 rounded-xl bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all duration-200 group"
                      title="Transférer vers une autre table"
                    >
                      <ArrowLeftRight className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Informations Client */}
            {!isServerParticipationMode && selectedCustomerForOrder && (
              <div
                className="mb-6 p-4 rounded-2xl text-white shadow-lg"
                style={{
                  background: `linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)`,
                }}
              >
                <div className="font-bold text-lg flex items-center">
                  <UserPlus className="mr-2" size={18} />
                  Client Associé
                </div>
                <div className="text-white/90 text-sm mt-1">
                  {selectedCustomerForOrder.civility}{" "}
                  {selectedCustomerForOrder.first_name}{" "}
                  {selectedCustomerForOrder.last_name}
                </div>
                <div className="text-white/80 text-sm">
                  📞 {selectedCustomerForOrder.phone}
                </div>
                {selectedCustomerForOrder.email && (
                  <div className="text-white/80 text-sm">
                    ✉�? {selectedCustomerForOrder.email}
                  </div>
                )}
                <div className="flex justify-between items-center mt-2">
                  <span className="text-white/70 text-xs">
                    Client #{selectedCustomerForOrder.id}
                  </span>
                  <button
                    onClick={removeCustomerFromOrder}
                    className="text-white/80 hover:text-white text-xs flex items-center"
                  >
                    <X size={14} className="mr-1" />
                    Retirer
                  </button>
                </div>
              </div>
            )}

            {/* Zone scrollable du contenu panier */}
            {(selectedTable || canOrderWithoutTable) && (
              <div key={cartRenderKey} className="space-y-3">
              {(() => {
                const isDeliveryOrder = cartViewModel.isDeliveryOrder;
                const shouldShowExistingSection =
                  cartExistingItems.length > 0;

                if (cartIsEmpty) {
                  return (
                    <div className="text-center py-12 text-gray-400">
                      <ShoppingCart
                        size={48}
                        className="mx-auto mb-4 opacity-50"
                      />
                      <p>Sélectionnez des produits dans le menu</p>
                    </div>
                  );
                }

                return (
                  <>
                    {courseModeEnabled && visibleCourseSummaries.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between px-1">
                          <span className="text-sm font-bold text-blue-200">
                            Service en plusieurs tours
                          </span>
                          <span className="rounded-full bg-blue-500/20 px-2 py-1 text-xs text-blue-100">
                            {activeCourseIsEditable ? "Saisie" : "Dernier"} : tour{" "}
                            {displayedCourseNumber}
                          </span>
                        </div>
                        {visibleCourseSummaries.map((course) => {
                          const number = Number(course.course_number || 1);
                          const status = String(course.status || "held");
                          const draftItems = cartNewItems.filter(
                            (item) =>
                              Number(item?.courseNumber ?? item?.course_number ?? 1) ===
                              number,
                          );
                          const savedItems = cartExistingItems.filter(
                            (item) =>
                              Number(item?.courseNumber ?? item?.course_number ?? 1) ===
                              number,
                          );
                          const courseItems = [...draftItems, ...savedItems];
                          const itemQuantity = courseItems.reduce(
                            (sum, item) => sum + Number(item?.quantity || 1),
                            0,
                          );
                          const isEditable = status === "held";
                          const isSelected =
                            number === Number(displayedCourseNumber);
                          const isActive =
                            isEditable && isSelected;
                          const label =
                            status === "completed"
                              ? "Finalisé"
                              : status === "fired"
                                ? "Envoyé"
                                : status === "cancelled"
                                  ? "Annulé"
                                  : isActive
                                    ? "En cours de saisie"
                                    : "À suivre";
                          const canFire =
                            isEditable &&
                            courseItems.length > 0 &&
                            Boolean(getCurrentCourseOrderId());
                          const courseOrderId =
                            course?.order_id ??
                            course?.orderId ??
                            getCurrentCourseOrderId();
                          const courseActionKey = `${courseOrderId}:${number}`;
                          const courseDispatchState =
                            courseDispatchStates[courseActionKey] || null;
                          const productionDispatchStatus = String(
                            course?.production_dispatch_status || "",
                          );
                          const dispatchStillRunning =
                            status === "fired" &&
                            productionDispatchStatus === "in_progress" &&
                            Number(course?.dispatch_stale) !== 1;
                          const displayedDispatchState =
                            courseDispatchState ||
                            (dispatchStillRunning ? "verifying" : null);
                          const canRetryDispatch =
                            status === "fired" &&
                            productionDispatchStatus !== "sent" &&
                            (productionDispatchStatus !== "in_progress" ||
                              Number(course?.dispatch_stale) === 1);
                          const canCancel = isEditable && courseItems.length === 0;

                          return (
                            <section
                              key={`course-group-${number}`}
                              onClick={() => {
                                if (isEditable) setActiveCourseNumber(number);
                              }}
                              className={`rounded-2xl border p-3 transition-all ${
                                isSelected
                                  ? "border-blue-400/70 bg-blue-500/10 shadow-[0_0_0_1px_rgba(96,165,250,0.2)]"
                                  : isEditable
                                    ? "cursor-pointer border-white/15 bg-white/5 hover:border-blue-300/40"
                                    : "border-white/10 bg-white/[0.03]"
                              }`}
                              title={
                                isEditable
                                  ? `Cliquer pour continuer le tour ${number}`
                                  : undefined
                              }
                            >
                              <div className="mb-3 flex items-center justify-between gap-2">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-bold text-white">Tour {number}</span>
                                    {isSelected && (
                                      <span className="rounded-full bg-blue-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                                        {isEditable ? "Sélectionné" : "Dernier tour"}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-white/60">
                                    État : {label} · {itemQuantity} article(s)
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {canFire && !displayedDispatchState && (
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        void fireCourse(number);
                                      }}
                                      className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-400"
                                    >
                                      <Send size={14} /> Réclamer
                                    </button>
                                  )}
                                  {canRetryDispatch && !displayedDispatchState && (
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        void fireCourse(number);
                                      }}
                                      className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-3 py-2 text-xs font-bold text-white hover:bg-orange-400"
                                      title={course?.production_dispatch_error || "Envoi production non confirmé"}
                                    >
                                      <RotateCcw size={14} /> Réessayer l’envoi
                                    </button>
                                  )}
                                  {["sending", "verifying"].includes(
                                    displayedDispatchState,
                                  ) && (
                                    <button
                                      type="button"
                                      disabled
                                      className="inline-flex cursor-wait items-center gap-2 rounded-lg bg-blue-500/50 px-3 py-2 text-xs font-bold text-white"
                                    >
                                      <RotateCcw size={14} className="animate-spin" />
                                      {displayedDispatchState === "sending"
                                        ? "Envoi…"
                                        : "Vérification…"}
                                    </button>
                                  )}
                                  {displayedDispatchState === "uncertain" && (
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        void verifyCourseDispatch(number);
                                      }}
                                      className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-3 py-2 text-xs font-bold text-white hover:bg-orange-400"
                                      title="Vérifier le statut serveur sans réimprimer"
                                    >
                                      <RotateCcw size={14} /> Vérifier
                                    </button>
                                  )}
                                  {canCancel && number > 1 && !displayedDispatchState && (
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        void cancelEmptyCourse(number);
                                      }}
                                      className="inline-flex items-center gap-2 rounded-lg border border-red-300/30 bg-red-500/10 px-3 py-2 text-xs font-bold text-red-100 hover:bg-red-500/20"
                                    >
                                      <X size={14} /> Annuler
                                    </button>
                                  )}
                                </div>
                              </div>

                              {courseItems.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-white/15 px-3 py-4 text-center text-xs text-white/50">
                                  {isActive
                                    ? "Ajoutez maintenant les articles de ce tour depuis le menu."
                                    : "Aucun article dans ce tour."}
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {courseItems.map((item, index) => {
                                    const isDraft = !item?.order_item_id;
                                    const itemExtrasPrice = Array.isArray(item?.extras)
                                      ? item.extras.reduce(
                                          (sum, extra) =>
                                            sum +
                                            (extra?.type === "accompaniment"
                                              ? 0
                                              : Number(extra?.price || 0)) *
                                              Number(extra?.quantity || 1),
                                          0,
                                        )
                                      : 0;
                                    const totalItemPrice =
                                      (Number(item?.price || 0) + itemExtrasPrice) *
                                      Number(item?.quantity || 1);
                                    const statusDescription = !isDraft
                                      ? getStatusDescription(item.status)
                                      : null;
                                    return (
                                      <div
                                        key={
                                          item?.client_line_id ||
                                          item?.order_item_id ||
                                          `course-${number}-item-${index}`
                                        }
                                        onClick={(event) => event.stopPropagation()}
                                        className={`rounded-xl border p-3 ${
                                          isDraft
                                            ? "border-emerald-400/25 bg-emerald-500/5"
                                            : "border-amber-300/20 bg-amber-500/5"
                                        }`}
                                      >
                                        <div className="flex items-center gap-3">
                                          <img
                                            src={
                                              item.image_path ||
                                              item.image_url ||
                                              PRODUCT_PLACEHOLDER
                                            }
                                            alt={item.name}
                                            className="h-11 w-11 rounded-lg object-cover"
                                            onError={(event) => {
                                              event.currentTarget.onerror = null;
                                              event.currentTarget.src = PRODUCT_PLACEHOLDER;
                                            }}
                                          />
                                          <div className="min-w-0 flex-1">
                                            <div className="whitespace-normal break-words font-semibold leading-tight text-white">
                                              {item.name}
                                            </div>
                                            <div className="text-xs text-white/65">
                                              {Number(item?.price || 0).toLocaleString()} F ×{" "}
                                              {Number(item?.quantity || 1)}
                                            </div>
                                            <div className="text-sm font-bold text-emerald-300">
                                              {totalItemPrice.toLocaleString()} F
                                            </div>
                                            {statusDescription && (
                                              <div className="mt-1 text-[11px] text-white/55">
                                                {statusDescription.icon} {statusDescription.text}
                                              </div>
                                            )}
                                          </div>
                                          {isDraft ? (
                                            <div className="flex items-center gap-1">
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  updateDraftItemQuantity(
                                                    item,
                                                    -1,
                                                    isDeliveryOrder,
                                                  )
                                                }
                                                className="rounded-lg bg-slate-600 p-2 text-white hover:bg-slate-500"
                                              >
                                                <Minus size={14} />
                                              </button>
                                              <span className="w-6 text-center font-bold text-white">
                                                {item.quantity}
                                              </span>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  updateDraftItemQuantity(
                                                    item,
                                                    1,
                                                    isDeliveryOrder,
                                                  )
                                                }
                                                className="rounded-lg bg-amber-400 p-2 text-slate-950 hover:bg-amber-300"
                                              >
                                                <Plus size={14} />
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => removeFromOrder(item)}
                                                className="rounded-lg bg-red-500 p-2 text-white hover:bg-red-400"
                                                title="Retirer l’article"
                                              >
                                                <Trash2 size={14} />
                                              </button>
                                            </div>
                                          ) : (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                void openItemCancellation(item);
                                              }}
                                              className="rounded-lg bg-red-500 p-2 text-white hover:bg-red-400"
                                              title="Annuler l’article"
                                            >
                                              <Trash2 size={14} />
                                            </button>
                                          )}
                                        </div>
                                        {(hasVariationDisplay(item) ||
                                          (item.extras && item.extras.length > 0)) && (
                                          <div className="mt-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/70">
                                            {hasVariationDisplay(item) && (
                                              <div>
                                                Variante : {getItemVariationDisplay(item)}
                                              </div>
                                            )}
                                            {(item.extras || []).map((extra, extraIndex) => (
                                              <div key={extraIndex}>
                                                {Number(extra?.quantity || 1) > 1
                                                  ? `${extra.quantity}× `
                                                  : ""}
                                                {extra.name}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </section>
                          );
                        })}
                      </div>
                    )}
                    {/* Section Nouveaux Ajouts */}
                    {!courseModeEnabled && cartNewItems.length > 0 && (
                      <>
                        <div
                          className={`sticky top-0 z-10 px-4 py-2 rounded-xl shadow-lg border ${
                            isDarkTheme
                              ? "bg-white/5 border-emerald-300/40 text-emerald-50 backdrop-blur"
                              : "bg-gradient-to-r from-blue-500 to-purple-600 text-white border-transparent"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-sm">
                              🆕 Nouveaux Ajouts
                            </span>
                            <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
                              {cartNewItemsQuantity}{" "}
                              article(s)
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2 mb-4">
                          {cartNewItems.map((item, index) => {
                            const itemKey =
                              item.client_line_id ||
                              item.clientLineId ||
                              `new-${item.id}-${index}`;
                            const itemExtrasPrice = item.extras
                              ? item.extras.reduce(
                                  (sum, extra) =>
                                    sum +
                                    (extra.type === "accompaniment"
                                      ? 0
                                      : extra.price) *
                                      extra.quantity,
                                  0,
                                )
                              : 0;
                            const totalItemPrice =
                              (item.price + itemExtrasPrice) * item.quantity;

                            return (
                              <div
                                key={itemKey}
                                className={`rounded-2xl group transition-colors border ${
                                  isDarkTheme
                                    ? "border-emerald-400/30 bg-emerald-500/5 hover:bg-emerald-500/10"
                                    : "bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-400"
                                }`}
                              >
                                  {/* Article principal */}
                                  <div className="flex items-center p-4">
                                    {/* Image produit avec placeholder si absent */}
                                    <div className="mr-3">
	                                      <img
	                                        src={
	                                          item.image_path ||
	                                          item.image_url ||
	                                          PRODUCT_PLACEHOLDER
	                                        }
	                                        alt={item.name}
	                                        loading="lazy"
	                                        decoding="async"
	                                        className="w-12 h-12 rounded-xl object-cover"
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = PRODUCT_PLACEHOLDER;
                                        }}
                                      />
                                    </div>

                                  <div className="flex-1">
                                    <div
                                      className={`font-semibold ${
                                        isDarkTheme
                                          ? "text-white"
                                          : "text-gray-800"
                                      }`}
                                    >
                                      {item.name}
                                    </div>
                                    {courseModeEnabled && (
                                      <div className="mt-1 inline-flex rounded-full bg-blue-500/15 px-2 py-0.5 text-[11px] font-semibold text-blue-200">
                                        Tour {Number(item?.courseNumber ?? item?.course_number ?? 1)}
                                      </div>
                                    )}
                                    <div
                                      className={`text-sm ${
                                        isDarkTheme
                                          ? "text-slate-200"
                                          : "text-gray-600"
                                      }`}
                                    >
                                      {item.price.toLocaleString()} F ×{" "}
                                      {item.quantity}
                                      {itemExtrasPrice > 0 && (
                                        <span
                                          className={
                                            isDarkTheme
                                              ? "text-emerald-300"
                                              : "text-blue-600"
                                          }
                                        >
                                          {" "}
                                          + extras
                                        </span>
                                      )}
                                    </div>
                                    <div
                                      className={`text-sm font-bold ${
                                        isDarkTheme
                                          ? "text-emerald-300"
                                          : "text-blue-600"
                                      }`}
                                    >
                                      {totalItemPrice.toLocaleString()} F
                                    </div>
                                  </div>

                                  <div className="flex items-center space-x-2">
                                    {/* Contrôles de quantité pour nouveaux articles */}
                                    <button
                                      onClick={() =>
                                        updateDraftItemQuantity(
                                          item,
                                          -1,
                                          isDeliveryOrder,
                                        )
                                      }
                                      className="p-2 rounded-xl bg-gray-500 text-white hover:bg-gray-600 transition-colors"
                                    >
                                      <Minus size={16} />
                                    </button>
                                    <span className="font-bold text-lg w-8 text-center">
                                      {item.quantity}
                                    </span>
                                    <button
                                      onClick={() =>
                                        updateDraftItemQuantity(
                                          item,
                                          1,
                                          isDeliveryOrder,
                                        )
                                      }
                                      className="p-2 rounded-xl text-white hover:opacity-90 transition-opacity"
                                      style={{
                                        backgroundColor: colors.primary,
                                      }}
                                    >
                                      <Plus size={16} />
                                    </button>

                                    {!courseModeEnabled &&
                                      item.order_item_id &&
                                      String(item.production_dispatch_status || "") ===
                                        "failed" && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            void retryOrdinaryItemPrint(item)
                                          }
                                          disabled={productionRetryingItemIds.has(
                                            Number(item.order_item_id),
                                          )}
                                          className="inline-flex items-center gap-1 rounded-xl bg-orange-500 px-2 py-2 text-xs font-bold text-white hover:bg-orange-400 disabled:opacity-50"
                                          title={
                                            item.production_dispatch_error ||
                                            "Impression échouée — Réessayer"
                                          }
                                        >
                                          <RotateCcw
                                            size={14}
                                            className={
                                              productionRetryingItemIds.has(
                                                Number(item.order_item_id),
                                              )
                                                ? "animate-spin"
                                                : ""
                                            }
                                          />
                                          Réimprimer
                                        </button>
                                      )}

                                    {/* Bouton corbeille pour suppression */}
                                    <button
                                      onClick={() => {
                                        // Si l'article n'est pas encore envoyé (pas d'order_item_id), on le retire directement
                                        if (!item.order_item_id) {
                                          removeFromOrder(item);
                                          triggerSuccessAnimation(
                                            `"${item.name}" retiré du panier`,
                                          );
                                          return;
                                        }

                                        void openItemCancellation(item);
                                      }}
                                      className="p-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors"
                                      title="Supprimer l'article"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </div>

                                {/* Affichage des variantes et extras */}
                                {(hasVariationDisplay(item) ||
                                  (item.extras && item.extras.length > 0)) && (
                                  <div className="px-4 pb-3">
                                    <div
                                      className={`rounded-xl p-3 space-y-1 ${
                                        isDarkTheme
                                          ? "bg-white/5 border border-white/10"
                                          : "bg-white/50"
                                      }`}
                                    >
                                      {hasVariationDisplay(item) && (
                                        <div className="space-y-1 mb-2">
                                          <div
                                            className={`text-xs font-semibold ${
                                              isDarkTheme
                                                ? "text-slate-100"
                                                : "text-gray-700"
                                            }`}
                                          >
                                            Variante sélectionnée :
                                          </div>
                                          <div
                                            className={`text-sm ${
                                              isDarkTheme
                                                ? "text-slate-100"
                                                : "text-gray-700"
                                            }`}
                                          >
                                            {getItemVariationDisplay(item)}
                                          </div>
                                        </div>
                                      )}
                                      {item.extras && item.extras.length > 0 && (
                                        <>
                                          <div
                                            className={`text-xs font-semibold mb-2 ${
                                              isDarkTheme
                                                ? "text-slate-100"
                                                : "text-gray-700"
                                            }`}
                                          >
                                            ✨ Extras sélectionnés :
                                          </div>
                                          {item.extras.map((extra, extraIndex) => (
                                            <div
                                              key={extraIndex}
                                              className="flex justify-between items-center text-xs"
                                            >
                                              <span
                                                className={
                                                  isDarkTheme
                                                    ? "text-white"
                                                    : "text-gray-600"
                                                }
                                              >
                                                {extra.quantity > 1 &&
                                                  `${extra.quantity}× `}
                                                {extra.name}
                                                {extra.type === "accompaniment" && (
                                                  <span className="ml-1 px-1 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                                                    A
                                                  </span>
                                                )}
                                                {extra.type === "supplement" && (
                                                  <span className="ml-1 px-1 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                                    S
                                                  </span>
                                                )}
                                              </span>
                                              <span
                                                className={
                                                  isDarkTheme
                                                    ? "text-slate-200"
                                                    : "text-gray-500"
                                                }
                                              >
                                                {extra.type === "accompaniment"
                                                  ? "Gratuit"
                                                  : `+${(extra.price * extra.quantity).toLocaleString()} F`}
                                              </span>
                                            </div>
                                          ))}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {/* Section Articles Déjà Envoyés */}
                    {!courseModeEnabled && !isBoutique && shouldShowExistingSection && (
                      <>
                        <div
                          className={`sticky top-0 z-10 px-4 py-2 rounded-xl shadow-lg border ${
                            isDarkTheme
                              ? "bg-white/5 border-amber-300/40 text-amber-50 backdrop-blur"
                              : "bg-gradient-to-r from-orange-500 to-red-600 text-white border-transparent"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-sm">
                              {courseModeEnabled
                                ? "Articles enregistrés"
                                : "Déjà Envoyé(s)"}
                            </span>
                            <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
                              {cartExistingItemsQuantity}{" "}
                              article(s)
                            </span>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {cartExistingItems.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
                              <p className="text-sm">
                                Chargement des articles envoyés...
                              </p>
                            </div>
                          ) : (
                            cartExistingItems.map((item, index) => {
                              const itemKey = `existing-${item.order_item_id}-${index}`;
                              const statusClass = getStatusClass(item.status);
                              const statusDescription = getStatusDescription(
                                item.status,
                              );
                              const itemExtrasPrice = item.extras
                                ? item.extras.reduce(
                                    (sum, extra) =>
                                      sum +
                                      (extra.type === "accompaniment"
                                        ? 0
                                        : extra.price) *
                                        extra.quantity,
                                    0,
                                  )
                                : 0;
                              const totalItemPrice =
                                (item.price + itemExtrasPrice) * item.quantity;

                              return (
                                <div
                                  key={itemKey}
                                  className={`rounded-2xl group transition-colors border ${
                                    isDarkTheme
                                      ? "border-amber-400/30 bg-amber-500/5 hover:bg-amber-500/10"
                                      : "bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-400"
                                  }`}
                                >
                                  {/* Article principal */}
                                  <div className="flex items-center p-4">
                                    {/* Image produit avec placeholder si absent */}
                                    <div className="mr-3">
	                                      <img
	                                        src={
	                                          item.image_path ||
	                                          item.image_url ||
	                                          PRODUCT_PLACEHOLDER
	                                        }
	                                        alt={item.name}
	                                        loading="lazy"
	                                        decoding="async"
	                                        className="w-12 h-12 rounded-xl object-cover"
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = PRODUCT_PLACEHOLDER;
                                        }}
                                      />
                                    </div>

                                    <div className="flex-1">
                                      <div
                                        className={`font-semibold ${
                                          isDarkTheme
                                            ? "text-white"
                                            : "text-gray-800"
                                        }`}
                                      >
                                        {item.name}
                                      </div>
                                      {courseModeEnabled && (
                                        <div className="mt-1 inline-flex rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-200">
                                          Tour {Number(item?.courseNumber ?? item?.course_number ?? 1)}
                                        </div>
                                      )}
                                      <div
                                        className={`text-sm ${
                                          isDarkTheme
                                            ? "text-slate-200"
                                            : "text-gray-600"
                                        }`}
                                      >
                                        {item.price.toLocaleString()} F ×{" "}
                                        {item.quantity}
                                        {itemExtrasPrice > 0 && (
                                          <span
                                            className={
                                              isDarkTheme
                                                ? "text-amber-300"
                                                : "text-orange-600"
                                            }
                                          >
                                            {" "}
                                            + extras
                                          </span>
                                        )}
                                      </div>
                                      <div
                                        className={`text-sm font-bold ${
                                          isDarkTheme
                                            ? "text-amber-300"
                                            : "text-orange-600"
                                        }`}
                                      >
                                        {totalItemPrice.toLocaleString()} F
                                      </div>
                                      <div
                                        className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${statusClass}`}
                                      >
                                        {statusDescription.icon}{" "}
                                        {statusDescription.text}
                                      </div>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                      {/* Pas de contrôles de quantité pour articles existants */}
                                      <span className="font-bold text-lg w-8 text-center text-gray-500">
                                        {item.quantity}
                                      </span>

                                      {!courseModeEnabled &&
                                        String(item.production_dispatch_status || "") ===
                                          "failed" && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              void retryOrdinaryItemPrint(item)
                                            }
                                            disabled={productionRetryingItemIds.has(
                                              Number(item.order_item_id),
                                            )}
                                            className="inline-flex items-center gap-1 rounded-xl bg-orange-500 px-2 py-2 text-xs font-bold text-white hover:bg-orange-400 disabled:opacity-50"
                                            title={
                                              item.production_dispatch_error ||
                                              "Impression échouée — Réessayer"
                                            }
                                          >
                                            <RotateCcw
                                              size={14}
                                              className={
                                                productionRetryingItemIds.has(
                                                  Number(item.order_item_id),
                                                )
                                                  ? "animate-spin"
                                                  : ""
                                              }
                                            />
                                            Réimprimer
                                          </button>
                                        )}

                                    {/* Bouton corbeille pour suppression (avec vérification de statut) */}
                                      <button
                                        onClick={() => {
                                          void openItemCancellation(item);
                                        }}
                                        className="p-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors"
                                        title="Supprimer l'article"
                                      >
                                        <Trash2 size={16} />
                                    </button>
                                    </div>
                                  </div>

                                  {/* Affichage des variantes / extras pour articles existants */}
                                  {(hasVariationDisplay(item) ||
                                    (item.extras &&
                                      item.extras.length > 0)) && (
                                    <div className="px-4 pb-3">
                                      <div
                                        className={`rounded-xl p-3 space-y-1 ${
                                          isDarkTheme
                                            ? "bg-white/5 border border-white/10"
                                            : "bg-white/50"
                                        }`}
                                      >
                                        {hasVariationDisplay(item) && (
                                          <div className="space-y-1 mb-2">
                                            <div
                                              className={`text-xs font-semibold ${
                                                isDarkTheme
                                                  ? "text-white"
                                                  : "text-gray-700"
                                              }`}
                                            >
                                              Variante sélectionnée :
                                            </div>
                                            <div
                                              className={`text-sm ${
                                                isDarkTheme
                                                  ? "text-white"
                                                  : "text-gray-700"
                                              }`}
                                            >
                                              {getItemVariationDisplay(item)}
                                            </div>
                                          </div>
                                        )}
                                        {item.extras && item.extras.length > 0 && (
                                          <>
                                            <div
                                              className={`text-xs font-semibold mb-2 ${
                                                isDarkTheme
                                                  ? "text-white"
                                                  : "text-gray-700"
                                              }`}
                                            >
                                              ✨ Extras sélectionnés :
                                            </div>
                                            {item.extras.map((extra, extraIndex) => (
                                              <div
                                                key={extraIndex}
                                                className="flex justify-between items-center text-xs"
                                              >
                                                <span
                                                  className={
                                                    isDarkTheme
                                                      ? "text-white"
                                                      : "text-gray-600"
                                                  }
                                                >
                                                  {extra.quantity > 1 &&
                                                    `${extra.quantity}× `}
                                                  {extra.name}
                                                  {extra.type === "accompaniment" && (
                                                    <span className="ml-1 px-1 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                                                      A
                                                    </span>
                                                  )}
                                                  {extra.type === "supplement" && (
                                                    <span className="ml-1 px-1 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                                      S
                                                    </span>
                                                  )}
                                                </span>
                                                <span
                                                  className={
                                                    isDarkTheme
                                                      ? "text-slate-100"
                                                      : "text-gray-500"
                                                  }
                                                >
                                                  {extra.type === "accompaniment"
                                                    ? "Gratuit"
                                                    : `+${(
                                                        extra.price * extra.quantity
                                                      ).toLocaleString()} F`}
                                                </span>
                                              </div>
                                            ))}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
              </div>
            )}

            {/* Section rabais et frais */}
            {selectedTable &&
              (discountAmount > 0 ||
                additionalFees > 0 ||
                (Number(getDiscountTotal()) || 0) > 0) && (
              <div
                className={`mb-6 p-4 rounded-2xl ${
                  isDarkTheme
                    ? "bg-white/5 border border-white/10"
                    : "bg-gray-50"
                }`}
              >
                <h3
                  className={`font-bold text-lg mb-3 ${
                    isDarkTheme ? "text-white" : "text-gray-800"
                  }`}
                >
                  💰 Ajustements
                </h3>
                {discountAmount > 0 && (
                  <div className="flex justify-between items-center mb-2">
                    <span
                      className={`text-sm ${
                        isDarkTheme ? "text-slate-200" : "text-gray-600"
                      }`}
                    >
                      Rabais (
                      {discountType === "percentage"
                        ? `${discountAmount}%`
                        : "Montant"}
                      ):
                    </span>
                    <span className="font-bold text-green-600">
                      -{getDiscountValue().toLocaleString()} F
                    </span>
                  </div>
                )}
                {(Number(getDiscountTotal()) || 0) > 0 && (
                  <div className="flex justify-between items-center mb-2">
                    <span
                      className={`text-sm ${
                        isDarkTheme ? "text-slate-200" : "text-gray-600"
                      }`}
                    >
                      Remises (génération facture):
                    </span>
                    <span className="font-bold text-green-600">
                      -{(Number(getDiscountTotal()) || 0).toLocaleString()} F
                    </span>
                  </div>
                )}
                {additionalFees > 0 && (
                  <div className="flex justify-between items-center">
                    <span
                      className={`text-sm ${
                        isDarkTheme ? "text-slate-200" : "text-gray-600"
                      }`}
                    >
                      Frais (
                      {feesType === "percentage"
                        ? `${additionalFees}%`
                        : "Montant"}
                      ):
                    </span>
                    <span className="font-bold text-red-600">
                      +{getFeesValue().toLocaleString()} F
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {(selectedTable || canOrderWithoutTable) && (
            <div className="caisse-cart-collapse-toggle-wrap">
              <button
                onClick={() => setCartBottomCollapsed((prev) => !prev)}
                className="caisse-cart-collapse-toggle"
                title={
                  cartBottomCollapsed
                    ? "Afficher totaux et actions"
                    : "Masquer la zone basse"
                }
              >
                {cartBottomCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>
          )}

          {/* Zone fixe du total et boutons d'action */}
          {(selectedTable || canOrderWithoutTable) && (
            <div
              className={`caisse-cart-bottom-panel flex-shrink-0 rounded-b-3xl ${
                isDarkTheme
                  ? "border-t border-white/10 bg-white/5"
                  : "border-t border-gray-100 bg-white"
              } ${cartBottomCollapsed ? "is-collapsed" : ""}`}
            >
              {/* Total fixe */}
              <div className="p-6 pb-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span
                      className={`text-lg ${
                        isDarkTheme ? "text-slate-200" : "text-gray-600"
                      }`}
                    >
                      Sous-total:
                    </span>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`text-xl font-bold ${
                          isDarkTheme ? "text-white" : "text-gray-800"
                        }`}
                      >
                        {getSubtotalWithExisting().toLocaleString()} F
                      </span>
                    </div>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-600">Rabais:</span>
                      <span className="text-sm font-bold text-green-600">
                        -{getDiscountValue().toLocaleString()} F
                      </span>
                    </div>
                  )}
                  {(Number(getDiscountTotal()) || 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-600">Remises:</span>
                      <span className="text-sm font-bold text-green-600">
                        -{(Number(getDiscountTotal()) || 0).toLocaleString()} F
                      </span>
                    </div>
                  )}
                  {additionalFees > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-red-600">Frais:</span>
                      <span className="text-sm font-bold text-red-600">
                        +{getFeesValue().toLocaleString()} F
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center border-t pt-2">
                    <span
                      className={`text-xl font-bold ${
                        isDarkTheme ? "text-white" : "text-gray-800"
                      }`}
                    >
                      Total:
                    </span>
                    <span
                      className="text-3xl font-bold"
                      style={{ color: colors.secondary }}
                    >
                      {getTotal().toLocaleString()} F
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Boutons d'action fixes en bas */}
          <div
            className={`caisse-cart-actions ${
              shouldShowMainCartActions ? "" : "is-collapsed"
            }`}
          >
            <div className="space-y-3">
              {!isBoutique && !isFastFoodMode && !isAggregatorOrderMode && (
                <button
                  onClick={
                    hasExistingOrders ? addToExistingTable : sendToCuisineBar
                  }
                  className="caisse-production-send-button w-full py-4 text-white rounded-2xl font-bold hover:opacity-90 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                  style={{ backgroundColor: "#22c55e", color: "#fff" }}
                  disabled={cartAddDisabled}
                  aria-disabled={cartAddDisabled}
                  title={
                    cartAddDisabled && !cartHasPendingItems
                      ? "Aucun nouvel article à envoyer"
                      : undefined
                  }
                >
                  {isOrderActionInProgress ? (
                    <RefreshCw className="mr-2 animate-spin" size={20} />
                  ) : (
                    <Send className="mr-2" size={20} />
                  )}
                  {isOrderActionInProgress
                    ? orderActionKind === "add"
                      ? "Ajout en cours..."
                      : "Envoi en cours..."
                    : selectedTable?.isDelivery
                      ? selectedTable.deliveryMode === "delivery"
                        ? "Envoyer pour Livraison"
                        : "Préparer pour Emporter"
                      : hasExistingOrders
                        ? selectedTable?.isDelivery
                          ? "Ajouter à la livraison"
                          : isBoutique
                            ? "Ajouter au panier"
                            : "Ajouter à la table"
                        : isBoutique
                          ? "Ajouter au panier"
                          : "Envoyer en Cuisine/Bar"}
                </button>
              )}

              {!isServerParticipationMode && (
                <>
                  {!isBoutique && !isFastFoodMode && !isAggregatorOrderMode ? (
                    <>
                      <div className="grid w-full grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={openSplitManagement}
                          className="flex w-full items-center justify-center rounded-2xl bg-amber-500 py-4 font-bold text-white shadow-lg transition-all hover:scale-105 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 disabled:transform-none"
                          disabled={cartAdjustDisabled || processingAction}
                        >
                          {processingAction ? (
                            <RefreshCw className="mr-2 animate-spin" size={20} />
                          ) : (
                            <Scissors className="mr-2" size={20} />
                          )}
                          Diviser
                        </button>
                        <button
                          type="button"
                          onClick={openInvoiceGenerationModal}
                          className="flex w-full items-center justify-center rounded-2xl bg-amber-500 py-4 font-bold text-white shadow-lg transition-all hover:scale-105 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 disabled:transform-none"
                          disabled={cartAdjustDisabled}
                        >
                          <Receipt className="mr-2" size={20} />
                          Réduction
                        </button>
                      </div>

                      <div className="grid w-full grid-cols-2 gap-2">
                        <button
                          onClick={quickPrintInvoice}
                          className="w-full py-4 text-white rounded-2xl font-bold hover:opacity-90 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center bg-amber-500"
                          style={{ color: "#fff" }}
                          disabled={cartPrintDisabled}
                        >
                          <Printer className="mr-2" size={20} />
                          Imprimer
                        </button>
                        <button
                          onClick={startPaymentFromCart}
                          className="w-full py-4 text-white rounded-2xl font-bold hover:opacity-90 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                          style={{ backgroundColor: colors.secondary, color: "#fff" }}
                          disabled={cartPaymentDisabled}
                        >
                          {isPreparingPayment ? (
                            <RefreshCw className="mr-2 animate-spin" size={20} />
                          ) : (
                            <DollarSign className="mr-2" size={20} />
                          )}
                          {isPreparingPayment ? "Vérification..." : "Payer"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={startPaymentFromCart}
                        className="w-full py-4 text-white rounded-2xl font-bold hover:opacity-90 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                        style={{ backgroundColor: colors.secondary, color: "#fff" }}
                        disabled={cartPaymentDisabled}
                      >
                        {isPreparingPayment ? (
                          <RefreshCw className="mr-2 animate-spin" size={20} />
                        ) : (
                          <DollarSign className="mr-2" size={20} />
                        )}
                        {isPreparingPayment ? "Vérification..." : "Payer"}
                      </button>
                      {!isFastFoodMode && !isAggregatorOrderMode && (
                      <button
                        onClick={openInvoiceGenerationModal}
                        className="w-full py-4 text-white rounded-2xl font-bold hover:opacity-90 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                        style={{ backgroundColor: colors.accent, color: "#fff" }}
                        disabled={cartAdjustDisabled}
                      >
                        <Receipt className="mr-2" size={20} />
                        Réduction
                      </button>
                      )}
                    </>
                  )}
                </>
              )}

              {cartHasPendingItems && (
                <button
                  onClick={clearOrder}
                  className="w-full py-3 bg-gray-500 text-white rounded-2xl font-semibold hover:bg-gray-600 transition-colors flex items-center justify-center"
                >
                  <Trash2 className="mr-2" size={18} />
                  Vider la commande
                </button>
              )}
            </div>
          </div>

          {shouldUseCompactCartActions && (
              <div className="caisse-cart-compact-actions">
                {!isBoutique && !isFastFoodMode && !isAggregatorOrderMode && (
                  <button
                    onClick={hasExistingOrders ? addToExistingTable : sendToCuisineBar}
                    className="caisse-production-send-button w-full py-3 text-white rounded-2xl font-bold hover:opacity-90 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    style={{ backgroundColor: "#22c55e", color: "#fff" }}
                    disabled={cartAddDisabled}
                    aria-disabled={cartAddDisabled}
                    title={
                      cartAddDisabled && !cartHasPendingItems
                        ? "Aucun nouvel article à envoyer"
                        : hasExistingOrders
                          ? "Ajouter à la table"
                          : "Envoyer en Cuisine/Bar"
                    }
                  >
                    {isOrderActionInProgress
                      ? <RefreshCw className="animate-spin" size={20} />
                      : <Send size={20} />}
                  </button>
                )}
                <button
                  onClick={quickPrintInvoice}
                  className="w-full py-3 text-white rounded-2xl font-bold hover:opacity-90 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center bg-amber-500"
                  style={{ color: "#fff" }}
                  disabled={cartPrintDisabled}
                  title="Imprimer"
                >
                  <Printer size={20} />
                </button>
                <button
                  onClick={startPaymentFromCart}
                  className="w-full py-3 text-white rounded-2xl font-bold hover:opacity-90 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  style={{ backgroundColor: colors.secondary, color: "#fff" }}
                  disabled={cartPaymentDisabled}
                  title="Payer"
                >
                  {isPreparingPayment ? (
                    <RefreshCw className="animate-spin" size={20} />
                  ) : (
                    <span className="text-lg font-bold">{getTotal().toLocaleString()} F</span>
                  )}
                </button>
              </div>
            )}
        </div>
      </div>

      {/* Modal de sélection de quantité */}
      {showQuantityModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 animate-fadeIn modal-backdrop p-4">
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-gradient-to-br from-[#020617] via-[#020b1f] to-[#020617] text-white animate-bounceIn">
            <div className="pointer-events-none absolute inset-0 opacity-60">
              <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl" />
              <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            <div className="relative z-10 p-8 space-y-6">
              <div className="text-center space-y-3">
                <div className="w-24 h-24 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center mx-auto shadow-lg overflow-hidden">
	                  <img
	                    src={
	                      selectedProduct.image_path ||
	                      selectedProduct.image_url ||
	                      PRODUCT_PLACEHOLDER
	                    }
	                    alt={selectedProduct.name}
	                    decoding="async"
	                    className="w-full h-full object-cover"
                    onError={(e) => {
                      if (e.target.dataset.fallbackApplied) return;
                      e.target.dataset.fallbackApplied = "true";
                      e.target.src = PRODUCT_PLACEHOLDER;
                    }}
                  />
                </div>
                <h3 className="text-2xl font-bold text-white">
                  {selectedProduct.name}
                </h3>
                <div className="text-xl font-bold text-amber-300">
                  Prix unitaire :{" "}
                  {getUnitPriceForSelectionMode(
                    selectedProduct,
                    selectedProduct?.product_type === "variable"
                      ? showVariationSection
                        ? effectiveVariation
                        : selectedVariation
                      : null,
                    activeAggregator,
                  ).toLocaleString()}{" "}
                  F
                </div>
              </div>

              {selectedExtras.length > 0 && (
                <div className="p-3 rounded-xl bg-white/5 border border-white/10 space-y-2">
                  <h4 className="text-sm font-semibold text-white mb-1">
                    Extras sélectionnés
                  </h4>
                  <div className="space-y-1">
                    {selectedExtras.map((extra) => (
                      <div
                        key={extra.id}
                        className="flex justify-between items-center text-sm text-white/80"
                      >
                        <span>
                          {extra.name}{" "}
                          {extra.selectedQuantity > 1 &&
                            `× ${extra.selectedQuantity}`}
                        </span>
                        <span className="text-amber-200">
                          {extra.type === "accompaniment"
                            ? "Gratuit"
                            : `+${(extra.price * (extra.selectedQuantity || 1)).toLocaleString()} F`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Quantité souhaitée
                </label>
                <div className="flex items-center justify-center space-x-4">
                  <button
                    onClick={() => {
                      const currentQty =
                        document.getElementById("quantity-input").value;
                      const newQty = Math.max(1, parseInt(currentQty) - 1);
                      document.getElementById("quantity-input").value = newQty;
                      const totalPriceElement = document.getElementById(
                        "total-price-display",
                      );
                      if (totalPriceElement) {
                        totalPriceElement.textContent =
                          (
                            calculateTotalPriceWithExtras() * newQty
                          ).toLocaleString() + " F";
                      }
                    }}
                    className="w-12 h-12 rounded-full transition-all duration-300 flex items-center justify-center modal-button-hover bg-white/10 text-white border border-white/15 hover:bg-white/20"
                  >
                    <Minus size={20} />
                  </button>
                  <input
                    id="quantity-input"
                    type="number"
                    min="1"
                    defaultValue="1"
                    onChange={(e) => {
                      const quantity = parseInt(e.target.value) || 1;
                      const totalPriceElement = document.getElementById(
                        "total-price-display",
                      );
                      if (totalPriceElement) {
                        totalPriceElement.textContent =
                          (
                            calculateTotalPriceWithExtras() * quantity
                          ).toLocaleString() + " F";
                      }
                    }}
                    className="w-20 h-12 text-center text-2xl font-bold rounded-xl focus:outline-none modal-input-focus transition-all duration-300 bg-white/10 text-gray-200 border border-white/15 focus:border-amber-300/60"
                  />
                  <button
                    onClick={() => {
                      const currentQty =
                        document.getElementById("quantity-input").value;
                      const newQty = parseInt(currentQty) + 1;
                      document.getElementById("quantity-input").value = newQty;
                      const totalPriceElement = document.getElementById(
                        "total-price-display",
                      );
                      if (totalPriceElement) {
                        totalPriceElement.textContent =
                          (
                            calculateTotalPriceWithExtras() * newQty
                          ).toLocaleString() + " F";
                      }
                    }}
                    className="w-12 h-12 rounded-full text-white hover:opacity-90 transition-all duration-300 flex items-center justify-center modal-button-hover"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => {
                    setShowQuantityModal(false);
                    setSelectedProduct(null);
                    setSelectedExtras([]);
                  }}
                  className="flex-1 py-3 bg-white/10 text-white rounded-2xl font-semibold hover:bg-white/20 transition-all duration-300 modal-button-hover border border-white/10"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    const quantity = parseInt(
                      document.getElementById("quantity-input").value,
                    );
                    addToOrderWithQuantity(quantity);
                  }}
                  className="flex-1 py-3 text-slate-900 rounded-2xl font-semibold hover:opacity-90 transition-all duration-300 modal-button-hover"
                  style={{ backgroundColor: colors.primary }}
                >
                  Ajouter (
                  {(
                    calculateTotalPriceWithExtras() *
                    parseInt(
                      document.getElementById("quantity-input")?.value || 1,
                    )
                  ).toLocaleString()}{" "}
                  F)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de variantes & extras */}
      {showExtrasModal && selectedProductForExtras && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn modal-backdrop">
          <div className="relative w-full max-w-4xl mx-4 shadow-2xl rounded-3xl overflow-hidden transform animate-slideInFromBottom max-h-[95vh] flex flex-col border border-white/10 bg-gradient-to-br from-[#0b1224] via-[#0a1326] to-[#050814] text-white">
            <div className="pointer-events-none absolute inset-0 opacity-60">
              <div className="absolute -top-28 -left-24 h-64 w-64 rounded-full bg-amber-500/15 blur-3xl" />
              <div className="absolute -bottom-28 -right-24 h-72 w-72 rounded-full bg-blue-500/15 blur-3xl" />
            </div>

            <div className="relative z-10 sticky top-0 p-6 border-b border-white/10 bg-white/5 backdrop-blur-sm">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center overflow-hidden">
	                    <img
	                      src={
	                        selectedProductForExtras.image_path ||
	                        selectedProductForExtras.image_url ||
	                        PRODUCT_PLACEHOLDER
	                      }
	                      alt={selectedProductForExtras.name}
	                      decoding="async"
	                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (e.target.dataset.fallbackApplied) return;
                        e.target.dataset.fallbackApplied = "true";
                        e.target.src = PRODUCT_PLACEHOLDER;
                      }}
                    />
                  </div>
                  <div>
                    {!isBoutique && selectedProductForExtras.destination !== "none" && (
                      <p className="text-xs uppercase tracking-[0.3em] text-white/60 flex items-center gap-2">
                        {`Service ${
                          selectedProductForExtras.destination_name ||
                          (selectedProductForExtras.destination === "bar"
                            ? "Bar"
                            : selectedProductForExtras.destination === "cuisine"
                              ? "Cuisine"
                              : selectedProductForExtras.destination || "Production")
                        }`}
                      </p>
                    )}
                    <h3 className="text-2xl font-bold">
                      {selectedProductForExtras.name}
                    </h3>
                    <p className="text-sm text-amber-200">
                      Prix de base : {formatPrice(modalBasePrice)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeExtrasModal}
                  className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors border border-white/15"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="relative z-10 p-6 flex-1 overflow-y-auto">
              {loadingExtras ? (
                <div className="text-center py-12">
                  <div className="w-14 h-14 border-4 border-amber-200/60 border-t-amber-400 rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-white/70">Chargement des options...</p>
                </div>
              ) : (
                <>
                  {showVariationSection && (
                    <div className="mb-8">
                      <h4 className="text-lg font-bold text-white mb-4 flex items-center">
                        <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3">
                          V
                        </div>
                        Variantes disponibles
                        <span className="ml-2 text-sm text-white/60 font-normal">
                          (choisissez une option)
                        </span>
                      </h4>
                      {variationAttributesMeta.length > 0 && (
                        <div className="mb-4 flex flex-wrap gap-2">
                          {variationAttributesMeta.map((attribute) => (
                            <span
                              key={`summary-${attribute.name}`}
                              className={`px-3 py-1 rounded-full border text-xs font-semibold ${
                                variationSelections[attribute.name]
                                  ? "border-emerald-300 bg-emerald-500/10 text-emerald-200"
                                  : "border-white/15 bg-white/5 text-white/60"
                              }`}
                            >
                              {attribute.name} :{" "}
                              {variationSelections[attribute.name] ||
                                "non sélectionné"}
                            </span>
                          ))}
                        </div>
                      )}

                      {variationAttributesMeta.length > 0 && (
                        <div className="flex flex-col md:flex-row md:flex-wrap gap-4">
                          {variationAttributesMeta.map((attribute, index) => {
                            const availableOptions =
                              getAvailableOptionsForAttribute(attribute.name);
                            const availableSet = new Set(availableOptions);
                            const previousAttributes =
                              variationAttributesMeta.slice(0, index);
                            const previousRequired = previousAttributes.every(
                              (attr) => variationSelections[attr.name],
                            );
                            const isDisabledGroup =
                              !previousRequired && index !== 0;
                            return (
                              <div
                                key={attribute.name}
                                className={`flex-1 min-w-[220px] rounded-2xl border p-4 ${
                                  isDisabledGroup
                                    ? "border-white/10 bg-white/5 opacity-50 cursor-not-allowed"
                                    : "border-white/15 bg-white/5"
                                }`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <h5 className="text-lg font-semibold text-white">
                                    {attribute.name}
                                  </h5>
                                  {variationSelections[attribute.name] && (
                                    <span className="text-sm font-semibold text-emerald-200">
                                      {variationSelections[attribute.name]}
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-3">
                                  {attribute.values.map((value) => {
                                    const isSelected =
                                      variationSelections[attribute.name] ===
                                      value;
                                    const isDisabled =
                                      isDisabledGroup ||
                                      (availableSet.size > 0 &&
                                        !availableSet.has(value));
                                    return (
                                      <button
                                        type="button"
                                        key={`${attribute.name}-${value}`}
                                        onClick={() =>
                                          handleAttributeSelection(
                                            attribute.name,
                                            value,
                                            isDisabled,
                                          )
                                        }
                                        disabled={isDisabled}
                                        className={`px-4 py-2 rounded-2xl border text-sm font-semibold transition-all ${
                                          isSelected
                                            ? "border-amber-300 bg-amber-500/10 text-amber-100 shadow-sm"
                                            : "border-white/15 bg-white/5 text-white/70 hover:border-amber-200/60"
                                        } ${
                                          isDisabled
                                            ? "opacity-40 cursor-not-allowed"
                                            : "cursor-pointer"
                                        }`}
                                      >
                                        {value}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {variationAttributesMeta.length === 0 &&
                        variationOptions.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {variationOptions.map((variation) => {
                              const isSelected =
                                selectedVariation?.id === variation.id;
                              return (
                                <button
                                  key={variation.id}
                                  onClick={() => handleVariationSelect(variation)}
                                  className={`p-4 rounded-2xl border-2 transition-all transform hover:scale-105 ${
                                    isSelected
                                      ? "border-amber-300 bg-amber-500/10 shadow-lg shadow-amber-500/20"
                                      : "border-white/15 bg-white/5 hover:border-amber-200/60"
                                  }`}
                                >
                                  <div className="flex items-center justify-between gap-4">
                                    <div className="text-left flex-1">
                                      <h5 className="font-semibold text-white">
                                        {variation.name || "Variante"}
                                      </h5>
                                      <div className="text-amber-200 font-bold mt-2">
                                        {formatPrice(variation.price)}
                                      </div>
                                    </div>
                                    <div
                                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                        isSelected
                                          ? "border-amber-300 bg-amber-200/20"
                                          : "border-white/30"
                                      }`}
                                    >
                                      {isSelected && (
                                        <Check
                                          size={16}
                                          className="text-amber-200"
                                        />
                                      )}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  )}

                  {hasAccompanimentExtras && (
                    <div className="mb-8">
                      <h4 className="text-lg font-bold text-white mb-4 flex items-center">
                        <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3">
                          A
                        </div>
                        Accompagnements
                        <span className="ml-2 text-sm text-white/60 font-normal">
                          {selectedAccompanimentQuantity} sur {modalAccompanimentQuota}
                        </span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {accompanimentExtras.map((extra) => {
                          const isSelected = selectedExtras.some(
                            (selected) => selected.id === extra.id,
                          );
                          const selectedExtra = selectedExtras.find(
                            (selected) => selected.id === extra.id,
                          );
                          const quantity = selectedExtra?.selectedQuantity || 1;
                          const quotaReached =
                            !isSelected &&
                            selectedAccompanimentQuantity >= modalAccompanimentQuota;
                          return (
                            <div
                              key={extra.id}
                              className={`p-4 rounded-2xl border transition-all text-left ${
                                isSelected
                                  ? "border-emerald-300 bg-emerald-500/10 shadow-lg shadow-emerald-500/20"
                                  : "border-white/15 bg-white/5 hover:border-emerald-200/60"
                              } ${quotaReached ? "opacity-45" : ""}`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="text-left">
                                  <h5 className="font-semibold text-white">
                                    {extra.name}
                                  </h5>
                                  <p className="text-sm text-white/70 mt-1">
                                    {extra.description}
                                  </p>
                                  <div className="text-emerald-200 font-bold mt-2">
                                    Gratuit
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  disabled={quotaReached}
                                  onClick={() => toggleExtra(extra, "accompaniment")}
                                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                    isSelected
                                      ? "border-emerald-300 bg-emerald-200/20"
                                      : "border-white/30"
                                  }`}
                                >
                                  {isSelected && (
                                    <Check size={16} className="text-emerald-200" />
                                  )}
                                </button>
                              </div>
                              {isSelected &&
                                (Number(extra.max_quantity || 1) > 1 ||
                                  modalAccompanimentQuota > 1) && (
                                  <div className="mt-3 flex items-center justify-end gap-2">
                                    <button
                                      type="button"
                                      onClick={() => updateExtraQuantity(extra, -1)}
                                      className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center"
                                    >
                                      <Minus size={16} />
                                    </button>
                                    <span className="min-w-[20px] text-center font-semibold">
                                      {quantity}
                                    </span>
                                    <button
                                      type="button"
                                      disabled={
                                        quantity >= Number(extra.max_quantity || 1) ||
                                        selectedAccompanimentQuantity >= modalAccompanimentQuota
                                      }
                                      onClick={() => updateExtraQuantity(extra, 1)}
                                      className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center disabled:opacity-35"
                                    >
                                      <Plus size={16} />
                                    </button>
                                  </div>
                                )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {hasSupplementExtras && (
                    <div className="mb-8">
                      <h4 className="text-lg font-bold text-white mb-4 flex items-center">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3">
                          S
                        </div>
                        Suppléments
                        <span className="ml-2 text-sm text-white/60 font-normal">
                          (frais supplémentaires - choix multiples)
                        </span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {supplementExtras.map((extra) => {
                          const selectedExtra = selectedExtras.find(
                            (selected) => selected.id === extra.id,
                          );
                          const isSelected = Boolean(selectedExtra);
                          const quantity = selectedExtra
                            ? selectedExtra.selectedQuantity || 1
                            : 1;

                          return (
                            <div
                              key={extra.id}
                              className={`p-4 rounded-2xl border transition-all ${
                                isSelected
                                  ? "border-blue-300 bg-blue-500/10 shadow-lg shadow-blue-500/20"
                                  : "border-white/15 bg-white/5 hover:border-blue-200/60"
                              }`}
                            >
                              <div className="flex items-center justify-between mb-3 gap-3">
                                <div className="text-left flex-1">
                                  <h5 className="font-semibold text-white">
                                    {extra.name}
                                  </h5>
                                  <p className="text-sm text-white/70 mt-1">
                                    {extra.description}
                                  </p>
                                  <div className="text-blue-200 font-bold mt-2">
                                    +{extra.price.toLocaleString()} F
                                  </div>
                                </div>
                                <button
                                  onClick={() => toggleExtra(extra, "supplement")}
                                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                    isSelected
                                      ? "border-blue-300 bg-blue-200/20"
                                      : "border-white/30 hover:border-blue-200/70"
                                  }`}
                                >
                                  {isSelected && (
                                    <Check size={16} className="text-blue-200" />
                                  )}
                                </button>
                              </div>

                              {isSelected && extra.max_quantity > 1 && (
                                <div className="flex items-center justify-between">
                                  <div className="text-xs text-white/60 font-semibold">
                                    Quantité
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => updateExtraQuantity(extra, -1)}
                                      className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-white/10"
                                    >
                                      <Minus size={16} />
                                    </button>
                                    <span className="font-semibold text-white min-w-[20px] text-center">
                                      {quantity}
                                    </span>
                                    <button
                                      onClick={() => updateExtraQuantity(extra, 1)}
                                      className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-white/10"
                                    >
                                      <Plus size={16} />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {!loadingExtras &&
                    !showVariationSection &&
                    !hasAccompanimentExtras &&
                    !hasSupplementExtras && (
                      <div className="text-center py-12 text-white/60">
                        <Search size={56} className="mx-auto text-gray-400 mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">
                          Aucune option supplémentaire
                        </h3>
                        <p>
                          Ce produit ne possède pas d'extras configurés pour le
                          moment.
                        </p>
                      </div>
                    )}
                </>
              )}
            </div>

            <div className="relative z-10 bg-white/5 border-t border-white/10 p-6 rounded-b-3xl backdrop-blur">
              <div className="flex flex-col md:flex-row gap-3">
                <button
                  onClick={closeExtrasModal}
                  className="flex-1 py-3 bg-white/10 text-white rounded-2xl font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center border border-white/10"
                >
                  <X className="mr-2" size={18} />
                  Annuler
                </button>
                <button
                  onClick={confirmExtrasSelection}
                  className={`flex-1 py-3 text-slate-900 rounded-2xl font-semibold transition-all duration-300 flex items-center justify-center ${
                    isConfirmExtrasDisabled ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  style={{ backgroundColor: colors.primary }}
                  disabled={isConfirmExtrasDisabled}
                >
                  <ShoppingCart className="mr-2" size={18} />
                  {loadingExtras
                    ? "Chargement..."
                    : `Ajouter au panier (${modalTotalWithExtras.toLocaleString()} F)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Nouveau PaymentInterface */}
      {showPaymentInterface && currentOrderForPayment && (
        <PaymentInterface
          order={currentOrderForPayment}
          onPaymentComplete={handlePaymentComplete}
          onClose={handlePaymentClose}
          currentUser={currentUser}
          currentSession={currentSession}
          onCashSessionMismatch={recoverAuthenticatedCashSession}
          onTableCycleConflict={({ currentCycle }) => {
            const table = selectedTableRef.current;
            if (!table?.id || table.isDelivery) return;
            setShowPaymentInterface(false);
            setCurrentOrderForPayment(null);
            stageTableDraftRecovery({
              tableId: table.id,
              pendingItems: currentOrderRef.current?.items || [],
              occupancyCycle:
                Number(currentCycle) || resolveTableOccupancyCycle(table),
              reason: "cycle-changed-during-payment",
            });
          }}
          activeAggregator={activeAggregator}
          onCounterRecoveryUpdate={(patch) => {
            if (!isCounterContext) return;
            const current =
              fastFoodCartSnapshotRef.current ||
              buildFastFoodCartSnapshot(currentOrderRef.current?.items || []);
            if (!current) return;
            const next = {
              ...current,
              ...patch,
              timestamp: Date.now(),
            };
            fastFoodCartSnapshotRef.current = next;
            writeFastFoodCartSnapshotToStorage(next);
          }}
        />
      )}

      {/* Modal de facture */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn modal-backdrop">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4 transform animate-flipIn glass-effect shadow-2xl">
            <div className="text-center mb-6">
              <h3 className="text-3xl font-bold text-gray-800 mb-2 animate-pulse">
                🧾 Facture
              </h3>
              <p className="text-gray-600">
                {getTableDisplayName(selectedTable)}
              </p>
            </div>

            <div className="mb-6">
              <div className="space-y-3">
                {currentOrder.items.map((item, index) => {
                  // Calculer le prix des extras pour cet article
                  const itemExtrasPrice = item.extras
                    ? item.extras.reduce(
                        (extSum, ext) => extSum + ext.price * ext.quantity,
                        0,
                      )
                    : 0;
                  const totalItemPrice =
                    (item.price + itemExtrasPrice) * item.quantity;

                  return (
                    <div
                      key={item.id}
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-xl transition-all duration-300 hover:bg-gray-100 modal-button-hover"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="flex items-center">
                        <span className="text-2xl mr-3 animate-pulse">Article</span>
                        <div>
                          <div className="font-semibold">{item.name}</div>
                          <div className="text-sm text-gray-600">
                            {item.price.toLocaleString()} F × {item.quantity}
                          </div>
                          {/* Affichage des extras */}
                          {item.extras && item.extras.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              {item.extras.map((extra, extraIndex) => (
                                <span key={extraIndex} className="mr-2">
                                  +{extra.name}{" "}
                                  {extra.quantity > 1
                                    ? `×${extra.quantity}`
                                    : ""}
                                  {extra.price > 0
                                    ? ` (+${(extra.price * extra.quantity).toLocaleString()} F)`
                                    : " (gratuit)"}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="font-bold">
                        {totalItemPrice.toLocaleString()} F
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t mt-4 pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>Sous-total:</span>
                  <span>{getSubtotal().toLocaleString()} F</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-600 animate-pulse">
                    <span>Rabais:</span>
                    <span>-{getDiscountValue().toLocaleString()} F</span>
                  </div>
                )}
                {additionalFees > 0 && (
                  <div className="flex justify-between text-red-600 animate-pulse">
                    <span>Frais:</span>
                    <span>+{getFeesValue().toLocaleString()} F</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-xl border-t pt-2">
                  <span>Total:</span>
                  <span>{getTotal().toLocaleString()} F</span>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowInvoiceModal(false)}
                className="flex-1 py-3 bg-gray-500 text-white rounded-2xl font-semibold hover:bg-gray-600 transition-all duration-300 modal-button-hover"
              >
                Fermer
              </button>
              <button
                onClick={() => {
                  // Ici on pourrait imprimer ou générer un PDF
                  triggerSuccessAnimation("Impression de la facture...");
                  setShowInvoiceModal(false);
                }}
                className="flex-1 py-3 text-white rounded-2xl font-semibold hover:opacity-90 transition-all duration-300 modal-button-hover"
                style={{ backgroundColor: colors.primary }}
              >
                Imprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'annulation OAA - Design moderne et professionnel */}
      {showCancellationModal && cancellationOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-[9999] animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
            {/* En-tête du modal */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-8 py-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white bg-opacity-20 rounded-full p-2">
                    <AlertCircle size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">
                      {cancellationOrder.isCartCancellation
                        ? "Annulation du panier complet"
                        : cancellationOrder.isPartialCancellation
                          ? "Annulation d'article"
                          : "Annulation de commande"}
                    </h3>
                    <p className="text-red-100 text-sm">
                      {cancellationOrder.isCartCancellation
                        ? `${cancellationOrder.items?.length || 0} articles concernés`
                        : `Commande`}
                    </p>
                  </div>
                </div>
                {cancellationOrder.requiresManagerAuth && (
                  <div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-semibold">
                    <Lock size={12} className="inline mr-1" />
                    OAA Requis
                  </div>
                )}
              </div>
            </div>

            {/* Corps du modal */}
            <div className="px-8 py-6">
              {/* Articles concernés */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <ShoppingCart size={16} className="mr-2" />
                  Articles concernés ({cancellationOrder.items?.length || 0})
                </h4>
                <div className="bg-gray-50 rounded-xl p-4 max-h-60 overflow-y-auto">
                  <div className="space-y-3">
                    {(() => {
                      const activeItems = (
                        cancellationOrder.items || []
                      ).filter(isItemActive);

                      if (activeItems.length === 0) {
                        return (
                          <div className="text-center py-8 text-gray-500">
                            <AlertCircle
                              size={48}
                              className="mx-auto mb-4 text-yellow-500"
                            />
                            <h4 className="font-semibold text-gray-700 mb-2">
                              Aucun article à annuler
                            </h4>
                            <p className="text-sm">
                              Tous les articles de cette sélection ont déjà été
                              annulés.
                            </p>
                            <button
                              onClick={resetCancellationModal}
                              className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              Fermer
                            </button>
                          </div>
                        );
                      }

                      return activeItems.map((item, index) => (
                        <div
                          key={item.id || index}
                          className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm border-l-4 border-red-400"
                        >
                          <div className="flex items-center space-x-3">
                            {item.image_path || item.image_url ? (
	                              <img
	                                src={item.image_path || item.image_url}
	                                alt={
	                                  item.product_name || item.name || "Article"
	                                }
	                                loading="lazy"
	                                decoding="async"
	                                className="w-10 h-10 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                                <span className="text-xl">?</span>
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900">
                                {item.product_name ||
                                  item.name ||
                                  "Article inconnu"}
                              </p>
                              <p className="text-sm text-gray-500">
                                Quantité: {item.quantity || 1}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">
                              {(
                                item.total_price ||
                                item.totalPrice ||
                                item.price * item.quantity ||
                                0
                              ).toLocaleString()}{" "}
                              F
                            </p>
                            <div className="flex items-center justify-end mt-1">
                              <div
                                className={`w-2 h-2 rounded-full mr-2 ${getStatusInfo(item.status).color}`}
                              />
                              <span className="text-xs text-gray-600">
                                {getStatusInfo(item.status).text}
                              </span>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>

              {/* Étape 1: Raison de l'annulation */}
              {cancellationStep === "reason" && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
                    <FileText size={16} className="mr-2" />
                    Raison de l'annulation (obligatoire)
                  </label>
                  <textarea
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none transition-all duration-200 resize-none"
                    rows="4"
                    placeholder="Décrivez précisément la raison de cette annulation (erreur de commande, problème en cuisine, demande client, etc.)"
                  />
                  <div className="mt-2 flex justify-between text-xs text-gray-500">
                    <span>Minimum 10 caractères requis</span>
                    <span>{cancellationReason.length}/500</span>
                  </div>
                </div>
              )}

              {/* Étape 2: Authentification manager */}
              {cancellationStep === "auth" && (
                <div className="mb-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center mb-2">
                      <Lock size={16} className="mr-2 text-yellow-600" />
                      <span className="font-semibold text-yellow-800">
                        Authentification Manager Requise
                      </span>
                    </div>
                    <p className="text-sm text-yellow-700">
                      Cette annulation concerne des articles déjà validés en
                      cuisine/bar. Une authentification manager est obligatoire
                      selon les règles OAA.
                    </p>
                  </div>

                  <label className="block text-sm font-semibold text-gray-800 mb-3">
                    Code Manager
                  </label>
                  <input
                    type="password"
                    value={managerPassword}
                    onChange={(e) => setManagerPassword(e.target.value)}
                    className="w-full px-4 py-4 text-center text-xl font-mono border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200"
                    placeholder="Saisissez votre code"
                    maxLength="10"
                    autoFocus
                  />
                  <div className="mt-3 text-center">
                    <p className="text-xs text-gray-600 mb-2">
                      <span className="inline-flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Codes autorisés: Admin, Manager, Superviseur
                      </span>
                    </p>
                    <p className="text-xs text-blue-600">
                      Seul le code manager est accepté pour cette validation.
                    </p>
                  </div>
                </div>
              )}

              {/* Étape 3: Confirmation finale */}
              {cancellationStep === "confirm" && (
                <div className="mb-6">
                  <div className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6">
                    <div className="flex items-start">
                      <AlertCircle
                        size={20}
                        className="mr-3 text-red-500 mt-1 flex-shrink-0"
                      />
                      <div className="flex-1">
                        <h4 className="font-bold text-red-800 mb-3">
                          Confirmation d'annulation définitive
                        </h4>
                        <div className="space-y-2 text-sm text-red-700">
                          <p>
                            <span className="font-semibold">Action:</span>{" "}
                            {cancellationOrder.isCartCancellation
                              ? `Annulation complète du panier (${cancellationOrder.items?.length || 0} articles)`
                              : cancellationOrder.isPartialCancellation
                                ? "Annulation d'un article spécifique"
                                : `Annulation de la commande`}
                          </p>
                          <p>
                            <span className="font-semibold">Raison:</span>{" "}
                            {cancellationReason || "Non spécifiée"}
                          </p>
                          {cancellationOrder.requiresManagerAuth && (
                            <p>
                              <span className="font-semibold">
                                Authentification:
                              </span>{" "}
                              Code manager validé ✓
                            </p>
                          )}
                          <p>
                            <span className="font-semibold">
                              Montant concerné:
                            </span>{" "}
                            {(
                              cancellationOrder.total_amount || 0
                            ).toLocaleString()}{" "}
                            F
                          </p>
                        </div>
                        <div className="mt-4 p-3 bg-white rounded-lg border border-red-200">
                          <p className="text-xs text-red-600 font-semibold">
                            ⚠�? Cette action est irréversible et sera enregistrée
                            dans les logs administrateur
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pied du modal */}
            <div className="px-8 py-6 bg-gray-50 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Étape{" "}
                  {cancellationStep === "reason"
                    ? "1"
                    : cancellationStep === "auth"
                      ? "2"
                      : "3"}{" "}
                  sur 3
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={resetCancellationModal}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 flex items-center"
                  >
                    <X size={16} className="mr-2" />
                    Annuler
                  </button>
                  <button
                    onClick={handleCancellationStep}
                    disabled={
                      (cancellationStep === "reason" &&
                        cancellationReason.length < 10) ||
                      (cancellationStep === "auth" &&
                        !managerPassword.trim()) ||
                      cancellationOrder?.items?.filter(
                        (item) => item.status !== "cancelled",
                      )?.length === 0
                    }
                    className="px-6 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 flex items-center"
                  >
                    {cancellationStep === "reason" && (
                      <>
                        <ChevronRight size={16} className="mr-2" />
                        Suivant
                      </>
                    )}
                    {cancellationStep === "auth" && (
                      <>
                        <Check size={16} className="mr-2" />
                        Valider Code
                      </>
                    )}
                    {cancellationStep === "confirm" && (
                      <>
                        <AlertCircle size={16} className="mr-2" />
                        Confirmer Annulation
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation d'ajout à commande existante */}
      {isAddingToExistingOrder && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 animate-fadeIn modal-backdrop p-4">
          <div className="relative w-full max-w-md mx-4 rounded-3xl overflow-hidden border border-white/10 shadow-2xl transform animate-slideInFromLeft bg-gradient-to-br from-[#0b1224] via-[#0a1326] to-[#050814] text-white">
            <div className="pointer-events-none absolute inset-0 opacity-60">
              <div className="absolute -top-20 -left-16 h-48 w-48 rounded-full bg-amber-500/15 blur-3xl" />
              <div className="absolute -bottom-24 -right-20 h-52 w-52 rounded-full bg-blue-500/15 blur-3xl" />
            </div>

            <div className="relative z-10 p-8 space-y-6">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/10 border border-white/15 shadow-lg">
                  <span className="text-xl">➕</span>
                </div>
                <h3 className="text-2xl font-bold text-white">
                  {selectedTable?.isDelivery
                    ? "Ajouter à la livraison"
                    : isBoutique
                      ? "Ajouter au panier"
                      : "Ajouter à la table"}
                </h3>
                <p className="text-white/70">Nouveaux articles à ajouter</p>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {(() => {
                  const { newItems: allNewItems } = getSeparatedItems();
                  const newItems =
                    courseModeEnabled &&
                    courseModeContextForTable(selectedTable)
                      ? selectCourseDraftItems(
                          allNewItems,
                          selectedDraftCourseNumber,
                        )
                      : allNewItems;
                  const newItemsTotal = newItems.reduce((sum, item) => {
                    const itemExtrasPrice = item.extras
                      ? item.extras.reduce(
                          (extSum, ext) => extSum + ext.price * ext.quantity,
                          0,
                        )
                      : 0;
                    return sum + (item.price + itemExtrasPrice) * item.quantity;
                  }, 0);

                  return (
                    <>
                      {newItems.map((item, index) => {
                        const itemExtrasPrice = item.extras
                          ? item.extras.reduce(
                              (extSum, ext) =>
                                extSum + ext.price * ext.quantity,
                              0,
                            )
                          : 0;
                        const totalItemPrice =
                          (item.price + itemExtrasPrice) * item.quantity;

                        return (
                          <div
                            key={`new-${item.id}-${index}`}
                            className="p-3 rounded-xl transition-all duration-300 border border-white/10 bg-white/5 hover:border-amber-200/40 mb-2"
                            style={{ animationDelay: `${index * 0.1}s` }}
                          >
                            <div className="flex justify-between items-center gap-3">
                              <div className="flex items-center">
                                <div className="w-7 h-7 rounded-lg bg-white/10 border border-white/15 overflow-hidden mr-2">
	                                  <img
	                                    src={
	                                      item.image_path ||
	                                      item.image_url ||
	                                      PRODUCT_PLACEHOLDER
	                                    }
	                                    alt={item.name}
	                                    loading="lazy"
	                                    decoding="async"
	                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      if (e.target.dataset.fallbackApplied)
                                        return;
                                      e.target.dataset.fallbackApplied = "true";
                                      e.target.src = PRODUCT_PLACEHOLDER;
                                    }}
                                  />
                                </div>
                                <span className="font-semibold text-white">
                                  {item.name}
                                </span>
                              </div>
                              <div className="text-right">
                                <span className="text-sm text-white/70">
                                  ×{item.quantity}
                                </span>
                                <div className="font-bold text-amber-200">
                                  {totalItemPrice.toLocaleString()} F
                                </div>
                              </div>
                            </div>

                            {item.product_type === "variable" &&
                              getItemVariationDisplay(item) && (
                              <div className="mt-2 pl-6 border-l border-purple-300/40">
                                <div className="flex items-center mb-1">
                                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-purple-500 text-white text-[10px] font-bold mr-2">
                                    V
                                  </span>
                                  <span className="text-xs font-medium text-purple-200">
                                    Variante sélectionnée :
                                  </span>
                                </div>
                                <div className="text-xs text-white/80 font-semibold">
                                  {getItemVariationDisplay(item)}
                                </div>
                              </div>
                            )}

                            {item.extras && item.extras.length > 0 && (
                              <div className="mt-2 pl-6 border-l border-blue-300/30">
                                <div className="flex items-center mb-1">
                                  <span className="text-xs font-medium text-blue-200 mr-1">
                                    ✨
                                  </span>
                                  <span className="text-xs font-medium text-blue-200">
                                    Extras sélectionnés :
                                  </span>
                                </div>
                                <div className="space-y-1">
                                  {item.extras.map((extra, extraIndex) => (
                                    <div
                                      key={extraIndex}
                                      className="flex justify-between items-center text-xs text-white/80"
                                    >
                                      <div className="flex items-center">
                                        <span
                                          className={`inline-block w-4 h-4 rounded text-center text-white text-[10px] font-bold mr-2 ${
                                            extra.type === "accompaniment"
                                              ? "bg-emerald-500"
                                              : "bg-orange-500"
                                          }`}
                                        >
                                          {extra.type === "accompaniment"
                                            ? "A"
                                            : "S"}
                                        </span>
                                        <span>
                                          {extra.name}{" "}
                                          {extra.quantity > 1
                                            ? `×${extra.quantity}`
                                            : ""}
                                        </span>
                                      </div>
                                      <span
                                        className={`font-medium ${
                                          extra.type === "accompaniment"
                                            ? "text-emerald-200"
                                            : "text-amber-200"
                                        }`}
                                      >
                                        {extra.type === "accompaniment"
                                          ? "Gratuit"
                                          : `+${(extra.price * extra.quantity).toLocaleString()} F`}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => setIsAddingToExistingOrder(false)}
                  disabled={isOrderActionInProgress}
                  className="flex-1 py-3 bg-white/10 text-white rounded-2xl font-semibold hover:bg-white/20 transition-all duration-300 modal-button-hover border border-white/10"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    const targetCourseNumber = selectedDraftCourseNumber;
                    const added = await addToExistingOrder(
                      undefined,
                      courseModeEnabled &&
                        courseModeContextForTable(selectedTable)
                        ? {
                            suppressDispatch: true,
                            courseNumber: selectedDraftCourseNumber,
                          }
                        : {},
                    );
                    if (
                      added &&
                      courseModeEnabled &&
                      courseModeContextForTable(selectedTable)
                    ) {
                      await advanceAfterCourseMutation({
                        orderId: getCurrentCourseOrderId(),
                        courses: orderCoursesRef.current,
                        courseNumber: targetCourseNumber,
                      });
                    }
                  }}
                  disabled={isOrderActionInProgress}
                  className="flex-1 py-3 text-slate-900 rounded-2xl font-semibold hover:opacity-90 transition-all duration-300 modal-button-hover"
                  style={{ backgroundColor: colors.primary }}
                >
                  {isOrderActionInProgress ? (
                    <span className="inline-flex items-center justify-center gap-2">
                      <RefreshCw className="animate-spin" size={18} />
                      Ajout...
                    </span>
                  ) : (
                    "Confirmer l'ajout"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS pour les animations modernes */}
      <style>{`
        /* Animations d'entrée */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInFromTop {
          from {
            opacity: 0;
            transform: translateY(-100px) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes slideInFromBottom {
          from {
            opacity: 0;
            transform: translateY(100px) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes slideInFromLeft {
          from {
            opacity: 0;
            transform: translateX(-100px) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes slideInFromRight {
          from {
            opacity: 0;
            transform: translateX(100px) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3) translateY(-50px);
          }
          50% {
            opacity: 1;
            transform: scale(1.05) translateY(0);
          }
          70% {
            transform: scale(0.9) translateY(0);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes flipIn {
          0% {
            opacity: 0;
            transform: perspective(400px) rotateY(90deg);
          }
          40% {
            transform: perspective(400px) rotateY(-20deg);
          }
          60% {
            transform: perspective(400px) rotateY(10deg);
          }
          80% {
            transform: perspective(400px) rotateY(-5deg);
          }
          100% {
            opacity: 1;
            transform: perspective(400px) rotateY(0deg);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: translateX(-5px);
          }
          20%,
          40%,
          60%,
          80% {
            transform: translateX(5px);
          }
        }

        @keyframes wiggle {
          0%,
          7% {
            transform: rotateZ(0);
          }
          15% {
            transform: rotateZ(-15deg);
          }
          20% {
            transform: rotateZ(10deg);
          }
          25% {
            transform: rotateZ(-10deg);
          }
          30% {
            transform: rotateZ(6deg);
          }
          35% {
            transform: rotateZ(-4deg);
          }
          40%,
          100% {
            transform: rotateZ(0);
          }
        }

        /* Classes d'animation */
        .animate-fadeIn {
          animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .animate-slideInFromTop {
          animation: slideInFromTop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .animate-slideInFromBottom {
          animation: slideInFromBottom 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .animate-slideInFromLeft {
          animation: slideInFromLeft 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .animate-slideInFromRight {
          animation: slideInFromRight 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .animate-bounceIn {
          animation: bounceIn 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }

        .animate-zoomIn {
          animation: zoomIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .animate-flipIn {
          animation: flipIn 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .animate-pulse {
          animation: pulse 2s infinite;
        }

        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }

        .animate-wiggle {
          animation: wiggle 1s ease-in-out;
        }

        /* Effets de hover améliorés */
        .modal-button-hover {
          transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.18s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.18s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .modal-button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }

        .modal-button-hover:active {
          transform: translateY(0);
        }

        /* Effets de focus améliorés */
        .modal-input-focus:focus {
          transform: scale(1.02);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        /* Backdrop blur */
        .modal-backdrop {
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }

        /* Effet de glassmorphism */
        .glass-effect {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        /* Animation de chargement */
        @keyframes loadingPulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .loading-pulse {
          animation: loadingPulse 1.5s ease-in-out infinite;
        }

        /* Effet de succès */
        @keyframes successBounce {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        .success-bounce {
          animation: successBounce 0.6s ease-out;
        }

        /* Effet d'erreur */
        @keyframes errorShake {
          0%,
          100% {
            transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: translateX(-10px);
          }
          20%,
          40%,
          60%,
          80% {
            transform: translateX(10px);
          }
        }

        .error-shake {
          animation: errorShake 0.8s ease-in-out;
        }

        /* Animations pour le slide in/out des tables */
        @keyframes slideOutLeft {
          from {
            opacity: 1;
            transform: translateX(0);
            width: auto;
          }
          to {
            opacity: 0;
            transform: translateX(-100%);
            width: 0;
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-100%);
            width: 0;
          }
          to {
            opacity: 1;
            transform: translateX(0);
            width: auto;
          }
        }

        .slide-out-left {
          animation: slideOutLeft 0.5s ease-in-out forwards;
        }

        .slide-in-left {
          animation: slideInLeft 0.5s ease-in-out forwards;
        }

        /* Styles pour les boutons de toggle */
        .toggle-button {
          transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.18s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.18s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .toggle-button:hover {
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .toggle-button:active {
          transform: translateY(0) scale(1);
        }

        /* Animation pour les groupes de boutons */
        @keyframes slideInFromTop {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slide-in-top {
          animation: slideInFromTop 0.4s ease-out;
        }

        /* Styles pour scrollbars personnalisées */
        .scrollbar-thin {
          scrollbar-width: thin;
        }

        .scrollbar-thumb-gray-300::-webkit-scrollbar-thumb {
          background-color: #d1d5db;
          border-radius: 0.375rem;
        }

        .scrollbar-track-gray-100::-webkit-scrollbar-track {
          background-color: #f3f4f6;
        }

        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
        }
      `}</style>

      {/* Notifications animées */}
      {showSuccessAnimation && (
        <div className="caisse-transient-notification fixed top-4 right-4 z-[9999] animate-slideInFromRight">
          <div
            role="status"
            aria-label="Notification de succès. Faites-la glisser pour la fermer."
            onPointerDown={beginNotificationSwipe}
            onPointerMove={moveNotificationSwipe}
            onPointerUp={(event) => finishNotificationSwipe(event)}
            onPointerCancel={(event) => finishNotificationSwipe(event, true)}
            className="caisse-transient-notification-card bg-green-500 text-black px-6 py-4 rounded-2xl shadow-2xl glass-effect success-bounce select-none cursor-grab active:cursor-grabbing"
            style={{
              touchAction: "pan-y",
              transform: `translateX(${notificationSwipeOffset}px)`,
              opacity: Math.max(0.35, 1 - Math.abs(notificationSwipeOffset) / 220),
              transition: notificationSwipeDragging
                ? "none"
                : "transform 180ms ease, opacity 180ms ease",
            }}
          >
            <div className="flex items-center space-x-3">
              <div className="text-2xl">✅</div>
              <div>
                <div className="font-semibold text-black">Succès!</div>
                <div className="text-sm text-gray-800">{successAnimationMessage}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showErrorAnimation && (
        <div className="caisse-transient-notification fixed top-4 right-4 z-[9999] animate-slideInFromRight">
          <div
            role="alert"
            aria-label="Notification d’erreur. Faites-la glisser pour la fermer."
            onPointerDown={beginNotificationSwipe}
            onPointerMove={moveNotificationSwipe}
            onPointerUp={(event) => finishNotificationSwipe(event)}
            onPointerCancel={(event) => finishNotificationSwipe(event, true)}
            className="caisse-transient-notification-card bg-red-500 text-white px-6 py-4 rounded-2xl shadow-2xl glass-effect error-shake select-none cursor-grab active:cursor-grabbing"
            style={{
              touchAction: "pan-y",
              transform: `translateX(${notificationSwipeOffset}px)`,
              opacity: Math.max(0.35, 1 - Math.abs(notificationSwipeOffset) / 220),
              transition: notificationSwipeDragging
                ? "none"
                : "transform 180ms ease, opacity 180ms ease",
            }}
          >
            <div className="flex items-center space-x-3">
              <div className="text-2xl">⚠️</div>
              <div>
                <div className="font-semibold text-black">Erreur!</div>
                <div className="text-sm text-gray-900 bg-white/90 px-2 py-1 rounded font-medium">
                  {errorAnimationMessage?.replace(
                    /^(⚠️\s*)?(Erreur!?\s*:?\s*)?/i,
                    "",
                  ) || errorAnimationMessage}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {draftPreservedNotice && (
        <div className="pointer-events-none fixed right-4 top-20 z-[9998] w-[min(88vw,24rem)] animate-slideInFromRight">
          <div
            role="status"
            aria-live="polite"
            className="rounded-xl border border-amber-300/35 bg-slate-950/95 px-4 py-3 text-sm font-semibold text-amber-100 shadow-xl backdrop-blur"
          >
            {draftPreservedNotice}
          </div>
        </div>
      )}

      {/* Modal de saisie du nombre de clients */}
      {showCustomerCountModal && !isBoutique && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 animate-fadeIn modal-backdrop p-4">
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-gradient-to-br from-[#020617] via-[#020b1f] to-[#020617] text-white animate-bounceIn">
            <div className="pointer-events-none absolute inset-0 opacity-60">
              <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl" />
              <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            <div className="relative z-10 p-8">
              <div className="text-center mb-6 space-y-2">
                <div className="w-20 h-20 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-4xl mx-auto shadow-lg">
                  👥
                </div>
                <h3 className="text-2xl font-bold text-white mt-2">
                  Table {pendingTableSelection?.number}
                </h3>
                <div className="text-base text-white/70">
                  Combien de clients à cette table ?
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Nombre de clients
                </label>
                <div className="flex items-center justify-center space-x-4">
                  <button
                    onClick={() => {
                      const currentValue =
                        customerCount === "" ? 0 : parseInt(customerCount);
                      setCustomerCount(
                        Math.max(0, currentValue - 1).toString(),
                      );
                    }}
                    className="w-12 h-12 rounded-full transition-all duration-300 flex items-center justify-center modal-button-hover bg-white/10 text-white border border-white/15 hover:bg-white/20"
                  >
                    <Minus size={20} />
                  </button>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    placeholder="0"
                    value={customerCount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (
                        value === "" ||
                        (parseInt(value) >= 0 && parseInt(value) <= 20)
                      ) {
                        setCustomerCount(value);
                      }
                    }}
                    className="w-20 h-12 text-center text-2xl font-bold rounded-xl focus:outline-none modal-input-focus transition-all duration-300 bg-white/10 text-gray-200 border border-white/15 focus:border-amber-300/60"
                  />
                  <button
                    onClick={() => {
                      const currentValue =
                        customerCount === "" ? 0 : parseInt(customerCount);
                      setCustomerCount(
                        Math.min(20, currentValue + 1).toString(),
                      );
                    }}
                    className="w-12 h-12 rounded-full text-white hover:opacity-90 transition-all duration-300 flex items-center justify-center modal-button-hover"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <Plus size={20} />
                  </button>
                </div>
                <div className="text-xs text-white/60 mt-2 text-center">
                  Laissez vide si vous ne souhaitez pas saisir cette information
                </div>
              </div>

              {courseModeEligibleForTable(pendingTableSelection) && (
                <div className="mb-6 flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                  <div className="font-semibold text-white">
                    Commande en plusieurs tours
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={courseModeEnabled}
                    onClick={() => setCourseModeEnabled((enabled) => !enabled)}
                    className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${
                      courseModeEnabled ? "bg-amber-400" : "bg-white/20"
                    }`}
                  >
                    <span
                      className={`absolute left-1 top-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                        courseModeEnabled ? "translate-x-6" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    void releasePendingTableReservation(pendingTableSelection);
                    setShowCustomerCountModal(false);
                    setPendingTableSelection(null);
                    setCourseModeEnabled(false);
                    setActiveCourseNumber(1);
                    orderCoursesRef.current = [];
                    setOrderCourses([]);
                  }}
                  className="flex-1 py-3 bg-white/10 text-white rounded-2xl font-semibold hover:bg-white/20 transition-all duration-300 modal-button-hover border border-white/10"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmTableSelection}
                  className="flex-1 py-3 text-slate-900 rounded-2xl font-semibold hover:opacity-90 transition-all duration-300 modal-button-hover"
                  style={{ backgroundColor: colors.primary }}
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation suppression */}
      {showPermissionModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#0b1328] text-gray-800 dark:text-white rounded-2xl w-full max-w-md p-6 shadow-2xl border border-white/10 animate-scaleIn">
            <h3 className="text-lg font-bold mb-3">Action non autorisée</h3>
            <p className="text-sm text-gray-600 dark:text-white/70 mb-6">
              {permissionMessage ||
                "Vous ne pouvez pas procéder à l'annulation, faites appel au manager."}
            </p>
            <div className="flex justify-between items-center gap-3">
              <button
                onClick={() => {
                  if (pendingApprovalContext) {
                    setShowPermissionModal(false);
                    openManagerApproval(
                      pendingApprovalContext.mode,
                      pendingApprovalContext.item,
                    );
                    setPendingApprovalContext(null);
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 font-semibold shadow hover:opacity-90"
              >
                <span role="img" aria-label="validation">
                  OK
                </span>
                Faire valider par un manager
              </button>
              <button
                onClick={() => setShowPermissionModal(false)}
                className="px-4 py-2 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors text-sm"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {managerApproval.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-3xl mx-4 rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border border-white/10 shadow-2xl overflow-hidden animate-scaleIn">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-white/5">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-amber-300">
                  {managerApproval.requiresManagerAuth
                    ? "Validation manager requise"
                    : "Annulation autorisée"}
                </p>
                <h3 className="text-xl font-semibold">
                  {managerApproval.mode === "cart"
                    ? "Annuler tout le panier"
                    : "Annuler un article"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() =>
                  setManagerApproval((prev) => ({ ...prev, open: false }))
                }
                className="rounded-full bg-white/10 text-white hover:bg-white/20 px-3 py-2 text-sm font-semibold"
              >
                Fermer
              </button>
            </div>

            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {managerApproval.mode === "item" && managerApproval.item && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                    <p className="text-xs uppercase tracking-[0.2em] text-white/60">
                      Article ciblé
                    </p>
                    <p className="text-lg font-semibold">
                      {managerApproval.item.name}
                    </p>
                    {hasVariationDisplay(managerApproval.item) ? (
                      <p className="text-sm text-white/70">
                        Variante : {getItemVariationDisplay(managerApproval.item)}
                      </p>
                    ) : managerApproval.item.extras?.length ? (
                      <div className="text-sm text-white/70 space-y-1 mt-1">
                        <p className="font-semibold">Extras :</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          {managerApproval.item.extras.map((ext, idx) => (
                            <li key={idx}>
                              {ext.quantity && ext.quantity > 1
                                ? `${ext.quantity} × `
                                : ""}
                              {ext.name}{" "}
                              <span className="text-xs text-white/60">
                                [{ext.type === "accompaniment" ? "A" : "S"}]
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    <p className="text-sm text-white/70">
                      Quantité actuelle : {managerApproval.item.quantity}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-3 flex flex-col gap-2">
                    <label className="text-sm font-semibold text-white">
                      Quantité à annuler
                    </label>
                    <div className="grid grid-cols-[48px_minmax(72px,1fr)_48px] gap-2">
                      <button
                        type="button"
                        aria-label="Diminuer la quantité à annuler"
                        disabled={managerApproval.qty <= 1}
                        onClick={() => setManagerApproval((prev) => ({ ...prev, qty: Math.max(1, prev.qty - 1) }))}
                        className="rounded-xl border border-white/10 bg-white/10 text-2xl font-semibold disabled:opacity-30"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={1}
                        max={managerApproval.maxQty}
                        value={managerApproval.qty}
                        onChange={(e) =>
                          setManagerApproval((prev) => ({
                            ...prev,
                            qty: Math.max(
                              1,
                              Math.min(Number(e.target.value) || 1, prev.maxQty),
                            ),
                          }))
                        }
                        className="min-w-0 rounded-xl bg-slate-950/60 border border-white/10 px-3 py-2 text-center text-lg font-semibold text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                      />
                      <button
                        type="button"
                        aria-label="Augmenter la quantité à annuler"
                        disabled={managerApproval.qty >= managerApproval.maxQty}
                        onClick={() => setManagerApproval((prev) => ({ ...prev, qty: Math.min(prev.maxQty, prev.qty + 1) }))}
                        className="rounded-xl border border-white/10 bg-white/10 text-2xl font-semibold disabled:opacity-30"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-semibold text-white">
                  Motif d'annulation
                </label>
                <textarea
                  rows={3}
                  value={managerApproval.reason}
                  onChange={(e) =>
                    setManagerApproval((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  className="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                  placeholder="Ex: erreur de commande, client s'est désisté..."
                />
              </div>

              {managerApproval.requiresManagerAuth && (
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-white">
                    Code manager (PIN)
                  </label>
                  <input
                    type="password"
                    value={managerApproval.pin}
                    onChange={(e) =>
                      setManagerApproval((prev) => ({
                        ...prev,
                        pin: e.target.value,
                      }))
                    }
                    className="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                    placeholder="Saisir le code"
                  />
                </div>
              </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10 bg-white/5">
              <button
                type="button"
                onClick={() =>
                  setManagerApproval((prev) => ({ ...prev, open: false }))
                }
                className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/15 text-white hover:border-amber-300/60"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleManagerApprove}
                disabled={isManagerValidating}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 shadow ${
                  isManagerValidating ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                {isManagerValidating ? "Validation..." : "Valider"}
              </button>
            </div>
          </div>
        </div>
      )}
      <DiscountModal
        open={showInvoiceGenerationModal}
        tableLabel={getTableDisplayName(selectedTable)}
        customerCount={Number(selectedTable?.customer_count || selectedTable?.customerCount || 0)}
        items={invoiceItems}
        adjustments={tempAdjustments}
        draft={discountDraft}
        setDraft={setDiscountDraft}
        canApply={canApplyDiscount() && persistedSplitBill?.status !== "active"}
        globalDisabledReason={persistedSplitBill?.status === "active"
          ? "La table est divisée. Réduisez chaque facture depuis « Diviser »."
          : ""}
        formatPrice={formatPrice}
        formatItemName={formatInvoiceItemName}
        computeItemTotal={computeItemTotalWithExtras}
        getItemKey={getInvoiceItemKey}
        onOpenItem={openItemDiscountModal}
        onRemove={handleRemoveDiscount}
        onApplyGlobal={handleApplyDiscount}
        onClose={() => closeInvoiceGenerationModal({ force: true })}
      />

      {/* Ancienne présentation conservée hors affichage pendant la migration UX. */}
      {false && showInvoiceGenerationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md">
          <div
            className="relative w-full max-w-5xl mx-4 overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-gradient-to-br from-[#0b1328] via-[#0f1c36] to-[#0b1328] text-white max-h-[90vh] h-[90vh]"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(124,92,255,0.25),transparent_35%),radial-gradient(circle_at_80%_10%,rgba(59,130,246,0.2),transparent_30%),radial-gradient(circle_at_30%_80%,rgba(16,185,129,0.15),transparent_30%)] opacity-90 pointer-events-none" />
            <div className="relative z-10 flex flex-col h-full">
              <div className="flex items-start justify-between px-6 py-5 border-b border-white/10 bg-white/5 backdrop-blur">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center text-amber-300 shadow-lg">
                    <Receipt className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-amber-200">
                      Facturation caisse
                    </p>
                    <h3 className="text-2xl font-bold leading-tight">
                      Génération de facture
                    </h3>
                    <p className="text-sm text-white/70">
                      {invoiceTotalQuantity} article(s)
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-2 text-right">
                    <p className="text-xs text-white/60">Total estimé</p>
                    <p className="text-lg font-bold text-amber-200">
                      {formatPrice(getTotalWithAdjustments())}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => closeInvoiceGenerationModal({ force: true })}
                    className="rounded-2xl bg-white/10 hover:bg-white/20 border border-white/10 p-3 transition-colors"
                    aria-label="Fermer"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-4 p-6 flex-1 overflow-hidden min-h-0">
                <div className="lg:col-span-2 flex flex-col gap-4 h-full overflow-hidden min-h-0">
                  <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4 flex flex-col h-full min-h-0">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-amber-200">
                          Détail commande
                        </p>
                        <h4 className="text-lg font-semibold">
                          Articles et suppléments
                        </h4>
                      </div>
                      <div className="text-right text-xs text-white/60">
                        <p>{invoiceTotalQuantity} article(s)</p>
                        <p className="font-semibold text-white">
                          {formatPrice(getSubtotalWithExisting())}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3 flex-1 overflow-y-auto pr-1">
                      {invoiceItems.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 p-6 text-center text-white/60">
                          {isBoutique
                            ? "Aucun article à facturer pour ce panier."
                            : "Aucun article à facturer pour cette table."}
                        </div>
                      ) : (
                        invoiceItems.map((item, index) => {
                          const extrasUnit = getItemExtrasUnitTotal(item);
                          const totalExtras = extrasUnit * (item.quantity || 1);
                          const totalPrice = computeItemTotalWithExtras(item);
                          const variation = getItemVariationLabel(item);
                          return (
                            <div
                              key={item.__invoiceKey || index}
                              className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur px-4 py-3 hover:-translate-y-0.5 hover:border-amber-300/40 transition-all duration-200"
                              role="button"
                              onClick={() => openItemDiscountModal(item)}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="px-2 py-1 rounded-lg bg-white/10 border border-white/10 text-xs font-semibold">
                                    {item.quantity}x
                                  </span>
                                  <p className="font-semibold leading-tight">
                                    {formatInvoiceItemName(item)}
                                  </p>
                                </div>
                                  {item.extras?.length ? (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {item.extras.map((extra, extraIndex) => {
                                        const extraQty =
                                          extra?.selectedQuantity ??
                                          extra?.quantity ??
                                          1;
                                        const extraTotal =
                                          (Number(extra?.price) || 0) * extraQty;
                                        return (
                                          <span
                                            key={`${item.__invoiceKey || index}-extra-${extraIndex}`}
                                            className="px-2 py-1 rounded-xl bg-white/10 border border-white/10 text-xs text-white/80"
                                          >
                                            + {extra.name}
                                            {extraQty > 1 ? ` ×${extraQty}` : ""}
                                            {" · "}
                                            {extraTotal > 0
                                              ? formatPrice(extraTotal)
                                              : "offert"}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  ) : null}
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-amber-200">
                                    {formatPrice(totalPrice)}
                                  </p>
                                  {totalExtras > 0 && (
                                    <p className="text-xs text-white/60">
                                      Incl. suppléments: {formatPrice(totalExtras)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                </div>

                <div className="flex flex-col gap-4 h-full overflow-hidden min-h-0">
                  <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4 space-y-4 flex-1 min-h-0 overflow-y-auto pr-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-amber-200">
                          Réductions panier
                        </p>
                        <h4 className="text-lg font-semibold">
                          Remise globale
                        </h4>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                          canApplyDiscount()
                            ? "border-emerald-300 text-emerald-200 bg-emerald-500/10"
                            : "border-red-300 text-red-200 bg-red-500/10"
                        }`}
                      >
                        {canApplyDiscount() ? "Permissions OK" : "Bloqué"}
                      </span>
                    </div>

                    {!hasItemDiscounts && (
                    <>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-white/80">
                          Valeur
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={discountDraft.value}
                          onChange={(e) =>
                            setDiscountDraft((prev) => ({
                              ...prev,
                              value: e.target.value,
                            }))
                          }
                          className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-white/80">
                          Unité
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setDiscountDraft((prev) => ({
                                ...prev,
                                unit: "amount",
                              }))
                            }
                            className={`px-3 py-2 rounded-xl border text-sm font-semibold transition ${
                              discountDraft.unit === "amount"
                                ? "border-amber-300 bg-amber-500/10 text-amber-100"
                                : "border-white/10 bg-white/5 text-white/70 hover:border-white/20"
                            }`}
                          >
                            F
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setDiscountDraft((prev) => ({
                                ...prev,
                                unit: "percentage",
                              }))
                            }
                            className={`px-3 py-2 rounded-xl border text-sm font-semibold transition ${
                              discountDraft.unit === "percentage"
                                ? "border-amber-300 bg-amber-500/10 text-amber-100"
                                : "border-white/10 bg-white/5 text-white/70 hover:border-white/20"
                            }`}
                          >
                            %
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-white/80">
                        Motif
                      </label>
                      <textarea
                        rows={3}
                        value={discountDraft.reason}
                        onChange={(e) =>
                          setDiscountDraft((prev) => ({
                            ...prev,
                            reason: e.target.value,
                          }))
                        }
                        className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                        placeholder="Ex : geste commercial, plat en retard..."
                      />
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleApplyDiscount();
                      }}
                      disabled={!canApplyDiscount()}
                      className={`w-full py-3 rounded-2xl font-semibold flex items-center justify-center gap-2 transition ${
                        canApplyDiscount()
                          ? "bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 hover:shadow-lg hover:shadow-amber-500/30"
                          : "bg-white/10 text-white/50 cursor-not-allowed"
                      }`}
                    >
                      <Plus size={18} />
                      Appliquer la remise globale
                    </button>
                    </>
                    )}

                    <div className="space-y-2">
                      <p className="text-xs text-white/60">
                        Réductions appliquées
                      </p>
                      {tempAdjustments.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-white/15 bg-white/5 px-3 py-4 text-center text-white/50 text-sm">
                          Aucune réduction pour le moment.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {tempAdjustments.map((adj) => (
                            <div
                              key={adj.id}
                              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 flex items-start justify-between gap-2"
                            >
                              <div className="space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-200 text-[11px] font-semibold border border-emerald-400/30">
                                    {adj.scope === "item" ? "Article" : "Panier"}
                                  </span>
                                  <span className="text-xs text-white/60">
                                    {adj.targetLabel || "Panier"}
                                  </span>
                                </div>
                                <p className="font-semibold leading-tight">
                                  -{formatPrice(adj.amount)}
                                </p>
                                {adj.reason ? (
                                  <p className="text-xs text-white/60">
                                    Motif : {adj.reason}
                                  </p>
                                ) : null}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveDiscount(adj.id)}
                                className="text-white/60 hover:text-red-300 transition"
                                aria-label="Supprimer la réduction"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-amber-200">
                          Synthèse
                        </p>
                        <h4 className="text-lg font-semibold">Totaux</h4>
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold border border-white/10 bg-white/5 text-white/70">
                        Suppléments inclus
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span>Sous-total</span>
                        <span className="font-semibold">
                          {formatPrice(getSubtotalWithExisting())}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-emerald-200">
                        <span>Réductions</span>
                        <span>-{formatPrice(getDiscountTotal())}</span>
                      </div>
                      <div className="border-t border-white/10 pt-3 flex items-center justify-between">
                        <span className="font-semibold">Total à facturer</span>
                        <span className="text-xl font-bold text-amber-200">
                          {formatPrice(getTotalWithAdjustments())}
                        </span>
                      </div>
                    </div>
                    {!isCurrentTableSplit && (
                      <button
                        type="button"
                        onClick={initializeSplit}
                        className="w-full mt-2 px-3 py-3 rounded-2xl border border-amber-300/40 bg-amber-500/10 text-amber-100 font-semibold hover:border-amber-300 hover:bg-amber-500/20 transition flex items-center justify-center gap-2"
                      >
                        <Scissors size={18} /> Scinder la facture
                      </button>
                    )}
                  </div>

                </div>
              </div>

              <div className="px-6 py-4 border-t border-white/10 bg-white/5 flex flex-wrap gap-3 justify-end">
                <button
                  onClick={() => closeInvoiceGenerationModal({ force: true })}
                  className="px-4 py-3 rounded-2xl bg-white/5 text-white border border-white/10 hover:border-white/20 transition"
                >
                  Annuler
                </button>
                <button
                    onClick={async () => {
                      if (isCurrentTableSplit) {
                        await openStoredSplitPayment(null);
                        return;
                      }
                      const started = await processPayment();
                      if (started) setShowInvoiceGenerationModal(false);
                    }}
                    className="px-5 py-3 rounded-2xl bg-emerald-500 text-slate-900 font-semibold hover:bg-emerald-400 transition flex items-center gap-2"
                  >
                    <DollarSign size={18} />
                    {isCurrentTableSplit ? "Payer le solde" : "Paiement global"}
                  </button>
                <button
                  onClick={() => {
                    if (!selectedTable) {
                      triggerErrorAnimation(
                        isBoutique
                          ? "Aucun panier actif."
                          : "Aucune table sélectionnée !",
                      );
                      return;
                    }

                    const userLabel = getUserShortLabel(currentUser);
                    const serviceStarterLabel = resolveServiceStarterName({
                      table: selectedTable,
                      fallbackUser: currentUser,
                    });

                    const tableLabelRaw = normalizeTableLabel(selectedTable);
                    const tableNumberOnly =
                      selectedTable?.number !== undefined &&
                      selectedTable?.number !== null
                        ? String(selectedTable.number)
                        : (() => {
                            const raw = String(tableLabelRaw || "");
                            const m = raw.match(/(\d+)/);
                            if (m?.[1]) return m[1];
                            return (
                              raw.replace(/^table\s*:?\s*/i, "").trim() || raw
                            );
                          })();
                    const mergedInvoiceItems = invoiceItems.map((item, index) => {
                      const displayName = formatInvoiceItemName(item);
                      return {
                        ...item,
                        name: displayName,
                        display_name: displayName,
                        _printKey: getInvoiceItemKey(item, index),
                      };
                    });

                    const invoiceData = {
                      table_number: tableNumberOnly,
                      area_name: printAreaOnDocuments ? (selectedTable?.zone_name || selectedTable?.zone || "") : "",
                      cashier_name: userLabel,
                      server_name: serviceStarterLabel,
                      subtotal: getSubtotalWithExisting(),
                      total_adjustments: -getDiscountTotal(),
                      final_total: getTotalWithAdjustments(),
                      items: mergedInvoiceItems,
                      adjustments: tempAdjustments,
                      restaurant_name: restaurantInfo?.name || "Restaurant",
                      restaurant_address: restaurantInfo?.address || "",
                      restaurant_phone: restaurantInfo?.phone || "",
                      timestamp: new Date(),
                    };
                    void printInvoice(invoiceData);
                    closeInvoiceGenerationModal({ force: true });
                    scheduleLoadTables("invoice-generated");
                    if (selectedTable) {
                      loadTableOrders(selectedTable.id);
                    }
                    return;
                    /* Legacy printWindow supprimé (utiliser `printInvoice`) :
                     * - évite le double template/les incohérences USB/Ethernet
                     * - réduit le poids du bundle
                     *
                    const adjustmentsDiscount = adjustmentsList.reduce(
                      (sum, adj) => sum + (Number(adj?.amount) || 0),
                      0,
                    );
                    const totalAdjustmentsRaw = Number(
                      invoiceData.total_adjustments || 0,
                    );
                    const invoiceDiscount = Math.max(
                      0,
                      adjustmentsDiscount,
                      totalAdjustmentsRaw < 0 ? Math.abs(totalAdjustmentsRaw) : 0,
                    );
                    const showInvoiceDiscount = invoiceDiscount > 0.01;

                    const printWindow = window.open("", "_blank");

                    const printContent = `
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <title>Facture</title>
                        <style>
                          body { font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif; margin: 0; padding: 20px; font-size: 12px; color: #111; }
                          .header { text-align: center; margin-bottom: 20px; }
                          .restaurant-name { font-size: 18px; font-weight: bold; }
                          .invoice-info { margin: 20px 0; }
                          .items { margin: 20px 0; font-family: 'Courier New', 'Times New Roman', serif; }
                          .item { display: flex; justify-content: space-between; margin: 5px 0; }
                          .adjustments { margin: 15px 0; border-top: 1px dashed #000; padding-top: 10px; }
                          .total { margin-top: 15px; border-top: 1px solid #000; padding-top: 10px; font-weight: bold; }
                          .footer { text-align: center; margin-top: 30px; font-size: 10px; }
                          @media print {
                            body { margin: 0; }
                            .no-print { display: none; }
                          }
                        </style>
                      </head>
                      <body>
                        <div class="header">
                          <div class="restaurant-name">${invoiceData.restaurant_name}</div>
                          ${invoiceData.restaurant_address ? `<div>${invoiceData.restaurant_address}</div>` : ""}
                          ${invoiceData.restaurant_phone ? `<div>Tel: ${invoiceData.restaurant_phone}</div>` : ""}
                        </div>

                        <div class="invoice-info">
                          <div>Date: ${new Date(invoiceData.timestamp || Date.now()).toLocaleString("fr-FR")}</div>
                          ${isBoutique ? "" : `<div>${invoiceData.table_number}</div>`}
                          ${!isBoutique && invoiceData.area_name ? `<div>Aire : ${escapeHtml(invoiceData.area_name)}</div>` : ""}
                          ${isBoutique ? "" : `<div>Serveur : ${invoiceData.server_name}</div>`}
                          <div>Caissier : ${invoiceData.cashier_name}</div>
                        </div>

                        <div class="items">
                          <div style="border-bottom: 1px dashed #000; padding-bottom: 5px; margin-bottom: 10px;">
                            <strong>Article(s)</strong>
                          </div>
                          ${invoiceData.items
                            .map((item) => {
                              const itemExtrasPrice = item.extras
                                ? item.extras.reduce(
                                    (extSum, ext) =>
                                      extSum +
                                      (Number(ext.price) || 0) *
                                        (ext.quantity || ext.selectedQuantity || 1),
                                    0,
                                  )
                                : 0;
                              const totalItemPrice =
                                (Number(item.price) + itemExtrasPrice) *
                                (item.quantity || 1);
                              const itemLabel =
                                item.display_name || formatInvoiceItemName(item);

                              return `
                            <div class="item">
                              <span>${item.quantity || 1}x ${itemLabel}</span>
                              <span>${Number(totalItemPrice).toLocaleString()} F</span>
                            </div>
                            ${
                              item.extras && item.extras.length > 0
                                ? item.extras
                                    .map(
                                      (extra) => `
                              <div class="item" style="font-size: 10px; margin-left: 20px; color: #666;">
                                <span>  +${extra.name} ${
                                  (extra.quantity || extra.selectedQuantity || 1) > 1
                                    ? "×" + (extra.quantity || extra.selectedQuantity || 1)
                                    : ""
                                }</span>
                                <span>${
                                  Number(extra.price) > 0
                                    ? "+" +
                                      (
                                        (extra.price || 0) *
                                        (extra.quantity || extra.selectedQuantity || 1)
                                      ).toLocaleString() +
                                      " F"
                                    : "gratuit"
                                }</span>
                              </div>
                            `,
                                    )
                                    .join("")
                                : ""
                            }`;
                            })
                            .join("")}
                        </div>

                        <div class="adjustments">
                          <div class="item" style="font-weight:bold;">
                            <span>Sous-total:</span>
                            <span>${Number(invoiceData.subtotal).toLocaleString()} F</span>
                          </div>
                          ${
                            showInvoiceDiscount
                              ? `
                          <div class="item" style="font-weight:bold;">
                            <span>Réduction:</span>
                            <span>-${Number(invoiceDiscount).toLocaleString()} F</span>
                          </div>
                          `
                              : ""
                          }
                          ${
                            invoiceData.adjustments.length > 0
                              ? `
                            <div style="margin-top: 10px;">
                              <strong>Réductions:</strong>
                              ${invoiceData.adjustments
                                .map(
                                  (adj) => `
                                <div class="item">
                                  <span>${adj.scope === "item" ? "Article" : "Panier"}${adj.targetLabel ? " · " + adj.targetLabel : ""}${adj.reason ? " (" + adj.reason + ")" : ""}</span>
                                  <span>-${Number(adj.amount || 0).toLocaleString()} F</span>
                                </div>
                              `,
                                )
                                .join("")}
                            </div>
                          `
                              : ""
                          }
                        </div>

                        <div class="total">
                          <div class="item" style="font-size: 16px;">
                            <span>Total:</span>
                            <span>${Number(invoiceData.final_total).toLocaleString()} F</span>
                          </div>
                        </div>

                        <div class="footer">
                          <div>Merci de votre visite !</div>
                          <div>Powered by MOO Sync - ${new Date().getFullYear()}</div>
                        </div>

                        <div class="no-print" style="margin-top: 30px; text-align: center;">
                          <button onclick="window.print()" style="padding: 10px 20px; font-size: 14px;">Imprimer</button>
                          <button onclick="window.close()" style="padding: 10px 20px; font-size: 14px; margin-left: 10px;">Fermer</button>
                        </div>
                      </body>
                    </html>
                  `;

                    printWindow.document.write(printContent);
                    printWindow.document.close();

                    setTimeout(() => {
                      printWindow.print();
                    }, 500);

                    closeInvoiceGenerationModal({ force: true });
                    triggerSuccessAnimation("Facture prête à imprimer.");

                    scheduleLoadTables("invoice-printed");
                    if (selectedTable) {
                      loadTableOrders(selectedTable.id);
                    }
                    */
                  }}
                  className="px-5 py-3 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition flex items-center gap-2"
                >
                  <Printer size={18} />
                  Imprimer la facture
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ItemDiscountModal
        open={showItemDiscountModal}
        draft={itemDiscountDraft}
        setDraft={setItemDiscountDraft}
        error={itemDiscountError}
        formatPrice={formatPrice}
        formatItemName={formatInvoiceItemName}
        computeItemTotal={computeItemTotalWithExtras}
        onApply={handleApplyItemDiscount}
        onClose={() => setShowItemDiscountModal(false)}
      />

      {false && showItemDiscountModal && itemDiscountDraft.item && (
        <div role="dialog" aria-modal="true" aria-label="Réduction article" className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fadeIn">
          <div className="w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-3xl border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/5">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-amber-200">
                  Réduction article
                </p>
                <h3 className="text-lg font-semibold">
                  {formatInvoiceItemName(itemDiscountDraft.item)}
                </h3>
              </div>
              <button
                onClick={() => setShowItemDiscountModal(false)}
                className="rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 p-2"
                aria-label="Fermer"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {(() => {
                const item = itemDiscountDraft.item;
                const unitWithExtras =
                  computeItemTotalWithExtras({ ...item, quantity: 1 }) || 0;
                const chosenQty = Math.min(
                  Math.max(1, Number(itemDiscountDraft.quantity) || 1),
                  Number(item.quantity) || 1,
                );
	                const baseAmount = unitWithExtras * chosenQty;
	                const rawValue = Number(itemDiscountDraft.value);
	                const value = Number.isFinite(rawValue) ? rawValue : 0;
	                const computedRaw =
	                  itemDiscountDraft.unit === "percentage"
	                    ? (baseAmount * value) / 100
	                    : value * chosenQty;
	                const computed = Math.max(0, computedRaw);
	                const isOverLimit =
	                  itemDiscountDraft.unit === "percentage"
	                    ? value > 100
	                    : value > unitWithExtras;
	                return (
	                  <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80">
	                    <div className="flex items-center justify-between">
	                      <span>Base remisable ({chosenQty}×)</span>
	                      <span className="font-semibold text-amber-200">
	                        {formatPrice(baseAmount)}
	                      </span>
	                    </div>
	                    <div className="mt-1 flex items-center justify-between text-xs text-white/60">
	                      <span>Réduction estimée</span>
	                      <span
	                        className={`font-semibold ${
	                          isOverLimit ? "text-red-300" : "text-emerald-200"
	                        }`}
	                      >
	                        -{formatPrice(computed)}
	                      </span>
	                    </div>
	                    {isOverLimit ? (
	                      <p className="mt-2 text-xs text-red-200">
	                        La remise dépasse le maximum autorisé ({formatPrice(
	                          itemDiscountDraft.unit === "amount"
	                            ? unitWithExtras
	                            : baseAmount,
	                        )}). Corrigez la valeur.
	                      </p>
	                    ) : null}
	                  </div>
	                );
	              })()}

              <div className="space-y-2">
                <label className="text-sm font-semibold text-white/80">
                  Quantité à remiser (max {itemDiscountDraft.item.quantity || 1})
                </label>
                <input
                  type="number"
                  min="1"
                  max={Number(itemDiscountDraft.item.quantity) || 1}
                  value={itemDiscountDraft.quantity}
                  onChange={(e) => {
                    const max = Number(itemDiscountDraft.item.quantity) || 1;
                    const val = Math.max(
                      1,
                      Math.min(Number(e.target.value) || 1, max),
                    );
                    setItemDiscountDraft((prev) => ({
                      ...prev,
                      quantity: val,
                    }));
                  }}
                  className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-white/80">
                    {itemDiscountDraft.unit === "amount"
                      ? "Remise par unité"
                      : "Pourcentage"}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={
                      itemDiscountDraft.unit === "percentage"
                        ? 100
                        : (() => {
                            const item = itemDiscountDraft.item;
                            const unitWithExtras =
                              computeItemTotalWithExtras({ ...item, quantity: 1 }) || 0;
                            return unitWithExtras;
                          })()
                    }
                    step="0.01"
	                    value={itemDiscountDraft.value}
	                    onChange={(e) =>
	                      setItemDiscountDraft((prev) => ({
	                        ...prev,
	                        value: e.target.value,
	                      }))
	                    }
                    className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-white/80">
                    Unité
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setItemDiscountDraft((prev) => ({
                          ...prev,
                          unit: "amount",
                        }))
                      }
                      className={`px-3 py-2 rounded-xl border text-sm font-semibold transition ${
                        itemDiscountDraft.unit === "amount"
                          ? "border-amber-300 bg-amber-500/10 text-amber-100"
                          : "border-white/10 bg-white/5 text-white/70 hover:border-white/20"
                      }`}
                    >
                      F
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setItemDiscountDraft((prev) => ({
                          ...prev,
                          unit: "percentage",
                        }))
                      }
                      className={`px-3 py-2 rounded-xl border text-sm font-semibold transition ${
                        itemDiscountDraft.unit === "percentage"
                          ? "border-amber-300 bg-amber-500/10 text-amber-100"
                          : "border-white/10 bg-white/5 text-white/70 hover:border-white/20"
                      }`}
                    >
                      %
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-white/80">
                  Motif
                </label>
                <textarea
                  rows={3}
                  value={itemDiscountDraft.reason}
                  onChange={(e) =>
                    setItemDiscountDraft((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                  placeholder="Ex : geste commercial, plat en retard..."
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t border-white/10 bg-white/5 flex gap-3 justify-end">
              <button
                onClick={() => setShowItemDiscountModal(false)}
                className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 text-white hover:border-white/20 transition"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleApplyItemDiscount();
                }}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition"
              >
                Appliquer
              </button>
            </div>
            {itemDiscountError && (
              <p role="alert" className="mx-5 mb-4 rounded-xl border border-red-400/40 bg-red-950 px-4 py-3 text-sm text-white">
                {itemDiscountError}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Modal de scission de facture */}
      {showSplitModal && (() => {
        const activeSplit = currentOrderForPayment?.splitBill || persistedSplitBill;
        const dashboardInvoices = activeSplit?.id
          ? hydrateSplitInvoices(activeSplit)
          : splitInvoices;
        const availableLineTotal = (item) => {
          const sourceQuantity = Number(item?.quantity || item?.source_quantity || 1);
          const availableQuantity = Number(
            item?.available_quantity ?? item?.quantity ?? 0,
          );
          const fullTotal = Number(item?.total_price || item?.totalPrice || 0);
          return sourceQuantity > 0
            ? fullTotal * (availableQuantity / sourceQuantity)
            : 0;
        };
        const dashboardUnassigned = activeSplit?.unassigned_items || [];
        const unassignedTotal = dashboardUnassigned.reduce(
          (sum, item) => sum + availableLineTotal(item),
          0,
        );
        const paidInvoices = dashboardInvoices.filter(
          (invoice) => invoice.status === "paid",
        );
        const openInvoices = dashboardInvoices.filter(
          (invoice) => invoice.status !== "paid",
        );
        const paidTotal = paidInvoices.reduce(
          (sum, invoice) =>
            sum +
            (invoice.settlement_net_minor !== null && invoice.settlement_net_minor !== undefined
              ? Number(invoice.settlement_net_minor) / 100
              : Number(invoice.subtotal || 0)),
          0,
        );
        const openTotal = openInvoices.reduce(
          (sum, invoice) => sum + Number(invoice.pricing?.amount_due ?? invoice.subtotal ?? 0),
          0,
        );
        const remainingTotal = openTotal + unassignedTotal;
        const tableTotal = dashboardInvoices.reduce(
          (sum, invoice) => sum + Number(invoice.pricing?.subtotal ?? invoice.subtotal ?? 0),
          unassignedTotal,
        );
        const customerCount = Number(
          selectedTable?.customer_count || selectedTable?.customerCount || 0,
        );
        const paymentMethodLabel = (method) => {
          const normalized = String(method || "").toLowerCase();
          if (normalized === "fully_discounted") return "remise 100 %";
          if (normalized === "complimentary") return "offert";
          if (["cash", "especes", "espèces"].includes(normalized)) {
            return "espèces";
          }
          return String(method || "paiement").replaceAll("_", " ");
        };

        if (isSplitMode) {
          const invoice =
            splitInvoices.find((entry) => entry.id === selectedSplitInvoiceId) ||
            splitInvoices[0];
          const selectionRows = buildSplitEditorSelectionRows({
            unassignedItems,
            selectedItems: invoice?.items || [],
          }).map((row) => ({
            ...row,
            label: formatInvoiceItemName(
              row.selectedItems[0] || row.availableItems[0],
            ),
          }));
          const selectedQuantity = (invoice?.items || []).reduce(
            (sum, item) => sum + Number(item.quantity || 0),
            0,
          );
          const selectedEditorTotal = selectionRows.reduce(
            (sum, row) => sum + Number(row.unitTotal || 0) * Number(row.selected || 0),
            0,
          );
          const editorTableTotal = activeSplit?.id
            ? tableTotal
            : getAssignedTotal() + getUnassignedTotal();

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-md">
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="split-editor-title"
                className="relative flex h-[90vh] max-h-[820px] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0b1328] via-[#0f1c36] to-[#0b1328] text-white shadow-2xl"
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(124,92,255,0.25),transparent_35%),radial-gradient(circle_at_80%_10%,rgba(59,130,246,0.2),transparent_30%),radial-gradient(circle_at_30%_80%,rgba(16,185,129,0.15),transparent_30%)] opacity-90" />
                <header className="relative z-10 flex items-start justify-between border-b border-white/10 bg-white/5 px-6 py-5">
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-amber-300">
                      <Scissors size={24} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.25em] text-amber-200">
                        Table {getTableDisplayName(selectedTable)}
                        {customerCount > 0 ? ` · ${customerCount} personne(s)` : ""}
                      </p>
                      <h3 id="split-editor-title" className="text-2xl font-bold">
                        Diviser l'addition
                      </h3>
                      <p className="text-sm text-white/65">
                        {invoice?.name || "Nouvelle facture"} — choisissez ce que paie ce client
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-right">
                      <p className="text-xs text-white/55">Total de la table</p>
                      <p className="font-bold text-amber-200">
                        {formatPrice(editorTableTotal)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeSplitEditor}
                      className="rounded-2xl border border-white/10 bg-white/10 p-3 hover:bg-white/20"
                      aria-label="Fermer"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </header>

                <main className="relative z-10 min-h-0 flex-1 overflow-y-auto p-6">
                  <div className="mb-4 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80">
                    <AlertCircle size={20} className="shrink-0 text-amber-300" />
                    <p>
                      Appuyez sur <strong className="text-white">+</strong> pour mettre un article dans cette facture. Ce que vous ne choisissez pas reste sur la table.
                    </p>
                  </div>

                  <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="mb-3 flex items-baseline justify-between gap-3">
                      <p className="text-xs uppercase tracking-[0.25em] text-amber-200">
                        Articles de la table
                      </p>
                      <p className="text-sm text-white/55">
                        {selectionRows.reduce(
                          (sum, row) => sum + row.available + row.selected,
                          0,
                        )} article(s)
                      </p>
                    </div>
                    <div className="space-y-2">
                      {selectionRows.map((row) => {
                        const capacity = row.available + row.selected;
                        const unitTotal = row.unitTotal;
                        const selectedLineTotal = unitTotal * row.selected;
                        return (
                          <div
                            key={row.key}
                            className={`grid items-center gap-3 rounded-xl border px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto_120px] ${
                              row.selected > 0
                                ? "border-amber-300/40 bg-amber-500/10"
                                : "border-white/10 bg-white/5"
                            }`}
                          >
                            <div>
                              <p className="font-semibold">{row.label}</p>
                              <p className="text-sm text-white/55">
                                {formatPrice(unitTotal)} l'unité · {capacity} sur la table
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() =>
                                  moveNatureInSplitInvoice(
                                    invoice.id,
                                    row.key,
                                    "remove",
                                  )
                                }
                                disabled={row.selected <= 0}
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/10 disabled:opacity-30"
                                aria-label={`Retirer un ${row.label}`}
                              >
                                <Minus size={20} />
                              </button>
                              <div
                                className={`flex h-11 w-16 items-center justify-center rounded-xl border text-xl font-bold ${
                                  row.selected > 0
                                    ? "border-amber-300/60 bg-white/10"
                                    : "border-white/15 bg-white/5 text-white/30"
                                }`}
                              >
                                {row.selected}
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  moveNatureInSplitInvoice(
                                    invoice.id,
                                    row.key,
                                    "add",
                                  )
                                }
                                disabled={row.available <= 0}
                                className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-400 text-slate-950 disabled:cursor-not-allowed disabled:opacity-30"
                                aria-label={`Ajouter un ${row.label}`}
                              >
                                <Plus size={20} />
                              </button>
                            </div>
                            <p className={`text-right font-bold ${row.selected > 0 ? "text-amber-200" : "text-white/25"}`}>
                              {row.selected > 0 ? formatPrice(selectedLineTotal) : "—"}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                </main>

                <footer className="relative z-10 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 bg-white/5 px-6 py-4">
                  <div>
                    <p className="text-sm text-white/65">
                      Ce client paie · {selectedQuantity} article(s)
                    </p>
                    <p className="text-3xl font-bold text-amber-200">
                      {formatPrice(selectedEditorTotal)}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={closeSplitEditor}
                      className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 font-semibold hover:bg-white/10"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={finalizeSplit}
                      disabled={!invoice?.items?.length || isSplitActionPending}
                      className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-6 py-3 font-semibold text-slate-950 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Check size={18} />
                      {splitEditorPartId ? "Enregistrer" : "Créer la facture"}
                    </button>
                  </div>
                </footer>
              </div>
            </div>
          );
        }

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-md">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="split-dashboard-title"
              className="relative flex h-[92vh] max-h-[900px] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0b1328] via-[#0f1c36] to-[#0b1328] text-white shadow-2xl"
            >
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(124,92,255,0.25),transparent_35%),radial-gradient(circle_at_80%_10%,rgba(59,130,246,0.2),transparent_30%),radial-gradient(circle_at_30%_80%,rgba(16,185,129,0.15),transparent_30%)] opacity-90" />
              <header className="relative z-10 flex items-start justify-between border-b border-white/10 bg-white/5 px-6 py-5">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-amber-300">
                    <Scissors size={24} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-amber-200">
                      Table {getTableDisplayName(selectedTable)}
                      {customerCount > 0 ? ` · ${customerCount} personne(s)` : ""}
                    </p>
                    <h3 id="split-dashboard-title" className="text-2xl font-bold">
                      Diviser l'addition
                    </h3>
                    <p className="text-sm text-white/65">
                      {dashboardInvoices.length} facture(s) créée(s) · la table n'est pas encore soldée
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSplitModal(false)}
                  className="rounded-2xl border border-white/10 bg-white/10 p-3 hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <X size={20} />
                </button>
              </header>

              <main className="relative z-10 min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                <section className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <p className="text-sm text-white/55">Total de la table</p>
                    <p className="mt-1 text-2xl font-bold">{formatPrice(tableTotal)}</p>
                  </div>
                  <div className="rounded-2xl border border-emerald-300/25 bg-emerald-500/10 p-4">
                    <p className="flex items-center gap-2 text-sm text-emerald-200/80">
                      <Check size={15} /> Déjà payé
                    </p>
                    <p className="mt-1 text-2xl font-bold text-emerald-200">
                      {formatPrice(paidTotal)}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-amber-300/30 bg-amber-500/10 p-4">
                    <p className="text-sm text-amber-200/80">Reste à payer</p>
                    <p className="mt-1 text-2xl font-bold text-amber-200">
                      {formatPrice(remainingTotal)}
                    </p>
                  </div>
                </section>

                <section className="rounded-2xl border border-emerald-300/25 bg-emerald-500/10 p-4">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Scissors size={20} className="text-emerald-200" />
                      <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-emerald-200">
                          Factures
                        </p>
                        <p className="font-semibold">{dashboardInvoices.length} facture(s)</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={openNewProgressiveSplitInvoice}
                      disabled={isSplitActionPending}
                      className="flex items-center gap-2 rounded-xl border border-emerald-300/30 bg-emerald-500/15 px-4 py-3 font-semibold text-emerald-100 hover:bg-emerald-500/25 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Plus size={18} /> Nouvelle facture
                    </button>
                  </div>

                  <div className="grid gap-3 lg:grid-cols-2">
                    {dashboardInvoices.map((invoice) => {
                      const isPaid = invoice.status === "paid";
                      const displayedTotal =
                        isPaid && Number(invoice.settlement_net_minor) >= 0
                          ? Number(invoice.settlement_net_minor) / 100
                          : Number(invoice.pricing?.amount_due ?? invoice.subtotal ?? 0);
                      return (
                        <article
                          key={invoice.id}
                          className={`rounded-2xl border bg-white/5 p-4 ${
                            isPaid ? "border-white/10" : "border-amber-300/35"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <h4 className="font-semibold">{invoice.name}</h4>
                            <span className={`rounded-lg border px-2 py-1 text-xs font-semibold ${
                              isPaid
                                ? "border-emerald-300/30 bg-emerald-500/15 text-emerald-200"
                                : "border-amber-300/30 bg-amber-500/15 text-amber-200"
                            }`}>
                              {isPaid
                                ? invoice.settlement_kind === "global_remainder"
                                  ? "Payée avec le reste"
                                  : "Payée"
                                : "À payer"}
                            </span>
                          </div>
                          <div className="mt-3 space-y-1 text-sm text-white/75">
                            {(invoice.items || []).map((item, index) => {
                              const extraRows = getSplitExtraRows(item);
                              return (
                                <div key={`${invoice.id}-${getItemKey(item)}-${index}`}>
                                  <div className="flex justify-between gap-3">
                                    <span>{item.quantity} × {formatInvoiceItemName(item)}</span>
                                    <span>{formatPrice(item.totalPrice || item.total_price || 0)}</span>
                                  </div>
                                  {extraRows.map((extra, extraIndex) => (
                                    <div
                                      key={`${extra.id ?? extra.name}-${extraIndex}`}
                                      className="flex justify-between gap-3 pl-3 text-xs text-white/55"
                                    >
                                      <span>+ {extra.quantity} × {extra.name}</span>
                                      <span>{extra.total > 0 ? formatPrice(extra.total) : ""}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                          <div className="mt-3 flex items-end justify-between gap-3 border-t border-white/10 pt-3">
                            <p className="text-xs text-white/55">
                              {isPaid && invoice.settlement_paid_at
                                ? `Réglée en ${paymentMethodLabel(invoice.settlement_payment_method)} à ${new Date(invoice.settlement_paid_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`
                                : "Total"}
                            </p>
                            <p className={`text-xl font-bold ${isPaid ? "text-emerald-200" : "text-amber-200"}`}>
                              {formatPrice(displayedTotal)}
                            </p>
                          </div>
                          {invoice.pricing?.discount_total > 0 && (
                            <p className="mt-1 text-sm text-emerald-200">
                              Réduction : -{formatPrice(invoice.pricing.discount_total)}
                            </p>
                          )}
                          {invoice.pricing_error && !isPaid && (
                            <p role="alert" className="mt-2 text-sm text-red-200">
                              Réduction à corriger : {invoice.pricing_error}
                            </p>
                          )}
                          {isPaid ? (
                            <button
                              type="button"
                              onClick={() => handlePrintSplitInvoice(invoice)}
                              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/10 px-3 py-3 font-semibold hover:bg-white/15"
                            >
                              <Printer size={17} /> Imprimer le reçu
                            </button>
                          ) : (
                            <>
                                <button
                                  type="button"
                                  onClick={() => openStoredSplitPayment(invoice)}
                                  disabled={Boolean(invoice.pricing_error)}
                                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 px-3 py-3 font-semibold text-slate-950 hover:bg-emerald-400"
                              >
                                <DollarSign size={17} /> Payer cette facture
                              </button>
                              <div className="mt-2 grid grid-cols-3 gap-2">
                                <button
                                  type="button"
                                  onClick={() => handlePrintSplitInvoice(invoice)}
                                  className="flex items-center justify-center gap-1 rounded-xl border border-white/10 bg-white/10 px-2 py-2 text-sm hover:bg-white/15"
                                >
                                  <Printer size={15} /> Imprimer
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openSplitInvoiceEditor(invoice)}
                                  disabled={isSplitActionPending}
                                  className="flex items-center justify-center gap-1 rounded-xl border border-white/10 bg-white/10 px-2 py-2 text-sm hover:bg-white/15"
                                >
                                  <Edit3 size={15} /> Modifier
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setSplitDiscountEditor({
                                    invoiceId: invoice.id,
                                    scope: invoice.adjustments?.some((entry) => entry.scope === "item") ? "item" : "cart",
                                    unit: "percentage",
                                    value: "",
                                    reason: "",
                                    orderItemId: invoice.items?.[0]?.order_item_id || invoice.items?.[0]?.id || "",
                                    quantity: 1,
                                  })}
                                  disabled={!canApplyDiscount()}
                                  className="flex items-center justify-center gap-1 rounded-xl border border-white/10 bg-white/10 px-2 py-2 text-sm hover:bg-white/15"
                                >
                                  <Receipt size={15} /> Réduction
                                </button>
                              </div>
                            </>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </section>

                {dashboardUnassigned.length > 0 ? (
                  <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="mb-3 flex items-baseline justify-between gap-3">
                      <p className="text-xs uppercase tracking-[0.25em] text-amber-200">
                        Pas encore dans une facture
                      </p>
                      <p className="text-sm text-white/55">
                        {dashboardUnassigned.reduce(
                          (sum, item) => sum + Number(item.available_quantity || 0),
                          0,
                        )} article(s) · {formatPrice(unassignedTotal)}
                      </p>
                    </div>
                    <div className="grid gap-2 md:grid-cols-2">
                      {dashboardUnassigned.map((item) => (
                        <div key={item.id} className="flex justify-between gap-3 rounded-xl bg-white/5 px-3 py-2 text-sm">
                          <span>{item.available_quantity} × {item.product_name}</span>
                          <span className="text-white/65">{formatPrice(availableLineTotal(item))}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                ) : null}
              </main>

              <footer className="relative z-10 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 bg-white/5 px-6 py-4">
                <div>
                  <p className="text-sm text-white/65">Reste à payer sur la table</p>
                  <p className="text-3xl font-bold text-amber-200">
                    {formatPrice(remainingTotal)}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowSplitModal(false)}
                    className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 font-semibold hover:bg-white/10"
                  >
                    Fermer
                  </button>
                  <button
                    type="button"
                    onClick={() => openStoredSplitPayment(null)}
                    disabled={openInvoices.length === 0 && dashboardUnassigned.length === 0}
                    className="flex items-center gap-2 rounded-2xl bg-emerald-500 px-6 py-3 font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-40"
                  >
                    <DollarSign size={18} /> Payer le reste
                  </button>
                </div>
              </footer>
            </div>
          </div>
        );
      })()}

      {splitDiscountEditor && (() => {
        const split = currentOrderForPayment?.splitBill || persistedSplitBill;
        const invoice = hydrateSplitInvoices(split).find(
          (entry) => String(entry.id) === String(splitDiscountEditor.invoiceId),
        );
        if (!invoice) return null;
        const discountItems = invoice.items.map((item) => ({
          ...item,
          unitTotal: Number(item.totalPrice || 0) / Math.max(1, Number(item.quantity || 1)),
        }));
        const discounts = (invoice.adjustments || []).map((entry, index) => ({
          ...entry,
          type: "discount",
          amount: Number(invoice.pricing?.priced_adjustments?.[index]?.amount || 0),
          targetItemId: String(entry.order_item_id || ""),
        }));
        return (
          <>
            <DiscountModal
              open={!splitItemDiscountDraft}
              tableLabel={`Table ${getTableDisplayName(selectedTable)} · ${invoice.name}`}
              customerCount={0}
              items={discountItems}
              adjustments={discounts}
              draft={splitDiscountEditor}
              setDraft={setSplitDiscountEditor}
              canApply={canApplyDiscount()}
              formatPrice={formatPrice}
              formatItemName={formatInvoiceItemName}
              computeItemTotal={(item) => Number(item.unitTotal || 0) * Number(item.quantity || 1)}
              getItemKey={(item) => String(item.order_item_id || item.id)}
              onOpenItem={(item, adjustment) => setSplitItemDiscountDraft({
                invoiceId: invoice.id,
                item,
                scope: "item",
                orderItemId: item.order_item_id || item.id,
                quantity: Number(adjustment?.target_quantity || 1),
                unit: adjustment?.unit || "amount",
                value: adjustment?.value || "",
                reason: adjustment?.reason || "",
              })}
              onRemove={(adjustmentId) => removeSplitInvoiceDiscount(invoice.id, adjustmentId)}
              onRemoveAll={() => persistSplitInvoiceAdjustments(invoice.id, [], "Réductions retirées.")}
              onApplyGlobal={() => saveSplitInvoiceDiscount(splitDiscountEditor)}
              onClose={() => setSplitDiscountEditor(null)}
              wholeLabel="Toute cette facture"
            />
            <ItemDiscountModal
              open={Boolean(splitItemDiscountDraft)}
              draft={splitItemDiscountDraft || {}}
              setDraft={setSplitItemDiscountDraft}
              error={null}
              formatPrice={formatPrice}
              formatItemName={formatInvoiceItemName}
              computeItemTotal={(item) => Number(item.unitTotal || 0) * Number(item.quantity || 1)}
              onApply={() => saveSplitInvoiceDiscount(splitItemDiscountDraft)}
              onClose={() => setSplitItemDiscountDraft(null)}
            />
          </>
        );
      })()}

      {/* Modal de gestion des clients */}
      {showCustomerModal && (
        <CustomerCaisseModal
          isOpen={showCustomerModal}
          initialView={customerModalInitialView}
          onClose={() => {
            setShowCustomerModal(false);
            resetCustomerModalState();
          }}
          onSelectCustomer={handleCustomerModalSelect}
        />
      )}

      {showCreditRecoveryModal && (
        <CreditRecoveryModal
          isOpen={showCreditRecoveryModal}
          onClose={() => setShowCreditRecoveryModal(false)}
          currentUser={currentUser}
          currentSession={currentSession}
          apiBase={API_BASE}
          onRecoveryCompleted={() => {
            if (currentSession?.id) {
              setCartRenderKey((prev) => prev + 1);
            }
          }}
        />
      )}
      {/* Modal de confirmation d'impression du reçu */}
      {/* Modal de confirmation d'impression du reçu */}
      {showPrintReceiptModal && pendingReceiptData && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleCancelPrintReceipt}
        >
          <div
            className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 transform animate-slideInFromBottom shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-3xl"
                style={{ backgroundColor: colors.primary }}
              >
                <Printer className="w-9 h-9" strokeWidth={2.4} />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Impression du reçu
              </h3>
              <p className="text-gray-600">Paiement effectué avec succès !</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4 mb-6">
              <div className="text-center space-y-2">
                <div className="text-sm text-gray-600">
                  {pendingReceiptData.fastFoodMode
                    ? "Commande comptoir"
                    : pendingReceiptData.table?.isDelivery
                    ? "Paiement Livraison"
                    : `Table ${pendingReceiptData.table?.number}`}
                </div>
                {receiptPaymentNumber && (
                  <div className="text-xs text-gray-500">
                    Commande : {receiptPaymentNumber}
                  </div>
                )}
                <div className="text-2xl font-bold text-gray-800">
                  {receiptServiceTotal.toLocaleString()} F
                </div>
                <div className="text-sm text-gray-600">
                  <span>Perçu : {formatReceiptOptionalAmount(receiptPerceivedTotal)}</span>
                  <span className="ml-3">
                    Monnaie : {formatReceiptOptionalAmount(receiptChangeAmount)}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-center text-gray-600 mb-6">
              Souhaitez-vous imprimer le reçu pour ce paiement ?
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleCancelPrintReceipt}
                className="flex-1 py-3 px-6 bg-gray-200 text-gray-700 rounded-2xl font-semibold hover:bg-gray-300 transition-all duration-200"
              >
                Non, merci
              </button>
              <button
                onClick={handleConfirmPrintReceipt}
                disabled={isReceiptPrinting}
                className="flex-1 py-3 px-6 text-white rounded-2xl font-semibold hover:opacity-90 transition-all duration-200 flex items-center justify-center"
                style={{
                  backgroundColor: colors.primary,
                  opacity: isReceiptPrinting ? 0.75 : 1,
                  cursor: isReceiptPrinting ? "not-allowed" : "pointer",
                }}
              >
                <Printer className="w-5 h-5 mr-2" />
                {isReceiptPrinting ? "Impression..." : "Imprimer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de transfert de table */}
      {showTableTransferModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className="relative w-full max-w-3xl mx-4 overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-gradient-to-br from-[#0b1328] via-[#0f1c36] to-[#0b1328] text-white h-[85vh]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.2),transparent_35%),radial-gradient(circle_at_80%_10%,rgba(56,189,248,0.18),transparent_25%),radial-gradient(circle_at_10%_80%,rgba(251,191,36,0.16),transparent_30%)] pointer-events-none opacity-90" />
            <div className="relative z-10 flex flex-col h-full">
              {/* Header */}
              <div className="flex items-start justify-between px-6 py-5 border-b border-white/10 bg-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-sky-400/25 border border-sky-300/40 flex items-center justify-center text-sky-100 shadow-lg">
                    <ArrowLeftRight className="w-7 h-7" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-sky-200">
                      Salle
                    </p>
                    <h3 className="text-2xl font-bold leading-tight">
                      Transférer
                    </h3>
                    <p className="text-sm text-white/70">
                      Changez le numéro de table ou le serveur responsable,
                      sans modifier les articles en cours.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowTableTransferModal(false);
                    setAvailableTablesForTransfer([]);
                    setAvailableServersForTransfer([]);
                  }}
                  className="rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 p-2"
                  aria-label="Fermer"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 min-h-0 p-6 space-y-6 overflow-y-auto">
                {/* Table actuelle */}
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-white/60 mb-1">
                      Table actuelle
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-white/60">Table</span>
                      <span className="text-2xl font-bold">
                        {selectedTable?.number}
                      </span>
                    </div>
                    <p className="text-xs text-white/60 mt-1">
                      {selectedTable?.customerCount || 0} personne(s)
                    </p>
                  </div>
                  <div className="text-sm text-white/80 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-8 rounded-full bg-sky-500/15 px-3 border border-sky-300/40 text-sky-100 text-xs font-semibold">
                      📦{" "}
                      {selectedTable?.isDelivery
                        ? currentDeliveryOrder.items.length +
                          existingDeliveryItems.length
                        : currentOrder.items.length +
                          existingItemsForPayment.length}{" "}
                      article(s)
                    </span>
                  </div>
                </div>

                <div className={`grid ${stewardModeEnabled ? "grid-cols-3" : "grid-cols-2"} gap-3 rounded-2xl border border-white/10 bg-white/5 p-2`}>
                  <button
                    type="button"
                    onClick={() => setTableTransferMode("table")}
                    className={`rounded-xl px-4 py-3 text-sm font-semibold transition ${
                      tableTransferMode === "table"
                        ? "bg-sky-500 text-white shadow-lg"
                        : "text-white/70 hover:bg-white/10"
                    }`}
                  >
                    Transfert de table
                  </button>
                  <button
                    type="button"
                    onClick={() => setTableTransferMode("server")}
                    className={`rounded-xl px-4 py-3 text-sm font-semibold transition ${
                      tableTransferMode === "server"
                        ? "bg-emerald-500 text-white shadow-lg"
                        : "text-white/70 hover:bg-white/10"
                    }`}
                  >
                    Changer de serveur
                  </button>
                  {stewardModeEnabled && (
                    <button
                      type="button"
                      onClick={() => setTableTransferMode("steward")}
                      className={`rounded-xl px-4 py-3 text-sm font-semibold transition ${
                        tableTransferMode === "steward"
                          ? "bg-amber-500 text-slate-950 shadow-lg"
                          : "text-white/70 hover:bg-white/10"
                      }`}
                    >
                      Autre steward
                    </button>
                  )}
                </div>

                {/* Tables libres pour transfert */}
                {tableTransferMode === "table" && availableTablesForTransfer.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-xs uppercase tracking-[0.25em] text-white/60">
                      Tables disponibles
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {availableTablesForTransfer.map((table) => (
                        <button
                          key={table.id}
                          onClick={() => confirmTableTransfer(table)}
                          disabled={isTableOperationInProgress}
                          className="group rounded-2xl border border-white/10 bg-white/5 hover:border-sky-300/60 hover:bg-sky-500/10 transition-all duration-200 p-4 text-left flex flex-col justify-between gap-3 disabled:cursor-wait disabled:opacity-50"
                        >
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs uppercase tracking-[0.25em] text-white/50">
                                  Table
                                </p>
                                <p className="text-lg font-semibold">
                                  {table.number}
                                </p>
                              </div>
                              <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center text-sm">
                                →
                              </div>
                            </div>
                            <p className="text-xs text-white/60">
                              Capacité: {table.capacity ?? "—"} pers.
                            </p>
                            {table.location && (
                              <p className="text-xs text-white/50">
                                Lieu: {table.location}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center justify-between text-xs text-white/50 pt-1 border-t border-white/10">
                            <span>
                              {isTableOperationInProgress
                                ? "Transfert en cours…"
                                : `Transférer vers table ${table.number}`}
                            </span>
                            <span className="group-hover:text-sky-200 group-hover:translate-x-0.5 transition-transform">
                              ⇢
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : tableTransferMode === "table" ? (
                  <div className="text-center py-10">
                    <div className="text-4xl mb-3">😔</div>
                    <p className="text-sm text-white/70">
                      Aucune table libre disponible.
                    </p>
                  </div>
                ) : tableTransferMode === "steward" ? (
                  stewardTransferTargets.length > 0 ? (
                    <div className="space-y-5">
                      {stewardTransferTargets.map((group) => (
                        <div key={group.key} className="space-y-2">
                          <p className="font-semibold text-amber-200">
                            {group.display_name}
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {(group.tables || []).map((table) => (
                              <button
                                key={table.id}
                                type="button"
                                disabled={isTableOperationInProgress}
                                onClick={() =>
                                  confirmTableTransfer(table, { mode: "steward" })
                                }
                                className={`rounded-2xl border p-4 text-left transition disabled:opacity-50 ${
                                  String(table.status || "free") === "free"
                                    ? "border-white/10 bg-white/5 hover:border-amber-300/60 hover:bg-amber-500/10"
                                    : "border-red-400/60 bg-red-500/10 hover:bg-red-500/20"
                                }`}
                              >
                                <span className="block text-xs text-white/50">Table</span>
                                <span className="text-lg font-bold">{table.number}</span>
                                {Number(table.amount_due || 0) > 0 && (
                                  <span className="mt-1 block text-xs text-amber-200">
                                    {formatPrice(table.amount_due)}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-sm text-white/70">
                      Aucune autre affectation disponible.
                    </div>
                  )
                ) : availableServersForTransfer.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-xs uppercase tracking-[0.25em] text-white/60">
                      Serveurs actifs
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {availableServersForTransfer.map((server) => {
                        const serverName =
                          [server.first_name, server.last_name]
                            .filter(Boolean)
                            .join(" ") || server.username || `Serveur ${server.id}`;
                        const isCurrent =
                          String(selectedTable?.active_owner_user_id || selectedTable?.managed_by_user_id || "") ===
                          String(server.id);
                        return (
                          <button
                            key={server.id}
                            type="button"
                            onClick={() => confirmServerTransfer(server)}
                            disabled={isCurrent || isTableOperationInProgress}
                            className="rounded-2xl border border-white/10 bg-white/5 p-4 text-left transition hover:border-emerald-300/60 hover:bg-emerald-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <div className="font-semibold text-lg">{serverName}</div>
                            <div className="mt-1 text-xs text-white/60">
                              {isCurrent ? "Déjà responsable" : "Confier cette table"}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10 text-sm text-white/70">
                    Aucun serveur actif disponible.
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-white/10 bg-white/5 flex justify-end">
                <button
                  onClick={() => {
                    setShowTableTransferModal(false);
                    setAvailableTablesForTransfer([]);
                    setAvailableServersForTransfer([]);
                  }}
                  className="px-5 py-2 rounded-2xl bg-white/5 text-white border border-white/10 hover:bg-white/10 transition"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de fusion de tables */}
	      {showTableMergeModal && (
	        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
	          <div className="relative w-full max-w-3xl mx-4 overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-gradient-to-br from-[#0b1328] via-[#0f1c36] to-[#0b1328] text-white h-[85vh]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(251,191,36,0.18),transparent_35%),radial-gradient(circle_at_80%_10%,rgba(59,130,246,0.18),transparent_25%),radial-gradient(circle_at_10%_80%,rgba(16,185,129,0.14),transparent_30%)] pointer-events-none opacity-90" />
            <div className="relative z-10 flex flex-col h-full">
              {/* Header */}
              <div className="flex items-start justify-between px-6 py-5 border-b border-white/10 bg-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-amber-400/20 border border-amber-300/40 flex items-center justify-center text-amber-200 shadow-lg">
                    <GitMerge className="w-7 h-7" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-amber-200">
                      Salle
                    </p>
                    <h3 className="text-2xl font-bold leading-tight">
                      Fusion de tables
                    </h3>
                    <p className="text-sm text-white/70">
                      Sélectionnez une table occupée à fusionner avec la table{" "}
                      <span className="font-semibold">
                        {selectedTable?.number}
                      </span>
                      .
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowTableMergeModal(false);
                    setAvailableTablesForMerge([]);
                  }}
                  className="rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 p-2"
                  aria-label="Fermer"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-6 overflow-y-auto">
                {/* Destination table */}
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-white/60 mb-1">
                      Table de destination
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-white/60">Table</span>
                      <span className="text-2xl font-bold">
                        {selectedTable?.number}
                      </span>
                    </div>
                    <p className="text-xs text-white/60 mt-1">
                      {selectedTable?.customerCount || 0} personne(s)
                    </p>
                  </div>
                  <div className="text-sm text-white/80 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-8 rounded-full bg-amber-500/15 px-3 border border-amber-300/40 text-amber-100 text-xs font-semibold">
                      📦 {getCartTotalQuantity()} article(s)
                    </span>
                  </div>
                </div>

                {/* Tables à fusionner */}
                {availableTablesForMerge.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-xs uppercase tracking-[0.25em] text-white/60">
                      Tables disponibles
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {availableTablesForMerge.map((table) => (
                        <button
                          key={table.id}
                          onClick={() => confirmTableMerge(table)}
                          disabled={isTableOperationInProgress}
                          className="group rounded-2xl border border-white/10 bg-white/5 hover:border-amber-300/60 hover:bg-amber-500/10 transition-all duration-200 p-4 text-left flex flex-col justify-between gap-3 disabled:cursor-wait disabled:opacity-50"
                        >
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs uppercase tracking-[0.25em] text-white/50">
                                  Table
                                </p>
                                <p className="text-lg font-semibold">
                                  {table.number}
                                </p>
                              </div>
                              <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center text-sm">
                                ->
                              </div>
                            </div>
                            <p className="text-xs text-white/60">
                              Capacité: {table.capacity ?? "—"} pers.
                            </p>
                            {table.location && (
                              <p className="text-xs text-white/50">
                                Lieu: {table.location}
                              </p>
                            )}
                            <p className="text-xs text-white/60">
                              👥 {table.customerCount || 0} personne(s)
                            </p>
                          </div>
                          <div className="flex items-center justify-between text-xs text-white/50 pt-1 border-t border-white/10">
                            <span>
                              {isTableOperationInProgress
                                ? "Fusion en cours…"
                                : `Fusionner dans table ${selectedTable?.number}`}
                            </span>
                            <span className="group-hover:text-amber-200 group-hover:translate-x-0.5 transition-transform">
                              ⇢
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <div className="text-4xl mb-3">😔</div>
                    <p className="text-sm text-white/70">
                      Aucune table occupée disponible pour fusion.
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-white/10 bg-white/5 flex justify-end">
                <button
                  onClick={() => {
                    setShowTableMergeModal(false);
                    setAvailableTablesForMerge([]);
                  }}
                  className="px-5 py-2 rounded-2xl bg-white/5 text-white border border-white/10 hover:bg-white/10 transition"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
	        </div>
	      )}

		      {/* Modal Rapport X */}
      {!isServerParticipationMode && showXReport && (
	      <XReportModal
	          isOpen={showXReport}
          onClose={() => {
            setShowXReport(false);
            if (isXReportFromLogout) {
              setIsXReportFromLogout(false);
              performLogout();
            }
          }}
          currentUser={currentUser}
          currentSession={currentSession}
        />
      )}

      {showCashLogoutValidation && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-gradient-to-br from-[#020617] via-[#0b1120] to-[#020617] text-white">
            <div className="pointer-events-none absolute inset-0 opacity-60">
              <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-amber-500/10 blur-3xl" />
              <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-cyan-500/10 blur-3xl" />
            </div>

            <div className="relative z-10 border-b border-white/10 bg-white/5 backdrop-blur-xl">
              <div className="relative p-6 sm:p-8">
                <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-20 translate-x-10 -translate-y-16 bg-amber-400 blur-2xl" />
                <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full opacity-10 -translate-x-8 translate-y-8 bg-cyan-400 blur-2xl" />
                <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-black/30 border border-white/10 flex items-center justify-center text-2xl">
                      <Receipt size={22} className="text-amber-200" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                        Validation encaissements
                      </p>
                      <h3 className="mt-1 text-2xl font-bold">
                        Confirmer les montants avant déconnexion
                      </h3>
                      <p className="text-sm text-white/70 mt-1">
                        Vérifiez les montants encaissés par mode de paiement.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCashLogoutValidation(false)}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 border border-white/15 hover:bg-white/20 transition"
                  >
                    <X size={18} className="text-white" />
                  </button>
                </div>
              </div>
            </div>

            <div className="relative z-10 p-6 sm:p-8 max-h-[70vh] overflow-y-auto space-y-6">
              {cashLogoutLoading && (
                <div className="flex items-center gap-3 text-sm text-white/70">
                  <div className="h-4 w-4 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
                  Chargement des données...
                </div>
              )}

              {cashLogoutError && (
                <div className="rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                  {cashLogoutError}
                </div>
              )}

              {cashLogoutReport && (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-xs uppercase tracking-[0.2em] text-white/60">
                        Ajustements
                      </p>
                      <div className="mt-3 space-y-2 text-sm text-white/80">
                        <div className="flex justify-between">
                          <span>Remises</span>
                          <span className="font-semibold text-white">
                            {formatPrice(
                              cashLogoutReport.sales_summary?.total_discounts || 0,
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Annulations</span>
                          <span className="font-semibold text-white">
                            {cashLogoutReport.statistics?.cancelled_items || 0}{" "}
                            article(s)
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Entrées caisse</span>
                          <span className="font-semibold text-white">
                            {formatPrice(
                              cashLogoutReport.adjustments?.cash_in || 0,
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Sorties caisse</span>
                          <span className="font-semibold text-white">
                            {formatPrice(
                              cashLogoutReport.adjustments?.cash_out || 0,
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-xs uppercase tracking-[0.2em] text-white/60">
                        Totaux
                      </p>
                      <div className="mt-3 space-y-2 text-sm text-white/80">
                        <div className="flex justify-between">
                          <span>Total théorique</span>
                          <span className="font-semibold text-white">
                            {formatPrice(cashLogoutSnapshot.totalExpected)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total saisi</span>
                          <span className="font-semibold text-white">
                            {formatPrice(cashLogoutSnapshot.totalActual)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Écart global</span>
                          <span
                            className={`font-semibold ${
                              cashLogoutSnapshot.isTotalBalanced
                                ? "text-emerald-300"
                                : "text-rose-300"
                            }`}
                          >
                            {cashLogoutSnapshot.totalDiff > 0
                              ? `+${formatPrice(cashLogoutSnapshot.totalDiff)}`
                              : formatPrice(cashLogoutSnapshot.totalDiff)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 overflow-hidden">
                    <div className="grid grid-cols-3 bg-white/5 text-xs uppercase tracking-[0.2em] text-white/60 px-4 py-3">
                      <span>Mode</span>
                      <span className="text-center">Théorique</span>
                      <span className="text-right">Saisi & écart</span>
                    </div>
                    <div className="max-h-[260px] overflow-y-auto">
                      {cashLogoutSnapshot.methods.map((method) => {
                        const diffClass =
                          Math.abs(method.diff) <= 0.01
                            ? "text-emerald-300"
                            : "text-rose-300";
                        const diffLabel =
                          method.diff > 0
                            ? `+${formatPrice(method.diff)}`
                            : formatPrice(method.diff);
                        return (
                          <div
                            key={method.key}
                            className="grid grid-cols-3 items-center px-4 py-3 border-t border-white/10 text-sm"
                          >
                            <div className="font-semibold text-white">
                              {method.name}
                            </div>
                            <div className="text-center text-white/70">
                              {formatPrice(method.expected)}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <input
                                type="number"
                                inputMode="decimal"
                                placeholder="0"
                                className="w-32 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-right text-sm text-white placeholder-white/40"
                                value={cashLogoutInputs[method.key] || ""}
                                onChange={(e) => {
                                  cashLogoutInputsDirtyRef.current = true;
                                  setCashLogoutInputs((prev) => ({
                                    ...prev,
                                    [method.key]: e.target.value,
                                  }));
                                }}
                              />
                              <span className={`text-xs font-semibold ${diffClass}`}>
                                {diffLabel}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    setIsXReportFromLogout(false);
                    setShowXReport(true);
                  }}
                  className="w-full sm:flex-1 py-3 px-4 rounded-xl border border-white/10 bg-white/10 text-white/80 font-semibold hover:bg-white/20"
                >
                  Afficher le rapport X
                </button>
                <button
                  type="button"
                  disabled={!cashLogoutSnapshot.isTotalBalanced || cashLogoutLoading}
                  onClick={handleConfirmCashLogout}
                  className={`w-full sm:flex-1 py-3 px-4 rounded-xl font-semibold ${
                    !cashLogoutSnapshot.isTotalBalanced || cashLogoutLoading
                      ? "bg-white/10 text-white/40 cursor-not-allowed"
                      : "bg-emerald-500/80 text-white hover:bg-emerald-500"
                  }`}
                >
                  Déconnecter
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForceLogoutModal(true);
                    setForceLogoutError("");
                  }}
                  className="w-full sm:flex-1 py-3 px-4 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600"
                >
                  Forcer la déconnexion
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForceLogoutModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60] p-4">
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-gradient-to-br from-[#020617] via-[#0b1120] to-[#020617] text-white">
            <div className="pointer-events-none absolute inset-0 opacity-60">
              <div className="absolute -top-20 -right-20 h-48 w-48 rounded-full bg-rose-500/15 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-amber-400/10 blur-3xl" />
            </div>
            <div className="relative z-10 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                    Déconnexion forcée
                  </p>
                  <h3 className="text-lg font-bold mt-2">
                    Validation manager requise
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowForceLogoutModal(false)}
                  className="h-10 w-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center"
                >
                  <X size={18} className="text-white" />
                </button>
              </div>

              {forceLogoutError && (
                <div className="mt-4 rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                  {forceLogoutError}
                </div>
              )}

              <div className="mt-5 space-y-4">
                <div>
                  <label className="text-sm font-semibold text-white/80">
                    Code manager / admin
                  </label>
                  <input
                    type="password"
                    className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-white placeholder-white/40"
                    value={forceLogoutManagerCode}
                    onChange={(e) => setForceLogoutManagerCode(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-white/80">
                    Motif de l'écart
                  </label>
                  <textarea
                    rows={3}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-white placeholder-white/40"
                    value={forceLogoutReason}
                    onChange={(e) => setForceLogoutReason(e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={handleForceLogout}
                  disabled={forceLogoutSubmitting}
                  className={`w-full py-3 px-4 rounded-xl font-semibold text-white ${
                    forceLogoutSubmitting
                      ? "bg-white/10 text-white/40 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  Confirmer la déconnexion forcée
                </button>
                <button
                  type="button"
                  onClick={() => setShowForceLogoutModal(false)}
                  className="w-full py-3 px-4 rounded-xl bg-white/10 text-white/70 font-semibold hover:bg-white/20"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de déconnexion */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              {/* Icône */}
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogOut className="w-8 h-8 text-red-600" />
              </div>

              {/* Titre */}
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Confirmer la déconnexion
              </h3>

              {/* Description */}
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir vous déconnecter ?
                <br />
                <span className="text-sm text-red-600 font-medium">
                  Toutes les données non sauvegardées seront perdues.
                </span>
              </p>

              {/* Boutons d'action */}
              <div className="flex flex-col space-y-3">
                {/* Bouton Rapport X pour caissiers + admin */}
                {currentUser &&
                  ["cashier", "admin"].includes(
                    String(currentUser.role || "").toLowerCase(),
                  ) &&
                  !isServerParticipationMode && (
                  <button
                    onClick={async () => {
                      const occupiedTables = await checkTablesWithOrders();
                      if (occupiedTables.length > 0) {
                        setOccupiedTablesDetails(occupiedTables);
                        setShowOccupiedTablesModal(true);
                        triggerErrorAnimation(getLogoutBlockedMessage(occupiedTables.length));
                        return;
                      }
                      performLogoutWithXReport();
                    }}
                    className="w-full py-3 px-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Déconnexion avec rapport X
                  </button>
                )}

                {/* Bouton déconnexion directe */}
                <button
                  onClick={async () => {
                    if (isServerParticipationMode) {
                      await performLogout();
                      return;
                    }
                    const occupiedTables = await checkTablesWithOrders();
                    if (occupiedTables.length > 0) {
                      setOccupiedTablesDetails(occupiedTables);
                      setShowOccupiedTablesModal(true);
                      triggerErrorAnimation(getLogoutBlockedMessage(occupiedTables.length));
                      return;
                    }
                    performLogout();
                  }}
                  className="w-full py-3 px-4 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors"
                >
                  Déconnexion sans rapport X
                </button>

                {/* Bouton annuler */}
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="w-full py-3 px-4 bg-gray-200 text-gray-800 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal des logs de débogage */}
      {showDebugLogs && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Logs de Débogage ({debugLogs.length})
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={forceSyncInterface}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                  disabled={!selectedTable}
                  title="Synchroniser l'interface"
                >
                  🔄 Sync
                </button>
                <button
                  onClick={forceCleanCancelledItems}
                  className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                  title="Vider les articles annulés"
                >
                  🚨 Nettoyer
                </button>
                <button
                  onClick={copyLogsToClipboard}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                  disabled={debugLogs.length === 0}
                >
                  Copier
                </button>
                <button
                  onClick={clearDebugLogs}
                  className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                  disabled={debugLogs.length === 0}
                >
                  Vider
                </button>
                <button
                  onClick={() => setShowDebugLogs(false)}
                  className="p-2 hover:bg-gray-100 rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Statut du panier */}
            <div className="mb-4 p-3 bg-gray-100 rounded">
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium">Panier actuel:</span>
                  <span className="ml-2">
                    {currentOrder.items?.length || 0} articles
                  </span>
                </div>
                <div>
                  <span className="font-medium">Articles existants:</span>
                  <span className="ml-2">
                    {existingItemsForPayment?.length || 0} articles
                  </span>
                </div>
                <div>
                  <span className="font-medium">Table:</span>
                  <span className="ml-2">
                    {selectedTable?.name || "Aucune"}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-auto border rounded bg-gray-50 p-4">
              {debugLogs.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">
                    Aucun log disponible. Les logs d'annulation apparaîtront
                    ici.
                  </p>
                  <div className="text-xs text-gray-400">
                    <p>
                      💡 <strong>Conseil:</strong> Si des articles restent
                      visibles après annulation:
                    </p>
                    <p>
                      1. Cliquez sur{" "}
                      <span className="bg-red-100 px-1 rounded">
                        🚨 Nettoyer
                      </span>{" "}
                      pour forcer le nettoyage
                    </p>
                    <p>
                      2. Cliquez sur{" "}
                      <span className="bg-green-100 px-1 rounded">🔄 Sync</span>{" "}
                      pour synchroniser
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {debugLogs
                    .slice()
                    .reverse()
                    .map((log) => (
                      <div
                        key={log.id}
                        className={`border-l-4 pl-4 py-2 ${
                          log.level === "ERROR"
                            ? "border-red-500 bg-red-50"
                            : log.level === "WARNING"
                              ? "border-yellow-500 bg-yellow-50"
                              : log.level === "SUCCESS"
                                ? "border-green-500 bg-green-50"
                                : "border-blue-500 bg-blue-50"
                        }`}
                      >
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-500 font-mono">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                          <span
                            className={`font-medium px-2 py-1 rounded text-xs ${
                              log.level === "ERROR"
                                ? "bg-red-200 text-red-800"
                                : log.level === "WARNING"
                                  ? "bg-yellow-200 text-yellow-800"
                                  : log.level === "SUCCESS"
                                    ? "bg-green-200 text-green-800"
                                    : "bg-blue-200 text-blue-800"
                            }`}
                          >
                            {log.level}
                          </span>
                          <span className="text-gray-800 flex-1">
                            {log.message}
                          </span>
                        </div>
                        {log.data && (
                          <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto border">
                            {log.data}
                          </pre>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal d'aide pour les annulations OAA */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800">
                🔧 Guide d'Utilisation - Système d'Annulation OAA
              </h3>
              <button
                onClick={() => setShowHelpModal(false)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Règles OAA */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-bold text-blue-800 mb-3">
                  📋 Règles OAA (Origine Autorisation Annulation)
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✅</span>
                    <span>
                      <strong>Article non envoyé</strong> : Annulation libre par
                      le caissier
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-orange-600">🔒</span>
                    <span>
                      <strong>Article en cuisine/bar</strong> : Code manager
                      obligatoire
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-blue-600">?</span>
                    <span>
                      <strong>Toutes les annulations</strong> : Logs
                      automatiques dans l'admin
                    </span>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-bold text-gray-800 mb-3">
                  Boutons d'Action
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center">
                      <X size={16} className="text-red-600" />
                    </div>
                    <div>
                      <div className="font-medium">Annuler Panier</div>
                      <div className="text-gray-600">
                        Annule tous les articles du panier
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                      <FileText size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium">Logs Debug</div>
                      <div className="text-gray-600">
                        Affiche les erreurs et actions
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
                      <RefreshCw size={16} className="text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium">Synchroniser</div>
                      <div className="text-gray-600">
                        Corrige les incohérences d'affichage
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-yellow-100 rounded flex items-center justify-center">
                      <AlertCircle size={16} className="text-yellow-600" />
                    </div>
                    <div>
                      <div className="font-medium">Aide</div>
                      <div className="text-gray-600">
                        Ce guide d'utilisation
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Procédures */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-bold text-green-800 mb-3">
                  🚀 Procédures d'Annulation
                </h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="font-medium text-green-700">
                      1. Article non envoyé en cuisine
                    </div>
                    <div className="text-gray-600 ml-4">
                      → Clic sur le produit → Confirmation → Annulation
                      immédiate
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-orange-700">
                      2. Article en préparation
                    </div>
                    <div className="text-gray-600 ml-4">
                      → Clic produit → Saisir motif → Code manager → Validation
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-red-700">
                      3. Annulation panier complet
                    </div>
                    <div className="text-gray-600 ml-4">
                      → Bouton rouge → Motif → Code manager si nécessaire →
                      Validation
                    </div>
                  </div>
                </div>
              </div>

              {/* Codes d'erreur */}
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-bold text-red-800 mb-3">
                  Codes d'Erreur Courants
                </h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">"Article déjà annulé"</span>
                    <div className="text-gray-600 ml-4">
                      → Cliquer sur Synchroniser pour mettre à jour l'affichage
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">"Code manager invalide"</span>
                    <div className="text-gray-600 ml-4">
                      → Vérifier le code manager
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">"Le statut a changé"</span>
                    <div className="text-gray-600 ml-4">
                      → Actualiser la page et réessayer
                    </div>
                  </div>
                </div>
              </div>

              {/* Diagnostic */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-bold text-purple-800 mb-3">
                  �? En cas de Problème
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-purple-600">1.</span>
                    <span>
                      Ouvrir les <strong>Logs Debug</strong> pour voir les
                      erreurs
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-purple-600">2.</span>
                    <span>
                      Cliquer <strong>Copier</strong> et envoyer les logs au
                      support
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-purple-600">3.</span>
                    <span>
                      Utiliser <strong>Synchroniser</strong> pour corriger
                      l'affichage
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-purple-600">4.</span>
                    <span>
                      Si le problème persiste, actualiser la page (F5)
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-gray-100 p-4 rounded-lg text-center">
                <p className="text-sm text-gray-600">
                  <strong>Support Technique :</strong> Le Digitalivore - Expert
                  MOO Sync
                  <br />
                  Système de caisse professionnel pour restaurants africains 🇨🇮
                </p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowHelpModal(false)}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {onlineAssignOrder && (
        <div className="fixed inset-0 z-[130] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div
            className={`w-full max-w-xl rounded-3xl border shadow-2xl ${
              isDarkTheme
                ? "bg-[#0b1530] border-white/15 text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                  Commande en ligne
                </p>
                <h3 className="text-2xl font-bold mt-1">Accepter la commande</h3>
              </div>
              <button
                onClick={closeOnlineAcceptModal}
                className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 hover:bg-white/20 transition flex items-center justify-center"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div
                className={`rounded-2xl border px-4 py-3 ${
                  isDarkTheme
                    ? "bg-white/5 border-white/15 text-white/90"
                    : "bg-gray-50 border-gray-200 text-gray-800"
                }`}
              >
                <p className="text-xs uppercase tracking-[0.2em] text-white/60">Commande</p>
                <p className="text-lg font-bold mt-1">
                  {getOnlineOrderDisplayCode(onlineAssignOrder)}
                </p>
                <p className="text-sm mt-1">
                  Table client: {onlineAssignOrder?.table_label_client || "Non précisée"}
                </p>
                <p className="text-sm">
                  Total: {getOnlineOrderAmount(onlineAssignOrder).toLocaleString("fr-FR")} F
                </p>
                {Array.isArray(onlineAssignOrder?.items) &&
                  onlineAssignOrder.items.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {(onlineAssignOrder.items || []).map((item, index) => {
                        const variationLabel = getOnlineOrderItemVariationLabel(item);
                        const quantity = Number(item?.quantity || 1) || 1;
                        const unitPrice = Number(item?.unit_price || item?.price || 0) || 0;
                        const lineTotal = getOnlineOrderLineTotal(item);
                        const extras = Array.isArray(item?.extras) ? item.extras : [];
                        return (
                          <div
                            key={`${onlineAssignOrder?.id || "online"}-line-${index}`}
                            className={`rounded-xl border px-3 py-2 ${
                              isDarkTheme
                                ? "border-white/10 bg-white/5 text-white/90"
                                : "border-gray-200 bg-white text-gray-800"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold truncate">
                                  {quantity}x {getOnlineOrderItemName(item)}
                                </p>
                                {variationLabel && (
                                  <p
                                    className={`text-xs mt-1 ${
                                      isDarkTheme ? "text-white/60" : "text-gray-600"
                                    }`}
                                  >
                                    {variationLabel}
                                  </p>
                                )}
                              </div>
                              <p className="text-sm font-semibold text-emerald-200 whitespace-nowrap">
                                {lineTotal.toLocaleString("fr-FR")} F
                              </p>
                            </div>
                            <p
                              className={`text-xs mt-1 ${
                                isDarkTheme ? "text-white/60" : "text-gray-600"
                              }`}
                            >
                              {unitPrice.toLocaleString("fr-FR")} F / unité
                            </p>
                            {extras.length > 0 && (
                              <div
                                className={`mt-1 text-xs ${
                                  isDarkTheme ? "text-white/60" : "text-gray-600"
                                }`}
                              >
                                Extras:{" "}
                                {extras
                                  .map((extra) => {
                                    const extraQty = Number(extra?.quantity || 1) || 1;
                                    const extraPrice = Number(extra?.price || 0) || 0;
                                    const extraName = String(extra?.name || "Option").trim();
                                    return `${extraQty}x ${extraName}${
                                      extraPrice > 0
                                        ? ` (+${(extraPrice * extraQty).toLocaleString("fr-FR")} F)`
                                        : ""
                                    }`;
                                  })
                                  .join(" • ")}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.25em] mb-2 text-white/70">
                  Table système
                </label>
                <select
                  value={onlineAssignTableId}
                  onChange={(event) => setOnlineAssignTableId(event.target.value)}
                  className={`w-full rounded-2xl px-4 py-3 border ${
                    isDarkTheme
                      ? "bg-slate-900/70 border-white/20 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Sélectionner une table</option>
                  {availableOnlineAcceptTables.map((table) => {
                    const ownerName = getTableOwnerFirstName(table);
                    const occupied = String(table?.status || "").toLowerCase() === "occupied";
                    const label = occupied
                      ? `${getTableDisplayName(table)} · Occupée${ownerName ? ` (${ownerName})` : ""}`
                      : `${getTableDisplayName(table)} · Libre`;
                    return (
                      <option key={table.id} value={String(table.id)}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.25em] mb-2 text-white/70">
                  Nombre de clients (optionnel)
                </label>
                <input
                  type="number"
                  min="0"
                  value={onlineAssignCovers}
                  onChange={(event) => setOnlineAssignCovers(event.target.value)}
                  placeholder="Ex: 2"
                  className={`w-full rounded-2xl px-4 py-3 border ${
                    isDarkTheme
                      ? "bg-slate-900/70 border-white/20 text-white placeholder-white/35"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>

            <div className="px-6 pb-6 flex justify-end gap-3">
              <button
                onClick={closeOnlineAcceptModal}
                className="px-5 py-3 rounded-2xl border border-white/20 text-white/80 hover:bg-white/10 transition"
              >
                Annuler
              </button>
              <button
                onClick={submitOnlineOrderAccept}
                disabled={
                  !onlineAssignTableId ||
                  onlineActionLoadingId === Number(onlineAssignOrder?.id || 0)
                }
                className="px-5 py-3 rounded-2xl bg-emerald-500 text-slate-900 font-semibold hover:bg-emerald-400 transition disabled:opacity-60"
              >
                Confirmer l'acceptation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals caisse */}
      <CashMovementModal
        isOpen={!isServerParticipationMode && showCashMovementModal}
        onClose={() => setShowCashMovementModal(false)}
        currentUser={currentUser}
        currentSession={currentSession}
        onMovementCreated={handleMovementCreated}
        darkAmountInput
      />
      <CashJournalModal
        isOpen={!isServerParticipationMode && showCashJournal}
        onClose={() => setShowCashJournal(false)}
        currentUser={currentUser}
        currentSession={currentSession}
      />
      <CashDrawerModal
        isOpen={!isServerParticipationMode && showAdminCashDrawer}
        user={currentUser}
        lastAmount={adminLastAmount}
        onConfirm={handleAdminCashDrawerConfirm}
        onError={handleAdminCashDrawerError}
      />

      {/* Notification Système Intelligent */}
      {shouldShowCounterTreatmentBanner({
        hasForeignRecovery: Boolean(foreignCounterRecovery),
        hasDispatchPending: Boolean(counterDispatchPending),
        openOrderCount: openCounterOrders.length,
        isCounterOperator: isCounterOperatorRole,
        isServerMode: isServerParticipationMode,
        isCounterPaymentActive: Boolean(
          currentOrderForPayment?.fast_food_mode === true,
        ),
      }) && (
          <div className="fixed left-1/2 top-24 z-[70] w-[min(92vw,680px)] -translate-x-1/2 rounded-2xl border border-amber-400/40 bg-slate-950/95 p-4 text-white shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-bold text-amber-300">Transaction Comptoir à traiter</p>
                {foreignCounterRecovery && (
                  <p className="mt-1 text-sm text-white/75">
                    Brouillon interrompu de {foreignCounterRecovery.origin_user_name || "un autre caissier"}
                    {foreignCounterRecovery.stage ? ` · ${foreignCounterRecovery.stage}` : ""}.
                  </p>
                )}
                {counterDispatchPending && (
                  <div className="mt-2 flex items-center gap-3 text-sm">
                    <span>Paiement confirmé · envoi Cuisine/Bar à finaliser</span>
                    <button
                      type="button"
                      onClick={() => retryCounterDispatch(counterDispatchPending)}
                      className="rounded-lg bg-emerald-500 px-3 py-1 font-semibold text-white hover:bg-emerald-400"
                    >
                      Relancer l’envoi
                    </button>
                  </div>
                )}
                {openCounterOrders.map((entry) => (
                  <div key={entry.id} className="mt-2 flex items-center gap-3 text-sm">
                    <span>
                      {entry.order_number || `Commande ${entry.id}`} · {entry.cashier_first_name || "Caissier"}
                    </span>
                    {canManageCounterRecovery && (
                      <button
                        type="button"
                        onClick={() => cancelCounterRecovery(entry.id)}
                        className="rounded-lg bg-red-500/20 px-3 py-1 text-red-200 hover:bg-red-500/30"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {foreignCounterRecovery && canManageCounterRecovery && (
                <button
                  type="button"
                  onClick={() =>
                    cancelCounterRecovery(
                      foreignCounterRecovery.created_order_id || null,
                    )
                  }
                  className="rounded-xl bg-red-500 px-3 py-2 text-sm font-semibold"
                >
                  Annuler le brouillon
                </button>
              )}
            </div>
          </div>
        )}

      {showIntelligentRefreshNotification && (
        <div className="fixed top-20 right-4 z-50 bg-blue-500/90 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg border border-blue-400/30 animate-pulse">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-300 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">
              {intelligentRefreshMessage}
            </span>
          </div>
        </div>
      )}

      {kdsActionNotice && (
        <div className="fixed right-4 top-20 z-[80] w-[min(28rem,calc(100vw-2rem))] rounded-2xl border border-red-300/40 bg-slate-950/95 p-4 text-white shadow-2xl backdrop-blur-xl">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-xl bg-red-500/20 p-2 text-red-200">
              <AlertCircle size={20} aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">
                Article refusé par {kdsActionNotice.department || "le KDS"}
              </p>
              <p className="mt-1 text-sm text-slate-300">
                {(kdsActionNotice.items || [])
                  .map((item) => `${Number(item.quantity || 1)}× ${item.name || "Article"}`)
                  .join(", ") || "Un article"}
                {kdsActionNotice.tableNumber
                  ? ` · Table ${kdsActionNotice.tableNumber}`
                  : " · Comptoir"}
              </p>
              <p className="mt-2 text-xs text-amber-200">
                La vente reste facturable. Ouvrez le panier pour retirer ou offrir l’article si nécessaire.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {kdsActionNotice.tableId !== null &&
                  kdsActionNotice.tableId !== undefined && (
                    <button
                      type="button"
                      onClick={() => {
                        const runtimeTables = socketRuntimeRef.current?.tables || [];
                        const table = runtimeTables.find(
                          (entry) => String(entry?.id) === String(kdsActionNotice.tableId),
                        );
                        if (!table) {
                          triggerErrorAnimation("Table introuvable. Actualisez le plan de salle.");
                          return;
                        }
                        setActiveTab("tables");
                        setMobileServerTab("tables");
                        setKdsActionNotice(null);
                        void selectTableRef.current?.(table);
                      }}
                      className="rounded-xl bg-amber-400 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-300"
                    >
                      Voir la table
                    </button>
                  )}
                <button
                  type="button"
                  onClick={() => setKdsActionNotice(null)}
                  className="rounded-xl border border-white/20 px-3 py-2 text-sm font-semibold text-white hover:bg-white/10"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Barre de navigation mobile — rôle serveur uniquement */}
      {isServerParticipationMode && (
        <nav className="caisse-server-bottomnav" aria-label="Navigation mobile serveur">
          <button
            className={`caisse-server-tab${mobileServerTab === "tables" ? " active" : ""}`}
            onClick={() => switchMobileServerTab("tables")}
            aria-label="Tables"
          >
            <Users size={22} aria-hidden="true" />
            <span>Tables</span>
          </button>
          <button
            className={`caisse-server-tab${mobileServerTab === "menu" ? " active" : ""}`}
            onClick={() => switchMobileServerTab("menu")}
            aria-label="Menu"
          >
            <ChefHat size={22} aria-hidden="true" />
            <span>Menu</span>
          </button>
          <button
            className={`caisse-server-tab${mobileServerTab === "cart" ? " active" : ""}`}
            onClick={() => switchMobileServerTab("cart")}
            aria-label="Panier"
          >
            <span className="caisse-server-tab-icon-wrap">
              <ShoppingCart size={22} aria-hidden="true" />
              {cartNewItems.length > 0 && (
                <span className="caisse-server-tab-badge">
                  {cartNewItems.length > 99 ? "99+" : cartNewItems.length}
                </span>
              )}
            </span>
            <span>Panier</span>
          </button>
        </nav>
      )}
    </div>
  );
};

const HeaderButton = ({ children, title, onClick, badge = null }) => (
  <button
    onClick={onClick}
    className="caisse-header-button relative p-3 rounded-2xl bg-white/10 hover:bg-white/20 border border-white/10 text-white transition"
    title={title}
  >
    {children}
    {badge && (
      <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
        {badge}
      </span>
    )}
  </button>
);

export default MOOCaisseInterface;
